const __swcNsC=new Map();const __swcNsE=Object.freeze({});const __swcNs=(m)=>{if(m==null)return __swcNsE;if(typeof m!=='object')return m;if(__swcNsC.has(m))return __swcNsC.get(m);const{__default:__d,...r}=m;const ns='__default' in m?{...r,default:__d}:{...r};__swcNsC.set(m,ns);return ns;};
const __m0 = (() => {
// A plain object is one whose prototype is Object.prototype or null: an object
// literal, a JSON.parse result, an Object.create(null) map. Everything else
// that is an object (Date, Map, Set, Blob, File, ArrayBuffer, a typed array,
// a class instance, an element) has a shape that a spread destroys, so the
// store keeps it BY REFERENCE and the serialisers leave it out.
function isPlainObject(v) {
  if (v === null || typeof v !== 'object') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

// One replacer for every place that serialises store values (persistence, the
// SSR hydration payload, the dirty-tracking baselines). JSON.stringify hands a
// replacer the value AFTER toJSON (a Date is already a string), so the RAW
// value is read off the holder (`this[key]`) to decide: an array or a plain
// object passes, a Date passes only with keepDates, anything else is omitted
// at any depth. Inside an array JSON.stringify writes `null` for an omitted
// element, so [date, blob] serialises as [null, null]; an omitted object
// property disappears and is absent when the JSON is read back.
function omitNonPlain({ keepDates = false } = {}) {
  return function replacer(key, value) {
    const raw = this[key];
    if (raw === null || typeof raw !== 'object') return value;
    if (Array.isArray(raw) || isPlainObject(raw)) return value;
    if (keepDates && raw instanceof Date) return value;
    return undefined;
  };
}

// Persistence drops Dates (a Date does not round-trip through JSON); dirty
// tracking keeps them (a changed Date is a change).
const PERSIST_REPLACER = omitNonPlain({ keepDates: false });
const DIRTY_REPLACER = omitNonPlain({ keepDates: true });

// The two runtime prefix lists (persistence exclusion, transience) are created
// on first use and hold each prefix once.
const addOnce = (list, item) => {
  const next = Array.isArray(list) ? list : [];
  return next.includes(item) ? next : [...next, item];
};

const hasPrefixIn = (prefixes, key) => Array.isArray(prefixes) && prefixes.some((prefix) => key.startsWith(prefix));

// Every key-indexed map in the store is null-prototype: a key is whatever an
// app names it, and `hasOwnProperty`, `constructor`, `toString` or
// `__proto__` on a plain object literal resolve to an inherited member, so a
// subscribe or a setValue under such a name read the prototype instead of the
// map (F-826). The same rule the HTTP client's name-keyed maps follow.
const keyed = () => Object.create(null);

class Store {
  subscriptions = keyed();
  stores = keyed();
  _config = null;
  _name = null;

  // Batching
  _batchQueue = null;
  _flushScheduled = false;

  // Persistence
  _localStorage = null;
  _persistKey = null;
  _fromStorageEvent = false;

  // Dirty tracking
  _tracking = false;
  _baseline = keyed();
  _transientPrefixes = null;

  configure(config) {
    this._config = config;
    this._localStorage = config._localStorage || (typeof globalThis !== 'undefined' ? globalThis.localStorage : null);

    // Compute persistence key
    if (this._shouldPersist()) {
      this._persistKey = this._computePersistKey(config);
      this._hydrate();
      this._listenCrossTab(config);
    }
  }

  _shouldPersist() {
    const p = this._config?.persist;
    return p !== undefined && p !== false;
  }

  _computePersistKey(config) {
    const base = `swc4:${config.appId}`;

    if (config.session?.route) {
      const url = config._currentUrl || (typeof window !== 'undefined' ? window.location.pathname : '');
      const params = this._prefixMatchRoute(url, config.session.route);
      if (params) {
        const sessionId = Object.values(params)[0];
        if (sessionId) return `${base}:${sessionId}`;
      }
    }

    return base;
  }

  _prefixMatchRoute(url, routePattern) {
    const urlParts = url.split('/');
    const routeParts = routePattern.split('/');
    const params = {};
    for (let i = 0; i < routeParts.length; i++) {
      if (i >= urlParts.length) return null;
      if (routeParts[i].startsWith(':')) {
        params[routeParts[i].slice(1)] = urlParts[i];
      } else if (routeParts[i] !== urlParts[i]) {
        return null;
      }
    }
    return params;
  }

  _hydrate() {
    if (!this._localStorage || !this._persistKey) return;
    const raw = this._localStorage.getItem(this._persistKey);
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      if (data?.stores) {
        for (const [key, value] of Object.entries(data.stores)) {
          this.stores[key] = value;
        }
      }
    } catch (e) {
      // Corrupted data: ignore
    }
  }

  _listenCrossTab(config) {
    const addListener = config._addEventListener ||
      (typeof window !== 'undefined' ? (e, fn) => window.addEventListener(e, fn) : null);

    if (!addListener) return;

    addListener('storage', (event) => {
      if (event.key !== this._persistKey || !event.newValue) return;
      try {
        const data = JSON.parse(event.newValue);
        if (!data?.stores) return;

        this._fromStorageEvent = true;
        for (const [key, value] of Object.entries(data.stores)) {
          this.stores[key] = value;
          this._notifySubscribers(key);
        }
        this._fromStorageEvent = false;
      } catch (e) {
        this._fromStorageEvent = false;
      }
    });
  }

  _isExcluded(key) {
    if (key === 'route' || key.startsWith('router.')) return true;
    if (key === '_navigationBlocked' || key === '_navigationDenied') return true;
    // Runtime excludes (e.g. the flow engine excluding flow.<id>.* when it
    // owns that flow's persistence: app-level persist:true must not
    // double-persist a draft the engine already manages).
    if (hasPrefixIn(this._excludePrefixes, key)) return true;
    const persist = this._config?.persist;
    if (typeof persist === 'object' && persist?.exclude) {
      // An entry ending `*` excludes the whole prefix; exact match otherwise.
      return persist.exclude.some((entry) => (entry.endsWith('*')
        ? key.startsWith(entry.slice(0, -1))
        : key === entry));
    }
    return false;
  }

  /**
   * Exclude a key prefix from persistence at runtime (idempotent). With
   * `transient: true` the prefix is also excluded from dirty tracking: a key
   * under it is never recorded as touched and never enters a baseline, so a
   * value the framework writes on its own (an in-flight list, a streaming
   * partial) cannot make a page report unsaved changes. The plain call keeps
   * its meaning: out of storage, still dirty when it changes.
   */
  addPersistExclude(prefix, { transient = false } = {}) {
    if (typeof prefix !== 'string' || !prefix) return;
    this._excludePrefixes = addOnce(this._excludePrefixes, prefix);
    if (transient) this._transientPrefixes = addOnce(this._transientPrefixes, prefix);
  }

  _isTransient(key) {
    return hasPrefixIn(this._transientPrefixes, key);
  }

  _persistToStorage() {
    if (!this._shouldPersist() || !this._localStorage || !this._persistKey) return;
    if (this._fromStorageEvent) return;

    const toSave = keyed();
    Object.entries(this.stores)
      .filter(([key]) => !this._isExcluded(key))
      .forEach(([key, value]) => { toSave[key] = value; });

    // The replacer leaves out what JSON cannot carry back (a Blob, a Map, a
    // Date, an element) rather than saving an empty object in its place; an
    // omitted key is simply absent after hydration.
    this._localStorage.setItem(this._persistKey, JSON.stringify({
      _version: 1,
      _timestamp: Date.now(),
      stores: toSave
    }, PERSIST_REPLACER));
  }

  subscribe(storeName, callback, opts = {}) {
    if (typeof storeName !== 'string' || typeof callback !== 'function') {
      throw new Error('Invalid arguments: storeName must be a string and callback must be a function');
    }
    if (!this.subscriptions[storeName]) {
      this.subscriptions[storeName] = [];
    }
    const subscriptionId = Symbol('subscriptionId');
    const entry = { callback, id: subscriptionId, paused: opts.paused || false };
    this.subscriptions[storeName].push(entry);
    if (!entry.paused && this.stores[storeName] !== undefined) {
      callback(this.stores[storeName]);
    }
    return {
      unsubscribe: () => this.unsubscribe(storeName, subscriptionId),
      pause: () => { entry.paused = true; },
      resume: () => {
        entry.paused = false;
        if (this.stores[storeName] !== undefined) {
          entry.callback(this.stores[storeName]);
        }
      },
    };
  }

  unsubscribe(storeName, subscriptionId) {
    if (this.subscriptions[storeName]) {
      this.subscriptions[storeName] = this.subscriptions[storeName].filter(
        sub => sub.id !== subscriptionId
      );
    }
  }

  setValue(storeName, value, defaultValue) {
    if (typeof storeName !== 'string') {
      throw new Error('Invalid argument: storeName must be a string');
    }
    const v = value ?? defaultValue;
    // No-op primitive write (same value already stored) skips the notify below.
    // Every object write notifies: an array or a plain object gets a fresh
    // shallow copy (so mutation stays non-reactive), and anything else (a
    // Date, a Map, a Blob, a typed array, a class instance, an element) is
    // stored BY REFERENCE, because a spread would reduce it to a bag of its
    // own enumerable properties.
    const unchanged = (v === null || typeof v !== 'object')
      && storeName in this.stores && this.stores[storeName] === v;
    if (Array.isArray(v)) {
      this.stores[storeName] = [...v];
    } else if (isPlainObject(v)) {
      this.stores[storeName] = { ...v };
    } else {
      this.stores[storeName] = v;
    }
    if (unchanged) return;

    // Record the key so dirty checks only re-serialise what actually changed;
    // a transient key (addPersistExclude with transient: true) is never dirty.
    if (this._tracking && !this._isTransient(storeName)) this._touched.add(storeName);

    if (this._config?.batch) {
      if (!this._batchQueue) this._batchQueue = new Set();
      this._batchQueue.add(storeName);
      if (!this._flushScheduled) {
        this._flushScheduled = true;
        queueMicrotask(() => this._flush());
      }
    } else {
      this._notifySubscribers(storeName);
    }
  }

  _notifySubscribers(storeName) {
    if (this.subscriptions[storeName]) {
      const value = this.stores[storeName];
      // Snapshot: a callback may subscribe/unsubscribe during the loop.
      [...this.subscriptions[storeName]].forEach(sub => {
        if (!sub.paused) sub.callback(value);
      });
    }
  }

  _flush() {
    this._flushScheduled = false;

    if (this._batchQueue) {
      const keys = this._batchQueue;
      this._batchQueue = null;

      for (const key of keys) {
        if (!(key in this.stores) || !this.subscriptions[key]) continue;
        this._notifySubscribers(key);
      }
    }

    this._persistToStorage();
  }

  getValue(storeName, defaultValue = '') {
    return this.stores[storeName] ?? defaultValue;
  }

  get(...keys) {
    return keys.map(k => this.stores[k]);
  }

  setValues(obj) {
    if (!obj || typeof obj !== 'object') return;
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        this.setValue(key, obj[key]);
      }
    }
  }

  removeStore(storeName) {
    delete this.stores[storeName];
    delete this.subscriptions[storeName];

    // Schedule persist to remove key from storage
    if (this._shouldPersist()) {
      if (!this._flushScheduled) {
        this._flushScheduled = true;
        queueMicrotask(() => this._flush());
      }
    }
  }

  hasStore(storeName) {
    return storeName in this.stores;
  }

  getStoreNames() {
    return Object.keys(this.stores);
  }

  clear(includePersisted = false) {
    this.stores = keyed();
    this.subscriptions = keyed();
    if (includePersisted && this._localStorage && this._persistKey) {
      this._localStorage.removeItem(this._persistKey);
    }
  }

  // SubStore

  createSubStore(name) {
    const sub = new Store();
    sub._name = name || 'sub-' + Math.random().toString(36).slice(2, 7);
    sub.configure({ batch: true });
    return sub;
  }

  // Dirty tracking

  // A key's comparable form. The replacer omits every non-plain value (a live
  // element under `<key>.appendTarget` carries the store itself: a cycle) and
  // keeps Dates, so a changed Date is dirty and nothing here ever throws.
  _snapshot(key) {
    return JSON.stringify(this.stores[key], DIRTY_REPLACER);
  }

  _changed(key) {
    return this._snapshot(key) !== this._baseline[key];
  }

  _baselineAll() {
    this._touched = new Set();
    this._baseline = keyed();
    Object.keys(this.stores)
      .filter((key) => !this._isTransient(key))
      .forEach((key) => { this._baseline[key] = this._snapshot(key); });
  }

  startTracking() {
    this._tracking = true;
    this._baselineAll();
  }

  // Compare only keys written since startTracking against their baseline: a
  // key set then reverted is correctly not dirty, and the scan is O(writes),
  // not O(state).
  isDirty() {
    if (!this._tracking) return false;
    return [...this._touched].some((key) => this._changed(key));
  }

  getDirtyKeys() {
    if (!this._tracking) return [];
    return [...this._touched].filter((key) => this._changed(key));
  }

  resetDirty() {
    this._baselineAll();
  }
}

const store = new Store();
return { isPlainObject, omitNonPlain, Store, store };
})();

const __m1 = (() => {
// Route pattern matching — the ONE canonical matcher. The client router
// (core/services/router.js), the SSR router (server/ssr/ssr-router.js) and the
// API router (server/api-router.js) all match through this module, so the three
// runtimes cannot drift. Pure functions: no DOM, no node builtins — the browser
// and workerd run the same code. Nothing here throws: an unmatched route is
// `null`, a malformed pattern degrades to a literal comparison.
//
// ── Pattern vocabulary ────────────────────────────────────────────────────
//
//   /docs/api        static segment      — matches itself
//   /docs/:slug      named param         — matches exactly ONE segment,
//                                          captured under `slug`
//   /docs/*          catch-all           — matches ONE OR MORE trailing
//                                          segments (NOT the bare /docs)
//   /docs/**         optional catch-all  — matches ZERO or more, so it also
//                                          matches the parent path /docs
//   /docs/:path*     named catch-all, required form
//   /docs/:path**    named catch-all, optional form
//   *                the whole-route wildcard — the global 404 route. Matches
//                    every URL, captures nothing. Unchanged from day one.
//
// Why the doubled star: it mirrors Next.js's doubled brackets one-for-one
// (`[...slug]` → `*`, `[[...slug]]` → `**`), so a converter maps a segment by
// counting characters rather than carrying a side-channel flag. And it keeps
// the existing vocabulary intact — `:name` still means "capture under this
// name", the star suffix is the only new spelling, and `:path*` reads as
// ":path, and the rest of the URL with it".
//
// A star is a catch-all only in the LAST segment of a pattern. Anywhere else
// it is compared literally, exactly as before this module learned catch-alls —
// `/docs/*/edit` is a path with a literal `*` segment, not a wildcard, because
// guessing which of two readings the author meant is worse than doing the
// documented thing.
//
// ── Captured values ───────────────────────────────────────────────────────
//
// A catch-all named `foo` sets TWO params:
//
//   foo          the remaining segments joined with '/'   → 'a/b/c'
//   fooSegments  the same remainder as an array           → ['a','b','c']
//
// The optional form matching its own parent path captures `''` and `[]`.
// Anonymous catch-alls (`*` / `**`) capture under `wildcard` /
// `wildcardSegments`. Empty segments (a trailing slash, a doubled slash) are
// dropped from both. Values are NOT URL-decoded — same contract as `:param`.
// The router mirrors every param onto the store as `router.<key>`, so a
// `/docs/:path*` route exposes `router.path` and `router.pathSegments`.
//
// ── Specificity ───────────────────────────────────────────────────────────
//
// With wildcards in play, first-match-wins over the declaration order is not
// good enough: `/docs/intro` has to beat `/docs/*` however the two are
// declared. `matchRouteTable` therefore scores every matching route and picks
// the most specific one, comparing segment scores left to right:
//
//   static (5) > named param (4) > end-of-pattern (3)
//              > catch-all (2) > optional catch-all (1) > global '*' (-1)
//
// "End of pattern" is what a shorter pattern is padded with, and it is what
// makes `/docs` beat `/docs/**` for the URL `/docs`. Ties — two patterns with
// identical shapes — fall back to declaration order, so nothing that matches
// today changes rank.

const CATCHALL_ANON = 'wildcard';
const SEGMENTS_SUFFIX = 'Segments';
const COLON = 58; // ':'.charCodeAt(0)

const SCORE_STATIC = 5;
const SCORE_PARAM = 4;
const SCORE_END = 3;
const SCORE_CATCHALL = 2;
const SCORE_CATCHALL_OPTIONAL = 1;
const SCORE_GLOBAL = -1;

/**
 * Read a pattern segment as a catch-all.
 * @param {string} segment
 * @returns {{name: string, optional: boolean}|null} null when it is not one.
 */
function parseCatchAll(segment) {
  if (segment === undefined) return null;
  if (segment === '*') return { name: CATCHALL_ANON, optional: false };
  if (segment === '**') return { name: CATCHALL_ANON, optional: true };
  if (segment.charCodeAt(0) !== COLON) return null;
  if (segment.endsWith('**')) {
    const name = segment.slice(1, -2);
    return name ? { name, optional: true } : null;
  }
  if (segment.endsWith('*')) {
    const name = segment.slice(1, -1);
    return name ? { name, optional: false } : null;
  }
  return null;
}

/**
 * Match a URL path against one route.
 *
 * @param {string} url - path only, no query string
 * @param {Object} route - { path } or { url } — the pattern
 * @returns {Object|null} extracted params, or null when the route does not match
 */
function matchUrlToRoute(url, route) {
  const routeUrl = route && (route.url || route.path);

  // A route with no pattern matches nothing. Render paths do not throw.
  if (typeof routeUrl !== 'string' || typeof url !== 'string') return null;

  // The global wildcard: the whole route is '*'. Matches anything, captures
  // nothing — the 404 route's contract since day one.
  if (routeUrl === '*') return {};

  const urlParts = url.split('/');
  const routeParts = routeUrl.split('/');
  const lastIdx = routeParts.length - 1;
  const catchAll = parseCatchAll(routeParts[lastIdx]);

  if (catchAll) {
    // Every segment BEFORE the catch-all still has to be there.
    if (urlParts.length < lastIdx) return null;
  } else if (urlParts.length !== routeParts.length) {
    return null;
  }

  const params = {};
  for (let i = 0; i < routeParts.length; i++) {
    if (catchAll && i === lastIdx) {
      // Drop empty segments so a trailing slash never becomes a captured ''.
      const rest = [];
      for (let j = i; j < urlParts.length; j++) {
        if (urlParts[j] !== '') rest.push(urlParts[j]);
      }
      if (rest.length === 0 && !catchAll.optional) return null;
      params[catchAll.name] = rest.join('/');
      params[catchAll.name + SEGMENTS_SUFFIX] = rest;
      return params;
    }
    const part = routeParts[i];
    if (part.charCodeAt(0) === COLON) {
      params[part.slice(1)] = urlParts[i];
    } else if (part !== urlParts[i]) {
      return null;
    }
  }
  return params;
}

/**
 * Score a route pattern for specificity: an array of per-segment scores,
 * compared left to right by compareRouteSpecificity.
 *
 * Deliberately not memoised — a route table is tens of entries and the SSR
 * path already rebuilds it per request, so a Map here would buy nothing and
 * cost a long-lived worker some unbounded state.
 *
 * @param {Object|string} route - a route ({ path } / { url }) or the pattern
 * @returns {number[]}
 */
function routeSpecificity(route) {
  const raw = typeof route === 'string' ? route : route && (route.url || route.path);
  const pattern = typeof raw === 'string' ? raw : '';
  if (pattern === '*') return [SCORE_GLOBAL];

  const parts = pattern.split('/');
  const lastIdx = parts.length - 1;
  const catchAll = parseCatchAll(parts[lastIdx]);
  return parts.map((part, i) => {
    if (catchAll && i === lastIdx) {
      return catchAll.optional ? SCORE_CATCHALL_OPTIONAL : SCORE_CATCHALL;
    }
    return part.charCodeAt(0) === COLON ? SCORE_PARAM : SCORE_STATIC;
  });
}

/**
 * Order two routes by specificity — a sort comparator, most specific first.
 * Accepts routes, patterns, or pre-computed score arrays.
 *
 * @returns {number} negative when `a` is more specific, 0 when equal
 */
function compareRouteSpecificity(a, b) {
  const sa = Array.isArray(a) ? a : routeSpecificity(a);
  const sb = Array.isArray(b) ? b : routeSpecificity(b);
  const len = sa.length > sb.length ? sa.length : sb.length;
  for (let i = 0; i < len; i++) {
    const va = i < sa.length ? sa[i] : SCORE_END;
    const vb = i < sb.length ? sb[i] : SCORE_END;
    if (va !== vb) return vb - va;
  }
  return 0;
}

/**
 * Match a URL against a flat route table and return the MOST SPECIFIC match,
 * not the first one declared. Routes of equal specificity keep declaration
 * order, so a table without wildcards resolves exactly as it always did.
 *
 * @param {string} url - path only, no query string
 * @param {Array} routes - flat route table (see route-table.js flattenRoutes)
 * @returns {{route: Object, params: Object, index: number}|null}
 */
function matchRouteTable(url, routes) {
  if (!routes || routes.length === 0) return null;
  let best = null;
  let bestScore = null;
  for (let i = 0; i < routes.length; i++) {
    const route = routes[i];
    const params = matchUrlToRoute(url, route);
    if (params === null) continue;
    const score = routeSpecificity(route);
    if (best === null || compareRouteSpecificity(score, bestScore) < 0) {
      best = { route, params, index: i };
      bestScore = score;
    }
  }
  return best;
}
return { matchUrlToRoute, routeSpecificity, compareRouteSpecificity, matchRouteTable };
})();

const __m2 = (() => {
// Base element class name → native HTML tag. Shared by the client component
// factory (create-component) and the SSR registry (component-registry) so the
// two resolvers never drift — a mismatch would make SSR emit a different tag
// than the client, breaking hydration of customized built-ins.
const nativeTagForClass = {
  HTMLInputElement: 'input',
  HTMLTextAreaElement: 'textarea',
  HTMLButtonElement: 'button',
  HTMLFormElement: 'form',
  HTMLSelectElement: 'select',
  HTMLLabelElement: 'label',
  HTMLAnchorElement: 'a',
  HTMLDetailsElement: 'details',
  HTMLTableElement: 'table',
  HTMLTableCellElement: 'th',
};

// Native tag → the property a data binding writes on it (its data SINK). Text
// elements take text, form controls take `value`, media takes `src`; anything
// else — a component — takes `data`. Shared for the same reason as the table
// above: the client's binding target, SSR's serialisation of that binding, and
// the redact pass that must remove and mask exactly that target all need the
// same answer.
const NATIVE_DATA_FIELDS = new Map();
[
  ['textContent', 'h1','h2','h3','h4','h5','h6','p','span','label','li','td','th','dt','dd',
   'figcaption','caption','summary','legend','a','strong','em','b','i','small',
   'code','pre','blockquote','cite','abbr','div','section','article','nav',
   'header','footer','main','aside','mark','time','s','u','sub','sup'],
  ['value', 'input','textarea','select','progress','meter'],
  ['attr:src', 'img','video','audio','source'],
].forEach(([field, ...tags]) => {
  tags.forEach((tag) => NATIVE_DATA_FIELDS.set(tag, field));
});

function dataSinkFor(tag) {
  return NATIVE_DATA_FIELDS.get(tag) || 'data';
}
return { nativeTagForClass, NATIVE_DATA_FIELDS, dataSinkFor };
})();

const __m3 = (() => {
const NATIVE_DATA_FIELDS = __m2.NATIVE_DATA_FIELDS;
// Client-side SSR hydration — walks SSR'd DOM to re-attach store subscriptions
// and event listeners that can't be serialized as HTML, and lends the DSL
// builder a cursor so it ADOPTS those nodes instead of replacing them.

// Global hydration flag. Resolved from the document the first time it is read,
// not only when CreateApp runs: the component barrel registers its defs at
// module evaluation, which is BEFORE CreateApp on every app that imports it at
// the top of main.js, and `define()` upgrades every matching element already in
// the document right there. Reading the flag from the DOM means those upgrades
// see hydration too — without it they would build their template ALONGSIDE the
// server's copy of it (which only ever looked correct because an ancestor's
// clear-and-rebuild threw the duplicate away a moment later).


let _hydrating = null;

function isHydrating() {
  if (_hydrating === null) {
    _hydrating = typeof document !== 'undefined' && typeof document.querySelector === 'function'
      && !!document.querySelector('[data-swc-ssr]');
  }
  return _hydrating;
}
function setHydrating(value) { _hydrating = !!value; }

// ── SSR DOM adoption ─────────────────────────────────────────────────────
// Hydration used to delete every child the server rendered for a component and
// rebuild the lot from the DSL, because refs, listeners and store bindings have
// to exist before connected() runs and only the DSL walk creates them. The
// nodes were never the problem — the walk was. So the walk keeps happening and
// the nodes stay: an adoption frame is a cursor over one parent's existing
// children, and every point where the builder would have called
// createElement() asks the cursor first. A claimed node has the def's tokens
// applied to it in place, so it ends up carrying exactly what a freshly built
// one would: same refs, same listeners, same subscriptions, same attributes.
//
// A frame belongs to ONE parent element and is only consulted for that parent
// (top(parent) below), so any code that appends into a different container —
// list stamping, projected regions, virtual scroll — is untouched by adoption
// and keeps its own semantics.
//
// When the server's node is not the one the def asks for, the frame is
// ABANDONED: everything it has not claimed is removed and the rest of that
// parent is built fresh. That is precisely the old behaviour, so a subtree the
// server cannot hand over degrades to what it did before rather than to
// anything new.
const frames = [];

function top(parent) {
  const f = frames[frames.length - 1];
  return f && f.parent === parent && f.active ? f : null;
}

function dropUntil(f, stop) {
  while (f.next && f.next !== stop) {
    const node = f.next;
    f.next = node.nextSibling;
    if (node.parentNode) node.parentNode.removeChild(node);
  }
}

function abandon(f) {
  f.active = false;
  dropUntil(f, f.stopAt);
  adoptStats.abandoned += 1;
}

// Counters for the census in tests/tools — how much of a page the server
// actually managed to hand over.
const adoptStats = { claimed: 0, created: 0, replaced: 0, abandoned: 0, frames: 0 };

// Open a cursor over `parent`'s children. `from` is the first node to consider
// (a second pass over the same parent resumes where the first stopped) and
// `stopAt` is an exclusive end — the boundary comment between a component's
// own template and the instance children authored around it.
function beginAdopt(parent, from, stopAt) {
  frames.push({
    parent,
    next: from === undefined ? parent.firstChild : from,
    stopAt: stopAt || null,
    active: true,
  });
  adoptStats.frames += 1;
}

// Close the cursor. `dropRest` removes whatever the DSL never claimed — the DSL
// is authoritative for a parent it owns end to end. A frame that stopped at a
// boundary leaves the tail alone: those nodes belong to the other pass.
function endAdopt(parent, dropRest) {
  const f = frames[frames.length - 1];
  if (!f || f.parent !== parent) return;
  frames.pop();
  if (f.active && dropRest) dropUntil(f, f.stopAt);
}

// Claim the node the cursor is on for `tag`, or null when there is nothing to
// claim (no frame, an abandoned frame, or the cursor sitting on a slot end).
//
// `declarative` says the DSL is the only thing that writes this element's
// children. When it is false — a link that relocates what it was given into a
// real <a>, a drawer or a calendar that builds its own scaffold, anything with
// an `ssr` expansion — the client owns its inside and the owner must not walk
// it. That element is REPLACED here, one node out and one node in, which is
// what the clear-and-rebuild did for it anyway and leaves the cursor in step so
// its siblings still adopt.
function adoptElement(parent, tag, declarative) {
  const f = top(parent);
  if (!f) return null;
  const node = f.next;
  // At a slot end (an empty server show-if branch, or the template/authored
  // boundary) there is no node to claim and nothing is wrong — the builder
  // inserts at the cursor and the frame stays good.
  if (!node || node === f.stopAt || node === f.slotEnd) return null;
  if (node.nodeType !== 1 || node.localName !== tag) { abandon(f); return null; }
  f.next = node.nextSibling;
  if (!declarative) {
    node.parentNode.removeChild(node);
    adoptStats.replaced += 1;
    return null;
  }
  adoptStats.claimed += 1;
  return node;
}

// Text children (a bare string in a children array) are claimed the same way.
function adoptText(parent, data) {
  const f = top(parent);
  if (!f) return null;
  const node = f.next;
  if (!node || node === f.stopAt || node === f.slotEnd) return null;
  if (node.nodeType !== 3) { abandon(f); return null; }
  f.next = node.nextSibling;
  if (node.data !== data) node.data = data;
  adoptStats.claimed += 1;
  return node;
}

// Put a node the builder created at the cursor rather than at the end, so a
// def the server could not hand over lands in its authored position among the
// ones it could.
function placeChild(parent, node) {
  const f = top(parent);
  if (f) parent.insertBefore(node, f.next);
  else parent.appendChild(node);
  adoptStats.created += 1;
}

// A server-rendered conditional is bracketed by <!--swc-if:key--> … the
// element, or nothing … <!--/swc-if:key-->. The CLOSE marker becomes the
// client's show-if anchor, so a later flip inserts exactly where the server put
// it, and the cursor is parked inside the slot for the element itself.
// The name after the colon is whatever the serializer wrote for the rule: the
// bare key, `!key` for a negated rule, the whole `?key=value` token for a
// value match. It is read here as an opaque string, never parsed: the close is
// the open with a slash in front, and pairing is positional, so the two
// engines agree on a slot without agreeing on a grammar.
// Returns the anchor, or null when this parent is not being adopted.
function adoptShowIfSlot(parent) {
  const f = top(parent);
  if (!f) return null;
  const open = f.next;
  if (!open || open === f.stopAt) return null;
  if (open.nodeType !== 8 || open.data.slice(0, 7) !== 'swc-if:') { abandon(f); return null; }
  const closeData = `/${open.data}`;
  let close = open.nextSibling;
  while (close && !(close.nodeType === 8 && close.data === closeData)) close = close.nextSibling;
  if (!close) { abandon(f); return null; }
  f.next = open.nextSibling;
  f.slotEnd = close;
  return close;
}

// Leave the slot: anything inside it the client did not claim is server output
// the client's own show-if evaluation says should not be there.
function closeShowIfSlot(parent, close) {
  const f = top(parent);
  if (!f) return;
  dropUntil(f, close);
  f.slotEnd = null;
  f.next = close.nextSibling;
}

// The comment the serializer writes between a component's own template and the
// instance children authored around it. Without it the two passes could not
// tell where one ends and the other begins, and they do not run in a fixed
// order: whichever of the component and its owner upgrades first goes first.
const AUTHORED_BOUNDARY = 'swc-a';

function authoredBoundary(el) {
  for (let n = el.firstChild; n; n = n.nextSibling) {
    if (n.nodeType === 8 && n.data === AUTHORED_BOUNDARY) return n;
  }
  return null;
}

// A claimed element may already carry the bindings hydrateElement reattached
// from its data-swc-h marker. Adoption is about to re-apply the very tokens
// that marker was serialized FROM, so the marker's copy is released first and
// the element ends up with exactly one subscription per binding instead of two
// (and the marker is consumed, so a later hydrateElement walk skips it).
function releaseHydrationBinding(el) {
  if (el._storeUnsub) { el._storeUnsub(); el._storeUnsub = null; }
  // The write-back a form control got from its marker goes with the
  // subscription, and the guard that stops a second one clears with it, so
  // the adoption that follows binds the control to ITS store (the owner's
  // for `$key`, the global one for `:dataSource`) with exactly one writer.
  if (el._storeValueListener) {
    el.removeEventListener('input', el._storeValueListener);
    el.removeEventListener('change', el._storeValueListener);
    el._storeValueListener = null;
    el._storeValueListenerBound = false;
  }
  if (el._templateAttrSubs) {
    el._templateAttrSubs.forEach((sub) => sub?.unsubscribe?.());
    el._templateAttrSubs = null;
  }
  if (el.hasAttribute && el.hasAttribute('data-swc-h')) el.removeAttribute('data-swc-h');
}

// Walk an element tree and re-attach store bindings from data-swc-h markers
function hydrateElement(el, storeRef) {
  if (!el || !storeRef) return;

  const marker = el.getAttribute?.('data-swc-h');
  if (marker) {
    // A marker hydrated twice (a re-hydration with a changed key) binds the
    // new key only: whatever the last walk attached is released first.
    if (el._storeUnsub || el._storeValueListener) releaseHydrationBinding(el);
    el._pendingSinkBind = null;
    const bindings = marker.split('|');
    for (const binding of bindings) {
      reattachBinding(el, binding, storeRef);
    }
    // A store binding on a native element writes the element's sink, which a
    // `:dataField` in the same marker may name AFTER the binding token: bind
    // once every token of the marker has been read (the client's F-805 rule).
    if (el._pendingSinkBind) {
      const { key, fallback } = el._pendingSinkBind;
      el._pendingSinkBind = null;
      bindNativeSink(el, storeRef, key, fallback);
    }
    el.removeAttribute('data-swc-h');
  }

  // Recurse into children (light DOM only — shadow DOM components hydrate themselves)
  const children = el.children;
  if (children) {
    for (let i = 0; i < children.length; i++) {
      hydrateElement(children[i], storeRef);
    }
  }
}

// A store binding on a plain HTML element, written to the element's sink:
// the `:dataField` the marker named, else the tag's default (native-tags.js).
// Text is text; `value` and `checked` are two-way, with the write-back
// recorded so releaseHydrationBinding can take it off again; `attr:name` is
// that attribute. The same rule bindStoreToElement applies in the runtime, on
// the element the runtime built; this is the one it did not build.
function bindNativeSink(el, storeRef, key, fallback) {
  const field = el.dataField || NATIVE_DATA_FIELDS.get(el.localName) || 'textContent';
  const apply = (value) => {
    const resolved = (value == null || value === '') && fallback != null ? fallback : value;
    if (resolved && typeof resolved === 'object') return;
    if (field.startsWith('attr:')) {
      const name = field.slice(5);
      if (resolved == null || resolved === false || resolved === '') el.removeAttribute(name);
      else el.setAttribute(name, resolved === true ? '' : String(resolved));
    } else if (field === 'textContent') {
      el.textContent = resolved == null ? '' : String(resolved);
    } else if (field === 'checked') {
      el.checked = Boolean(resolved);
    } else {
      el[field] = resolved == null ? '' : resolved;
    }
  };
  const sub = storeRef.subscribe(key, apply);
  el._storeUnsub = sub?.unsubscribe || null;
  if ((field === 'value' || field === 'checked') && !el._storeValueListenerBound) {
    const handler = () => { if (typeof storeRef.setValue === 'function') storeRef.setValue(key, el[field]); };
    el.addEventListener('input', handler);
    el.addEventListener('change', handler);
    el._storeValueListener = handler;
    el._storeValueListenerBound = true;
  }
}

function reattachBinding(el, binding, storeRef) {
  if (!binding) return;
  const prefix = binding[0];

  switch (prefix) {
    case '$': {
      const raw = binding.slice(1);
      const eqIdx = raw.indexOf('=');

      if (eqIdx > 0) {
        // Template attribute: $attr=template{key}
        const attrName = raw.slice(0, eqIdx);
        const template = raw.slice(eqIdx + 1);
        const keyPattern = /\{([^}]+)\}/g;
        const keys = [];
        let match;
        while ((match = keyPattern.exec(template)) !== null) {
          keys.push(match[1]);
        }

        if (keys.length > 0) {
          const evaluate = () => {
            let result = template;
            for (const key of keys) {
              const val = storeRef.getValue(key);
              result = result.replace(`{${key}}`, val ?? '');
            }
            return result;
          };

          if (!el._templateAttrSubs) el._templateAttrSubs = [];
          for (const key of keys) {
            const sub = storeRef.subscribe(key, () => {
              el.setAttribute(attrName, evaluate());
            });
            el._templateAttrSubs.push(sub);
          }
        } else {
          // Single store key as attribute
          if (!el._templateAttrSubs) el._templateAttrSubs = [];
          const sub = storeRef.subscribe(template, (value) => {
            if (value == null || value === false || value === '') el.removeAttribute(attrName);
            else el.setAttribute(attrName, value === true ? '' : value);
          });
          el._templateAttrSubs.push(sub);
        }
        break;
      }

      // Text binding: $key|fallback
      const pipeIdx = raw.indexOf('|');
      const key = pipeIdx >= 0 ? raw.slice(0, pipeIdx) : raw;
      const fallback = pipeIdx >= 0 ? raw.slice(pipeIdx + 1) : undefined;

      el._storeKey = key;
      el._storeFallback = fallback;
      el._boundStoreRef = storeRef;

      // Custom elements handle their own store binding via connectedCallback →
      // bindStoreToElement, which knows about itemRenderer, .data, .value, etc.
      // Just set the key/ref above so connectedCallback can pick it up.
      if (el.tagName.includes('-')) break;

      // Plain HTML elements: bound to the element's sink once the whole
      // marker has been read — text for a text element, value for a form
      // control (two-way), src for media (R-06).
      el._pendingSinkBind = { key, fallback };
      break;
    }

    case '@': {
      // @attr=$storeKey
      const eqIdx = binding.indexOf('=$');
      if (eqIdx === -1) break;
      const attrName = binding.slice(1, eqIdx);
      const storeKey = binding.slice(eqIdx + 2);

      // Recorded like the $attr= subscriptions above so releaseHydrationBinding
      // can hand the element over to an adoption pass without leaving a second
      // writer on the same attribute.
      if (!el._templateAttrSubs) el._templateAttrSubs = [];
      el._templateAttrSubs.push(storeRef.subscribe(storeKey, (v) => {
        if (v == null || v === false || v === '') el.removeAttribute(attrName);
        else el.setAttribute(attrName, v === true ? '' : v);
      }));
      break;
    }

    case '^':
    case '%': {
      // Data bindings — will be re-applied when component sets data
      // No store subscription needed, just mark for data binding system
      break;
    }

    case ':': {
      // Property/list bindings on an SSR'd element. The DSL processing that would
      // normally set these is skipped during hydration, so record the marker
      // values as the same underscore-props the constructor/connectedCallback
      // read (set before upgrade, so they survive it). This is what re-binds a
      // :dataSource list/grid so it stays reactive instead of frozen at its SSR
      // markup (and, for the grid, avoids a blank re-render on hydration).
      const eqIdx = binding.indexOf('=');
      if (eqIdx === -1) break;
      const name = binding.slice(1, eqIdx);
      const raw = binding.slice(eqIdx + 1);
      if (name === 'dataSource') {
        el._dataSourceKey = raw;
        // A native element has no connectedCallback to bind it: it is bound
        // here, to the global store, the way :dataSource always binds (R-06).
        if (!el.tagName.includes('-')) el._pendingSinkBind = { key: raw, fallback: undefined };
      } else if (name === 'dataField') el.dataField = raw;
      else if (name === 'dataKey') el._dataKey = raw;
      else if (name === 'itemRenderer') el._ssrItemRenderer = raw;
      else if (name === 'itemRendererField') el._ssrItemRendererField = raw;
      else if (raw[0] === '$') {
        // :prop=$storeKey — a reactive property binding (e.g. the grid's :cols).
        if (!el._ssrPropBindings) el._ssrPropBindings = {};
        el._ssrPropBindings[name] = raw.slice(1);
      }
      break;
    }

    default:
      break;
  }
}
return { isHydrating, setHydrating, adoptStats, beginAdopt, endAdopt, adoptElement, adoptText, placeChild, adoptShowIfSlot, closeShowIfSlot, AUTHORED_BOUNDARY, authoredBoundary, releaseHydrationBinding, hydrateElement };
})();

const __m4 = (() => {
// Unregistered tags — recover, correct, or fall back to a real SWC element.
//
// The case this exists for: something creates an element for a tag nothing
// imported. Canonically a list handed `itemRenderer: 'my-new-renderer'` — a
// plain STRING arriving with the data, so nothing imported it and nothing
// could — but the same is true of a route's `component`, a grid column's
// `renderer`, a tab's `panelRenderer`, and any tag that reaches the DOM from
// data rather than from code. `document.createElement` still succeeds and
// returns an inert unknown element: it renders children as bare markup,
// ignores `.data`, and knows nothing about the framework. The row silently
// does not appear.
//
// A tag can be missing for three different reasons, and they want three
// different answers:
//
//   1. A near-miss NAME (`s-buton`, `swc-card`). Corrected before the element
//      is created, where we get the chance. Always reported: the render must
//      not degrade while a human works out the typo.
//   2. It EXISTS somewhere and is not in this bundle — tree-shaken out, or
//      published by a registry this app was told about. Fetching and defining
//      it is a full recovery, and elements already in the DOM upgrade in place.
//   3. It exists nowhere. Then the floor is a real SWC element — one built by
//      CreateComponent, so it has the data plumbing, the item protocol and the
//      binding machinery — decorated enough to look deliberate, not broken.
//
// Correction is deliberately NOT applied after the fact. Once the element
// exists, the only way to change what its tag means is to define that tag, and
// defining a misspelling from the real component's def re-applies that def's
// CSS under a name none of its selectors mention — duplicated bytes that style
// nothing, and a component stripped of the fallback decoration that was the
// one thing holding it together. A post-hoc near-miss gets the floor and a
// `suggestion` in the report instead.
//
// ── Registries ────────────────────────────────────────────────────────────
//
// `CreateApp({ registries: [...] })` names the places to ask beyond this
// app's own server. An entry is:
//
//   { name, url, prefix, via }
//
//   prefix   this registry OWNS that namespace: `prefix: 'acme-'` sends
//            <acme-chart> there first. A registry with NO prefix is asked
//            about every missing tag, alongside our own server and at the
//            same time as it.
//   url      where its endpoint lives. Omit it and the tag is resolved by
//            NAME through our own server instead (see `via`).
//   via      'direct' — the browser asks the registry itself. Public
//            registries only, and they must send CORS headers.
//            'server' — the browser asks OUR server, which asks the registry.
//            This is the mode for a paid or private registry: the credential
//            lives in the server's config and never reaches a bundle.
//
// Put a registry on ONE side. A `via:'direct'` registry belongs in this config;
// a credentialed one belongs in swc.config.js and nowhere else. Listing the
// same registry in both means it is asked twice per missing tag — once by the
// browser and once by our server on the browser's behalf.
//
// The server half of the protocol, and the reasoning behind the credential
// boundary, are in server/components-endpoint.js and docs/component-registry-spec.md.
//
// ── A note for the edge runtime ───────────────────────────────────────────
//
// This module is reachable from SSR (core/services/router.js imports it), so
// it stays free of node builtins. Everything it does is guarded on `document`
// and is inert on the server. Its module-level state is per-isolate and shared
// across requests, so nothing server-side should ever call
// configureUnregistered — registry config would become cross-tenant.
//
// Nothing here blocks. The element is created immediately whatever happens;
// a definition arriving later upgrades it, which is what custom elements do.

/** Every tag CreateComponent has defined, including aliases. */
const knownTags = new Set();

/** tag → why it was not found, for the console and for tests. */
const report = [];

/** Tags waiting on the next batched request. */
let pending = new Set();

/** Tags this module has taken responsibility for, and how far it has got. */
const unresolved = new Set();
const handled = new Set();
const fallbackTags = new Set();

// tag → the tag used instead, or null for "no confident match". Deliberately
// NOT merged into `handled`: correcting a NAME at a creation site says nothing
// about an ELEMENT that already carries the misspelling. Treating the two as
// one decision left a real <swc-crd> in the document inert and undecorated,
// because a list elsewhere had already had its renderer name corrected.
const corrections = new Map();
let flushScheduled = false;

/** Injected by create-component to avoid a circular import. */
let define = null;

/** The app's own server. Ranked among the registries, never one of them. */
const LOCAL = { name: '', local: true, prefix: null, via: 'local' };

let registries = [];

// The endpoint caps one question at this many tags and drops the rest, so the
// client splits rather than let the tail go silently unanswered. Kept in step
// with MAX_TAGS in server/components-endpoint.js by tests/component-registry.spec.mjs
// — core must not import from server, and a silently-diverging pair of limits
// would lose tags with nothing to show for it.
const MAX_TAGS_PER_REQUEST = 100;

let config = {
  // Where to ask for definitions of tags this bundle does not carry. Matches
  // the framework's reserved path (server/reserved.js COMPONENTS_PATH). Set to
  // null to disable the network entirely and go straight to the fallback.
  endpoint: '/swc/components',
  enabled: true,
  // How far the autocorrect may reach. 0 disables fuzzy matching and leaves
  // only exact alias/prefix normalisation.
  maxEditDistance: 2,
  // How long a queued tag is given to define itself before we go looking for
  // it. A component whose module is mid-evaluation is not missing, it is late,
  // and asking a registry about it would be a wasted round trip and a false
  // report. customElements.whenDefined answers early when it can, so this is a
  // ceiling and not a delay.
  graceMs: 50,
  // The page is not finished loading until it says so, and a module that has
  // not run cannot have registered anything. Recovery waits for that before
  // starting its own clock — capped, because a page can stall on an asset that
  // has nothing to do with components.
  maxWaitMs: 3000,
  // Watch the document for tags that never got created through a path this
  // module hooks (authored HTML, a third-party script, a DSL tag that came
  // from data). CreateApp turns it on; importing this module does not.
  observe: true,
};

function configureUnregistered(opts = {}) {
  const { registries: list, ...rest } = opts;
  config = { ...config, ...rest };
  if (list !== undefined) registries = (list || []).map(normaliseRegistry).filter(Boolean);
}

function setDefiner(fn) { define = fn; }

function registerKnownTag(tag) { knownTags.add(tag); }

/** What could not be resolved, and what was done about it. Read by e2e tests. */
function getUnregisteredReport() { return report.slice(); }

/** The normalised registry list, for diagnostics. Never contains a credential. */
function getRegistries() { return registries.map((r) => ({ ...r })); }

/**
 * True if THIS module registered that tag as the floor.
 *
 * create-component asks before warning about a duplicate def: a real component
 * arriving after its tag was floored is a very different problem from two defs
 * fighting over a name, and it needs a different thing said about it.
 */
function isFallbackTag(tag) { return fallbackTags.has(tag); }

/**
 * Forget what has already been tried, so missing tags can be looked for again.
 *
 * A tag is examined once and the outcome sticks — otherwise a registry that is
 * down at boot would be re-asked on every render. That is the right default and
 * the wrong permanent state: after a registry comes back, or a bundle is
 * updated, this reopens the question. Tags already DEFINED are untouched;
 * nothing can undo a definition.
 */
function resetUnregistered() {
  handled.clear();
  unresolved.clear();
  corrections.clear();
  pending = new Set();
}

// Hyphenated names the platform already owns. They look exactly like custom
// elements and customElements.define REFUSES them — so treating one as a
// missing component would end in a thrown SyntaxError on the recovery path,
// which is the one thing this module must never do.
const RESERVED_TAGS = new Set([
  'annotation-xml', 'color-profile', 'font-face', 'font-face-src', 'font-face-uri',
  'font-face-format', 'font-face-name', 'missing-glyph',
]);

/**
 * A custom element name — one hyphen, starts with a letter, not one of the
 * platform's own. Anything else is a native tag and none of this applies to it.
 */
function isCustomTagName(tag) {
  return typeof tag === 'string'
    && /^[a-z][a-z0-9]*-[a-z0-9-]*$/.test(tag)
    && !RESERVED_TAGS.has(tag);
}

const isDefined = (tag) => knownTags.has(tag)
  || (typeof customElements !== 'undefined' && !!customElements.get(tag));

// ── registries ────────────────────────────────────────────────────────────

// Names that can only be a credential. Deliberately NOT the near-misses — a
// registry entry may legitimately carry a `key` (a React-ism, an id, a sort
// key), and rerouting a correctly-configured public registry because of one
// would break a working app to enforce a rule it was already keeping.
const SECRET_KEYS = ['token', 'apiKey', 'apikey', 'secret', 'password', 'authorization', 'auth', 'headers'];

function normaliseRegistry(entry, i) {
  if (!entry) return null;
  const raw = typeof entry === 'string' ? { url: entry } : entry;
  const name = raw.name || (raw.url ? String(raw.url).replace(/^https?:\/\//, '').split('/')[0] : `registry-${i}`);
  let via = raw.via || (raw.url ? 'direct' : 'server');

  const leaked = SECRET_KEYS.filter((k) => raw[k] != null);
  if (leaked.length) {
    // Note what this can and cannot do. The value is in the bundle already —
    // it shipped, and every visitor has it. Nothing here un-publishes it. What
    // this does is stop the app from USING it in the browser, and say plainly
    // that the secret is now a former secret.
    console.error(`[swc] registry "${name}" carries ${leaked.join(', ')} in the app config. `
      + 'That value is in the browser bundle and has already been served to every visitor — '
      + 'treat it as compromised and ROTATE it. The registry has been switched to '
      + 'via:"server"; configure the credential server-side instead, in swc.config.js '
      + 'components.registries[].auth.env (an environment variable name).');
    via = 'server';
  }

  // Only the fields below ever leave this function, so nothing unrecognised
  // can be carried into a request even by accident.
  return {
    name,
    url: via === 'server' ? null : (raw.url || null),
    prefix: raw.prefix || null,
    via,
    local: false,
  };
}

/**
 * Where to ask about `tag`, in ranks. Everything within one rank is asked at
 * the same time; a later rank only runs if the earlier one came up empty.
 *
 * Rank 0 is the registry that claims the namespace — it is the authority on
 * its own prefix, and asking anyone else first would be guessing. Rank 1 is
 * our own server together with every registry that claimed no namespace, which
 * is what "checked there as well, in addition to our running server" means:
 * at the same time, not after. Priority inside the rank is declaration order,
 * with our own server first.
 *
 * A prefixed registry is never asked about a tag outside its prefix.
 */
function ranksFor(tag) {
  const reachable = (r) => r.local || r.via === 'direct' || !!config.endpoint;
  const owner = registries.filter((r) => r.prefix && tag.startsWith(r.prefix) && reachable(r));
  const open = [
    ...(config.endpoint ? [LOCAL] : []),
    ...registries.filter((r) => !r.prefix && reachable(r)),
  ];
  return [owner, open].filter((rank) => rank.length);
}

const targetKey = (t) => (t.local ? 'local' : `${t.via}:${t.name}`);

const baseFor = (t) => (t.local || t.via === 'server' ? config.endpoint : t.url);

/** One batched question to one target. Returns { tag: absolute module URL }. */
async function askTarget(target, tags) {
  if (tags.length > MAX_TAGS_PER_REQUEST) {
    const chunks = [];
    for (let i = 0; i < tags.length; i += MAX_TAGS_PER_REQUEST) {
      chunks.push(tags.slice(i, i + MAX_TAGS_PER_REQUEST));
    }
    const parts = await Promise.all(chunks.map((c) => askTarget(target, c)));
    return Object.assign({}, ...parts);
  }
  const base = baseFor(target);
  if (!base) return {};
  const origin = typeof location !== 'undefined' ? location.href : 'http://localhost/';
  let url;
  try {
    url = new URL(base, origin);
  } catch { return {}; }
  url.searchParams.set('tags', tags.join(','));
  // The browser names the registry; it never names a URL. The server resolves
  // the name against its OWN config, which is what keeps this from being an
  // open proxy (server/components-endpoint.js).
  if (!target.local && target.via === 'server') url.searchParams.set('registry', target.name);

  let body;
  try {
    const res = await fetch(url.toString(), { headers: { accept: 'application/json' } });
    if (!res.ok) return {};
    body = await res.json();
  } catch {
    // Offline, no such endpoint, a server that does not implement it, a CORS
    // refusal — all mean the same thing here, and all fall through to the
    // next rank and finally to the floor.
    return {};
  }

  const out = {};
  for (const tag of tags) {
    const src = body && body[tag];
    if (!src) continue;
    // A registry may answer with a path relative to its own endpoint.
    try { out[tag] = new URL(src, url).toString(); } catch { /* unusable */ }
  }
  return out;
}

// ── autocorrect ───────────────────────────────────────────────────────────
// Only ever maps to a tag that is ALREADY defined in this page. Suggesting a
// name that is itself missing would just move the problem.

const normalise = (t) => String(t).toLowerCase().replace(/[^a-z0-9]/g, '');

/** Levenshtein, bailing out as soon as it exceeds the budget. */
function editDistance(a, b, budget) {
  if (Math.abs(a.length - b.length) > budget) return budget + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      row[j] = a[i - 1] === b[j - 1]
        ? prev[j - 1]
        : 1 + Math.min(prev[j - 1], prev[j], row[j - 1]);
      if (row[j] < best) best = row[j];
    }
    if (best > budget) return budget + 1;
    prev = row;
  }
  return prev[b.length];
}

/**
 * The closest DEFINED tag, or null.
 *
 * Exact-ish rules first (a case difference or an `swc-`/`s-` prefix swap is a
 * certainty, not a guess); fuzzy distance last and only within a tight budget,
 * because a wrong correction renders a different component and looks like a
 * styling bug rather than a missing one. That is also why every correction is
 * warned about, and why `maxEditDistance: 0` turns the fuzzy half off.
 *
 * A tag inside a registry's namespace is never autocorrected: `acme-chart` is
 * that registry's to define, and guessing at a local lookalike would hide the
 * fact that the registry never answered.
 */
function correctTag(tag) {
  if (!isCustomTagName(tag)) return null;
  if (registries.some((r) => r.prefix && tag.startsWith(r.prefix))) return null;
  const want = normalise(tag);

  // The two prefixes the framework uses for the same component.
  const swapped = tag.startsWith('swc-') ? `s-${tag.slice(4)}`
    : tag.startsWith('s-') ? `swc-${tag.slice(2)}` : null;
  if (swapped && isDefined(swapped)) return swapped;

  for (const known of knownTags) {
    if (normalise(known) === want) return known;
  }

  const budget = Math.max(0, config.maxEditDistance | 0);
  if (!budget) return null;
  let best = null;
  let bestScore = budget + 1;
  for (const known of knownTags) {
    // Only compare names of comparable length: `s-b` is within two edits of
    // half the catalogue, and matching it would be noise, not a correction.
    const k = normalise(known);
    if (Math.abs(k.length - want.length) > budget || k.length < 3) continue;
    const d = editDistance(k, want, budget);
    if (d < bestScore) { bestScore = d; best = known; }
  }
  return bestScore <= budget ? best : null;
}

// ── recovery ──────────────────────────────────────────────────────────────

function note(entry) {
  report.push(entry);
  if (typeof window !== 'undefined') {
    (window.__swcUnregistered ||= []).push(entry);
  }
}

/**
 * Register a bare SWC element for a tag nothing can define.
 *
 * This is the floor: not an inert unknown tag, but a real component —
 * CreateComponent gives it `.data`, the item protocol, bindings and lifecycle,
 * so a list row built from it behaves like every other row. The
 * `data-swc-unregistered` attribute is what the one shared stylesheet rule
 * hangs off, so a hundred unknown tags still cost exactly one rule.
 *
 * The attribute is stamped by the component itself rather than by whoever
 * happened to create the first element. Instances keep arriving long after the
 * tag was floored — a list re-renders, a route comes back — and marking only
 * the ones present at recovery time left two elements of the SAME tag laid out
 * differently on the same page.
 */
function defineFallback(tag) {
  if (isDefined(tag) || !define) return;
  fallbackTags.add(tag);
  try {
  // Nothing but the tag and the marker. `attrs` is a list of observed attribute
  // NAMES, not a map of values — passing an object made customElements.define
  // reject the class outright, which left the very elements this is meant to
  // rescue undefined.
    define({
      tags: [tag],
      callbacks: {
        connected() { this.setAttribute('data-swc-unregistered', ''); },
      },
    });
  } catch (err) {
    // The floor is the last thing standing between a missing component and a
    // broken page. If even this cannot be registered, the element stays an
    // inert unknown tag — which is what it already was — and the page carries
    // on. Recovering from a missing component must never itself be the thing
    // that breaks the render.
    fallbackTags.delete(tag);
    note({ tag, action: 'fallback-failed', error: String((err && err.message) || err) });
  }
}

/** One module script, resolved when it has run (or failed). */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') { reject(new Error('no document')); return; }
    const el = document.createElement('script');
    el.type = 'module';
    el.src = src;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(el);
  });
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

/** Resolves when the page has finished loading, or immediately if it has. */
function documentSettled() {
  if (typeof document === 'undefined' || document.readyState === 'complete') return Promise.resolve();
  if (typeof window === 'undefined') return Promise.resolve();
  return new Promise((r) => window.addEventListener('load', r, { once: true }));
}

/**
 * Give queued tags a moment to define themselves.
 *
 * A component whose module is still evaluating is late, not missing. Two
 * things are waited on, and the difference matters: the PAGE finishing (a
 * module that has not run cannot have registered anything, and during boot
 * that is the usual reason a tag looks unknown), and then a short window on
 * top for a registration already in flight. Either is cut short the moment
 * every queued tag is defined.
 *
 * This narrows the window but cannot close it. A tag floored here and defined
 * for real afterwards — a route chunk fetched much later — cannot be undone,
 * because customElements.define is permanent. create-component says so loudly
 * when it happens (isFallbackTag), and resetUnregistered re-opens the question
 * for tags that have not been floored yet.
 */
function grace(tags) {
  if (typeof customElements === 'undefined' || !customElements.whenDefined) return Promise.resolve();
  const allDefined = Promise.all(tags.map((t) => customElements.whenDefined(t).catch(() => {})));
  const settledThenGrace = (async () => {
    await Promise.race([documentSettled(), delay(Math.max(0, config.maxWaitMs | 0))]);
    await delay(Math.max(0, config.graceMs | 0));
  })();
  return Promise.race([allDefined, settledThenGrace]);
}

/**
 * The text stamped into an element while its tag was unknown.
 *
 * A list gives a leaf renderer the item's text directly, because an element
 * with no children of its own has nowhere else to put it. That is right while
 * the tag is unknown and wrong the moment it is not: the real component renders
 * the same field itself, and the stray node sits above its output as a
 * duplicate. Snapshotted BEFORE the definition lands so exactly those nodes can
 * be dropped afterwards — anything the component itself renders is untouched,
 * because it did not exist when this ran.
 */
function placeholderTextNodes(tag) {
  const out = [];
  if (typeof document === 'undefined' || !document.querySelectorAll) return out;
  let found;
  try { found = document.querySelectorAll(`${tag}[data-swc-unregistered]`); } catch { return out; }
  for (const el of found) {
    for (const node of el.childNodes) {
      if (node.nodeType === 3 && node.textContent.trim()) out.push(node);
    }
  }
  return out;
}

function dropPlaceholders(nodes) {
  for (const node of nodes) {
    // Only once the component has rendered something of its own. A recovered
    // component that renders no elements — a leaf, like the one it replaced —
    // still needs that text, and removing it would blank the row.
    if (node.parentNode && node.parentNode.firstElementChild) node.remove();
  }
}

/** Drop the fallback decoration from elements whose tag turned out to be real. */
function clearMark(tag) {
  unresolved.delete(tag);
  if (typeof document === 'undefined' || !document.querySelectorAll) return;
  for (const el of document.querySelectorAll(`${tag}[data-swc-unregistered]`)) {
    el.removeAttribute('data-swc-unregistered');
  }
}

/** Ask one rank — every target in it at once — and load whatever came back. */
async function askRank(rank, tags) {
  const groups = new Map();
  for (const target of rank) groups.set(targetKey(target), { target, tags });

  const answers = await Promise.all(
    [...groups.values()].map((g) => askTarget(g.target, g.tags).then((found) => ({ g, found })))
  );

  for (const tag of tags) {
    if (isDefined(tag)) continue;
    // Declaration order decides, not who answered first — otherwise a fast
    // registry would outrank the one the app listed ahead of it.
    const hit = answers.find(({ found }) => found[tag]);
    if (!hit) continue;
    const src = hit.found[tag];
    const registry = hit.g.target.name || 'local';
    try {
      // Loaded as a SCRIPT, not by import().
      //
      // The bundler refuses a dynamic import whose specifier is not a literal
      // — it has to know every module at build time — and it is right to. This
      // module is the one case that rule cannot cover: the component is being
      // fetched precisely because the build did not include it, so its URL
      // cannot exist at build time. A script element is the honest way to say
      // "not a build dependency".
      //
      // The endpoint serves a self-registering wrapper, so nothing has to be
      // handed back across the boundary. Elements already in the DOM upgrade
      // in place when it defines the tag.
      const stale = placeholderTextNodes(tag);
      await loadScript(src);
      if (isDefined(tag)) {
        dropPlaceholders(stale);
        clearMark(tag);
        note({ tag, action: 'loaded', from: src, registry });
      } else {
        note({ tag, action: 'load-incomplete', from: src, registry });
      }
    } catch (err) {
      note({ tag, action: 'load-failed', from: src, registry, error: String((err && err.message) || err) });
    }
  }
}

async function flush() {
  flushScheduled = false;
  const queued = [...pending];
  pending = new Set();
  if (!queued.length) return;

  await grace(queued);
  for (const tag of queued) {
    if (isDefined(tag) && unresolved.has(tag)) { clearMark(tag); note({ tag, action: 'late' }); }
  }
  let remaining = queued.filter((t) => !isDefined(t));

  if (config.enabled) {
    // Tags are GROUPED BY TARGET, so a list of 50 rows with an unknown renderer
    // costs one request per registry consulted — never one per row. At most two
    // ranks: the namespace owner, then everyone else at once.
    const depth = remaining.reduce((n, t) => Math.max(n, ranksFor(t).length), 0);
    for (let rank = 0; rank < depth && remaining.length; rank++) {
      const byRank = new Map();
      for (const tag of remaining) {
        const targets = ranksFor(tag)[rank];
        if (!targets || !targets.length) continue;
        const key = targets.map(targetKey).join('|');
        if (!byRank.has(key)) byRank.set(key, { targets, tags: [] });
        byRank.get(key).tags.push(tag);
      }
      if (!byRank.size) break;
      await Promise.all([...byRank.values()].map((r) => askRank(r.targets, r.tags)));
      remaining = remaining.filter((t) => !isDefined(t));
    }
  }

  for (const tag of remaining) {
    // Reported, never applied. Correcting a tag whose element already exists
    // means defining the misspelling, and a def re-run under the wrong name
    // duplicates its CSS under selectors that do not mention it.
    const suggestion = correctTag(tag);
    defineFallback(tag);
    // `applied: false` on the suggestion, so a tag that appears twice in the
    // report — corrected as a NAME, floored as an ELEMENT — says which is
    // which without anyone having to work it out.
    note(suggestion
      ? { tag, action: 'fallback', suggestion, applied: false }
      : { tag, action: 'fallback' });
    console.warn(`[swc] no definition for <${tag}> — rendered as a base SWC element.`
      + (suggestion ? ` Did you mean <${suggestion}>?` : '')
      + ' Import it, add the registry that publishes it, or serve it from the component endpoint.');
  }
}

function queue(tag) {
  if (handled.has(tag) || pending.has(tag)) return;
  handled.add(tag);
  pending.add(tag);
  unresolved.add(tag);
  if (!flushScheduled) {
    flushScheduled = true;
    // Microtask, so every unknown tag in one render pass groups into one round.
    //
    // The catch is not decoration. Recovery runs detached from whatever
    // triggered it, so anything thrown in here surfaces as an unhandled
    // rejection in the app's error reporting — a missing component would then
    // look like a crash, from a mechanism whose entire job is to make sure it
    // does not.
    Promise.resolve().then(flush).catch((err) => {
      flushScheduled = false;
      note({ action: 'recovery-failed', error: String((err && err.message) || err) });
      console.warn('[swc] unregistered-tag recovery failed; affected elements keep '
        + 'whatever they had.', err);
    });
  }
}

/**
 * Called when something is about to create an element for `tag`.
 *
 * Returns the tag to actually use — the correction when there is a confident
 * one, otherwise the original. Never throws, never blocks: recovery happens
 * after the render pass and upgrades whatever was created in the meantime.
 */
function resolveTag(tag) {
  // Browser only. On the server there is no customElements registry, so EVERY
  // custom tag reads as undefined — the whole corpus would queue itself and
  // flush() would try to fetch a relative URL from node, which hangs the
  // render. The client repeats this walk on hydration, where the question can
  // actually be answered.
  if (typeof document === 'undefined' || typeof customElements === 'undefined') return tag;
  if (!isCustomTagName(tag)) return tag;
  // A REAL definition ends the question. A floored one does not: the floor is
  // a placeholder registered because an element of that name needed to be
  // something, and it must not start outranking a correction we already know
  // is better. Without this, the same renderer name rendered <swc-card> before
  // recovery and an empty floor element after it — a list that re-rendered
  // changed what it displayed, for no reason the app could see.
  if (isDefined(tag) && !fallbackTags.has(tag)) return tag;

  // Decided once per tag. correctTag walks the whole vocabulary, and this runs
  // per ELEMENT — a thousand-row list with one bad renderer name would
  // otherwise pay for a thousand identical searches.
  if (!corrections.has(tag)) {
    const found = correctTag(tag);
    corrections.set(tag, found);
    if (found) {
      // `applied: true` — this one was intercepted in time to change. The
      // fallback entry for the same tag carries `applied: false`, because an
      // element already bearing the name could only be floored.
      note({ tag, action: 'corrected', to: found, applied: true });
      console.warn(`[swc] unknown element <${tag}> — using <${found}>. `
        + 'Fix the name; this correction is a convenience, not a contract.');
    }
  }

  const corrected = corrections.get(tag);
  if (corrected) return corrected;
  if (isDefined(tag)) return tag;

  queue(tag);
  return tag;
}

/**
 * Mark an element whose tag this module has taken responsibility for.
 *
 * Covers the window before the tag is defined; once it IS floored, the
 * component's own connected callback marks every instance (defineFallback).
 * Deliberately NOT "any tag not yet defined" — during boot a component is
 * often created a tick before its own module registers it, and marking those
 * put the fallback styling on real components and dragged them through the
 * pre-upgrade property dance, which broke hydration in six projection and
 * adoption tests.
 */
function markUnregistered(el, tag) {
  if (el && el.setAttribute && unresolved.has(tag)) el.setAttribute('data-swc-unregistered', '');
}

// ── the sweep ─────────────────────────────────────────────────────────────
//
// Everything above intercepts a tag BEFORE the element exists, which is only
// possible at the creation sites the framework owns. This catches the rest:
// authored HTML, a DSL tag that came from data, an element inserted by a
// script the framework never saw. A definition arriving late upgrades an
// element that is already in the DOM, so finding one after the fact loses
// nothing — which is why the same recovery works from either end.
//
// It sees the document tree only. `:not(:defined)` does not cross a shadow
// boundary and a MutationObserver on documentElement does not see inside one;
// pass a shadow root to recoverUnregistered to cover it.

/** Queue every undefined custom element under `root`. */
function recoverUnregistered(root) {
  if (typeof document === 'undefined') return;
  const scope = root || document;
  if (!scope.querySelectorAll) return;
  let found;
  try {
    found = scope.querySelectorAll(':not(:defined)');
  } catch {
    return; // no :defined support
  }
  for (const el of found) {
    const tag = el.tagName && el.tagName.toLowerCase();
    if (!isCustomTagName(tag) || handled.has(tag) || isDefined(tag)) continue;
    queue(tag);
    markUnregistered(el, tag);
  }
}

let observer = null;
let sweepScheduled = false;
let sweepRoots = [];
// Past this many changed subtrees in one turn, scanning them individually
// stops being cheaper than scanning the document once.
const ROOTS_BEFORE_FULL_SWEEP = 64;

/**
 * One sweep per turn, over what actually changed.
 *
 * A subtree can arrive in a single mutation — a row built while detached and
 * then appended shows up as ONE added node carrying all its children — so the
 * added nodes alone are not enough and something has to look inside. Scanning
 * per node would make a thousand-row render a thousand subtree queries;
 * scanning the whole document instead would tax every insertion in the app for
 * its lifetime, whether or not anything was ever unregistered. Scanning the
 * changed roots, once, is proportional to the change.
 */
function scheduleSweep() {
  if (sweepScheduled) return;
  sweepScheduled = true;
  setTimeout(() => {
    sweepScheduled = false;
    const roots = sweepRoots;
    sweepRoots = [];
    // Detached from any call stack the app owns, so an exception here would be
    // an uncaught error the app cannot see a cause for.
    try {
      if (roots.length >= ROOTS_BEFORE_FULL_SWEEP) { recoverUnregistered(document); return; }
      for (const root of roots) if (root.isConnected) recoverUnregistered(root);
    } catch { /* a sweep that fails costs a recovery, not the page */ }
  }, 0);
}

/**
 * Watch for undefined custom elements entering the document.
 *
 * The per-mutation cost is a Set lookup per added element, and a tag is only
 * ever examined once (`handled`), so a list that appends a thousand rows of a
 * known renderer costs a thousand Set hits and one bounded sweep.
 */
function observeUnregistered(root) {
  if (!config.observe) return;
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;
  if (observer) return;
  const scope = root || document.documentElement;
  if (!scope) return;
  observer = new MutationObserver((records) => {
    try {
      scan(records);
    } catch { /* watching for missing components must not break the DOM writes */ }
  });
  observer.observe(scope, { childList: true, subtree: true });

  function scan(records) {
    for (const rec of records) {
      for (const node of rec.addedNodes) {
        if (node.nodeType !== 1) continue;
        const tag = node.tagName && node.tagName.toLowerCase();
        if (isCustomTagName(tag) && !handled.has(tag) && !isDefined(tag)) {
          queue(tag);
          markUnregistered(node, tag);
        }
        if (node.firstElementChild && sweepRoots.length < ROOTS_BEFORE_FULL_SWEEP) {
          sweepRoots.push(node);
        }
        if (node.firstElementChild) scheduleSweep();
      }
    }
  }
}

function stopObservingUnregistered() {
  if (observer) { observer.disconnect(); observer = null; }
  sweepScheduled = false;
  sweepRoots = [];
}
return { MAX_TAGS_PER_REQUEST, configureUnregistered, setDefiner, registerKnownTag, getUnregisteredReport, getRegistries, isFallbackTag, resetUnregistered, isCustomTagName, correctTag, resolveTag, markUnregistered, recoverUnregistered, observeUnregistered, stopObservingUnregistered };
})();

const __m5 = (() => {
const isHydrating = __m3.isHydrating;
const setHydrating = __m3.setHydrating;
const registerKnownTag = __m4.registerKnownTag;
// Chunked component registration — the fix for one very long init task.
//
// `customElements.define()` upgrades every matching element already in the
// document, synchronously. On a server-rendered page that means the whole
// bundle's registration AND the whole document's upgrade run inside the single
// task that evaluates the bundle. Total Blocking Time counts only the excess
// over 50ms PER TASK, so one 900ms task costs 850ms while the identical work
// split into twenty 45ms tasks costs nothing. This module does the splitting.
//
// What it does NOT change is the order defs are registered in. Order is a
// contract here: customized built-ins come before autonomous ones, framework
// primitives before the app components whose DSL creates them, and a component
// before the one that renders it. The queue is strictly FIFO and every def
// keeps the hydration flag it would have had if it had been registered inline,
// so the only difference from registering synchronously is where the task
// boundaries fall.
//
// The queue is used only when the page was server-rendered. A client-rendered
// boot renders from the DSL immediately after CreateApp returns, and that path
// really does need every tag defined by then.
//
// CreateComponent arrives as an argument rather than an import: the router
// flushes this queue before it renders a page, and importing create-component
// here would close a router -> queue -> create-component -> router cycle.



// Wall-clock budget per slice. It is wall clock, not work, so a slow phone (or
// Lighthouse's 4x CPU throttle) gets the same short tasks a fast one does — it
// just fits fewer defs into each. A slice can still overshoot by one def, so
// the tail is set by the single most expensive component, not by this number.
const SLICE_MS = 8;

// A capture-phase listener on each of these drains the queue synchronously
// before the event reaches anything. That closes the window the slicing would
// otherwise open: an element whose definition is still queued has no listeners,
// so a tap on it would do nothing and be gone. Flushing on the way in means the
// element is upgraded — and its connectedCallback listeners registered —
// before the event finishes propagating to it, because the DOM looks listeners
// up per node as the event arrives, not when it is dispatched.
const FLUSH_EVENTS = ['pointerdown', 'keydown', 'click', 'submit', 'focusin'];

const queue = [];
const pendingTags = new Set();
let draining = false;
let hooked = false;
let drainWaiters = [];

function notifyDrained() {
  if (queue.length) return;
  const waiters = drainWaiters;
  drainWaiters = [];
  for (const resolve of waiters) resolve();
}

// Resolves when every queued def has registered — immediately when nothing is
// queued. The unregistered-tag sweep waits on this: while registration is in
// flight, "is this element's tag missing?" has no honest answer, and a sweep
// that runs early reads a server-rendered element whose slice has not landed
// yet as a missing component.
function whenComponentQueueDrained() {
  if (!queue.length) return Promise.resolve();
  return new Promise((resolve) => drainWaiters.push(resolve));
}

const g = globalThis;
const now = () => (g.performance && typeof g.performance.now === 'function' ? g.performance.now() : Date.now());

// Yield the thread for real. A microtask (queueMicrotask, Promise.then) runs
// inside the task that queued it and would buy nothing at all.
function yieldToMain() {
  const s = g.scheduler;
  if (s && typeof s.yield === 'function') {
    try { return Promise.resolve(s.yield()).catch(() => {}); } catch { /* fall through */ }
  }
  if (s && typeof s.postTask === 'function') {
    try { return Promise.resolve(s.postTask(() => {}, { priority: 'user-blocking' })).catch(() => {}); } catch { /* fall through */ }
  }
  return new Promise((resolve) => {
    if (typeof g.MessageChannel === 'function') {
      const ch = new g.MessageChannel();
      ch.port1.onmessage = () => { ch.port1.close(); resolve(); };
      ch.port2.postMessage(0);
    } else if (typeof g.setTimeout === 'function') {
      g.setTimeout(resolve, 0);
    } else {
      resolve();
    }
  });
}

function tagsOf(def) {
  if (!def) return [];
  if (Array.isArray(def.tags)) return def.tags;
  return def.tag ? [def.tag] : [];
}

function registerNow(entry) {
  const prev = isHydrating();
  if (entry.hydrating !== prev) setHydrating(entry.hydrating);
  try {
    entry.register(entry.def);
  } catch (e) {
    // Registration is a render path: one bad def must not wedge the rest of
    // the queue, and the page is already on screen.
    if (g.console && g.console.error) g.console.error('[SWC] component registration failed', tagsOf(entry.def)[0], e);
  } finally {
    if (isHydrating() !== prev) setHydrating(prev);
    for (const t of tagsOf(entry.def)) pendingTags.delete(t);
  }
}

// Does this event reach an element whose definition has not landed yet?
function touchesPending(e) {
  if (!pendingTags.size) return false;
  let node = e.target;
  while (node && node.nodeType === 1) {
    if (pendingTags.has(node.localName)) return true;
    const is = node.getAttribute && node.getAttribute('is');
    if (is && pendingTags.has(is)) return true;
    node = node.parentNode;
  }
  return false;
}

function onEarlyInput(e) {
  if (touchesPending(e)) flushComponentQueue();
}

function hookInput() {
  if (hooked || typeof document === 'undefined' || !document.addEventListener) return;
  hooked = true;
  for (const type of FLUSH_EVENTS) document.addEventListener(type, onEarlyInput, true);
}

function unhookInput() {
  if (!hooked) return;
  hooked = false;
  for (const type of FLUSH_EVENTS) document.removeEventListener(type, onEarlyInput, true);
}

// Register everything still queued, right now. Called on the first user input
// that reaches a pending element, and available to anything that needs the
// registry complete before it can proceed.
function flushComponentQueue() {
  while (queue.length) registerNow(queue.shift());
  pendingTags.clear();
  unhookInput();
  notifyDrained();
}

async function drain() {
  while (queue.length) {
    await yieldToMain();
    const t0 = now();
    while (queue.length) {
      registerNow(queue.shift());
      if (now() - t0 >= SLICE_MS) break;
    }
  }
  draining = false;
  unhookInput();
  notifyDrained();
}

// Queue a batch of defs for registration in later tasks. `register` is
// CreateComponent (see the note at the top); `hydrating` records the hydration
// flag in force at the call site so each def upgrades under the same
// conditions it would have inline.
function queueComponents(defs, { register, hydrating = false } = {}) {
  if (typeof register !== 'function') return;
  for (const def of defs) {
    queue.push({ def, register, hydrating });
    for (const t of tagsOf(def)) {
      pendingTags.add(t);
      // A queued tag is registered-in-progress, not unknown: telling the
      // unregistered-tag recovery about it now keeps a sliced registration
      // from sending the framework's own tags to a registry.
      registerKnownTag(t);
    }
  }
  if (!queue.length) return;
  hookInput();
  if (!draining) {
    draining = true;
    drain();
  }
}
return { whenComponentQueueDrained, yieldToMain, flushComponentQueue, queueComponents };
})();

const __m6 = (() => {
// Shared route-table logic — the ONE canonical implementation used by both
// the client router (core/services/router.js) and the SSR router
// (server/ssr/ssr-router.js). Joins the framework's shared client/server
// anti-drift modules (matchUrlToRoute, parseI18nPattern, parseShowIfToken):
// pure functions only — no DOM, no node builtins — safe in the browser and
// on Cloudflare workerd alike.

/**
 * Flatten a nested route config into a flat route table.
 *
 * - Child paths are prefixed with the parent path (path prefix sugar).
 * - canActivate guards merge parent-first, then the route's own.
 * - `frame` (the component that wraps the page; see core/services/router.js)
 *   is inherited by the children that do not name their own, and a child's
 *   own value REPLACES the parent's: frames never stack. `frame: null` is a
 *   bare page (no frame, no persistent layout or overlays), and
 *   `chrome: false` is its alias. A row with no frame anywhere above it has
 *   no `frame` key at all, which is the default chrome.
 * - A parent with a component is a route itself AND a prefix for children.
 * - '*' stays '*' (catch-all is never prefixed).
 */
function flattenRoutes(routes, parentPath = '', parentGuards = [], parentFrame = undefined) {
  const flat = [];
  for (const route of routes) {
    const fullPath = route.path === '*' ? '*' : parentPath + route.path;

    // Merge canActivate: parent guards first, then child's own
    const ownGuards = route.canActivate || [];
    const mergedGuards = [...parentGuards, ...ownGuards];

    // The frame: the route's own statement when it makes one (a tag, or
    // null), else the alias, else the parent's.
    const frame = Object.prototype.hasOwnProperty.call(route, 'frame') ? route.frame
      : (route.chrome === false ? null : parentFrame);

    const entry = { ...route, path: fullPath };
    delete entry.children;
    if (mergedGuards.length > 0) {
      entry.canActivate = mergedGuards;
    }
    if (frame !== undefined) entry.frame = frame;

    // Parent with component → it's a route itself
    if (route.component) {
      flat.push(entry);
    }

    // Recurse children (path prefix sugar)
    if (route.children) {
      flat.push(...flattenRoutes(route.children, fullPath, mergedGuards, frame));
    }
  }
  return flat;
}

/**
 * Consume i18n locale segments from the front of a URL path.
 *
 * For each configured path segment (from parseI18nPattern), reads the
 * matching URL segment, reports its value(s) through setValue, and strips
 * it from the returned path.
 *
 * A plain (non-compound) segment is only a locale if it IS one: when
 * localeValues is provided, an unlisted value stays part of the route and
 * consumption stops. Without this guard the router used to consume ANY
 * first segment as the locale — /pricing became locale='pricing' and the
 * home route matched instead.
 *
 * @param {string} url - path to strip (no query string)
 * @param {Object|null} descriptor - result of parseI18nPattern
 * @param {Object} [options]
 * @param {Set|Array} [options.localeValues] - valid values for a plain segment
 * @param {Function} [options.setValue] - (storeKey, value) sink for extracted values
 * @returns {string} the bare path with consumed segments removed
 */
function consumeI18nSegments(url, descriptor, options = {}) {
  if (!descriptor || !descriptor.pathSegments || descriptor.pathSegments.length === 0) {
    return url;
  }

  const setValue = options.setValue || (() => {});
  // An EMPTY list means "no guard", not "nothing is a locale" — mirrors the
  // client router's init, which only builds the guard from a non-empty
  // locale.supported. Without this, [] would make the server refuse every
  // locale prefix the client happily consumes.
  const raw = options.localeValues;
  const localeValues = raw && (raw instanceof Set ? raw.size : raw.length)
    ? (raw instanceof Set ? raw : new Set(raw))
    : null;

  const parts = url.split('/').filter(Boolean);

  let consumed = 0;
  for (const seg of descriptor.pathSegments) {
    if (consumed >= parts.length) break;
    const part = parts[consumed];

    if (seg.compound) {
      // Split compound: 'ch-de' with separator '-' → [ch, de]
      const values = part.split(seg.separator);
      for (let i = 0; i < seg.storeKeys.length && i < values.length; i++) {
        setValue(seg.storeKeys[i], values[i]);
      }
    } else {
      // A plain segment is only a locale if it IS one: an unlisted value
      // (/pricing on a bare URL) stays part of the route.
      if (localeValues && !localeValues.has(part)) break;
      setValue(seg.storeKey, part);
    }
    consumed++;
  }

  // Rebuild path without consumed segments
  const remaining = parts.slice(consumed);
  let path = '/' + remaining.join('/');
  if (path !== '/' && path.endsWith('/')) path = path.slice(0, -1);
  if (!path) path = '/';
  return path;
}
return { flattenRoutes, consumeI18nSegments };
})();

const __m7 = (() => {
const matchUrlToRoute = __m1.matchUrlToRoute;
const matchRouteTable = __m1.matchRouteTable;
const routeSpecificity = __m1.routeSpecificity;
const compareRouteSpecificity = __m1.compareRouteSpecificity;
const flushComponentQueue = __m5.flushComponentQueue;
const flattenRoutes = __m6.flattenRoutes;
const consumeI18nSegments = __m6.consumeI18nSegments;
const resolveUnknownTag = __m4.resolveTag;
const markUnregistered = __m4.markUnregistered;
const store = __m0.store;
// Import + re-export from dedicated module (also used by server/api-router.js).
// matchRouteTable is the shared best-match selector: catch-all patterns make
// first-match-wins wrong, so client, SSR and API routers all rank through it.



// Component registration is sliced across tasks while a server-rendered page
// hydrates; _createElement drains what is left before it builds a page.


// Shared route-table logic (also used by server/ssr/ssr-router.js — one
// canonical flatten + locale-strip, so client and server cannot drift)





// ── i18n URL pattern parser ──

/**
 * Parse a urlPattern string into a descriptor.
 *
 * Supports:
 *   '/:country-:lang'       → compound path segment with separator
 *   '/:country/:lang'       → multiple path segments
 *   '/:lang?country=cn'     → path + query (storeKey=paramName mapping)
 *   '?lang=language&country=cn' → query-only with mapped param names
 *   '/:locale'              → single path segment
 *
 * Returns: { pathSegments: [...], queryParams: [...] }
 */
function parseI18nPattern(pattern) {
  if (!pattern) return null;

  const result = { pathSegments: [], queryParams: [] };
  const [pathPart, queryPart] = pattern.split('?');

  // Parse path part: each /-delimited segment
  if (pathPart) {
    const segments = pathPart.split('/').filter(Boolean);
    for (const seg of segments) {
      const paramMatches = [...seg.matchAll(/:(\w+)/g)];
      if (paramMatches.length === 0) continue;

      if (paramMatches.length === 1 && seg === `:${paramMatches[0][1]}`) {
        // Simple segment: /:lang
        result.pathSegments.push({ storeKey: paramMatches[0][1] });
      } else {
        // Compound segment: /:country-:lang
        // Find the separator between params
        const keys = paramMatches.map(m => m[1]);
        // Build a regex to extract values: replace :key with capture group
        let regexStr = seg.replace(/:(\w+)/g, '([^/]+?)');
        // But we need non-greedy + separator anchors, so rebuild properly
        // For ":a-:b" → separator is "-"
        const separators = seg.split(/:(\w+)/).filter((_, i) => i % 2 === 0).filter(Boolean);
        result.pathSegments.push({
          compound: true,
          storeKeys: keys,
          separator: separators[0] || '-',
        });
      }
    }
  }

  // Parse query part: key=paramName pairs separated by &
  if (queryPart) {
    const pairs = queryPart.split('&').filter(Boolean);
    for (const pair of pairs) {
      const eqIdx = pair.indexOf('=');
      if (eqIdx > 0) {
        // storeKey=paramName mapping
        result.queryParams.push({
          storeKey: pair.slice(0, eqIdx),
          paramName: pair.slice(eqIdx + 1),
        });
      } else {
        // storeKey is also the param name
        result.queryParams.push({ storeKey: pair, paramName: pair });
      }
    }
  }

  return result;
}

// The route frame's outlet, either spelling (components/container/outlet.js).
const OUTLET_SELECTOR = 's-ou, s-ou';

// ── Router class ──

class Router {
  _initialised = false;
  _basePath = '';
  _routeTable = [];
  _pageCache = new Map();
  _outlet = null;
  // The route FRAME mounted in the app outlet, when the current route names
  // one (core/services/route-table.js): the tag, the element, and the outlet
  // inside it that holds the page. Null when the page sits in the app outlet.
  _frame = null;
  // The app's default chrome (persistent layout before the outlet, overlays
  // after it), handed over by CreateApp so a navigation can take it down for
  // a framed or bare route and put it back for a default one:
  // { layout, overlays, host, render, mounted: { layout: [], overlays: [] } }.
  _chrome = null;
  _currentRoutePattern = null;
  _currentUrl = null;
  _i18nDescriptor = null;
  _forwardSlugMap = {};   // { 'fr-fr': { 'products': 'produits', ... } }
  _reverseSlugMap = {};   // { 'fr-fr': { 'produits': 'products', ... } }
  _pendingNavigation = null;

  constructor(store) {
    this.store = store;
  }

  // ── Initialization ──

  init(config, outlet) {
    if (this._initialised) return;
    this._initialised = true;

    this._outlet = outlet;
    if (config.chrome) {
      this._chrome = { ...config.chrome, mounted: config.chrome.mounted || { layout: [], overlays: [] } };
    }
    if (config.basePath) this._basePath = config.basePath;
    if (config.urlPattern) this._i18nDescriptor = parseI18nPattern(config.urlPattern);
    // Valid values for a single-segment locale pattern (from appConfig
    // locale.supported). Without this list the router used to consume ANY
    // first segment as the locale — /components became locale='components'
    // and every later navigateTo carried the bogus prefix.
    if (Array.isArray(config.localeValues) && config.localeValues.length) {
      this._localeValues = new Set(config.localeValues);
    }
    if (config.routes) this._setRoutes(config.routes);

    this._bindPopstate();

    if (config.hydrating) {
      // SSR hydration: page already in outlet, just sync route state
      const url = typeof window !== 'undefined' ? window.location.pathname : '/';
      const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
      const path = this._syncStore(url, params);
      // Set current route pattern so subsequent navigation works correctly
      const match = this._matchRoute(path);
      if (match) {
        this._currentRoutePattern = match.config.path;
        // With the query, like every later navigation: a refusal restores
        // the page AND its state.
        this._currentUrl = `${path}${typeof window !== 'undefined' ? window.location.search || '' : ''}`;
        // Adopt the frame the server rendered (its outlet is where the page
        // sits), then cache the existing page element.
        const frame = match.config.frame;
        const frameEl = outlet.firstElementChild;
        if (typeof frame === 'string' && frameEl && frameEl.localName === frame) {
          this._frame = { tag: frame, el: frameEl, outlet: this._frameOutlet(frameEl) };
        }
        const existingPage = this._leafOutlet().firstElementChild;
        if (existingPage) {
          this._pageCache.set(match.config.path, existingPage);
        }
      }
    } else {
      this._dispatchCurrent();
    }
  }

  // ── Route table management ──

  _setRoutes(routes) {
    this._routeTable = this._flattenRoutes(routes);
  }

  _flattenRoutes(routes, parentPath = '', parentGuards = []) {
    // Delegates to the shared module — the SSR router flattens with the
    // exact same function, so the two tables cannot drift.
    return flattenRoutes(routes, parentPath, parentGuards);
  }

  addRoutes(routes) {
    const flattened = this._flattenRoutes(routes);
    this._routeTable.push(...flattened);
  }

  removeRoutes(paths) {
    const pathSet = new Set(paths);
    this._routeTable = this._routeTable.filter(r => !pathSet.has(r.path));
  }

  replaceRoutes(routes) {
    this._routeTable = [];
    this._pageCache.clear();
    this._setRoutes(routes);
  }

  // ── Route matching ──

  _matchRoute(url) {
    // Most specific match, not first declared — /docs/intro beats /docs/*
    // beats the global '*' whatever order the table lists them in. Equal
    // shapes keep declaration order, so wildcard-free tables are unaffected.
    const match = matchRouteTable(url, this._routeTable);
    return match ? { config: match.route, params: match.params } : null;
  }

  // ── Guard evaluation ──

  async _evaluateGuards(routeConfig, currentPage, opts = {}) {
    // 1. canActivate guards (developer-configured)
    if (routeConfig.canActivate) {
      for (const guard of routeConfig.canActivate) {
        // A THROWING guard denies. It used to escape as an unhandled rejection
        // through the un-awaited _handleNavigation, leaving the user on the
        // protected URL with nothing rendered — neither allowed nor blocked.
        let result;
        try {
          // The target rides along as a second argument: a per-record guard
          // needs the params of the route being ENTERED, and on a same-pattern
          // navigation those are not in the store yet (they reach it only once
          // the guards allowed them).
          result = await guard(this.store, opts.target);

          if (result === true) continue;
          if (typeof result === 'string' && result) {
            return { allowed: false, redirect: this._safeRedirect(result) };
          }
          // A truthy primitive is a pass — but a FUNCTION is not a primitive
          // and must not slip through. `canActivate: [requireRole]` written
          // instead of `requireRole('admin')` passes the uncalled factory, and
          // that is the commonest way real guard code goes wrong: it returned a
          // function, which is truthy and not an object, and the route opened.
          if (result && typeof result !== 'object' && typeof result !== 'function') continue;
          if (result && typeof result.redirect === 'string' && result.redirect) {
            return { allowed: false, redirect: this._safeRedirect(result.redirect) };
          }
          return { allowed: false, reason: 'denied' };
        } catch (err) {
          // Reading `result.redirect` can throw too (a getter, a Proxy), so the
          // inspection lives inside the try alongside the call.
          console.warn('[swc] route guard threw — blocking the navigation:', err);
          // An ERROR, not a denial: the app can tell "no access" from "the
          // auth service is down" and offer "try again" (see _publishDenied).
          return { allowed: false, reason: 'error', message: err?.message || String(err) };
        }

      }
    }

    // 2. canDeactivate — automatic dirty check (skip if force). Not for a
    // same-pattern navigation: a param change does not leave the page.
    if (!opts.force && !opts.activateOnly && currentPage?.substore?.isDirty?.()) {
      return { allowed: false, reason: 'dirty' };
    }

    return { allowed: true };
  }

  // Every refused navigation, from either path (a new route or a same-pattern
  // param change), ends here: publish what happened, then redirect or restore.
  _blocked(url, guardResult) {
    if (guardResult.reason === 'dirty') {
      this.store.setValue('_navigationBlocked', { targetUrl: url, reason: 'dirty' });
    }
    this._publishDenied(url, guardResult);

    // Every block that navigates counts toward one cap, not just redirects.
    // A chain can be a redirect loop (a guard on /sign-in redirecting to
    // /sign-in) OR a restore loop (the fallback route's own guard blocking),
    // and both starve the event loop the same way — pure microtask recursion,
    // a frozen tab rather than a busy one. _currentRoutePattern is only set on
    // a SUCCESSFUL activation, so the same-route check upstream cannot see it.
    this._blockDepth = (this._blockDepth || 0) + 1;
    if (this._blockDepth > 10) {
      this._blockDepth = 0;
      console.warn(`[swc] route guards blocked 10 navigations in a row around '${url}' — giving up rather than looping.`);
      return;
    }

    // Re-dispatch through navigateTo rather than rewriting history by hand.
    // The hand-rolled version called _syncStore(url) with one argument — its
    // signature is (url, searchParams) — so it THREW on every plain block,
    // and 1084 tests passed anyway. It also left the blocked route's params
    // in the store (router.section of a page the guard just denied) and never
    // re-rendered the outlet, so URL, store and outlet could all disagree.
    //
    // replace:true because navigateTo already pushed the blocked target;
    // pushing the restore over it left a junk entry that Back landed on.
    const target = guardResult.redirect || this._currentUrl || '/';
    // `restoring`: the navigation that puts the user back is not a success
    // that clears the refusal it exists to report.
    this.navigateTo(target, { replace: true, restoring: !guardResult.redirect });
  }

  // A refused navigation, published. `_navigationBlocked` means "stopped and
  // RESUMABLE" — a dirty form, a challenge with a remedy — and the guide's
  // unsaved-changes modal keys on its truthiness, so a plain refusal must not
  // land there. This key says the intent was refused, and why: `denied` (a
  // guard said no) or `error` (a guard threw — an outage, not a verdict).
  // Cleared, like _navigationBlocked, by the next successful navigation.
  _publishDenied(url, guardResult) {
    if (guardResult.redirect || guardResult.reason === 'dirty') return;
    this.store.setValue('_navigationDenied', {
      targetUrl: url,
      reason: guardResult.reason || 'denied',
      ...(guardResult.message ? { message: guardResult.message } : {}),
    });
  }

  // ── Same-route detection ──

  _isSameRoutePattern(patternA, patternB) {
    return patternA === patternB;
  }

  // ── i18n URL helpers ──

  /**
   * Strip locale segments from a full URL path + search params.
   * Sets extracted locale values in the store.
   * Returns the bare path (locale segments removed).
   */
  // `setValue` receives every i18n store write (locale, i18n query params).
  // Required, no default: the only caller collects them (_resolve) so they
  // are applied once the guards have allowed the target — a default that
  // wrote the store would be the pre-guard locale switch coming back.
  _stripLocale(url, searchParams, setValue) {
    if (typeof setValue !== 'function') throw new Error('[swc] router: _stripLocale needs the i18n write sink');
    const desc = this._i18nDescriptor;
    if (!desc) return url;

    // Extract from path segments — shared with the SSR router so a bare URL
    // (/components, /pricing) is treated identically on client and server:
    // an unlisted value stays part of the route and the store keeps its
    // current locale.
    const path = consumeI18nSegments(url, desc, {
      localeValues: this._localeValues,
      setValue,
    });

    // Extract from query params
    for (const qp of desc.queryParams) {
      const val = searchParams.get(qp.paramName);
      if (val) {
        setValue(qp.storeKey, val);
        searchParams.delete(qp.paramName);
      }
    }

    return path;
  }

  /**
   * Build a full URL from a bare path by prepending locale path segments
   * and appending locale query params based on current store values.
   */
  _buildUrl(barePath) {
    const desc = this._i18nDescriptor;
    if (!desc) return barePath;

    let prefix = '';
    let queryParts = [];

    // Build path prefix from store values. An UNSET segment adds nothing:
    // '/' + '' made every navigateTo emit '//path' — a protocol-relative URL
    // pushState resolves to a foreign origin — the moment an app declared a
    // locale pattern while its default language kept bare paths.
    for (const seg of desc.pathSegments) {
      if (seg.compound) {
        const values = seg.storeKeys.map(k => this.store.getValue(k) || '');
        if (values.every(v => !v)) continue;
        prefix += '/' + values.join(seg.separator);
      } else {
        const val = this.store.getValue(seg.storeKey) || '';
        if (!val) continue;
        prefix += '/' + val;
      }
    }

    // Build query params from store values
    for (const qp of desc.queryParams) {
      const val = this.store.getValue(qp.storeKey);
      if (val) queryParts.push(`${qp.paramName}=${encodeURIComponent(val)}`);
    }

    // Translate canonical slugs to locale slugs
    const locale = this.store.getValue('locale');
    const translatedPath = this._translateOutbound(barePath, locale);

    let fullUrl = prefix + translatedPath;

    // Merge with existing query params if in browser
    if (typeof window !== 'undefined') {
      const existing = new URLSearchParams(window.location.search);
      // Remove locale params from existing
      for (const qp of desc.queryParams) {
        existing.delete(qp.paramName);
      }
      // Add locale params
      for (const part of queryParts) {
        const [k, v] = part.split('=');
        existing.set(k, decodeURIComponent(v));
      }
      const qs = existing.toString();
      if (qs) fullUrl += '?' + qs;
    } else if (queryParts.length) {
      fullUrl += '?' + queryParts.join('&');
    }

    return fullUrl;
  }

  // ── URL-to-store sync ──

  // URL → the bare canonical path (basePath and locale stripped, inbound
  // slugs translated) plus the i18n store writes the URL implies, COLLECTED
  // rather than applied: a refused /de/doc/2 must not switch the user's
  // locale on the way to being refused. The candidate locale still drives
  // the slug translation, so the route match sees the target as it is.
  _resolve(url, searchParams) {
    let path = url;
    if (this._basePath && path.startsWith(this._basePath)) {
      path = path.slice(this._basePath.length) || '/';
    }
    const i18n = {};
    path = this._stripLocale(path, searchParams, (key, value) => { i18n[key] = value; });
    const locale = i18n.locale !== undefined ? i18n.locale : this.store.getValue('locale');
    return { path: this._translateInbound(path, locale), i18n };
  }

  _resolvePath(url, searchParams) {
    return this._resolve(url, searchParams).path;
  }

  _applyI18n(i18n) {
    for (const [key, value] of Object.entries(i18n || {})) this.store.setValue(key, value);
  }

  // Resolve AND commit — the hydration path, where there are no guards to
  // wait for (the server already rendered the page).
  _syncStore(url, searchParams) {
    const { path, i18n } = this._resolve(url, searchParams);
    this._applyI18n(i18n);
    this._commitRoute(path, searchParams);
    return path;
  }

  // The store writes for a route the guards ALLOWED: `route`, the query
  // params as router.*, and the cleanup of stale router.* keys. Until the
  // guards have answered the store keeps describing the page the user is on —
  // a refused target must never be readable as the current route, and the
  // current record's params must not blank while a guard is awaited.
  _commitRoute(path, searchParams) {
    // Collect old router.* keys for cleanup
    const oldKeys = this.store.getStoreNames().filter(k => k.startsWith('router.'));
    const newKeys = new Set();

    // Set new values
    this.store.setValue('route', path);
    searchParams.forEach((value, key) => {
      const storeKey = `router.${key}`;
      this.store.setValue(storeKey, value);
      newKeys.add(storeKey);
    });

    // Clean up stale router.* keys — use setValue(null) not removeStore
    // removeStore destroys subscriptions; setValue preserves them so active
    // components receive the null notification and can react accordingly
    for (const key of oldKeys) {
      if (!newKeys.has(key)) {
        this.store.setValue(key, null);
      }
    }
  }

  // ── Frames ──

  // Where the page goes: the frame's outlet while a frame is mounted, the app
  // outlet otherwise.
  _leafOutlet() {
    return this._frame ? this._frame.outlet : this._outlet;
  }

  // The outlet inside a frame element (components/container/outlet.js), or
  // the frame itself when its template declares none: the page still has a
  // place, and the frame is then the page's direct parent.
  _frameOutlet(el) {
    const found = typeof el.querySelector === 'function' ? el.querySelector(OUTLET_SELECTOR) : null;
    return found || el;
  }

  // Bring the mounted frame in line with the target route's `frame`
  // (undefined: the default chrome and no frame; a tag: that frame and no
  // chrome; null: neither). The page is already out of the outlets when this
  // runs. A frame whose tag is unchanged stays mounted untouched, which is
  // the point of a frame: sibling pages under it swap without it moving.
  _syncFrame(frame) {
    const tag = typeof frame === 'string' ? frame : null;
    if (!(this._frame && this._frame.tag === tag)) {
      if (this._frame) {
        const old = this._frame;
        this._frame = null;
        if (typeof old.el.deactivate === 'function') old.el.deactivate();
        this._removeEl(old.el);
      }
      if (tag) {
        const el = this._createElement(tag);
        this._outlet.appendChild(el);
        this._frame = { tag, el, outlet: this._frameOutlet(el) };
      }
      // One main landmark: the frame's outlet carries it while a frame is
      // mounted, the app outlet otherwise (create-app and render.js agree).
      if (this._frame) {
        if (this._frame.outlet !== this._frame.el) this._setAttr(this._frame.outlet, 'role', 'main');
        this._setAttr(this._outlet, 'role', null);
      } else {
        this._setAttr(this._outlet, 'role', 'main');
      }
    }
    this._setChrome(frame === undefined);
  }

  // The default chrome, rendered around the app outlet on the way into a
  // default route and taken down on the way into a framed or bare one.
  _setChrome(on) {
    const c = this._chrome;
    if (!c) return;
    const mounted = c.mounted.layout.length > 0 || c.mounted.overlays.length > 0;
    if (on && !mounted) {
      c.mounted.layout = (c.layout || []).map((def) => c.render(def)).filter(Boolean);
      c.mounted.layout.forEach((el) => c.host.insertBefore(el, this._outlet));
      c.mounted.overlays = (c.overlays || []).map((def) => c.render(def)).filter(Boolean);
      c.mounted.overlays.forEach((el) => c.host.appendChild(el));
    } else if (!on && mounted) {
      [...c.mounted.layout, ...c.mounted.overlays].forEach((el) => this._removeEl(el));
      c.mounted = { layout: [], overlays: [] };
    }
  }

  _removeEl(el) {
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  _setAttr(el, name, value) {
    if (!el || typeof el.setAttribute !== 'function') return;
    if (value === null) el.removeAttribute(name);
    else el.setAttribute(name, value);
  }

  // ── Page lifecycle ──

  // Override points for testing (no real DOM in Node.js)
  _createElement(tag) {
    // A page rendered from the DSL sets properties on the elements it creates,
    // and a property set on an element whose definition has not landed yet is
    // an own property that shadows the accessor the upgrade installs. During
    // hydration the registry is filled across several tasks, so a navigation
    // that starts inside that window finishes it first. This sits after
    // _matchRoute deliberately: the specificity selector can pick a catch-all
    // whose component declaration order would not have reached yet, and the
    // flush covers whichever component won. After the drain it is a single
    // boolean read. It also runs BEFORE the unknown-tag resolution below: a
    // tag still sitting in the queue is registered, not unknown, and must not
    // be handed to recovery.
    flushComponentQueue();
    // A route's `component` is a tag STRING in config data, so a page whose
    // module was tree-shaken out reads exactly like a typo. Resolve before
    // creating: a near-miss name is corrected, an unknown one is queued for
    // recovery and upgrades in place when its definition lands.
    const resolved = resolveUnknownTag(tag);
    const el = document.createElement(resolved);
    markUnregistered(el, resolved);
    return el;
  }

  // A redirect target comes from application code and is handed straight to
  // pushState. Before this, `{ redirect: '//evil.test' }` and
  // `{ redirect: 'javascript:alert(1)' }` were accepted verbatim — and for the
  // object form that was a NEW sink, because the same value previously fell
  // through as ALLOWED. Fixing a fail-open into an unvalidated redirect is not
  // a fix.
  //
  // Same discipline as safeResume in core/core/bindings/guards.js: same-origin
  // path only, and reject rather than coerce. Returns undefined when unsafe, so
  // the guard blocks with no redirect.
  _safeRedirect(target) {
    if (typeof target !== 'string' || !target) return undefined;
    const raw = target.trim();
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
      console.warn(`[swc] route guard redirect '${target}' is not a same-origin path — blocking without redirecting.`);
      return undefined;
    }
    if (/[\u0000-\u0020\u007f]/.test(raw) || /^\/+[a-z][a-z0-9+.-]*:/i.test(raw)) {
      console.warn(`[swc] route guard redirect '${target}' is malformed — blocking without redirecting.`);
      return undefined;
    }
    return raw;
  }

  _pushState(url) {
    if (typeof window !== 'undefined') {
      history.pushState({}, '', url);
    }
  }

  // Restoring after a blocked navigation REPLACES rather than pushes:
  // navigateTo has already pushed the blocked target, so pushing the restore
  // over it leaves a junk entry that Back lands on, re-blocks, and pushes
  // again — Back becomes a no-op that grows the stack on every press.
  _replaceState(url) {
    if (typeof window !== 'undefined') {
      history.replaceState({}, '', url);
    }
  }

  async _handleNavigation(url, opts = {}) {
    // Sequence id: a later navigation started while this one awaited its guards
    // must win, so bail if we have been superseded before mutating the outlet.
    const navId = this._navSeq = (this._navSeq || 0) + 1;
    const match = this._matchRoute(url);
    if (!match) {
      // No route, so nothing to guard: the store follows the URL as it
      // always did — `route` is state a page can use without a route config.
      this._applyI18n(opts.i18n);
      this._commitRoute(url, opts.searchParams
        || (typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()));
      // If no match and we have an i18n descriptor, the path might use
      // translated slugs but manifest hasn't loaded yet — queue for replay
      if (this._i18nDescriptor && url !== '/') {
        const locale = this.store.getValue('locale');
        this._pendingNavigation = { path: url, locale };
      }
      return;
    }

    const { config, params } = match;
    // The store describes the CURRENT page until the guards allow the target:
    // route, query params and path params commit together, after the answer.
    const searchParams = opts.searchParams
      || (typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams());
    const commit = () => {
      this._applyI18n(opts.i18n);
      this._commitRoute(url, searchParams);
      // Sync route path params to store (e.g. /:pageSlug → router.pageSlug)
      for (const [key, value] of Object.entries(params)) {
        this.store.setValue(`router.${key}`, value);
      }
      // The BARE resolved path plus the query — never _buildUrl's
      // locale-prefixed form, which navigateTo prefixes again on a restore.
      // With the query: a refusal restores the page AND its state.
      const search = searchParams.toString();
      this._currentUrl = search ? `${url}?${search}` : url;
    };

    // Same-route-pattern? Skip the lifecycle — data flows through
    // subscriptions, no teardown — but NOT the guards: /doc/1 → /doc/2 is a
    // navigation a per-record guard must see, or it guards the first record
    // only. canActivate runs; the dirty check does not (the page is not being
    // left). The params reach the store only once the guards allowed them.
    const samePattern = this._isSameRoutePattern(config.path, this._currentRoutePattern);
    if (samePattern) {
      const activate = await this._evaluateGuards(config, null, { ...opts, activateOnly: true, target: { url, params, path: config.path } });
      if (this._navSeq !== navId) return;
      if (!activate.allowed) return this._blocked(url, activate);
      this._blockDepth = 0;
      if (!opts.restoring) {
        if (this.store.getValue('_navigationBlocked')) this.store.setValue('_navigationBlocked', null);
        if (this.store.getValue('_navigationDenied')) this.store.setValue('_navigationDenied', null);
      }
      commit();
      return;
    }

    // Evaluate guards against current page in its outlet (the frame's, when
    // one is mounted)
    const pageOutlet = this._leafOutlet();
    const currentPage = pageOutlet?.firstChild || null;
    const guardResult = await this._evaluateGuards(config, currentPage, { ...opts, target: { url, params, path: config.path } });
    if (this._navSeq !== navId) return; // superseded by a newer navigation

    if (!guardResult.allowed) return this._blocked(url, guardResult);

    // A navigation got through, so any block chain has ended.
    this._blockDepth = 0;

    // Clear any previous navigation block or refusal
    if (this.store.getValue('_navigationBlocked')) {
      this.store.setValue('_navigationBlocked', null);
    }
    if (!opts.restoring && this.store.getValue('_navigationDenied')) {
      this.store.setValue('_navigationDenied', null);
    }
    commit();

    // Deactivate current page
    if (currentPage && currentPage.parentNode === pageOutlet) {
      if (typeof currentPage.deactivate === 'function') currentPage.deactivate();
      pageOutlet.removeChild(currentPage);
      // Cache by previous route pattern
      if (this._currentRoutePattern) {
        this._pageCache.set(this._currentRoutePattern, currentPage);
      }
    }

    // The frame the target route names, kept when it is the one mounted
    this._syncFrame(config.frame);

    // Activate target page (from cache or create new)
    let element = this._pageCache.get(config.path);
    if (!element) {
      element = this._createElement(config.component);
      this._pageCache.set(config.path, element);
    }
    this._leafOutlet().appendChild(element);
    if (typeof element.activate === 'function') element.activate();
    this._currentRoutePattern = config.path;

    // SEO: update document title and meta description from route config
    if (config.meta && typeof document !== 'undefined') {
      this._updateMeta(config.meta);
    }
  }

  _updateMeta(meta) {
    if (meta.title) {
      const title = this.store.getValue(meta.title) || meta.title;
      document.title = title;
    }
    if (meta.description) {
      const desc = this.store.getValue(meta.description) || meta.description;
      let el = document.querySelector('meta[name="description"]');
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', 'description');
        document.head.appendChild(el);
      }
      el.setAttribute('content', desc);
    }
  }

  // ── Browser integration ──

  _bindPopstate() {
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', () => this._dispatchCurrent());
    }
  }

  _dispatchCurrent() {
    if (typeof window === 'undefined') return;
    const url = window.location.pathname;
    const params = new URLSearchParams(window.location.search);

    // If i18n path segments are configured and URL is bare root '/',
    // redirect to default locale prefix
    if (this._i18nDescriptor?.pathSegments.length > 0 && url === '/') {
      const defaultUrl = this._buildUrl('/');
      if (defaultUrl !== '/') {
        history.replaceState({}, '', defaultUrl);
        const redirectParams = new URLSearchParams(window.location.search);
        const { path, i18n } = this._resolve(defaultUrl, redirectParams);
        this._handleNavigation(path, { searchParams: redirectParams, i18n });
        return;
      }
    }

    const { path, i18n } = this._resolve(url, params);
    this._handleNavigation(path, { searchParams: params, i18n });
  }

  navigateTo(url, opts = {}) {
    // Build full URL with locale prefix/query params. A query on the given
    // url (a refusal restoring '/doc/1?tab=summary') rides along after the
    // locale prefix, merged with whatever the i18n descriptor adds.
    const [bare, search] = url.split('?');
    let fullUrl = this._buildUrl(bare);
    if (search) fullUrl += (fullUrl.includes('?') ? '&' : '?') + search;

    if (typeof window !== 'undefined' && fullUrl !== window.location.pathname + window.location.search) {
      // replace: swap the current entry (e.g. redirecting /builder → /builder/<new-uuid> so Back does
      // not land on the bare route and mint yet another id). Default is a normal pushState entry.
      if (opts.replace) history.replaceState({}, '', fullUrl);
      else history.pushState({}, '', fullUrl);
    }
    // dispatch: false — the original lean-router contract: record the URL and
    // stop. No store sync, no outlet work; the caller drives its own store
    // keys and the page projects in place (selection stays, scroll stays).
    if (opts.dispatch === false) return;
    // Sync + handle using the bare path (strip locale back out)
    const searchParams = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
    const { path, i18n } = this._resolve(fullUrl.split('?')[0], searchParams);
    this._handleNavigation(path, { ...opts, searchParams, i18n });
  }

  /**
   * Update one URL query segment WITHOUT navigating (the lean-router
   * contract): the URL records the state for deep links and Back/Forward,
   * and the matching router.<key> store value drives whatever projection the
   * page binds. Content swaps in place — the outlet, the page element and
   * the scroll position never move.
   */
  setSegment(key, value) {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.set(key, value);
      const url = `${window.location.pathname}?${params.toString()}`;
      if (url !== window.location.pathname + window.location.search) {
        history.pushState({}, '', url);
      }
    }
    this.store.setValue(`router.${key}`, value);
  }

  /**
   * Remove a query segment entirely.
   *
   * setSegment had no inverse, so "no value" could only be expressed as an
   * empty one — leaving `?theme=` on the URL after a choice was cleared. That
   * reads as a setting that is still on, survives sharing, and forces every
   * consumer to treat empty-string and absent as the same thing.
   */
  clearSegment(key) {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.has(key)) {
        params.delete(key);
        const qs = params.toString();
        const url = window.location.pathname + (qs ? `?${qs}` : '');
        if (url !== window.location.pathname + window.location.search) {
          history.pushState({}, '', url);
        }
      }
    }
    // null rather than '': the router's own cleanup uses null for "this
    // segment is not in the URL", and subscribers already read it that way.
    this.store.setValue(`router.${key}`, null);
  }

  /**
   * Rebuild the current URL with updated locale store values.
   * Called when locale/country/lang changes in the store.
   * Uses the stored bare route path — does NOT re-read/strip the URL.
   */
  updateLocaleUrl() {
    if (typeof window === 'undefined') return;
    if (!this._i18nDescriptor && Object.keys(this._forwardSlugMap).length === 0) return;
    // Use the current bare route from store (already stripped, canonical)
    const barePath = this.store.getValue('route') || '/';
    const newUrl = this._buildUrl(barePath);
    const currentUrl = window.location.pathname + window.location.search;
    // Only update if URL actually differs — prevents loops
    if (newUrl !== currentUrl) {
      history.replaceState({}, '', newUrl);
    }
  }

  /**
   * Get all store keys that the i18n pattern uses.
   * Used by CreateApp to subscribe to locale changes.
   */
  getI18nStoreKeys() {
    const desc = this._i18nDescriptor;
    if (!desc) return [];
    const keys = [];
    for (const seg of desc.pathSegments) {
      if (seg.compound) keys.push(...seg.storeKeys);
      else keys.push(seg.storeKey);
    }
    for (const qp of desc.queryParams) {
      keys.push(qp.storeKey);
    }
    return keys;
  }

  // ── i18n Manifest ──

  /**
   * Load an i18n manifest (from API/CMS/inlined SSR).
   * Merges routes, builds slug maps, sets locale metadata in store.
   *
   * @param {Object} manifest - { locales: [...], routes: [...] }
   */
  loadI18n(manifest) {
    if (!manifest) return;

    // Store locale metadata for dropdown / components
    if (manifest.locales) {
      this.store.setValue('i18nLocales', manifest.locales);
    }

    // Merge routes and build slug maps
    if (manifest.routes) {
      this._mergeManifestRoutes(manifest.routes);
      this._buildSlugMaps(manifest.routes);
    }

    // Replay pending navigation if we had a translated slug that couldn't match
    if (this._pendingNavigation) {
      const { path, locale } = this._pendingNavigation;
      this._pendingNavigation = null;
      const canonical = this._translateInbound(path, locale);
      this._handleNavigation(canonical);
    }

    // Re-evaluate current URL with slug support
    this.updateLocaleUrl();
  }

  /**
   * Merge manifest routes into the existing route table.
   * - Existing routes: enrich with slugs, override component if manifest provides one
   * - New routes: add (must have component)
   * - Guards from existing routes are always preserved
   */
  _mergeManifestRoutes(manifestRoutes, parentPath = '') {
    for (const mRoute of manifestRoutes) {
      const fullPath = mRoute.path === '*' ? '*' : parentPath + mRoute.path;
      const existing = this._routeTable.find(r => r.path === fullPath);

      if (existing) {
        // Enrich existing route
        if (mRoute.component) {
          // Component override — invalidate page cache
          if (existing.component !== mRoute.component) {
            this._pageCache.delete(fullPath);
          }
          existing.component = mRoute.component;
        }
        if (mRoute.slugs) {
          existing.slugs = mRoute.slugs;
        }
      } else if (mRoute.component) {
        // New route from manifest
        const entry = { path: fullPath, component: mRoute.component };
        if (mRoute.slugs) entry.slugs = mRoute.slugs;
        this._routeTable.push(entry);
      }

      // Recurse children
      if (mRoute.children) {
        this._mergeManifestRoutes(mRoute.children, fullPath);
      }
    }
  }

  /**
   * Build forward and reverse slug maps from manifest routes.
   * Forward: canonical → translated (for outbound URLs)
   * Reverse: translated → canonical (for inbound URL matching)
   */
  _buildSlugMaps(manifestRoutes, parentPath = '') {
    this._forwardSlugMap = {};
    this._reverseSlugMap = {};
    this._collectSlugs(manifestRoutes);
  }

  _collectSlugs(routes) {
    for (const route of routes) {
      if (route.slugs) {
        // The canonical slug is the last segment of the path (without leading /)
        const canonical = route.path.replace(/^\//, '');
        for (const [locale, translated] of Object.entries(route.slugs)) {
          if (!this._forwardSlugMap[locale]) this._forwardSlugMap[locale] = {};
          if (!this._reverseSlugMap[locale]) this._reverseSlugMap[locale] = {};
          this._forwardSlugMap[locale][canonical] = translated;
          this._reverseSlugMap[locale][translated] = canonical;
        }
      }
      if (route.children) {
        this._collectSlugs(route.children);
      }
    }
  }

  /**
   * Translate a bare path's static segments from canonical to locale slugs (outbound).
   * Dynamic params (from route patterns) pass through untranslated.
   */
  _translateOutbound(barePath, locale) {
    if (!locale || !this._forwardSlugMap[locale]) return barePath;
    const map = this._forwardSlugMap[locale];
    const segments = barePath.split('/').filter(Boolean);
    const translated = segments.map(seg => map[seg] || seg);
    return '/' + translated.join('/') || '/';
  }

  /**
   * Translate a locale-slugged path back to canonical segments (inbound).
   * Used when matching incoming URLs against the canonical route table.
   */
  _translateInbound(path, locale) {
    if (!locale || !this._reverseSlugMap[locale]) return path;
    const map = this._reverseSlugMap[locale];
    const segments = path.split('/').filter(Boolean);
    const canonical = segments.map(seg => map[seg] || seg);
    return '/' + canonical.join('/') || '/';
  }

  back() {
    if (typeof window !== 'undefined') {
      history.back();
    }
  }
}

// Singleton — uses the global store

const router = new Router(store);
return { matchUrlToRoute, matchRouteTable, routeSpecificity, compareRouteSpecificity, flattenRoutes, consumeI18nSegments, parseI18nPattern, Router, router };
})();

const __m8 = (() => {
// Terse aliases for binding-token property names. The short form is what gets
// authored and emitted (LLM output, samples); the framework maps it to the
// canonical long name at parse time, in BOTH pipelines (client applyToken and
// SSR processTokens). Long forms keep working unchanged. Rule: short = the
// leading letters of the long name, so the scheme is predictable and learnable.
const BINDING_ALIASES = {
  dk: 'dataKey',
  ds: 'dataSource',
  df: 'dataField',
  rf: 'ref',
  lf: 'labelFor',
};

function normalizeBindingName(name) {
  return BINDING_ALIASES[name] || name;
}
return { BINDING_ALIASES, normalizeBindingName };
})();

const __m9 = (() => {
// Shared utilities used by create-component and its mixins.
// Extracted to break circular imports in the prod bundle.

// Module-scoped: tracks which component is currently rendering children.
let _renderingComponent = null;
function setRenderingComponent(c) { _renderingComponent = c; }
function getRenderingComponent() { return _renderingComponent; }

function toKebabCase(name) {
  return name.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
}

function setAttrValue(el, name, value) {
  if (!el || !name) return false;
  // The DOM lowercases attribute names, so a camelCase name like "pageSize"
  // would become the dead attribute "pagesize". Kebab-convert it instead so
  // observedAttributes (which watch the kebab spelling declared via props)
  // and CSS attribute selectors see one stable name.
  const attrName = /[A-Z]/.test(name) ? toKebabCase(name) : name;
  if (value === undefined || value === null || value === false) {
    el.removeAttribute(attrName);
    return false;
  }
  if (value === true) {
    el.setAttribute(attrName, '');
    return true;
  }
  el.setAttribute(attrName, String(value));
  return true;
}

// textContent assignment replaces the node's children, so writing the text that
// is already there throws away a text node and builds an identical one. On a
// server-rendered page that is the common case, not the rare one.
function setTextContent(el, value) {
  const next = value == null ? '' : String(value);
  if (el.textContent !== next) el.textContent = next;
}

// Discard the show-if gates an element owns. `keep`, when given, is the set
// of gated ELEMENTS that survive this pass — a list rebuild retains its
// `static` children (list-mixin.js), and their gates must keep both their
// store subscriptions and their prop registrations, since nothing replays
// them while the owner stays connected. A gate marked `static` survives such
// a pass even while hidden, when it has no element to match. Without a keep
// set every gate on the element goes, which is what a discarded subtree
// wants.
function cleanupShowIfState(el, keep) {
  if (!el || !el._showIfState) return;
  const state = el._showIfState;
  // A gate survives a keep pass when its element is one the caller is
  // keeping, or when it is a `static` gate — which a list rebuild retains
  // whether it is showing or not, and a hidden one has no element to match.
  const survives = (entry) => Boolean(keep && entry && (entry.static || (entry.el && keep.has(entry.el))));

  let keptBinding = false;
  [...state.bindings.keys()].forEach((id) => {
    const remaining = (state.bindings.get(id) || []).filter(survives);
    if (remaining.length) {
      state.bindings.set(id, remaining);
      keptBinding = true;
      return;
    }
    state.bindings.delete(id);
    state.subs.get(id)?.unsubscribe?.();
    state.subs.delete(id);
  });

  // A `?prop:` gate is pushed by the OWNER's prop setter, not by a store
  // subscription, so its registration lives in the owner's `_propDeps` and
  // has to be removed here too. Without this a gate discarded by a region
  // rebuild (renderBoundChildren: cleanupShowIfTree then replaceChildren)
  // stayed on the owner's list, and the next prop write ran the dead gate
  // beside the live one — appending the old element next to the new one.
  // `_propsRecorded` is cleared with it, so the reconnect replay
  // (_showIfRebinds) registers the gate again rather than finding it filed.
  const keptProps = (state.propDeps || []).filter((record) => {
    if (survives(record.entry)) return true;
    const list = record.owner?._propDeps?.get(record.propKey);
    const at = list ? list.indexOf(record.dep) : -1;
    if (at >= 0) list.splice(at, 1);
    if (record.entry) record.entry._propsRecorded = false;
    return false;
  });
  state.propDeps = keptProps;

  // Nothing survived: the state itself goes, exactly as it always did.
  if (!keptBinding && !keptProps.length) {
    state.subs.forEach((sub) => sub?.unsubscribe?.());
    state.subs.clear();
    state.bindings.clear();
    el._showIfState = null;
  }
}

// Highlight the option at `index` in a combobox/menu option list: flag it
// [focused] (CSS styles the active row), clear the others, and scroll it into
// view. Shared by select / auto-complete / mentions.
// Middle-elide a label too long to sit in a combobox display: keep the first and
// last `edge` characters and drop the middle. A path like
// "Somerset / Bruton / High Street" stays recognisable at both ends, which
// head-truncation ("Somerset / Bru…") does not — the distinguishing part of a
// path is usually its tail.
//
// The guard asks for a MEANINGFUL saving, not merely a shorter string. At
// break-even (edge*2+1) eliding "src / app.js" yields "src /…pp.js": one
// character narrower and much harder to read. Requiring a few characters of
// gain leaves borderline labels whole and still elides everything genuinely long.
const TRUNCATE_MIN_GAIN = 4;
function middleTruncate(text, edge = 5) {
  const str = String(text ?? '');
  if (str.length <= edge * 2 + TRUNCATE_MIN_GAIN) return str;
  return `${str.slice(0, edge)}…${str.slice(-edge)}`;
}

function highlightOption(items, index, scroll = true) {
  const list = Array.from(items);
  // Clear-all-then-set-one (not a per-index toggle) so a list that repeats the
  // same element reference stays correct.
  list.forEach((el) => el.removeAttribute('focused'));
  if (list[index]) {
    list[index].setAttribute('focused', '');
    if (scroll) list[index].scrollIntoView({ block: 'nearest' });
  }
}

// Register a capture-phase document click listener on behalf of `el` (the caller
// decides what counts as "outside" and what to do). Stored on the element so
// removeOutsideClick can tear it down on disconnect — centralising the add/remove
// pair so a component can't leak the document listener.
function onOutsideClick(el, handler) {
  el._outsideClick = handler;
  document.addEventListener('click', handler, true);
}

function removeOutsideClick(el) {
  if (!el._outsideClick) return;
  document.removeEventListener('click', el._outsideClick, true);
  el._outsideClick = null;
}

// Read the `static` marker off every gate that still has an element. An
// attribute binding can add or remove it after the gate was built, and the
// element is the renderer's own answer — so a retention decision (which
// elements and which ANCHORS a list rebuild keeps) is made on what is true
// now rather than on what the def said once.
function refreshShowIfStatic(state) {
  if (!state) return;
  const touch = (entry) => {
    if (entry && entry.el && typeof entry.el.hasAttribute === 'function') {
      entry.static = entry.el.hasAttribute('static');
    }
  };
  state.bindings.forEach((entries) => entries.forEach(touch));
  (state.propDeps || []).forEach((record) => touch(record.entry));
}

function cleanupShowIfTree(root, keep) {
  if (!root) return;
  // An element the caller is keeping is not being discarded: its gates, its
  // template subscriptions and everything under it stay as they are.
  if (keep && keep.has(root)) return;
  if (root._showIfState) cleanupShowIfState(root, keep);
  if (root._templateAttrSubs) {
    root._templateAttrSubs.forEach((sub) => { if (sub.unsubscribe) sub.unsubscribe(); });
    root._templateAttrSubs = [];
  }
  const lightChildren = Array.from(root.children || []);
  const shadowChildren = root.shadowRoot ? Array.from(root.shadowRoot.children || []) : [];
  lightChildren.concat(shadowChildren).forEach((child) => {
    // The keep set belongs to THIS element's own gates. A child in it is
    // retained and left alone; every other child is being discarded, so it
    // is cleaned in full — a `static` marker inside a discarded row does not
    // make that row survive, and its gates must not outlive it.
    if (keep && keep.has(child)) return;
    cleanupShowIfTree(child);
  });
}

function getByPath(obj, path) {
  if (!path || typeof path !== 'string') return undefined;
  return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
}

// The dotted-path setter, the write half of getByPath: creates intermediate
// null-prototype objects and assigns the leaf, returning the target (or the
// value itself for an empty path). Shared by the client's internal store
// (`a.b.c` keys) and the server's model planner (a provider's nested wire
// spellings such as `output_config.effort`), so there is one implementation.
function setByPath(target, path, value) {
  const parts = String(path).split('.').filter(Boolean);
  if (parts.length === 0) return value;
  let current = target;
  parts.slice(0, -1).forEach((key) => {
    current = current[key] && typeof current[key] === 'object' ? current[key] : (current[key] = Object.create(null));
  });
  current[parts[parts.length - 1]] = value;
  return target;
}

// Resolve a row-interpolated store key: each dot-segment starting with ^ is
// replaced by that field's value from the row, so `t.nav.^key` over
// { key: 'home' } resolves to `t.nav.home`. Shared by the client binder and
// the SSR serializer — one resolver, so the two cannot drift. Returns null
// when a ^field is absent from the row (nothing sane to bind to), and the
// key unchanged when it has no ^ at all.
function resolveRowKey(key, row) {
  if (typeof key !== 'string' || !key.includes('^')) return key;
  let missing = false;
  const resolved = key.split('.').map((segment) => {
    if (segment[0] !== '^') return segment;
    const value = getByPath(row, segment.slice(1));
    if (value == null || value === '') { missing = true; return segment; }
    return String(value);
  }).join('.');
  return missing ? null : resolved;
}

function applyBinding(el, binding, data) {
  if (binding.type === 'dataSource') {
    // Row-interpolated :dataSource (t.nav.^key) — deferred like ^label, bound
    // when the row arrives. The bind closure lives in create-component.js,
    // which owns the store and bindStoreToElement.
    binding.bind(el, binding.key, data);
    return;
  }

  const value = binding.field ? getByPath(data, binding.field) : binding.value;

  if (binding.type === 'text') {
    setTextContent(el, value);
    return;
  }

  if (binding.type === 'prop') {
    el[binding.name] = value;
    return;
  }

  if (binding.type === 'attr') {
    setAttrValue(el, binding.name, value);
  }
}

function applyBindingsRecursive(root, data, isRoot = true) {
  if (!root) return;
  if (Array.isArray(root._bindings)) {
    root._bindings.forEach((binding) => applyBinding(root, binding, data));
  }
  // A nested list owns its own item subtree: its stamped items are bound against
  // their own row data, not this owner's. Its own bindings (e.g. &data feeding the
  // list from an item field) are applied above; descending further would re-bind
  // those items against the owner's data and wipe them. Stop here.
  //
  // The test is "currently rendering items", NOT "declares an itemRenderer".
  // A component can be both a list and an item — s-tree is exactly that, using
  // itemRenderer: 's-tree' to recurse — and in node mode its children are its own
  // template (s-tw, s-cbx, s-ic, s-l), not somebody else's rows. Guarding on the
  // declaration alone returned before reaching them, so ['s-l', '^label'] never
  // bound and every tree node rendered a blank label. The icon survived only
  // because $n=nodeIcon is an internal-store binding applied by another path.
  //
  // A list rendering items holds an ARRAY in _data; an item holds its own row.
  // That is what separates the two modes, and a nested list not yet fed has no
  // stamped children to protect.
  if (root.itemRenderer && Array.isArray(root._data)) return;
  Array.from(root.children || []).forEach((child) => applyBindingsRecursive(child, data));
}

// Cascade activation through an element tree (walks through non-SWC elements)
function cascadeActivate(el, activate) {
  if (typeof el.activate === 'function') {
    if (activate) el.activate();
    else el.deactivate();
    return;
  }
  // Walk through non-SWC elements to find custom element descendants
  for (const child of el.children || []) {
    cascadeActivate(child, activate);
  }
}

// Frame-batched measure/write for components that read their own geometry and
// then write styles from it. Run inline, several instances interleave
// read-write-read during upgrade, and every read after a write forces a
// synchronous reflow (the segmented pill alone did this once per instance).
// Queued, one rAF runs EVERY pending measure against a single clean layout,
// then every write. Trade-off: the write lands a frame later — fine for
// decorative geometry (highlight pills, column counts), wrong for anything
// the user must see in the same frame.
let _mwQueue = null;
function measureThenWrite(measure, write) {
  if (typeof requestAnimationFrame !== 'function') { write(measure()); return; }
  if (!_mwQueue) {
    _mwQueue = [];
    requestAnimationFrame(() => {
      const queue = _mwQueue;
      _mwQueue = null;
      const measured = queue.map((entry) => entry.measure());
      queue.forEach((entry, i) => entry.write(measured[i]));
    });
  }
  _mwQueue.push({ measure, write });
}
return { setRenderingComponent, getRenderingComponent, toKebabCase, setAttrValue, setTextContent, middleTruncate, highlightOption, onOutsideClick, removeOutsideClick, refreshShowIfStatic, cleanupShowIfTree, getByPath, setByPath, resolveRowKey, applyBinding, applyBindingsRecursive, cascadeActivate, measureThenWrite };
})();

const __m10 = (() => {
const normalizeBindingName = __m8.normalizeBindingName;
const dataSinkFor = __m2.dataSinkFor;
const toKebabCase = __m9.toKebabCase;
// Permission guards — what a user may see and do, decided as DATA.
//
// READ THIS FIRST: a guard is not a security boundary. It decides AFFORDANCES;
// the endpoint decides authorisation. The same rule name keys both, so drift
// becomes a missing case rather than an invisible one. The documented failure
// this exists to avoid is Salesforce field-level security — auto-enforced in
// the standard UI, silently not in Apex.
//
// The consequence that makes generated permission-aware UI safe: a component
// naming the WRONG guard produces a UX bug, never a breach. Preserve that.
//
// Six outcomes, not a boolean. Verified against SAP Fiori, IBM Carbon and
// HashiCorp Helios, which converge on the same vocabulary:
//
//   enabled                — may see and act
//   hidden                 — no permission. THE DEFAULT FOR DENIAL in all three
//   readOnly               — may see, may not modify
//   redact                 — may know it exists, may not see the value
//   disabled:precondition  — temporary, USER-RESOLVABLE (needs a reason)
//   disabled:busy          — in flight; pairs with an idempotency key
//   conflict               — submitted and version-rejected; needs reconciling
//
// `disabled` is NEVER the permission case. Fiori is explicit: "Do not disable
// line item actions. If an action cannot be used, hide it." A greyed-out
// control that will never become available teaches the user nothing and is
// unreachable by assistive technology.





const GUARD_STATES = Object.freeze([
    'enabled',
    'hidden',
    'readOnly',
    'redact',
    'disabled:precondition',
    'disabled:busy',
    'conflict',
]);

// Denial defaults to `hidden` — the position all three design systems take.
const DEFAULT_DENIED = 'hidden';

const STATE_SET = new Set(GUARD_STATES);

// A guard reference is a NAME, never a closure: `guard="order.delete"`, with an
// optional denial outcome, `guard="salary.view:redact"`. The name is what
// crosses the wire and what keys the server's rule of the same name — a closure
// can do neither.
//
// Returns { name, denied } or null when the token is not a guard reference.
function parseGuardToken(token) {
    if (typeof token !== 'string') return null;
    const raw = token.trim();
    if (!raw) return null;
    const colon = raw.indexOf(':');
    if (colon < 0) return { name: raw, denied: DEFAULT_DENIED };
    const name = raw.slice(0, colon);
    const denied = raw.slice(colon + 1);
    // An unrecognised denial outcome falls back to hiding rather than rendering
    // the control as though it were permitted. Fail closed on a typo.
    return { name, denied: STATE_SET.has(denied) && denied !== 'enabled' ? denied : DEFAULT_DENIED };
}

// A rule may answer in any of three shapes, because all three are natural:
//   true / false            — permitted or not, use the reference's `denied`
//   'readOnly' | 'hidden'   — a state directly
//   { state, reason }       — a state plus the reason a disabled control needs
//
// Anything else is treated as a denial. Undefined is not permission.
function normaliseOutcome(result, denied) {
    const fallback = denied || DEFAULT_DENIED;
    if (result === true) return { state: 'enabled' };
    if (result === false || result === null || result === undefined) return { state: fallback };
    if (typeof result === 'string') {
        return STATE_SET.has(result) ? { state: result } : { state: fallback };
    }
    if (typeof result === 'object') {
        const state = STATE_SET.has(result.state) ? result.state : fallback;
        const out = { state };
        if (result.reason) out.reason = String(result.reason);
        // A REMEDY is the named fix for a denial — `auth.signIn`, `auth.stepUp`,
        // `access.justify`. It is a field, not a seventh state: `reason` tells a
        // human why, `remedy` tells the UI what to run. Adding a state would
        // mean a state with a required companion field, which is a strictly
        // worse vocabulary than six orthogonal states plus an optional field.
        if (result.remedy) out.remedy = String(result.remedy);
        if (result.resume) out.resume = safeResume(result.resume);
        // A remedy you cannot reach is not a remedy. `hidden` renders nothing,
        // so there is no affordance to activate — an outcome that offers a fix
        // is promoted to `redact`, which renders a mask the user can act on.
        if (out.remedy && out.state === 'hidden') out.state = 'redact';
        return out;
    }
    return { state: fallback };
}

// A resume target must be a same-origin PATH. It arrives from the server inside
// a verdict and is later navigated to, so an unchecked value is an open
// redirect in a security feature.
//
// Django's `next` has taken a decade of attacks and its rules are the ones worth
// copying: validate against the request's own origin, and on anything unsafe
// fall back SILENTLY rather than erroring — a poisoned target degrades to the
// default landing page instead of becoming a visible failure to probe.
function safeResume(target) {
    if (typeof target !== 'string' || !target) return null;
    const raw = target.trim();
    // Must be a rooted path. This rejects absolute URLs ("https://evil.test"),
    // scheme-relative ones ("//evil.test" — a path that is not a path), and
    // backslash variants that some parsers normalise into a host.
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
    // Control characters and whitespace: a newline or tab can split a
    // header or slip past a naive prefix check.
    if (/[\u0000-\u0020\u007f]/.test(raw)) return null;
    if (/^\/+[a-z][a-z0-9+.-]*:/i.test(raw)) return null;
    return raw;
}

// Resolve one guard.
//
// Two sources, decided by where the answer can honestly come from:
//
//   SUBJECT-BOUND ("may I delete THIS record") — the server already knows, and
//   shipped the answer inline with the row as `_guards`. Preferred whenever
//   present: it is one round trip for the whole table rather than N permission
//   calls, and the client cannot talk itself into a different answer.
//
//   AMBIENT (role, capability, feature flag) — evaluates locally from the
//   session, and identically during SSR, because the session is present on both
//   sides. This is what keeps first paint and hydration agreeing.
//
// ctx: { rules, user, store, data }
//   rules — { [name]: (ctx) => outcome }, the app's named rules
//   data  — the row/record, whose `_guards` the server may have populated
//
// Fails closed. An unknown rule name, a thrown rule, or a malformed answer all
// deny — because the alternative is an affordance appearing when nobody
// authorised it.
function resolveGuard(reference, ctx = {}) {
    const ref = typeof reference === 'string' ? parseGuardToken(reference) : reference;
    // A nameless reference DENIES. This is only reached when a guard was
    // declared — the render paths call this solely on an existing `@guard=`
    // token, and the grid solely on a truthy `col.guard` — so an empty or
    // whitespace name means a guard was intended and did not resolve. Returning
    // `enabled` here was a fail-OPEN sitting inside a fail-closed evaluator:
    // `@guard=` with an interpolated empty value rendered the control live.
    if (!ref || !ref.name) return { state: DEFAULT_DENIED, missing: true };

    // ── Source 1: the server-shipped, subject-bound verdict on the record ──
    //
    // Most specific wins, but only when it is answering about the RIGHT subject
    // and only when it actually answers.
    const guards = ctx.data && ctx.data._guards;

    // Subject stamp. A payload fetched for one user must not decide affordances
    // for another — the mid-session identity change (sign in as a different
    // account mid-flow) is exactly the case that produces rows computed for the
    // previous subject. Unstamped payloads are accepted, so this is
    // forward-compatible with servers that do not emit it yet.
    if (guards && guards._subject !== undefined) {
        const subject = ctx.user && (ctx.user.id ?? ctx.user.accountId);
        if (subject === undefined || subject !== guards._subject) {
            return { state: ref.denied || DEFAULT_DENIED, staleSubject: true };
        }
    }

    const bound = guards ? guards[ref.name] : undefined;
    // `defer` is not an outcome — it is the record declining to answer, so the
    // type-level sources below get their turn. Without it, `false` and "ask
    // someone else" are the same token, and "you may not create orders
    // generally, but you own this one" is inexpressible.
    if (bound !== undefined && bound !== 'defer') return normaliseOutcome(bound, ref.denied);

    const rules = ctx.rules || {};
    const rule = rules[ref.name];
    // An unknown guard name is a deny, not an allow. A typo must not open a
    // control, and the missing case is then visible in testing.
    if (typeof rule !== 'function') return { state: ref.denied || DEFAULT_DENIED, missing: true };

    try {
        return normaliseOutcome(rule({ user: ctx.user, store: ctx.store, data: ctx.data, name: ref.name }), ref.denied);
    } catch {
        // Never let a throwing rule become an open door.
        return { state: ref.denied || DEFAULT_DENIED, failed: true };
    }
}

// Does this outcome mean the element is not rendered at all?
//
// `hidden` MUST mean the markup was never produced — not `display:none`. OWASP
// A01 names "modifying the HTML page" as a bypass, so a hidden affordance that
// ships its content to the browser is not hidden, it is merely inconvenient.
function isOmitted(outcome) {
    return !!outcome && outcome.state === 'hidden';
}

// The presentation attributes an outcome contributes. Never a style — the
// stylesheet owns appearance.
//
// Note what is NOT here: `redact` does not carry the value. Redaction removes
// the value from the markup and substitutes a mask; a component must never
// serialise the real value and cover it up.
function guardAttributes(outcome) {
    if (!outcome || (outcome.state === 'enabled' && !outcome.remedy)) return [];
    const attrs = [`@data-guard=${outcome.state}`];
    // An outcome offering a fix stays ACTIONABLE. It carries the remedy name so
    // the UI can run it, and deliberately skips aria-disabled below — a control
    // the user is being invited to press must not announce itself as disabled.
    if (outcome.remedy) {
        attrs.push(`@data-remedy=${outcome.remedy}`);
        if (outcome.resume) attrs.push(`@data-resume=${outcome.resume}`);
        return attrs;
    }
    switch (outcome.state) {
        case 'readOnly':
            attrs.push('@readonly=', '@aria-readonly=true');
            break;
        case 'redact':
            attrs.push('@aria-hidden=true');
            break;
        case 'disabled:precondition':
        case 'disabled:busy':
            // aria-disabled rather than the disabled property: a disabled
            // element is unreachable by assistive technology, and the user
            // still needs to be told why.
            attrs.push('@aria-disabled=true');
            if (outcome.reason) attrs.push(`@title=${outcome.reason}`);
            break;
        case 'conflict':
            attrs.push('@aria-invalid=true');
            break;
        default:
            break;
    }
    return attrs;
}

// ── Remedies ──
//
// A remedy is the named fix for a denial, registered exactly as a rule is:
// the component references a NAME, the app supplies the behaviour. Naming them
// is what makes step-up re-auth and reason-for-access the same mechanism as
// sign-in rather than three bespoke flows.
let REMEDIES = {};

function registerRemedies(remedies) {
    if (remedies && typeof remedies === 'object') REMEDIES = { ...REMEDIES, ...remedies };
    return REMEDIES;
}

function resetRemedies() { REMEDIES = {}; }

// The handler for an outcome's remedy, or null. Null is not an error — it means
// the app has not wired that remedy, and the affordance simply does not offer
// one, which is the same outcome as a plain denial.
function resolveRemedy(outcome) {
    const name = outcome && outcome.remedy;
    if (!name) return null;
    const fn = REMEDIES[name];
    return typeof fn === 'function' ? fn : null;
}

// ── The challenge payload ──
//
// Shape and key live here, beside the outcome vocabulary, because they are pure
// data and because both the render path and the HTTP layer need them without
// either pulling in the router. The orchestration — running the remedy and
// resuming — is core/services/challenge.js.
//
// The key is the router's existing one: `_navigationBlocked` already means "an
// intent was stopped and can be resumed" (router.js:412). A second key would be
// a second resume mechanism.
const CHALLENGE_KEY = '_navigationBlocked';

function challengeFor(outcome, options = {}) {
    const remedy = (outcome && outcome.remedy) || options.remedy;
    if (!remedy) return null;
    return {
        reason: 'access',
        remedy,
        // `targetUrl` is the router's field name for the same idea.
        targetUrl: safeResume((outcome && outcome.resume) || options.resume || null),
        message: (outcome && outcome.reason) || options.message || null,
    };
}

// The mask a redacted value renders instead of the value. Dataverse's masked
// column is the primary-source precedent for redaction as a distinct outcome.
const REDACTED_TEXT = '••••••';

// ── Token plumbing ──
//
// Both render paths (core/core/create-component.js, server/ssr/dsl-to-html.js)
// take a node's guards off its token list HERE, with one function, so the two
// sides cannot disagree about which authored forms count as a guard. They once
// did their own scan, and it matched only the first RAW string token: an
// object-token guard, a whitespace-prefixed one, or a second one on the same
// node rendered the affordance fully enabled with the guard sitting in the DOM
// as a stray attribute. A generator that emitted the object form believed it
// had gated something and had not.
//
// `@guard=` rather than a new prefix character on purpose: the guard needs no
// new vocabulary, it reads as what it is, and `!` — the obvious candidate — is
// already this codebase's canonical example of a prefix nothing owns, relied on
// by the diagnostics fixtures.
//
// This is the token's DEFINITION, not a use of it. An empty guard reference in
// authored DSL is a real finding — it would deny everything — so the lint rule
// stays strict and these lines opt out.
// swc-lint-ok
const GUARD_TOKEN = '@guard=';
const GUARD_KEY = '@guard';
const GUARD_NAME = GUARD_KEY.slice(1);   // what namesGuard scans for

function isObjectToken(token) {
    return !!token && typeof token === 'object' && !Array.isArray(token);
}

// The reference a token key names, or undefined when the key is not a guard.
//
// Anything that SPELLS a guard is one, including the spellings that are wrong:
// `@Guard=x` (case), a bare `@guard` with no value, `{ '@guard=x': undefined }`
// (the name folded into the key). Each of those used to fall through to the
// attribute path and render enabled with a stray attribute — which is the one
// thing a token the author wrote as a guard must never do. A malformed guard
// resolves to a reference no rule is registered under, or to an empty one,
// and both deny.
// Does this token name the guard attribute, without allocating?
//
// guardReferenceOf runs for EVERY string token of EVERY node in both render
// engines — the framework's hottest loop — and its first act was `key.trim()`,
// an allocation per token, followed by slice/trim/toLowerCase for anything
// starting with '@' (which is most attribute tokens). Guards are rare, so the
// common answer is "no": settle it by scanning characters instead.
//
// Exactly the predicate the parse below applies: optional whitespace, '@',
// optional whitespace, the letters of `guard` in any case, optional
// whitespace, then '=' or the end of the token.
const isWs = (c) => c === 32 || c === 9 || c === 10 || c === 13 || c === 12;

function namesGuard(key) {
    if (typeof key !== 'string') return false;
    const n = key.length;
    let i = 0;
    while (i < n && isWs(key.charCodeAt(i))) i += 1;
    if (key.charCodeAt(i) !== 64) return false;   // '@'
    i += 1;
    while (i < n && isWs(key.charCodeAt(i))) i += 1;
    // `| 32` lowercases ASCII letters; a non-letter can only match a letter's
    // code if it IS that letter's other case, so the comparison stays exact.
    for (let k = 0; k < GUARD_NAME.length; k += 1, i += 1) {
        if (i >= n || (key.charCodeAt(i) | 32) !== GUARD_NAME.charCodeAt(k)) return false;
    }
    while (i < n && isWs(key.charCodeAt(i))) i += 1;
    return i >= n || key.charCodeAt(i) === 61;    // '=' or nothing after
}

function guardReferenceOf(key, value) {
    // Fast bail before the first allocation — see namesGuard.
    if (!namesGuard(key)) return undefined;
    const spelled = key.trim();
    // The name is everything before the first `=`, trimmed — so `@guard = x`
    // and `@guard =x` are the guard they spell, not an attribute named guard.
    if (spelled[0] !== '@') return undefined;
    const eq = spelled.indexOf('=');
    // `@ guard` and `@guard ` both name the attribute `guard` to the renderers.
    const name = (eq < 0 ? spelled : spelled.slice(0, eq)).slice(1).trim().toLowerCase();
    if (`@${name}` !== GUARD_KEY) return undefined;

    const reference = eq < 0 ? '' : spelled.slice(eq + 1).trim();
    const text = value === null || value === undefined ? '' : String(value);
    if (!text) return reference;
    return reference ? `${reference}=${text}` : text;
}


// Remove every guard reference from a node's token list and return them in
// authored order. Recognised wherever the DSL accepts an attribute token:
//
//   '@guard=order.delete'          a string token — trimmed, like every other
//                                  token the renderers apply
//   { '@guard': 'order.delete' }   an object-token key, alongside whatever
//                                  else the object carries
//
// String tokens are spliced out of `tokens`. An object token is REPLACED by a
// copy without its guard key (or removed when nothing else is in it) — never
// mutated, because the object is shared with the def it came from, and a def
// stripped of its guard on first render would re-render unguarded.
function spliceGuardReferences(tokens) {
    const references = [];
    let i = 0;
    while (i < tokens.length) {
        const token = tokens[i];
        if (typeof token === 'string') {
            const reference = guardReferenceOf(token, undefined);
            if (reference !== undefined) {
                references.push(reference);
                tokens.splice(i, 1);
                continue;
            }
        } else if (isObjectToken(token)) {
            let copy = null;
            for (const key of Object.keys(token)) {
                const reference = guardReferenceOf(key, token[key]);
                if (reference === undefined) continue;
                references.push(reference);
                if (!copy) copy = { ...token };
                delete copy[key];
            }
            if (copy) {
                if (Object.keys(copy).length) tokens[i] = copy;
                else { tokens.splice(i, 1); continue; }
            }
        }
        i += 1;
    }
    return references;
}


// Resolve every guard on one node to the single outcome the node renders with.
//
// All of them apply: the node is enabled only when each guard permits it. An
// omission (`hidden`) wins outright — nothing else can be seen when the markup
// is not produced. Otherwise the first non-enabled outcome in authored order is
// the one applied, so a node that is both `readOnly` by one rule and
// `disabled:precondition` by another reports whichever its author wrote first.
// There is deliberately no severity lattice across the remaining states: none
// of the three design systems the vocabulary comes from defines one, and
// inventing it here would be a decision the author can make by ordering.
//
// Among enabled outcomes, one that carries a remedy is the one kept, wherever
// it was authored — a remedy is an affordance (`data-remedy`), and its presence
// must not depend on token order. A denial replaces it: the node then renders
// the denial, and the denial's own remedy, if any, is the one offered.
//
// Two guards on one node is unusual authoring — a control does one thing — but
// the runtime meaning must still be closed, not "the second one is ignored".
function resolveGuards(references, ctx = {}) {
    let outcome = null;
    for (const reference of references) {
        const one = resolveGuard(reference, ctx);
        if (isOmitted(one)) return one;
        if (!outcome) { outcome = one; continue; }
        if (outcome.state !== 'enabled') continue;
        if (one.state !== 'enabled' || (one.remedy && !outcome.remedy)) outcome = one;
    }
    return outcome || { state: 'enabled' };
}


// Under `redact`, strip the node down to an INERT mask — nothing on it carries
// data, nothing on it displays, nothing on it acts — and write the mask into
// the element's own sink, so the mask is the only value the node ever shows,
// on either side of hydration.
//
// What goes:
//   every binding, whatever it targets —
//     '=literal'  '^field'  '$key'                 text
//     '$attr=key'  '%attr=field'  '@attr=$key'     attribute bindings
//     '&prop=field'  ':prop=$key'                  property bindings
//     ':dataSource='  ':dataKey='  ':data'         data-sink bindings
//     '#parentProp[:childProp]'  '*src[:dst]'      projections and pass-throughs
//     ':textContent=' ':innerHTML=' ':innerText='  content written as a prop
//   the subtree                                    [ …children ]
//   every `:` prop except `:ref` and `:tag` — a composite's `:items`,
//     `:options`, `:series`, `:markdown`, `:min`/`:max` ARE its value, and a
//     mask needs no configuration
//   every attribute a composite DECLARES (props/attrs — declaredAttributeNames):
//     `@headline=£185,000` on a card is the value in another spelling, and the
//     card's own template would render it inside the mask
//   every attribute, static or not, on ANY element, that
//     holds the value        value checked selected content datetime data open
//                            min max step low high optimum start, and the ARIA
//                            twins aria-checked/-selected/-pressed/-expanded/
//                            -valuenow/-valuemin/-valuemax/-valuetext
//     displays text          label placeholder alt title srcdoc, and the ARIA
//                            text aria-label/-description/-placeholder/
//                            -roledescription/-keyshortcuts/-braille*
//     references a resource  src srcset poster href xlink:href action
//                            formaction download ping cite code style
//     carries data           data-* (the guard's own data-* are added after)
//   every event '~…' — unless the outcome carries a REMEDY, in which case the
//   node is the remedy's affordance and keeps its events. The remedy is
//   activated through the node's own `~` events; a composite's delegated
//   `data-act` dispatch is data-* and goes.
// What stays is structural: `@name=`, `@type=`, `@rg=`, layout and tone
// attributes, id references (`aria-labelledby`, `for`, `list`), show-if,
// `:tag`.
//
// A static literal is stripped as readily as a binding, because a def is not
// always code an author typed: a converted static site inlines its content
// into defs, and there `@title=£185,000` IS the data. The rule cannot tell
// the two apart, so it does not try. A whitelist of what stays is not on
// offer either — the framework's layout, tone and state attributes are an
// open set, and a redacted node that lost its layout would be a different
// kind of wrong.
//
// Events go because a masked link that still navigates to the record, or a
// masked checkbox that still toggles, shows the value's shape while hiding its
// text. They stay for a remedy because the remedy is precisely "press this to
// get the value" — an outcome that offers a fix is promoted from hidden to
// redact for that reason (normaliseOutcome), and a mask nobody can press would
// undo the promotion.
//
// The mask goes where the value would have gone. A text element shows it as
// text. A text input gets it as `value`; a textarea as its content (that is a
// textarea's value); both get `readonly` (a value you may not see is not one
// you may edit). A select, or an input the browser sanitises (number, date,
// checkbox), shows EMPTY rather than the mask — there is no leak, just no
// glyph. Media loses its sources and gets the mask as `alt`. Every redacted
// node without a remedy gets `tabindex=-1`: it carries `aria-hidden`
// (guardAttributes), and an aria-hidden element must not sit in the tab order
// — a focusable composite would otherwise make itself tabbable after this
// pass. A remedy node is reachable, and carries no aria-hidden.
//
// This removes the AFFORDANCE. It does not make the value secret: data a user
// may not see must be redacted server-side and never serialised (rule 4 in
// docs/llm/10-guards.md), or it is in the page for anyone with the inspector.
const VALUE_NAMES = new Set([
    // holds the value
    'value', 'checked', 'selected', 'content', 'datetime', 'data', 'open',
    'min', 'max', 'step', 'low', 'high', 'optimum', 'start',
    'aria-checked', 'aria-selected', 'aria-pressed', 'aria-expanded',
    'aria-valuenow', 'aria-valuemin', 'aria-valuemax', 'aria-valuetext',
    // displays text
    'label', 'placeholder', 'alt', 'title', 'srcdoc',
    'aria-label', 'aria-description', 'aria-placeholder', 'aria-roledescription',
    'aria-keyshortcuts', 'aria-braillelabel', 'aria-brailleroledescription',
    // references a resource
    'src', 'srcset', 'poster', 'href', 'xlink:href', 'action', 'formaction', 'download', 'ping',
    'cite', 'code', 'style',
]);
// The one `:` prop a masked node still needs: which element it is. Everything
// else a prop carries is configuration or data, and a mask has no use for
// either — a composite's `:items`, `:options`, `:series` or `:markdown` ARE
// its value, and the client's object-token loop sets any prop it is given.
// `:ref` goes too: a ref is the owner's handle for feeding a node
// (`this.refs.list.data = rows` is the standard pattern), and nothing
// legitimately feeds a mask.
const STRUCTURAL_PROPS = new Set(['tag']);

function isRedactedToken(key, value, keepEvents) {
    const prefix = key[0];
    if (prefix === '~') return !keepEvents;
    if ('=^$%&#*'.includes(prefix)) return true;
    if (prefix === ':') return !STRUCTURAL_PROPS.has(normalizeBindingName(key.slice(1).trim()));
    if (prefix !== '@') return false;
    if (typeof value === 'string' && value.trim()[0] === '$') return true;
    const name = key.slice(1).trim().toLowerCase();
    return VALUE_NAMES.has(name) || name.startsWith('data-');
}

function splitStringToken(token) {
    const trimmed = token.trim();
    // `=literal` is all value; for the rest the first `=` after the name ends it.
    const eq = trimmed.indexOf('=', 1);
    return eq < 0 ? [trimmed, undefined] : [trimmed.slice(0, eq), trimmed.slice(eq + 1)];
}

// The attribute names a component DECLARES as its inputs — `props: ['@headline']`
// reflects as `headline=`, `attrs: ['maxLen']` observes `max-len=` — lowercased
// in both spellings. A composite's declared attributes are its data by
// definition (`@headline=£185,000` is the value in the attribute's clothes),
// and they are not in any list of native names, so a redact pass strips them
// by asking the def. Accepts a client def or the SSR registry entry; both
// carry `props` and `attrs` under those names.
function declaredAttributeNames(def) {
    const names = new Set();
    if (!def || typeof def !== 'object') return names;
    const add = (name) => {
        if (typeof name !== 'string' || !name) return;
        const bare = name.replace(/^[@:]/, '');
        names.add(bare.toLowerCase());
        names.add(toKebabCase(bare).toLowerCase());
    };
    (Array.isArray(def.props) ? def.props : []).forEach(add);
    (Array.isArray(def.attrs) ? def.attrs : []).forEach(add);
    return names;
}

// `tag` is the HTML tag the node serialises as — a customized built-in's
// native tag, or the tag itself — which decides the sink the mask goes to.
// `keepEvents` is set when the outcome carries a remedy. `declared` is the
// set from declaredAttributeNames for a composite, empty for a native tag.
function redactTokens(tokens, tag = '', { keepEvents = false, declared = null } = {}) {
    const stripped = (key, value) => isRedactedToken(key, value, keepEvents)
        || (key[0] === '@' && !!declared && declared.has(key.slice(1).trim().toLowerCase()));
    for (let i = tokens.length - 1; i >= 0; i -= 1) {
        const token = tokens[i];
        if (Array.isArray(token)) { tokens.splice(i, 1); continue; }
        if (typeof token === 'string') {
            if (token.trim() && stripped(...splitStringToken(token))) tokens.splice(i, 1);
            continue;
        }
        if (!isObjectToken(token)) continue;
        const keys = Object.keys(token).filter((key) => stripped(key.trim(), token[key]));
        if (!keys.length) continue;
        const copy = { ...token };
        keys.forEach((key) => delete copy[key]);
        if (Object.keys(copy).length) tokens[i] = copy;
        else tokens.splice(i, 1);
    }
    const sink = dataSinkFor(tag);
    if (tag === 'textarea') tokens.push(`=${REDACTED_TEXT}`, '@readonly=');
    else if (sink === 'value') tokens.push(`@value=${REDACTED_TEXT}`, '@readonly=');
    else if (sink === 'attr:src') tokens.push(`@alt=${REDACTED_TEXT}`);
    else tokens.push(`=${REDACTED_TEXT}`);
    // A redacted node carries `aria-hidden` (guardAttributes), and an
    // aria-hidden element must not be reachable by keyboard — on ANY tag,
    // because a composite makes itself focusable after this pass (SSR and the
    // client both set tabindex=0 if absent). A remedy node is the opposite: it
    // is the one thing here the user must be able to reach, and it carries no
    // aria-hidden.
    // `inert` takes the whole SUBTREE out of focus, pointer events and the
    // accessibility tree — the host's own tabindex cannot speak for content a
    // composite builds inside itself, and `aria-hidden` on a host with
    // focusable descendants is the axe violation. tabindex=-1 rides along for
    // engines without `inert`: there the host itself is still out of the tab
    // order, and a redacted host has no descendants on either side.
    if (!keepEvents) tokens.push('@tabindex=-1', '@inert=');
}

// ── The named-rule registry ──

//
// The app declares its rules once, by name. A component references the NAME.
// This is the boundary that crosses the wire and that keys the server's rule of
// the same name; a closure can do neither, and element identity should not.
//
// Module-level on purpose, and safe on the server: rules are static app
// configuration, pure functions of the context handed to them. Per-request
// state — who the user is — arrives from the per-request store instead, which
// is what lets SSR and the client reach the same verdict.
let REGISTRY = {};

function registerGuardRules(rules) {
    if (rules && typeof rules === 'object') REGISTRY = { ...REGISTRY, ...rules };
    return REGISTRY;
}

function guardRules() { return REGISTRY; }

// Test seam. Not for application use.
function resetGuardRules() { REGISTRY = {}; }

// Build the evaluation context from the store, so both render paths read the
// session from the same place and cannot disagree at hydration.
//
// The user is read from a store key rather than captured in a closure — that is
// what makes an ambient rule evaluable during SSR at all.
function guardContext(store, data, userKey = 'session.user') {
    const user = store && typeof store.getValue === 'function' ? store.getValue(userKey) : undefined;
    return { rules: REGISTRY, user, store, data };
}

// Bulk actions: per-item evaluation with a partial-result channel.
//
// Fiori keeps a batch action ENABLED over a mixed-permission selection and
// reports "X of Y items cannot be processed" — it does not gate the button.
// Gating it would leave the user unable to act on the items they DO control,
// with no explanation of why.
function evaluateBulk(reference, items, ctx = {}) {
    const list = Array.isArray(items) ? items : [];
    const allowed = [];
    const denied = [];
    for (const item of list) {
        const outcome = resolveGuard(reference, { ...ctx, data: item });
        (outcome.state === 'enabled' ? allowed : denied).push(item);
    }
    return {
        // The action stays available while anything at all can be acted on.
        state: allowed.length ? 'enabled' : DEFAULT_DENIED,
        allowed,
        denied,
        partial: denied.length > 0 && allowed.length > 0,
        // Pre-formatted because every caller needs the same sentence.
        summary: denied.length ? `${denied.length} of ${list.length} items cannot be processed` : null,
    };
}
return { GUARD_STATES, DEFAULT_DENIED, parseGuardToken, normaliseOutcome, safeResume, resolveGuard, isOmitted, guardAttributes, registerRemedies, resetRemedies, resolveRemedy, CHALLENGE_KEY, challengeFor, REDACTED_TEXT, GUARD_TOKEN, spliceGuardReferences, resolveGuards, declaredAttributeNames, redactTokens, registerGuardRules, guardRules, resetGuardRules, guardContext, evaluateBulk };
})();

const __m11 = (() => {
// Virtual scroll decorator — standalone, applied when element has virtualHeight.
// Layout via [virtual-scroll] attribute + .vs-spacer/.vs-wrapper classes in base.css.

function applyVirtualScroll(element, { itemHeight, overscan = 5, createItem }) {
  const spacer = document.createElement('div');
  spacer.className = 'vs-spacer';

  const wrapper = document.createElement('div');
  wrapper.className = 'vs-wrapper';

  element.setAttribute('virtual-scroll', '');
  element.appendChild(spacer);
  element.appendChild(wrapper);

  element._virtualState = {
    spacer,
    wrapper,
    pool: [],
    itemHeight,
    overscan,
    createItem,
    startIdx: -1,
    endIdx: -1,
  };

  element.addEventListener('scroll', () => updateVirtual(element), { passive: true });
  return element._virtualState;
}

function updateVirtual(element) {
  const state = element._virtualState;
  const items = element._data;
  if (!state || !items?.length) return;

  const { itemHeight, overscan, pool, wrapper, spacer, createItem } = state;

  spacer.style.height = `${items.length * itemHeight}px`;

  const scrollTop = element.scrollTop;
  const viewHeight = element.clientHeight;

  const startIdx = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const visibleCount = Math.ceil(viewHeight / itemHeight);
  const endIdx = Math.min(items.length, startIdx + visibleCount + overscan * 2);

  if (startIdx === state.startIdx && endIdx === state.endIdx) return;
  state.startIdx = startIdx;
  state.endIdx = endIdx;

  wrapper.style.transform = `translateY(${startIdx * itemHeight}px)`;

  const needed = endIdx - startIdx;

  // Grow pool if needed
  while (pool.length < needed) {
    const el = createItem();
    el.style.height = `${itemHeight}px`;
    pool.push(el);
    wrapper.appendChild(el);
  }

  // Update visible items
  pool.forEach((el, i) => {
    const dataIndex = startIdx + i;
    if (dataIndex < endIdx) {
      el.hidden = false;
      el._index = dataIndex; // property, not a data-index attribute (see list-mixin)
      if (el._data !== items[dataIndex]) {
        el.data = items[dataIndex];
      }
    } else {
      el.hidden = true;
    }
  });

  // Infinite scroll trigger: near bottom
  if (element._storeKey && endIdx >= items.length - overscan) {
    const entry = element.store?.getValue(`${element._storeKey}.loadMore`);
    if (entry?.hasMore && entry.fn) entry.fn();
  }
}
return { applyVirtualScroll, updateVirtual };
})();

const __m12 = (() => {
// Infinite scroll decorator — standalone, applied when store has loadMore entry.
// Uses WeakMap for state so sentinel survives replaceChildren.
// Detects scroll root automatically (element if scrollable, viewport otherwise).

const scrollState = new WeakMap();

function applyInfiniteScroll(element, loadMoreFn) {
  // Clean up existing before re-applying
  removeInfiniteScroll(element);

  const isScrollable = ['auto', 'scroll'].includes(getComputedStyle(element).overflowY);
  const root = isScrollable ? element : null;

  const sentinel = document.createElement('div');
  sentinel.setAttribute('static', '');
  sentinel.style.height = '1px';

  const observer = new IntersectionObserver((entries) => {
    entries
      .filter(e => e.isIntersecting)
      .forEach(() => loadMoreFn());
  }, { root, rootMargin: '200px' });

  element.appendChild(sentinel);
  observer.observe(sentinel);

  scrollState.set(element, { sentinel, observer });
}

function updateSentinelPosition(element) {
  const state = scrollState.get(element);
  if (!state?.sentinel) return;
  // Move sentinel to end of children (after newly appended items)
  element.appendChild(state.sentinel);
}

function removeInfiniteScroll(element) {
  const state = scrollState.get(element);
  if (!state) return;
  state.observer.disconnect();
  state.sentinel.remove();
  scrollState.delete(element);
}

function hasInfiniteScroll(element) {
  return scrollState.has(element);
}
return { applyInfiniteScroll, updateSentinelPosition, removeInfiniteScroll, hasInfiniteScroll };
})();

const __m13 = (() => {
function unquote(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function parseLiteral(value) {
  const raw = unquote(value);
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(raw)) return Number(raw);
  return raw;
}

function parseValue(value) {
  if (typeof value !== 'string') return value;
  const raw = value.trim();
  if (raw.startsWith('{') || raw.startsWith('[')) {
    try {
      return JSON.parse(raw);
    } catch (error) {
      return parseLiteral(value);
    }
  }
  return parseLiteral(value);
}

function parseAssignment(value) {
  if (typeof value !== 'string') return null;
  const idx = value.indexOf('=');
  if (idx === -1) return null;
  return {
    name: value.slice(0, idx).trim(),
    raw: value.slice(idx + 1).trim()
  };
}
return { unquote, parseLiteral, parseValue, parseAssignment };
})();

const __m14 = (() => {
const getByPath = __m9.getByPath;
const parseLiteral = __m13.parseLiteral;
const EVENT_MAP = {
  cl: 'click',
  in: 'input',
  ch: 'change',
  kd: 'keydown',
  ku: 'keyup',
  sel: 'selected',
  itcl: 'itemclick',
  it2cl: 'itemdblclick',
};

function resolveEventName(short) {
  return EVENT_MAP[short] || short;
}

function resolveActionValue(token, ctx = {}) {
  if (!token) return undefined;
  if (token === '$value') return ctx.event?.target?.value;
  if (token === '$checked') return Boolean(ctx.event?.target?.checked);
  if (token === '$index') return ctx.index;
  if (token === '$item') return ctx.item;
  if (token === '$event') return ctx.event;
  if (token.startsWith('$detail.')) return getByPath(ctx.event?.detail, token.slice(8));
  if (token.startsWith('$dataset.')) return ctx.event?.target?.dataset?.[token.slice(9)];
  if (token.startsWith('^')) return getByPath(ctx.item || ctx.data, token.slice(1));
  if (token.startsWith('{') || token.startsWith('[')) {
    try {
      return JSON.parse(token);
    } catch (error) {
      return token;
    }
  }
  return parseLiteral(token);
}

function resolveActionTarget(target, context = {}) {
  if (!target) return null;
  if (target.startsWith('store:')) {
    return { store: context.store, key: target.slice(6) };
  }
  if (target.startsWith('data:')) {
    return { store: context.internalStore, key: target.slice(5) };
  }
  return { store: context.store, key: target };
}
return { EVENT_MAP, resolveEventName, resolveActionValue, resolveActionTarget };
})();

const __m15 = (() => {
const resolveActionValue = __m14.resolveActionValue;
const router = __m7.router;
// A nav token is a real destination, so it is written as one.
//
// `~cl:nav:/docs` installs a click listener that calls the router. That is
// enough for a left click and for nothing else: the cursor, the middle click,
// the context menu, "open in new tab", a crawler and assistive technology all
// read an href, and none of them read a listener. The SSR serializer already
// emits one (server/ssr/dsl-to-html.js, case '~') for exactly that reason, so
// a client that does not would take it back off the page the server sent.
//
// Its own module because two callers need it and they sit on opposite sides of
// an import: create-component binds the token, and list-mixin re-applies it to
// a row once that row has data (see applyNavHrefs for why that second pass is
// not optional).



/** The row data an element sits in, or undefined. Walks up, nearest wins. */
function getItemData(el) {
  let current = el;
  while (current) {
    if (current._data !== undefined && current._data !== null) return current._data;
    current = current.parentElement || current.parentNode;
  }
  return undefined;
}

// The token, kept on the element so a later pass can finish the job.
const TOKEN = '__swcNav';

/**
 * The URL a nav token points at, or '' when it cannot be built yet.
 *
 * A ^field token resolves from the row the element sits in. When that row is
 * not there yet this answers '' and the caller leaves the element alone: no
 * href is bad, and an href to the WRONG place is worse.
 */
function hrefFor(el, target, valueToken, ctx) {
  const base = target || '';
  let bare = base || '/';
  if (valueToken) {
    const item = (ctx && ctx.item) || getItemData(el);
    const value = resolveActionValue(valueToken, { ...(ctx || {}), item, data: item });
    if (value === undefined || value === null || value === '') return '';
    bare = base + value;
  }
  // The router's own outbound builder: locale prefix, locale query params and
  // slug translation, so the href is exactly where the click would land rather
  // than a second implementation of the same rules, free to drift from it.
  try {
    if (router && typeof router._buildUrl === 'function') {
      const built = router._buildUrl(bare);
      if (built) return built;
    }
  } catch {
    // No router, or one with no i18n descriptor: the bare path is still a
    // correct destination, so this is a fallback rather than a failure.
  }
  return bare;
}

// Write it where the element keeps it. A native <a> takes the attribute; a
// component that declares an `href` prop (s-ln) takes the property, and its
// own onHrefChange forwards it to the inner <a> it owns. An element whose
// definition has not landed yet takes the attribute, which is what the prop
// getter reads once it upgrades.
function write(el, url) {
  if (el.tagName === 'A') return el.setAttribute('href', url);
  if ('href' in el) { el.href = url; return; }
  if (el.localName && el.localName.includes('-')) el.setAttribute('href', url);
}

/**
 * Bind a nav token's href, now if it can be resolved and later if it cannot.
 *
 * A list row's children are bound BEFORE the row is given its data, so a
 * ^field token resolves to nothing on this pass. The token is therefore kept
 * on the element and applyNavHrefs finishes it once the data has landed.
 */
function bindNavHref(el, target, valueToken, ctx) {
  if (valueToken) el[TOKEN] = { target, valueToken };
  const url = hrefFor(el, target, valueToken, ctx);
  if (url) write(el, url);
}

/**
 * Second pass over a subtree whose row data has now arrived.
 *
 * Called by list-mixin after the row is in the tree with its data set. Without
 * it every list-stamped link is destination-less — which is most of the links
 * in a real app, because a nav bar is a list.
 */
function applyNavHrefs(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  const pending = [root, ...root.querySelectorAll('*')].filter((el) => el[TOKEN]);
  pending.forEach((el) => {
    const { target, valueToken } = el[TOKEN];
    const url = hrefFor(el, target, valueToken, null);
    if (url) write(el, url);
  });
}
return { getItemData, bindNavHref, applyNavHrefs };
})();

const __m16 = (() => {
const setAttrValue = __m9.setAttrValue;
const applyBindingsRecursive = __m9.applyBindingsRecursive;
const cleanupShowIfTree = __m9.cleanupShowIfTree;
const refreshShowIfStatic = __m9.refreshShowIfStatic;
const setRenderingComponent = __m9.setRenderingComponent;
const getRenderingComponent = __m9.getRenderingComponent;
const applyVirtualScroll = __m11.applyVirtualScroll;
const updateVirtual = __m11.updateVirtual;
const applyInfiniteScroll = __m12.applyInfiniteScroll;
const updateSentinelPosition = __m12.updateSentinelPosition;
const hasInfiniteScroll = __m12.hasInfiniteScroll;
const resolveUnknownTag = __m4.resolveTag;
const markUnregistered = __m4.markUnregistered;
const applyNavHrefs = __m15.applyNavHrefs;
// List mixin — adds data-driven list rendering to components.
// Applied automatically when component definition has itemRenderer or itemRendererField.







function applyListMixin(proto, { itemRenderer, itemRendererField }) {
  // Init function called from constructor
  proto._initList = function () {
    // An SSR'd instance override (:itemRenderer) is recorded pre-upgrade by
    // hydrate.js; prefer it over the definition default so a hydrated list stamps
    // the same renderer the server did instead of the built-in default.
    this.itemRenderer = this._ssrItemRenderer || itemRenderer || null;
    this.itemRendererField = this._ssrItemRendererField || itemRendererField || null;
    this._itemTemplate = null;
    this._appendStartIndex = 0;
  };

  // Override data setter with append support
  Object.defineProperty(proto, 'data', {
    get() { return this._data; },
    set(value) {
      // A redacted list host takes no rows (see the base setter).
      if (this._redacted) return;
      if (this._appendMode && Array.isArray(this._data) && Array.isArray(value)) {
        this._appendStartIndex = this._data.length;
        this._data = [...this._data, ...value];
        this._appendMode = false;
      } else {
        this._appendStartIndex = 0;
        this._data = value;
      }
      if (this._isConnected) this.render();
    },
    enumerable: true,
    configurable: true,
  });

  // itemAttrs: list-level config stamped onto every item element — the item
  // renderer's ATTRIBUTES, not its data. Accepts an object ({ l: 'horizontal' })
  // or a compact DSL-style token string ('@l=horizontal @sz=sm'; the leading @
  // is optional, a valueless token becomes a presence attribute). This is what
  // lets a generic component be the renderer without a wrapper def: a list of
  // lists with itemAttrs '@l=horizontal' IS a table.
  proto._applyItemAttrs = function (itemEl) {
    const config = this.itemAttrs;
    if (!config) return;
    const entries = typeof config === 'string'
      ? config.trim().split(/\s+/).filter(Boolean).map((token) => {
          const bare = token.replace(/^@/, '');
          const eq = bare.indexOf('=');
          return eq === -1 ? [bare, true] : [bare.slice(0, eq), bare.slice(eq + 1) || true];
        })
      : Object.entries(config);
    entries.forEach(([key, value]) => setAttrValue(itemEl, key, value));
  };

  // showKeys: prepend a synthetic header element as element 0, built from the
  // FIRST row's keys — cell.key per cell for array rows (falling back to the
  // index), the property names themselves for object rows. It renders through
  // the same item renderer and itemAttrs as every data row so columns line up,
  // carries the [keys] attribute for styling, and is never selectable.
  proto._keysRowFor = function (row) {
    if (Array.isArray(row)) {
      return row.map((cell, i) => (cell && typeof cell === 'object'
        ? { text: String(cell.key ?? i), attrs: cell.attrs }
        : String(i)));
    }
    if (row && typeof row === 'object') {
      return Object.fromEntries(Object.keys(row).map((key) => [key, key]));
    }
    return '';
  };

  proto._resolveItemRenderer = function (item) {
    if (this.itemRendererField && item && typeof item === 'object') {
      const fieldValue = item[this.itemRendererField];
      if (fieldValue) return fieldValue;
    }
    if (item && typeof item === 'object' && item.itemRenderer) {
      return item.itemRenderer;
    }
    return this.itemRenderer || 'div';
  };

  proto._renderList = function (items) {
    if (this._redacted || !this._applyShowIf()) return;

    // Virtual scroll mode — delegate to decorator
    if (this._virtualState) {
      updateVirtual(this);
      return;
    }

    // Apply virtual scroll decorator on first render if virtualHeight is set.
    // Pool rows get the same :itemAttrs config as the standard path (they
    // used to lose their @l/@sz tokens silently); showKeys stays list-path
    // only — a windowed pool has no slot for a synthetic header row.
    if (this.virtualHeight && !this._virtualState) {
      applyVirtualScroll(this, {
        itemHeight: this.virtualHeight,
        createItem: () => {
          // An itemRenderer arrives as a STRING with the data, so nothing
          // imported it and nothing could. Resolve before creating: a near-miss
          // name is corrected, an unknown one is queued for recovery.
          const rendererTag = resolveUnknownTag(this.itemRenderer || 'div');
          const el = document.createElement(rendererTag);
          markUnregistered(el, rendererTag);
          if (typeof this._applyItemAttrs === 'function') this._applyItemAttrs(el);
          return el;
        },
      });
      updateVirtual(this);
      return;
    }

    const prev = getRenderingComponent();
    setRenderingComponent(this);

    // Append mode: skip clearing, only render new items
    const appendMode = Boolean(this._appendStartIndex);
    const startIndex = appendMode ? this._appendStartIndex : 0;

    const container = this._listContainer || this;
    if (!appendMode) {
      // The `static` children survive this rebuild, so their gates are not
      // discarded with the rest: nothing would replay their subscriptions or
      // their prop registrations while the owner stays connected.
      // A gated static child is retained through its ANCHOR as well — the
      // comment the gate inserts before, which is all there is of it while
      // it is hidden, and what puts it back in its place when it shows.
      // What the marker says NOW: an attribute binding may have added or
      // removed it since these gates were built, and the element decides.
      refreshShowIfStatic(container._showIfState);
      const retained = Array.from(container.children || []).filter((child) => child.hasAttribute('static'));
      const anchors = new Set();
      const collect = (entry) => { if (entry?.static && entry.anchor) anchors.add(entry.anchor); };
      const gateState = container._showIfState;
      if (gateState) {
        gateState.bindings.forEach((entries) => entries.forEach(collect));
        (gateState.propDeps || []).forEach((record) => collect(record.entry));
      }
      const keptNodes = Array.from(container.childNodes).filter((node) =>
        (node.nodeType === 1 && node.hasAttribute?.('static')) || anchors.has(node));
      cleanupShowIfTree(container, new Set(retained));
      // Drop item-scoped subscriptions from the previous render so they don't
      // pile up on the owner and fire against the discarded item DOM.
      if (Array.isArray(this._subs)) {
        this._subs.filter((e) => e.item).forEach((e) => e.sub.unsubscribe?.());
        this._subs = this._subs.filter((e) => !e.item);
      }
      if (typeof this._resetChildIndexMaps === 'function') this._resetChildIndexMaps();
      if (keptNodes.length) {
        // Removed one by one, not through replaceChildren: that detaches and
        // reinserts the nodes it is given, and a retained CUSTOM element
        // disconnecting runs its own cleanup — which takes the prop
        // registrations of gates its ancestor authored inside it off that
        // ancestor, and the ancestor never disconnected, so nothing replays
        // them. Removing only what goes leaves the retained nodes untouched,
        // in place and in order.
        const kept = new Set(keptNodes);
        Array.from(container.childNodes).forEach((node) => { if (!kept.has(node)) node.remove(); });
      } else {
        container.replaceChildren();
      }
    }

    // Mark this pass so subscriptions created for item templates are tagged
    // item-scoped (see pushOwnerSub) and can be cleared on the next rebuild.
    this._itemSubMark = true;
    if (this.showKeys && items.length && !appendMode) {
      const headerTag = resolveUnknownTag(this._resolveItemRenderer(items[0]) || 'div');
      const headerEl = document.createElement(headerTag);
      markUnregistered(headerEl, headerTag);
      headerEl.setAttribute('keys', '');
      this._applyItemAttrs(headerEl);
      headerEl.data = this._keysRowFor(items[0]);
      container.appendChild(headerEl);
    }
    items.forEach((item, index) => {
      if (index < startIndex) return;
      const rendererTag = this._resolveItemRenderer(item);
      const resolvedTag = resolveUnknownTag(rendererTag || 'div');
      const itemEl = document.createElement(resolvedTag);
      markUnregistered(itemEl, resolvedTag);

      // The item's index is a JS property, not a `data-index` attribute — nothing reads
      // it from the DOM, and an attribute on every row just inflates the HTML. Access an
      // item element by index with `[...container.children].find(c => c._index === i)`.
      itemEl._index = index;
      if (this.id) itemEl.id = `${this.id}-${index}`;
      if (this.ls) itemEl._boundStoreRef = this.ls;
      // Attributes before data: a renderer's data setter may read its own
      // configuration (layout, size) while building children.
      this._applyItemAttrs(itemEl);
      itemEl.data = item;

      if (this._itemTemplate) {
        this._applyChildElements(this._itemTemplate, itemEl, { item, index });
      }

      if (!this._itemTemplate) {
        // A renderer that owns children owns its content: either statically
        // (a def children template, _childElements) or built by its `data`
        // setter (the item-component pattern — .data ran above, so real
        // element children exist by now). Only leaf renderers with neither
        // get item.text stamped into textContent. This mirrors the SSR
        // serializer, which ignores the text token once children rendered,
        // and ends the recurring "text field wiped my renderer" trap.
        const rendererHasChildren = itemEl._childElements != null || itemEl.firstElementChild != null;
        if (item === null || item === undefined) {
          if (!rendererHasChildren) itemEl.textContent = '';
        } else if (typeof item === 'string' || typeof item === 'number') {
          if (!rendererHasChildren) itemEl.textContent = String(item);
        } else if (typeof item === 'object') {
          if (item.class) itemEl.className = String(item.class);
          if (item.value !== undefined) {
            // The value is a JS PROPERTY, never a data-value attribute (rule
            // 15): selection mixins and change events read option.value, and
            // emits' detail.value reads the item's _data. Nothing reads it
            // from the markup.
            itemEl.value = item.value;
          }
          if (item.attrs && typeof item.attrs === 'object') {
            Object.entries(item.attrs).forEach(([key, value]) => {
              setAttrValue(itemEl, key, value);
            });
          }
          if (item.text !== undefined && !rendererHasChildren) {
            itemEl.textContent = String(item.text ?? '');
          }
        }
      }

      if (item?.selected) {
        itemEl.setAttribute('selected', '');
        itemEl.selected = true;
      }

      applyBindingsRecursive(itemEl, item);
      if (typeof this.registerSelectableChild === 'function') {
        this.registerSelectableChild(itemEl, index);
        this._stampItemRole(itemEl);
      }
      container.appendChild(itemEl);
      // A ~cl:nav::^field link in this row was bound before the row had data,
      // so its href could not be built then. It can now: the row is in the
      // tree and carries its item. Without this pass every list-stamped link
      // is destination-less, which is most links in a real app — a nav bar is
      // a list. See core/core/bindings/nav-href.js.
      applyNavHrefs(itemEl);
    });
    this._itemSubMark = false;

    // Re-apply pending value after items render
    if (this._isSelectable && this._value != null) {
      this.value = this._value;
    }

    setRenderingComponent(prev);

    // Post-render hook
    if (typeof this._afterRender === 'function') {
      this._afterRender();
    }

    // Infinite scroll: set up or update sentinel position
    if (this._storeKey && this.store) {
      const loadMoreEntry = this.store.getValue(`${this._storeKey}.loadMore`);
      if (loadMoreEntry?.hasMore && loadMoreEntry.fn) {
        if (!hasInfiniteScroll(this)) {
          // Register for appendData via store
          this.store.setValue(`${this._storeKey}.appendTarget`, this);
          applyInfiniteScroll(this, () => {
            const entry = this.store.getValue(`${this._storeKey}.loadMore`);
            if (entry?.hasMore && entry.fn) entry.fn();
          });
        } else {
          updateSentinelPosition(this);
        }
      }
    }
  };
}
return { applyListMixin };
})();

const __m17 = (() => {
// Shared ARIA vocabularies and helpers for the a11y machinery in
// create-component, the selection/sync mixins and the SSR serializer.
// Pure data + one id helper — imports nothing, so both DSL engines and every
// mixin can use it without cycles.

// Which aria state attribute an item's role implies. When a selectable
// container declares `aria.itemRole`, selection co-writes this state alongside
// the bare `selected` attribute, so screen readers hear what CSS already
// shows. Roles whose APG pattern tracks selection use aria-selected; roles
// modelling a check use aria-checked.
const ITEM_STATE = {
  option: 'aria-selected',
  tab: 'aria-selected',
  treeitem: 'aria-selected',
  row: 'aria-selected',
  gridcell: 'aria-selected',
  radio: 'aria-checked',
  checkbox: 'aria-checked',
  menuitemradio: 'aria-checked',
  menuitemcheckbox: 'aria-checked',
  switch: 'aria-checked',
};

// Container role → panel role for index-paired widgets. The sync mixin holds
// both sides of a pair (e.g. s-tb-l tabs syncing an s-st stack); when the
// source container's role is here, sync auto-writes aria-controls on each
// source item and aria-labelledby on its target panel, and stamps the panel
// role if absent.
const PAIR_ROLES = {
  tablist: 'tabpanel',
};

// Roles whose APG pattern requires the element itself be keyboard-focusable.
// A def with `emits: { click: … }` and one of these roles gets `focusable`
// auto-derived (tabindex=0 + Enter/Space activation) unless it opts out.
const INTERACTIVE_ROLES = new Set([
  'button', 'checkbox', 'radio', 'switch', 'tab', 'option', 'menuitem',
  'menuitemradio', 'menuitemcheckbox', 'treeitem', 'link', 'slider',
  'spinbutton', 'separator', 'gridcell', 'combobox',
]);

// Ensure an element has an id an aria-* reference can point at, generating a
// stable sequential one when absent (the swcf<n> labelFor precedent).
let ariaIdSeq = 0;
function ensureAriaId(el) {
  if (!el.id) el.id = `swca${++ariaIdSeq}`;
  return el.id;
}

// Boolean aria states become "true"/"false" from a source attribute's
// presence; everything else mirrors the source's value.
const BOOL_ARIA = new Set(['expanded', 'selected', 'checked', 'pressed', 'modal', 'hidden', 'busy', 'disabled', 'invalid', 'required', 'readonly', 'multiselectable', 'current', 'grabbed']);

const ARIA_RESERVED = new Set(['role', 'itemRole', 'refs', 'roleWhen']);
const kebab = (name) => name.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());

// Compile a def's `aria:` config into the shape both runtimes consume — the
// client binder (create-component) and the SSR serializer share THIS compiler
// so the two cannot drift. `=literal` values are fixed aria values; any other
// value names a source attribute whose state reflects onto the aria-*
// attribute; `role`/`itemRole`/`refs` are structural.
function compileAria(aria) {
  const out = { role: null, roleWhen: null, itemRole: null, statics: [], reflect: {}, sourceAttrs: [], refs: [] };
  if (!aria) return out;
  out.role = aria.role || null;
  // `roleWhen` names the attributes that make this def interactive. Without it
  // a role is unconditional, which is right for a def that is ALWAYS the thing
  // it announces. With it, the role, its aria state and its tab stop apply
  // only to instances that carry one of those attributes — for a def that is
  // interactive in some uses and inert in others. A chip is the case: a filter
  // chip is a button, a status chip is a word, and announcing the word as an
  // unpressed toggle button with a tab stop is a lie told to exactly the
  // people who cannot check it against the screen.
  out.roleWhen = aria.roleWhen ? [].concat(aria.roleWhen).map(kebab) : null;
  out.itemRole = aria.itemRole || null;
  out.refs = aria.refs ? Object.entries(aria.refs) : [];
  Object.entries(aria).forEach(([key, value]) => {
    if (ARIA_RESERVED.has(key) || !value) return;
    if (typeof value === 'string' && value[0] === '=') {
      out.statics.push([key, value.slice(1)]);
    } else {
      const src = kebab(value);
      (out.reflect[src] || (out.reflect[src] = [])).push({ ariaAttr: key, bool: BOOL_ARIA.has(key) });
    }
  });
  out.sourceAttrs = Object.keys(out.reflect);
  return out;
}

/**
 * Whether this def's announced role applies to THIS element.
 *
 * `has` is the runtime's own attribute test, so the client asks the element
 * and the serializer asks the attribute bag it is about to write, and the two
 * cannot answer differently.
 */
function ariaRoleApplies(compiled, has) {
  if (!compiled || !compiled.roleWhen) return true;
  return compiled.roleWhen.some((attr) => has(attr));
}

// Whether a def's element must itself be keyboard-focusable: forced by
// `focusable: true`, suppressed by `focusable: false`, otherwise derived —
// click-interactive with an interactive announced role and no native
// semantics (native elements are focusable already).
function deriveFocusable(focusable, hasNativeTag, emitsClick, role) {
  return focusable === true ||
    (focusable !== false && !hasNativeTag && emitsClick && role != null && INTERACTIVE_ROLES.has(role));
}
return { ITEM_STATE, PAIR_ROLES, INTERACTIVE_ROLES, ensureAriaId, BOOL_ARIA, compileAria, ariaRoleApplies, deriveFocusable };
})();

const __m18 = (() => {
function parseTagSpec(spec) {
  const input = String(spec || '').trim();
  if (!input) return { tag: 'div', id: null, classes: [] };

  let i = 0;
  let tag = '';
  let id = null;
  const classes = [];

  while (i < input.length && input[i] !== '#' && input[i] !== '.') {
    tag += input[i];
    i += 1;
  }

  while (i < input.length) {
    const marker = input[i];
    i += 1;

    let value = '';
    while (i < input.length && input[i] !== '#' && input[i] !== '.') {
      value += input[i];
      i += 1;
    }

    if (!value) continue;
    if (marker === '#') id = value;
    if (marker === '.') classes.push(value);
  }

  return {
    tag: tag || 'div',
    id,
    classes
  };
}
return { parseTagSpec };
})();

const __m19 = (() => {
function parseShowIfToken(token) {
  if (typeof token !== 'string') return null;
  const trimmed = token.trim();
  if (!trimmed.startsWith('?')) return null;

  let raw = trimmed.slice(1).trim();
  if (!raw) return { source: 'literal', key: null, negate: false, literal: false, equals: null, raw: trimmed };

  let negate = false;
  if (raw.startsWith('!')) {
    negate = true;
    raw = raw.slice(1).trim();
  }

  if (raw === 'true' || raw === 'false') {
    return { source: 'literal', key: null, negate, literal: raw === 'true', equals: null, raw: trimmed };
  }

  // Value match: ?key=value (negated: ?!key=value). Split on the FIRST `=`
  // before the source-prefix colon parse so `=` composes with prefixes
  // (?data:tab=source) and the value may itself contain `:`.
  let equals = null;
  const eq = raw.indexOf('=');
  if (eq > 0) {
    equals = raw.slice(eq + 1);
    raw = raw.slice(0, eq);
  }

  const colon = raw.indexOf(':');
  if (colon > 0) {
    const source = raw.slice(0, colon);
    const key = raw.slice(colon + 1);
    // Validate known sources: data, store, prop
    if (source === 'data' || source === 'store' || source === 'prop') {
      return { source, key, negate, literal: null, equals, raw: trimmed };
    }
    // Unknown prefix — treat as store binding
    return { source: 'store', key: raw, negate, literal: null, equals, raw: trimmed };
  }

  // No prefix — treat as store binding
  return { source: 'store', key: raw, negate, literal: null, equals, raw: trimmed };
}

// Single predicate shared by the client evaluator below and the server's SSR
// evaluator (server/ssr/dsl-to-html.js): value-match when the rule carries an
// `equals`, truthiness otherwise, then negation. Never fork this comparison.
function matchesShowIf(rawValue, rule) {
  if (!rule) return true;
  const matched = rule.equals != null
    ? String(rawValue) === rule.equals
    : Boolean(rawValue);
  return rule.negate ? !matched : matched;
}

function resolveShowIfStore(rule, context = {}) {
  if (!rule) return null;
  if (rule.source === 'data') return context.internalStore;
  if (rule.source === 'store') return context.store;
  // prop source does not use a store — evaluated directly
  return null;
}

// D3: two or more `?` tokens on one element are a CONJUNCTION — the element
// shows when every condition holds. Each condition keeps its own source, so
// `?store:ready` and `?data:ready` are two conditions, not one; the client
// subscribes to each on its own source and re-evaluates the conjunction on
// any change. An empty list is no condition at all, which shows.
function evaluateShowIfAll(rules, context = {}) {
  if (!Array.isArray(rules)) return evaluateShowIf(rules, context);
  return rules.every((rule) => evaluateShowIf(rule, context));
}

// The name the server writes into its `<!--swc-if:…-->` brackets, and the one
// place that spells it. The bare key for a boolean rule, the SENSE in front
// of a negated one (`!sent`), the whole raw token for a value match — so two
// sibling faces of the same key never write byte-identical markers. A
// conjunction joins its conditions with `&`. Hydration reads the name as an
// opaque string and pairs positionally (hydrate.js adoptShowIfSlot), so this
// is documentation, not a grammar the two engines have to agree on.
function showIfMarkerKey(rules) {
  const list = Array.isArray(rules) ? rules : [rules];
  return list.filter(Boolean).map((rule) => (rule.equals != null
    ? (rule.raw || rule.key || '')
    : `${rule.negate ? '!' : ''}${rule.key || ''}`)).join('&');
}

function evaluateShowIf(rule, context = {}) {
  if (!rule) return true;

  if (rule.source === 'literal') {
    const value = Boolean(rule.literal);
    return rule.negate ? !value : value;
  }

  if (rule.source === 'prop') {
    return matchesShowIf(context.owner?.[rule.key], rule);
  }

  const source = resolveShowIfStore(rule, context);
  const raw = source && typeof source.getValue === 'function'
    ? source.getValue(rule.key)
    : undefined;
  return matchesShowIf(raw, rule);
}
return { parseShowIfToken, matchesShowIf, resolveShowIfStore, evaluateShowIfAll, showIfMarkerKey, evaluateShowIf };
})();

const __m20 = (() => {
const parseTagSpec = __m18.parseTagSpec;
const parseAssignment = __m13.parseAssignment;
const parseLiteral = __m13.parseLiteral;
const parseValue = __m13.parseValue;
const parseShowIfToken = __m19.parseShowIfToken;
const normalizeBindingName = __m8.normalizeBindingName;
// Dev diagnostics: collect a warning instead of throwing. The collector is an
// optional array threaded in by the caller (SSR dev mode); when absent — the
// prod path and every client render — this is a no-op and behavior is
// byte-identical to before the channel existed. Nothing throws either way.
function warn(diagnostics, code, message, path) {
  if (diagnostics) diagnostics.push({ level: 'warn', code, message, path: path || '' });
}

function createElementModel() {
  return {
    tag: 'div',
    id: null,
    classes: [],
    attrs: {},
    props: {},
    events: [],
    bindings: [],
    children: [],
    text: null,
    dataKey: null,
    dataSource: null,
    dataField: null,
    // The list contract an instance carries: its item renderer, the row field
    // that names one per row, and the owner field a nested list is fed from
    // (`&data=field`). Carried here so a consumer reads them off the model
    // rather than off a property it has to know to look for.
    itemRenderer: null,
    itemRendererField: null,
    dataFromField: null,
    // The element's own data, when a `:data` object was supplied directly
    // (an object token's typed `:data`, or an object-form def's `data`):
    // present only then, so a consumer can tell data the instance was handed
    // from data it inherits from its context. `props` is the propValues.
    explicitData: undefined,
    // Every SOURCE binding in AUTHORED order — a bare `$key` (the owner's
    // internal store on the client), a `:dataSource` (the global store) or a
    // `:dataKey` (the internal store) — each with its origin and key, and
    // `interpolated: true` on a row-interpolated key (`:ds=rows.^id`), which
    // binds in the row phase, after the plain ones, and is inert outside a
    // row; `inert: true` on a falsy key the runtime skips, `rejected: true`
    // on a non-string key the Store refuses (see addSource). An attribute
    // binding (`$title=key`) is not a source. An object-form def lists them
    // in the order the renderers REPLAY it: its string props, dataKey,
    // dataSource, then its raw `$key` bindings.
    //
    // `sourceOrigin` is the origin of the last-authored one. That is an
    // authoring fact, not a promise about selection: the server reads the
    // last-authored of `$key` / `:dataSource` but prefers `:dataSource` to
    // `:dataKey` in any order, the fresh client binds `:dataKey` after every
    // deferred store source so it wins in any order, and an adopted tree
    // re-stamps from `$key`. Making the renderers agree is the fix plan's C1;
    // tests/dsl-parity.spec.mjs pins each as it is.
    sources: [],
    sourceOrigin: null,
    // `#parentProp[:childProp]` and `*src[:dst]` are real tokens the runtime
    // honours (parent-prop and data pass-through); they are represented, not
    // dropped, so a consumer of the model can see them.
    passThrough: [],
    // The FIRST `?` condition's text, as it always was, and every condition
    // parsed, in authored order: a `?` string token, a `?` object-token key,
    // and an object-form def's `showIf`. Two or more are a conjunction (D3),
    // each subscribing on its own source.
    showIf: null,
    conditions: [],
    ref: null
  };
}

// THE object-token expansion, shared by the client (create-component.js) and
// the server (dsl-to-html.js): an object token is a sequence of operations in
// the order its keys were written. A `:prop` whose value is an object or an
// array is a TYPED property (a column set, a schema, a data object), assigned
// as the value it is under the literal name it was written with; every other
// entry is the string token it spells: `{ '@a': 'x' }` is `@a=x`, `{ '=':
// 'Hi' }` is `=Hi`, `{ '?flag': '' }` is `?flag`. Order matters — an
// attribute and a reflected property of the same name land in the order
// authored — so the operations come back as one ordered list.
function objectTokenOps(obj) {
  const ops = [];
  const assignmentPrefixes = '@:%&';
  const concatPrefixes = '^=$~?';
  Object.entries(obj || {}).forEach(([key, value]) => {
    const pfx = typeof key === 'string' ? key[0] : '';
    if (pfx === ':' && value !== null && typeof value === 'object') {
      ops.push({ kind: 'typed', name: key.slice(1), value });
      return;
    }
    let token;
    if (assignmentPrefixes.includes(pfx) || (!concatPrefixes.includes(pfx) && value !== undefined)) token = `${key}=${value}`;
    else if (concatPrefixes.includes(pfx)) token = `${key}${value ?? ''}`;
    else token = String(key);
    ops.push({ kind: 'token', token });
  });
  return ops;
}

// The string tokens of an object token, in order (the typed properties left
// out), and the typed properties by their literal names.
const expandObjectToken = (obj) => objectTokenOps(obj).filter((op) => op.kind === 'token').map((op) => op.token);
const typedProps = (obj) => Object.fromEntries(objectTokenOps(obj).filter((op) => op.kind === 'typed').map((op) => [op.name, op.value]));

// A source binding, recorded in authored order, with the key the runtime
// binds: a bare `$key|fallback` reads `key` (the `$` branch splits the
// fallback off), while `:dataSource` and `:dataKey` bind their value whole
// (`:ds=a|fallback` binds the key `a|fallback`). Only a `:dataSource` with a
// `^` segment is row-interpolated and binds in the row phase; the `$` branch
// queues such a key literally.
// The key is the parsed literal the runtime is handed: `:ds=null` is the
// string 'null' (parseLiteral knows no null), `:ds=5` the number 5. Whether
// it BINDS follows the runtime's rules: a falsy key (`false`, `0`) is
// skipped by bindStoreToElement, so the source is `inert` and the next one
// binds as usual; a truthy non-string key is refused by the Store
// ("storeName must be a string" — the internal store's "path must be a
// string"), which THROWS out of the element's binding phase, so the
// source is `rejected` and no source on the element is usable.
function addSource(model, origin, rawKey) {
  let key = rawKey;
  if (typeof key === 'string') {
    key = key.trim();
    if (origin === 'owner') {
      const pipe = key.indexOf('|');
      if (pipe >= 0) key = key.slice(0, pipe).trim();
      // A bare `$` binds nothing (the `$` branch returns on an empty name).
      if (key === '') return;
    }
    // `:ds=` / `:dk=` assign the empty string: an inert entry in the queue,
    // or a `:dataKey` slot CLEARED — retained, so the last-written slot is
    // known to be empty.
  } else if (typeof key !== 'number' && typeof key !== 'boolean') {
    return;
  }
  const source = { origin, key };
  if (!key) source.inert = true;
  else if (typeof key !== 'string') source.rejected = true;
  else if (origin === 'global' && key.includes('^')) source.interpolated = true;
  model.sources.push(source);
  model.sourceOrigin = origin;
}

function applyToken(model, token, diagnostics) {
  if (typeof token !== 'string') {
    warn(diagnostics, 'invalid-token', `token of type ${typeof token} is not a string, array, or object — ignored`, model.tag);
    return;
  }
  const trimmed = token.trim();
  if (!trimmed) return;

  const prefix = trimmed[0];
  const suffix = trimmed.slice(1);

  if (prefix === '?') {
    if (model.showIf === null) model.showIf = suffix.trim();
    const rule = parseShowIfToken(trimmed);
    if (rule) model.conditions.push(rule);
    return;
  }

  if (prefix === '=') {
    model.text = parseLiteral(suffix);
    return;
  }

  if (prefix === '~') {
    const [event = '', action = '', firstTarget = '', ...rest] = suffix.split(':');
    let target = firstTarget;
    const valueParts = [...rest];
    if ((target === 'store' || target === 'data') && valueParts.length > 0) {
      target = `${target}:${valueParts.shift()}`;
    }
    model.events.push({
      event,
      action,
      target,
      value: valueParts.length ? valueParts.join(':') : undefined,
      raw: trimmed
    });
    return;
  }

  if (prefix === '^') {
    model.bindings.push({ type: 'text', field: suffix.trim(), raw: trimmed });
    return;
  }

  if (prefix === '$') {
    const raw = suffix.trim();
    // `$name=key` and `$name=template{key}` are ATTRIBUTE bindings on both
    // renderers (create-component.js case '$', dsl-to-html.js case '$'): the
    // attribute `name` follows the store key, or the template with each
    // `{key}` substituted. Modelled as one here too, with the keys it reads,
    // so a consumer of the model (the manifest) sees the same reads the
    // renderers make rather than a text binding on a key called
    // 'name=key' (F-805).
    const eq = raw.indexOf('=');
    if (eq > 0) {
      const template = raw.slice(eq + 1);
      const keys = [...template.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);
      model.bindings.push({
        type: 'store-attr', name: raw.slice(0, eq), template, keys: keys.length ? keys : [template], raw: trimmed,
      });
      return;
    }
    model.bindings.push({ type: 'store', key: raw, raw: trimmed });
    addSource(model, 'owner', raw);
    return;
  }

  if (prefix === '@' || prefix === ':' || prefix === '%' || prefix === '&') {
    const parsed = parseAssignment(suffix);
    if (!parsed || !parsed.name) {
      warn(diagnostics, 'malformed-assignment', `token "${trimmed}" needs the form ${prefix}name=value — ignored`, model.tag);
      return;
    }

    if (prefix === ':') {
      // The short spellings (:ds, :dk, :df, :rf) are what gets authored and
      // emitted; both renderers canonicalise them, so the model does too.
      const name = normalizeBindingName(parsed.name);
      if (name === 'dataKey') { model.dataKey = parseLiteral(parsed.raw); addSource(model, 'internal', model.dataKey); return; }
      if (name === 'dataSource') { model.dataSource = parseLiteral(parsed.raw); addSource(model, 'global', model.dataSource); return; }
      if (name === 'dataField') { model.dataField = parseLiteral(parsed.raw); return; }
      if (name === 'ref') { model.ref = parseLiteral(parsed.raw); return; }
      if (name === 'itemRenderer') { model.itemRenderer = parseLiteral(parsed.raw); }
      if (name === 'itemRendererField') { model.itemRendererField = parseLiteral(parsed.raw); }
      model.props[name] = parseValue(parsed.raw);
      return;
    }

    if (prefix === '@') {
      model.attrs[parsed.name] = parsed.raw === '' ? true : parseLiteral(parsed.raw);
      return;
    }

    // `&data=field` feeds a nested list from the owner item's field: the
    // binding is kept as one, and the field is named where the serializer
    // and the runtime look for it.
    if (prefix === '&' && parsed.name === 'data') model.dataFromField = parsed.raw;
    model.bindings.push({
      type: prefix === '%' ? 'attr' : 'prop',
      name: parsed.name,
      field: parsed.raw,
      raw: trimmed
    });
    return;
  }

  // '#parentProp[:childProp]' binds a child prop (textContent by default) to
  // the parent's prop; '*dst' hands the parent's DATA to the child's `dst`
  // (`*data` → child.data = parent._data, `*items` → child.items =
  // parent._data), and '*src:dst' hands the parent's `src` (or `data.path`)
  // to the child's `dst`. Both are real tokens the runtime honours
  // (create-component.js cases '#' and '*'); the model carries them as
  // pass-through operations with the runtime's own source and target.
  if (prefix === '#' || prefix === '*') {
    const raw = suffix.trim();
    if (!raw) return;
    const colon = raw.indexOf(':');
    if (prefix === '#') {
      model.passThrough.push({ kind: 'prop', from: colon > 0 ? raw.slice(0, colon) : raw, to: colon > 0 ? raw.slice(colon + 1) : 'textContent', raw: trimmed });
    } else {
      model.passThrough.push({ kind: 'data', from: colon > 0 ? raw.slice(0, colon) : 'data', to: colon > 0 ? raw.slice(colon + 1) : raw, raw: trimmed });
    }
    return;
  }

  warn(diagnostics, 'unknown-token-prefix', `unknown token prefix "${prefix}" in "${trimmed}" — token ignored`, model.tag);
}

function normalizeElementDef(def, diagnostics) {
  if (def === null || def === undefined) return null;
  if (typeof def === 'string' || typeof def === 'number') {
    return { type: 'text', value: String(def) };
  }

  if (!Array.isArray(def)) {
    if (!def || typeof def !== 'object') {
      warn(diagnostics, 'invalid-def', `element def of type ${typeof def} is not an array, object, or string — ignored`, '');
      return null;
    }
    const model = createElementModel();
    const {
      tag = def.component, tags, id, classes, attrs, props, events, bindings,
      children, text, dataKey, dataSource, dataField, showIf, ref,
      itemRenderer, itemRendererField, dataFromField, passThrough, data,
    } = def;
    const spec = tag || (Array.isArray(tags) ? tags[0] : tags) || 'div';
    Object.assign(model, parseTagSpec(spec));
    if (id) model.id = id;
    if (classes) model.classes = Array.isArray(classes) ? classes : String(classes).split(/\s+/).filter(Boolean);
    if (attrs) model.attrs = { ...attrs };
    if (props) model.props = { ...props };
    if (events) model.events = Array.isArray(events) ? [...events] : [events];
    if (bindings) model.bindings = Array.isArray(bindings) ? [...bindings] : [bindings];
    if (children) model.children = Array.isArray(children) ? children : [children];
    if (text !== undefined) model.text = text;
    model.dataKey = dataKey ?? null;
    model.dataSource = dataSource ?? null;
    model.dataField = dataField ?? null;
    model.itemRenderer = itemRenderer ?? (props && typeof props.itemRenderer === 'string' ? props.itemRenderer : null);
    model.itemRendererField = itemRendererField ?? (props && typeof props.itemRendererField === 'string' ? props.itemRendererField : null);
    model.dataFromField = dataFromField ?? null;
    if (data !== null && typeof data === 'object') model.explicitData = data;
    // The order both renderers replay an object-form def in: its `props` as
    // `:name=value` tokens first (so a string `props.dataSource`, or an
    // alias like `props.ds`, is a source authored BEFORE the dedicated
    // fields), then `:dataKey`, `:dataSource`, then its bindings — a
    // `{ type: 'store', raw: '$a' }` binding is replayed from its raw token,
    // so it is the source `$a` is.
    // Each prop goes through the SAME expansion and token application the
    // renderers replay it with, so a scalar of any type (`dataSource: 5`,
    // `dataSource: null`) becomes the source token it spells there; a typed
    // (object / array) value is assigned, never a source.
    Object.entries(model.props).forEach(([name, value]) => {
      objectTokenOps({ [`:${name}`]: value }).forEach((op) => {
        if (op.kind !== 'token') return;
        const scratch = createElementModel();
        applyToken(scratch, op.token, []);
        scratch.sources.forEach((source) => { model.sources.push(source); model.sourceOrigin = source.origin; });
      });
    });
    // The dedicated fields replay as `:dataKey=` / `:dataSource=` tokens
    // only when truthy (a falsy field emits no token at all), and their
    // value is parsed as the literal the token spells — through the same
    // token application, so `dataSource: 5` is the rejected number 5.
    const replayField = (name, value) => {
      if (!value) return;
      const scratch = createElementModel();
      applyToken(scratch, `:${name}=${value}`, []);
      scratch.sources.forEach((source) => { model.sources.push(source); model.sourceOrigin = source.origin; });
    };
    replayField('dataKey', dataKey);
    replayField('dataSource', dataSource);
    model.bindings.forEach((b) => {
      if (!b || typeof b !== 'object' || b.type !== 'store' || typeof b.raw !== 'string') return;
      const raw = b.raw.trim();
      if (raw.startsWith('$') && !raw.includes('=')) addSource(model, 'owner', raw.slice(1));
    });
    if (Array.isArray(passThrough)) model.passThrough = [...passThrough];
    model.showIf = showIf ?? null;
    if (typeof showIf === 'string' && showIf.trim()) {
      const rule = parseShowIfToken(showIf.trim().startsWith('?') ? showIf.trim() : `?${showIf.trim()}`);
      if (rule) model.conditions.push(rule);
    }
    model.ref = ref ?? null;
    return model;
  }

  if (def.length === 0) {
    warn(diagnostics, 'empty-def', 'empty array is not an element def — ignored', '');
    return null;
  }
  const [spec, ...tokens] = def;
  const model = createElementModel();
  Object.assign(model, parseTagSpec(spec));

  tokens.forEach((token) => {
    if (Array.isArray(token)) {
      model.children = token;
      return;
    }
    if (token && typeof token === 'object') {
      // The object's operations in the order they were written: a typed
      // property as the value it is, everything else as the token it spells.
      objectTokenOps(token).forEach((op) => {
        if (op.kind === 'typed') {
          model.props[op.name] = op.value;
          if (op.name === 'data') model.explicitData = op.value;
        } else {
          applyToken(model, op.token, diagnostics);
        }
      });
      return;
    }
    applyToken(model, token, diagnostics);
  });

  return model;
}
return { objectTokenOps, expandObjectToken, typedProps, normalizeElementDef };
})();

const __m21 = (() => {
const normalizeElementDef = __m20.normalizeElementDef;
// The declarative two-way link between a selectable container and a router
// segment: `@seg=<key>` (F-811).
//
// A tab list, a toggle row, a shell nav or an accordion observes
// `router.<key>` and selects itself from it, and a user selection writes it
// back through router.setSegment, so a panel is URL-addressable (reload,
// Back/Forward, a shared link, SSR) with no per-page glue. The read half
// existed (every query param is mirrored to router.<key> on both sides) and
// the write half existed as an API (setSegment / clearSegment); this joins
// them once, so no page spells the recipe again.
//
// Three halves, all here so the selection mixin and the accordion def share
// one implementation:
//   - bindSegment: the subscription, registered in the element's own `_subs`
//     so a disconnect tears it down and the next connect replays it, paused
//     while the element is deactivated like every other binding it owns;
//   - writeSegment: the write, which clears the segment when the value is
//     the container's default (what it shows with no segment in the address)
//     so a default choice leaves a clean URL, and skips the write when the
//     store already says so (a selection the segment itself caused);
//   - selectDefByValue: the SSR half, used by a def's `ssr` hook to mark the
//     authored child whose value the address names, so the server paints the
//     selected face and hydration finds it already right.
//
// Pure and workerd-safe: no node builtins, no DOM access beyond the element
// handed in.


const routerKeyFor = (key) => `router.${key}`;

// The attribute, read at connect: `@seg=tab` names the query key `tab`.
const segmentKeyOf = (el) => (el && typeof el.getAttribute === 'function' && el.getAttribute('seg')) || null;

// True while the element already holds a live subscription to this segment
// (the entry is tagged, so a reconnect that replays bindings does not stack
// a second one on the same key).
const segmentBound = (el, key) => Array.isArray(el._subs)
  && el._subs.some((entry) => entry.seg && entry.name === routerKeyFor(key));

// Subscribe the element to router.<key>. Registered in `_subs` like every
// subscription the element owns: paused while deactivated, cleared on
// disconnect, replayed by the next connect through the caller.
function bindSegment(el, key, onValue) {
  if (!el || !el.store || typeof el.store.subscribe !== 'function' || !Array.isArray(el._subs)) return;
  const name = routerKeyFor(key);
  const sub = el.store.subscribe(name, onValue, { paused: !el._isActivated });
  el._subs.push({ sub, name, seg: true });
}

// Write a selection back to the address. `null`, or the container's default,
// clears the segment; anything else sets it. A value the store already holds
// is not written again: that is the selection the segment itself caused
// arriving back at the container, and writing it would only churn the store.
function writeSegment(el, key, value, defaultValue) {
  const router = el && el.router;
  if (!router || typeof router.setSegment !== 'function' || typeof router.clearSegment !== 'function') return;
  const isDefault = value === null || value === undefined
    || (defaultValue !== null && defaultValue !== undefined && String(value) === String(defaultValue));
  const current = el.store && typeof el.store.getValue === 'function' ? el.store.getValue(routerKeyFor(key), null) : null;
  const currentAbsent = current === null || current === undefined || current === '';
  if (isDefault) {
    if (!currentAbsent) router.clearSegment(key);
    return;
  }
  if (!currentAbsent && String(current) === String(value)) return;
  router.setSegment(key, String(value));
}

// The value a selectable child answers to: its `value` property (a stamped
// item carries it as a JS property, rule 15), else its authored attribute.
const itemValueOf = (child) => (child && (child.value ?? (typeof child.getAttribute === 'function' ? child.getAttribute('value') : null))) ?? null;

// ── SSR ──

const isDef = (def) => def && typeof def === 'object';

// The `value` an authored child def declares, as `@value=` or `:value=`.
function defValueOf(def) {
  const model = isDef(def) ? normalizeElementDef(def) : null;
  if (!model || model.type === 'text') return null;
  const attr = model.attrs && model.attrs.value;
  const prop = model.props && model.props.value;
  const value = attr !== undefined && attr !== null ? attr : prop;
  return value === undefined || value === null ? null : String(value);
}

const isAttrToken = (token, attr) => typeof token === 'string'
  && (token === `@${attr}` || token === `@${attr}=` || token.startsWith(`@${attr}=`));

// A copy of the def with the presence attribute set or removed. Array defs
// keep their token order (the attribute goes right after the spec); object
// defs get their `attrs` map edited. The authored def is never mutated.
function withAttr(def, attr, on) {
  if (Array.isArray(def)) {
    const [spec, ...tokens] = def;
    const kept = tokens
      .filter((token) => !isAttrToken(token, attr))
      .map((token) => (token && typeof token === 'object' && !Array.isArray(token) && Object.hasOwn(token, `@${attr}`)
        ? Object.fromEntries(Object.entries(token).filter(([k]) => k !== `@${attr}`))
        : token));
    return on ? [spec, `@${attr}=`, ...kept] : [spec, ...kept];
  }
  if (!isDef(def)) return def;
  const attrs = { ...(def.attrs || {}) };
  if (on) attrs[attr] = true;
  else delete attrs[attr];
  return { ...def, attrs };
}

// The SSR half of @seg=: mark the child whose value the address names with
// the container's state attribute (`selected` for a selectable row, `open`
// for an accordion panel) and clear it from the others. `null` when the
// address names nothing, or nothing matches, so the caller keeps the
// authored defs as they are (the static render path).
//
// The marked children carry ONLY their selection: the container's item
// contract (role and aria state, from its def's aria.itemRole) is the
// serializer's to stamp, and it stamps an ssr() expansion the way it stamps
// the static path (server/ssr/dsl-to-html.js, __itemA11y), so a hook has
// nothing to add and nothing to get wrong.
//
// (`valueFor`, not `valueOf`: every object inherits Object.prototype.valueOf,
// so an option of that name is never absent and its default never applies.)
function selectDefByValue(childDefs, value, { attr, valueFor = defValueOf } = {}) {
  if (value === null || value === undefined || value === '' || !Array.isArray(childDefs)) return null;
  const wanted = String(value);
  const matched = childDefs.some((def) => isDef(def) && valueFor(def) === wanted);
  if (!matched) return null;
  return childDefs.map((def) => (isDef(def) ? withAttr(def, attr, valueFor(def) === wanted) : def));
}

// What a def's `ssr` hook needs to answer for a container carrying @seg=:
// the marked children, or null to leave the static path alone. A multi row
// (`@multi=`) has no single value in an address and keeps the static path.
function ssrSegmentChildren({ store, attrs, childDefs }, options) {
  const key = attrs && attrs.seg;
  if (!key || !store || typeof store.getValue !== 'function') return null;
  if (attrs.multi !== undefined || attrs['multi-select'] !== undefined) return null;
  return selectDefByValue(childDefs, store.getValue(routerKeyFor(key), null), options);
}
return { routerKeyFor, segmentKeyOf, segmentBound, bindSegment, writeSegment, itemValueOf, defValueOf, withAttr, selectDefByValue, ssrSegmentChildren };
})();

const __m22 = (() => {
const setAttrValue = __m9.setAttrValue;
const cascadeActivate = __m9.cascadeActivate;
const ITEM_STATE = __m17.ITEM_STATE;
const PAIR_ROLES = __m17.PAIR_ROLES;
const ensureAriaId = __m17.ensureAriaId;
const segmentKeyOf = __m21.segmentKeyOf;
const segmentBound = __m21.segmentBound;
const bindSegment = __m21.bindSegment;
const writeSegment = __m21.writeSegment;
const itemValueOf = __m21.itemValueOf;
// Selection mixin — adds selectable behavior to list components.
// Applied automatically when component definition has selectable: true.




function applySelectionMixin(proto) {
  // Init function called from constructor
  proto._initSelection = function () {
    this._isSelectable = true;
    this._selectableSetup = false;
    this._selectedItems = new Set();
    this._nextChildIndex = 0;
    this._childIndexMap = new Map();
    this._indexChildMap = [];
    this._segKey = null;
    this._segLock = false;
  };

  // ── @seg=<key>: the router segment link (F-811) ──
  //
  // On connect the container reads router.<key> as its selected value and
  // subscribes to it; a user selection writes it back through the router, and
  // the container's DEFAULT (its first item, what it shows with no segment in
  // the address) clears the segment instead. Exclusive containers only: a
  // multi row has no single value to put in an address. Called from
  // connectedCallback on every connect; the subscription lives in `_subs`, so
  // a disconnect tears it down and the next connect binds again.
  proto._bindSegment = function () {
    const key = segmentKeyOf(this);
    if (!key || segmentBound(this, key)) return;
    this._segKey = key;
    bindSegment(this, key, (value) => this._selectFromSegment(value));
    // A store subscription fires only for a value that exists. With no
    // segment in the address and nothing selected (nothing authored, nothing
    // the server marked), the container takes its default now, so a bare
    // tab list carrying @seg= always shows a value: the address's, else its
    // own.
    const current = this.store.getValue(`router.${key}`, null);
    const absent = current === null || current === undefined || current === '';
    if (absent && this._selectedItems.size === 0) this._selectFromSegment(null);
  };

  proto._segDefault = function () {
    const first = this.items[0];
    return first ? itemValueOf(first) : null;
  };

  // The read half. Locked so the selection it causes does not write itself
  // straight back into the address. An absent segment selects the default,
  // or, on a row that may hold no selection (@optional=), clears it.
  proto._selectFromSegment = function (value) {
    if (this._isMultiSelect()) return;
    const absent = value === null || value === undefined || value === '';
    const target = absent ? this._segDefault() : value;
    this._segLock = true;
    try {
      if (absent && target === null) return;
      if (absent && this._isOptional()) { this.selectedItems = []; return; }
      this.value = target;
    } finally {
      this._segLock = false;
    }
  };

  // The write half, from _setSelectedItem: skipped while the segment itself
  // is selecting, and in multi mode.
  proto._writeSegment = function () {
    if (!this._segKey || this._segLock || this._isMultiSelect()) return;
    const selected = [...this._selectedItems][0];
    writeSegment(this, this._segKey, selected ? itemValueOf(selected) : null, this._segDefault());
  };

  proto._isOptional = function () {
    return Boolean(this.optional
      || (typeof this.hasAttribute === 'function' && this.hasAttribute('optional')));
  };

  proto._resetChildIndexMaps = function () {
    this._childIndexMap.clear();
    this._indexChildMap = [];
    this._selectedItems.clear();
    this._nextChildIndex = 0;
  };

  proto.registerSelectableChild = function (child, index) {
    if (!child) return;
    const idx = typeof index === 'number' ? index : this._nextChildIndex++;
    if (typeof index === 'number') {
      this._nextChildIndex = Math.max(this._nextChildIndex, idx + 1);
    }
    this._childIndexMap.set(child, idx);
    this._indexChildMap[idx] = child;
    const selected = Boolean(child.selected || child.hasAttribute?.('selected'));
    if (selected) this._selectedItems.add(child);
  };

  // aria.itemRole on the container: stamp the item's role and seed its
  // selection state so AT hears what CSS already shows. Called from the two
  // ITEM-shaped registration paths — list-stamped rows and the authored-markup
  // sweep — NOT from registerSelectableChild itself, which template children
  // (a dropdown's display span) also pass through. Explicit authored roles
  // win; the state attribute always exists once a role implies one
  // (aria-selected/aria-checked absent means "not selectable" to a reader).
  proto._stampItemRole = function (child) {
    const itemRole = this._ariaItemRole;
    if (!itemRole || !child || !child.setAttribute) return;
    // An item tag with TWO announced contracts settles it itself, given the
    // container's itemRole — the s-tgb precedent, where the same toggle is a
    // radio in an exclusive group and a pressed button in a multi one. s-di is
    // the same shape: a value choice inside a dropdown, a plain action inside
    // a split button. Only the item knows how to hold both, and it runs after
    // its own def rather than before it.
    if (typeof child._syncAria === 'function') { child._syncAria(itemRole); return; }
    // A component that declares its OWN aria role keeps it: stamping runs
    // before the child's def applies, so without this check a generic list's
    // 'listitem' overrode e.g. the chip's role=button — and its aria-pressed
    // then sat on a role that does not allow it (an axe critical).
    if (child._ariaRole) return;
    if (!child.hasAttribute('role')) child.setAttribute('role', itemRole);
    const stateAttr = ITEM_STATE[itemRole];
    if (stateAttr) {
      const selected = Boolean(child.selected || child.hasAttribute('selected'));
      child.setAttribute(stateAttr, selected ? 'true' : 'false');
    }
  };

  // One writer for an item's selected state: the bare `selected` attribute
  // (what CSS and the framework key on) plus, when the container declares an
  // itemRole, the role-appropriate aria state.
  proto._markItemSelected = function (child, on) {
    if (on) { if (child.setAttribute) child.setAttribute('selected', ''); }
    else if (child.removeAttribute) child.removeAttribute('selected');
    const stateAttr = this._ariaItemRole && ITEM_STATE[this._ariaItemRole];
    if (stateAttr && child.setAttribute) child.setAttribute(stateAttr, on ? 'true' : 'false');
  };

  proto.unregisterSelectableChild = function (child) {
    if (!child) return;
    const idx = this._childIndexMap.get(child);
    if (idx !== undefined) {
      this._childIndexMap.delete(child);
      this._indexChildMap[idx] = undefined;
    }
    this._selectedItems.delete(child);
  };

  Object.defineProperty(proto, 'items', {
    get() { return this._indexChildMap.filter(Boolean); },
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(proto, 'selectedItems', {
    get() { return [...this._selectedItems]; },
    set(items) {
      const list = Array.isArray(items) ? items : [items];
      this._selectedItems.forEach(item => { item.selected = false; });
      this._selectedItems.clear();
      list.forEach(item => {
        if (item && this._childIndexMap.has(item)) {
          item.selected = true;
          this._selectedItems.add(item);
        }
      });
    },
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(proto, 'selectedIndex', {
    get() {
      const item = [...this._selectedItems][0];
      return item ? (this._childIndexMap.get(item) ?? -1) : -1;
    },
    set(idx) {
      const child = this._indexChildMap[idx];
      if (child) this._setSelectedItem(child);
    },
    enumerable: true,
    configurable: true,
  });

  // Multi-select mode comes from the multi/multiSelect properties or the
  // multi-select/multi attributes (DSL: '@multi-select=').
  proto._isMultiSelect = function () {
    return Boolean(this.multi || this.multiSelect ||
      (typeof this.hasAttribute === 'function' &&
        (this.hasAttribute('multi-select') || this.hasAttribute('multi'))));
  };

  proto._setSelectedItem = function (child, toggle = false) {
    if (!child) return;
    // A child that is no longer in the document is a stale reference: a data
    // rebuild replaced the stamped items MID-EVENT (the calendar rebuilds on
    // value change while the click that caused it is still bubbling), and
    // "selecting" the detached element would deselect the freshly stamped
    // one — the DOM ends with nothing marked. Selection state belongs to the
    // live children only.
    if (child.isConnected === false) return;
    const multi = this._isMultiSelect();

    if (multi) {
      // Compute from the group's own selection set, not child.selected —
      // stays correct even when the child flips its own attribute first.
      const shouldSelect = toggle ? !this._selectedItems.has(child) : true;
      child.selected = shouldSelect;
      this._markItemSelected(child, shouldSelect);
      if (shouldSelect) this._selectedItems.add(child);
      else this._selectedItems.delete(child);
      this._updateSelectionStore();
      // Same after-change hook the single path gets: a multi group's def can
      // reflect its aggregate value (s-tgb-g[multi] joins selected values).
      if (typeof this._onSelectionChange === 'function') this._onSelectionChange(child);
      return;
    }

    // Deactivate previously selected items (for activation-managing parents)
    const shouldManageActivation = this._isActivated && this._managesChildActivation;
    this._selectedItems.forEach((item) => {
      if (item !== child) {
        item.selected = false;
        this._markItemSelected(item, false);
        if (shouldManageActivation) cascadeActivate(item, false);
      }
    });
    this._selectedItems.clear();

    const shouldSelect = toggle ? !child.selected : true;
    child.selected = shouldSelect;
    this._markItemSelected(child, shouldSelect);
    if (shouldSelect) {
      this._selectedItems.add(child);
      if (shouldManageActivation) cascadeActivate(child, true);
    }
    this._updateSelectionStore();
    if (typeof this._onSelectionChange === 'function') {
      this._onSelectionChange(child, shouldSelect);
    }
    this._writeSegment();
  };

  proto._updateSelectionStore = function () {
    if (!this.id) return;
    const multi = this._isMultiSelect();
    const selected = [...this._selectedItems];
    const index = selected.length ? (this._childIndexMap.get(selected[0]) ?? null) : null;
    const item = selected.length ? (selected[0]._data ?? selected[0]) : null;

    this.store.setValue(this.id, multi
      ? { selectedIndex: null, selectedIndexes: selected.map(s => this._childIndexMap.get(s) ?? null), selectedItem: null, selectedItems: selected.map(s => s._data ?? s) }
      : { selectedIndex: index, selectedIndexes: [], selectedItem: item, selectedItems: [] }
    );
  };

  proto._setupSelectable = function () {
    // Items authored as markup (SSR output, hand-written HTML) never pass
    // through the DSL stamping that registers selectable children, so sweep
    // the ones already in the light DOM. Direct children only, and only the
    // item renderer's tag — a nested list's items are its own concern. This
    // also seeds the selection set from an authored `selected` attribute, so
    // the first click can deselect it.
    if (this.itemRenderer) {
      Array.from(this.children).forEach((child) => {
        if (child.matches && child.matches(this.itemRenderer) && !this._childIndexMap.has(child)) {
          this.registerSelectableChild(child);
          this._stampItemRole(child);
        }
      });
    }
    const handler = (event) => {
      if (event.target === this) return;
      const detail = event.detail || {};
      const item = detail.item || event.target;
      if (!item) return;
      // Ownership: an item belongs to its CLOSEST selectable ancestor. A
      // bubbled select from a component nested deeper (a standalone toggle
      // inside a gallery row, an inner list's rows) must not be adopted by
      // an outer list — that turned unrelated ancestors into exclusive
      // selection over elements they never stamped.
      let owner = item.parentElement;
      while (owner && owner !== this && !owner._isSelectable) owner = owner.parentElement;
      if (owner !== this) return;
      // Multi-select: every click toggles (matches the documented checkbox
      // group / chip list contract). Single-select stays select-only.
      this._setSelectedItem(item, Boolean(detail.toggle) || this._isMultiSelect());
    };
    this.addEventListener('select', handler);
    this.addEventListener('selected', handler);
  };
}

function applySelectableValue(proto, baseElement) {
  // Only define value getter/setter when base class doesn't provide a native one
  if (Object.getOwnPropertyDescriptor(baseElement.prototype, 'value')) return;

  Object.defineProperty(proto, 'value', {
    set(v) {
      this._value = v;
      if (this._isSelectable && v != null) {
        for (const child of this.items) {
          // Object state first: stamped items carry .value as a JS property
          // (rule 15 — data-value is gone); authored children declare @value=.
          const cv = child.value ?? child.getAttribute('value');
          if (cv != null && String(cv) === String(v)) {
            this._setSelectedItem(child);
            return;
          }
        }
      }
    },
    get() { return this._value; },
    enumerable: true,
    configurable: true,
  });
}

function applySyncMixin(proto) {
  proto._initSync = function () {
    this._syncSetup = false;
    this._syncRetry = false;
    this._syncSource = null;
    this._syncHandler = null;
  };

  proto._setupSync = function () {
    const syncId = this.getAttribute('sync');
    if (!syncId) return;

    const source = this.parentElement?.querySelector(`#${syncId}`) ||
      document.getElementById(syncId);
    if (!source) {
      if (!this._syncRetry) {
        this._syncRetry = true;
        requestAnimationFrame(() => {
          this._syncSetup = false;
          this._setupSync();
        });
      }
      return;
    }

    this._syncSource = source;
    this._syncHandler = (event) => {
      const detail = event.detail || {};
      const item = detail.item || event.target;
      const index = source._childIndexMap?.get(item);
      if (typeof index === 'number') {
        const target = this._indexChildMap[index];
        if (target) this._setSelectedItem(target);
      }
    };
    source.addEventListener('select', this._syncHandler);
    // Items announce with either name — s-tb dispatches 'select', s-tgb (and
    // every emits:{click:'selected'} item) 'selected'. The adoption handler
    // above listens for both; a sync that hears only one goes quietly deaf
    // for exactly the carousel-indicator case it was built for.
    source.addEventListener('selected', this._syncHandler);

    // Index-paired ARIA: when the source is a paired container (tablist →
    // tabpanel), each source item controls its same-index panel and each panel
    // is labelled by its item — auto-id'd, re-linked after this side's renders
    // so restamped panels keep their wiring. The source may not even be
    // UPGRADED yet (upgrade order follows definition order, not DOM order), so
    // the role check waits for its definition; an authored role attribute
    // wins over the def role on the prototype.
    const linkIfPaired = () => {
      const sourceRole = (source.getAttribute && source.getAttribute('role')) || source._ariaRole;
      const panelRole = sourceRole && PAIR_ROLES[sourceRole];
      if (!panelRole) return;
      this._linkAriaPairs(source, panelRole);
      if (!this._ariaPairsChained) {
        this._ariaPairsChained = true;
        const existing = typeof this._afterRender === 'function' ? this._afterRender.bind(this) : null;
        this._afterRender = () => { if (existing) existing(); this._linkAriaPairs(source, panelRole); };
      }
    };
    const sourceTag = source.localName || '';
    if (sourceTag.includes('-') && typeof customElements !== 'undefined') {
      customElements.whenDefined(sourceTag).then(linkIfPaired);
    } else {
      linkIfPaired();
    }

    if (source._selectedItems?.size) {
      const firstSelected = source._selectedItems.values().next().value;
      const index = source._childIndexMap?.get(firstSelected);
      const target = typeof index === 'number' ? this._indexChildMap[index] : null;
      if (target) this._setSelectedItem(target);
    }
  };

  proto._linkAriaPairs = function (source, panelRole) {
    (source._indexChildMap || []).forEach((item, idx) => {
      const panel = this._indexChildMap[idx];
      if (!item || !panel || !panel.setAttribute) return;
      // The pair role is more specific than the container's own generic
      // itemRole stamp (a stack marks panels role=group; synced to a tablist
      // they are tabpanels) — replace our own stamp, respect anything else.
      const current = panel.getAttribute('role');
      if (!current || current === this._ariaItemRole) panel.setAttribute('role', panelRole);
      item.setAttribute('aria-controls', ensureAriaId(panel));
      panel.setAttribute('aria-labelledby', ensureAriaId(item));
    });
  };

  proto._cleanupSync = function () {
    if (this._syncSource && this._syncHandler) {
      this._syncSource.removeEventListener('select', this._syncHandler);
      this._syncSource.removeEventListener('selected', this._syncHandler);
    }
  };
}
return { applySelectionMixin, applySelectableValue, applySyncMixin };
})();

const __m23 = (() => {
// Floating panel mixin — adds native popover open/close/dismiss behavior.
// Used by dropdown, select, date-picker, time-picker, color-picker,
// auto-complete, cascader, tree-select.
//
// Expects the element to have an '@open' prop (from props system).
// Handles: popover setup, anchor positioning, toggle sync, open/close/toggle methods.
//
// floatingPanel values:
//   '.selector'  — find panel by CSS selector (safest — works even if panel is created later)
//   true         — panel is the last child element
//   number       — panel is the child at that index
//
// Engines without the Popover API (Chrome/Edge <114, Firefox <125, Safari <17)
// get a FALLBACK: the panel is absolutely positioned under its host via the
// `data-fp-host`/`data-fp-fallback` reset.css rules, visibility is the
// `data-fp-open` attribute, and light dismiss (outside pointerdown, Escape) is
// a document listener bound only while open. `popover="auto"` is still stamped
// in fallback mode — the attribute is inert on those engines but keeps every
// `[popover]` styling selector matching, so panels stay skinned. Detection is
// per-call-guarded through HAS_POPOVER because `:popover-open` in matches()
// THROWS (invalid selector) on engines that predate it.

const HAS_POPOVER = typeof HTMLElement !== 'undefined'
    && 'togglePopover' in HTMLElement.prototype;

// Anchor positioning is a SEPARATE feature that shipped years after popover,
// and assuming one implies the other is what broke every dropdown on iPhone.
// Safari has had popover since 16.4 and anchor positioning only very recently,
// so on a large share of real devices the panel became a top-layer popover
// whose `position-anchor` silently did nothing — leaving the UA's own
// `inset: 0` to govern it, which rendered a 128px header dropdown as a
// full-height column down the side of the screen. Popover is still used there
// (top layer and light dismiss are worth keeping); only the POSITIONING falls
// back to being computed here.
const HAS_ANCHOR = typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    && CSS.supports('position-anchor: --fp-probe');

// Safe "is this panel open" for both modes — cascader/tree-select poll this
// to defer their own rendering to the frame the panel actually shows.
function isPanelOpen(panel) {
    if (!panel) return false;
    return HAS_POPOVER ? panel.matches(':popover-open') : panel.hasAttribute('data-fp-open');
}

let _fpUid = 0;

function applyFloatingPanelMixin(proto, config) {
    proto._initFloatingPanel = function () {
        this._fpId = ++_fpUid;
    };

    proto._setupFloatingPanel = function () {
        if (this._floatingPanelSetup) return;

        let panel;
        if (typeof config === 'string') {
            panel = this.querySelector(config);
        } else {
            const children = Array.from(this.children);
            const idx = config === true ? children.length - 1 : config;
            panel = children[idx];
        }

        this._panel = panel;
        if (!this._panel) return;

        this._floatingPanelSetup = true;

        // Unique anchor name per instance — prevents cross-anchoring
        // when multiple instances of the same component exist on page
        const anchorId = `--fp-${this.tagName.toLowerCase()}-${this._fpId}`;
        this.style.anchorName = anchorId;
        this._panel.style.positionAnchor = anchorId;
        this._panel.setAttribute('popover', 'auto');

        if (!HAS_POPOVER) {
            // Positioning + hide hooks for reset.css's fallback rules.
            this.setAttribute('data-fp-host', '');
            this._panel.setAttribute('data-fp-fallback', '');
            return;
        }

        // Sync native popover state → open prop
        this._panel.addEventListener('toggle', (e) => {
            const isOpen = e.newState === 'open';
            if (this.open !== isOpen) this.open = isOpen;
        });
    };

    proto._togglePanel = function () {
        if (HAS_POPOVER) { this._panel?.togglePopover(); return; }
        if (isPanelOpen(this._panel)) this._closePanel();
        else this._openPanel();
    };

    /**
     * Place the panel against its trigger when the engine cannot.
     *
     * Only runs where anchor positioning is missing. Writes the same geometry
     * the CSS would have produced — under the host, left-aligned, at least as
     * wide as it — then keeps it inside the viewport, because a phone-sized
     * screen is exactly where an unplaced panel does the most damage.
     */
    proto._fpPlace = function () {
        const panel = this._panel;
        if (!panel || HAS_ANCHOR) return;
        const host = this.getBoundingClientRect();
        panel.style.position = 'fixed';
        panel.style.margin = '0';
        panel.style.minWidth = `${Math.round(host.width)}px`;
        // Measure after the width is applied so the flip test uses the real
        // height rather than the pre-layout one.
        const gap = 4;
        const below = window.innerHeight - host.bottom - gap;
        const above = host.top - gap;
        const height = panel.offsetHeight;
        const flip = height > below && above > below;
        panel.style.maxHeight = `${Math.max(80, Math.round(flip ? above : below))}px`;
        panel.style.overflowY = 'auto';
        const top = flip ? Math.max(gap, host.top - Math.min(height, above) - gap) : host.bottom + gap;
        const left = Math.max(gap, Math.min(host.left, window.innerWidth - panel.offsetWidth - gap));
        panel.style.top = `${Math.round(top)}px`;
        panel.style.left = `${Math.round(left)}px`;
        panel.style.right = 'auto';
        panel.style.bottom = 'auto';
    };

    proto._fpUnplace = function () {
        const panel = this._panel;
        if (!panel || HAS_ANCHOR) return;
        for (const prop of ['position', 'margin', 'minWidth', 'maxHeight', 'overflowY', 'top', 'left', 'right', 'bottom']) {
            panel.style[prop] = '';
        }
    };

    proto._openPanel = function () {
        if (isPanelOpen(this._panel)) return;
        if (HAS_POPOVER) {
            this._panel?.showPopover();
            if (!HAS_ANCHOR) {
                this._fpPlace();
                // A fixed panel does not travel with its trigger, so it has to
                // be re-placed while the page moves under it.
                this._fpReplace = () => this._fpPlace();
                window.addEventListener('scroll', this._fpReplace, true);
                window.addEventListener('resize', this._fpReplace);
            }
            return;
        }
        if (!this._panel) return;
        this._panel.setAttribute('data-fp-open', '');
        if (this.open !== true) this.open = true;
        // Light dismiss, popover="auto"-style: any pointerdown outside the
        // host, or Escape anywhere, closes. Capture phase so an inner
        // stopPropagation can't strand an open panel.
        if (!this._fpDismiss) {
            this._fpDismiss = (e) => {
                if (!this.isConnected) { this._unbindFpDismiss(); return; }
                if (e.type === 'keydown') {
                    if (e.key === 'Escape') this._closePanel();
                    return;
                }
                if (!this.contains(e.target)) this._closePanel();
            };
            document.addEventListener('pointerdown', this._fpDismiss, true);
            document.addEventListener('keydown', this._fpDismiss, true);
        }
    };

    proto._closePanel = function () {
        if (!isPanelOpen(this._panel)) return;
        if (HAS_POPOVER) {
            this._panel?.hidePopover();
            if (this._fpReplace) {
                window.removeEventListener('scroll', this._fpReplace, true);
                window.removeEventListener('resize', this._fpReplace);
                this._fpReplace = null;
            }
            this._fpUnplace();
            return;
        }
        this._panel.removeAttribute('data-fp-open');
        // The open prop setter fires the def's onOpenChange hook, same as the
        // popover 'toggle' listener path does.
        if (this.open !== false) this.open = false;
        this._unbindFpDismiss();
    };

    proto._unbindFpDismiss = function () {
        if (!this._fpDismiss) return;
        document.removeEventListener('pointerdown', this._fpDismiss, true);
        document.removeEventListener('keydown', this._fpDismiss, true);
        this._fpDismiss = null;
    };
}
return { HAS_POPOVER, HAS_ANCHOR, isPanelOpen, applyFloatingPanelMixin };
})();

const __m24 = (() => {
// Focus-trap mixin — for overlay components (modal, drawer, popconfirm).
//
// When the overlay opens (its `open` attribute is set) it: records the currently
// focused element, moves focus inside (to [autofocus] or the first focusable),
// traps Tab / Shift+Tab within the overlay, closes on Escape (unless [blocking]),
// and on close returns focus to the element that had it before.
//
// Opted in via `focusTrap: true` on the component def, or automatically when the
// def declares `aria: { role: 'dialog' }`. Uses querySelector to enumerate
// focusable descendants (there is no declarative way to know which native/SWC
// children are focusable); this is a11y infrastructure, mirroring how the
// floating-panel mixin resolves its panel.

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable]:not([contenteditable="false"])',
  's-b:not([disabled])',
  's-i',
  's-ta',
  's-sel',
  // s-cb/s-rb (focusable-derived tabindex) and roving-managed items
  // (s-tb/s-di/s-mn-i) are deliberately NOT listed by tag: the former carry a
  // tabindex attribute the selector above matches, and the latter must
  // contribute only their ACTIVE item (tabindex=0) to the Tab cycle — listing
  // the tags would pull the -1 rest into the dialog's tab order.
].join(',');

function applyFocusTrapMixin(proto) {
  proto._getFocusable = function () {
    return Array.from(this.querySelectorAll(FOCUSABLE)).filter(
      (el) => !el.hasAttribute('disabled') &&
        (el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement),
    );
  };

  proto._trapFocus = function () {
    if (this._focusTrapActive) return;
    this._focusTrapActive = true;
    this._returnFocusTo = document.activeElement;

    // A dialog's labelled-by target is often show-if mounted with the dialog
    // itself; re-resolve the aria refs now that the content exists.
    if (typeof this._applyAriaRefs === 'function') this._applyAriaRefs();

    // Modalise the background: everything OUTSIDE the dialog's ancestor chain
    // becomes inert (unfocusable and invisible to AT), which is what
    // aria-modal promises and what Tab-cycling alone cannot deliver. Live
    // regions and status stacks are never inerted — announcements must
    // survive an open dialog. Restored exactly on release.
    this._inertedBackground = [];
    // How far up to modalise. Normally the whole document — that is what
    // aria-modal means. But a dialog can be a PREVIEW inside a bounded region
    // (a docs demo stage, a design canvas): there the surrounding page is not
    // background to be shut off, and inerting it took the site's own header and
    // navigation down with it while the sample looked perfectly fine. An
    // ancestor marked [modal-scope] stops the sweep at itself, so the overlay
    // modalises its own region and nothing beyond it.
    const boundary = this.parentElement && this.parentElement.closest
      ? this.parentElement.closest('[modal-scope]')
      : null;
    let node = this;
    while (node && node.parentElement && node !== document.body && node !== boundary) {
      for (const sibling of Array.from(node.parentElement.children)) {
        if (sibling === node || sibling.inert) continue;
        const role = sibling.getAttribute && (sibling.getAttribute('role') || '');
        if ((sibling.hasAttribute && sibling.hasAttribute('aria-live')) || /^(status|alert|log)$/.test(role)) continue;
        sibling.inert = true;
        this._inertedBackground.push(sibling);
      }
      node = node.parentElement;
    }

    const focusable = this._getFocusable();
    const initial = this.querySelector('[autofocus]') || focusable[0] || this;
    // Let the container itself receive focus when it has no focusable content.
    if (initial === this && !this.hasAttribute('tabindex')) this.setAttribute('tabindex', '-1');
    if (typeof initial.focus === 'function') initial.focus();

    this._focusTrapHandler = (e) => {
      if (e.key === 'Escape') {
        if (!this.hasAttribute('blocking')) this.removeAttribute('open');
        return;
      }
      if (e.key !== 'Tab') return;
      const items = this._getFocusable();
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !this.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !this.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    this.addEventListener('keydown', this._focusTrapHandler);
  };

  proto._releaseFocus = function () {
    if (!this._focusTrapActive) return;
    this._focusTrapActive = false;
    if (this._inertedBackground) {
      this._inertedBackground.forEach((el) => { el.inert = false; });
      this._inertedBackground = null;
    }
    if (this._focusTrapHandler) {
      this.removeEventListener('keydown', this._focusTrapHandler);
      this._focusTrapHandler = null;
    }
    const target = this._returnFocusTo;
    this._returnFocusTo = null;
    if (target && typeof target.focus === 'function') target.focus();
  };

  // Watch the `open` attribute: trap when opened, release when closed.
  proto._setupFocusTrap = function () {
    const react = () => {
      if (this.hasAttribute('open')) this._trapFocus();
      else this._releaseFocus();
    };
    react();
    if (typeof MutationObserver !== 'undefined') {
      if (!this._focusTrapObserver) this._focusTrapObserver = new MutationObserver(react);
      this._focusTrapObserver.observe(this, { attributes: true, attributeFilter: ['open'] });
    }
  };

  proto._teardownFocusTrap = function () {
    if (this._focusTrapObserver) this._focusTrapObserver.disconnect();
    this._releaseFocus();
  };
}
return { applyFocusTrapMixin };
})();

const __m25 = (() => {
const ensureAriaId = __m17.ensureAriaId;
const highlightOption = __m9.highlightOption;
// Keyboard-navigation mixin — arrow-key navigation for composite widgets
// (tablist, radiogroup, menu, toolbar, listbox). Applied when a component def
// declares `keyboardNav: { orientation, selectOnFocus, typeahead, mode, cols }`.
//
// Two modes:
// - roving tabindex (default): a single item keeps tabindex=0 so Tab reaches
//   the group at the active item; arrows move DOM focus between items.
// - `mode: 'activedescendant'` + `anchor: <selector>`: focus STAYS on the
//   anchor (the combobox input, per APG); arrows move aria-activedescendant
//   on the anchor to point at the active item, highlighted via [focused].
//
// `typeahead: true` adds first-letters matching (printable keys accumulate
// for a second, focus jumps to the next item whose text starts with the
// buffer) — the APG listbox/menu/tree behaviour.
//
// Builds on the selection mixin: `this.items` is the navigable set and
// `_setSelectedItem` performs selection. The keydown listener is delegated on
// self / the anchor — no per-item listeners. Managing tabindex + focus on
// items is a11y infrastructure, mirroring the focus-trap mixin.



const HORIZONTAL = { next: 'ArrowRight', prev: 'ArrowLeft' };
const VERTICAL = { next: 'ArrowDown', prev: 'ArrowUp' };

function applyKeyboardNavMixin(proto, {
  orientation = 'both', selectOnFocus = false, typeahead = false,
  mode = 'roving', anchor = null, cols = 0,
} = {}) {
  const horizontal = orientation === 'horizontal' || orientation === 'both';
  const vertical = orientation === 'vertical' || orientation === 'both';
  const nextKeys = [horizontal && HORIZONTAL.next, vertical && VERTICAL.next].filter(Boolean);
  const prevKeys = [horizontal && HORIZONTAL.prev, vertical && VERTICAL.prev].filter(Boolean);
  const activeDescendant = mode === 'activedescendant';

  // Navigable items: registered selectable children that are actual options —
  // not labels or disabled items.
  proto._navItems = function () {
    // A redacted item is inert and aria-hidden (guards): it is not a stop,
    // and must not be handed tabindex=0 as the fallback active item.
    return (this.items || []).filter((el) =>
      el && el.tagName !== 'LABEL' && !el.hasAttribute?.('disabled')
      && !el.hasAttribute?.('inert') && el.getAttribute?.('data-guard') !== 'redact');

  };

  // The item that should hold focus: the focused one, else the selected one,
  // else the first.
  proto._activeNavItem = function () {
    const items = this._navItems();
    const focused = items.filter((el) => el === document.activeElement)[0];
    if (focused) return focused;
    const selected = items.filter((el) => this.selectedItems?.includes(el))[0];
    return selected || items[0] || null;
  };

  proto._applyRovingTabindex = function (activeItem) {
    const items = this._navItems();
    const active = items.includes(activeItem) ? activeItem : items[0];
    items.forEach((el) => { el.tabIndex = el === active ? 0 : -1; });
  };

  // Re-run after item renders so the roving tabindex survives list re-renders.
  proto._refreshRovingTabindex = function () {
    this._applyRovingTabindex(this._activeNavItem());
  };

  proto._focusNavItem = function (item) {
    if (!item) return;
    if (activeDescendant) {
      // Focus stays on the anchor; the item becomes the active descendant.
      const items = this._navItems();
      highlightOption(items, items.indexOf(item));
      this._activeDescendantItem = item;
      if (this._navAnchor) this._navAnchor.setAttribute('aria-activedescendant', ensureAriaId(item));
      if (selectOnFocus && typeof this._setSelectedItem === 'function') this._setSelectedItem(item);
      return;
    }
    this._applyRovingTabindex(item);
    item.focus();
    if (selectOnFocus && typeof this._setSelectedItem === 'function') {
      this._setSelectedItem(item);
    }
  };

  // The first item whose visible text starts with the typed prefix, searching
  // forward from the active item so repeated letters cycle matches.
  proto._typeaheadMatch = function (buffer) {
    const items = this._navItems();
    if (!items.length) return null;
    const start = items.indexOf(this._activeNavItem()) + 1;
    const q = buffer.toLowerCase();
    for (let i = 0; i < items.length; i++) {
      const item = items[(start + i) % items.length];
      if ((item.textContent || '').trim().toLowerCase().startsWith(q)) return item;
    }
    return null;
  };

  proto._setupKeyboardNav = function () {
    if (activeDescendant) {
      // The anchor (combobox input/display) owns focus and the keydown stream.
      this._navAnchor = anchor ? this.querySelector(anchor) : this;
      if (this._navAnchor) this._navAnchor.addEventListener('keydown', (e) => this._handleNavKey(e));
      return;
    }
    // Chain the list mixin's post-render hook so the roving tabindex is
    // re-applied after every render without clobbering the component's own
    // _afterRender (e.g. the dropdown's display sync).
    const existing = typeof this._afterRender === 'function' ? this._afterRender.bind(this) : null;
    this._afterRender = () => { if (existing) existing(); this._refreshRovingTabindex(); };
    this._refreshRovingTabindex();
    this.addEventListener('keydown', (e) => this._handleNavKey(e));
  };

  proto._handleNavKey = function (e) {
    const items = this._navItems();
    if (!items.length) return;
    const active = activeDescendant ? (this._activeDescendantItem || null) : this._activeNavItem();
    const idx = items.indexOf(active);

    // `cols` turns the flat list into a grid for the arrows: left/right step
    // one item, up/down step one visual row, Home/End stay within the row
    // (APG grid pattern: clamped at the edges, never wrapped — wrapping in a
    // calendar walks focus into a different week than the eye expects).
    if (cols > 0 && ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
      const at = idx >= 0 ? idx : 0;
      let to = at;
      if (e.key === 'ArrowRight') to = at + 1;
      else if (e.key === 'ArrowLeft') to = at - 1;
      else if (e.key === 'ArrowDown') to = at + cols;
      else if (e.key === 'ArrowUp') to = at - cols;
      else if (e.key === 'Home') to = at - (at % cols);
      else if (e.key === 'End') to = Math.min(at - (at % cols) + cols - 1, items.length - 1);
      if (idx < 0) to = 0;
      if (to >= 0 && to < items.length) this._focusNavItem(items[to]);
      return;
    }

    if (nextKeys.includes(e.key)) {
      e.preventDefault();
      this._focusNavItem(items[(idx + 1) % items.length]);
    } else if (prevKeys.includes(e.key)) {
      e.preventDefault();
      this._focusNavItem(items[(idx - 1 + items.length) % items.length]);
    } else if (e.key === 'Home') {
      e.preventDefault();
      this._focusNavItem(items[0]);
    } else if (e.key === 'End') {
      e.preventDefault();
      this._focusNavItem(items[items.length - 1]);
    } else if ((!selectOnFocus || activeDescendant) && (e.key === 'Enter' || (e.key === ' ' && !activeDescendant)) && idx >= 0 && !e.defaultPrevented) {
      // Manual activation: Enter (and Space outside a text anchor) selects the
      // active item. Space must keep typing spaces in an activedescendant input.
      // defaultPrevented: an item whose interactive role synthesises its own
      // Enter/Space activation (focusable derivation) has already handled this
      // very event — activating again here turned a calendar pick into an
      // instant toggle-off.
      e.preventDefault();
      if (typeof this._setSelectedItem === 'function') this._setSelectedItem(items[idx], true);
    } else if (typeahead && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !(activeDescendant && e.key === ' ')) {
      // Printable characters accumulate briefly and jump to the next match.
      this._typeBuffer = (this._typeBuffer || '') + e.key;
      clearTimeout(this._typeTimer);
      this._typeTimer = setTimeout(() => { this._typeBuffer = ''; }, 1000);
      const match = this._typeaheadMatch(this._typeBuffer);
      if (match) { e.preventDefault(); this._focusNavItem(match); }
    }
  };
}
return { applyKeyboardNavMixin };
})();

const __m26 = (() => {
// Grid-navigation mixin — 2D arrow-key navigation for grid-shaped widgets
// (table, data-grid, tree-grid, calendar). Applied when a component def
// declares `gridNav: true`. Deliberately separate from keyboard-nav-mixin:
// 1D roving over `items` and 2D row/column traversal share no state model.
//
// The grid model is the ARIA structure itself: rows are descendants with
// role=row, a row's cells are its children with a cell role. A component with
// a different DOM shape overrides _gridRows()/_gridCells(row) in its methods
// (def methods land on the prototype after mixins, so they win).
//
// Keys (APG grid pattern): arrows move cell-to-cell clamped at the edges,
// Home/End jump within the row, Ctrl+Home/End jump to the grid's corners.
// A roving tabindex keeps exactly one cell in the tab order.

const CELL_ROLES = 'gridcell,cell,columnheader,rowheader'.split(',').map((r) => `[role=${r}]`).join(',');

function applyGridNavMixin(proto) {
  proto._gridRows = function () {
    return Array.from(this.querySelectorAll('[role=row]'));
  };

  proto._gridCells = function (row) {
    // A redacted cell is inert and aria-hidden (guards) — not a stop.
    return Array.from(row.children).filter((el) => el.matches && el.matches(CELL_ROLES)
      && !el.hasAttribute('inert') && el.getAttribute('data-guard') !== 'redact');

  };

  proto._gridPosition = function (cell) {
    const rows = this._gridRows();
    for (let r = 0; r < rows.length; r++) {
      const c = this._gridCells(rows[r]).indexOf(cell);
      if (c >= 0) return { r, c, rows };
    }
    return null;
  };

  proto._focusGridCell = function (cell) {
    if (!cell) return;
    const rows = this._gridRows();
    rows.forEach((row) => this._gridCells(row).forEach((el) => { el.tabIndex = el === cell ? 0 : -1; }));
    cell.focus();
  };

  proto._refreshGridTabindex = function () {
    const rows = this._gridRows();
    const first = rows.length ? this._gridCells(rows[0])[0] : null;
    rows.forEach((row, r) => this._gridCells(row).forEach((el, c) => {
      el.tabIndex = (r === 0 && c === 0 && first) ? 0 : -1;
    }));
  };

  proto._setupGridNav = function () {
    const existing = typeof this._afterRender === 'function' ? this._afterRender.bind(this) : null;
    this._afterRender = () => { if (existing) existing(); this._refreshGridTabindex(); };
    this._refreshGridTabindex();
    // Rows are usually stamped by CHILD lists (header list, body list), whose
    // renders never call the host's _afterRender — watch the subtree so the
    // entry tabindex exists once content lands (childList only: the refresh
    // writes tabindex attributes, which this filter does not observe).
    if (typeof MutationObserver !== 'undefined' && !this._gridNavObserver) {
      this._gridNavObserver = new MutationObserver(() => this._refreshGridTabindex());
      this._gridNavObserver.observe(this, { childList: true, subtree: true });
    }

    this.addEventListener('keydown', (e) => {
      const cell = e.target.closest ? e.target.closest(CELL_ROLES) : null;
      if (!cell || !this.contains(cell)) return;
      const pos = this._gridPosition(cell);
      if (!pos) return;
      const { r, c, rows } = pos;
      const cellAt = (ri, ci) => {
        const cells = this._gridCells(rows[ri] || rows[rows.length - 1]);
        return cells[Math.min(ci, cells.length - 1)] || null;
      };

      let next = null;
      if (e.key === 'ArrowRight') next = this._gridCells(rows[r])[c + 1] || null;
      else if (e.key === 'ArrowLeft') next = this._gridCells(rows[r])[c - 1] || null;
      else if (e.key === 'ArrowDown') next = r + 1 < rows.length ? cellAt(r + 1, c) : null;
      else if (e.key === 'ArrowUp') next = r > 0 ? cellAt(r - 1, c) : null;
      else if (e.key === 'Home' && e.ctrlKey) next = cellAt(0, 0);
      else if (e.key === 'End' && e.ctrlKey) next = cellAt(rows.length - 1, Infinity);
      else if (e.key === 'Home') next = this._gridCells(rows[r])[0] || null;
      else if (e.key === 'End') { const cells = this._gridCells(rows[r]); next = cells[cells.length - 1] || null; }
      else return;

      e.preventDefault();
      if (next) this._focusGridCell(next);
    });
  };
}
return { applyGridNavMixin };
})();

const __m27 = (() => {
// The framework-held drag reference. One drag happens at a time per document,
// so the dragged element rides a module singleton as an OBJECT REFERENCE —
// identity never round-trips through dataTransfer strings (which are unreadable
// during dragover anyway: protected mode). Set by the dragSource contract in
// create-component, read by dropZone handlers, cleared on dragend.
let dragged = null;

function setDragged(el) { dragged = el; }
function getDragged() { return dragged; }
function clearDragged() { dragged = null; }
return { setDragged, getDragged, clearDragged };
})();

const __m28 = (() => {
const getByPath = __m9.getByPath;
const setByPath = __m9.setByPath;
// Shallow clone only — deep mutation is not reactive in v1.
// Use setValue('path.to.key', value) for nested updates.
function cloneValue(value) {
  if (Array.isArray(value)) return [...value];
  if (value && typeof value === 'object') return { ...value };
  return value;
}

class InternalDataStore {
  constructor(initial = {}) {
    this._values = Object.create(null);
    Object.entries(cloneValue(initial) || {}).forEach(([k, v]) => { this._values[k] = v; });
    this._subscriptions = new Map();
  }

  getValue(path, defaultValue = undefined) {
    if (!path) return this._values;
    const value = getByPath(this._values, path);
    return value === undefined ? defaultValue : value;
  }

  setValue(path, value) {
    if (!path) return;
    const strPath = String(path);
    if (!strPath.includes('.')) {
      this._values[strPath] = cloneValue(value);
    } else {
      setByPath(this._values, strPath, cloneValue(value));
    }
    this._notify(strPath);
  }

  setValues(values) {
    if (!values || typeof values !== 'object') return;
    Object.entries(values).forEach(([key, value]) => this.setValue(key, value));
  }

  subscribe(path, callback, opts = {}) {
    if (typeof path !== 'string' || typeof callback !== 'function') {
      throw new Error('Invalid arguments: path must be a string and callback must be a function');
    }
    const entry = { callback, paused: Boolean(opts.paused), id: Symbol(path) };
    const list = this._subscriptions.get(path) || [];
    list.push(entry);
    this._subscriptions.set(path, list);
    if (!entry.paused && this.getValue(path) !== undefined) {
      callback(this.getValue(path));
    }
    return {
      unsubscribe: () => this._unsubscribe(path, entry.id),
      pause: () => { entry.paused = true; },
      resume: () => {
        entry.paused = false;
        if (this.getValue(path) !== undefined) callback(this.getValue(path));
      }
    };
  }

  _unsubscribe(path, id) {
    const list = this._subscriptions.get(path);
    if (!list) return;
    const filtered = list.filter((entry) => entry.id !== id);
    if (filtered.length === 0) {
      this._subscriptions.delete(path);
    } else {
      this._subscriptions.set(path, filtered);
    }
  }

  _notify(path) {
    const keys = new Set([path]);
    const parts = String(path).split('.');
    while (parts.length > 1) {
      parts.pop();
      keys.add(parts.join('.'));
    }
    keys.forEach((key) => {
      const list = this._subscriptions.get(key);
      if (!list) return;
      const value = this.getValue(key);
      list.forEach((entry) => {
        if (!entry.paused) entry.callback(value);
      });
    });
  }
}

function createDataProxy(store) {
  return new Proxy(Object.create(null), {
    get(_target, prop) {
      if (typeof prop === 'symbol') return undefined;
      return store.getValue(String(prop));
    },
    set(_target, prop, value) {
      if (typeof prop === 'symbol') return false;
      store.setValue(String(prop), value);
      return true;
    },
    has(_target, prop) {
      return store.getValue(String(prop)) !== undefined;
    },
    ownKeys() {
      const root = store.getValue();
      return root && typeof root === 'object' ? Object.keys(root) : [];
    },
    getOwnPropertyDescriptor(_target, prop) {
      if (store.getValue(String(prop)) === undefined) return undefined;
      return { enumerable: true, configurable: true };
    }
  });
}
return { InternalDataStore, createDataProxy };
})();

const __m29 = (() => {
const setAttrValue = __m9.setAttrValue;
const WRITABLE_FIELDS = new Set(['value', 'checked', 'selected', 'data']);

function writeDataField(el, field = 'data', value, context = {}) {
  const target = field || 'data';

  if (target === 'children') {
    if (typeof context.renderChildren === 'function') {
      context.renderChildren(el, value);
    } else {
      el.textContent = value == null ? '' : String(value);
    }
    return;
  }

  if (target === 'textContent') {
    el.textContent = value == null ? '' : String(value);
    return;
  }

  if (target === 'className') {
    el.className = value == null ? '' : String(value);
    return;
  }

  if (target.startsWith('attr:')) {
    setAttrValue(el, target.slice(5), value);
    return;
  }

  // Writable fields: coerce null to empty string for value targets
  if (WRITABLE_FIELDS.has(target)) {
    el[target] = value == null && target === 'value' ? '' : value;
    return;
  }

  el[target] = value;
}

function readDataField(el, field = 'data') {
  const target = field || 'data';
  if (target === 'textContent') return el.textContent;
  if (target.startsWith('attr:')) return el.getAttribute(target.slice(5));
  return el[target];
}

function isWritableDataField(field) {
  return WRITABLE_FIELDS.has(field || 'data');
}

function applyDataBinding(el, binding, context = {}) {
  if (!binding) return null;
  const { dataKey, dataSource, dataField = 'data' } = binding;
  const sourceKey = dataKey || dataSource;
  if (!sourceKey) return null;

  const source = dataKey ? context.internalStore : context.store;
  if (!source || typeof source.subscribe !== 'function') return null;

  const applyValue = (value) => writeDataField(el, dataField, value, context);
  const sub = source.subscribe(sourceKey, applyValue);

  if (typeof source.getValue === 'function') {
    const current = source.getValue(sourceKey);
    if (current !== undefined) applyValue(current);
  }

  // Upward sync for writable targets — returns cleanup function
  const cleanups = [sub];

  if (WRITABLE_FIELDS.has(dataField)) {
    const eventName = dataField === 'checked' || dataField === 'selected' ? 'change' : 'input';
    const handler = () => {
      if (typeof source.setValue === 'function') {
        source.setValue(sourceKey, readDataField(el, dataField));
      }
    };
    el.addEventListener(eventName, handler);
    el.addEventListener('change', handler);

    cleanups.push({
      unsubscribe: () => {
        el.removeEventListener(eventName, handler);
        el.removeEventListener('change', handler);
      }
    });
  }

  return {
    unsubscribe: () => cleanups.forEach((c) => c?.unsubscribe?.())
  };
}
return { writeDataField, readDataField, isWritableDataField, applyDataBinding };
})();

const __m30 = (() => {
const store = __m0.store;
const router = __m7.router;
const isHydrating = __m3.isHydrating;
const hydrateElement = __m3.hydrateElement;
const beginAdopt = __m3.beginAdopt;
const endAdopt = __m3.endAdopt;
const adoptElement = __m3.adoptElement;
const adoptText = __m3.adoptText;
const placeChild = __m3.placeChild;
const adoptShowIfSlot = __m3.adoptShowIfSlot;
const closeShowIfSlot = __m3.closeShowIfSlot;
const authoredBoundary = __m3.authoredBoundary;
const releaseHydrationBinding = __m3.releaseHydrationBinding;
const resolveGuards = __m10.resolveGuards;
const spliceGuardReferences = __m10.spliceGuardReferences;
const redactTokens = __m10.redactTokens;
const declaredAttributeNames = __m10.declaredAttributeNames;
const guardContext = __m10.guardContext;
const isOmitted = __m10.isOmitted;
const guardAttributes = __m10.guardAttributes;
const NATIVE_DATA_FIELDS = __m2.NATIVE_DATA_FIELDS;
const applyListMixin = __m16.applyListMixin;
const applySelectionMixin = __m22.applySelectionMixin;
const applySelectableValue = __m22.applySelectableValue;
const applySyncMixin = __m22.applySyncMixin;
const applyFloatingPanelMixin = __m23.applyFloatingPanelMixin;
const applyFocusTrapMixin = __m24.applyFocusTrapMixin;
const applyKeyboardNavMixin = __m25.applyKeyboardNavMixin;
const applyGridNavMixin = __m26.applyGridNavMixin;
const nativeTagForClass = __m2.nativeTagForClass;
const setDragged = __m27.setDragged;
const getDragged = __m27.getDragged;
const clearDragged = __m27.clearDragged;
const hasInfiniteScroll = __m12.hasInfiniteScroll;
const removeInfiniteScroll = __m12.removeInfiniteScroll;
const setRenderingComponent = __m9.setRenderingComponent;
const getRenderingComponent = __m9.getRenderingComponent;
const setAttrValue = __m9.setAttrValue;
const setTextContent = __m9.setTextContent;
const cleanupShowIfTree = __m9.cleanupShowIfTree;
const applyBinding = __m9.applyBinding;
const applyBindingsRecursive = __m9.applyBindingsRecursive;
const cascadeActivate = __m9.cascadeActivate;
const getByPath = __m9.getByPath;
const toKebabCase = __m9.toKebabCase;
const resolveRowKey = __m9.resolveRowKey;
const parseTagSpec = __m18.parseTagSpec;
const parseAssignment = __m13.parseAssignment;
const parseLiteral = __m13.parseLiteral;
const parseValue = __m13.parseValue;
const normalizeElementDef = __m20.normalizeElementDef;
const registerKnownTag = __m4.registerKnownTag;
const setDefiner = __m4.setDefiner;
const isFallbackTag = __m4.isFallbackTag;
const normalizeBindingName = __m8.normalizeBindingName;
const objectTokenOps = __m20.objectTokenOps;
const ensureAriaId = __m17.ensureAriaId;
const compileAria = __m17.compileAria;
const deriveFocusable = __m17.deriveFocusable;
const ariaRoleApplies = __m17.ariaRoleApplies;
const InternalDataStore = __m28.InternalDataStore;
const createDataProxy = __m28.createDataProxy;
const applyDataBinding = __m29.applyDataBinding;
const parseStructuredShowIfToken = __m19.parseShowIfToken;
const evaluateShowIf = __m19.evaluateShowIf;
const evaluateShowIfAll = __m19.evaluateShowIfAll;
const resolveShowIfStore = __m19.resolveShowIfStore;
const resolveEventName = __m14.resolveEventName;
const resolveActionValue = __m14.resolveActionValue;
const resolveActionTarget = __m14.resolveActionTarget;
const getItemData = __m15.getItemData;
const bindNavHref = __m15.bindNavHref;
// Service registry — services register themselves to avoid circular imports.
// Components access registered services via this.services.name
const _services = {};
function registerService(name, instance) { _services[name] = instance; }



























// dataField defaults for native elements — lookup map, applied per-instance in createElementFromDSL.
// NATIVE_DATA_FIELDS lives in native-tags.js — the redact pass and SSR need
// the same table.


// CreateComponent with compact DSL tokens for bindings, store orchestration, and list rendering.
// DSL tokens (string form):
// - "tag#id.class" for element spec
// - ="literal" for literal text
// - ^field for text binding
// - @attr=value for literal attributes
// - %attr=field for attribute binding
// - :prop=value for literal properties
// - &prop=field for property binding
// - $store.path for store binding
// - ?store.path for conditional render (parent-managed)
// - ~cl:action:target:value for events (cl=click, in=input, ch=change)
// Actions: set, toggle (store is the orchestrator)

const cssApplied = new Set();

// SSR pre-seed: the server may have inlined the rendered components' CSS as
// <style id="swc-component-css">, each block preceded by the same "/* tag */"
// marker applyCSS writes below. Scan the markers once, on the first applyCSS
// call, so hydration doesn't re-inject (duplicate) CSS already in the document.
let ssrCssScanned = false;
function seedCssAppliedFromSSR() {
  ssrCssScanned = true;
  const ssrStyle = document.getElementById('swc-component-css');
  if (!ssrStyle) return;
  const marker = /(?:^|\n)\/\* ([A-Za-z0-9-]+) \*\/\n/g;
  const text = ssrStyle.textContent;
  let m;
  while ((m = marker.exec(text)) !== null) {
    cssApplied.add(m[1]);
  }
}

// Registry for customized built-in elements: customTag → nativeTag
const builtInRegistry = new Map();

// Tags whose children only ever come from the DSL. Hydration may keep a
// server-rendered element and walk its children in place ONLY for these: a def
// with a connected() callback, an `ssr` expansion, a projection or a list
// renderer writes or relocates its own content, and every one of those was
// written against a subtree the DSL had just built. See adoptElement().
const declarativeTags = new Set();

function isDeclarativeTag(tag) {
  return !tag.includes('-') || declarativeTags.has(tag);
}

// ── Own properties written before the definition landed ──────────────────
// A property assigned to an element whose tag has no definition yet is an own
// VALUE property on a plain HTMLElement. When the definition arrives and the
// element upgrades, that own property shadows the prototype accessor for
// good: reads still answer, but the setter never runs again, so no
// on<Prop>Change fires and a live $-token subscription writes into a dead
// slot. Three populations arrive in that state: the unregistered-tag recovery
// (a list writes `el.data = row` and the definition comes from a registry a
// microtask later), a server-rendered child whose owner walked its template
// before the child's slice of a chunked registration landed, and a
// region-stamped child built from a projected region while its tag was still
// undefined. The standard upgrade-property pattern covers all three: for each
// own property that has a setter on the upgraded prototype chain, delete the
// own copy and write the value back through the accessor.
//
// Name lookup follows property resolution: the first descriptor found on the
// chain decides, so a def method shadowing a mixin accessor is honoured. Own
// properties with no setter anywhere (`_dataSourceKey`, `dataField`, the
// hydration bookkeeping) are left exactly where they are.
function hasSetter(proto, key) {
  let p = proto;
  while (p) {
    const desc = Object.getOwnPropertyDescriptor(p, key);
    if (desc) return typeof desc.set === 'function';
    p = Object.getPrototypeOf(p);
  }
  return false;
}

// `keys` is the element's own enumerable property list taken at the top of
// the upgrade constructor, before the constructor wrote a single field, so it
// holds only what the element was given before it had a definition. Runs at
// the END of the constructor, while `_isConnected` is still false: every setter
// then behaves exactly as it does for a pre-connect assignment on a defined
// tag (no render, no re-project, the handler sees the value once), and
// connectedCallback picks the lifted state up the way it always has. Running
// it from connectedCallback instead, after `_isConnected` was set, re-entered
// `set data` with render() live and threw away the server's adopted nodes,
// which is why the lift used to be gated to the recovery population alone.
// One setter that throws must not fail the upgrade (a failed upgrade is
// permanent), so each key is written on its own.
function liftPresetProps(el, keys) {
  const proto = Object.getPrototypeOf(el);
  keys.filter((key) => hasSetter(proto, key)).forEach((key) => {
    const value = el[key];
    delete el[key];
    try {
      el[key] = value;
    } catch (err) {
      console.warn(`[swc] <${el.localName}>: could not restore "${key}" through its accessor after upgrade`, err);
    }
  });
}

function isDeclarativeDef(def) {
  return !!def && !def.callbacks?.connected && !def.ssr && typeof def.project !== 'function'
    && !def.shadowDom && !def.itemRenderer && !def.itemRendererField;
}

// Classify a whole batch BEFORE any of it is registered. Registration order is
// a contract here, but it is not the DOM's order: a page component can be
// registered before the section components its template names, and adoption
// would then have to guess about tags it had not seen yet.
function noteDeclarativeTags(defs) {
  (defs || []).forEach((def) => {
    if (!isDeclarativeDef(def)) return;
    const list = def.tags || (def.tag ? [def.tag] : []);
    list.forEach((t) => declarativeTags.add(t));
  });
}

// tag → the def object that registered it. Shared defs are a legitimate
// pattern (several composites export the same chartDef / s-act-b, and an app
// config spreads them all), so re-registering the SAME object is silent; only
// a DIFFERENT def claiming an existing tag is a real conflict worth a warning.
const registeredDefs = new Map();

// tag → native tag for every def an app has DECLARED, recorded before any of
// them registers. `builtInRegistry` only learns a customized built-in when
// CreateComponent reaches its def, and under hydration defs register in sliced
// tasks in list order — so a consumer listed before `{ tag: 'x-input',
// baseElement: 'HTMLInputElement' }` would walk its template while the child's
// native tag was still unknown. The redact pass needs that tag (it decides
// whether the mask goes to `value` or to text), and a wrong answer there is a
// value shipped on the client that SSR had masked. CreateApp announces the
// whole list first; the lookup below prefers the registry once it has caught up.
const announcedNativeTags = new Map();
// tag → the declared def, for the same reason: a redact pass strips the
// attributes a composite declares (declaredAttributeNames), and must know them
// before the composite has registered.
const announcedDefs = new Map();

function noteNativeTags(defs) {
  (defs || []).forEach((def) => {
    if (!def || typeof def !== 'object') return;
    const nativeTag = resolveNativeTag(def.baseElement, resolveBaseElement(def.baseElement));
    // The same list CreateComponent registers: `tags` when present, else `tag`.
    (def.tags || (def.tag ? [def.tag] : [])).forEach((t) => {
      announcedDefs.set(t, def);
      if (nativeTag) announcedNativeTags.set(t, nativeTag);
    });
  });
}

function nativeTagOf(tag) {
  return builtInRegistry.get(tag) || announcedNativeTags.get(tag) || null;
}

function defOf(tag) {
  return registeredDefs.get(tag) || announcedDefs.get(tag) || null;
}


function resolveBaseElement(name) {
  if (typeof name !== 'string') return name || HTMLElement;
  const g = typeof globalThis !== 'undefined' ? globalThis : {};
  return g[name] || HTMLElement;
}

function resolveNativeTag(baseElementOption, baseElement) {
  if (typeof baseElementOption === 'string') {
    if (nativeTagForClass[baseElementOption]) {
      return nativeTagForClass[baseElementOption];
    }
    // Native tag string (e.g. 'main', 'nav', 'video') — lowercase, no hyphen
    if (baseElementOption === baseElementOption.toLowerCase() && !baseElementOption.includes('-')) {
      return baseElementOption;
    }
  }
  if (baseElement && baseElement !== HTMLElement) {
    return nativeTagForClass[baseElement.name] || null;
  }
  return null;
}

function applyCSS(cssText, tag, shadowRoot) {
  if (!cssText) return;
  if (shadowRoot) {
    const style = document.createElement('style');
    style.textContent = cssText;
    shadowRoot.appendChild(style);
    return;
  }

  if (!ssrCssScanned) seedCssAppliedFromSSR();
  if (cssApplied.has(tag)) return;
  cssApplied.add(tag);

  // In inline mode, append to the existing bundle <style> tag
  const bundleStyle = document.getElementById('swc-bundle');
  if (bundleStyle) {
    bundleStyle.textContent += '\n/* ' + tag + ' */\n' + cssText;
    return;
  }

  // Otherwise create a new <style> tag
  const style = document.createElement('style');
  style.setAttribute('data-swc', tag);
  style.textContent = cssText;
  document.head.appendChild(style);
}

function toCamelCase(name) {
  return name.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

function coerceAttributeValue(value, missingValue = undefined) {
  if (value === null) return missingValue;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value || true;
}

// getByPath — imported from component-utils.js

// setAttrValue — imported from component-utils.js

function toggleAttr(el, name, force) {
  if (!el || !name) return false;
  const shouldSet = typeof force === 'boolean' ? force : !el.hasAttribute(name);
  setAttrValue(el, name, shouldSet);
  return shouldSet;
}

function parseShowIfToken(token) {
  // Expand $$ to the owner-scoped global store key ($$ → {ownerId}.),
  // mirroring the expansion applyToken performs for non-showIf tokens.
  let resolved = token;
  if (typeof resolved === 'string' && resolved.includes('$$') && getRenderingComponent()?.id) {
    resolved = resolved.replace(/\$\$(?!\$)/g, `${getRenderingComponent().id}.`);
  }
  return parseStructuredShowIfToken(resolved);
}

// Every `?` condition on a def, in authored order: a `?` string token, a `?`
// object-token key (spelled through the one object expansion the server uses
// too), and — for an object-form def, which reaches here already round-tripped
// into string tokens — its `showIf`. Two or more are a CONJUNCTION (D3): the
// element shows when every one holds, and each subscribes on its own source,
// so `?store:ready` and `?data:ready` are two conditions on two stores.
// Returns null when the def carries no condition at all.
function extractShowIfRules(def) {
  if (!Array.isArray(def)) {
    // An object-form def carries its condition in `showIf`. It is gated HERE,
    // like every other form: the round-trip into string tokens happens inside
    // createElementFromDSL, by which point the element is already being built.
    const raw = def && typeof def === 'object' ? def.showIf : null;
    if (typeof raw !== 'string' || !raw.trim()) return null;
    const rule = parseShowIfToken(raw.trim().startsWith('?') ? raw.trim() : `?${raw.trim()}`);
    return rule ? buildShowIfRules([rule]) : null;
  }
  const rules = [];
  for (let i = 1; i < def.length; i += 1) {
    const token = def[i];
    if (typeof token === 'string') {
      const rule = parseShowIfToken(token);
      if (rule) rules.push(rule);
    } else if (token && typeof token === 'object' && !Array.isArray(token)) {
      objectTokenOps(token).forEach((op) => {
        // No prefix test of our own: the parser trims and answers, so a
        // padded spelling (`{ ' ?flag': undefined }`) is the condition the
        // renderer honours rather than one this reading drops.
        if (op.kind !== 'token') return;
        const rule = parseShowIfToken(op.token);
        if (rule) rules.push(rule);
      });
    }
  }
  if (!rules.length) return null;
  return buildShowIfRules(rules);
}

// The conjunction, and what it needs from its surroundings: the predicate,
// the ambient store for the gated subtree, one dependency per store-backed
// condition, and the prop names a `?prop:` condition waits on.
// Does this def carry the `static` marker a list rebuild retains? Answered
// the way the RENDERER answers it, because that is what list-mixin reads off
// the element: a token without `=` is ignored by the assignment parser and
// sets nothing, `@static=` sets the empty attribute, and a false value
// REMOVES the attribute (setAttrValue), so neither `@static` nor
// `@static=false` makes an element static. The last mention wins, as it does
// on the element. A `$`-bound value is only known at runtime, so the def
// claims nothing and the element answers for itself once it exists
// (updateShowIfEntry records it on creation).
//
// A gate on a static child survives a list rebuild whether it is showing or
// not, so the entry has to carry this: while it is hidden there is no
// element left to ask.
function declaresStatic(def) {
  let value;
  const consider = (raw) => {
    // Trimmed first, as applyToken trims: ' @static=' is a token the
    // renderer honours, so it is one this reading honours too.
    const token = typeof raw === 'string' ? raw.trim() : '';
    if (token[0] !== '@') return;
    const parsed = parseAssignment(token.slice(1));
    if (!parsed || parsed.name !== 'static') return;
    value = parsed.raw.startsWith('$') ? false
      : parsed.raw === '' ? true
      : parseLiteral(parsed.raw);
  };
  if (!Array.isArray(def)) {
    // An object-form def's `attrs` are stringified into `@name=value` tokens
    // before anything parses them (createElementFromDSL), so they go through
    // the very same reading: `{ static: 'false' }` is `@static=false` and
    // removes the attribute, while `{ static: null }` is `@static=null`,
    // which is the STRING 'null' and sets it.
    const attrs = def && typeof def === 'object' ? def.attrs : null;
    if (attrs && Object.prototype.hasOwnProperty.call(attrs, 'static')) consider(`@static=${attrs.static}`);
  } else {
    def.slice(1).forEach((token) => {
      if (typeof token === 'string') { consider(token); return; }
      if (!token || typeof token !== 'object' || Array.isArray(token)) return;
      objectTokenOps(token).forEach((op) => { if (op.kind === 'token') consider(op.token); });
    });
  }
  return value !== undefined && value !== null && value !== false;
}

function buildShowIfRules(rules) {
  const fn = (ctx = {}, storeRef) => {
    const owner = ctx.owner || getRenderingComponent();
    return evaluateShowIfAll(rules, {
      owner,
      internalStore: owner?._internalData,
      // Ambient store for this render scope: global at the top level, the owner's
      // internal store inside data-driven (?data: / itemRenderer) blocks.
      store: storeRef,
    });
  };
  // The ambient store the gated subtree renders against: the first condition
  // that names a store of its own decides it, so a `?data:` gate still hands
  // its children the owner's internal store and everything else still hands
  // them the ambient one.
  const storeFor = (context) => {
    for (const rule of rules) {
      const resolved = resolveShowIfStore(rule, context);
      if (resolved) return resolved;
    }
    return null;
  };
  // What the entry subscribes to: one dependency per store-backed condition,
  // each on ITS OWN source. A `?prop:` condition is not here — it is pushed by
  // the owner's prop setter (registerPropDep).
  const depsFor = (context) => rules
    .filter((rule) => rule.key && (rule.source === 'store' || rule.source === 'data'))
    .map((rule) => ({ source: rule.source, key: rule.key, store: resolveShowIfStore(rule, context) }))
    .filter((dep) => dep.store && typeof dep.store.subscribe === 'function');
  const propKeys = rules.filter((rule) => rule.source === 'prop' && rule.key).map((rule) => rule.key);
  return { fn, rules, storeFor, depsFor, propKeys, key: rules[0].key, source: rules[0].source, rule: rules[0] };
}

function ensureShowIfState(parent) {
  if (!parent._showIfState) {
    parent._showIfState = {
      bindings: new Map(),
      subs: new Map(),
      // The `?prop:` registrations this parent's gates put on their owner,
      // so cleanupShowIfState can take them off again (component-utils.js).
      propDeps: []
    };
  }
  if (!parent._showIfState.propDeps) parent._showIfState.propDeps = [];
  return parent._showIfState;
}

function updateShowIfEntry(entry, storeRef) {
  let visible = true;
  // Two different stores, and conflating them was a bug. `evalStore` is the
  // AMBIENT store of the render scope — the global one at the top level, the
  // owner's internal store inside a data-driven block — and it is what a
  // `?store:` or bare-key condition reads, whatever the other conditions
  // name. `storeRef` on the entry is the store the gated SUBTREE renders
  // against, which a leading `?data:` condition switches to the internal
  // store; evaluating against that made `['?data:allowed', '?store:ready']`
  // read `ready` off the internal store while its subscription sat on the
  // global one, so the same two conditions disagreed with themselves by
  // authored order, and with the server either way.
  const evalStore = entry.evalStore || storeRef;
  const sourceStore = entry.storeRef || storeRef;
  try {
    visible = Boolean(entry.showIfFn({ ...entry.ctx, owner: entry.owner }, evalStore));
  } catch (error) {
    console.warn('[swc] show-if evaluation threw; hiding element:', error);
    visible = false;
  }

  if (visible) {
    if (!entry.el) {
      // Recreation happens inside a store callback, outside the owner's build
      // pass. Restore the owner so $ and :dataKey bindings in the recreated
      // subtree resolve against the owner's internal store, not the global one.
      const prevOwner = getRenderingComponent();
      if (entry.owner) setRenderingComponent(entry.owner);
      const result = createElementFromDSL(entry.def, entry.ctx, sourceStore, entry.asItemTemplate, entry.parent);
      setRenderingComponent(prevOwner);
      if (!result || !result.el) return;
      entry.el = result.el;
      // The element is the renderer's own answer about the static marker,
      // including a `$`-bound one the def could not settle.
      if (typeof entry.el.hasAttribute === 'function') entry.static = entry.el.hasAttribute('static');
      // A list re-render may have detached the anchor; append in that case.
      const ref = entry.anchor?.parentNode === entry.parent ? entry.anchor : null;
      // An adopted element is already where the server put it. insertBefore
      // would still detach and re-insert it, which fires disconnected/connected
      // on the whole subtree — the exact churn adoption exists to avoid.
      if (!result.adopted) entry.parent?.insertBefore(entry.el, ref);
      if (typeof entry.parent.registerSelectableChild === 'function') {
        entry.parent.registerSelectableChild(entry.el);
      }
      if (typeof entry.parent.registerRouteChild === 'function') {
        entry.parent.registerRouteChild(entry.el);
      }
    }
    return;
  }

  if (entry.el) {
    if (typeof entry.parent.unregisterSelectableChild === 'function') {
      entry.parent.unregisterSelectableChild(entry.el);
    }
    // The last chance to read the marker off the element: an attribute
    // binding may have changed it since this gate was built, and after the
    // removal there is nothing left to ask.
    if (typeof entry.el.hasAttribute === 'function') entry.static = entry.el.hasAttribute('static');
    // The subtree is discarded, and a native element has no disconnect
    // callback to tidy up after it: the gates INSIDE it go now, or they stay
    // registered on their owner and on this parent, updating a subtree
    // nobody can see. It is rebuilt from the def if the gate reopens.
    cleanupShowIfTree(entry.el);
    entry.el.remove();
    entry.el = null;
  }
}

function registerShowIfEntry(parent, storeRef, entry) {
  const deps = entry.deps || [];
  const propKeys = entry.propKeys || [];
  const ambient = entry.storeRef || storeRef;
  if (deps.length || propKeys.length) {
    const state = ensureShowIfState(parent);
    deps.forEach(({ source, key, store }) => {
      const sourceStore = store || ambient;
      if (!sourceStore || typeof sourceStore.subscribe !== 'function') return;
      // Keyed by SOURCE and key: `?store:ready` and `?data:ready` name the
      // same key on two different stores and hold two subscriptions.
      const id = `${source}:${key}`;
      const list = state.bindings.get(id) || [];
      // A replay may reach an entry that is already registered (two moves before
      // one microtask); the same entry twice would evaluate twice for nothing.
      if (!list.includes(entry)) {
        list.push(entry);
        state.bindings.set(id, list);
      }
      if (!state.subs.has(id)) {
        const sub = trackedSubscribe(sourceStore, key, () => {
          const entries = state.bindings.get(id) || [];
          // Re-evaluate the whole conjunction: this source changed, the
          // others are read again through the entry's own function.
          entries.forEach((item) => updateShowIfEntry(item, item.storeRef || storeRef));
        });
        state.subs.set(id, sub);
      }
    });
  }
  // A `?prop:` condition is pushed by the owner's prop setter, not by a store
  // subscription; the whole conjunction is re-evaluated when it fires. The
  // registration is recorded on this parent's show-if state so that
  // discarding the gate takes it off the owner again (cleanupShowIfState).
  const owner = entry.owner || getRenderingComponent();
  if (owner && propKeys.length && !entry._propsRecorded) {
    entry._propsRecorded = true;
    const state = ensureShowIfState(parent);
    propKeys.forEach((propKey) => {
      const dep = { type: 'showIf', entry, update: () => updateShowIfEntry(entry, ambient) };
      registerPropDep(owner, propKey, dep);
      state.propDeps.push({ owner, propKey, dep, entry });
    });
  }
  if (!deps.length && !propKeys.length) return;
  // The owner replays this registration on a reconnect (a router-cached page
  // re-inserted, a subtree moved): disconnect wipes every show-if state under
  // it and clears the subscriptions, and without the replay a cached page came
  // back with its gates FROZEN — the store moved, nothing showed or hid. The
  // same regression :dataKey regions had (see _dataKeyRebinds). Recorded once
  // per entry; a re-registration on reconnect does not record again.
  if (owner && owner._subs && !entry._recorded) {
    entry._recorded = true;
    (owner._showIfRebinds || (owner._showIfRebinds = [])).push({ parent, storeRef: ambient, entry });
  }
}

// cleanupShowIfState, cleanupShowIfTree — imported from component-utils.js

function appendChildDefs(parent, defs, ctx, storeRef) {
  const list = Array.isArray(defs) ? defs : [defs];
  list.forEach((def) => {
    if (def === null || def === undefined) return;
    const showIfRules = extractShowIfRules(def);
    if (showIfRules) {
      const owner = getRenderingComponent();
      const context = { owner, internalStore: owner?._internalData, store: storeRef };
      const showIfStore = showIfRules.storeFor(context);
      // Adopting: the server's closing marker IS the anchor, so a later flip
      // inserts the element back into the slot the server left for it.
      const slotEnd = adoptShowIfSlot(parent);
      const anchor = slotEnd || document.createComment('swc-showif');
      if (!slotEnd) placeChild(parent, anchor);
      const entry = {
        def,
        ctx,
        anchor,
        parent,
        el: null,
        showIfFn: showIfRules.fn,
        deps: showIfRules.depsFor(context),
        propKeys: showIfRules.propKeys,
        storeRef: showIfStore,
        evalStore: storeRef,
        owner,
        static: declaresStatic(def),
        asItemTemplate: false
      };
      updateShowIfEntry(entry, showIfStore || storeRef);
      if (slotEnd) closeShowIfSlot(parent, slotEnd);
      registerShowIfEntry(parent, showIfStore || storeRef, entry);
      return;
    }
    const result = createElementFromDSL(def, ctx, storeRef, false, parent);
    if (!result || !result.el) return;
    if (!result.adopted) placeChild(parent, result.el);
    if (typeof parent.registerSelectableChild === 'function') {
      parent.registerSelectableChild(result.el);
    }
    if (typeof parent.registerRouteChild === 'function') {
      parent.registerRouteChild(result.el);
    }
  });
}

// cssVars maps may be keyed camelCase (strokeWidth) while runtime attribute
// names arrive kebab-cased (stroke-width). Resolve the map's own key spelling
// so both spellings match and the css var name stays stable (--strokeWidth).
function resolveCssVarKey(cssVars, attrName) {
  const isArray = Array.isArray(cssVars);
  const hasKey = (name) => (isArray
    ? cssVars.includes(name)
    : Object.prototype.hasOwnProperty.call(cssVars, name));
  return [attrName, toCamelCase(attrName), toKebabCase(attrName)].find(hasKey);
}

function applyCssVar(el, cssVars, attrName, value) {
  if (!cssVars) return;
  const key = resolveCssVarKey(cssVars, attrName);
  if (key === undefined) return;

  const suffix = Array.isArray(cssVars) ? '' : (cssVars[key] || '');
  const cssVarName = `--${key}`;

  if (value === null || value === undefined || value === '') {
    el.style.removeProperty(cssVarName);
  } else {
    el.style.setProperty(cssVarName, `${value}${suffix}`);
  }
}

function applyAllCssVars(el, cssVars) {
  if (!cssVars) return;
  const attrNames = Array.isArray(cssVars) ? cssVars : Object.keys(cssVars);
  attrNames
    .map((attrName) => ({
      attrName,
      value: el.getAttribute(toKebabCase(attrName)) ?? el.getAttribute(attrName),
    }))
    .filter(({ value }) => value !== null)
    .forEach(({ attrName, value }) => applyCssVar(el, cssVars, attrName, value));
}

function applyPassAttr(el, passAttr, attrName, value) {
  if (!passAttr || !attrName) return;
  Object.entries(passAttr).forEach(([selector, attrs]) => {
    const attrList = Array.isArray(attrs) ? attrs : [attrs];
    if (!attrList.includes(attrName)) return;

    const children = el.querySelectorAll(selector);
    children.forEach((child) => {
      if (value === null || value === undefined) {
        child.removeAttribute(attrName);
      } else {
        child.setAttribute(attrName, value);
      }
    });
  });
}

function applyAllPassAttrs(el, passAttr) {
  if (!passAttr) return;
  Object.entries(passAttr).forEach(([selector, attrs]) => {
    const attrList = Array.isArray(attrs) ? attrs : [attrs];
    attrList.forEach((attrName) => {
      const value = el.getAttribute(attrName);
      if (value !== null) {
        applyPassAttr(el, passAttr, attrName, value);
      }
    });
  });
}

// applyBinding, applyBindingsRecursive — imported from component-utils.js

// cascadeActivate — imported from component-utils.js

// Create a store subscription that respects the activation lifecycle.
// Starts paused if the owning component is deactivated. Registered in the owner's
// single _subs registry, which the lifecycle pauses/resumes on (de)activation and
// unsubscribes on disconnect.
function trackedSubscribe(storeRef, key, callback) {
  const owner = getRenderingComponent();
  const paused = owner ? !owner._isActivated : false;
  const sub = storeRef.subscribe(key, callback, { paused });
  pushOwnerSub(owner, sub);
  return sub;
}

// Register a subscription in the owner's _subs. During list item rendering (the
// owner sets _itemSubMark), the entry is tagged `item` so a re-render can drop
// just the per-item subs — otherwise item subscriptions accumulate on every
// replaceChildren rebuild and keep firing against detached nodes.
function pushOwnerSub(owner, sub) {
  if (!sub || !owner?._subs) return;
  owner._subs.push(owner._itemSubMark ? { sub, item: true } : { sub });
}

function bindStoreToElement(el, storeRef, key, fallback) {
  if (!storeRef || typeof storeRef.subscribe !== 'function') return;
  if (!key) return;
  const field = el.dataField;

  const applyValue = (value) => {
    const resolved = (value == null || value === '') && fallback != null ? fallback : value;
    if (field.startsWith('attr:')) {
      setAttrValue(el, field.slice(5), resolved);
    } else if (field === 'textContent') {
      setTextContent(el, resolved);
    } else {
      el[field] = resolved;
    }
  };

  const sub = trackedSubscribe(storeRef, key, (value) => applyValue(value));
  el._storeUnsub = sub ? sub.unsubscribe : null;

  if (typeof storeRef.getValue === 'function') {
    applyValue(storeRef.getValue(key));
  }

  // Two-way sync for form fields. Guarded so a reconnect re-bind (which reuses
  // the same global store key) does not stack duplicate input/change listeners.
  if ((field === 'value' || field === 'checked') && !el._storeValueListenerBound) {
    el._storeValueListenerBound = true;
    const handler = () => {
      if (typeof storeRef.setValue === 'function') {
        storeRef.setValue(key, el[field]);
      }
    };
    el.addEventListener('input', handler);
    el.addEventListener('change', handler);
    // Recorded so releaseHydrationBinding (a re-hydration, an adoption) can
    // take this writer off before another store's is attached.
    el._storeValueListener = handler;
  }
}

// A store binding that writes to el[dataField] ($key, :dataSource) is
// recorded while the tokens apply and bound by createElementFromDSL once they
// all have, so the field it writes to is the one the def names wherever the
// :dataField token sits (F-805). Tokens are applied in authored order, and a
// binding taken at its own token read whatever dataField was at that moment.
function deferStoreBind(el, storeRef, key, fallback) {
  (el._pendingStoreBinds || (el._pendingStoreBinds = [])).push({ storeRef, key, fallback });
}

function bindDeferredStores(el) {
  const binds = el._pendingStoreBinds;
  if (!binds) return;
  el._pendingStoreBinds = null;
  binds.forEach(({ storeRef, key, fallback }) => bindStoreToElement(el, storeRef, key, fallback));
}

// Bind a row-interpolated :dataSource once its row is known (called from
// applyBinding when applyBindingsRecursive walks the stamped item). Stores the
// RESOLVED key in _dataSourceKey so the reconnect re-bind in connectedCallback
// works unchanged. Re-applying the same row is a no-op; a changed row swaps the
// subscription to the newly resolved key.
function bindRowDataSource(el, rawKey, row) {
  const key = resolveRowKey(rawKey, row);
  if (!key || key === el._dataSourceKey) return;
  if (el._storeUnsub) { el._storeUnsub(); el._storeUnsub = null; }
  el._dataSourceKey = key;
  bindStoreToElement(el, store, key);
}

// Walk up DOM to find nearest element with _data (for list item context)
function findPropParent(el, propName) {
  let parent = el.parentElement;
  while (parent && !(propName in parent)) parent = parent.parentElement;
  return parent;
}

function registerPropDep(component, propName, dep) {
  if (!component._propDeps) component._propDeps = new Map();
  if (!component._propDeps.has(propName)) component._propDeps.set(propName, []);
  component._propDeps.get(propName).push(dep);
}

// Push a prop value to its registered dependents (#prop bindings and ?prop: show-ifs).
// Called from the prop setter and from attributeChangedCallback, so dependents react
// both to property writes and to direct attribute changes.
function firePropDeps(component, propName, value) {
  const deps = component._propDeps?.get(propName) || [];
  deps
    .filter((dep) => dep.type === 'prop')
    .forEach((dep) => { dep.child[dep.childProp] = value; });
  deps
    .filter((dep) => dep.type === 'showIf')
    .forEach((dep) => dep.update());
}

let labelForSeq = 0;

function applyEventToken(el, token, ctx, storeRef) {
  const trimmed = token.trim();
  if (!trimmed.startsWith('~')) return;

  const suffix = trimmed.slice(1);
  const parts = suffix.split(':');
  const eventPart = parts[0] || '';
  const action = parts[1];
  let target = parts[2];
  let valueParts = parts.length > 3 ? parts.slice(3) : [];
  if ((target === 'store' || target === 'data') && valueParts.length > 0) {
    target = `${target}:${valueParts.shift()}`;
  }
  const valueToken = valueParts.length ? valueParts.join(':') : undefined;
  // `kd.enter` / `ku.escape`: the dot names the KEY the handler waits for.
  // The whole family was dead until now — 'kd.enter' passed through as a
  // literal event name addEventListener happily binds and nothing ever
  // dispatches, so every Enter-to-send authored this way silently never
  // worked. The filter compares e.key case-insensitively.
  let keyFilter = null;
  let eventShort = eventPart;
  const keyDot = eventPart.indexOf('.');
  if (keyDot > 0) {
    keyFilter = eventPart.slice(keyDot + 1).toLowerCase();
    eventShort = eventPart.slice(0, keyDot);
  }
  const eventName = resolveEventName(eventShort);
  const owner = getRenderingComponent();

  // A nav token is a real destination, so it is written as one: cursor, middle
  // click, context menu, "open in new tab", crawlers and assistive tech all
  // read the href and none of them read a click listener.
  //
  // Three things this has to match, because the SSR serializer already does
  // all three (server/ssr/dsl-to-html.js, case '~') and a client that does
  // fewer of them takes the href back off the page the server sent:
  //   - an s-ln, not only a literal <a>. s-ln owns an inner <a> and forwards
  //     its href prop to it, so it is the element the app actually authors;
  //   - a ^field value token, which is how a list-stamped row gets its own
  //     destination (~cl:nav::^route);
  //   - the locale prefix and slug translation, taken from the router's own
  //     outbound builder so the href is exactly where a click would land,
  //     rather than a second implementation that can drift from it.
  if (action === 'nav') bindNavHref(el, target, valueToken, ctx);

  el.addEventListener(eventName, (event) => {
    if (keyFilter && String(event.key || '').toLowerCase() !== keyFilter) return;
    // Resolve item data from parent for list item context
    const itemData = ctx?.item || getItemData(el);
    const actionCtx = { ...ctx, event, item: itemData, data: itemData };

    if (action === 'nav') {
      event.preventDefault();
      const value = resolveActionValue(valueToken, actionCtx);
      const url = value !== undefined ? (target || '') + value : (target || '/');
      router.navigateTo(url);
      return;
    }

    if (action === 'set') {
      if (!target) return;
      if (target.startsWith('^')) {
        const parent = findPropParent(el, target.slice(1));
        if (parent) parent[target.slice(1)] = resolveActionValue(valueToken, actionCtx);
        return;
      }
      const destination = resolveActionTarget(target, {
        internalStore: owner?._internalData,
        store: storeRef,
      });
      if (!destination?.store || typeof destination.store.setValue !== 'function') return;
      destination.store.setValue(destination.key, resolveActionValue(valueToken, actionCtx));
      return;
    }

    if (action === 'toggle') {
      if (!target) return;
      if (target.startsWith('^')) {
        const propName = target.slice(1);
        const parent = findPropParent(el, propName);
        if (parent) parent[propName] = !parent[propName];
        return;
      }
      const destination = resolveActionTarget(target, {
        internalStore: owner?._internalData,
        store: storeRef,
      });
      if (!destination?.store || typeof destination.store.setValue !== 'function') return;
      const current = destination.store.getValue(destination.key);
      destination.store.setValue(destination.key, !current);
      return;
    }

    if (action === 'call') {
      if (!target || typeof owner[target] !== 'function') return;
      // No value token → pass the event, so handlers like _sort(e) receive it.
      owner[target](valueToken === undefined ? event : resolveActionValue(valueToken, actionCtx));
      return;
    }

    if (action === 'em') {
      // Emit a bubbling event (defaults to the form's valueChanged contract) from
      // the owner component. Deferred a microtask so the value-writeback binding,
      // also an input listener registered after this one, updates the store first.
      if (!owner) return;
      queueMicrotask(() => {
        owner.dispatchEvent(new CustomEvent(target || 'valueChanged', {
          bubbles: true,
          detail: { name: owner._name, value: owner.value },
        }));
      });
    }
  });
}

function applyToken(el, token, ctx, storeRef) {
  if (!token || typeof token !== 'string') return;
  let trimmed = token.trim();
  if (!trimmed) return;

  // Expand $$ to component-scoped store key: $$ → {parentId}.
  // The (?!\$) guard keeps a leading bind prefix intact: $$$title → $ownerId.title
  let scopeExpanded = false;
  if (trimmed.includes('$$') && getRenderingComponent()?.id) {
    trimmed = trimmed.replace(/\$\$(?!\$)/g, `${getRenderingComponent().id}.`);
    scopeExpanded = true;
  }

  const prefix = trimmed[0];
  const suffix = trimmed.slice(1);
  const parseAssignment = (value) => {
    const idx = value.indexOf('=');
    if (idx === -1) return null;
    return {
      name: value.slice(0, idx).trim(),
      raw: value.slice(idx + 1).trim()
    };
  };
  switch (prefix) {
    case '~': {
      applyEventToken(el, trimmed, ctx, storeRef);
      return;
    }
    case '$': {
      // $ subscribes to owner's internal store if available, otherwise global store.
      // $$-scoped keys are id-prefixed by design and always live in the global store.
      const owner = getRenderingComponent();
      const targetStore = scopeExpanded ? store : (owner?._internalData || store);

      const raw = suffix.trim();
      if (!raw) return;

      // Reactive attribute binding — $attr=key
      const eqIdx = raw.indexOf('=');
      if (eqIdx > 0) {
        const attrName = raw.slice(0, eqIdx);
        const storeKey = raw.slice(eqIdx + 1);
        // Registered in the owner's _subs via trackedSubscribe, so it is
        // paused/resumed and unsubscribed with the rest.
        trackedSubscribe(targetStore, storeKey, (value) => {
          if (value == null || value === false || value === '') el.removeAttribute(attrName);
          else el.setAttribute(attrName, value === true ? '' : value);
        });
        const initial = targetStore.getValue(storeKey);
        if (initial != null && initial !== false) el.setAttribute(attrName, initial === true ? '' : initial);
        return;
      }

      // Plain $key: subscribe and write to el.dataField. Recorded, not bound:
      // createElementFromDSL binds it once every token has applied, so a
      // :dataField token in ANY position names the field it writes to.
      const pipeIdx = raw.indexOf('|');
      const key = pipeIdx >= 0 ? raw.slice(0, pipeIdx) : raw;
      const fallback = pipeIdx >= 0 ? raw.slice(pipeIdx + 1) : undefined;
      deferStoreBind(el, targetStore, key, fallback);
      return;
    }
    case '=': {
      const valueToken = suffix;
      const value = parseLiteral(valueToken);
      setTextContent(el, value);
      return;
    }
    case '^': {
      const field = suffix.trim();
      if (!field) return;
      if (!el._bindings) el._bindings = [];
      el._bindings.push({ type: 'text', field });
      return;
    }
    case '@':
    case ':': {
      const type = prefix === '@' ? 'attr' : 'prop';
      const parsed = parseAssignment(suffix);
      if (!parsed || !parsed.name) return;
      if (type === 'prop') {
        parsed.name = normalizeBindingName(parsed.name);
        if (parsed.name === 'labelFor') {
          // :lf=<controlRef> — associate this label with the owner's ref'd control.
          el._labelForRef = parseLiteral(parsed.raw);
          return;
        }
        if (parsed.name === 'dataSource') {
          // :dataSource always binds to the global store. Record the key on a
          // dedicated property so the subscription is re-established on reconnect:
          // the router caches and re-inserts page elements, and disconnectedCallback
          // tears down _storeUnsub. (_storeKey is intentionally not reused here — it
          // is overloaded by the infinite-scroll mixins.)
          const dsKey = parseLiteral(parsed.raw);
          if (typeof dsKey === 'string' && dsKey.includes('^')) {
            // Row-interpolated key (:dataSource=t.nav.^key). Deferred exactly
            // like ^label: the ^segment resolves from the row when bindings
            // apply, then registers a normal reactive binding to the resolved
            // key — locale switches update the text with no restamp. Outside a
            // row context the ^field never resolves and the token is inert.
            if (!el._bindings) el._bindings = [];
            el._bindings.push({ type: 'dataSource', key: dsKey, bind: bindRowDataSource });
            return;
          }
          el._dataSourceKey = dsKey;
          // Deferred like :dataKey below: bound by createElementFromDSL after
          // every token, so :dataField is honoured wherever it sits (F-805).
          // Binding here wrote the value to the element's DEFAULT field when
          // :dataField came later, which for a button meant the store value
          // landed as its text.
          deferStoreBind(el, store, dsKey);
          return;
        }
        if (parsed.name === 'dataKey') {
          // :dataKey binds to the owner's internal store. Deferred to
          // createElementFromDSL so a :dataField token in any position is respected.
          el._dataKey = parseLiteral(parsed.raw);
          return;
        }
        if (parsed.name === 'dataField') {
          el.dataField = parseLiteral(parsed.raw) || 'data';
          return;
        }
        if (parsed.name === 'ref') {
          el._swcRef = parseLiteral(parsed.raw);
          return;
        }
      }
      // Reactive store binding: @placeholder=$placeholder or :prop=$storeKey
      if (parsed.raw.startsWith('$') && storeRef) {
        const storeKey = parsed.raw.slice(1);
        trackedSubscribe(storeRef, storeKey, (v) => {
          if (type === 'attr') {
            if (v == null || v === false || v === '') el.removeAttribute(parsed.name);
            else el.setAttribute(parsed.name, v === true ? '' : v);
          } else {
            el[parsed.name] = v;
          }
        });
        return;
      }
      // aria-* attribute values are token STRINGS — 'true'/'false' must never
      // collapse to boolean/presence (mirrored in the SSR serializer).
      const value = type === 'prop' ? parseValue(parsed.raw)
        : (parsed.raw === '' ? true
          : (parsed.name.startsWith('aria-') ? parsed.raw : parseLiteral(parsed.raw)));
      const binding = { type, name: parsed.name, value };
      applyBinding(el, binding, ctx?.data);
      return;
    }
    case '%':
    case '&': {
      const type = prefix === '%' ? 'attr' : 'prop';
      const parsed = parseAssignment(suffix);
      if (!parsed || !parsed.name || !parsed.raw) return;
      if (!el._bindings) el._bindings = [];
      el._bindings.push({ type, name: parsed.name, field: parsed.raw });
      return;
    }
    case '*': {
      // Data/property pass-through from parent to child
      // *data              → child.data = parent._data
      // *srcProp:dstProp   → child[dstProp] = parent[srcProp]
      // *data.path:dstProp → child[dstProp] = getByPath(parent, '_data.path')
      const raw = suffix.trim();
      if (!raw) return;
      const rc = getRenderingComponent();
      if (rc) {
        if (!rc._dataPassTargets) rc._dataPassTargets = [];
        const colonIdx = raw.indexOf(':');
        if (colonIdx > 0) {
          let srcProp = raw.slice(0, colonIdx);
          const dstProp = raw.slice(colonIdx + 1);
          // Remap 'data' prefix to internal '_data' property
          if (srcProp === 'data' || srcProp.startsWith('data.')) {
            srcProp = '_' + srcProp;
          }
          rc._dataPassTargets.push({ el, src: srcProp, prop: dstProp });
        } else {
          // Data pass-through: *propName → child[propName] = parent._data
          rc._dataPassTargets.push({ el, prop: raw });
        }
      }
      return;
    }
    case '#': {
      // Parent prop binding: #parentProp or #parentProp:childProp
      const raw = suffix.trim();
      const rc2 = getRenderingComponent();
      if (!raw || !rc2) return;
      const colonIdx = raw.indexOf(':');
      const parentProp = colonIdx > 0 ? raw.slice(0, colonIdx) : raw;
      const childProp = colonIdx > 0 ? raw.slice(colonIdx + 1) : 'textContent';

      registerPropDep(rc2, parentProp, { child: el, type: 'prop', childProp });

      const current = rc2[parentProp];
      if (current !== undefined) el[childProp] = current;
      return;
    }
    default:
      return;
  }
}

// SVG elements must be created in the SVG namespace or they render as inert
// HTMLUnknownElements. Only unambiguous SVG tags are listed — tags that also
// exist in HTML (a, title, script, style, image/img) stay HTML.
const SVG_NS = 'http://www.w3.org/2000/svg';
const SVG_TAGS = new Set([
  'svg', 'path', 'circle', 'rect', 'ellipse', 'line', 'polyline', 'polygon',
  'g', 'defs', 'use', 'symbol', 'marker', 'mask', 'pattern', 'clipPath',
  'linearGradient', 'radialGradient', 'stop', 'tspan', 'textPath', 'filter',
  'feGaussianBlur', 'feOffset', 'feBlend', 'feColorMatrix', 'feComposite',
  'feFlood', 'feMerge', 'feMergeNode', 'feDropShadow',
  // 'text' was missing while its own children tspan/textPath were present, so
  // ['svg', [['text', '="Label"']]] created an HTML-namespace HTMLUnknownElement
  // that never rendered.
  'text', 'foreignObject', 'desc', 'switch', 'view', 'mpath', 'set',
  'animate', 'animateTransform', 'animateMotion',
  'feTurbulence', 'feImage', 'feTile', 'feMorphology', 'feConvolveMatrix',
  'feDisplacementMap', 'feComponentTransfer', 'feFuncR', 'feFuncG', 'feFuncB',
  'feFuncA', 'feSpecularLighting', 'feDiffuseLighting',
  'fePointLight', 'feSpotLight', 'feDistantLight',
  // Deliberately NOT here: title, a, script, style, image. SVG_TAGS is checked
  // globally with no SVG context, so adding those would hijack their HTML
  // namesakes everywhere.
]);

// adoptParent — the element this def is being built INTO, passed only by
// callers that may be adopting server-rendered nodes (see hydrate.js). It is
// consulted through the adoption frame, which exists only for that exact
// parent, so passing it can never change what a normal build does.
// Returned when a guard denies an element outright — the caller renders nothing.
const OMIT = Symbol('guard:omit');

// Take the node's guards off the token list — every authored form, string or
// object-token, via the evaluator's own plumbing so it never reaches applyToken
// and lands as a stray DOM attribute — resolve them to one outcome, and fold
// that outcome back in as ordinary presentation tokens the normal loop applies.
//
// The guard is evaluated from the store, not from a closure, so the SSR pass
// reaches the same verdict from the same session — see server/ssr/dsl-to-html.js,
// which imports this same evaluator AND this same token plumbing rather than
// reimplementing either. The scan used to live here and there separately; the
// two copies agreed with each other and were both wrong (only the first raw
// string token counted).
function applyGuardTokens(tokens, ctx, storeRef, htmlTag, def) {

  const references = spliceGuardReferences(tokens);
  if (!references.length) return null;

  const outcome = resolveGuards(references, guardContext(storeRef, ctx && ctx.data));
  if (isOmitted(outcome)) return OMIT;

  // Redact strips every binding and the subtree, and masks through the
  // element's own sink. This removes the AFFORDANCE. It does not make the
  // value secret: data a user may not see must be redacted server-side and
  // never serialised, or it is sitting in the page for anyone who opens the
  // inspector.
  if (outcome.state === 'redact') {
    redactTokens(tokens, htmlTag, { keepEvents: !!outcome.remedy, declared: declaredAttributeNames(def) });
  }

  guardAttributes(outcome).forEach((token) => tokens.push(token));
  return outcome;
}



function createElementFromDSL(def, ctx, storeRef, asItemTemplate, adoptParent) {
  if (def === null || def === undefined) return null;
  if (typeof def === 'string' || typeof def === 'number') {
    const text = String(def);
    const claimed = adoptParent ? adoptText(adoptParent, text) : null;
    return { el: claimed || document.createTextNode(text), childDefs: null, adopted: !!claimed };
  }
  if (!Array.isArray(def)) {
    if (!def || typeof def !== 'object') return null;
    const normalized = normalizeElementDef(def);
    if (!normalized || normalized.type === 'text') {
      if (normalized?.type !== 'text') return null;
      const claimed = adoptParent ? adoptText(adoptParent, normalized.value) : null;
      return { el: claimed || document.createTextNode(normalized.value), childDefs: null, adopted: !!claimed };
    }
    const tokens = [];
    Object.entries(normalized.attrs || {}).forEach(([name, value]) => tokens.push(`@${name}=${value}`));
    Object.entries(normalized.props || {}).forEach(([name, value]) => tokens.push({ [`:${name}`]: value }));
    if (normalized.dataKey) tokens.push(`:dataKey=${normalized.dataKey}`);
    if (normalized.dataSource) tokens.push(`:dataSource=${normalized.dataSource}`);
    if (normalized.dataField) tokens.push(`:dataField=${normalized.dataField}`);
    if (normalized.ref) tokens.push(`:ref=${normalized.ref}`);
    if (normalized.conditions?.length) normalized.conditions.forEach((rule) => tokens.push(rule.raw));
    else if (normalized.showIf) tokens.push(`?${normalized.showIf}`);
    (normalized.events || []).forEach((event) => {
      if (event.raw) tokens.push(event.raw);
      else tokens.push(`~${event.event}:${event.action}:${event.target}${event.value !== undefined ? `:${event.value}` : ''}`);
    });
    (normalized.bindings || []).forEach((binding) => {
      if (binding.raw) tokens.push(binding.raw);
      else if (binding.type === 'text') tokens.push(`^${binding.field}`);
      else if (binding.type === 'attr') tokens.push(`%${binding.name}=${binding.field}`);
      else if (binding.type === 'prop') tokens.push(`&${binding.name}=${binding.field}`);
    });
    if (normalized.text !== null && normalized.text !== undefined) tokens.push(`=${normalized.text}`);
    if (normalized.children?.length) tokens.push(normalized.children);
    const spec = `${normalized.tag}${normalized.id ? `#${normalized.id}` : ''}${normalized.classes?.length ? `.${normalized.classes.join('.')}` : ''}`;
    return createElementFromDSL([spec, ...tokens], ctx, storeRef, asItemTemplate, adoptParent);
  }
  if (def.length === 0) return null;

  const [spec, ...tokens] = def;

  // A guard token (`@guard=order.delete`, `@guard=salary.view:redact`) is resolved BEFORE
  // the element exists, because `hidden` has to mean the markup was never
  // produced. Hiding it afterwards with CSS leaves the content in the page,
  // which OWASP A01 names as a bypass — "modifying the HTML page".
  //
  // `tokens` is a fresh array from the rest-destructure above, so removing the
  // guard token here does not mutate the caller's def.
  //
  // The tag is parsed first only to learn the HTML tag the node serialises as,
  // which a redact outcome masks through (`value` on a form control, text on a
  // text element) — see redactTokens. No element exists yet.
  const { tag, id, classes } = parseTagSpec(spec);
  const guarded = applyGuardTokens(tokens, ctx, storeRef, nativeTagOf(tag) || tag, defOf(tag));
  if (guarded === OMIT) return null;



  // Deliberately NOT routed through the unregistered-tag recovery.
  //
  // A tag in authored DSL is written in code the bundler reads, so an unknown
  // one there is a build error the build already reports — the recovery is for
  // tags that arrive at RUNTIME as data, which is the itemRenderer string
  // (list-mixin.js). Hooking this path also broke hydration: the walk runs
  // before every module has registered, so legitimately-pending components read
  // as unknown and got queued mid-adoption.
  // Through the announced table too: under hydration this def may not have
  // registered yet, and adopting the server's <input is=…> needs its native tag.
  const nativeBase = nativeTagOf(tag);
  // A customized built-in serializes as its native tag carrying is=, so that is
  // the tag name the server's node has.
  // An item TEMPLATE is recorded, not placed, so it must not consume a node.
  const adopted = adoptParent && !asItemTemplate
    ? adoptElement(adoptParent, nativeBase || tag, isDeclarativeTag(tag)) : null;
  const el = adopted || (nativeBase ? document.createElement(nativeBase, { is: tag })
    : SVG_TAGS.has(tag) ? document.createElementNS(SVG_NS, tag)
    : document.createElement(tag));
  // A node built HERE is the client's, whatever the hydration flag says: a
  // component a def renders from its own connected() while the page is still
  // hydrating (the flow composite's live form, built as its host upgrades)
  // must not read the instance children it was just given as a server
  // template to adopt; it did, and hydrated pages doubled those children.
  if (!adopted) el._swcBuilt = true;
  // The server's marker encodes the very tokens applied below; releasing it
  // first keeps the element at one subscription per binding.
  if (adopted) releaseHydrationBinding(el);
  // The guard's verdict, on the object itself — read by connectedCallback
  // ahead of the `data-guard` attribute, which is the only signal left when a
  // server-rendered composite upgrades before its owner's template walks.
  if (guarded && guarded.state === 'redact') el._redacted = true;
  // Reflect `is` as an attribute: createElement's { is } sets it internally but
  // does not always expose it for `[is=...]` CSS matching or SSR serialization.
  if (nativeBase && el.getAttribute('is') !== tag) el.setAttribute('is', tag);
  if (!el.dataField) el.dataField = NATIVE_DATA_FIELDS.get(tag) || 'data';

  // Expand $$ in element IDs to component-scoped prefix
  const resolvedId = (id && id.includes('$$') && getRenderingComponent()?.id)
    ? id.replaceAll('$$', `${getRenderingComponent().id}.`)
    : id;
  if (resolvedId && el.id !== resolvedId) el.id = resolvedId;
  if (classes.length) el.classList.add(...classes);

  let childDefs = null;

  tokens.forEach((token) => {
    if (Array.isArray(token)) {
      childDefs = token;
      return;
    }

    if (token && typeof token === 'object' && !Array.isArray(token)) {
      // The object's operations in the order they were written, through the
      // one expansion the server uses too: a typed property (a `:prop` whose
      // value is an object or an array) is set as the value it is, under its
      // literal name; everything else is the string token it spells.
      objectTokenOps(token).forEach((op) => {
        if (op.kind === 'typed') el[op.name] = op.value;
        else applyToken(el, op.token, ctx, storeRef);
      });
      return;
    }

    applyToken(el, token, ctx, storeRef);
  });

  // Every token has applied, so el.dataField is final: bind the $key and
  // :dataSource subscriptions recorded above (F-805).
  bindDeferredStores(el);

  const owner = getRenderingComponent();
  if (owner && el._swcRef) {
    if (!owner.refs) owner.refs = {};
    owner.refs[el._swcRef] = el;
  }
  if (owner && el._labelForRef) {
    (owner._labelForEls || (owner._labelForEls = [])).push(el);
  }

  // The three store bindings are all bound here, after the tokens, so a
  // :dataField token applies first regardless of its position in the def:
  // $ → owner._internalData via bindStoreToElement (recorded in applyToken case '$')
  // :dataSource → global store via bindStoreToElement (recorded in applyToken case ':')
  // :dataKey → owner._internalData via applyDataBinding, below.
  if (el._dataKey && owner?._internalData) {
    const renderBoundChildren = (target, value) => {
      cleanupShowIfTree(target);
      target.replaceChildren();
      if (value === null || value === undefined) return;
      if (typeof value === 'string' || typeof value === 'number') {
        target.textContent = String(value);
        return;
      }
      const defs = Array.isArray(value) && typeof value[0] === 'string' ? [value] : value;
      const prevOwner = getRenderingComponent();
      setRenderingComponent(owner);
      appendChildDefs(target, defs, ctx, storeRef);
      setRenderingComponent(prevOwner);
    };
    const bindNow = () => {
      // Idempotent: a re-bind (cached-page reattach) drops the previous
      // subscription first, so a reattach never stacks a second listener.
      if (typeof el._dataKeyUnsub === 'function') el._dataKeyUnsub();
      const sub = applyDataBinding(el, { dataKey: el._dataKey, dataField: el.dataField }, {
        internalStore: owner._internalData,
        renderChildren: renderBoundChildren,
      });
      el._dataKeyUnsub = sub && sub.unsubscribe ? () => sub.unsubscribe() : null;
      pushOwnerSub(owner, sub);
    };
    bindNow();
    // The subscription lives in the OWNER's _subs, which disconnect wipes —
    // and, unlike _dataSourceKey, the bound element may be a native tag with
    // no lifecycle of its own. The owner therefore keeps the re-bind hook
    // and replays it on reconnect (see connectedCallback), the same contract
    // the :dataSource re-bind follows.
    // Prune before appending: a component that re-renders its template while
    // staying connected never reaches the reconnect prune below, so entries
    // for elements the re-render detached would accumulate for its lifetime.
    const rebinds = owner._dataKeyRebinds || (owner._dataKeyRebinds = []);
    for (let i = rebinds.length - 1; i >= 0; i--) {
      if (!rebinds[i].el.isConnected) rebinds.splice(i, 1);
    }
    rebinds.push({ el, rebind: bindNow });
  }

  if (childDefs && !asItemTemplate) {
    // Remember the authored instance children. A component that ALSO has a
    // children template (e.g. the form: an <s-cn> template plus authored fields)
    // loses these to the hydration clear-and-rebuild, which only rebuilds the
    // template — they are re-applied from here so the fields survive hydration.
    // The AUTHORING owner is remembered with them: their #prop hand-downs must
    // re-resolve against the component whose def wrote them, not against the
    // element they happen to sit in (the sign-in submit label bug).
    el._authoredChildDefs = childDefs;
    el._authoredChildOwner = getRenderingComponent();
    // Adopting: these defs own the tail of the claimed element's children —
    // after the boundary comment when the element also has a template of its
    // own, from the first child when it does not. A component whose template is
    // still pending (its own upgrade has not happened yet, and there is no
    // boundary to aim at) is left alone: its connectedCallback rebuilds the
    // subtree the way it always did, and re-applies these defs after it.
    const adoptKids = adopted && el.firstChild && openAuthoredAdoption(el);
    appendChildDefs(el, childDefs, ctx, storeRef);
    if (adoptKids) endAdopt(el, true);
  }

  return { el, childDefs, adopted: !!adopted };
}

// Open an adoption frame for the INSTANCE children of a claimed element.
function openAuthoredAdoption(el) {
  const boundary = authoredBoundary(el);
  if (boundary) { beginAdopt(el, boundary.nextSibling); return true; }
  // No seam marker means the server emitted one kind of child or the other, and
  // only an element with no template of its own can be sure these are them.
  if (el._childElements) return false;
  beginAdopt(el, el.firstChild);
  return true;
}

// Reflect a source attribute's state onto its mapped aria-* attributes. Boolean
// aria states become "true"/"false" from the source's presence; value states
// mirror the source's value. (The mapping shapes come from compileAria in
// aria-utils.js — shared with the SSR serializer so the two cannot drift.)
function reflectAria(el, sourceAttr, mappings) {
  const has = el.hasAttribute(sourceAttr);
  const val = el.getAttribute(sourceAttr);
  mappings.forEach(({ ariaAttr, bool }) => {
    const name = `aria-${ariaAttr}`;
    if (bool) el.setAttribute(name, has ? 'true' : 'false');
    else if (has && val != null && val !== '') el.setAttribute(name, val);
    else el.removeAttribute(name);
  });
}

// The recovery path needs to DEFINE things, and lives in its own module to keep
// this one from growing a network dependency. Injected rather than imported the
// other way round, so the two files stay acyclic.
setDefiner((def) => CreateComponent(def));

function CreateComponent(options = {}) {
  const {
    tag,
    tags,
    baseElement: baseElementOption = HTMLElement,
    shadowDom = false,
    shadowDomMode = 'open',
    css,
    props,
    attrs = [],
    methods,
    callbacks = { connected: undefined, disconnected: undefined, adopted: undefined },
    emits = {},
    toggles = {},
    childEmitToggles = {},
    storeAttrs = [],
    subscriptions = null,
    listeners = null,
    dragSource = false,
    dropZone = null,
    cssVars = null,
    passAttr = null,
    aria = null,
    children,
    itemRenderer,
    itemRendererField,
    selectable = false,
    floatingPanel = null,
    focusTrap = false,
    focusable = null,
    keyboardNav = null,
    gridNav = false,
    syncFromParent = false,
    managesChildActivation = false,
    dataField = null,
    project = null,
  } = options;

  const baseElement = resolveBaseElement(baseElementOption);
  const nativeTag = resolveNativeTag(baseElementOption, baseElement);
  // Detect if children DSL has * (data pass-through) tokens
  // Detect *data (no colon) tokens — these receive parent._data so bindStoreToElement should use el.data
  const hasDataPass = children?.flat(1).some(t => typeof t === 'string' && t.startsWith('*') && !t.includes(':')) || false;

  // Props system: parse into attribute-reflected and plain property lists
  const _attr = (props || []).filter(p => p.startsWith('@')).map(name => [name.slice(1), toKebabCase(name.slice(1))]);
  const _props = (props || []).filter(p => !p.startsWith('@'));
  const _propNames = new Set([..._props, ..._attr.map(([name]) => name)]);
  // Per def, so the `selected` coercion is reported once per tag (see the setter).
  let selectedCoercionWarned = false;

  // Declarative projection seam: project(props) is a PURE function returning
  // { regionKey: dslArray }. The client runs it on connect and on every declared
  // prop change, writing each region through setData — the same path the
  // hand-written `connected(){ this._project(); }` wiring used. SSR runs the
  // SAME function (carried through the component registry) so projected regions
  // serialize server-side instead of rendering as empty shells.
  const projectFn = typeof project === 'function' ? project : null;

  // Accessibility: the `aria` def maps aria-* attributes to a source state
  // (a prop/attribute name), plus an optional static `role`. Applied on connect
  // and kept in sync via a MutationObserver on the source attributes, so every
  // generated component can ship correct roles/aria-state by default.
  // Two structural sub-keys are reserved (not aria-* attributes themselves):
  // `itemRole` — the role stamped on selectable/stamped children, whose
  // selection state the selection mixin then co-writes (ITEM_STATE); and
  // `refs` — ID relationships ({ controls: '<selector>' } → aria-controls
  // pointing at the first subtree match, auto-id'd), resolved on connect and
  // re-resolved after list renders. compileAria is shared with the SSR
  // serializer, so first-paint markup and hydrated markup cannot drift.
  const compiledAria = compileAria(aria);
  const ariaRole = compiledAria.role;
  const ariaItemRole = compiledAria.itemRole;
  const ariaRefsEntries = compiledAria.refs;
  const ariaReflect = compiledAria.reflect;
  const ariaSourceAttrs = compiledAria.sourceAttrs;
  const ariaStaticEntries = compiledAria.statics;
  // Overlays (modal/drawer/popconfirm) get focus trap + return-focus + Escape.
  const useFocusTrap = focusTrap === true || (aria != null && aria.role === 'dialog');

  // Focusability: an element announced with an interactive role must be
  // keyboard-operable, so a def that emits on click AND carries such a role
  // gets tabindex + Enter/Space activation automatically; `focusable: true`
  // forces it, `focusable: false` opts out (e.g. when a child anchor carries
  // the interaction). Native-based components (button/input/…) are already
  // focusable and are excluded from the auto-derivation.
  const useFocusable = deriveFocusable(focusable, Boolean(nativeTag), Boolean(emits.click), ariaRole);

  const tagList = [...new Set(tags || (tag ? [tag] : []))];
  if (!tagList.length) {
    throw new Error('CreateComponent requires a tag or tags');
  }

  // Declarative = the DSL writes every child this element ever has, so a
  // hydrating owner can keep the server's copy and bind it in place.
  if (isDeclarativeDef(options)) tagList.forEach((t) => declarativeTags.add(t));

  class CustomElement extends baseElement {
    static get observedAttributes() {
      return _attr.length ? _attr.map(([, a]) => a) : attrs;
    }

    constructor() {
      super();
      // Whatever this element was given before it had a definition. Empty for
      // an element created after define() (nothing can write to it first), so
      // the lift at the end of this constructor costs a fresh element nothing.
      const presetKeys = Object.keys(this);
      this._isConnected = false;
      this._data = null;
      this._dataTextNode = null;
      this._childElements = children || null;
      this._subs = [];
      this._storeAttrSubs = {};
      this._isActivated = false;
      this._hasDataPass = hasDataPass;
      this._managesChildActivation = managesChildActivation;
      this._attrLock = false;
      this._eventsSetup = false;
      this._showIf = () => true;
      this._routeMap = new Map();
      this._segmentMap = new Map();
      this._propDeps = null;
      this.refs = {};
      // Mixin init — only allocate list/selection/sync properties when needed
      if (typeof this._initList === 'function') this._initList();
      if (typeof this._initSelection === 'function') this._initSelection();
      if (typeof this._initSync === 'function') this._initSync();
      if (typeof this._initFloatingPanel === 'function') this._initFloatingPanel();
      this._baseURL = '';
      this._routerSegment = '';
      this._routeParams = null;
      this.store = store;
      this.router = router;
      this.services = _services;
      this._internalData = new InternalDataStore();
      this._dataProxy = createDataProxy(this._internalData);
      this.showIf = options?.showIf;

      if (shadowDom) {
        this.attachShadow({ mode: shadowDomMode });
      }

      if (css) {
        applyCSS(css, tagList[0], this.shadowRoot);
      }

      if (props && typeof props === 'object') {
        Object.entries(props).forEach(([key, value]) => {
          this[key] = value;
        });
      }

      // The def's own construction hook. Data reaches a component whenever
      // its source has it: before connect (a :dataSource or $key token at
      // creation, a list stamping `row.data = item` before the append),
      // during connectedCallback (a hydration marker re-bound before the
      // def's connected() runs), or later. A def's own `set data` therefore
      // cannot assume connected() has run, and state it needs is created
      // HERE, not there (F-574): before the lift below, so a value written
      // ahead of the definition finds it too.
      if (callbacks.created) callbacks.created.call(this);

      // Last, so an instance value written before the definition (a row's
      // `data`, a `:status=$status` token, a `?prop:` show-if verdict) lands
      // over the def-level defaults set above, in the order a defined tag
      // would have seen them. See liftPresetProps.
      if (presetKeys.length) liftPresetProps(this, presetKeys);
    }

    connectedCallback() {
      this._isConnected = true;
      const isVisible = this._applyShowIf();
      // A REDACTED host is a mask and nothing else: its template is never
      // built (the server sent none either — see renderElement), its def's
      // connected() never runs (it would read refs from that template), and
      // the mask text set at creation is all it shows. `data-guard` is the
      // framework's own attribute, stamped by the guard pass after everything
      // authored was stripped, so this is the guard's verdict being read back.
      if (this._redacted || this.getAttribute('data-guard') === 'redact') {
        this._redacted = true;
        this._childrenBuilt = true;
      }
      // Defer children DSL for list components with no data — prevents infinite recursion
      // for self-referencing itemRenderers (e.g. s-tree). Children DSL builds when data arrives.
      // Markup-driven list components (inline itemRenderer children, no data
      // binding) still need their template built — the items get projected
      // into it (e.g. s-dd moves s-di children into its panel).
      const hasMarkupItems = !!(this.itemRenderer && Array.from(this.children).some(
        (c) => c.matches && c.matches(this.itemRenderer)));
      const deferChildren = this._childElements && this.itemRenderer && this._data == null && !hasMarkupItems;
      if (deferChildren && !this._childrenBuilt) {
        // Mark the deferral so the first data arrival (render()) builds the
        // template and runs the held connected() — see _completeDeferredChildren.
        this._childrenDeferred = true;
      }
      if (this._childElements && !this._childrenBuilt && !deferChildren) {
        if (isVisible) {
          if (typeof this._resetChildIndexMaps === 'function') this._resetChildIndexMaps();
          this._childrenBuilt = true;
          // SSR hydration: the server already emitted this component's children.
          // The DSL walk still runs — refs and event delegation must be in place
          // before connected() runs, and only the walk creates them — but it
          // walks the server's nodes and ADOPTS them instead of deleting them
          // first. Same walk, same order, same refs contract; what changes is
          // that a matching node is kept and has the def's tokens applied to it
          // in place rather than being rebuilt beside its own corpse. Where a
          // node does not match, the frame is abandoned and the rest of the
          // subtree is built exactly the way the clear-and-rebuild built it.
          // Only a node the server sent is adopted from: one the DSL builder
          // created during the hydration window (_swcBuilt) holds its own
          // authored children, not a template.
          const hydrating = isHydrating() && this.firstChild != null && !this._swcBuilt;
          // The seam the server writes between this component's own template
          // and the instance children authored around it — the template pass
          // stops there and the authored pass starts after it.
          const boundary = hydrating ? authoredBoundary(this) : null;
          if (hydrating) beginAdopt(this, this.firstChild, boundary);
          this._labelForEls = null;
          this._applyChildElements(this._childElements, this);
          // Anything the template did not claim goes — unless a boundary said
          // the tail belongs to the authored pass, which owns it instead.
          if (hydrating) endAdopt(this, !boundary);
          // The authored instance children (e.g. a form's fields) are a second
          // pass by the ORIGINAL authoring owner, so #prop hand-downs in that
          // subtree keep binding to the component whose def wrote them (not to
          // this element). Passed through ctx.owner — setting the rendering
          // component around the call was useless, because _applyChildElements
          // re-set it to `this` internally.
          if (hydrating && this._authoredChildDefs) {
            if (boundary) beginAdopt(this, boundary.nextSibling);
            this._applyChildElements(this._authoredChildDefs, this,
              this._authoredChildOwner ? { owner: this._authoredChildOwner } : {});
            if (boundary) endAdopt(this, true);
          }
          // Authored (non-data-driven) selectable children get the same
          // pending-value re-apply the list-data path has: a value that was
          // bound before the options existed selects its option now, so a
          // client-built seg/tab shows its initial [selected].
          if (this._isSelectable && this._value != null) {
            this.value = this._value;
          }
          (this._labelForEls || [])
            .filter((labelEl) => this.refs?.[labelEl._labelForRef])
            .forEach((labelEl) => {
              const control = this.refs[labelEl._labelForRef];
              const id = control.id || ('swcf' + (++labelForSeq));
              control.id = id;
              labelEl.htmlFor = id;
            });
        }
      }
      if (!this._eventsSetup) {
        this._eventsSetup = true;
        this._setupEmits();
        this._setupToggles();
        this._setupChildEmitToggles();
        this._setupDeclaredListeners();
        this._setupDragContract();
      }
      if (typeof this._setupSelectable === 'function' && !this._selectableSetup) {
        this._selectableSetup = true;
        this._setupSelectable();
      }
      // @seg=<key>: on EVERY connect, not once. The subscription is in _subs,
      // which disconnect clears, so a cached page coming back re-reads the
      // address it comes back to (see selection-mixin.js).
      if (typeof this._bindSegment === 'function') this._bindSegment();
      if (typeof this._setupKeyboardNav === 'function' && !this._keyboardNavSetup) {
        this._keyboardNavSetup = true;
        this._setupKeyboardNav();
      }
      if (typeof this._setupGridNav === 'function' && !this._gridNavSetup) {
        this._gridNavSetup = true;
        this._setupGridNav();
      }
      if (typeof this._setupSync === 'function' && !this._syncSetup) {
        this._syncSetup = true;
        this._setupSync();
      }
      if (typeof this._setupFloatingPanel === 'function' && !this._floatingPanelSetup) {
        this._setupFloatingPanel();
      }
      this._setupStoreAttrBindings();
      applyAllCssVars(this, cssVars);
      applyAllPassAttrs(this, passAttr);
      if (this._storeKey && !this._storeUnsub) {
        const prev = getRenderingComponent();
        setRenderingComponent(this);
        bindStoreToElement(this, this.store, this._storeKey, this._storeFallback);
        setRenderingComponent(prev);
      }
      // Re-establish reactive :prop=$store bindings recorded during SSR hydration
      // (e.g. the data-grid's :cols). Bound before :dataSource below so the grid
      // has its columns when its rows arrive — _pushRows needs cols first.
      if (this._ssrPropBindings && !this._ssrPropsBound) {
        this._ssrPropsBound = true;
        const prev = getRenderingComponent();
        setRenderingComponent(this);
        Object.entries(this._ssrPropBindings).forEach(([prop, key]) => {
          trackedSubscribe(this.store, key, (v) => { this[prop] = v; });
          this[prop] = this.store.getValue(key);
        });
        setRenderingComponent(prev);
      }
      // Re-establish a :dataSource binding torn down on the previous disconnect
      // (cached-page revisit). :dataSource always targets the global store.
      if (this._dataSourceKey && !this._storeUnsub) {
        const prev = getRenderingComponent();
        setRenderingComponent(this);
        bindStoreToElement(this, store, this._dataSourceKey);
        setRenderingComponent(prev);
      }
      // Re-establish :dataKey region bindings owned by THIS component — they
      // were torn down with _clearSubscriptions on disconnect, and the bound
      // container may be a native tag with no lifecycle of its own, so the
      // owner replays the re-bind hooks recorded at render time. Runs only
      // after a real teardown (the marker below), never on first connect.
      // Entries whose element left the tree for good are pruned; a show-if
      // recreation registers a fresh hook for its fresh element.
      // Re-establish the show-if gates owned by THIS component — disconnect
      // wiped every show-if state under it (cleanupShowIfTree) and cleared
      // their subscriptions, so the owner replays the registrations recorded
      // at render time: each entry goes back under its parent (a fresh state,
      // a fresh subscription) and is re-evaluated against the store as it is
      // NOW. Entries whose slot left the tree for good are pruned; a gate
      // inside a currently hidden gate is recreated by that gate when it
      // shows, and records itself afresh.
      //
      // Deferred a microtask, and this is load-bearing: a DOM move processes
      // each element's reactions in the order the elements were first
      // enqueued, so this owner's connected runs BEFORE its descendants'
      // disconnected — and a descendant component's disconnect wipes the
      // show-if state on itself (cleanupShowIfTree), which is where this
      // owner's entries under it live. Replaying synchronously registered the
      // gates and then watched the descendant erase them. After the
      // microtask every reaction of the move has run.
      if (this._showIfTornDown && this._showIfRebinds) {
        this._showIfTornDown = false;
        queueMicrotask(() => {
          if (!this.isConnected || !this._showIfRebinds) return;
          const prev = getRenderingComponent();
          setRenderingComponent(this);
          this._showIfRebinds = this._showIfRebinds.filter(({ parent, storeRef, entry }) => {
            if (!parent.isConnected || !(entry.anchor?.isConnected || entry.el?.isConnected)) return false;
            registerShowIfEntry(parent, storeRef, entry);
            updateShowIfEntry(entry, storeRef);
            return true;
          });
          setRenderingComponent(prev);
        });
      }
      if (this._dataKeyTornDown && this._dataKeyRebinds) {
        this._dataKeyTornDown = false;
        this._dataKeyRebinds = this._dataKeyRebinds.filter((entry) => {
          if (!entry.el.isConnected) return false;
          entry.rebind();
          return true;
        });
      }
      // Declared subscriptions are torn down on disconnect (tracked in _subs), so
      // re-establish them on each connect, mirroring the store-binding above.
      if (subscriptions && !this._declaredSubsSetup) {
        this._declaredSubsSetup = true;
        this._setupDeclaredSubscriptions();
      }
      // A def with `roleWhen` is interactive only in the uses that say so, so
      // its role, its aria state and its tab stop are per-instance rather than
      // per-def. Without roleWhen this is always true and nothing changes.
      const roleApplies = ariaRoleApplies(compiledAria, (a) => this.hasAttribute(a));
      if (roleApplies && ariaRole && !this.hasAttribute('role')) this.setAttribute('role', ariaRole);
      if (roleApplies) ariaStaticEntries.forEach(([k, v]) => {
        if (!this.hasAttribute(`aria-${k}`)) this.setAttribute(`aria-${k}`, v);
      });
      if (ariaRefsEntries.length) {
        this._applyAriaRefs();
        // Re-resolve after list renders (a deferred template may not exist at
        // connect). Chained once, the keyboardNav pattern.
        if (!this._ariaRefsChained) {
          this._ariaRefsChained = true;
          const existing = typeof this._afterRender === 'function' ? this._afterRender.bind(this) : null;
          this._afterRender = () => { if (existing) existing(); this._applyAriaRefs(); };
        }
      }
      if (ariaSourceAttrs.length && roleApplies) {
        ariaSourceAttrs.forEach((src) => reflectAria(this, src, ariaReflect[src]));
        if (typeof MutationObserver !== 'undefined') {
          if (!this._ariaObserver) {
            this._ariaObserver = new MutationObserver((muts) => {
              muts.forEach((m) => {
                if (ariaReflect[m.attributeName]) reflectAria(this, m.attributeName, ariaReflect[m.attributeName]);
              });
            });
          }
          this._ariaObserver.observe(this, { attributes: true, attributeFilter: ariaSourceAttrs });
        }
      }
      if (useFocusable && roleApplies) {
        // Reachable by Tab (unless SSR/roving already placed a tabindex) and
        // operable like a native button: Enter activates on keydown, Space on
        // keyup (native semantics), both suppress default scrolling.
        if (!this.hasAttribute('tabindex')) this.tabIndex = 0;
        if (!this._focusableKeysSetup) {
          this._focusableKeysSetup = true;
          this.addEventListener('keydown', (e) => {
            if (e.target !== this) return;
            if (e.key === 'Enter') { e.preventDefault(); this.click(); }
            else if (e.key === ' ') e.preventDefault();
          });
          this.addEventListener('keyup', (e) => {
            if (e.target !== this) return;
            if (e.key === ' ') { e.preventDefault(); this.click(); }
          });
        }
      }
      if (useFocusTrap) this._setupFocusTrap();
      // Declarative projection runs where the hand-written `connected(){
      // this._project(); }` wiring did: after the children template exists
      // (regions live in it), before the def's own connected(). Deferred
      // templates defer the projection the same way they hold connected().
      if (projectFn) {
        if (this._childrenDeferred) this._pendingProject = true;
        else this._runProject();
      }
      // A redacted host runs no connected(): the def's connected() reads refs
      // from a template that was never built (see the _redacted block above).
      if (callbacks.connected && !this._redacted) {
        // A deferred-children list holds its connected() until the template
        // exists (first data arrival) — its refs live in that template.
        if (this._childrenDeferred) this._pendingConnected = true;
        else callbacks.connected.call(this);
      }

      if (isVisible && this._data != null) this.render();

      // Auto-activation: activate if no ancestor is deactivated and
      // parent doesn't manage child activation (or this is selected)
      if (!this._isActivated && this._shouldAutoActivate()) {
        this.activate();
      }
    }

    disconnectedCallback() {
      this._isConnected = false;
      this._isActivated = false;
      if (this._ariaObserver) this._ariaObserver.disconnect();
      if (this._teardownFocusTrap) this._teardownFocusTrap();
      // The data binding's own sub is in _subs (registered via trackedSubscribe),
      // so _clearSubscriptions unsubscribes it; just reset the marker so a
      // reconnect re-binds.
      this._storeUnsub = null;
      this._declaredSubsSetup = false;
      // _clearSubscriptions below also kills the :dataKey region bindings this
      // component owns; mark it so the next connect replays their re-binds.
      if (this._dataKeyRebinds && this._dataKeyRebinds.length) this._dataKeyTornDown = true;
      // cleanupShowIfTree below wipes every show-if state this component owns;
      // mark it so the next connect replays the registrations.
      if (this._showIfRebinds && this._showIfRebinds.length) this._showIfTornDown = true;
      cleanupShowIfTree(this);
      this._clearSubscriptions();
      // Tear down the infinite-scroll IntersectionObserver so a detached (or
      // cached) list does not retain its sentinel/subtree; re-applied on the
      // next render after reconnect.
      if (hasInfiniteScroll(this)) removeInfiniteScroll(this);
      if (typeof this._cleanupSync === 'function') this._cleanupSync();
      if (callbacks.disconnected) callbacks.disconnected.call(this);
    }

    // ── Activation lifecycle ──

    // The state change is once-only; the CASCADE is not. A node can reach the
    // activated state before its own ancestors exist — on a server-rendered
    // page the primitives upgrade as their definitions land, and until the
    // component above them has upgraded, _shouldAutoActivate sees no
    // deactivated ancestor and lets them in. Returning early on the second call
    // then stopped the ancestor's cascade dead at that node, and everything
    // below it kept its subscriptions paused for good (a data grid with columns
    // and no rows). Passing through costs one tree walk and cannot loop: the
    // cascade only ever goes down.
    activate() {
      const first = !this._isActivated;
      this._isActivated = true;
      if (first) {
        this._subs.forEach((entry) => entry.sub.resume?.());
        if (callbacks.activated) callbacks.activated.call(this);
      }
      this._cascadeActivation(true);
    }

    deactivate() {
      const first = this._isActivated;
      this._isActivated = false;
      if (first) {
        this._subs.forEach((entry) => entry.sub.pause?.());
        if (callbacks.deactivated) callbacks.deactivated.call(this);
      }
      this._cascadeActivation(false);
    }

    // Resolve each aria.refs selector to the first subtree match and point the
    // aria-* relationship attribute at it (auto-id'd). A string value targets
    // the HOST ({ controls: 's-cn' } → host aria-controls); the object form
    // { from, to } wires two subtree children ({ describedby: { from: 's-i',
    // to: 's-fe' } } → the input describes-by the error element). Core a11y
    // infrastructure may query the subtree — the focus-trap precedent;
    // component code may not.
    _applyAriaRefs() {
      ariaRefsEntries.forEach(([attr, spec]) => {
        const isPair = spec && typeof spec === 'object';
        const from = isPair ? this.querySelector(spec.from) : this;
        const target = this.querySelector(isPair ? spec.to : spec);
        if (from && target) from.setAttribute(`aria-${attr}`, ensureAriaId(target));
      });
    }

    _shouldAutoActivate() {
      // If direct parent manages child activation, only activate if selected
      if (this.parentElement?._managesChildActivation) {
        return this.hasAttribute('selected');
      }
      // Walk ancestors — if any SWC ancestor is deactivated, don't activate
      let el = this.parentElement;
      while (el) {
        if ('_isActivated' in el && !el._isActivated) return false;
        el = el.parentElement;
      }
      return true;
    }

    _cascadeActivation(activate) {
      const walk = (parent) => {
        for (const child of parent.children || []) {
          if (typeof child.activate === 'function') {
            // If this child's parent manages activation, respect selection
            if (child.parentElement?._managesChildActivation) {
              if (activate && child.hasAttribute('selected')) child.activate();
              else if (!activate) child.deactivate();
            } else {
              if (activate) child.activate();
              else child.deactivate();
            }
          } else {
            // Walk through non-SWC elements (div, span, etc.)
            walk(child);
          }
        }
      };
      walk(this);
    }

    adoptedCallback() {
      if (callbacks.adopted) callbacks.adopted.call(this);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (this._attrLock) return;
      if (oldValue === newValue) return;
      const propName = toCamelCase(name);
      const value = coerceAttributeValue(newValue, false);
      this._attrLock = true;
      this[`_${propName}`] = value;
      this._attrLock = false;
      firePropDeps(this, propName, value);
      applyCssVar(this, cssVars, name, newValue);
      applyPassAttr(this, passAttr, name, newValue);

      if (Array.isArray(storeAttrs) && storeAttrs.includes(name)) {
        if (this.id) {
          this.store.setValue(this.id, newValue !== null);
        }
      }
      const handlerName = `on${propName.charAt(0).toUpperCase()}${propName.slice(1)}Change`;
      const handler = this[handlerName];
      // A redacted host runs no change handler — it has no template for the
      // handler to read (same rule as connected()). Every DSL path that could
      // set a prop on it is stripped; this is for app code addressing it.
      if (typeof handler === 'function' && !this._redacted) {
        handler.call(this, newValue, oldValue);
      }

      // Direct attribute writes on @-declared props re-project too (the prop
      // setter path is locked out above, so this is the only trigger here).
      if (projectFn && this._isConnected && _propNames.has(propName)) this._scheduleProject();
    }

    set showIf(value) {
      if (typeof value === 'function') {
        this._showIf = value;
      } else if (value === undefined) {
        this._showIf = () => true;
      } else {
        const boolValue = Boolean(value);
        this._showIf = () => boolValue;
      }
      if (this._isConnected) {
        const isVisible = this._applyShowIf();
        if (isVisible && this._childElements && !this._childrenBuilt) {
          this._childrenBuilt = true;
          this._applyChildElements(this._childElements, this);
        }
        if (isVisible && this._data != null) this.render();
      }
    }

    get showIf() {
      return this._showIf;
    }

    // Selection methods provided by selection-mixin.js when selectable: true

    set baseURL(value) {
      this._baseURL = value;
    }

    get baseURL() {
      return this._baseURL;
    }

    set routerSegment(value) {
      this._routerSegment = value;
    }

    get routerSegment() {
      return this._routerSegment;
    }

    set routeParams(value) {
      this._routeParams = value;
    }

    get routeParams() {
      return this._routeParams;
    }

    // `selected` is the selection flag of the item protocol and reflects as
    // PRESENCE: the attribute is there or it is not. It is also a name a def
    // can declare in `props`, and the declared accessor never replaces this
    // one (defineProp keeps the first descriptor), so a component that stores
    // an id in it gets `selected=""` and reads back `true`, and nothing said
    // so (s-arc lost its selection mark to exactly this). The coercion stands
    // (the mixins and every stylesheet key off presence); what changes is
    // that a string or number is reported, once per tag. The check is one
    // typeof on the boolean path the mixins take, which is why it can sit in
    // the setter.
    set selected(value) {
      const coerced = (typeof value === 'string' && value !== '') || typeof value === 'number';
      if (coerced && !selectedCoercionWarned) {
        selectedCoercionWarned = true;
        console.warn(`[swc] <${tagList[0]}>: "selected" is the selection flag and reflects as a boolean attribute; ${JSON.stringify(value)} was coerced to ${Boolean(value)}. Keep an id in a plain prop or in the data.`);
      }
      setAttrValue(this, 'selected', Boolean(value));
    }

    get selected() {
      return this.hasAttribute('selected');
    }

    toggleSelected() {
      this.selected = !this.selected;
    }

    set data(value) {
      // A redacted host takes no data: whatever feeds it — an owner's ref, a
      // parent list stamping rows, a store binding — would render the value
      // the guard removed. The mask is the whole node.
      if (this._redacted) return;
      if (this._internalData) {
        // Keep the raw reference so item context lookups ($item, getItemData,
        // delegation handlers reading this._data) see this element's own data.
        this._data = value;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          this._internalData.setValues(value);
          if (!this._childElements && value.text !== undefined) {
            this.textContent = String(value.text ?? '');
          }
        } else {
          this._internalData.setValue('value', value);
          this._applyDataText(value);
        }
        // Data is a first-class project() input, so its arrival re-projects —
        // without this, a :dataSource-fed projecting component would project
        // once on connect with empty data and never again.
        if (projectFn && this._isConnected) this._scheduleProject();
        return;
      }
      // List mixin overrides this with append support when itemRenderer is set
      this._data = value;
      if (this._isConnected) this.render();
      if (projectFn && this._isConnected) this._scheduleProject();
    }

    // String/number data displays as the element's text. Mirrors the '=' text
    // token: on children-template components the value lives in a dedicated
    // leading text node so the template children survive; on template-less
    // components it stays a plain textContent write.
    _applyDataText(value) {
      const isText = typeof value === 'string' || typeof value === 'number';
      if (!this._childElements) {
        if (isText) this.textContent = String(value);
        return;
      }
      if (!isText) {
        if (this._dataTextNode) this._dataTextNode.textContent = '';
        return;
      }
      if (this._dataTextNode) {
        this._dataTextNode.textContent = String(value);
        return;
      }
      this._dataTextNode = document.createTextNode(String(value));
      this.insertBefore(this._dataTextNode, this.firstChild);
    }

    get data() {
      if (this._internalData) return this._dataProxy;
      return this._data;
    }

    setData(path, value) {
      if (!this._internalData) return;
      this._internalData.setValue(path, value);
    }

    // Declarative projection: collect the declared props (plus the element's
    // bound data) and run the def's pure project(props), writing each returned
    // region into the internal data store. Auto-wired on connect and on every
    // declared-prop/data change; components with out-of-band inputs (e.g. a
    // custom data setter) call this directly. Never throws — a broken project
    // renders its component without regions, matching the SSR side. Contract
    // note: return EVERY region on every call — omitted keys keep their
    // previous children (nothing clears them).
    _runProject() {
      if (!projectFn) return;
      let regions = null;
      try {
        const projectProps = { data: this._hasCustomData ? this.data : this._data };
        for (const name of _propNames) projectProps[name] = this[name];
        regions = projectFn(projectProps);
      } catch (err) {
        console.warn(`[CreateComponent] ${tagList[0]} project() failed`, err);
        return;
      }
      if (!regions || typeof regions !== 'object') return;
      for (const [key, value] of Object.entries(regions)) this.setData(key, value);
    }

    // Change-triggered re-projection coalesces on a microtask: a fetch cycle
    // flips several declared props back-to-back (loading, total, showBody, …)
    // and each would otherwise tear down and rebuild every region. One flush
    // per cascade keeps projection idempotent AND cheap.
    _scheduleProject() {
      if (!projectFn || this._projectQueued) return;
      this._projectQueued = true;
      queueMicrotask(() => {
        this._projectQueued = false;
        if (this._isConnected) this._runProject();
      });
    }

    getData(path, defaultValue = undefined) {
      return this._internalData ? this._internalData.getValue(path, defaultValue) : defaultValue;
    }

    subscribeData(path, callback) {
      return this._internalData ? this._internalData.subscribe(path, callback) : null;
    }

    // A list component that connected with no data deferred its children
    // TEMPLATE (and its def's connected(), which reads refs from that
    // template). The first data arrival completes both, in order: template,
    // then connected(). Without this, s-dd built without data never wired
    // its display/panel and rendered "permanently expanded".
    _completeDeferredChildren() {
      if (!this._childrenDeferred) return;
      this._childrenDeferred = false;
      if (this._childElements && !this._childrenBuilt) {
        if (typeof this._resetChildIndexMaps === 'function') this._resetChildIndexMaps();
        this._childrenBuilt = true;
        this._applyChildElements(this._childElements, this);
      }
      if (this._pendingProject) {
        this._pendingProject = false;
        this._runProject();
      }
      if (this._pendingConnected) {
        this._pendingConnected = false;
        if (callbacks.connected && !this._redacted) callbacks.connected.call(this);
      }
    }

    render() {
      if (!this._isConnected || this._redacted) return;
      if (!this._applyShowIf()) return;
      if (this._data != null) this._completeDeferredChildren();

      // Data/property pass-through to children
      if (this._dataPassTargets?.length) {
        for (const target of this._dataPassTargets) {
          if (target.src) {
            // Property mapping with dot-path support: parent[src] or getByPath(parent, src)
            const val = target.src.includes('.')
              ? getByPath(this, target.src)
              : this[target.src];
            if (val !== undefined) target.el[target.prop] = val;
          } else if (this._data != null) {
            // Data pass-through: parent._data → child[prop]
            target.el[target.prop] = this._data;
          }
        }
        // If any target was a data pass, skip list rendering
        if (this._data != null) return;
      }

      if (Array.isArray(this._data)) {
        if (typeof this._renderList === 'function') this._renderList(this._data);
        return;
      }

      if (this._data && typeof this._data === 'object') {
        if (this._childElements && !this._childrenBuilt) {
          this._childrenBuilt = true;
          this._applyChildElements(this._childElements, this);
        }
        applyBindingsRecursive(this, this._data);
        if (typeof this.onDataChange === 'function') this.onDataChange(this._data);
      }
    }

    _setupEmits() {
      if (!emits || Object.keys(emits).length === 0) return;

      Object.entries(emits).forEach(([triggerEvent, emitEvent]) => {
        this.addEventListener(triggerEvent, (event) => {
          if (event._swcEmitted) return;
          const rawIndex = this.dataset?.index;
          const index = rawIndex !== undefined ? Number(rawIndex) : undefined;
          const detail = {
            // Object state first (the stamped item's _data), then the authored
            // @value= attribute — never a data-value coordinate (rule 15).
            value: this._data?.value ?? this.getAttribute('value'),
            item: this,
            // The emitting item's tag: every list-like re-emits clicks as a
            // bubbling `select`, so a container wrapping several of them can
            // filter by origin instead of capture-stopping the event (which
            // would kill the inner component's own select handling).
            origin: this.tagName.toLowerCase(),
          };
          if (Number.isFinite(index)) detail.index = index;
          const customEvent = new CustomEvent(emitEvent, { bubbles: true, detail });
          customEvent._swcEmitted = true;
          this.dispatchEvent(customEvent);
        });
      });
    }

    _setupToggles() {
      if (!toggles || Object.keys(toggles).length === 0) return;

      Object.entries(toggles).forEach(([triggerEvent, attrName]) => {
        this.addEventListener(triggerEvent, () => {
          const hasAttr = toggleAttr(this, attrName);

          if (this.id) this.store.setValue(this.id, hasAttr);
        });
      });
    }

    _setupChildEmitToggles() {
      if (!childEmitToggles || Object.keys(childEmitToggles).length === 0) return;

      Object.entries(childEmitToggles).forEach(([childEvent, attrName]) => {
        this.addEventListener(childEvent, (event) => {
          if (event.target === this) return;

          const hasAttr = toggleAttr(this, attrName);

          if (this.id) this.store.setValue(this.id, hasAttr);
        });
      });
    }

    // Declarative event listeners: { eventName: 'methodName' | fn } bound on self
    // (event delegation), so components declare handlers instead of wiring them
    // imperatively in connected().
    _setupDeclaredListeners() {
      if (!listeners) return;
      Object.entries(listeners).forEach(([eventName, handler]) => {
        this.addEventListener(eventName, (event) => {
          const fn = typeof handler === 'function' ? handler : this[handler];
          if (typeof fn === 'function') fn.call(this, event);
        });
      });
    }

    // The declarative drag contract. `dragSource: true` — this element announces
    // itself when dragged: the framework holds it as an OBJECT REFERENCE (see
    // drag-context.js; ids never round-trip through dataTransfer), flips its
    // `dragging` prop (attribute-reflected when declared), and emits a bubbling
    // `itemdrag` / `itemdragend`. `dropZone: true | predicate` — this element
    // owns its own dragover/dragleave/drop, reflects the hover through its
    // `drop` prop, and announces `itemdrop` with { item, zone } — both live
    // object references. A zone only reacts to FRAMEWORK drags (one is held),
    // and a predicate (called with this = the element) can gate acceptance on
    // instance state, e.g. `dropZone() { return this.cell !== undefined; }`.
    _setupDragContract() {
      if (dragSource) {
        this.addEventListener('dragstart', (e) => {
          // Firefox refuses to start a drag with an empty payload.
          if (e.dataTransfer) { e.dataTransfer.setData('text/plain', ''); e.dataTransfer.effectAllowed = 'move'; }
          setDragged(this);
          this.dragging = true;
          this.dispatchEvent(new CustomEvent('itemdrag', { bubbles: true, detail: { item: this } }));
        });
        this.addEventListener('dragend', () => {
          this.dragging = false;
          clearDragged();
          this.dispatchEvent(new CustomEvent('itemdragend', { bubbles: true, detail: { item: this } }));
        });
      }
      if (dropZone) {
        const accepts = () => getDragged() !== null && (dropZone === true || Boolean(dropZone.call(this)));
        this.addEventListener('dragover', (e) => {
          if (!accepts()) return;
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
          this.drop = true;
        });
        this.addEventListener('dragleave', (e) => {
          if (!this.contains(e.relatedTarget)) this.drop = false;
        });
        this.addEventListener('drop', (e) => {
          if (!accepts()) return;
          e.preventDefault();
          this.drop = false;
          // drop fires before the source's dragend, so the reference is still held.
          this.dispatchEvent(new CustomEvent('itemdrop', { bubbles: true, detail: { item: getDragged(), zone: this } }));
        });
      }
    }

    // Declarative store subscriptions: { storeKey: 'methodName' | fn }. Uses the
    // tracked this.subscribe (auto pause/resume with activation, auto-cleanup on
    // disconnect), so no manual unsubscribe is needed.
    _setupDeclaredSubscriptions() {
      if (!subscriptions) return;
      Object.entries(subscriptions).forEach(([storeKey, handler]) => {
        this.subscribe(storeKey, (value) => {
          const fn = typeof handler === 'function' ? handler : this[handler];
          if (typeof fn === 'function') fn.call(this, value);
        });
      });
    }

    // _setupSelectable and _setupSync provided by selection-mixin.js

    _setupStoreAttrBindings() {
      if (!Array.isArray(storeAttrs) || storeAttrs.length === 0) return;
      if (!this.id) return;

      storeAttrs.forEach((attrName) => {
        if (this._storeAttrSubs[attrName]) return;
        const storeKey = this.id;
        const sub = this.store.subscribe(storeKey, (value) => {
          if (this._attrLock) return;
          this._attrLock = true;
          setAttrValue(this, attrName, Boolean(value));
          this._attrLock = false;
        });
        this._storeAttrSubs[attrName] = true;
        if (sub) this._subs.push({ sub, name: storeKey });

        const hasAttr = this.hasAttribute(attrName);
        const currentStoreValue = this.store.getValue(storeKey);
        if (currentStoreValue === undefined) {
          this.store.setValue(storeKey, hasAttr);
        } else if (currentStoreValue !== hasAttr) {
          setAttrValue(this, attrName, Boolean(currentStoreValue));
        }
      });
    }

    subscribe(storeName, callback) {
      if (typeof callback !== 'function') return null;

      const subscriptionId = Symbol('subscription');
      const wrapped = callback.bind(this);
      const sub = this.store.subscribe(storeName, wrapped);
      this._subs.push({ sub, id: subscriptionId, name: storeName });
      return subscriptionId;
    }

    unsubscribe(...args) {
      args.forEach((arg) => {
        const isStoreName = typeof arg === 'string';
        this._subs = this._subs.filter((entry) => {
          if ((isStoreName && entry.name === arg) || (!isStoreName && entry.id === arg)) {
            entry.sub.unsubscribe?.();
            return false;
          }
          return true;
        });
      });
    }

    // One registry: every store subscription (rendered-subtree bindings via
    // trackedSubscribe, storeAttr, template-attr, and public subscribe()) is
    // unsubscribed here.
    _clearSubscriptions() {
      this._subs.forEach((entry) => entry.sub.unsubscribe?.());
      this._subs = [];
      this._storeAttrSubs = {};
    }

    // List rendering (_renderList, _resolveItemRenderer) provided by list-mixin.js
    // Virtual scroll (_renderVirtual, _updateVirtual) provided by virtual-scroll.js
    // Infinite scroll (_setupInfiniteScroll) provided by infinite-scroll.js

    _applyChildElements(defs, target, ctx = {}) {
      const prev = getRenderingComponent();
      // ctx.owner: a rebuild pass replaying AUTHORED children (the hydration
      // clear in connectedCallback) must bind them as the ORIGINAL authoring
      // component — #prop hand-downs, ?prop: show-ifs, refs and store keys
      // belong to it, not to the host doing the re-apply. Setting the owner
      // BEFORE calling this method never worked: the line below re-set it to
      // `this`, which is how hydrated composites lost their # bindings (the
      // sign-in submit label bug).
      const owner = ctx.owner || this;
      setRenderingComponent(owner);
      const list = Array.isArray(defs) ? defs : [defs];
      list.forEach((def) => {
        const showIfRules = extractShowIfRules(def);
        if (showIfRules) {
          // One path for every source: a `?prop:` condition is pushed by the
          // owner's prop setter, a `?store:` / `?data:` one by its store, and
          // a conjunction of them by whichever fires — each re-evaluating the
          // whole thing through the entry's own function.
          const context = { owner, internalStore: owner._internalData, store: owner.store };
          const showIfStore = showIfRules.storeFor(context);
          const slotEnd = adoptShowIfSlot(target);
          const anchor = slotEnd || document.createComment('swc-showif');
          if (!slotEnd) placeChild(target, anchor);
          const entry = {
            def,
            ctx,
            anchor,
            parent: target,
            el: null,
            showIfFn: showIfRules.fn,
            deps: showIfRules.depsFor(context),
            propKeys: showIfRules.propKeys,
            storeRef: showIfStore,
            evalStore: owner.store,
            owner,
            static: declaresStatic(def),
            asItemTemplate: Boolean(ctx.item)
          };
          const storeForChildren = showIfStore || owner.store;
          updateShowIfEntry(entry, storeForChildren);
          if (slotEnd) closeShowIfSlot(target, slotEnd);
          registerShowIfEntry(target, storeForChildren, entry);
          return;
        }
        const result = createElementFromDSL(def, ctx, owner.store, Boolean(ctx.item), target);
        if (!result) return;
        const { el, childDefs } = result;

        if (ctx.item && childDefs) {
          this._itemTemplate = childDefs;
        }

        if (!ctx.item || !childDefs) {
          if (!result.adopted) placeChild(target, el);
          if (typeof this.registerSelectableChild === 'function') {
            this.registerSelectableChild(el);
          }
          if (typeof this.registerRouteChild === 'function') {
            this.registerRouteChild(el);
          }
        }
      });
      setRenderingComponent(prev);
    }

    _applyShowIf() {
      let visible = true;
      try {
        visible = Boolean(this.showIf({ data: this._data, item: this._data, el: this }, this.store));
      } catch (error) {
        console.warn('[swc] show-if evaluation threw; hiding element:', error);
        visible = false;
      }
      this.hidden = !visible;
      return visible;
    }
  }

  // The roles ride the prototype so the selection/sync mixins can read them
  // without threading def config through calls — and before connect order
  // has applied the role attribute (upgrade order follows definition order,
  // so a sync target may connect before its source carries `role`).
  if (ariaRole) CustomElement.prototype._ariaRole = ariaRole;
  if (ariaItemRole) CustomElement.prototype._ariaItemRole = ariaItemRole;

  // Apply mixins based on component config — only list components get list/selection code
  if (itemRenderer || itemRendererField) {
    applyListMixin(CustomElement.prototype, { itemRenderer, itemRendererField });
  }
  if (selectable) {
    applySelectionMixin(CustomElement.prototype);
    applySelectableValue(CustomElement.prototype, baseElement);
  }
  if (syncFromParent) {
    applySyncMixin(CustomElement.prototype);
  }
  if (floatingPanel != null) {
    applyFloatingPanelMixin(CustomElement.prototype, floatingPanel);
  }
  if (useFocusTrap) {
    applyFocusTrapMixin(CustomElement.prototype);
  }
  if (keyboardNav) {
    applyKeyboardNavMixin(CustomElement.prototype, keyboardNav === true ? {} : keyboardNav);
  }
  if (gridNav) {
    applyGridNavMixin(CustomElement.prototype);
  }

  if (methods && typeof methods === 'object') {
    const descriptors = Object.getOwnPropertyDescriptors(methods);
    if (descriptors.data?.set) CustomElement.prototype._hasCustomData = true;
    Object.keys(descriptors).forEach((name) => {
      Object.defineProperty(CustomElement.prototype, name, descriptors[name]);
    });
  }

  // Component dataField default: explicit option wins, then the wrapped native
  // element's documented field (s-i/s-ta extend input/textarea and get 'value'),
  // then 'data'. Lives on the prototype so per-element overrides (the :dataField
  // token) stay instance-level.
  CustomElement.prototype.dataField =
    dataField || (nativeTag && NATIVE_DATA_FIELDS.get(nativeTag)) || 'data';

  // Auto-generate property getters/setters for declared attrs
  // Skip attrs that already have getters/setters from methods
  if (attrs && attrs.length) {
    for (const attrName of attrs) {
      const camel = toCamelCase(attrName);
      if (Object.getOwnPropertyDescriptor(CustomElement.prototype, camel)) continue;
      if (Object.getOwnPropertyDescriptor(CustomElement.prototype, attrName)) continue;
      Object.defineProperty(CustomElement.prototype, camel, {
        get() { return this.getAttribute(attrName) || ''; },
        set(v) {
          if (v === null || v === undefined || v === false || v === '') this.removeAttribute(attrName);
          else if (v === true) this.setAttribute(attrName, '');
          else this.setAttribute(attrName, v);
        },
        enumerable: true,
        configurable: true,
      });
    }
  }

  // Props system: auto-generate getters/setters with child propagation
  function defineProp(proto, name, attrName) {
    if (Object.getOwnPropertyDescriptor(proto, name)) return;
    const handlerName = `on${name.charAt(0).toUpperCase()}${name.slice(1)}Change`;
    Object.defineProperty(proto, name, {
      get() {
        if (this['_' + name] !== undefined) return this['_' + name];
        if (!attrName) return undefined;
        return coerceAttributeValue(this.getAttribute(attrName));
      },
      set(value) {
        if (this['_' + name] === value) return;
        this['_' + name] = value;
        if (attrName) {
          this._attrLock = true;
          setAttrValue(this, attrName, value);
          this._attrLock = false;
        }
        // A redacted host has no template: no prop deps to fire, no handler
        // to run (see the attributeChangedCallback gate).
        if (this._redacted) return;
        firePropDeps(this, name, value);
        if (typeof this[handlerName] === 'function') this[handlerName](value);

        // Declared-prop change re-runs the declarative projection (pure, so a
        // full re-project is always safe; coalesced per microtask). Pre-connect
        // writes wait for the connect-time run — the template doesn't exist yet.
        if (projectFn && this._isConnected) this._scheduleProject();
      },
      enumerable: true,
      configurable: true,
    });
  }

  for (const name of _props) defineProp(CustomElement.prototype, name, null);
  for (const [name, attrName] of _attr) defineProp(CustomElement.prototype, name, attrName);

  // A prop/attr `x` is backed by the instance field `this._x` (see defineProp's
  // getter/setter). A method literally named `_x` is therefore SILENTLY shadowed
  // the first time `x` is set: the setter writes `this._x = value`, an instance
  // property that hides the prototype method, and any `~ev:call:_x` event binding
  // then reads a non-function and quietly no-ops. It is maddening to debug (the
  // event fires, the handler "runs", nothing happens). Surface it once, loudly,
  // at definition time rather than at some later interaction.
  if (methods && typeof methods === 'object') {
    const backing = new Set([..._props, ..._attr.map(([n]) => n)].map((n) => `_${n}`));
    for (const m of Object.keys(methods)) {
      if (backing.has(m)) {
        console.warn(`[swc] <${tag || tags?.[0] || 'component'}>: method "${m}" collides with the backing field of prop "${m.slice(1)}" and will be shadowed when that prop is set. Rename the method so it does not start with "_" + a prop name.`);
      }
    }
  }

  tagList.forEach((tagName) => {
    if (!customElements.get(tagName)) {
      registeredDefs.set(tagName, options);
      registerKnownTag(tagName);
      if (nativeTag) {
        builtInRegistry.set(tagName, nativeTag);
        customElements.define(tagName, class extends CustomElement { }, { extends: nativeTag });
      } else {
        customElements.define(tagName, class extends CustomElement { });
      }
    } else if (isFallbackTag(tagName)) {
      // The recovery got there first: the tag was missing when an element
      // needed it, so it was floored as a base SWC element — and
      // customElements.define is permanent, so THIS def, the real one, is now
      // dead. Silence here would leave a component that renders nothing and
      // ignores every prop, with no clue why. Say exactly what happened.
      console.error(`[CreateComponent] "${tagName}" arrived too late: an element needed it before `
        + 'this def ran, so it was registered as a fallback element and this definition has NO '
        + 'effect. Import the component before the first render that uses it, or raise '
        + 'components.maxWaitMs / components.graceMs in CreateApp.');
    } else if (registeredDefs.get(tagName) !== options) {
      // A different def wants an already-claimed tag — that one is a bug.
      console.warn(`[CreateComponent] "${tagName}" already defined by a DIFFERENT def, skipping`);
    }
  });

  return CustomElement;
}

function renderDSL(def, storeRef, parent) {
  const showIfRules = extractShowIfRules(def);
  if (showIfRules && parent) {
    const owner = getRenderingComponent();
    const context = { owner, internalStore: owner?._internalData, store: storeRef };
    const anchor = document.createComment('swc-showif');
    parent.appendChild(anchor);
    const entry = {
      def, ctx: {}, anchor, parent, el: null,
      showIfFn: showIfRules.fn, deps: showIfRules.depsFor(context),
      propKeys: showIfRules.propKeys, evalStore: storeRef, owner,
      static: declaresStatic(def), asItemTemplate: false,
    };
    updateShowIfEntry(entry, storeRef);
    registerShowIfEntry(parent, storeRef, entry);
    return entry.el;
  }
  if (showIfRules) {
    const visible = showIfRules.fn({}, storeRef);
    if (!visible) return null;
  }
  const result = createElementFromDSL(def, {}, storeRef, false);
  if (!result) return null;
  if (parent) parent.appendChild(result.el);
  return result.el;
}
return { registerService, setRenderingComponent, getRenderingComponent, setAttrValue, cleanupShowIfTree, applyBindingsRecursive, cascadeActivate, noteDeclarativeTags, noteNativeTags, createElementFromDSL, CreateComponent, renderDSL };
})();

const __m31 = (() => {
const normalizeElementDef = __m20.normalizeElementDef;
const segmentKeyOf = __m21.segmentKeyOf;
const segmentBound = __m21.segmentBound;
const bindSegment = __m21.bindSegment;
const writeSegment = __m21.writeSegment;
const defValueOf = __m21.defValueOf;
const ssrSegmentChildren = __m21.ssrSegmentChildren;
const PANEL_TAGS = new Set(['s-acc-d', 's-acc-d']);
const isDetails = (el) => el && el.tagName === 'S-DT';
const panelOf = (dt) => Array.from(dt.children).find((c) => c.matches && c.matches('s-acc-d, swc-accordion-detail'));
// A panel's value: the s-acc-d's own, else the s-dt's, so either may carry it.
const panelValueOf = (dt) => {
    const panel = panelOf(dt);
    const own = panel ? (panel.value ?? panel.getAttribute('value')) : null;
    return own ?? dt.getAttribute('value');
};

// The SSR side of the same lookup: the value of the s-acc-d def under an
// s-dt def, else the s-dt def's own.
const panelDefValueOf = (dtDef) => {
    const model = normalizeElementDef(dtDef);
    const children = (model && model.children) || [];
    const panel = children.find((child) => {
        const m = child && typeof child === 'object' ? normalizeElementDef(child) : null;
        return m && PANEL_TAGS.has(m.tag);
    });
    return (panel && defValueOf(panel)) ?? defValueOf(dtDef);
};

const __default = {
    tags: ['s-acc', 's-acc'],
    props: ['@multi'],
    childEmitToggles: { 'toggle': 'open' },
    // @seg=<key>: the server opens the panel the address names, so first
    // paint agrees with hydration (F-811). Authored children only; the
    // static path stands when the address names nothing.
    ssr(input) {
        return ssrSegmentChildren(input, { attr: 'open', valueFor: panelDefValueOf });
    },
    callbacks: {
        connected() {
            if (!this._toggleSetup) {
                this._toggleSetup = true;
                this.addEventListener('toggle', (e) => {
                    const targetDt = e.target.closest('s-dt');
                    if (!targetDt || targetDt.parentElement !== this) return;
                    if (!this.hasAttribute('multi') && targetDt.hasAttribute('open')) {
                        Array.from(this.children)
                            .filter(dt => isDetails(dt) && dt !== targetDt && dt.hasAttribute('open'))
                            .forEach(dt => dt.removeAttribute('open'));
                    }
                    this._writeSegment();
                });
            }
            // @seg=<key>: on every connect, the way the selection mixin does it.
            const key = segmentKeyOf(this);
            if (key && !segmentBound(this, key)) {
                this._segKey = key;
                bindSegment(this, key, (value) => this._openBySegment(value));
            }
        }
    },
    methods: {
        // The read half: open the panel whose value the address names and
        // close the rest; an absent segment closes them all (the default of a
        // router-driven accordion). Locked so the toggles it causes do not
        // write themselves back.
        _openBySegment(value) {
            const absent = value === null || value === undefined || value === '';
            this._segLock = true;
            try {
                Array.from(this.children).filter(isDetails).forEach((dt) => {
                    const open = !absent && String(panelValueOf(dt)) === String(value);
                    if (open !== dt.hasAttribute('open')) dt.open = open;
                });
            } finally {
                this._segLock = false;
            }
        },
        // The write half: the open panel's value, or nothing when all are
        // closed. A multi accordion has no single value for an address.
        _writeSegment() {
            if (!this._segKey || this._segLock || this.hasAttribute('multi')) return;
            const open = Array.from(this.children).filter(isDetails).find((dt) => dt.hasAttribute('open'));
            writeSegment(this, this._segKey, open ? panelValueOf(open) : null, null);
        },
    },
};
return { __default };
})();

const __m32 = (() => {
const __default = { tags: ['s-art', 's-art'], aria: { role: 'article' } };
return { __default };
})();

const __m33 = (() => {
const __default = { tags: ['s-as', 's-as'], aria: { role: 'complementary' } };
return { __default };
})();

const __m34 = (() => {
const __default = { tags: ['s-c', 's-c'] };
return { __default };
})();

const __m35 = (() => {
const __default = {
  tags: ['s-cn', 's-cn'],
  props: ['@cols'],
  methods: {
    onColsChange(value) {
      // Drive the grid template through a custom property; the [cols] CSS rule
      // owns display:grid and the responsive collapse (no imperative layout).
      if (!value) {
        this.style.removeProperty('--cols-tpl');
        return;
      }
      this.style.setProperty('--cols-tpl', value.includes('-')
        ? value.split('-').map(n => n + 'fr').join(' ')
        : `repeat(${value}, 1fr)`);
    },
  },
};
return { __default };
})();

const __m36 = (() => {
// The router outlet inside a route FRAME: the one place a frame says "the page
// goes here". A route names its frame (`frame: 'dash-frame'` in the route
// table, inherited by its children, replaced by a child that names its own),
// the router mounts that frame in the app outlet, finds this element inside
// it and mounts the page there; the server does the same with
// dslToHtml's outletFill. A bare container, no behaviour: the frame's own
// layout (`@l=`) decides where it sits, and the router owns what is in it.
const __default = {
  tags: ['s-ou', 's-ou'],
};
return { __default };
})();

const __m37 = (() => {
// Details / disclosure (s-dt): owns the `open` state; contains a summary header
// and a content region (used standalone and as each accordion panel). The
// WAI-ARIA disclosure pattern is declarative now: the summary's role/focus/keys
// come from ITS def (s-acc-s: role=button + focusable derivation), the region
// role from s-acc-d's def, and the aria-controls/labelledby pair from the refs
// below. The one imperative remainder is _reflectExpanded — the summary's
// aria-expanded mirrors THIS element's `open` attribute, a cross-element state
// reflection the declarative layer intentionally does not model.

const SUMMARY_SEL = 's-acc-s, swc-accordion-summary';
const CONTENT_SEL = 's-acc-d, swc-accordion-detail';

const __default = {
    tags: ['s-dt', 's-dt'],
    props: ['@open'],
    aria: {
        refs: {
            // Wired between the children, not the host: the summary controls
            // the region; the region is labelled by the summary.
            controls: { from: SUMMARY_SEL, to: CONTENT_SEL },
            labelledby: { from: CONTENT_SEL, to: SUMMARY_SEL },
        },
    },
    childEmitToggles: { toggle: 'open' },
    storeAttrs: ['open'],
    callbacks: {
        connected() {
            this._reflectExpanded();
        },
    },
    methods: {
        _reflectExpanded() {
            const summary = this.querySelector && Array.from(this.children).find((c) => c.matches && c.matches(SUMMARY_SEL));
            if (summary) summary.setAttribute('aria-expanded', this.hasAttribute('open') ? 'true' : 'false');
        },
        onOpenChange() {
            this._reflectExpanded();
        },
    },
};
return { __default };
})();

const __m38 = (() => {
const __default = {
    tags: ['s-dr', 's-dr'],
    dispatches: ['drawerexpand'],
    // @psn is the edge it slides from (left default, right, top, bottom);
    // @sz scales the panel along that axis (sm / lg).
    props: ['@open', '@psn', '@sz', '@dismiss', '@expandable'],
    storeAttrs: ['open'],
    aria: { role: 'dialog', modal: 'open' },
    callbacks: {
        connected() {
            // An @expandable drawer gets a maximize toggle in its panel, the way
            // s-md gets its close button. A NATIVE button: internal chrome must
            // not inherit the public s-b skin. Both glyphs are stamped and the
            // skin hides the one that does not apply, so toggling stays in CSS
            // rather than rewriting an icon name on every click.
            const [panel] = this.children;
            // Every drawer is dismissable, the way every modal is. The click
            // handler below has always matched .dr-close, but nothing ever
            // stamped one: unless the author happened to write that class
            // themselves, an open drawer had no way out at all — @dismiss= is
            // opt-in, and Escape closes only through the focus trap. Native
            // button, like the expand toggle: internal chrome must not inherit
            // the public s-b skin.
            if (panel && !this._drCloseBuilt && !panel.querySelector('.dr-close')) {
                this._drCloseBuilt = true;
                this._applyChildElements([
                    ['button.dr-close', '@type=button', '@aria-label=$t.a11y.close', [
                        ['s-ic', '@n=x'],
                    ]],
                ], panel);
            }
            if (this.expandable && panel && !this._drExpandBuilt) {
                this._drExpandBuilt = true;
                this._applyChildElements([
                    ['button.dr-expand', ':ref=expandBtn', '@type=button',
                        '@aria-label=$t.a11y.expand', '@aria-pressed=false', [
                        ['s-ic.dr-expand-in', '@n=maximize'],
                        ['s-ic.dr-expand-out', '@n=minimize'],
                    ]],
                ], panel);
            }

            this.addEventListener('click', (e) => {
                if (!this.hasAttribute('open')) return;

                // Expand toggle. Matched through the ref we stamped, not a
                // selector lookup — the button is ours, so we already hold it.
                if (this.refs.expandBtn && this.refs.expandBtn.contains(e.target)) {
                    const expanded = !this.hasAttribute('expanded');
                    this.toggleAttribute('expanded', expanded);
                    this.refs.expandBtn.setAttribute('aria-pressed', String(expanded));
                    this.dispatchEvent(new CustomEvent('drawerexpand', {
                        bubbles: true,
                        detail: { expanded },
                    }));
                    return;
                }

                // Close button click
                if (e.target.closest('.dr-close')) {
                    this.removeAttribute('open');
                    return;
                }

                // Backdrop dismiss (click on self, not on panel child)
                if (this.hasAttribute('dismiss') && e.target === this) {
                    this.removeAttribute('open');
                }
            });
        }
    }
};
return { __default };
})();

const __m39 = (() => {
const __default = { tags: ['s-ft', 's-ft'], aria: { role: 'contentinfo' } };
return { __default };
})();

const __m40 = (() => {
const __default = { tags: ['s-hd', 's-hd'], aria: { role: 'banner' } };
return { __default };
})();

const __m41 = (() => {
const __default = { tags: ['s-m', 's-m'] };
return { __default };
})();

const __m42 = (() => {
const __default = {
    tags: ['s-md', 's-md'],
    dispatches: ['modalclose'],
    props: ['@open', '@drop', '@dismiss', '@blocking'],
    storeAttrs: ['open'],
    aria: { role: 'dialog', modal: 'open' },
    callbacks: {
        connected() {
            // Add the close button as last child of the content box so every
            // modal is dismissable (styled by s-md .md-close, top-right).
            //
            // Except a BLOCKING one. `blocking` means the user must choose —
            // it is the alert-dialog semantic — and a close button is a third
            // exit that contradicts it. shadcn draws it the same way round: its
            // Dialog carries a close X and its AlertDialog deliberately does
            // not. The backdrop-dismiss path below is already gated the same
            // way, so this makes the affordance agree with the behaviour.
            const [content] = this.children;
            if (content && !this._mdCloseBuilt && !this.hasAttribute('blocking')) {
                this._mdCloseBuilt = true;
                // A real button: inside a focus trap a non-interactive close
                // affordance is a keyboard jail. Named via the framework's
                // t.a11y strings so locale packs can override it.
                this._applyChildElements([
                    ['s-b.md-close', '@v=ic', '@aria-label=$t.a11y.close', [['s-ic', '@n=x']]],
                ], content);
            }

            this.addEventListener('click', (e) => {
                if (!this.hasAttribute('open')) return;

                // Close button click (the button we add as last child of content).
                // Announce the user-initiated close so an owner driving the modal
                // from a data key can follow suit without matching our button.
                if (e.target.closest('.md-close')) {
                    this.removeAttribute('open');
                    this.dispatchEvent(new CustomEvent('modalclose', { bubbles: true }));
                    return;
                }

                // Backdrop dismiss (click on self, not on content child)
                if (this.hasAttribute('dismiss') && e.target === this) {
                    this.removeAttribute('open');
                    this.dispatchEvent(new CustomEvent('modalclose', { bubbles: true }));
                }
            });
        }
    }
};
return { __default };
})();

const __m43 = (() => {
// Named after <nav> but autonomous — the landmark role supplies what the
// tag name promises. (A baseElement conversion would break every s-nv tag
// selector in framework and site CSS.)
const __default = { tags: ['s-nv', 's-nv'], aria: { role: 'navigation' } };
return { __default };
})();

const __m44 = (() => {
const panelHeader = { tag: 's-pn-hd' };
const panelBody = { tag: 's-pn-bd' };
const panelFooter = { tag: 's-pn-ft' };

// Slot template used only when the panel is driven by a data object.
// Header/body/footer each render their slice of the data into the matching
// styled sub-tag. Content may be a plain string or a DSL child array —
// dataField=children handles both. The header and footer slots only appear
// when their data is present.
const dataSlots = [
    ['s-pn-hd', '?data:title', ':dataKey=title', ':dataField=children'],
    ['s-pn-bd', ':dataKey=body', ':dataField=children'],
    ['s-pn-ft', '?data:footer', ':dataKey=footer', ':dataField=children'],
];

// The panel supports two authoring styles that both render into the same
// styled slots (s-pn-hd / s-pn-bd / s-pn-ft):
//
//   1. Data-driven — pass a data object and the panel builds the slots:
//        ['s-pn', { ':data': { title, body: [...], footer: [...] } }]
//      Used by route content, which the client re-renders from the DSL.
//
//   2. Slotted — author the styled sub-tags directly as children:
//        ['s-pn', [['s-pn-hd', ...], ['s-pn-bd', [...]], ['s-pn-ft', [...]]]]
//      Used by persistent layout/overlay content (drawers), which is
//      server-rendered and hydrated in place rather than re-rendered, so the
//      content must exist as real serialized child elements.
//
// A children DSL template is deliberately not used: it would append the empty
// data slots on top of any authored slot children. Instead the data slots are
// built on demand from the data setter, so a slotted panel keeps exactly the
// children it was authored with.
const panel = {
    tags: ['s-pn', 's-pn'],
    props: ['@title'],
    methods: {
        set data(value) {
            this._data = value;
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                this._internalData.setValues(value);
            }
            if (!this._slotsBuilt) {
                this._slotsBuilt = true;
                this._applyChildElements(dataSlots, this);
            }
        },
        get data() {
            return this._dataProxy;
        },
    },
};

const __default = [panelHeader, panelBody, panelFooter, panel];
return { __default };
})();

const __m45 = (() => {
const onOutsideClick = __m9.onOutsideClick;
const removeOutsideClick = __m9.removeOutsideClick;
const __default = {
    tags: ['s-po', 's-po'],
    props: ['@open', '@psn'],
    storeAttrs: ['open'],
    callbacks: {
        connected() {
            onOutsideClick(this, (e) => {
                if (!this.hasAttribute('open')) return;
                if (this.parentElement?.contains(e.target)) return;
                this.store.setValue(this.id, false);
            });
            // A popover is a non-modal popup (deliberately NOT role=dialog —
            // that would focus-trap tooltip-ish content), but Escape must
            // dismiss it like a click outside does.
            if (!this._popoverKeySetup) {
                this._popoverKeySetup = true;
                this._popoverKeyHandler = (e) => {
                    if (e.key === 'Escape' && this.hasAttribute('open')) this.store.setValue(this.id, false);
                };
                document.addEventListener('keydown', this._popoverKeyHandler);
            }
        },
        disconnected() {
            removeOutsideClick(this);
            if (this._popoverKeyHandler) {
                document.removeEventListener('keydown', this._popoverKeyHandler);
                this._popoverKeySetup = false;
                this._popoverKeyHandler = null;
            }
        },
    },
};
return { __default };
})();

const __m46 = (() => {
const __default = { tags: ['s-sc', 's-sc'] };
return { __default };
})();

const __m47 = (() => {
const __default = {
    tags: ['s-afx', 's-afx'],
    props: ['@offsetTop', '@offsetBottom'],
    cssVars: { offsetTop: 'px', offsetBottom: 'px' },
};
return { __default };
})();

const __m48 = (() => {
// Base list — every list behaviour, no decoration.
//
// This is the whole list mechanism: the item renderer, selection, and through
// list-mixin the data binding, virtual scroll, infinite scroll, polymorphic
// per-item renderers and itemAttrs. It draws nothing. `s-lb` is a bare
// container that happens to stamp one element per row.
//
// Use it wherever the list IS the arrangement rather than a bordered block of
// rows: chip strips, layout containers, nested rows inside another list, a
// calendar's day cells. Before this existed, every one of those places styled
// `s-ls` and then undid the box by hand — the same four properties stripped in
// four separate files, framework and app alike.
//
// s-ls (list.js) is this definition plus a border. Nothing else.
const __default = {
  tags: ['s-lb', 's-lb'],
  // Generic list semantics: list/listitem, deliberately NOT listbox/option —
  // the default list is content (cards, rows, facts), and announcing every
  // content list as a pick-list would be wrong far more often than right.
  // Purpose-built selection widgets (select, menu, transfer, chip-list)
  // declare their own roles; s-ls inherits this via the spread.
  aria: { role: 'list', itemRole: 'listitem' },
  itemRenderer: 's-li',
  selectable: true,
};
return { __default };
})();

const __m49 = (() => {
const listBase = __m48.__default;
const MONTHS_FULL = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function formatDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDate(str) {
    if (!str) return new Date();
    const parts = str.split('-');
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2] || '1', 10));
}

function buildMonthData(year, month, selectedStr, todayStr) {
    // Headers render as labels, not cells: a label is never focusable, so the
    // roving tabindex has nothing to manage there — and aria-hidden keeps the
    // listbox's children all options as far as AT is concerned (each day cell
    // carries its full date, so "Mo" names nothing a reader needs).
    const items = WEEKDAYS.map((text) => ({ text, itemRenderer: 's-l', attrs: { weekday: '', role: 'presentation', 'aria-hidden': 'true' } }));
    const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();

    const dayLabel = (date, todayStr2) => {
        const l = `${date.getDate()} ${MONTHS_FULL[date.getMonth()]} ${date.getFullYear()}`;
        return formatDate(date) === todayStr2 ? `${l}, today` : l;
    };

    Array.from({ length: firstDayOfWeek }).forEach((_, i) => {
        const d = prevMonthDays - firstDayOfWeek + 1 + i;
        const date = new Date(year, month - 1, d);
        items.push({ text: String(d), value: formatDate(date), attrs: { other: '', 'aria-label': dayLabel(date, todayStr) } });
    });

    Array.from({ length: daysInMonth }).forEach((_, i) => {
        const d = i + 1;
        const date = new Date(year, month, d);
        const dateStr = formatDate(date);
        const item = { text: String(d), value: dateStr, attrs: { 'aria-label': dayLabel(date, todayStr) } };
        if (dateStr === todayStr) item.attrs.today = '';
        if (dateStr === selectedStr) item.selected = true;
        items.push(item);
    });

    const cellCount = items.length - WEEKDAYS.length;
    const remaining = (7 - (cellCount % 7)) % 7;
    Array.from({ length: remaining }).forEach((_, i) => {
        const date = new Date(year, month + 1, i + 1);
        items.push({ text: String(i + 1), value: formatDate(date), attrs: { other: '', 'aria-label': dayLabel(date, todayStr) } });
    });

    return items;
}

function buildYearData(year, selectedDate, today) {
    return MONTHS_SHORT.map((text, m) => {
        const item = { text, value: String(m), attrs: { 'aria-label': `${MONTHS_FULL[m]} ${year}` } };
        if (year === today.getFullYear() && m === today.getMonth()) item.attrs.today = '';
        if (year === selectedDate.getFullYear() && m === selectedDate.getMonth()) item.selected = true;
        return item;
    });
}

// The day grid: list-base plus what a date PICKER owes a keyboard. s-lb keeps
// generic list/listitem semantics on purpose; a calendar's cells are options
// in a pick-list, and before this existed they were mouse-only — day cells a
// Tab press could never reach. cols:7 turns the arrows into week/day moves
// (grid-nav-mixin wants role=row descendants; this grid is one flat stamped
// list, which the 1D mixin's cols option was made for).
// The calendar IS a pick-list, so its cell is an option with its selection
// announced — s-li stays the generic content row its own file documents, and
// per that file's rule a true pick-list gets a purpose-built item. A weekday
// header stamped into the same list arrives with an authored role=presentation
// + aria-hidden, which wins over the def role (create-component honours an
// existing role attribute).
const calendarCell = {
    tags: ['s-cal-cell', 's-cal-c'],
    props: ['@selected'],
    aria: { role: 'option', selected: 'selected' },
    emits: { click: 'selected' },
};

const calendarGrid = {
    ...listBase,
    tags: ['s-cal-grid', 's-cal-gr'],
    itemRenderer: 's-cal-c',
    aria: { role: 'listbox', itemRole: 'option' },
    keyboardNav: { cols: 7 },
    methods: {
        // Weekday headers are stamped into the same list; they are headings,
        // not stops. (The mixin's own filter drops disabled/inert only.)
        _navItems() {
            return (this.items || []).filter((el) =>
                el && !el.hasAttribute?.('weekday') && !el.hasAttribute?.('disabled')
                && !el.hasAttribute?.('inert') && el.getAttribute?.('data-guard') !== 'redact');
        },
    },
};

const calendar = {
    tags: ['s-cal', 's-cal'],
    dispatches: ['select'],
    props: ['@value', '@mode', '@fullscreen'],
    children: [
        ['s-cn', [
            ['s-b', '@v=ic', '@icon=chevron-left', '@aria-label=$t.a11y.prevMonth', '~cl:set:data:nav:-1'],
            ['s-l', '$title', '~cl:toggle:data:yearMode'],
            ['s-b', '@v=ic', '@icon=chevron-right', '@aria-label=$t.a11y.nextMonth', '~cl:set:data:nav:1'],
        ]],
        ['s-cal-gr', '$items', '~sel:set:data:pick:$detail.value'],
    ],
    callbacks: {
        connected() {
            this._viewDate = parseDate(this._value);
            if (!this._mode) this.mode = 'month';

            this.subscribeData('nav', (dir) => {
                if (dir == null) return;
                this.setData('nav', null);
                this._navigate(Number(dir));
            });

            this.subscribeData('yearMode', (val) => {
                this.mode = val ? 'year' : 'month';
            });

            this.subscribeData('pick', (val) => {
                if (val == null || val === '') return;
                this.setData('pick', null);
                if (this._mode === 'year') {
                    this._viewDate.setMonth(Number(val));
                    this.value = formatDate(this._viewDate);
                    this.mode = 'month';
                } else {
                    const prev = this._value;
                    this.value = val;
                    // The prop setter dedupes an unchanged value, which would
                    // swallow a repeat pick entirely — but a USER pick is
                    // always announced, so the owner can treat picking the
                    // already-selected day as a toggle. A changed value was
                    // announced by onValueChange; only the repeat needs this.
                    if (String(prev ?? '') === String(val ?? '')) {
                        this.dispatchEvent(new CustomEvent('select', { bubbles: true, detail: { value: val } }));
                    }
                }
            });

            this._buildData();
        },
    },
    methods: {
        onValueChange(v) {
            if (!this._isConnected) return;
            this._viewDate = parseDate(v);
            // Always rebuild: the selected day is part of the grid's DATA, so a
            // value change inside the same month repaints too. The old guard
            // (rebuild only when the visible month changed) let the click path
            // LOOK right — the selection mixin paints the clicked cell — while
            // a programmatic value (a bound date, a toggle-off) changed nothing
            // visible: the emphasised day and the value silently diverged.
            this._buildData();
            this.dispatchEvent(new CustomEvent('select', { bubbles: true, detail: { value: v } }));
        },
        onModeChange() {
            if (!this._isConnected) return;
            this._buildData();
        },
        _navigate(dir) {
            if (this._mode === 'year') {
                this._viewDate.setFullYear(this._viewDate.getFullYear() + dir);
            } else {
                this._viewDate.setMonth(this._viewDate.getMonth() + dir);
            }
            this._buildData();
        },
        _buildData() {
            const vd = this._viewDate || new Date();
            const year = vd.getFullYear();
            const month = vd.getMonth();
            const today = new Date();
            const todayStr = formatDate(today);
            const selected = parseDate(this._value);

            if (this._mode === 'year') {
                this.setData('title', String(year));
                this.setData('items', buildYearData(year, selected, today));
            } else {
                this.setData('title', `${MONTHS_FULL[month]} ${year}`);
                this.setData('items', buildMonthData(year, month, this._value, todayStr));
            }
        },
    },
};

const __default = [calendarCell, calendarGrid, calendar];
return { __default };
})();

const __m50 = (() => {
// Command palette (s-cmd) — a dialog hosting a filter box over grouped commands.
//
// It does NOT use the keyboard-nav mixin: that mixin walks a fixed item set with
// a roving tabindex, whereas a palette's item set changes on every keystroke and
// focus has to stay in the search box the whole time. So the active item is
// tracked with [active] + aria-activedescendant instead of real focus — the
// pattern a combobox uses, not the one a menu uses.
//
// Authored children are s-mn-i, optionally carrying @group (section heading) and
// @kbd (shortcut hint). Everything else — filtering, headings, empty state — is
// derived, so callers only declare commands.
const __default = {
    tags: ['s-cmd', 's-cmd'],
    dispatches: ['command'],
    props: ['@open', '@hotkey', '@placeholder'],
    storeAttrs: ['open'],
    aria: { role: 'dialog', modal: 'open' },
    children: [
        ['s-cn.cmd-box', [
            ['s-cn.cmd-search', [
                ['s-ic', '@n=search'],
                ['input.cmd-input', ':ref=cmdInput', '@type=text', '@role=combobox',
                    '@aria-expanded=true', '@aria-autocomplete=list', '@autocomplete=off'],
            ]],
            ['s-cn.cmd-list', ':ref=cmdList', '@static=', '@role=listbox'],
            ['s-cn.cmd-empty', ':ref=cmdEmpty', '@static=', '="No results"'],
        ]],
    ],
    callbacks: {
        connected() {
            const list = this.refs.cmdList;
            if (list) {
                Array.from(this.children)
                    .filter(c => c.tagName === 'S-MN-I' || c.tagName === 'SWC-MENU-ITEM')
                    .forEach(item => list.appendChild(item));
            }
            this._cmdBuildGroups();

            const input = this.refs.cmdInput;
            if (input && this.placeholder) input.placeholder = this.placeholder;

            input?.addEventListener('input', () => this._cmdFilter(input.value));

            this.addEventListener('keydown', (e) => {
                if (e.key === 'ArrowDown') { e.preventDefault(); this._cmdMove(1); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); this._cmdMove(-1); }
                else if (e.key === 'Home') { e.preventDefault(); this._cmdSetActive(this._cmdVisible()[0]); }
                else if (e.key === 'End') { e.preventDefault(); const v = this._cmdVisible(); this._cmdSetActive(v[v.length - 1]); }
                else if (e.key === 'Enter') { e.preventDefault(); this._cmdRun(this._cmdActive()); }
                else if (e.key === 'Escape') { e.preventDefault(); this._cmdClose(); }
            });

            this.addEventListener('click', (e) => {
                // Backdrop is the host itself; the box swallows its own clicks.
                if (e.target === this) { this._cmdClose(); return; }
                const item = e.target.closest('s-mn-i, swc-menu-item');
                if (item) this._cmdRun(item);
            });

            // Opt-in global shortcut: @hotkey=k gives Cmd/Ctrl+K.
            const hotkey = this.getAttribute('hotkey');
            if (hotkey) {
                this._cmdHotkey = (e) => {
                    if (e.key.toLowerCase() !== hotkey.toLowerCase()) return;
                    if (!(e.metaKey || e.ctrlKey)) return;
                    e.preventDefault();
                    if (this.hasAttribute('open')) this._cmdClose();
                    else this.setAttribute('open', '');
                };
                document.addEventListener('keydown', this._cmdHotkey);
            }

            if (typeof MutationObserver !== 'undefined') {
                // `open` arrives via a storeAttr binding, so onOpenChange does not
                // fire for it — same reason s-mn observes the attribute directly.
                this._cmdObserver = new MutationObserver(() => this._cmdOnToggle());
                this._cmdObserver.observe(this, { attributes: true, attributeFilter: ['open'] });
            }
            if (this.hasAttribute('open')) this._cmdOnToggle();
        },
        disconnected() {
            if (this._cmdHotkey) document.removeEventListener('keydown', this._cmdHotkey);
            this._cmdObserver?.disconnect();
        },
    },
    methods: {
        _cmdItems() {
            return Array.from(this.refs.cmdList?.querySelectorAll('s-mn-i, swc-menu-item') || []);
        },
        _cmdVisible() {
            return this._cmdItems().filter(el => !el.hidden);
        },
        _cmdActive() {
            return this._cmdItems().filter(el => el.hasAttribute('active'))[0] || null;
        },
        // Headings are derived from @group so the caller never authors markup that
        // could fall out of sync with the items it labels.
        _cmdBuildGroups() {
            const list = this.refs.cmdList;
            if (!list) return;
            let current = null;
            this._cmdItems().forEach((item) => {
                const group = item.getAttribute('group');
                if (group && group !== current) {
                    current = group;
                    const heading = document.createElement('div');
                    heading.className = 'cmd-group';
                    heading.setAttribute('role', 'presentation');
                    heading.textContent = group;
                    list.insertBefore(heading, item);
                }
                const kbd = item.getAttribute('kbd');
                if (kbd && !item.querySelector('.cmd-kbd')) {
                    const hint = document.createElement('kbd');
                    hint.className = 'cmd-kbd';
                    hint.textContent = kbd;
                    item.appendChild(hint);
                }
            });
        },
        _cmdFilter(query) {
            const q = (query || '').trim().toLowerCase();
            this._cmdItems().forEach((item) => {
                // Match the label only — the shortcut hint is chrome, and letting
                // it match would make "k" hit every command that shows a ⌘K.
                const label = (item.textContent || '')
                    .replace(item.querySelector('.cmd-kbd')?.textContent || '', '')
                    .toLowerCase();
                item.hidden = q ? !label.includes(q) : false;
            });

            // A heading with nothing under it is noise; hide it with its section.
            Array.from(this.refs.cmdList?.querySelectorAll('.cmd-group') || []).forEach((heading) => {
                let any = false;
                for (let el = heading.nextElementSibling; el && !el.classList.contains('cmd-group'); el = el.nextElementSibling) {
                    if (!el.hidden) { any = true; break; }
                }
                heading.hidden = !any;
            });

            const visible = this._cmdVisible();
            if (this.refs.cmdEmpty) this.refs.cmdEmpty.hidden = visible.length > 0;
            this._cmdSetActive(visible[0] || null);
        },
        _cmdSetActive(item) {
            this._cmdItems().forEach(el => el.removeAttribute('active'));
            if (!item) {
                this.refs.cmdInput?.removeAttribute('aria-activedescendant');
                return;
            }
            item.setAttribute('active', '');
            if (!item.id) item.id = `cmd-i-${Math.abs(this._cmdItems().indexOf(item))}-${this.id || 'x'}`;
            this.refs.cmdInput?.setAttribute('aria-activedescendant', item.id);
            item.scrollIntoView({ block: 'nearest' });
        },
        _cmdMove(delta) {
            const visible = this._cmdVisible();
            if (!visible.length) return;
            const idx = visible.indexOf(this._cmdActive());
            const next = idx === -1 ? 0 : (idx + delta + visible.length) % visible.length;
            this._cmdSetActive(visible[next]);
        },
        _cmdRun(item) {
            if (!item) return;
            this.dispatchEvent(new CustomEvent('command', {
                bubbles: true,
                detail: { value: item.getAttribute('value') || item.textContent.trim(), item },
            }));
            this._cmdClose();
        },
        _cmdClose() {
            this.removeAttribute('open');
        },
        _cmdOnToggle() {
            if (!this.hasAttribute('open')) {
                const target = this._cmdReturnFocus;
                this._cmdReturnFocus = null;
                if (target && typeof target.focus === 'function') target.focus();
                return;
            }
            this._cmdReturnFocus = document.activeElement;
            const input = this.refs.cmdInput;
            if (input) {
                // Reopening with a stale query would show yesterday's filter.
                input.value = '';
                this._cmdFilter('');
                requestAnimationFrame(() => input.focus());
            }
        },
    },
};
return { __default };
})();

const __m51 = (() => {
const __default = {
    tags: ['s-desc', 's-desc'],
    props: ['@title', '@bordered', '@column', '@sz', '@dir'],
    internalStore: true,
    cssVars: { column: '' },
    children: [
        ['s-cn.desc-title', '?data:title', ':dataKey=title'],
        ['s-cn.desc-grid', ':ref=grid', ':dataKey=gridChildren', ':dataField=children'],
    ],
    callbacks: {
        connected() {
            this.style.setProperty('--cols', this.column || '3');
            this.data.title = this.title || '';
            this._buildGrid();
        }
    },
    methods: {
        _buildGrid() {
            const items = Array.from(this.children)
                .filter(el => el.tagName === 'S-DESC-I' || el.tagName === 'SWC-DESCRIPTIONS-ITEM');
            if (items.length === 0) return;

            const layout = this.dir || 'horizontal';

            // Collect item data before removing elements
            const itemData = items.map(item => ({
                span: parseInt(item.getAttribute('span') || '1', 10),
                label: item.getAttribute('label') || '',
                childNodes: Array.from(item.childNodes),
            }));
            items.forEach(item => item.remove());

            if (layout === 'vertical') {
                this.data.gridChildren = itemData.map(item => {
                    const cellDef = ['s-cn.desc-cell'];
                    if (item.span > 1) cellDef.push(`@style=grid-column: span ${item.span}`);
                    cellDef.push([
                        ['s-cn.desc-label', `="${item.label}"`],
                        ['s-cn.desc-value'],
                    ]);
                    return cellDef;
                });

                // Move child nodes into desc-value containers after render
                requestAnimationFrame(() => {
                    const grid = this.refs?.grid;
                    if (!grid) return;
                    const cells = Array.from(grid.children);
                    cells.forEach((cell, i) => {
                        if (!itemData[i]) return;
                        const valSlot = cell.lastElementChild;
                        if (valSlot) itemData[i].childNodes.forEach(node => valSlot.appendChild(node));
                    });
                });
            } else {
                const children = [];
                itemData.forEach(item => {
                    children.push(['s-cn.desc-label', `="${item.label}"`]);
                    const valDef = ['s-cn.desc-value'];
                    if (item.span > 1) valDef.push(`@style=grid-column: span ${item.span * 2 - 1}`);
                    children.push(valDef);
                });
                this.data.gridChildren = children;

                // Move child nodes into desc-value containers after render
                requestAnimationFrame(() => {
                    const grid = this.refs?.grid;
                    if (!grid) return;
                    const valueEls = Array.from(grid.children)
                        .filter((_, idx) => idx % 2 === 1);
                    valueEls.forEach((val, i) => {
                        if (!itemData[i]) return;
                        itemData[i].childNodes.forEach(node => val.appendChild(node));
                    });
                });
            }
        },
        onColumnChange() {
            this.style.setProperty('--cols', this.column || '3');
        },
        onTitleChange() {
            this.data.title = this.title || '';
        }
    }
};
return { __default };
})();

const __m52 = (() => {
const measureThenWrite = __m9.measureThenWrite;
const __default = {
    tags: ['s-msn', 's-msn'],
    props: ['@columns', '@gap'],
    cssVars: { columns: '', gap: 'px' },
    callbacks: {
        connected() {
            if (!this.hasAttribute('columns')) {
                this._ro = new ResizeObserver(() => this._autoColumns());
                this._ro.observe(this);
                this._autoColumns();
            }
        },
        disconnected() {
            if (this._ro) {
                this._ro.disconnect();
                this._ro = null;
            }
        }
    },
    methods: {
        _autoColumns() {
            // Batched: reading offsetWidth right after another component's
            // style write forces a reflow; the shared queue reads once per
            // frame for every instance, then writes.
            const minItemWidth = 250;
            measureThenWrite(
                () => this.offsetWidth,
                (width) => {
                    const cols = Math.max(1, Math.floor(width / minItemWidth));
                    this.style.setProperty('--columns', cols);
                },
            );
        },
        onColumnsChange() {
            if (this.hasAttribute('columns') && this._ro) {
                this._ro.disconnect();
                this._ro = null;
            }
        }
    }
};
return { __default };
})();

const __m53 = (() => {
const onOutsideClick = __m9.onOutsideClick;
const removeOutsideClick = __m9.removeOutsideClick;
const __default = {
    tags: ['s-pcf', 's-pcf'],
    dispatches: ['cancel', 'confirm'],
    props: ['@title', '@open', '@okText', '@cancelText', '@okType'],
    // A small confirmation dialog: focus trap + Escape ride the role; the
    // labelledby ref resolves when the trap activates (the popover content is
    // show-if mounted, so it does not exist until open).
    aria: { role: 'dialog', refs: { labelledby: 's-cn.pcf-title' } },
    internalStore: true,
    storeAttrs: ['open'],
    children: [
        ['s-cn.pcf-popover', '?prop:open', [
            ['s-cn.pcf-title', ':dataKey=title'],
            // Native buttons have no default data field, so the bound store key
            // must say where the text goes — without it the pills render empty.
            ['s-cn.pcf-actions', [
                ['button.pcf-cancel', ':dataKey=cancelText', ':dataField=textContent'],
                ['button.pcf-ok', ':dataKey=okText', ':dataField=textContent'],
            ]],
        ]],
    ],
    callbacks: {
        connected() {
            this.data = {
                title: this.title || 'Are you sure?',
                okText: this.okText || 'OK',
                cancelText: this.cancelText || 'Cancel',
            };

            this.addEventListener('click', (e) => {
                if (e.target.closest('.pcf-ok')) {
                    this.removeAttribute('open');
                    this.dispatchEvent(new CustomEvent('confirm', { bubbles: true }));
                    return;
                }
                if (e.target.closest('.pcf-cancel')) {
                    this.removeAttribute('open');
                    this.dispatchEvent(new CustomEvent('cancel', { bubbles: true }));
                    return;
                }
                if (e.target.closest('.pcf-popover')) return;
                if (!this.hasAttribute('open')) {
                    this.setAttribute('open', '');
                }
            });

            onOutsideClick(this, (e) => {
                if (this.hasAttribute('open') && !this.contains(e.target)) {
                    this.removeAttribute('open');
                }
            });
        },
        disconnected() {
            removeOutsideClick(this);
        }
    }
};
return { __default };
})();

const __m54 = (() => {
const renderDSL = __m30.renderDSL;
// A resize handle between two panes. Distinguished by the [separator] attribute
// (never a class, never a data- marker) and carrying its boundary ordinal on
// :index for drag hit-testing. The grip is a real s-ic (never a unicode glyph):
// a single line of three dots per orientation; both render and the stylesheet
// shows the one matching @dir, the same stacked-icon pattern the transfer
// checkboxes use.
function separatorDef(index, direction, valuenow) {
    // APG window-splitter: the handle is a focusable separator announcing its
    // position (valuenow = the size of the pane before it) and orientation.
    // swc-lint-ok: :index is an expando the splitter reads back (sep.index), by design
    return ['s-cn', '@separator=', `:index=${index}`, '@role=separator', '@tabindex=0',
        `@aria-orientation=${direction === 'vertical' ? 'horizontal' : 'vertical'}`,
        `@aria-valuenow=${Math.round(valuenow)}`, '@aria-valuemin=10', '@aria-valuemax=90', [
        ['s-ic', '@n=ellipsis-vertical'],
        ['s-ic', '@n=ellipsis'],
    ]];
}

// ── s-spl ── Resizable split container.
//
// Doctrine: the splitter has NO children: template. A component that owns a
// children: template has its inline authored children wiped by the prod-SSR
// hydration rebuild (connectedCallback clears the server-rendered children then
// rebuilds only the template), which left the splitter with zero panes. With no
// template, the authored panes survive as real children. On connect the splitter
// reads those surviving panes and inserts a separator (built via renderDSL, the
// sanctioned DOM builder for SWC tags) between
// each adjacent pair, so the resize handles are real siblings of the panes.
// renderDSL is the sanctioned builder; the panes are never built imperatively.
const __default = {
    tags: ['s-spl', 's-spl'],
    dispatches: ['resize'],
    props: ['@dir', '@sizes'],
    callbacks: {
        connected() {
            this._buildLayout();

            this.addEventListener('mousedown', (e) => {
                const sep = this._separatorFor(e.target);
                if (!sep) return;
                this._startDrag(e, parseInt(sep.index, 10));
            });
            this.addEventListener('touchstart', (e) => {
                const sep = this._separatorFor(e.target);
                if (!sep) return;
                this._startDrag(e, parseInt(sep.index, 10));
            }, { passive: false });
            this.addEventListener('dblclick', (e) => {
                if (this._separatorFor(e.target)) this._resetSizes();
            });
            this.addEventListener('keydown', (e) => {
                const sep = this._separatorFor(e.target);
                if (!sep) return;
                const index = parseInt(sep.index, 10);
                const vertical = this._direction === 'vertical';
                const grow = vertical ? 'ArrowDown' : 'ArrowRight';
                const shrink = vertical ? 'ArrowUp' : 'ArrowLeft';
                if (e.key === grow) { e.preventDefault(); this._nudgeBoundary(index, 2); }
                else if (e.key === shrink) { e.preventDefault(); this._nudgeBoundary(index, -2); }
                else if (e.key === 'Home') { e.preventDefault(); this._nudgeBoundary(index, 10 - this._sizes[index]); }
                else if (e.key === 'End') { e.preventDefault(); this._nudgeBoundary(index, (100 - 10) - this._sizes[index]); }
                else if (e.key === 'Enter') { e.preventDefault(); this._resetSizes(); }
            });
        },
        disconnected() {
            if (this._onMove) document.removeEventListener('mousemove', this._onMove);
            if (this._onUp) document.removeEventListener('mouseup', this._onUp);
        }
    },
    methods: {
        // The handles are ours: _buildLayout records them, so locating the one
        // under a pointer or holding focus is a containment test against that
        // list rather than a climb back up the DOM on every event.
        _separatorFor(target) {
            return (this._separators || []).find((s) => s === target || s.contains(target)) || null;
        },
        _buildLayout() {
            this._separators = [];
            const direction = this.dir || 'horizontal';
            // Drop any separators from a prior build so a re-connect is idempotent,
            // then read the surviving authored panes (everything that is not a handle).
            Array.from(this.children)
                .filter(c => c.hasAttribute('separator'))
                .forEach(c => c.remove());
            const panels = Array.from(this.children)
                .filter(c => !c.hasAttribute('separator'));
            if (panels.length < 2) return;

            // Parse initial sizes or distribute equally. `sizes` may reach us as the
            // attribute string ("48,52") or, once the reactive prop has been assigned
            // (e.g. reflected from a store binding, or after a prior drag), as a numeric
            // array — accept both so a re-connect never throws on `.split`.
            let sizes;
            const raw = this.sizes;
            if (Array.isArray(raw)) {
                sizes = raw.map(Number).filter(n => !Number.isNaN(n));
            } else if (typeof raw === 'string' && raw.trim()) {
                sizes = raw.split(',').map(s => parseFloat(s.trim())).filter(n => !Number.isNaN(n));
            } else {
                sizes = [];
            }
            if (sizes.length < panels.length) {
                const pct = 100 / panels.length;
                sizes = panels.map(() => pct);
            }

            // Normalize sizes to 100%
            const total = sizes.reduce((a, b) => a + b, 0);
            sizes = sizes.map(s => (s / total) * 100);

            this._panels = panels;
            this._sizes = sizes;
            // The authored proportions, kept so a reset returns to them. Taken
            // before the first drag writes over _sizes, and only on the first
            // build — a re-connect reads `sizes` back as the DRAGGED array, so
            // recapturing here would quietly redefine "default" as wherever the
            // handle was last left.
            if (!this._authoredSizes) this._authoredSizes = sizes.slice();
            this._direction = direction;

            // Apply initial sizes via CSS custom properties
            const prop = direction === 'vertical' ? '--spl-h' : '--spl-w';
            panels.forEach((panel, i) => {
                panel.style.setProperty(prop, sizes[i] + '%');
            });

            // Insert a resize handle between each adjacent pair of panes. The handle
            // for boundary i sits before pane i+1, so :index=i matches the drag math
            // (adjusts _sizes[i] / _sizes[i+1]).
            panels.slice(1).forEach((panel, i) => {
                const sep = renderDSL(separatorDef(i, direction, sizes[i]));
                this.insertBefore(sep, panel);
                this._separators.push(sep);
            });
        },
        // Keyboard resize: the separator is focusable, arrows move the boundary
        // by 2% (the drag's minimum-10% clamp applies), Home/End jump to the
        // clamps, Enter/double-click reset. Mirrors _startDrag's size math.
        _nudgeBoundary(index, delta) {
            const min = 10;
            let newLeft = this._sizes[index] + delta;
            let newRight = this._sizes[index + 1] - delta;
            if (newLeft < min) { newRight -= (min - newLeft); newLeft = min; }
            if (newRight < min) { newLeft -= (min - newRight); newRight = min; }
            this._sizes[index] = newLeft;
            this._sizes[index + 1] = newRight;
            const prop = this._direction === 'vertical' ? '--spl-h' : '--spl-w';
            this._panels[index].style.setProperty(prop, newLeft + '%');
            this._panels[index + 1].style.setProperty(prop, newRight + '%');
            const sep = Array.from(this.children).find((c) => c.hasAttribute('separator') && parseInt(c.index, 10) === index);
            if (sep) sep.setAttribute('aria-valuenow', String(Math.round(newLeft)));
        },
        _startDrag(e, index) {
            e.preventDefault();
            const direction = this._direction;
            const isVertical = direction === 'vertical';
            const containerRect = this.getBoundingClientRect();
            const containerSize = isVertical ? containerRect.height : containerRect.width;

            const startPos = e.type === 'touchstart'
                ? (isVertical ? e.touches[0].clientY : e.touches[0].clientX)
                : (isVertical ? e.clientY : e.clientX);

            const startSizes = [...this._sizes];

            this._onMove = (ev) => {
                const currentPos = ev.type === 'touchmove'
                    ? (isVertical ? ev.touches[0].clientY : ev.touches[0].clientX)
                    : (isVertical ? ev.clientY : ev.clientX);

                const delta = ((currentPos - startPos) / containerSize) * 100;

                let newLeft = startSizes[index] + delta;
                let newRight = startSizes[index + 1] - delta;

                // Minimum constraint: 10%
                const min = 10;
                if (newLeft < min) {
                    newLeft = min;
                    newRight = startSizes[index] + startSizes[index + 1] - min;
                }
                if (newRight < min) {
                    newRight = min;
                    newLeft = startSizes[index] + startSizes[index + 1] - min;
                }

                this._sizes[index] = newLeft;
                this._sizes[index + 1] = newRight;

                const cssProp = isVertical ? '--spl-h' : '--spl-w';
                this._panels[index].style.setProperty(cssProp, newLeft + '%');
                this._panels[index + 1].style.setProperty(cssProp, newRight + '%');
            };

            this._onUp = () => {
                document.removeEventListener('mousemove', this._onMove);
                document.removeEventListener('mouseup', this._onUp);
                document.removeEventListener('touchmove', this._onMove);
                document.removeEventListener('touchend', this._onUp);
                this.dispatchEvent(new CustomEvent('resize', {
                    bubbles: true,
                    detail: { sizes: [...this._sizes] }
                }));
            };

            document.addEventListener('mousemove', this._onMove);
            document.addEventListener('mouseup', this._onUp);
            document.addEventListener('touchmove', this._onMove, { passive: false });
            document.addEventListener('touchend', this._onUp);
        },
        // Back to the proportions the author asked for, not to equal panes. A
        // splitter written as @sizes=34,66 that reset to 50/50 handed the user
        // a layout nobody chose, and lost the author's intent on the first
        // double-click. Equal is still the fallback when nothing was authored.
        _resetSizes() {
            const authored = this._authoredSizes;
            const pct = 100 / this._panels.length;
            const cssProp = this._direction === 'vertical' ? '--spl-h' : '--spl-w';
            this._panels.forEach((panel, i) => {
                const size = (authored && authored.length === this._panels.length) ? authored[i] : pct;
                this._sizes[i] = size;
                panel.style.setProperty(cssProp, size + '%');
            });
            this.dispatchEvent(new CustomEvent('resize', {
                bubbles: true,
                detail: { sizes: [...this._sizes] }
            }));
        }
    }
};
return { __default };
})();

const __m55 = (() => {
const childElementsByTag = (el, tag) => el.querySelectorAll(`:scope > ${tag}`);

/**
 * Position an element relative to a target rect.
 * @param {HTMLElement} el — element to position
 * @param {DOMRect} rect — target bounding rect
 * @param {string} placement — bottom|top|left|right
 * @param {number} gap — space between target and element
 */
function positionAt(el, rect, placement = 'bottom', gap = 12) {
    el.style.cssText = '';
    // Measure after the style reset. top/left placements offset by the
    // element's own box in the SAME coordinate space as the rect, instead of
    // anchoring to window.innerHeight/Width — which broke whenever the
    // containing block was not the viewport (transformed/contained ancestors,
    // e.g. the tour inside a contained demo stage).
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const positions = {
        bottom: { top: rect.bottom + gap, left: rect.left },
        top: { top: rect.top - gap - h, left: rect.left },
        left: { top: rect.top, left: rect.left - gap - w },
        right: { top: rect.top, left: rect.right + gap },
    };
    Object.entries(positions[placement] || positions.bottom).forEach(([k, v]) => {
        el.style[k] = v + 'px';
    });
}

/**
 * Size and position an element to cover a target rect with padding.
 * @param {HTMLElement} el — element to size
 * @param {DOMRect} rect — target bounding rect
 * @param {number} pad — padding around target
 */
function coverRect(el, rect, pad = 4) {
    Object.assign(el.style, {
        top: (rect.top - pad) + 'px',
        left: (rect.left - pad) + 'px',
        width: (rect.width + pad * 2) + 'px',
        height: (rect.height + pad * 2) + 'px',
    });
}
return { childElementsByTag, positionAt, coverRect };
})();

const __m56 = (() => {
const positionAt = __m55.positionAt;
const coverRect = __m55.coverRect;
const __default = {
    tags: ['s-tour', 's-tour'],
    props: ['@open', '@current'],
    // A dialog named by the step title: the role auto-enables the focus trap
    // on [open], and the trap re-resolves refs on activation so the labelledby
    // survives per-step re-renders.
    aria: { role: 'dialog', refs: { labelledby: 's-cn.tour-title' } },
    internalStore: true,
    storeAttrs: ['open', 'current'],
    children: [
        ['s-cn.tour-mask', ':ref=mask', '~cl:call:_close', [
            ['s-cn.tour-hole', ':ref=hole', '?data:holeVisible'],
        ]],
        ['s-cn.tour-tooltip', ':ref=tooltip', [
            ['s-b.tour-close', '@v=ic', '@aria-label=$t.a11y.close', '~cl:call:_close', [['s-ic', '@n=x']]],
            ['s-cn.tour-title', ':dataKey=stepTitle'],
            ['s-cn.tour-desc', ':dataKey=stepDesc'],
            ['s-cn.tour-footer', [
                ['span.tour-counter', ':dataKey=stepCounter'],
                ['s-cn.tour-buttons', [
                    ['s-b', '?data:showPrev', '~cl:call:_prev', '="Previous"'],
                    ['s-b', '?data:showNext', '~cl:call:_next', '="Next"'],
                    ['s-b', '?data:showFinish', '~cl:call:_close', '="Finish"'],
                ]],
            ]],
        ]],
    ],
    callbacks: {
        connected() {
            // Bound :steps= / :anchors= reach their setters BEFORE this runs.
            this._steps = this._steps || [];
            this._anchors = this._anchors || {};
            // An `open` attribute written in markup is already present here, so
            // it never arrives as a CHANGE — onOpenChange's empty-tour guard
            // does not see it. Apply the same rule on connect, or the focus
            // trap engages for a tour with nothing to show and inerts the page.
            if (this.hasAttribute('open') && !this.steps.length) {
                this._openWhenReady = true;
                // The ATTRIBUTE, not just the prop: the focus trap observes
                // [open] on the element, and the stylesheet keys display on it.
                this.removeAttribute('open');
            }
            this._onKey = (e) => {
                if (e.key === 'Escape' && this.open) this._close();
            };
            document.addEventListener('keydown', this._onKey);
        },
        disconnected() {
            document.removeEventListener('keydown', this._onKey);
        }
    },
    methods: {
        set steps(arr) {
            this._steps = arr || [];
            // The documented order is `open` in markup, steps bound after — so
            // this is where a deferred open (see onOpenChange) is honoured.
            if (this._openWhenReady && this._steps.length) {
                this._openWhenReady = false;
                this.open = true;
                return;
            }
            if (this.open) this._renderStep();
        },
        get steps() {
            return this._steps || [];
        },
        // Anchor contract: the owning screen registers its target elements by a
        // named key (e.g. { upload: this.refs.uploadBtn }). Each step names the
        // key it anchors to via step.anchor. The tour positions against the bound
        // element reference — never a runtime selector lookup.
        set anchors(map) {
            this._anchors = map || {};
            if (this.open) this._renderStep();
        },
        get anchors() {
            return this._anchors || {};
        },
        // Navigation drives the REFLECTED props, not the internal store: the
        // store write never reaches the attribute, so on<Prop>Change (and with
        // it the re-render / close) would never fire.
        _next() {
            const current = parseInt(this.current || '0', 10);
            if (current < this.steps.length - 1) this.current = current + 1;
        },
        _prev() {
            const current = parseInt(this.current || '0', 10);
            if (current > 0) this.current = current - 1;
        },
        _close() {
            this.open = false;
        },
        // Centre the tooltip in the mask's own coordinate space — the same
        // space positionAt() works in, so anchored and unanchored steps agree
        // about where "here" is whether the tour covers the viewport or is
        // embedded in a transformed ancestor.
        _centreTooltip() {
            requestAnimationFrame(() => {
                const mask = this.refs.mask;
                const tooltip = this.refs.tooltip;
                if (!mask || !tooltip) return;
                // Clear first, as positionAt does: a previous anchored step may
                // have left its own offsets on the element.
                tooltip.style.cssText = '';
                const box = mask.getBoundingClientRect();
                const tip = tooltip.getBoundingClientRect();
                tooltip.style.left = `${Math.max(0, (box.width - tip.width) / 2)}px`;
                tooltip.style.top = `${Math.max(0, (box.height - tip.height) / 2)}px`;
            });
        },
        _renderStep() {
            // Through the getter, not the raw field. `@open=` in markup reaches
            // the setter before any steps are bound, so a tour opened by
            // attribute threw on an undefined array before it could render the
            // no-step branch two lines down.
            const current = parseInt(this.current || '0', 10);
            const step = this.steps[current];
            if (!step) {
                this.refs.mask?.removeAttribute('active');
                this.refs.tooltip?.removeAttribute('active');
                this.data.holeVisible = false;
                return;
            }

            // Update content + button visibility in one batch
            this.data = {
                stepTitle: step.title,
                stepDesc: step.description,
                stepCounter: `${current + 1} / ${this.steps.length}`,
                showPrev: current > 0,
                showNext: current < this.steps.length - 1,
                showFinish: current >= this.steps.length - 1,
                holeVisible: true,
            };

            // AFTER the batch, on freshly read refs. The holeVisible flip
            // remounts the container through its show-if, so flags written
            // before the write land on nodes that are about to be detached —
            // which left an open tour permanently inactive, and therefore
            // invisible once [active] began gating display.
            this.refs.mask?.setAttribute('active', '');
            this.refs.tooltip?.setAttribute('active', '');

            // Position relative to the bound anchor element the owner registered
            // under step.anchor. Geometry (getBoundingClientRect) stays imperative;
            // resolving the element is a keyed property read, not a DOM lookup.
            const target = step.anchor ? this.anchors[step.anchor] : null;
            if (!target) {
                // A step with no anchor is a WELCOME step: it introduces the
                // tour rather than pointing at a control, so there is no hole
                // to cut and nothing to position against. Centred in the mask,
                // not left in its corner — the tooltip is position:fixed, so
                // without this it lands wherever the containing block starts.
                this.data.holeVisible = false;
                this._centreTooltip();
                return;
            }

            target.scrollIntoView({ behavior: 'smooth', block: 'center' });

            requestAnimationFrame(() => {
                // Re-read the refs: the holeVisible flip above remounts the
                // hole through its show-if, so the reference captured at the
                // top of _renderStep may point at the detached old node.
                const maskNow = this.refs.mask;
                const holeNow = this.refs.hole;
                const tooltipNow = this.refs.tooltip;
                const rect = target.getBoundingClientRect();
                // The mask and tooltip are position:fixed, which resolves
                // against the nearest containing block — the viewport normally,
                // but a transformed ancestor when the tour is embedded (demo
                // stages, zoomed shells). Working in the mask's own coordinate
                // space is correct in both cases: its origin IS that block —
                // and, both rects moving with the same scroll, the difference
                // is also immune to the smooth scrollIntoView still running.
                const base = maskNow ? maskNow.getBoundingClientRect() : { left: 0, top: 0 };
                const rel = {
                    left: rect.left - base.left, top: rect.top - base.top,
                    right: rect.right - base.left, bottom: rect.bottom - base.top,
                    width: rect.width, height: rect.height,
                    x: rect.x - base.left, y: rect.y - base.top,
                };
                if (holeNow) coverRect(holeNow, rel);
                positionAt(tooltipNow, rel, step.placement);
            });
        },
        onOpenChange() {
            if (this.open) {
                // Opened with nothing to show. `open` in markup reaches this
                // setter during stamping, before the owner binds steps — and
                // `open` is also what engages the dialog focus trap, which
                // inerts everything outside the tour. An empty tour therefore
                // did not merely render blank: it made the rest of the page
                // unclickable, silently, for as long as it stayed open. Stay
                // closed and let the steps setter open for real.
                if (!this.steps.length) {
                    this._openWhenReady = true;
                    this.removeAttribute('open');
                    return;
                }
                if (!this.current) this.data.current = 0;
                this._renderStep();
            } else {
                this.refs.mask?.removeAttribute('active');
                this.refs.tooltip?.removeAttribute('active');
                this.data.holeVisible = false;
            }
        },
        onCurrentChange() {
            if (this.open) this._renderStep();
        }
    }
};
return { __default };
})();

const __m57 = (() => {
// Response types for the declarative HTTP client (core/services/http-client.js).
//
// Import-free and workerd-safe. Everything here is a pure function over a
// Response or a content-type string, so the client keeps ONE rule for "what
// did the endpoint answer with" and the specs pin that rule with no network.
//
//   responseType  'json' (default) | 'text' | 'html' | 'blob' | 'arrayBuffer' | 'stream'
//   expect        a case-insensitive content-type substring the answer must
//                 carry; json and html have an implicit one, the others none
//
// The gate exists because a server without the API mounted answers unknown
// paths with an HTML document and status 200. Parsing that as JSON yields
// "Unexpected token '<'" in the error key, or a "success" against a login
// page for a caller that only checked res.ok. A declared type with a declared
// expectation is refused with a sentence that names both, never parsed.

const RESPONSE_TYPES = ['json', 'text', 'html', 'blob', 'arrayBuffer', 'stream'];

// The implicit gate per type. `json` keeps the client's historical guard,
// `html` expects a document or a fragment. The text and binary types have no
// implicit gate: their servers rarely agree on a content type, so the app
// declares `expect` when it wants one.
const DEFAULT_EXPECT = { json: 'json', html: 'text/html' };

function isResponseType(responseType) {
  return RESPONSE_TYPES.includes(responseType);
}

// The substring the content-type must carry, or null when nothing is gated.
function expectedContentType(responseType, expect) {
  if (typeof expect === 'string' && expect) return expect;
  return DEFAULT_EXPECT[responseType] || null;
}

// Case-insensitive substring match. A missing or empty header never matches,
// so a gated endpoint that answers without a content-type is refused rather
// than parsed on faith.
function contentTypeMatches(contentType, expected) {
  if (typeof contentType !== 'string' || !contentType) return false;
  return contentType.toLowerCase().includes(String(expected).toLowerCase());
}

// The sentence the client writes when the gate refuses. The json default
// keeps the historical wording verbatim (apps and specs match on it); every
// other refusal names both types, the status and the URL.
function mismatchMessage({ responseType, expect, contentType, expected, status, url }) {
  const legacy = responseType === 'json' && !(typeof expect === 'string' && expect);
  if (legacy) return `The endpoint is not available on this server (${status} at ${url}).`;
  return `The endpoint answered "${contentType}" where "${expected}" was expected (${status} at ${url}).`;
}

// Read the body in the declared shape. `stream` hands the body back unread
// (the stream consumer owns it, core/services/http-stream.js); an unknown
// type reads JSON, the historical behaviour, after the client warned once at
// registration.
const READERS = {
  json: (res) => res.json(),
  text: (res) => res.text(),
  html: (res) => res.text(),
  blob: (res) => res.blob(),
  arrayBuffer: (res) => res.arrayBuffer(),
  stream: (res) => Promise.resolve(res.body),
};

function readBody(res, responseType) {
  const read = READERS[responseType] || READERS.json;
  return read(res);
}
return { RESPONSE_TYPES, DEFAULT_EXPECT, isResponseType, expectedContentType, contentTypeMatches, mismatchMessage, readBody };
})();

const __m58 = (() => {
// The in-flight request registry behind cancellation (core/services/http-client.js).
//
// Pure and import-free: a Map of entries keyed by a monotonic id `svc.ep#n`,
// with the selector grammar resolved here so the client, the store projection
// (the global `http.inflight` key) and the specs share one vocabulary:
//
//   '*'             every entry
//   'svc.ep'        every entry of one endpoint
//   'svc.ep#3'      one entry
//   'tag:screen'    every entry whose endpoint declared tag: 'screen'
//   [...]           any mix of the above
//
// An entry owns the request's AbortController and ONE timer: armed with the
// endpoint's `timeout` at start (reason 'timeout'), re-armed with its
// `idleTimeout` by touch() on every chunk (reason 'idle'). abort(reason)
// records the reason BEFORE aborting, which is how the client tells a user
// cancel from a timeout without ever inspecting the thrown value.

const ABORT_REASONS = ['user', 'route', 'cleanup', 'supersede', 'timeout', 'idle'];
const ENTRY_KINDS = ['fetch', 'batch', 'upload', 'stream'];

// The plain projection written to the store: no controller, no timer, no
// promise, so it survives JSON.stringify and a same-shape comparison.
function projectEntry(entry) {
  const { id, service, endpoint, tag, kind, startedAt } = entry;
  return { id, service, endpoint, tag: tag || null, kind, startedAt };
}

function addSelectorToken(out, token) {
  if (typeof token !== 'string' || !token) return;
  if (token === '*') { out.all = true; return; }
  if (token.startsWith('tag:')) { out.tags.add(token.slice(4)); return; }
  if (token.includes('#')) { out.ids.add(token); return; }
  out.endpoints.add(token);
}

// '*' | 'svc.ep' | 'svc.ep#n' | 'tag:<name>' | an array of those
//   -> { all, ids: Set, endpoints: Set, tags: Set }
function parseSelector(selector) {
  const out = { all: false, ids: new Set(), endpoints: new Set(), tags: new Set() };
  const tokens = Array.isArray(selector) ? selector : [selector];
  tokens.forEach((token) => addSelectorToken(out, token));
  return out;
}

function matchesSelector(entry, parsed) {
  if (parsed.all) return true;
  if (parsed.ids.has(entry.id)) return true;
  if (parsed.endpoints.has(`${entry.service}.${entry.endpoint}`)) return true;
  return Boolean(entry.tag) && parsed.tags.has(entry.tag);
}

function createEntry(id, spec) {
  const entry = {
    id,
    service: spec.service,
    endpoint: spec.endpoint,
    tag: spec.tag || null,
    kind: spec.kind || 'fetch',
    startedAt: spec.startedAt ?? Date.now(),
    controller: spec.controller || new AbortController(),
    reason: null,
    timer: null,
    timeout: spec.timeout || 0,
    idleTimeout: spec.idleTimeout || 0,
    identity: spec.identity || null,
    targetStore: spec.targetStore || null,
    promise: null,
    batch: null,
    clearTimer() {
      if (entry.timer) clearTimeout(entry.timer);
      entry.timer = null;
    },
    // The single timer: ms <= 0 never fires.
    arm(ms, reason) {
      entry.clearTimer();
      if (!(ms > 0)) return;
      entry.timer = setTimeout(() => entry.abort(reason), ms);
    },
    // A chunk arrived: re-arm with the idle period (a no-op without one).
    touch() {
      entry.arm(entry.idleTimeout, 'idle');
    },
    // Record the reason, then abort. The first reason wins; a second abort
    // is a no-op, so a timeout racing a user cancel cannot relabel it.
    abort(reason) {
      entry.clearTimer();
      if (entry.controller.signal.aborted) return;
      entry.reason = reason || 'user';
      entry.controller.abort();
    },
  };
  return entry;
}

class InflightRegistry {
  constructor() {
    this._entries = new Map();
    this._seq = new Map();
  }

  get size() {
    return this._entries.size;
  }

  // Register a request; ids count per endpoint (`svc.ep#1`, `svc.ep#2`, ...)
  // and never repeat within one registry.
  add(spec) {
    const key = `${spec.service}.${spec.endpoint}`;
    const n = (this._seq.get(key) || 0) + 1;
    this._seq.set(key, n);
    const entry = createEntry(`${key}#${n}`, spec);
    this._entries.set(entry.id, entry);
    return entry;
  }

  // Forget an entry; answers the entry, or null when it was not registered.
  remove(id) {
    const entry = this._entries.get(id) || null;
    this._entries.delete(id);
    return entry;
  }

  get(id) {
    return this._entries.get(id) || null;
  }

  // Insertion order, which is start order.
  list() {
    return [...this._entries.values()];
  }

  forEndpoint(service, endpoint) {
    return this.list().filter((entry) => entry.service === service && entry.endpoint === endpoint);
  }

  select(selector) {
    const parsed = parseSelector(selector);
    return this.list().filter((entry) => matchesSelector(entry, parsed));
  }
}
return { ABORT_REASONS, ENTRY_KINDS, projectEntry, parseSelector, InflightRegistry };
})();

const __m59 = (() => {
// A self-clearing command key: the store pattern the flow engine introduced
// for flow.<id>.navCmd (core/services/flow.js _watchCommands), shared with
// the HTTP client's global http.cancel and per-endpoint cancelKey.
//
// A primitive written twice with the same value notifies nobody (the store's
// same-value no-op in core/services/store.js setValue), so a command key that
// KEPT its value would fire once and go deaf: the second Stop click, the
// second 'next', would be lost. Writing null back BEFORE acting makes every
// write a change, so one command can be issued any number of times.
//
//   watchCommand(store, key, handler) -> unsubscribe
//
// A falsy write (including the null it writes itself) is ignored; anything
// else clears the key, then reaches handler(command). The return value is a
// plain function that removes the subscription.
//
// A command acts on a WRITE made after the watch is installed. Store.subscribe
// replays the value a key already holds, so without the guard a store that
// carried a truthy http.cancel or navCmd (hydrated state, a value seeded
// before the client registered) would fire the command the moment the watch
// went in. The replayed value is cleared like any other (so the same command
// written next is a change the store notifies) but never reaches the handler.
function watchCommand(store, key, handler) {
  let installing = true;
  const sub = store.subscribe(key, (command) => {
    if (!command) return;
    store.setValue(key, null);
    if (installing) return;
    handler(command);
  });
  installing = false;
  return typeof sub === 'function' ? sub : () => sub.unsubscribe();
}
return { watchCommand };
})();

const __m60 = (() => {
const getByPath = __m9.getByPath;
// Stream frames: the pure parsers, the frame router and the ONE reader loop
// every consumer of a streamed body shares (the client's http-stream.js, the
// model layer's server-side reader). Nothing else in the framework parses a
// stream (R-X3).
//
// Three protocols, declared by the endpoint, never sniffed:
//   sse     text/event-stream: `event:` / `data:` / `id:` lines, a blank line
//           ends a frame, a line starting with `:` is a comment and is dropped
//   ndjson  application/x-ndjson: one JSON document per line
//   text    every chunk is a delta
//
// parseSse and parseNdjson are pure: they take the buffered text and answer
// the complete frames plus the tail that must wait for the next chunk, so a
// frame cut across two chunks is delivered once, whole. decodeFrame is
// JSON.parse that answers null instead of throwing. routeFrame gives a frame
// its kind from the endpoint's declared vocabulary (STREAM_DEFAULTS is
// agent_stack's). readFrames drives a ReadableStream through the parser and
// delivers frames in order; it never throws, it reports how the body ended.
// isObject is exported for http-stream.js, which sits on this module and
// asks the same question of payloads and frames.
//
// Workerd-safe: getByPath is the only import, there is no node builtin, no
// process, no for-await (a getReader() loop runs in every runtime). Rendering
// answers a document and never consumes a stream: tests/module-graph.spec.mjs
// asserts that no module under server/ssr imports this module or
// http-stream.js itself (the library's defs reach the HTTP client, which
// rendering reads and never calls).


const STREAM_DEFAULTS = Object.freeze({
  events: Object.freeze({
    delta: 'delta',
    meta: 'meta',
    thinking: 'thinking',
    done: 'done',
    error: 'error',
    ignore: Object.freeze(['ping']),
  }),
  nameField: 'type',
  deltaField: 'text',
  maxFrames: 500,
  requireDone: false,
});

const isObject = (v) => v !== null && typeof v === 'object';

// ── SSE ──

// One field line applied to the frame being built. The value drops ONE
// leading space (the spec's rule); data lines join with a newline.
function applySseLine(frame, line) {
  const colon = line.indexOf(':');
  const field = colon === -1 ? line : line.slice(0, colon);
  const raw = colon === -1 ? '' : line.slice(colon + 1);
  const value = raw.startsWith(' ') ? raw.slice(1) : raw;
  if (field === 'data') frame.data.push(value);
  else if (field === 'event') frame.event = value;
  else if (field === 'id') frame.id = value;
  return frame;
}

// A block (the lines between two blank lines) becomes a frame, or null when
// it carried no data line: a comment-only keepalive, an `event:` on its own.
function sseBlockToFrame(block) {
  const lines = block.split('\n').filter((line) => line !== '' && !line.startsWith(':'));
  const built = lines.reduce(applySseLine, { event: null, data: [], id: null });
  if (built.data.length === 0) return null;
  return { event: built.event, data: built.data.join('\n'), id: built.id };
}

function parseSse(buffer) {
  const text = typeof buffer === 'string' ? buffer : '';
  // A chunk ending on a bare CR may be the first half of a CRLF: hold it.
  const held = text.endsWith('\r') ? '\r' : '';
  const blocks = text.slice(0, text.length - held.length).replace(/\r\n?/g, '\n').split('\n\n');
  const rest = blocks.pop() + held;
  const frames = blocks.map(sseBlockToFrame).filter((frame) => frame !== null);
  return { frames, rest };
}

// ── NDJSON ──

function parseNdjson(buffer) {
  const text = typeof buffer === 'string' ? buffer : '';
  const lines = text.split('\n');
  const rest = lines.pop();
  const frames = lines
    .map((line) => (line.endsWith('\r') ? line.slice(0, -1) : line))
    .filter((line) => line.trim() !== '')
    .map((line) => ({ event: null, data: line, id: null }));
  return { frames, rest };
}

// ── Decoding and routing ──

function decodeFrame(data) {
  if (typeof data !== 'string') return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

// name = payload[nameField] when it is a string, else the SSE event name,
// else 'message'. `nameField: null` forces event-name routing.
function frameName(payload, raw, cfg) {
  const field = cfg.nameField;
  const named = field && isObject(payload) ? getByPath(payload, field) : undefined;
  if (typeof named === 'string') return named;
  return raw.event || 'message';
}

// The text of a delta or thinking frame: `deltaField` out of a JSON payload,
// the raw data when the payload was not JSON (a plain-text delta named by its
// event), nothing otherwise.
function textOf(payload, raw, cfg) {
  if (isObject(payload)) return getByPath(payload, cfg.deltaField || STREAM_DEFAULTS.deltaField);
  if (payload === null) return raw.data;
  return null;
}

function errorText(payload, raw) {
  if (!isObject(payload)) return typeof payload === 'string' ? payload : raw.data;
  if (typeof payload.error === 'string') return payload.error;
  if (isObject(payload.error) && typeof payload.error.message === 'string') return payload.error.message;
  if (typeof payload.message === 'string') return payload.message;
  return raw.data;
}

// A retained frame carries its name under nameField (a dotted field cannot be
// a key, so 'type' stands in), then the payload as it came.
function retainedFrame(name, payload, raw, cfg) {
  const field = typeof cfg.nameField === 'string' && !cfg.nameField.includes('.') ? cfg.nameField : STREAM_DEFAULTS.nameField;
  if (isObject(payload) && !Array.isArray(payload)) return { [field]: name, ...payload };
  return { [field]: name, data: payload === null ? raw.data : payload };
}

function textKind(kind, name, payload, raw, cfg) {
  const text = textOf(payload, raw, cfg);
  if (typeof text !== 'string') return { kind: 'ignore', name, payload, text: null };
  return { kind, name, payload, text };
}

// { kind, name, payload, text, frame? } where kind is one of
// 'error' | 'ignore' | 'meta' | 'delta' | 'thinking' | 'done' | 'frame',
// decided in that order. `cfg.events` is read as given: the client merges the
// endpoint's block over STREAM_DEFAULTS.events once, at registration.
function routeFrame(raw, cfg = STREAM_DEFAULTS) {
  if (cfg.protocol === 'text') return { kind: 'delta', name: 'delta', payload: null, text: raw.data };
  const events = cfg.events || STREAM_DEFAULTS.events;
  const ignore = Array.isArray(events.ignore) ? events.ignore : [];
  const payload = decodeFrame(raw.data);
  const name = frameName(payload, raw, cfg);
  if (name === events.error) return { kind: 'error', name, payload, text: errorText(payload, raw) };
  if (ignore.includes(name)) return { kind: 'ignore', name, payload, text: null };
  if (name === events.meta) return { kind: 'meta', name, payload, text: null };
  if (name === events.delta) return textKind('delta', name, payload, raw, cfg);
  if (name === events.thinking) return textKind('thinking', name, payload, raw, cfg);
  if (name === events.done) return { kind: 'done', name, payload, text: null };
  // Not JSON and not named by an event: nothing to route by and nothing worth
  // keeping (OpenAI's `data: [DONE]` sentinel is the everyday case).
  if (payload === null && !raw.event) return { kind: 'ignore', name, payload, text: null };
  return { kind: 'frame', name, payload, text: null, frame: retainedFrame(name, payload, raw, cfg) };
}

// ── The reader loop ──

const PARSERS = {
  sse: parseSse,
  ndjson: parseNdjson,
  text: (buffer) => ({ frames: buffer ? [{ event: null, data: buffer, id: null }] : [], rest: '' }),
};

// Appended on a clean close so a final frame without its terminator (a server
// that ends on `data: x\n` rather than `\n\n`) is still delivered.
const TERMINATORS = { sse: '\n\n', ndjson: '\n', text: '' };

// Reads `body` (a ReadableStream of bytes) to the end, calling onFrame(frame)
// for every complete frame in arrival order and onChunk() after each chunk's
// frames (a chunk with no complete frame still calls it: the sink re-arms its
// idle timer on a keepalive). Stops early on `signal` abort or when no chunk
// arrives within `idleTimeout` ms (0 never fires; onIdle() is told first).
// Resolves { end: 'close' | 'abort' | 'idle' | 'error', error }: it never
// throws, a rejected read() is reported as end 'error'.
async function readFrames(body, { protocol, idleTimeout = 0, signal, onIdle, onChunk } = {}, onFrame) {
  if (!body || typeof body.getReader !== 'function') return { end: 'close', error: null };
  const parse = PARSERS[protocol] || PARSERS.sse;
  const terminator = TERMINATORS[protocol] ?? TERMINATORS.sse;
  const deliver = typeof onFrame === 'function' ? onFrame : () => {};
  const chunkDone = typeof onChunk === 'function' ? onChunk : () => {};
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let rest = '';
  let end = null;
  let error = null;
  let timer = null;

  const finish = (why, err = null) => {
    if (end) return;
    end = why;
    error = err;
    clearTimeout(timer);
    reader.cancel().catch(() => {});
  };
  const arm = () => {
    clearTimeout(timer);
    if (idleTimeout > 0) {
      timer = setTimeout(() => {
        if (typeof onIdle === 'function') onIdle();
        finish('idle');
      }, idleTimeout);
    }
  };
  const onAbort = () => finish('abort');
  const consume = (text) => {
    const parsed = parse(rest + text);
    rest = parsed.rest;
    parsed.frames.forEach(deliver);
    return parsed.frames.length;
  };

  if (signal?.aborted) onAbort();
  else signal?.addEventListener('abort', onAbort, { once: true });
  arm();
  try {
    while (!end) {
      const { done, value } = await reader.read();
      if (done) break;
      arm();
      consume(decoder.decode(value, { stream: true }));
      chunkDone();
    }
    if (!end) {
      end = 'close';
      clearTimeout(timer);
      if (consume(decoder.decode() + terminator) > 0) chunkDone();
    }
  } catch (err) {
    // A rejected read() ends the loop the way an abort or an idle expiry
    // does: through finish, so the reader is released (cancelled once) on
    // every early end. finish is a no-op when the end was already recorded.
    finish('error', err);
  }
  signal?.removeEventListener('abort', onAbort);
  return { end, error };
}
return { STREAM_DEFAULTS, isObject, parseSse, parseNdjson, decodeFrame, routeFrame, readFrames };
})();

const __m61 = (() => {
const STREAM_DEFAULTS = __m60.STREAM_DEFAULTS;
const isObject = __m60.isObject;
const readFrames = __m60.readFrames;
const routeFrame = __m60.routeFrame;
// The client-side stream sink: what `responseType: 'stream'` does with a
// Response once its headers are in. One responsibility, the endpoint's
// declared stream keys: resolve the `stream` block once at registration,
// reset the keys at call start, and turn the frames readFrames delivers into
// store writes, at most one write per key per CHUNK, then settle the call.
//
// Cost rule (R-T3): per delta one local concat; per chunk one setValue on the
// text key and, only if structured frames arrived, one on the frames key,
// bounded to maxFrames. Delta 10,000 costs what delta 1 costs.
//
// Errors, one sink (R-T5): an in-band error frame, a body that rejects
// mid-read and a close without `done` under requireDone all land in
// httpError, errorStoreKey and status 'error' here, with the text, frames and
// meta retained. An abort of the registry entry is NOT settled here: the
// caller sees the entry's signal and lets its catch classify the reason
// (user, route, cleanup, supersede, timeout, idle), so a cancelled stream and
// a cancelled fetch settle through the same code.
//
// Workerd-safe, like stream-frames.js under it; server/ssr never imports it.


const PROTOCOLS = ['sse', 'ndjson', 'text'];
const KEY_NAMES = ['textKey', 'framesKey', 'metaKey', 'thinkingKey'];
const RESET = { textKey: '', framesKey: [], metaKey: null, thinkingKey: '' };

const declaredKeys = (cfg) => KEY_NAMES.filter((name) => typeof cfg[name] === 'string' && cfg[name] !== '');

// The endpoint's `stream` block merged over STREAM_DEFAULTS, once, at
// registration. Warns about what it cannot honour; never throws.
function resolveStreamConfig(stream, label) {
  const raw = isObject(stream) ? stream : {};
  const declared = PROTOCOLS.includes(raw.protocol);
  if (!declared) console.warn(`[swc] ${label}: stream.protocol must be 'sse', 'ndjson' or 'text' (got ${JSON.stringify(raw.protocol)}); reading it as 'sse'`);
  return {
    ...STREAM_DEFAULTS,
    ...raw,
    protocol: declared ? raw.protocol : 'sse',
    events: { ...STREAM_DEFAULTS.events, ...(isObject(raw.events) ? raw.events : {}) },
    maxFrames: raw.maxFrames > 0 ? raw.maxFrames : STREAM_DEFAULTS.maxFrames,
    frameFn: typeof raw.frameFn === 'function' ? raw.frameFn : null,
  };
}

// The store keys a stream endpoint declares (for the transient registration).
function streamStoreKeys(cfg) {
  return declaredKeys(cfg).map((name) => cfg[name]);
}

// Every declared key back to its empty value: a new call starts clean.
function resetStreamKeys(store, cfg) {
  declaredKeys(cfg).forEach((name) => store.setValue(cfg[name], RESET[name]));
}

// frameFn is the transformFn shape: return the frame to keep it (mapped or
// not), anything else to drop it; a throw keeps the frame as it came.
function mapFrame(frame, frameFn) {
  if (!frameFn) return frame;
  try {
    const out = frameFn(frame);
    return isObject(out) ? out : null;
  } catch {
    return frame;
  }
}

// What each routed kind does to the per-chunk buffer.
const ON_KIND = {
  ignore: () => {},
  meta: (chunk, routed) => { chunk.meta = routed.payload; },
  delta: (chunk, routed) => { chunk.text += routed.text; },
  thinking: (chunk, routed, cfg) => { chunk.thinking += cfg.thinkingKey ? routed.text : ''; },
  done: (chunk, routed) => { chunk.done = { payload: routed.payload }; },
  error: (chunk, routed) => { chunk.error = { message: routed.text, payload: routed.payload }; },
  frame: (chunk, routed, cfg) => { chunk.frames.push(mapFrame(routed.frame, cfg.frameFn)); },
};

const emptyChunk = () => ({ text: '', thinking: '', frames: [], meta: undefined, done: null, error: null });

function fail(store, lifecycle, message, payload) {
  store.setValue('httpError', { ...(isObject(payload) ? payload : {}), error: message });
  lifecycle({ status: 'error', error: message });
}

// consumeStream({ res, cfg, store, lifecycle, entry, dispatch }):
//   res        the Response (headers in, status ok)
//   cfg        the resolved stream config plus `url` (for messages)
//   store      the Store the declared keys are written to
//   lifecycle  (patch) => void, the call's _setRequestLifecycle bound to its keys
//   entry      the call's registry entry: `controller` (its signal ends the
//              read), `touch()` (re-arms the entry's idle timer, once per chunk)
//   dispatch   (payload) => void, lands the done payload in resultStoreKey
// Resolves the call's result (the done payload, else the final text) on a
// clean end; undefined after an error or an abort.
async function consumeStream({ res, cfg, store, lifecycle, entry, dispatch }) {
  const state = { text: '', thinking: '', frames: [], done: null, error: null, started: false };
  let chunk = emptyChunk();
  const stop = new AbortController();
  const signal = entry?.controller?.signal;
  const onEntryAbort = () => stop.abort();
  if (signal?.aborted) stop.abort();
  else signal?.addEventListener('abort', onEntryAbort, { once: true });

  const onFrame = (raw) => {
    const routed = routeFrame(raw, cfg);
    ON_KIND[routed.kind](chunk, routed, cfg);
  };

  // One write per key per chunk: meta first (it precedes the deltas it
  // describes), then text, thinking, and the bounded frames window.
  const flush = () => {
    if (entry && typeof entry.touch === 'function') entry.touch();
    if (!state.started) {
      state.started = true;
      lifecycle({ status: 'streaming' });
    }
    if (chunk.meta !== undefined && cfg.metaKey) store.setValue(cfg.metaKey, chunk.meta);
    if (chunk.text) {
      state.text += chunk.text;
      if (cfg.textKey) store.setValue(cfg.textKey, state.text);
    }
    if (chunk.thinking) {
      state.thinking += chunk.thinking;
      if (cfg.thinkingKey) store.setValue(cfg.thinkingKey, state.thinking);
    }
    const kept = chunk.frames.filter((frame) => frame !== null);
    if (kept.length) {
      state.frames = state.frames.concat(kept.slice(-cfg.maxFrames)).slice(-cfg.maxFrames);
      if (cfg.framesKey) store.setValue(cfg.framesKey, state.frames);
    }
    if (chunk.done) state.done = chunk.done;
    if (chunk.error) {
      state.error = chunk.error;
      stop.abort();
    }
    chunk = emptyChunk();
  };

  const outcome = await readFrames(res.body, { protocol: cfg.protocol, signal: stop.signal, onChunk: flush }, onFrame);
  signal?.removeEventListener('abort', onEntryAbort);

  if (state.error) return fail(store, lifecycle, state.error.message, state.error.payload);
  if (outcome.end === 'error') return fail(store, lifecycle, outcome.error?.message || 'Network error', null);
  if (signal?.aborted) return undefined;
  if (cfg.requireDone && !state.done) return fail(store, lifecycle, `The stream closed before its done frame (${cfg.url}).`, null);

  const result = state.done && state.done.payload !== null && state.done.payload !== undefined ? state.done.payload : state.text;
  dispatch(result);
  lifecycle({ status: 'done', progress: 100, error: null, result });
  return result;
}
return { resolveStreamConfig, streamStoreKeys, resetStreamKeys, consumeStream };
})();

const __m62 = (() => {
const store = __m0.store;
const isPlainObject = __m0.isPlainObject;
const matchUrlToRoute = __m7.matchUrlToRoute;
const CHALLENGE_KEY = __m10.CHALLENGE_KEY;
const challengeFor = __m10.challengeFor;
const isResponseType = __m57.isResponseType;
const expectedContentType = __m57.expectedContentType;
const contentTypeMatches = __m57.contentTypeMatches;
const mismatchMessage = __m57.mismatchMessage;
const readBody = __m57.readBody;
const InflightRegistry = __m58.InflightRegistry;
const projectEntry = __m58.projectEntry;
const watchCommand = __m59.watchCommand;
const consumeStream = __m61.consumeStream;
const resolveStreamConfig = __m61.resolveStreamConfig;
const resetStreamKeys = __m61.resetStreamKeys;
const streamStoreKeys = __m61.streamStoreKeys;
// Pure data — the challenge key and payload shape, so the 401 path can raise
// one without importing the router or a module the legacy module graph does
// not follow.






// The sentences a timer writes. Both classify as a failure with code
// 'timeout' (the shape apps already display for today's timeout); only the
// wording tells a time-to-headers expiry from an idle stream.
const TIMEOUT_MESSAGES = {
  timeout: (entry, url) => `Request timed out after ${entry.timeout} ms (${url}).`,
  idle: (entry, url) => `Stream idle for ${entry.idleTimeout} ms (${url}).`,
};

// A GET or HEAD cannot carry a body: fetch refuses the request outright,
// which surfaced as a silent "Network error". The methods on which a body is
// sent; on the others a plain-object body is folded into the query string.
const BODYLESS_METHODS = ['GET', 'HEAD'];

// Every map keyed by a caller-supplied name (a service, an endpoint, a store
// key) is a null-prototype object, so a name that happens to be on
// Object.prototype ('constructor', 'toString') is never answered by the
// prototype's function and never shadows a lookup or a scheduling flag.
const named = () => Object.create(null);

// The smallest positive value, or 0 when none is: the batch flush arms its
// shared timer with the tightest `timeout` among the entries it carries.
const smallestPositive = (values) => {
  const positives = values.filter((ms) => ms > 0);
  return positives.length ? Math.min(...positives) : 0;
};

class HTTPClient {
  constructor() {
    this._services = named();
    this._serviceOptions = named();
    this._endpointConfigs = named();   // [serviceName][endpointName] → config
    this._storeListeners = named();
    this._routerListeners = named();
    this._cache = named();
    this._authTokens = named();
    this._refreshTimers = named();
    this._batchQueues = named();       // [serviceName] → { [endpointName]: {endpointName, method, resolvedUrl, body, entry} }
    this._batchFlushScheduled = named();
    this._warned = new Set();
    // Cancellation: every in-flight request is a registry entry (id, controller,
    // timer), projected into the global `http.inflight` key; `http.cancel` and
    // each endpoint's `cancelKey` are self-clearing command keys.
    this._registry = new InflightRegistry();
    this._commandUnsubs = [];
    this._cancelWatch = null;
    this._routeWatch = null;
    this.store = store;
  }

  addServices(...httpServices) {
    this._registerGlobalKeys();
    httpServices.forEach(([serviceName, baseUrl, endpoints, options]) => {
      if (!this._services[serviceName]) {
        this._services[serviceName] = named();
      }
      if (!this._endpointConfigs[serviceName]) {
        this._endpointConfigs[serviceName] = named();
      }
      if (options) {
        this._serviceOptions[serviceName] = options;
      }

      // Determine if this service uses batching
      const batchUrl = options?.batchUrl || null;
      const authCfg = options?.auth;
      const autoExclude = new Set();
      if (authCfg?.endpoint) autoExclude.add(authCfg.endpoint);
      if (authCfg?.validateEndpoint) autoExclude.add(authCfg.validateEndpoint);
      if (authCfg?.refresh?.endpoint) autoExclude.add(authCfg.refresh.endpoint);

      endpoints.forEach(({
        name,
        url,
        routeUrl,
        method = 'GET',
        mode = 'cors',
        cache = 'no-cache',
        errorBody = false,
        credentials = 'same-origin',
        redirect = 'follow',
        referrerPolicy = 'no-referrer',
        headers = { 'Content-Type': 'application/json' },
        timeout = 10000,
        storeKey,
        storeQueryKey,
        routeParams,
        includeQueryParams = true,
        resultStoreKey,
        transformFn,
        paramMap = {},
        cacheTime,
        tokenKey = 'token',
        errorStoreKey,
        statusStoreKey,
        progressStoreKey,
        requestStoreKey,
        bodyType = 'json',
        excludeFromBatch = false,
        envelope = false,
        paginated = false,
        responseType = 'json',
        expect,
        tag,
        supersede = false,
        cancelKey,
        cancelOn,
        idleTimeout,
        stream,
      }) => {
        const resolvedType = this._resolveResponseType(serviceName, name, responseType, bodyType);
        const _params = {
          method: method.toUpperCase(),
          mode,
          cache,
          credentials,
          headers,
          redirect,
          referrerPolicy,
        };

        // A stream endpoint's declared block, resolved once. Its store keys are
        // transient (never persisted, never dirty): a partial answer is not
        // state the app owns. cacheTime cannot apply (there is no body to
        // cache) and says so once. The exclusion is by PREFIX, like every
        // addPersistExclude: a stream key's name must not prefix another key.
        const streamCfg = resolvedType === 'stream' ? resolveStreamConfig(stream, `${serviceName}.${name}`) : null;
        if (streamCfg) {
          streamStoreKeys(streamCfg).forEach((key) => this.store.addPersistExclude(key, { transient: true }));
          if (cacheTime) console.warn(`[swc] ${serviceName}.${name}: cacheTime is ignored on a stream endpoint`);
        }

        // Store endpoint config for batch response dispatch
        this._endpointConfigs[serviceName][name] = {
          resultStoreKey, transformFn, errorStoreKey, cacheTime, method: _params.method,
          baseUrl, url, paramMap, includeQueryParams, envelope, paginated,
          statusStoreKey, progressStoreKey, requestStoreKey, bodyType, timeout,
          responseType: resolvedType, expect, tag, supersede, cancelKey, cancelOn, idleTimeout,
          stream: streamCfg,
        };

        // Should this endpoint use the batch queue? The batch sub-dispatch can
        // only carry JSON (server/api-router.js reads each answer with
        // response.json()), so any other response type goes direct.
        const useBatch = batchUrl && !excludeFromBatch && !autoExclude.has(name) && resolvedType === 'json';

        // A batched call is one line of a shared POST: there is no call of its
        // own to supersede and no body to read a chunk at a time, so the two
        // options that describe one are inert there and say so at
        // registration, the way cacheTime on a stream does. `timeout` is
        // honoured by the flush (the tightest one among the entries bounds
        // the batch answer).
        if (useBatch && (supersede || idleTimeout)) {
          console.warn(`[swc] ${serviceName}.${name}: supersede and idleTimeout do not apply to a batched endpoint (it is one line of the service's batch POST)`);
        }

        const bodyless = BODYLESS_METHODS.includes(_params.method);

        this._services[serviceName][name] = async (callParams = {}, targetStore = null, dispatchDefault = true) => {
          const { queryParams, params } = callParams;

          let computedUrl = this.computeUrl(baseUrl, url, params, paramMap);
          if (includeQueryParams) {
            computedUrl = this.appendQueryParams(computedUrl, queryParams);
          }

          // The body rides on a mutating method only. On a GET or HEAD a
          // plain-object body becomes query parameters (what a caller handing
          // { body: { q } } to a GET means); anything else is dropped, with
          // one warning per endpoint, rather than handed to a fetch that
          // rejects it.
          const body = bodyless ? undefined : callParams.body;
          if (bodyless && callParams.body !== undefined && callParams.body !== null) {
            computedUrl = this._foldBodyIntoUrl(serviceName, name, computedUrl, callParams.body);
          }

          // Cache check: applies to both batched and non-batched, and to every
          // trigger (a route match and a store write call this same closure).
          const cacheKey = `${_params.method}-${computedUrl}`;
          const cachedData = this._cache[cacheKey];
          if (cachedData !== undefined && !streamCfg) {
            this.dispatchToStore(cachedData, targetStore, resultStoreKey, transformFn, dispatchDefault, envelope);
            return Promise.resolve(cachedData);
          }

          // Batch queue path. The entry is registered now so it can be cancelled
          // before the flush; the flush hands it the batch's controller.
          if (useBatch) {
            const entry = this._track({ service: serviceName, endpoint: name, tag, kind: 'batch', targetStore });
            this._enqueueBatch(serviceName, {
              endpointName: name, method: _params.method, resolvedUrl: computedUrl, body: body || null, entry,
            });
            return;
          }

          // Supersede: an identical call already in flight (same method, URL,
          // body and destination) is reused as it is; any other in-flight call
          // of this endpoint is cancelled first and writes nothing.
          const identity = `${_params.method} ${computedUrl} ${JSON.stringify(body ?? null)}`;
          const live = supersede ? this._reuseInflight(serviceName, name, identity, targetStore) : null;
          if (live) return live.promise;
          if (supersede) this._cancelEndpoint(serviceName, name, 'supersede');

          // Direct fetch path (non-batched or excluded). The entry owns the
          // AbortController and the single timer, armed with `timeout` until the
          // headers arrive.
          const entry = this._track({
            service: serviceName, endpoint: name, tag, targetStore, identity, timeout, idleTimeout,
            kind: bodyType === 'formData' ? 'upload' : (resolvedType === 'stream' ? 'stream' : 'fetch'),
          });
          const lifecycle = { statusStoreKey, progressStoreKey, requestStoreKey, errorStoreKey, errorBody };

          if (this._authTokens[serviceName]) {
            _params.headers['Authorization'] = `Bearer ${this._authTokens[serviceName]}`;
          }

          entry.promise = (async () => {
            try {
              this._setRequestLifecycle(lifecycle, { status: 'pending', progress: 0, error: null });
              if (streamCfg) resetStreamKeys(this.store, streamCfg);

              if (bodyType === 'formData') {
                const formData = this._createFormData(body);
                const uploadResult = await this._sendUploadRequest(computedUrl, {
                  ..._params,
                  signal: entry.controller.signal,
                }, formData, lifecycle);
                entry.clearTimer();
                if (uploadResult.aborted) {
                  this._settleAborted(entry, lifecycle, computedUrl);
                  return;
                }
                if (!uploadResult.ok) return;
                const uploaded = uploadResult.data;
                if (errorStoreKey) this.store.setValue(errorStoreKey, null);
                this.setCachedData(cacheKey, uploaded, cacheTime);
                this.dispatchToStore(uploaded, targetStore, resultStoreKey, transformFn, dispatchDefault, envelope);
                this._setRequestLifecycle(lifecycle, { status: 'done', progress: 100, error: null, result: uploaded });
                return uploaded;
              }

              const fetchBody = body ? JSON.stringify(body) : undefined;
              const res = await fetch(computedUrl, { ..._params, body: fetchBody, signal: entry.controller.signal });
              // The headers are in, so `timeout` is satisfied. From here the one
              // timer is the idle timer, when the endpoint declared a period.
              if (idleTimeout) entry.touch();
              else entry.clearTimer();

              if (!res.ok) {
                let data;
                try { data = await res.json(); } catch { data = { error: res.statusText }; }
                const svcAuthCfg = this._serviceOptions[serviceName]?.auth;
                if (res.status === 401 && svcAuthCfg &&
                    name !== svcAuthCfg.endpoint && name !== svcAuthCfg.validateEndpoint) {
                  this._clearAuth(serviceName);
                  // Two modes. `onUnauthorized: 'challenge'` raises a challenge in
                  // place: the user signs in without leaving the screen and is
                  // resumed to where they already were. Anything else keeps the
                  // original behaviour: forward to the login page ('/login' unless
                  // the auth config names one). Navigating away is the crude
                  // version (it loses the context and whatever the user was
                  // doing), so it stays the default only to avoid changing
                  // existing apps under them.
                  if (svcAuthCfg.onUnauthorized === 'challenge') {
                    this.store.setValue(CHALLENGE_KEY, challengeFor(null, {
                      remedy: svcAuthCfg.remedy || 'auth.signIn',
                      resume: typeof window !== 'undefined' && window.location
                        ? `${window.location.pathname}${window.location.search}` : null,
                    }));
                  } else {
                    Promise.resolve().then(() => __swcNs(__m7)).then(({ router }) => router.navigateTo(svcAuthCfg.onUnauthorized || '/login'));
                  }
                }
                // Carry the status: an app that treats one code specially (401 →
                // show the sign-up panel rather than an error line) could
                // otherwise only recover it by reading the message text, which
                // pushed those callers back to a hand-written fetch.
                this.store.setValue('httpError', { ...data, status: res.status });
                // errorBody: the endpoint asked for the whole answer — code,
                // human copy and status — because a screen that can only show
                // `data.error || data.message` shows the machine code whenever
                // both exist, and the server's carefully written message dies
                // one field away from the person it was written for. The
                // lifecycle helper is the ONE writer of errorStoreKey; a
                // second write here raced it and lost.
                this._setRequestLifecycle(lifecycle, {
                  status: 'error',
                  error: errorBody
                    ? { error: data.error || '', message: data.message || '', status: res.status }
                    : (data.error || data.message || res.statusText),
                });
                return;
              }

              // A stream: the frames go to the declared keys a chunk at a time and
              // the call settles there. The content-type gate and readBody never
              // run for it. An abort of the entry surfaces as a throw so the catch
              // below classifies its reason exactly as it does for a fetch.
              if (streamCfg) {
                const result = await consumeStream({
                  res,
                  cfg: { ...streamCfg, url: computedUrl },
                  store: this.store,
                  lifecycle: (patch) => this._setRequestLifecycle(lifecycle, patch),
                  entry,
                  dispatch: (payload) => this.dispatchToStore(payload, targetStore, resultStoreKey, transformFn, dispatchDefault, envelope),
                });
                entry.controller.signal.throwIfAborted();
                return result;
              }

              // The content-type gate. json keeps its historical rule: a 200 that
              // is not JSON means the endpoint is not mounted where we are pointed
              // (a server without the API 302s unknown paths into the SSR
              // renderer, which answers HTML with status 200; parsing that yields
              // `Unexpected token '<'` in errorStoreKey, or a "success" against a
              // login page). html expects text/html; text, blob and arrayBuffer
              // are gated only by an explicit `expect`. A mismatch is reported,
              // never parsed.
              const expected = expectedContentType(resolvedType, expect);
              const contentType = res.headers.get('content-type') || '';
              if (expected && !contentTypeMatches(contentType, expected)) {
                this._rejectResponse(lifecycle, mismatchMessage({
                  responseType: resolvedType, expect, contentType, expected, status: res.status, url: computedUrl,
                }));
                return;
              }

              const answer = await readBody(res, resolvedType);

              // Auth token capture: a JSON answer only (a text or binary body has
              // no token field to read).
              const svcAuthCfg = this._serviceOptions[serviceName]?.auth;
              const authEndpointName = svcAuthCfg?.endpoint || 'auth';
              const tk = svcAuthCfg?.tokenKey || tokenKey;
              if (resolvedType === 'json' && name === authEndpointName && answer?.[tk]) {
                this._authTokens[serviceName] = answer[tk];
                if (svcAuthCfg) {
                  this._persistToken(serviceName);
                  this._scheduleRefresh(serviceName);
                  this._runPostLogin(serviceName);
                }
              }

              if (errorStoreKey) this.store.setValue(errorStoreKey, null);
              // The cache holds the value by reference: a Blob or an ArrayBuffer
              // stays in memory for `cacheTime` seconds.
              this.setCachedData(cacheKey, answer, cacheTime);

              // Build paginated context for loadMore
              let paginatedCtx = null;
              if (paginated && envelope) {
                const lastParams = { ...callParams };
                paginatedCtx = {
                  loadMore: () => {
                    const meta = this.store.getValue(`${resultStoreKey}Meta`);
                    if (!meta?.hasMore) return;
                    // Find the target element and set appendData before fetch
                    this._setPaginatedAppend(resultStoreKey);
                    this._services[serviceName][name]({
                      ...lastParams,
                      queryParams: { ...lastParams.queryParams, offset: meta.offset + meta.limit, limit: meta.limit },
                    });
                  },
                };
              }

              // The value lands as it is: a string, a Blob, an ArrayBuffer or the
              // parsed JSON. The envelope unwrap only fires on an object carrying
              // both `data` and `meta` (dispatchToStore).
              this.dispatchToStore(answer, targetStore, resultStoreKey, transformFn, dispatchDefault, envelope, paginatedCtx);
              this._setRequestLifecycle(lifecycle, { status: 'done', progress: 100, error: null, result: answer });
              return answer;
            } catch (e) {
              // An entry aborted on purpose settles by its reason, never by the
              // thrown value: user, route and cleanup cancels are not errors, a
              // supersede writes nothing, a timeout or idle expiry is a failure
              // with the framework's sentence. Anything else is the failure path.
              if (entry.reason) {
                this._settleAborted(entry, lifecycle, computedUrl);
                return;
              }
              this.store.setValue('httpError', e);
              // The same shape on a network failure: an errorBody consumer
              // reads .message unconditionally and must never find a string.
              this._setRequestLifecycle(lifecycle, {
                status: 'error',
                error: errorBody
                  ? { error: 'network', message: e?.message || 'Network error', status: 0 }
                  : (e?.message || 'Network error'),
              });
            } finally {
              this._untrack(entry);
            }
          })();
          return entry.promise;
        };

        if (storeKey) {
          this.setupStoreListener(storeKey, storeQueryKey, this._services[serviceName][name], includeQueryParams, _params.method);
        }

        if (routeUrl) {
          this.setupRouterListener(routeUrl, routeParams, this._services[serviceName][name], includeQueryParams, paramMap);
        }

        // Per-endpoint cancellation triggers: a self-clearing command key, and
        // the opt-in route watch (one subscription for every endpoint that asks).
        if (cancelKey) {
          this._commandUnsubs.push(watchCommand(this.store, cancelKey, () => this._cancelEndpoint(serviceName, name, 'user')));
        }
        if (cancelOn === 'route') this._watchRoute();
      });
    });
  }

  // The effective response type of an endpoint, decided once at registration.
  // An unknown value warns and behaves as json (the historical path); a
  // non-json value on a formData endpoint warns because the upload path parses
  // its own JSON answer (an XHR has no Response object to read otherwise).
  _resolveResponseType(serviceName, name, responseType, bodyType) {
    if (!isResponseType(responseType)) {
      console.warn(`[swc] services: unknown responseType "${responseType}" on ${serviceName}.${name}; treated as json`);
      return 'json';
    }
    if (bodyType === 'formData' && responseType !== 'json') {
      console.warn(`[swc] services: responseType "${responseType}" on ${serviceName}.${name} is ignored: a formData endpoint parses its answer as JSON`);
    }
    return responseType;
  }

  // A plain-object body on a GET or HEAD is what the caller meant as query
  // parameters; anything else (a string, an array, a FormData) has no place
  // on those methods and is dropped, said once per endpoint.
  _foldBodyIntoUrl(serviceName, name, computedUrl, body) {
    if (isPlainObject(body)) return this.appendQueryParams(computedUrl, body);
    this._warnOnce(`${serviceName}.${name}:body`, `[swc] ${serviceName}.${name}: a GET or HEAD carries no body; the ${Array.isArray(body) ? 'array' : typeof body} body was dropped (a plain object would have been sent as query parameters)`);
    return computedUrl;
  }

  _warnOnce(label, message) {
    if (this._warned.has(label)) return;
    this._warned.add(label);
    console.warn(message);
  }

  // The three writes of a refused answer: httpError, errorStoreKey and the
  // lifecycle. Shared by the content-type gate and the timer classification.
  _rejectResponse(lifecycle, message, httpError = { error: message }) {
    this.store.setValue('httpError', httpError);
    if (lifecycle.errorStoreKey) this.store.setValue(lifecycle.errorStoreKey, message);
    this._setRequestLifecycle(lifecycle, { status: 'error', error: message });
  }

  // ── Cancellation registry ──

  // Once per client: the global command key and the transient prefix. Done on
  // the first addServices rather than in the constructor so a caller can swap
  // the store first, the way CreateApp configures it before registering.
  _registerGlobalKeys() {
    if (this._cancelWatch) return;
    if (typeof this.store.addPersistExclude === 'function') this.store.addPersistExclude('http.', { transient: true });
    this._cancelWatch = watchCommand(this.store, 'http.cancel', (selector) => this.cancel(selector, 'user'));
  }

  // Register an in-flight request: one entry, one controller, one timer
  // (armed with `timeout`, reason 'timeout'), one store write.
  _track(spec) {
    const entry = this._registry.add(spec);
    entry.arm(spec.timeout, 'timeout');
    this._projectInflight();
    return entry;
  }

  // Forget an entry without projecting (a batch releases several at once).
  _release(entry) {
    entry.clearTimer();
    return Boolean(this._registry.remove(entry.id));
  }

  _untrack(entry) {
    if (this._release(entry)) this._projectInflight();
  }

  _projectInflight() {
    this.store.setValue('http.inflight', this._registry.list().map(projectEntry));
  }

  // Abort one entry for a reason and release it; the caller projects once for
  // the whole selection, so an abort is one store write however many entries
  // it hits, and the entry leaves http.inflight the moment it is cancelled
  // (its closure settles the lifecycle keys a tick later and finds nothing
  // left to release). A direct fetch, an upload and a stream abort their own
  // controller. A queued batch entry is pulled from the queue; a flushed one
  // is struck from its batch (the result is dropped on arrival) and the batch
  // fetch is aborted only when no live entry remains. A batched call has no
  // lifecycle today (the batch path never writes `pending`), so a cancelled
  // one writes nothing either.
  _abortEntry(entry, reason) {
    if (entry.kind !== 'batch') {
      entry.abort(reason);
      this._release(entry);
      return;
    }
    entry.reason = reason;
    if (!entry.batch) {
      const queue = this._batchQueues[entry.service] || {};
      if (queue[entry.endpoint]?.entry === entry) delete queue[entry.endpoint];
      this._release(entry);
      return;
    }
    entry.batch.cancelled.add(entry.endpoint);
    entry.batch.live.delete(entry.id);
    this._release(entry);
    if (entry.batch.live.size === 0) entry.batch.controller.abort();
  }

  // Abort a selection and project once; answers the ids aborted.
  _abortAll(entries, reason) {
    entries.forEach((entry) => this._abortEntry(entry, reason));
    if (entries.length) this._projectInflight();
    return entries.map((entry) => entry.id);
  }

  // Settle an entry that was aborted on purpose. The reason was recorded
  // before controller.abort() (entry.abort), so the thrown value is never
  // read. user | route | cleanup: status 'aborted' with the reason, and
  // neither errorStoreKey nor httpError is written. supersede: nothing (the
  // successor owns every key). timeout | idle: a failure with the framework's
  // sentence and code 'timeout', the shape apps already display.
  _settleAborted(entry, lifecycle, url) {
    if (entry.reason === 'supersede') return;
    const failure = TIMEOUT_MESSAGES[entry.reason];
    if (!failure) {
      this._setRequestLifecycle(lifecycle, { status: 'aborted', reason: entry.reason });
      return;
    }
    const message = failure(entry, url);
    this._rejectResponse(lifecycle, message, { error: message, code: 'timeout' });
  }

  _cancelEndpoint(serviceName, name, reason) {
    this._abortAll(this._registry.forEndpoint(serviceName, name), reason);
  }

  _reuseInflight(serviceName, name, identity, targetStore) {
    return this._registry.forEndpoint(serviceName, name)
      .find((entry) => entry.promise && entry.identity === identity && entry.targetStore === targetStore) || null;
  }

  // One lazy subscription on `route` for every endpoint that opted in with
  // cancelOn: 'route'. The router writes `route` as the plain path (query
  // parameters land in router.<key>), so a query-only change never fires
  // this; the object form is compared on its path for the same reason.
  _watchRoute() {
    if (this._routeWatch) return;
    this._lastRoutePath = undefined;
    this._routeWatch = this.store.subscribe('route', (routerState) => {
      const path = typeof routerState === 'string' ? routerState : (routerState?.path ?? null);
      const changed = this._lastRoutePath !== undefined && path !== this._lastRoutePath;
      this._lastRoutePath = path;
      if (!changed) return;
      this._abortAll(this._registry.list()
        .filter((entry) => this._endpointConfigs[entry.service]?.[entry.endpoint]?.cancelOn === 'route'), 'route');
    });
  }

  // The imperative escape: cancel by selector ('*', 'svc.ep', 'svc.ep#n',
  // 'tag:<name>', or an array of those) and answer the ids aborted. The DSL
  // path is this same call through the http.cancel command key.
  cancel(selector, reason = 'user') {
    return this._abortAll(this._registry.select(selector), reason);
  }

  // Set append mode on the element bound to a paginated store key
  _setPaginatedAppend(storeKey) {
    const el = this.store.getValue(`${storeKey}.appendTarget`);
    if (el) el._appendMode = true;
  }

  // ── Batch queue ──

  _enqueueBatch(serviceName, record) {
    if (!this._batchQueues[serviceName]) this._batchQueues[serviceName] = named();
    // Deduplicate by endpoint name (last call wins). The replaced call leaves
    // the registry, so http.inflight never lists a request that will not go out.
    const replaced = this._batchQueues[serviceName][record.endpointName];
    if (replaced) this._untrack(replaced.entry);
    this._batchQueues[serviceName][record.endpointName] = record;

    if (!this._batchFlushScheduled[serviceName]) {
      this._batchFlushScheduled[serviceName] = true;
      // Double-defer: microtask + setTimeout(0) to capture calls across component lifecycles
      queueMicrotask(() => setTimeout(() => this._flushBatch(serviceName), 0));
    }
  }

  async _flushBatch(serviceName) {
    this._batchFlushScheduled[serviceName] = false;
    const queue = this._batchQueues[serviceName];
    if (!queue || Object.keys(queue).length === 0) return;

    // Take entries and clear queue
    const entries = Object.values(queue);
    this._batchQueues[serviceName] = named();

    const batchUrl = this._serviceOptions[serviceName]?.batchUrl;
    if (!batchUrl) {
      entries.forEach((record) => this._release(record.entry));
      this._projectInflight();
      return;
    }

    // One controller per flush, shared by every entry it carries. A cancelled
    // entry is struck from `live` and its result dropped; the fetch itself is
    // aborted only when the last live entry goes (the server does the work
    // for the others either way: docs/backlog.md).
    const batch = { controller: new AbortController(), cancelled: new Set(), live: new Set(), reason: null, timer: null };
    entries.forEach((record) => { record.entry.batch = batch; batch.live.add(record.entry.id); });

    // Build payload: [[endpointName, method, resolvedUrl, body], ...]
    const payload = entries.map(e => [e.endpointName, e.method, e.resolvedUrl, e.body]);

    const headers = { 'Content-Type': 'application/json' };
    if (this._authTokens[serviceName]) {
      headers['Authorization'] = `Bearer ${this._authTokens[serviceName]}`;
    }

    const configOf = (endpointName) => this._endpointConfigs[serviceName]?.[endpointName];
    // The error write for every entry still live that declared an error key.
    const setErrors = (message) => {
      entries
        .filter((record) => !batch.cancelled.has(record.endpointName) && configOf(record.endpointName)?.errorStoreKey)
        .forEach((record) => this.store.setValue(configOf(record.endpointName).errorStoreKey, message));
    };

    // The batch's one timer: the tightest `timeout` among its entries bounds
    // the whole answer (one JSON document, so there is no headers-then-body
    // split to arm separately). A batch that hangs settles every live entry
    // with the timeout sentence and code 'timeout', as a direct fetch does.
    const timeout = smallestPositive(entries.map((record) => configOf(record.endpointName)?.timeout));
    if (timeout > 0) {
      batch.timer = setTimeout(() => {
        batch.reason = 'timeout';
        batch.controller.abort();
      }, timeout);
    }

    try {
      const res = await fetch(batchUrl, {
        method: 'POST', headers, body: JSON.stringify(payload), signal: batch.controller.signal,
      });

      if (!res.ok) {
        // Batch request failed: set error on every entry still live
        const errData = await res.json().catch(() => ({ error: res.statusText }));
        this.store.setValue('httpError', errData);
        setErrors(errData.error || res.statusText);
        return;
      }

      const results = await res.json();
      // results: [[endpointName, statusCode, data], ...]. A cancelled entry's
      // result is dropped here; an unknown name is skipped as before.
      results
        .filter(([endpointName]) => !batch.cancelled.has(endpointName) && configOf(endpointName))
        .forEach(([endpointName, statusCode, data]) => this._settleBatchResult(serviceName, entries, endpointName, statusCode, data));
    } catch (e) {
      // The batch's own timer aborted it: a failure for every live entry.
      if (batch.reason === 'timeout') {
        const message = TIMEOUT_MESSAGES.timeout({ timeout }, batchUrl);
        this.store.setValue('httpError', { error: message, code: 'timeout' });
        setErrors(message);
        return;
      }
      // A batch whose every entry was cancelled aborted itself: nothing to write.
      if (batch.controller.signal.aborted) return;
      // Network failure: all entries failed
      this.store.setValue('httpError', e);
      setErrors('Network error');
    } finally {
      clearTimeout(batch.timer);
      entries.forEach((record) => this._release(record.entry));
      this._projectInflight();
    }
  }

  // One batch answer: the cache, the error key and the dispatch on success;
  // the error keys and the 401 handling otherwise.
  _settleBatchResult(serviceName, entries, endpointName, statusCode, data) {
    const cfg = this._endpointConfigs[serviceName][endpointName];
    if (statusCode >= 200 && statusCode < 300) {
      // Populate cache
      const record = entries.find(e => e.endpointName === endpointName);
      if (record && cfg.cacheTime) {
        const cacheKey = `${cfg.method}-${record.resolvedUrl}`;
        this.setCachedData(cacheKey, data, cfg.cacheTime);
      }
      if (cfg.errorStoreKey) this.store.setValue(cfg.errorStoreKey, null);
      this.dispatchToStore(data, null, cfg.resultStoreKey, cfg.transformFn, true, cfg.envelope);
      return;
    }
    // Individual sub-request error
    this.store.setValue('httpError', data);
    if (cfg.errorStoreKey) this.store.setValue(cfg.errorStoreKey, data?.error || data?.message || `Error ${statusCode}`);
    // 401 handling
    if (statusCode === 401) {
      const svcAuthCfg = this._serviceOptions[serviceName]?.auth;
      if (svcAuthCfg) {
        this._clearAuth(serviceName);
        Promise.resolve().then(() => __swcNs(__m7)).then(({ router }) => router.navigateTo(svcAuthCfg.onUnauthorized || '/login'));
      }
    }
  }

  create(serviceConfig) {
    if (!serviceConfig) return null;
    const { name, baseUrl, endpoints } = serviceConfig;
    if (!name || !baseUrl || !Array.isArray(endpoints)) return null;
    this.addServices([name, baseUrl, endpoints]);
    return this.getService(name);
  }

  setCachedData(key, data, cacheTime) {
    if (!cacheTime) return;
    this._cache[key] = data;
    if (cacheTime > 0) {
      setTimeout(() => { delete this._cache[key]; }, cacheTime * 1000);
    }
  }

  _setRequestLifecycle(keys = {}, patch = {}) {
    const { statusStoreKey, progressStoreKey, requestStoreKey, errorStoreKey, errorBody } = keys;
    if ('status' in patch && statusStoreKey) this.store.setValue(statusStoreKey, patch.status);
    if ('progress' in patch && progressStoreKey) this.store.setValue(progressStoreKey, patch.progress);
    if ('error' in patch && errorStoreKey) {
      // An errorBody consumer reads .message unconditionally; a path that
      // settles with a plain sentence (abort, timeout) is wrapped so the
      // shape survives every way a request can fail.
      const v = patch.error;
      this.store.setValue(errorStoreKey, errorBody && v != null && typeof v !== 'object'
        ? { error: '', message: String(v), status: 0 }
        : v);
    }
    if (requestStoreKey) {
      const current = this.store.getValue(requestStoreKey, {});
      const flat = typeof patch.error === 'object' && patch.error !== null
        ? { ...patch, error: patch.error.message || patch.error.error || 'error' } : patch;
      this.store.setValue(requestStoreKey, { ...current, ...flat });
    }
  }

  _createFormData(body = {}) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) return body;
    if (typeof FormData === 'undefined') {
      throw new Error('FormData is not available in this environment');
    }
    const formData = new FormData();
    Object.entries(body || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((item) => formData.append(key, item));
      } else {
        formData.append(key, value);
      }
    });
    return formData;
  }

  async _parseUploadResponse(xhr) {
    const text = xhr.responseText || '';
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  // Answers ONE shape on both paths: { ok, data, xhr, aborted? }. The XHR path
  // carries the request object (an app reads a response header off it); the
  // fetch fallback (no XMLHttpRequest: a worker, node) has none and says so
  // with `xhr: null`, so a consumer never branches on the property's absence.
  _sendUploadRequest(url, params, formData, lifecycle = {}, hooks = {}) {
    if (typeof XMLHttpRequest === 'undefined') {
      const headers = { ...(params.headers || {}) };
      delete headers['Content-Type'];
      delete headers['content-type'];
      return fetch(url, {
        ...params,
        headers,
        body: formData,
      }).then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          const error = data?.error || data?.message || res.statusText;
          this.store.setValue('httpError', data || error);
          this._setRequestLifecycle(lifecycle, { status: 'error', error });
          return { ok: false, data, xhr: null };
        }
        this._setRequestLifecycle(lifecycle, { status: 'done', progress: 100, error: null, result: data });
        return { ok: true, data, xhr: null };
      });
    }

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open(params.method || 'POST', url);

      const headers = { ...(params.headers || {}) };
      delete headers['Content-Type'];
      delete headers['content-type'];
      Object.entries(headers).forEach(([key, value]) => {
        if (value !== undefined && value !== null) xhr.setRequestHeader(key, value);
      });

      xhr.upload.addEventListener('progress', (event) => {
        if (!event.lengthComputable) return;
        const progress = Math.round((event.loaded / event.total) * 100);
        this._setRequestLifecycle(lifecycle, { status: 'uploading', progress });
        if (typeof hooks.onProgress === 'function') hooks.onProgress({ progress, event });
      });

      xhr.addEventListener('load', async () => {
        const data = await this._parseUploadResponse(xhr);
        if (xhr.status >= 200 && xhr.status < 300) {
          this._setRequestLifecycle(lifecycle, { status: 'done', progress: 100, error: null, result: data });
          resolve({ ok: true, data, xhr });
          return;
        }
        const error = data?.error || data?.message || xhr.statusText || `HTTP ${xhr.status}`;
        this.store.setValue('httpError', data || error);
        this._setRequestLifecycle(lifecycle, { status: 'error', error });
        resolve({ ok: false, data, xhr });
      });

      xhr.addEventListener('error', () => {
        const error = 'Network error';
        this.store.setValue('httpError', error);
        this._setRequestLifecycle(lifecycle, { status: 'error', error });
        resolve({ ok: false, data: null, xhr });
      });

      // Cancellation: the entry's signal aborts the XHR, and the abort event
      // settles the promise as `aborted` (never as an error) with the progress
      // key untouched; the caller classifies by the entry's reason.
      if (params.signal) params.signal.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.addEventListener('abort', () => resolve({ ok: false, aborted: true, data: null, xhr }));

      xhr.send(formData);
    });
  }

  upload(url, {
    file,
    fieldName = 'file',
    fields = {},
    method = 'POST',
    headers = {},
    tag,
    timeout = 0,
    resultStoreKey,
    errorStoreKey,
    statusStoreKey,
    progressStoreKey,
    requestStoreKey,
    onProgress,
  } = {}) {
    const body = { ...fields };
    if (file !== undefined) body[fieldName] = file;
    const formData = this._createFormData(body);
    const lifecycle = { statusStoreKey, progressStoreKey, requestStoreKey, errorStoreKey };
    // An imperative upload has no service or endpoint name: it is registered
    // as `upload.<fieldName>#n`, so `http.cancel = 'upload.file'` (or '*', or
    // its tag) reaches it. No timer unless a `timeout` is passed.
    const entry = this._track({ service: 'upload', endpoint: fieldName, tag, kind: 'upload', timeout });
    this._setRequestLifecycle(lifecycle, { status: 'pending', progress: 0, error: null });
    return this._sendUploadRequest(url, { method, headers, signal: entry.controller.signal }, formData, lifecycle, { onProgress })
      .then((result) => {
        if (result.aborted) this._settleAborted(entry, lifecycle, url);
        if (result.ok && resultStoreKey) this.store.setValue(resultStoreKey, result.data);
        return result;
      })
      .catch((e) => {
        // The fetch fallback (no XMLHttpRequest) rejects on abort; classify it
        // the same way and answer the aborted shape.
        if (!entry.reason) throw e;
        this._settleAborted(entry, lifecycle, url);
        return { ok: false, aborted: true, data: null, xhr: null };
      })
      .finally(() => this._untrack(entry));
  }

  // Land an answer. transformFn runs whenever there is a destination, with or
  // without a resultStoreKey: an endpoint whose whole job is the fan-out (a
  // side-effect-only transformFn writing several keys) declares no primary
  // key, and skipping it there made every such endpoint dead.
  dispatchToStore(data, targetStore, resultStoreKey, transformFn, dispatchDefault, envelope = false, paginatedCtx = null) {
    const dest = targetStore || (dispatchDefault ? this.store : null);
    if (!dest) return;

    // Envelope unwrapping: { data: ..., meta: ... } → dispatch data, store meta separately
    let payload = data;
    let meta = null;
    if (envelope && data && typeof data === 'object' && 'data' in data && 'meta' in data) {
      payload = data.data;
      meta = data.meta;
      if (resultStoreKey) dest.setValue(`${resultStoreKey}Meta`, meta);
    }

    // transformFn gets the destination store as its second argument, so a
    // response that fans out across several keys maps itself: setValues the
    // secondary keys, return the primary one. Without `dest` a fan-out would
    // have to close over the global store singleton, which is wrong the moment
    // an endpoint runs against a per-request store (SSR, worker). A throw
    // lands the untransformed payload.
    let transformed = payload;
    try {
      if (typeof transformFn === 'function') transformed = transformFn(payload, dest);
    } catch (e) {
      transformed = payload;
    }
    if (!resultStoreKey) return;
    dest.setValue(resultStoreKey, transformed);

    // Paginated: store loadMore function for infinite scroll
    if (paginatedCtx && meta) {
      dest.setValue(`${resultStoreKey}.loadMore`, {
        hasMore: meta.hasMore,
        fn: paginatedCtx.loadMore,
      });
    }
  }

  computeUrl(baseUrl, url, params = {}, paramMap = {}) {
    let computedUrl = `${baseUrl}${url}`;
    Object.entries(params).forEach(([key, value]) => {
      const mappedKey = paramMap[key] || key;
      // Replace :key as a whole token — the negative lookahead stops :id from
      // matching the :idx prefix — and every occurrence, not just the first.
      const pattern = new RegExp(`:${mappedKey}(?![A-Za-z0-9_])`, 'g');
      computedUrl = computedUrl.replace(pattern, encodeURIComponent(value));
    });
    return computedUrl;
  }

  appendQueryParams(url, queryParams = {}) {
    const searchParams = new URLSearchParams();
    // Skip null/undefined so they don't serialise as the literal "undefined".
    const defined = (v) => v !== undefined && v !== null;
    Object.entries(queryParams)
      .filter(([, value]) => defined(value))
      .forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.filter(defined).forEach(v => searchParams.append(key, v));
        } else {
          searchParams.append(key, value);
        }
      });
    const queryString = searchParams.toString();
    return queryString ? `${url}${url.includes('?') ? '&' : '?'}${queryString}` : url;
  }

  setupStoreListener(storeKey, storeQueryKey, apiCall, includeQueryParams, method = 'GET') {
    if (!this._storeListeners[storeKey]) {
      this._storeListeners[storeKey] = this.store.subscribe(storeKey, (newValue) => {
        if (newValue !== undefined && newValue !== null) {
          let queryParams = {};
          if (includeQueryParams && storeQueryKey) {
            const queryValue = this.store.getValue(storeQueryKey);
            if (queryValue) queryParams = queryValue;
          }
          // The value rides as `params` for :token substitution AND, on a
          // mutating method, as the request body: a store-triggered POST whose
          // payload IS the store value (a submitted form) could otherwise only
          // send an empty body, which made `storeKey` useless for every write
          // endpoint and pushed callers back to hand-written fetch.
          const call = { params: newValue, queryParams };
          if (method !== 'GET' && method !== 'HEAD') call.body = newValue;
          apiCall(call);
        }
      });
    }
  }

  setupRouterListener(routeUrl, routeParams, apiCall, includeQueryParams, paramMap) {
    const listenerKey = routeUrl;
    if (!this._routerListeners[listenerKey]) {
      this._routerListeners[listenerKey] = this.store.subscribe('route', (routerState) => {
        // swc-4x stores route as a plain string path
        const currentUrl = typeof routerState === 'string' ? routerState : (routerState.path || '/');
        const matchedParams = matchUrlToRoute(currentUrl, { url: routeUrl });
        if (matchedParams) {
          const params = Object.fromEntries(
            Object.entries(matchedParams).map(([key, value]) => [paramMap[key] || key, value])
          );
          let queryParams = {};
          if (includeQueryParams && typeof routerState === 'object' && routerState.query) {
            queryParams = routerState.query;
          }
          apiCall({ params, queryParams });
        }
      });
    }
  }

  // The registry maps are null-prototype (see `named`), so a name that is not
  // registered answers undefined whatever it is called: 'constructor' and
  // 'toString' are not services until addServices says so.
  getService(serviceName) {
    return this._services[serviceName];
  }

  // The registered config of one endpoint. A caller that invokes the service
  // itself (s-f, which calls the bound 'service.endpoint' on submit) has to
  // build the call the way setupStoreListener does, and that needs the method:
  // a body belongs on a mutating request only. Own keys only, so a service or
  // endpoint named after something on Object.prototype answers undefined
  // instead of a function.
  getEndpointConfig(serviceName, endpointName) {
    const configs = this._endpointConfigs;
    if (!Object.hasOwn(configs, serviceName)) return undefined;
    const forService = configs[serviceName];
    return Object.hasOwn(forService, endpointName) ? forService[endpointName] : undefined;
  }

  // Adopt a bearer the app obtained some other way — an API key the server
  // provisioned, a key the user pasted. Without this the only way into
  // _authTokens is a login response carrying tokenKey, so every key-based
  // consumer had to hand-build its own Authorization header (and its own fetch).
  setAuthToken(serviceName, token) {
    if (token) this._authTokens[serviceName] = token;
    else delete this._authTokens[serviceName];
  }

  clearAuthToken(serviceName) {
    delete this._authTokens[serviceName];
  }

  // --- Auth: storage abstraction ---
  // Token key: base + optional session ID (when session.route is configured in CreateApp)
  // No session route → single shared key. With session route → scoped per session (e.g. /u/:n).
  // storage: 'memory' disables persistence entirely.

  _getStorage(serviceName) {
    const mode = this._serviceOptions[serviceName]?.auth?.storage;
    if (mode === 'memory') return null;
    return globalThis.localStorage || null;
  }

  _getStorageKey(serviceName) {
    const authCfg = this._serviceOptions[serviceName]?.auth;
    const base = authCfg?.storageKey || `swc4:auth:${serviceName}`;

    // URL-scoped session (Gmail-style /u/:n) — only when configured
    const sessionRoute = this.store._config?.session?.route;
    if (sessionRoute) {
      const url = typeof window !== 'undefined' ? window.location.pathname : '';
      const params = this.store._prefixMatchRoute(url, sessionRoute);
      const sessionId = params ? Object.values(params)[0] : null;
      if (sessionId) return `${base}:${sessionId}`;
    }

    return base;
  }

  // --- Auth: token persistence ---

  _persistToken(serviceName) {
    const storage = this._getStorage(serviceName);
    if (!storage) return;
    const key = this._getStorageKey(serviceName);
    storage.setItem(key, this._authTokens[serviceName]);
  }

  _restoreToken(serviceName) {
    const storage = this._getStorage(serviceName);
    if (!storage) return;
    const key = this._getStorageKey(serviceName);
    const token = storage.getItem(key);
    if (token) this._authTokens[serviceName] = token;
  }

  _clearPersistedToken(serviceName) {
    const storage = this._getStorage(serviceName);
    if (!storage) return;
    const key = this._getStorageKey(serviceName);
    storage.removeItem(key);
  }

  // --- Auth: clear everything ---

  _clearAuth(serviceName) {
    delete this._authTokens[serviceName];
    this._clearPersistedToken(serviceName);
    if (this._refreshTimers[serviceName]) {
      clearTimeout(this._refreshTimers[serviceName]);
      delete this._refreshTimers[serviceName];
    }
    const authCfg = this._serviceOptions[serviceName]?.auth;
    (authCfg?.clearStoreKeys || []).forEach((key) => this.store.setValue(key, null));
    if (authCfg?.userStoreKey) {
      this.store.setValue(authCfg.userStoreKey, null);
    }
  }

  // --- Auth: post-login ---

  _runPostLogin(serviceName) {
    const authCfg = this._serviceOptions[serviceName]?.auth;
    if (!authCfg) return;
    this.store?.setValue('isAuthenticated', true);
    const service = this._services[serviceName];
    // Every onLogin name that is a registered endpoint of the service fires;
    // a name that is not one is skipped.
    const onLogin = service ? (authCfg.onLogin || []) : [];
    onLogin
      .filter((ep) => typeof service[ep] === 'function')
      .forEach((ep) => service[ep]());
    if (authCfg.loginRedirect) {
      Promise.resolve().then(() => __swcNs(__m7)).then(({ router }) => {
        // Stay on current page if it's not the login page, otherwise go to loginRedirect
        const current = window.location.pathname.replace(/\/$/, '') || '/';
        const loginPage = authCfg.onUnauthorized || '/login';
        if (current === loginPage || current === '/') {
          router.navigateTo(authCfg.loginRedirect);
        } else {
          router.navigateTo(current);
        }
      });
    }
  }

  // --- Auth: JWT refresh ---

  _getTokenExpiry(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1]));
      return payload.exp || null;
    } catch {
      return null;
    }
  }

  _scheduleRefresh(serviceName) {
    const authCfg = this._serviceOptions[serviceName]?.auth;
    if (!authCfg?.refresh?.endpoint) return;
    const token = this._authTokens[serviceName];
    if (!token) return;
    const exp = this._getTokenExpiry(token);
    if (!exp) return;
    const interval = authCfg.refresh.interval || 300;
    const now = Math.floor(Date.now() / 1000);
    const delay = Math.max((exp - interval - now) * 1000, 0);
    if (this._refreshTimers[serviceName]) {
      clearTimeout(this._refreshTimers[serviceName]);
    }
    this._refreshTimers[serviceName] = setTimeout(() => this._doRefresh(serviceName), delay);
  }

  async _doRefresh(serviceName) {
    const authCfg = this._serviceOptions[serviceName]?.auth;
    if (!authCfg?.refresh?.endpoint) return;
    const service = this._services[serviceName];
    if (!service?.[authCfg.refresh.endpoint]) return;
    const result = await service[authCfg.refresh.endpoint]();
    const tk = authCfg.tokenKey || 'token';
    if (result?.[tk]) {
      this._authTokens[serviceName] = result[tk];
      this._persistToken(serviceName);
      this._scheduleRefresh(serviceName);
    } else {
      this._clearAuth(serviceName);
      Promise.resolve().then(() => __swcNs(__m7)).then(({ router }) => router.navigateTo(authCfg.onUnauthorized || '/login'));
    }
  }

  // --- Auth: public API ---

  restoreTokens() {
    Object.entries(this._serviceOptions)
      .filter(([, options]) => Boolean(options?.auth))
      .forEach(([serviceName]) => this._restoreToken(serviceName));
  }

  async initAuth(serviceName) {
    const authCfg = this._serviceOptions[serviceName]?.auth;
    if (!authCfg) return false;

    // httpOnly cookie mode: browser sends cookie automatically, no JS token needed.
    // Skip token restore — go straight to validateEndpoint.
    if (authCfg.storage !== 'cookie') {
      this._restoreToken(serviceName);
      if (!this._authTokens[serviceName]) return false;
    }

    if (authCfg.validateEndpoint) {
      const service = this._services[serviceName];
      if (!service?.[authCfg.validateEndpoint]) return false;
      const result = await service[authCfg.validateEndpoint]();
      if (!result) {
        this._clearAuth(serviceName);
        return false;
      }
    }
    this._scheduleRefresh(serviceName);
    this._runPostLogin(serviceName);
    return true;
  }

  logout(serviceName) {
    this._clearAuth(serviceName);
    const authCfg = this._serviceOptions[serviceName]?.auth;
    if (authCfg) {
      Promise.resolve().then(() => __swcNs(__m7)).then(({ router }) => router.navigateTo(authCfg.onUnauthorized || '/login'));
    }
  }

  isAuthenticated(serviceName) {
    return !!this._authTokens[serviceName];
  }

  // --- Auth: SSO (architecture ready, basic impl) ---

  ssoLogin(serviceName, providerName) {
    const authCfg = this._serviceOptions[serviceName]?.auth;
    if (!authCfg?.sso) return;
    const provider = authCfg.sso.find(p => p.name === providerName);
    if (!provider) return;
    const params = new URLSearchParams({
      client_id: provider.clientId,
      redirect_uri: provider.redirectUri,
      scope: provider.scope || '',
      response_type: 'code',
      state: serviceName + ':' + providerName,
    });
    window.location.href = `${provider.authorizeUrl}?${params}`;
  }

  async handleSSORedirect() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) return false;
    const [serviceName, providerName] = state.split(':');
    const authCfg = this._serviceOptions[serviceName]?.auth;
    if (!authCfg?.sso) return false;
    const provider = authCfg.sso.find(p => p.name === providerName);
    if (!provider?.callbackEndpoint) return false;
    const service = this._services[serviceName];
    if (!service?.[provider.callbackEndpoint]) return false;
    const result = await service[provider.callbackEndpoint]({ body: { code, provider: providerName } });
    return !!result;
  }

  // Tear down: every in-flight request is aborted with reason 'cleanup' (it
  // settles 'aborted', never as an error), the projection is emptied at once,
  // and every subscription this client made is removed, the command keys
  // included. A later addServices registers the global keys again.
  cleanup() {
    this._registry.list().forEach((entry) => this._abortEntry(entry, 'cleanup'));
    this._projectInflight();
    Object.values(this._storeListeners).forEach((sub) => {
      if (typeof sub === 'function') sub();
      else if (sub?.unsubscribe) sub.unsubscribe();
    });
    Object.values(this._routerListeners).forEach((sub) => {
      if (typeof sub === 'function') sub();
      else if (sub?.unsubscribe) sub.unsubscribe();
    });
    this._storeListeners = named();
    this._routerListeners = named();
    this._commandUnsubs.forEach((unsub) => unsub());
    this._commandUnsubs = [];
    if (this._cancelWatch) this._cancelWatch();
    this._cancelWatch = null;
    if (this._routeWatch) this._routeWatch.unsubscribe();
    this._routeWatch = null;
    Object.values(this._refreshTimers).forEach((timer) => clearTimeout(timer));
    this._refreshTimers = named();
  }
}

const httpClient = new HTTPClient();
return { HTTPClient, httpClient };
})();

const __m63 = (() => {
// Screen-reader announcements — one shared visually-hidden live region per
// politeness level, created on first use. Components call announce() for
// state a sighted user sees but AT would miss: upload progress, form error
// summaries, async results. Distinct from role=status/alert components, which
// announce their own rendered content; this is for transient messages with no
// visual element of their own.
//
// The node text is cleared then set on a frame boundary so repeated identical
// messages re-announce (live regions only fire on content CHANGE).

const regions = {};

function region(assertive) {
  const key = assertive ? 'assertive' : 'polite';
  if (regions[key]) return regions[key];
  const el = document.createElement('div');
  el.setAttribute('aria-live', key);
  el.setAttribute('aria-atomic', 'true');
  // Visually hidden, but never display:none — that silences the region.
  el.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
  document.body.appendChild(el);
  regions[key] = el;
  return el;
}

function announce(message, { assertive = false } = {}) {
  if (typeof document === 'undefined' || !message) return;
  const el = region(assertive);
  el.textContent = '';
  requestAnimationFrame(() => { el.textContent = String(message); });
}
return { announce };
})();

const __m64 = (() => {
const httpClient = __m62.httpClient;
const announce = __m63.announce;
function uploadRowDef(row) {
    const children = [];

    if (row.thumbnailUrl) {
        children.push(['img.upl-thumb', '@alt=', `@src=${row.thumbnailUrl}`]);
    }

    children.push(['s-cn.upl-info', [
        ['span.upl-name', `="${row.name}"`],
        ['span.upl-size', `="${row.sizeText}"`],
    ]]);

    if (row.isUploading) {
        children.push(['s-cn.upl-progress', [
            ['s-cn.upl-progress-bar', `@style=width: ${row.percent}%`],
        ]]);
    }

    children.push(['button.upl-remove', '@type=button', `~cl:call:_removeFile:${row.index}`, '="\u00D7"']);

    return ['s-cn.upl-item',
        ...(row.isError ? ['@error='] : []),
        children,
    ];
}

const __default = {
    tags: ['s-upl', 's-upl'],
    dispatches: ['change', 'remove'],
    props: ['@action', '@accept', '@multiple', '@disabled', '@drag', '@listType'],
    internalStore: true,
    children: [
        // The drop zone is a real keyboard target: role=button (its visible
        // drop text is the name), tabbable, Enter/Space open the picker via
        // the keydown delegate in connected().
        ['s-cn.upl-drop', ':ref=drop', '?data:isDrag', '@role=button', '@tabindex=0', [
            ['s-cn.upl-drop-icon', '=\u2B06'],
            ['s-cn.upl-drop-text', ':dataKey=dropText', ':dataField=textContent'],
        ]],
        ['button.upl-trigger', ':ref=trigger', '@type=button', '?data:isTrigger', ':dataKey=triggerText', ':dataField=textContent'],
        ['s-cn.upl-list', ':ref=list', ':dataKey=fileRows', ':dataField=children'],
    ],
    callbacks: {
        connected() {
            this._files = this._files || [];
            this._objectUrls = this._objectUrls || new Set();
            this.data = {
                dropText: 'Click or drag files to upload',
                triggerText: '\u2B06 Upload',
            };
            this._syncShellState();
            this._syncFileRows();

            if (!this._uploadEventsSetup) {
                this._uploadEventsSetup = true;
                this.addEventListener('click', (event) => this._handleClick(event));
                // The drag-and-drop affordance must have a keyboard path: the
                // drop zone is a role=button target, so Enter/Space open the
                // same picker a click does.
                this.addEventListener('keydown', (event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    if (!event.target.closest || !event.target.closest('.upl-drop')) return;
                    event.preventDefault();
                    this._openPicker();
                });
            }

            if (this.drag) this._setupDragEvents();
        },
        disconnected() {
            this._revokeObjectUrls();
        }
    },
    methods: {
        _syncShellState() {
            const isDrag = Boolean(this.drag);
            this.data.isDrag = isDrag;
            this.data.isTrigger = !isDrag;
        },
        onDragChange() {
            this._syncShellState();
            if (this.drag) this._setupDragEvents();
        },
        onListTypeChange() {
            this._syncFileRows();
        },
        _setupDragEvents() {
            if (this._dragEventsSetup) return;
            this._dragEventsSetup = true;

            this.addEventListener('dragover', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.refs.drop?.setAttribute('dragover', '');
            });

            this.addEventListener('dragleave', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.refs.drop?.removeAttribute('dragover');
            });

            this.addEventListener('drop', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.refs.drop?.removeAttribute('dragover');
                const files = Array.from(event.dataTransfer?.files || []);
                if (files.length > 0) this._addFiles(files);
            });
        },
        _handleClick(event) {
            if (this.disabled) return;

            // The remove control carries its own row index and calls
            // _removeFile itself; this handler only owns the drop surface.
            if (event.target.closest('.upl-drop') || event.target.closest('.upl-trigger')) {
                this._openPicker();
            }
        },
        set fileList(files) {
            this._revokeObjectUrls();
            this._files = (files || []).map((item) => this._createFileEntry(item, 'done', 100));
            this._syncFileRows();
        },
        get fileList() {
            return this._files || [];
        },
        _openPicker() {
            if (this.disabled) return;

            const input = document.createElement('input');
            input.type = 'file';
            if (this.accept) input.accept = this.accept;
            if (this.multiple) input.multiple = true;
            input.addEventListener('change', () => {
                const files = Array.from(input.files || []);
                if (files.length > 0) this._addFiles(files);
            });
            input.click();
        },
        _addFiles(files) {
            files.forEach((file) => {
                const entry = this._createFileEntry(file, 'ready', 0);
                this._files.push(entry);

                if (this.action) {
                    this.upload(entry);
                } else {
                    entry.status = 'done';
                    entry.percent = 100;
                }
            });
            this._syncFileRows();
            this._emitFileChange();
        },
        upload(entry) {
            if (!this.action || !entry?.file) return Promise.resolve(null);

            entry.status = 'uploading';
            entry.percent = 0;
            this._syncFileRows();

            return httpClient.upload(this.action, {
                file: entry.file,
                onProgress: ({ progress }) => {
                    entry.percent = progress;
                    this._syncFileRows();
                },
            }).then((result) => {
                entry.status = result?.ok ? 'done' : 'error';
                entry.percent = result?.ok ? 100 : entry.percent;
                entry.response = result?.data;
                this._syncFileRows();
                this._emitFileChange();
                // The visual progress bar has no audible counterpart; announce
                // the outcome (errors assertively — they need action).
                announce(result?.ok ? `${entry.name} uploaded` : `${entry.name} upload failed`,
                    { assertive: !result?.ok });
                return result;
            });
        },
        _removeFile(index) {
            if (!Number.isFinite(index)) return;
            const removed = this._files.splice(index, 1);
            this._revokeEntryUrl(removed[0]);
            this._syncFileRows();
            this.dispatchEvent(new CustomEvent('remove', {
                bubbles: true,
                detail: { file: removed[0], fileList: this._files }
            }));
        },
        _createFileEntry(item, defaultStatus, defaultPercent) {
            const file = item?.file || item;
            return {
                file,
                name: item?.name || file?.name || 'unknown',
                size: item?.size || file?.size || 0,
                status: item?.status || defaultStatus,
                percent: item?.percent ?? defaultPercent,
                response: item?.response,
                thumbnailUrl: item?.thumbnailUrl || null,
            };
        },
        _syncFileRows() {
            if (!this._internalData) return;
            this.data.fileRows = (this._files || []).map((entry, index) => uploadRowDef({
                index,
                name: entry.name,
                sizeText: this._formatSize(entry.size),
                percent: entry.percent,
                isError: entry.status === 'error',
                isUploading: entry.status === 'uploading',
                thumbnailUrl: this._thumbnailUrl(entry),
            }));
        },
        _thumbnailUrl(entry) {
            if (this.listType !== 'picture' || entry.status !== 'done' || !entry.file) return null;
            if (entry.thumbnailUrl) return entry.thumbnailUrl;
            if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
            if (typeof File !== 'undefined' && !(entry.file instanceof File)) return null;

            try {
                entry.thumbnailUrl = URL.createObjectURL(entry.file);
                this._objectUrls.add(entry.thumbnailUrl);
                return entry.thumbnailUrl;
            } catch {
                return null;
            }
        },
        _revokeEntryUrl(entry) {
            if (!entry?.thumbnailUrl || typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function') return;
            URL.revokeObjectURL(entry.thumbnailUrl);
            this._objectUrls?.delete(entry.thumbnailUrl);
            entry.thumbnailUrl = null;
        },
        _revokeObjectUrls() {
            if (!this._objectUrls || typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function') return;
            this._objectUrls.forEach((url) => URL.revokeObjectURL(url));
            this._objectUrls.clear();
            (this._files || []).forEach((entry) => { entry.thumbnailUrl = null; });
        },
        _emitFileChange() {
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { fileList: this._files }
            }));
        },
        _formatSize(bytes) {
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
            return (bytes / 1048576).toFixed(1) + ' MB';
        }
    }
};
return { __default };
})();

const __m65 = (() => {
const FORMAT_REGEX = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  url: /^https?:\/\/.+/,
  tel: /^\+?[\d\s\-().]+$/,
  date: /^\d{4}-\d{2}-\d{2}$/,
};

function isEmpty(v) {
  return v === null || v === undefined || v === '';
}

function validateField(value, rules, data) {
  if (rules.required && isEmpty(value)) {
    return rules.message || 'Required';
  }

  // Skip further checks if empty and not required
  if (isEmpty(value)) return null;

  if (rules.format && typeof value === 'string') {
    const re = FORMAT_REGEX[rules.format];
    if (re && !re.test(value)) {
      return rules.message || `Invalid ${rules.format}`;
    }
  }

  if (rules.pattern && typeof value === 'string') {
    const re = new RegExp(rules.pattern);
    if (!re.test(value)) {
      return rules.message || 'Invalid format';
    }
  }

  if (rules.minLength != null && typeof value === 'string' && value.length < rules.minLength) {
    return rules.message || `Minimum ${rules.minLength} characters`;
  }

  if (rules.maxLength != null && typeof value === 'string' && value.length > rules.maxLength) {
    return rules.message || `Maximum ${rules.maxLength} characters`;
  }

  if (rules.min != null && typeof value === 'number' && value < rules.min) {
    return rules.message || `Minimum value is ${rules.min}`;
  }

  if (rules.max != null && typeof value === 'number' && value > rules.max) {
    return rules.message || `Maximum value is ${rules.max}`;
  }

  if (rules.minItems != null && Array.isArray(value) && value.length < rules.minItems) {
    return rules.message || `Select at least ${rules.minItems}`;
  }

  if (rules.maxItems != null && Array.isArray(value) && value.length > rules.maxItems) {
    return rules.message || `Select at most ${rules.maxItems}`;
  }

  if (rules.match != null && data) {
    if (value !== data[rules.match]) {
      return rules.matchError || rules.message || `Must match ${rules.match}`;
    }
  }

  if ('const' in rules && value !== rules.const) {
    return rules.message || `Must be ${rules.const}`;
  }

  if (rules.enum && !rules.enum.includes(value)) {
    return rules.message || `Must be one of: ${rules.enum.join(', ')}`;
  }

  return null;
}

function validateFormData(data, schema) {
  if (!schema) return {};

  const errors = {};
  for (const [field, rules] of Object.entries(schema)) {
    const error = validateField(data[field], rules, data);
    if (error) errors[field] = error;
  }
  return errors;
}
return { validateFormData };
})();

const __m66 = (() => {
const store = __m0.store;
// Field participation mixin — the ONE implementation of the form protocol.
//
// <form is="s-f"> knows a field by behavior, not by tag: on connect the field
// dispatches a bubbling `fieldConnected` carrying { name, field }, the form
// registers it, reads `field.value` at submit, writes `field.value` and
// `field.data.error` back, and hears `valueChanged` for live revalidation.
// Historically only the defs generated by components/input/fields.js spoke
// this protocol, so select, date-picker, slider, tree-select, cascader et al
// silently could not join a form. `asFormField(def, opts)` retrofits the
// protocol onto any def that owns a `value`, and FIELD_TAGS lets the form
// enumerate every participating tag without a hand-kept list.



// ── Protocol helpers (moved verbatim from components/input/fields.js) ───────

// Route 'value' through the field's value setter so field._value stays in
// sync (form.data reads field.value); everything else goes to the internal store.
function setFieldDataEntry(field, key, val) {
  const resolved = val == null ? '' : val;
  if (key === 'value') {
    field.value = resolved;
    return;
  }
  field.data[key] = resolved;
}

function fieldDataSetter(field, obj) {
  if (!obj || typeof obj !== 'object') return;
  if (field._dataSubs) {
    field._dataSubs.forEach(fn => fn());
    field._dataSubs = [];
  }
  Object.entries(obj).forEach(([key, val]) => {
    if (key === 'name') { field._name = val; return; }
    if (typeof val === 'string' && val.startsWith('$')) {
      const sub = store.subscribe(val.slice(1), (v) => {
        setFieldDataEntry(field, key, v);
      });
      if (!field._dataSubs) field._dataSubs = [];
      field._dataSubs.push(sub.unsubscribe);
    } else {
      setFieldDataEntry(field, key, val);
    }
  });
}

function fieldConnected(field) {
  if (!field._dataSubs) field._dataSubs = [];
  // Markup-authored fields carry their name as an attribute; :data routes it
  // through fieldDataSetter into _name. Attribute wins only when nothing has
  // set the property, so hydration order cannot flip an authored name.
  if (!field._name && field.getAttribute) field._name = field.getAttribute('name') || undefined;
  field.dispatchEvent(new CustomEvent('fieldConnected', {
    bubbles: true,
    detail: { name: field._name, field },
  }));
}

function fieldDisconnected(field) {
  if (field._dataSubs) {
    field._dataSubs.forEach(fn => fn());
    field._dataSubs = [];
  }
  field.dispatchEvent(new CustomEvent('fieldDisconnected', {
    bubbles: true,
    detail: { name: field._name, field },
  }));
}

function dispatchValueChanged(field, value) {
  field.dispatchEvent(new CustomEvent('valueChanged', {
    bubbles: true,
    detail: { value, name: field._name },
  }));
}

// ── Tag registry (replaces the form's hand-derived FIELD_TAGS list) ─────────

const fieldTagSet = new Set();
function registerFieldTags(tags) {
  (tags || []).forEach((t) => fieldTagSet.add(t));
}
// Read lazily — the set fills as component modules evaluate, and the form
// only scans it inside connected callbacks, long after registration.
function fieldTags() {
  return [...fieldTagSet];
}

// ── The def transformer ─────────────────────────────────────────────────────

// Wraps an existing def so it participates in s-f forms:
//   asFormField(def, {
//     getValue, setValue,   — only when the def has no `value` accessor of its
//                             own (e.g. s-cb, whose value IS its selected state)
//     changeEvents: ['selected'] — host events that signal a user edit; each
//                             one re-announces as a named valueChanged
//   })
// The def keeps every behavior it had; the mixin only fills protocol gaps. It
// deliberately does NOT touch rendering — label/error presentation is s-fld's
// job (components/input/field.js), composition stays declarative.
function asFormField(def, { getValue, setValue, changeEvents = [] } = {}) {
  registerFieldTags(def.tags);
  const methods = def.methods || (def.methods = {});
  const callbacks = def.callbacks || (def.callbacks = {});

  const hasOwnValue = Object.getOwnPropertyDescriptor(methods, 'value');
  if (!hasOwnValue && getValue) {
    Object.defineProperty(methods, 'value', {
      get() { return getValue.call(this); },
      set(v) { setValue && setValue.call(this, v); },
      enumerable: true, configurable: true,
    });
  }

  // field.data.error must be assignable (the form writes it after validate).
  // Defs with an internal store already route data; for the rest, expose a
  // stable per-instance object whose `error` setter reflects onto the host —
  // [error] drives both the CSS state and the aria-invalid reflection.
  const hasOwnData = Object.getOwnPropertyDescriptor(methods, 'data');
  // List-shaped defs (itemRenderer) use `data` for their ROWS — the list mixin
  // owns that setter and overriding it silently swallowed every row (the
  // empty checkbox-group). Their aggregate value rides getValue/setValue.
  if (!hasOwnData && !def.internalStore && !def.itemRenderer) {
    Object.defineProperty(methods, 'data', {
      get() {
        if (!this._fieldMeta) {
          const host = this;
          this._fieldMeta = {
            get name() { return host._name; },
            get error() { return host.getAttribute('error') || ''; },
            set error(msg) {
              if (msg) host.setAttribute('error', msg);
              else host.removeAttribute('error');
              host.dispatchEvent(new CustomEvent('fieldErrorChanged', {
                bubbles: true,
                detail: { name: host._name, error: msg || '' },
              }));
            },
          };
        }
        return this._fieldMeta;
      },
      set(obj) { fieldDataSetter(this, obj); },
      enumerable: true, configurable: true,
    });
  }

  const prevConnected = callbacks.connected;
  callbacks.connected = function () {
    if (prevConnected) prevConnected.call(this);
    fieldConnected(this);
    changeEvents.forEach((type) => {
      this.addEventListener(type, (e) => {
        // Items inside the field re-emit selection events; only announce once,
        // with the field's own name and current value.
        if (type === 'valueChanged' && e.detail && e.detail.name === this._name) return;
        dispatchValueChanged(this, this.value);
      });
    });
    // A def that dispatches its own nameless valueChanged (hand-rolled before
    // the protocol existed) gets its events re-announced with the name the
    // form needs; named ones pass through untouched.
    this.addEventListener('valueChanged', (e) => {
      if (e.target === this && (!e.detail || e.detail.name === undefined) && this._name) {
        e.stopImmediatePropagation();
        dispatchValueChanged(this, e.detail ? e.detail.value : this.value);
      }
    });
  };

  const prevDisconnected = callbacks.disconnected;
  callbacks.disconnected = function () {
    fieldDisconnected(this);
    if (prevDisconnected) prevDisconnected.call(this);
  };

  return def;
}
return { fieldDataSetter, fieldConnected, fieldDisconnected, dispatchValueChanged, registerFieldTags, fieldTags, asFormField };
})();

const __m67 = (() => {
// The one table from a field's `type` to the def that renders it, and the one
// DSL for one field. Shared by s-f (a schema's fields) and s-rfg (a group's
// inner fields) from a third module, so neither imports the other and the
// build's module graph stays acyclic. Pure: no DOM, importable in bare Node
// (build-catalog, build-registry and gen-a11y-report import every module
// under components/input/).
const TYPE_TO_TAG = {
  text: 's-tif', email: 's-mif', password: 's-pif', number: 's-nif',
  search: 's-sif', tel: 's-tlif', url: 's-uif', date: 's-dif',
  textarea: 's-taif', radio: 's-rbf', checkboxGroup: 's-cbgf', checkbox: 's-cbf',
  group: 's-rfg',
};

// Whether a `type` has a def to render it. An absent type means text.
function isKnownFieldType(type) {
  return type === undefined || Object.prototype.hasOwnProperty.call(TYPE_TO_TAG, type);
}

// Whether two field answers are the same. An unanswered control ('',
// nothing, or an empty selection) and a missing key are the same answer, so
// a commit never writes blanks into a padded instance and a record with a
// key missing is not dirty for it. An array (a checkbox group) compares by
// its CONTENTS: the group builds a new array on every selection, so identity
// said "moved" on every commit and re-emitted a valueChanged nothing had
// changed for (F-742). Everything else compares strictly, so a commit's type
// coercion (the number a text getter settles to) still counts as a move.
function answerKey(v) {
  if (v == null || v === '') return '';
  if (!Array.isArray(v)) return v;
  return v.length ? JSON.stringify(v) : '';
}

function sameAnswer(a, b) {
  return answerKey(a) === answerKey(b);
}

// Group-field options render through the list mixin, which stamps item.text
// as the option's label: carry it alongside label/value or the generated
// radios and checkboxes come out blank. A bare string is its own label.
function toOption(o) {
  return typeof o === 'string' ? { text: o, label: o, value: o } : { text: o.label || o.value, ...o };
}

/**
 * The child DSL for one field. `name` is the schema KEY (s-f's schema and the
 * flow composite's _formDef both strip `name` out of the def) and `def` its
 * rules. The :data bag carries what the field defs read through
 * fieldDataSetter (name first, so _name is set before anything renders). A
 * `group` def returns s-rfg with the WHOLE def on :group: it renders its own
 * inner fields through this same function. An unknown type renders as a
 * plain text field: the model validates the entry whatever its type, and a
 * dropped control left the user an error naming a field that was not on the
 * page (F-733). The caller lists the offenders with isKnownFieldType.
 */
function fieldChild(name, def) {
  const tag = isKnownFieldType(def.type) ? TYPE_TO_TAG[def.type || 'text'] : TYPE_TO_TAG.text;
  const fieldData = { name, label: def.label || name };
  if (def.type === 'group') return [tag, { ':data': fieldData, ':group': def }];
  if (def.placeholder) fieldData.placeholder = def.placeholder;
  if (def.disabled) fieldData.disabled = true;
  if (def.value !== undefined) fieldData.value = def.value;
  if (def.autocomplete) fieldData.autocomplete = def.autocomplete;
  if (def.opts) fieldData.options = def.opts.map(toOption);
  return [tag, { ':data': fieldData }];
}
return { TYPE_TO_TAG, isKnownFieldType, sameAnswer, fieldChild };
})();

const __m68 = (() => {
const fieldDataSetter = __m66.fieldDataSetter;
const fieldConnected = __m66.fieldConnected;
const fieldDisconnected = __m66.fieldDisconnected;
const dispatchValueChanged = __m66.dispatchValueChanged;
const registerFieldTags = __m66.registerFieldTags;
// ── Shared field logic ──
// The protocol (fieldConnected/fieldDisconnected/valueChanged, :data routing)
// lives in core/core/field-mixin.js — ONE implementation, shared with every
// rich input that participates via asFormField. This file keeps only the
// three def factories that compose label + control + error stacks.


function fieldCSS(tags, gap = '') {
  return `${tags[0]}, ${tags[1]} { display: flex; flex-direction: column;${gap ? ` gap: ${gap};` : ''} }`;
}

// ── Input / Textarea fields ──

function createFieldDef(inputType, tags) {
  const isTextarea = inputType === 'textarea';
  const inputTag = isTextarea ? 's-ta' : 's-i';
  // $attr=key subscribes the field's INTERNAL store (where :data lands) and
  // reflects the value as an attribute on the inner input. @attr=$key would
  // subscribe the global store and never see the field's :data.
  // $error=error carries validation state onto the input itself, where the
  // s-i/s-ta aria def reflects it to aria-invalid (and [error] CSS can style).
  const inputAttrs = isTextarea
    ? ['$placeholder=placeholder', '$disabled=disabled', '$required=required', '$error=error']
    : [`@type=${inputType}`, '$placeholder=placeholder', '$disabled=disabled', '$required=required', '$autocomplete=autocomplete', '$error=error'];
  const coerce = (v) => (inputType === 'number' ? (v === '' || v == null ? null : Number(v)) : v);

  return {
    tags,
    css: fieldCSS(tags),
    internalStore: true,
    // The children below are a static template (a label, one control, an error
    // element), so SSR may emit them and hydration adopt them. Without this the
    // server's HTML for any form is a row of empty <s-mif></s-mif> tags: the
    // fields exist only after hydration, which loses the first paint, the
    // no-script reader and the crawler.
    ssrChildren: true,
    // The error element PERSISTS (s-fe:empty hides it) so its auto-id is
    // stable and the input's aria-describedby always points somewhere real;
    // its alert role announces the message when validation writes it. The
    // `from` selector is the NATIVE tag — s-i/s-ta are customized built-ins
    // (<input is="s-i">), so their s- names never match in a selector.
    aria: { refs: { describedby: { from: isTextarea ? 'textarea' : 'input', to: 's-fe' } } },
    children: [
      // Terse binding: :dk value <-> internal store; ~in:em raises the form's
      // valueChanged on input; :lf associates the label with the control (a11y).
      ['s-l', ':dk=label', ':lf=inputEl'],
      [inputTag, ...inputAttrs, ':dk=value', ':df=value', ':rf=inputEl', '~in:em'],
      ['s-fe', ':dk=error'],
    ],
    methods: {
      get value() { return coerce(this.data.value); },
      set value(v) { this.data.value = v == null ? '' : v; },
      set data(obj) { fieldDataSetter(this, obj); },
      get data() {
        if (this._internalData) return this._dataProxy;
        return { name: this._name };
      },
    },
    callbacks: {
      connected() { fieldConnected(this); },
      disconnected() { fieldDisconnected(this); },
    },
  };
}

// ── Group fields (radio, checkbox group) ──

function createGroupFieldDef(groupTag, tags, { multiSelect = false } = {}) {
  return {
    tags,
    css: fieldCSS(tags, '12px'),
    internalStore: true,
    // A static template like the text field's (a label, the option group
    // stamped from the :data bag's options, an error element), so the
    // server emits it and hydration adopts it: a radio question served
    // as an empty <s-rbf> lost its label and its options to first paint.
    ssrChildren: true,
    // The group is labelled by the field's label and described by its error —
    // without labelledby a radiogroup/group announces as nameless.
    aria: {
      refs: {
        labelledby: { from: groupTag, to: 'label' },
        describedby: { from: groupTag, to: 's-fe' },
      },
    },
    children: [
      ['s-l', ':dataKey=label'],
      [groupTag, multiSelect ? '@multi-select=' : '', ':dataKey=options'],
      ['s-fe', ':dataKey=error'],
    ],
    methods: {
      get value() { return this._value; },
      set value(v) {
        this._value = v;
        this.data.value = v;
      },
      set data(obj) { fieldDataSetter(this, obj); },
      get data() {
        if (this._internalData) return this._dataProxy;
        return { name: this._name, value: this._value };
      },
    },
    callbacks: {
      connected() {
        fieldConnected(this);
        this._group = null;

        this.addEventListener('selected', (e) => {
          if (e.target === this) return;
          if (multiSelect) {
            if (!this._group) {
              this._group = (e.composedPath ? e.composedPath() : [])
                .find(el => el.tagName && el.tagName.toLowerCase() === groupTag);
            }
            // The group's own value getter (checkbox-group.js) knows how an
            // item carries its value (_data, _optionValue, the attribute);
            // a second mapping here read only _data and answered [null] for
            // every option rendered from a schema's opts.
            this._value = this._group ? [...(this._group.value || [])] : [];
          } else {
            const item = e.detail?.item;
            this._value = item?._data?.value ?? item?.dataset?.value;
          }
          dispatchValueChanged(this, this._value);
        });
      },
      disconnected() {
        this._group = null;
        fieldDisconnected(this);
      },
    },
  };
}

// ── Checkbox field ──

function createCheckboxFieldDef(tags) {
  return {
    tags,
    css: fieldCSS(tags, '4px'),
    internalStore: true,
    aria: { refs: { describedby: { from: 'input', to: 's-fe' } } },
    children: [
      ['s-cb-w', [
        ['input', '@type=checkbox', ':dataKey=value', ':dataField=checked', ':rf=cbInput'],
        ['s-l', ':dataKey=label', ':lf=cbInput'],
      ]],
      ['s-fe', ':dataKey=error'],
    ],
    methods: {
      get value() { return this._value || false; },
      set value(v) {
        this._value = Boolean(v);
        this.data.value = this._value;
      },
      set data(obj) { fieldDataSetter(this, obj); },
      get data() {
        if (this._internalData) return this._dataProxy;
        return { name: this._name, value: this._value || false };
      },
    },
    callbacks: {
      connected() {
        fieldConnected(this);
        this.addEventListener('change', (e) => {
          if (e.target === this || e.target.type !== 'checkbox') return;
          this._value = e.target.checked;
          dispatchValueChanged(this, this._value);
        });
      },
      disconnected() { fieldDisconnected(this); },
    },
  };
}

// ── Exports ──

const textInputField = createFieldDef('text', ['s-tif', 's-tif']);
const mailInputField = createFieldDef('email', ['s-mif', 's-mif']);
const passwordInputField = createFieldDef('password', ['s-pif', 's-pif']);
const numberInputField = createFieldDef('number', ['s-nif', 's-nif']);
const searchInputField = createFieldDef('search', ['s-sif', 's-sif']);
const telInputField = createFieldDef('tel', ['s-tlif', 's-tlif']);
const urlInputField = createFieldDef('url', ['s-uif', 's-uif']);
const dateInputField = createFieldDef('date', ['s-dif', 's-dif']);
const textareaInputField = createFieldDef('textarea', ['s-taif', 's-taif']);

const radioField = createGroupFieldDef('s-rb-g', ['s-rbf', 's-rbf']);
const checkboxGroupField = createGroupFieldDef('s-cb-g', ['s-cbgf', 's-cbgf'], { multiSelect: true });
const checkboxField = createCheckboxFieldDef(['s-cbf', 's-cbf']);

// Default export = the full list of field defs, matching the framework convention
// for multi-component files (e.g. tree-grid.js). The bundler registers component
// files via their default export; named-only exports are silently skipped.
const fieldDefs = [
  textInputField, mailInputField, passwordInputField, numberInputField,
  searchInputField, telInputField, urlInputField, dateInputField, textareaInputField,
  radioField, checkboxGroupField, checkboxField,
];
// These defs implement the protocol natively; register their tags so the
// form's authored-field scans see them alongside asFormField participants.
fieldDefs.forEach((def) => registerFieldTags(def.tags));

const __default = fieldDefs;
return { textInputField, mailInputField, passwordInputField, numberInputField, searchInputField, telInputField, urlInputField, dateInputField, textareaInputField, radioField, checkboxGroupField, checkboxField, __default };
})();

const __m69 = (() => {
const store = __m0.store;
const httpClient = __m62.httpClient;
const validateFormData = __m65.validateFormData;
const announce = __m63.announce;
const fieldTags = __m66.fieldTags;
const fieldChild = __m67.fieldChild;
const isKnownFieldType = __m67.isKnownFieldType;
// What counts as a field is a CAPABILITY, not a tag list: every def that
// speaks the fieldConnected protocol (fields.js factories and every
// asFormField participant — select, date-picker, slider, tree-select,
// cascader, …) registers its tags with the mixin. fieldTags() is read at
// call time so the set is complete no matter which module evaluated first.

const __default = {
  tags: ['s-f', 's-f'],
  dispatches: ['formSubmit'],
  baseElement: 'HTMLFormElement',
  internalStore: true,
  children: [
    ['s-cn', ':dataKey=formChildren', ':dataField=children'],
  ],
  methods: {
    // Both maps are created LAZILY, never only in connected(): connectedCallback
    // binds `_storeKey` and applies the key's current value to `data` BEFORE
    // callbacks.connected runs (core/core/create-component.js, the
    // bindStoreToElement call ahead of callbacks.connected), so a form placed
    // client-side with a store-bound object payload and a `:service` used to
    // dereference maps that did not exist yet (Studio gap F12). CreateComponent
    // has no per-def construction hook, so the guard lives here, on every path
    // that touches them.
    _fieldMap() {
      if (!this._fields) this._fields = new Map();
      return this._fields;
    },
    _pendingMap() {
      if (!this._pendingValues) this._pendingValues = new Map();
      return this._pendingValues;
    },
    // A repeat group's inner fields register too, under '<group>@<n>.<field>'
    // (s-rfg names them so errors and focus can reach them); the group's
    // array is the value, so the compound names never reach the payload.
    get data() {
      const result = {};
      Array.from(this._fieldMap().entries())
        .filter(([name]) => !String(name).includes('@'))
        .forEach(([name, field]) => { result[name] = field.value; });
      return result;
    },
    set data(obj) {
      const fields = this._fieldMap();
      const pending = this._pendingMap();
      // null/undefined clears every field and starts a fresh editing cycle.
      // Silently ignoring it was a trap: a consumer clearing after submit got
      // a no-op, and the pending values kept re-applying the sent payload on
      // every field reconnect.
      if (obj === null || obj === undefined) {
        this._hasAttemptedSubmit = false;
        pending.clear();
        this._ownFields().forEach(([, field]) => {
          field.value = '';
          field.data.error = '';
        });
        return;
      }
      if (typeof obj !== 'object') return;
      // Programmatic population is not a submit attempt: reset the validation
      // cycle first so the assignment does not ring required errors.
      this._hasAttemptedSubmit = false;
      Object.entries(obj).forEach(([key, val]) => pending.set(key, val));
      Object.entries(obj)
        .filter(([key]) => fields.has(key))
        .forEach(([key, val]) => {
          const field = fields.get(key);
          field.value = val;
          field.data.error = '';
        });
    },
    get schema() { return this._schema; },
    // `_isConnected` is the core's flag: connectedCallback sets it and
    // disconnectedCallback clears it before callbacks.disconnected runs, so
    // a schema handed to a detached form waits for its next connect.
    set schema(v) {
      this._schema = v;
      if (v && this._isConnected) this._renderFromSchema();
    },
    // The payload's floor: values posted BENEATH the fields, for what the
    // fields do not carry. A settings composite keeps the groups it is not
    // showing here, so Save sends what the admin typed on every tab; a record
    // editor can keep the id its endpoint's ':id' token needs. The fields
    // win on a shared key; validation reads the fields alone.
    get baseData() { return this._baseData; },
    set baseData(v) { this._baseData = v && typeof v === 'object' ? v : null; },
    _payload() {
      return { ...(this._baseData || {}), ...this.data };
    },
    get autoSubmit() { return this._autoSubmit; },
    set autoSubmit(v) { this._autoSubmit = Boolean(v); },
    get storeKey() { return this._storeKey; },
    set storeKey(v) { this._storeKey = v; },
    get service() { return this._service; },
    set service(v) { this._service = v; },
    get submitLabel() { return this._submitLabel; },
    // The generated button's text is BOUND to the internal `submitLabel` key
    // (see _renderFromSchema), so a label written after connect reaches the
    // live button through the store, never through a second render: a
    // locale switch relabels Submit in place (F-191). A label appearing or
    // vanishing changes whether there is a button at all, which is a render.
    set submitLabel(v) {
      const had = Boolean(this._submitLabel);
      this._submitLabel = v;
      this.setData('submitLabel', v == null ? '' : String(v));
      if (this._isConnected && this._schema && had !== Boolean(v)) this._renderFromSchema();
    },

    _containsAuthoredFields() {
      return fieldTags().some((tag) => this.getElementsByTagName(tag).length > 0);
    },

    // The registered fields this form OWNS: every one but a repeat group's
    // inner fields. Those ('<group>@<n>.<field>') register but belong to
    // s-rfg: the form never writes their value or their error (the group's
    // array is the value; the flow composite applies inner errors by
    // compound name). Ownership is the NAME GRAMMAR, not schema membership:
    // an authored field alongside a schema that does not name it is still
    // the form's, so validate() blanks its stale error and `data = null`
    // clears its value, as they did before the group grammar arrived (F-741).
    _ownsField(name) {
      return !String(name).includes('@');
    },
    _ownFields() {
      return Array.from(this._fieldMap().entries()).filter(([name]) => this._ownsField(name));
    },

    // Register fields already present in the DOM whose fieldConnected event fired
    // before this form's listener was attached — e.g. authored fields re-applied
    // during hydration, which connect inside the form's own connectedCallback.
    _registerExistingFields() {
      const fields = this._fieldMap();
      const pendingValues = this._pendingMap();
      fieldTags().forEach((tag) => {
        Array.from(this.getElementsByTagName(tag))
          .filter((field) => field._name && !fields.has(field._name))
          .forEach((field) => {
            fields.set(field._name, field);
            const pending = pendingValues.get(field._name);
            if (pending !== undefined) field.value = pending;
          });
      });
    },

    _renderFromSchema() {
      const schema = this._schema;
      if (!schema) return;

      // Hand-written field children own the form layout — schema is then only
      // used for validation. Detected once, before any generation has run.
      if (this._hasAuthoredFields === undefined) {
        this._hasAuthoredFields = this._containsAuthoredFields();
      }
      if (this._hasAuthoredFields) return;

      // One DSL per schema entry (field-types.js, shared with s-rfg). An
      // entry whose type has no def renders as a plain text field rather
      // than nothing: the model still validates it (a required one rings an
      // error naming a field), so a screen with no control for it could
      // never be completed. The offenders are listed on `unknownTypes` for
      // a lint or the flow model to read, and said once per render (F-733).
      this.unknownTypes = Object.entries(schema)
        .filter(([, def]) => !isKnownFieldType(def && def.type))
        .map(([name, def]) => ({ name, type: def && def.type }));
      if (this.unknownTypes.length) {
        const listed = this.unknownTypes.map((f) => `'${f.type}' (${f.name})`).join(', ');
        console.warn(`[swc] s-f: no field renders type ${listed}; rendered as text`);
      }
      const children = Object.entries(schema)
        .map(([name, def]) => fieldChild(name, def))
        .filter(Boolean);

      if (this._submitLabel) {
        // Label as a SPAN CHILD, not a bare `=` text token: s-b stamps its own
        // def children (the icon conditional) after tokens apply, which wiped
        // token-set text — production submit buttons rendered empty (an axe
        // button-name critical) for as long as this used the token form. The
        // span's text is bound to the internal key the submitLabel setter
        // writes, so a label set later reaches the live button (F-191).
        this.setData('submitLabel', String(this._submitLabel));
        children.push(['s-b', '@c=pr', [['span', '$submitLabel']]]);
      }

      this.setData('formChildren', children);
    },

    validate() {
      const errors = validateFormData(this.data, this._schema);
      const hasErrors = Object.keys(errors).length > 0;
      this._ownFields().forEach(([name, field]) => {
        field.data.error = errors[name] || '';
      });
      return !hasErrors;
    },
    _validateField(name) {
      if (!this._schema || !this._ownsField(name)) return;
      // A group's bounds are judged on submit (and, in a flow, by the engine
      // over the projected instances): live revalidation of the raw array on
      // every inner edit would ring the bounds message while the user types.
      // An authored field the schema does not name has no rules to ring.
      const rules = this._schema[name];
      if (!rules || rules.type === 'group') return;
      const errors = validateFormData(this.data, this._schema);
      const field = this._fields.get(name);
      if (field) field.data.error = errors[name] || '';

      Object.entries(this._schema)
        .filter(([, rules]) => rules.match === name)
        .forEach(([otherName]) => {
          const otherField = this._fields.get(otherName);
          if (otherField) otherField.data.error = errors[otherName] || '';
        });
    },
    // Call the bound 'service.endpoint' with the submitted data. Answers
    // whether the client TOOK the request, so submit() knows whether the
    // store key still has to fire it. The answer is the client's, not the
    // endpoint's existence (F-564): the endpoint closure returns its promise
    // when a request is in flight (a fetch, an upload, a stream) or a cached
    // answer was dispatched, and nothing when the call went onto the
    // service's batch queue, where the flush sends it a tick later. A queued
    // call is in the in-flight registry from the moment it is queued (the
    // client projects it to `http.inflight` as it tracks it), so that is the
    // second signal. A call that is neither did not go out, and the store
    // key is the fallback. What the request comes to afterwards is reported
    // through the endpoint's lifecycle keys, never here.
    _callService(data) {
      if (!this._service) return false;
      const [svcName, epName] = String(this._service).split('.');
      const svc = httpClient.getService(svcName);
      if (!svc?.[epName]) return false;
      // Build the call exactly as the store listener does (http-client
      // setupStoreListener). The value rides as `params`, so an endpoint whose
      // url carries a token ('/items/:id') is substituted and not requested
      // literally, and as the body only on a mutating method: fetch rejects a
      // GET or HEAD that carries one, which would leave the form with no
      // request at all and no error to show for it.
      const method = httpClient.getEndpointConfig(svcName, epName)?.method;
      const call = { params: data };
      if (method !== 'GET' && method !== 'HEAD') call.body = data;
      const answer = svc[epName](call);
      if (answer && typeof answer.then === 'function') return true;
      return (store.getValue('http.inflight') || [])
        .some((entry) => entry.service === svcName && entry.endpoint === epName);
    },
    submit(overrideData) {
      this._hasAttemptedSubmit = true;
      const data = overrideData ?? this._payload();
      if (!overrideData && !this.validate()) {
        // Each field's alert announces its own message, but a sighted user
        // also sees at a glance THAT the submit failed and how much is wrong —
        // give the screen reader the same summary, assertively.
        const count = [...this._fields.values()].filter((f) => f.data && f.data.error).length;
        announce(`${count} ${count === 1 ? 'field needs' : 'fields need'} attention`, { assertive: true });
        return;
      }
      // ONE request per submit. A form has two possible triggers: a `service`
      // it calls itself, and a `storeKey` an http-client row listens on
      // (setupStoreListener turns the write into the request, with the value
      // as the body on a mutating method). Wired both ways this did BOTH, and
      // store.setValue notifies for every object payload, so the same body was
      // posted twice, concurrently: a sign-in hashed the password and issued a
      // cookie twice, a sign-up raced its own first-account-is-admin check and
      // then failed the second insert on UNIQUE(email), a settings save wrote
      // two rows. The service is the explicit wiring, so it wins; the store
      // write is the fallback, including when the named service is not
      // registered, so a mistyped name cannot leave the form with no trigger
      // at all.
      if (!this._callService(data) && this._storeKey) store.setValue(this._storeKey, data);
      this.dispatchEvent(new CustomEvent('formSubmit', { bubbles: true, detail: data }));
    },
    // The listeners, declared below and bound by the core ONCE per element
    // (_setupDeclaredListeners runs behind the _eventsSetup guard). They
    // used to be added in connected(), which the core runs on EVERY connect:
    // a form the router cached and re-inserted carried a second set and
    // submitted twice per click, one request each (F-572).
    _onSubmit(e) {
      e.preventDefault();
      this.submit();
    },
    _onSelected(e) {
      if (!this._autoSubmit) return;
      const detail = e.detail || {};
      this.submit(detail.data || { value: detail.value });
    },
    _onFieldConnected(e) {
      const { name, field } = e.detail;
      if (!name) return;
      this._fieldMap().set(name, field);
      const pending = this._pendingMap().get(name);
      if (pending !== undefined) field.value = pending;
    },
    _onFieldDisconnected(e) {
      const { name } = e.detail;
      if (name) this._fieldMap().delete(name);
    },
    _onValueChanged(e) {
      const { name, value } = e.detail;
      if (!name) return;
      this._pendingMap().set(name, value);
      if (this._hasAttemptedSubmit) this._validateField(name);
    },
    _onKeydown(e) {
      if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
        e.preventDefault();
        this.submit();
      }
    },
    _onClick(e) {
      let el = e.target;
      while (el && el !== this) {
        // The button is a customized built-in (<button is="s-b">), so match on
        // the reflected `is` value rather than a custom element tagName.
        const isVal = el.getAttribute && el.getAttribute('is');
        if (isVal === 's-b' || isVal === 's-b') {
          this.submit();
          return;
        }
        el = el.parentElement;
      }
    },
  },
  listeners: {
    submit: '_onSubmit',
    selected: '_onSelected',
    fieldConnected: '_onFieldConnected',
    fieldDisconnected: '_onFieldDisconnected',
    valueChanged: '_onValueChanged',
    keydown: '_onKeydown',
    click: '_onClick',
  },
  callbacks: {
    connected() {
      // Ensure, never reset: a payload bound before connect (the store key's
      // current value) is pending here and reaches the fields as they register.
      this._fieldMap();
      this._pendingMap();
      this._hasAttemptedSubmit = false;
      this._autoSubmit = false;

      if (this._schema) this._renderFromSchema();

      // Catch any fields already mounted before this connect, or whose
      // fieldConnected fired while the form was detached.
      this._registerExistingFields();
    },
  },
};
return { __default };
})();

const __m70 = (() => {
// Default accessible-name strings for the framework's own affordances (close
// buttons, clear buttons, dismissals). Seeded UNDER the app's state on both
// the client and the SSR store, so components can bind '@aria-label=$t.a11y.*'
// and always resolve a name — and a locale pack that carries t.a11y.* keys
// overrides every one of these without any component knowing.
const A11Y_DEFAULT_STRINGS = {
  't.a11y.close': 'Close',
  't.a11y.clear': 'Clear',
  't.a11y.remove': 'Remove',
  't.a11y.dismiss': 'Dismiss',
  't.a11y.previous': 'Previous',
  't.a11y.next': 'Next',
  // Calendar chrome: "Previous" beside a "Next" step button is ambiguous the
  // moment a calendar sits inside a wizard — the month arrows say what they
  // move.
  't.a11y.prevMonth': 'Previous month',
  't.a11y.nextMonth': 'Next month',
  't.a11y.loading': 'Loading',
  't.a11y.copy': 'Copy',
  't.a11y.decrease': 'Decrease quantity',
  't.a11y.increase': 'Increase quantity',
  't.a11y.jumpLatest': 'Jump to latest',
  't.a11y.toggleNav': 'Toggle navigation',
  't.a11y.toggleSidebar': 'Show or hide the sidebar',
  't.a11y.skipToContent': 'Skip to content',
  't.a11y.expand': 'Expand',
  // Carousel chrome. The indicator is a bar of segments with no visible text
  // by design, so every one of its names has to come from here.
  't.a11y.slides': 'Slides',
  't.a11y.slide': 'Slide',
  // Mail template chrome. Icon-only toolbar buttons and placeholder-only
  // fields have no visible text to borrow a name from.
  't.a11y.searchMail': 'Search mail',
  't.a11y.archiveSelected': 'Archive selected',
  't.a11y.deleteSelected': 'Delete selected',
  't.a11y.markAllRead': 'Mark all read',
  't.a11y.to': 'To',
  't.a11y.subject': 'Subject',
  't.a11y.message': 'Message',
  't.a11y.from': 'From',
  't.a11y.attach': 'Attach a file',
  't.a11y.send': 'Send',
  't.a11y.compose': 'Compose',
  // Mid-conversation actions: an icon and an overflow trigger, both nameless
  // without these.
  't.a11y.reply': 'Reply',
  't.a11y.forward': 'Forward',
  't.a11y.moreActions': 'More actions',
  // Named by the migration emitters (server/tools/convert-engine, wp-migrate):
  // generated markup binds these rather than hardcoding a literal name.
  't.a11y.menu': 'Menu',
  't.a11y.dictate': 'Dictate',
  't.a11y.siteNav': 'Site',
  't.a11y.markUnread': 'Mark unread',
  't.a11y.back': 'Back',
  // The chat composites' stream controls: an icon-only Stop, and the toggle of
  // a collapsed reasoning block. An unnamed icon button is an axe critical.
  't.a11y.stop': 'Stop',
  't.a11y.thinking': 'Show thinking',
  // The repeat group's chrome (s-rfg): the Add button when the group's own
  // `repeat.addAnother` names none, and the two announcements a structural
  // change makes, with `{label}` standing for the instance's heading
  // ('Phone number 2'). Remove reuses t.a11y.remove above.
  't.a11y.addAnother': 'Add another',
  't.a11y.itemAdded': '{label} added',
  't.a11y.itemRemoved': '{label} removed',
};
return { A11Y_DEFAULT_STRINGS };
})();

const __m71 = (() => {
const validateFormData = __m65.validateFormData;
// The flow model — the ONE canonical implementation of declarative multi-step
// form semantics (GOV.UK-style question flows), shared by the client engine
// (core/services/flow.js), the SSR router and swc-lint. Pure functions only —
// no DOM, no node builtins — safe in the browser and on Cloudflare workerd
// alike (the route-table.js discipline).
//
// A flow is DATA: sections → screens → fields, with `next` rules diverging on
// answered values. Two answer layers give the engine its retention semantics:
// the RAW layer keeps everything ever entered; the PROJECTION (projectOutput)
// contains only what the currently-active path exposes — the summary and the
// submit body read the projection, so a de-activated branch's answers can
// never leak, yet re-activating the branch restores them from raw.
// A `group` FIELD is the one exception on the raw side: s-rfg REPLACES its
// array on add and remove, so a removed instance is gone from raw too (the
// projection still drops instances left wholly blank).
//
// Nothing here throws on bad config (house rule: vocabulary is enforced by
// tools/swc-lint.mjs via validateFlowConfig, not by runtime exceptions).



const OPERATOR_ALIASES = {
  '=': 'equals',
  '==': 'equals',
  '!=': 'notEquals',
  'greater than': 'greaterThan',
  'less than': 'lessThan',
};

const SUMMARY_TARGET = '@summary';
const IMPLICIT_SECTION = '_main';

// The summary label separator (a spaced em dash), built from its code point
// so new code in this file stays plain ASCII; repeat rows and group rows both
// read it.
const SUMMARY_SEP = ' ' + String.fromCharCode(0x2014) + ' ';

function isEmptyValue(v) {
  return v === null || v === undefined || v === ''
    || (Array.isArray(v) && v.length === 0);
}

// ── Conditions ──
//
// Vocabulary adopted from the agent-stack condition node ({field, operator,
// value}) and extended with combinators. Comparison discipline mirrors
// matchesShowIf: String() equality, so '18' matches 18. Numeric operators go
// through Number(); NaN never matches. Unknown shapes evaluate to false —
// a condition that cannot be understood must not fire a branch.

function evalCondition(cond, answers = {}) {
  if (cond == null) return true;
  if (typeof cond !== 'object') return false;

  if (Array.isArray(cond.all)) return cond.all.every((c) => evalCondition(c, answers));
  if (Array.isArray(cond.any)) return cond.any.some((c) => evalCondition(c, answers));
  if (cond.not != null) return !evalCondition(cond.not, answers);

  if (typeof cond.field !== 'string') return false;
  const value = answers[cond.field];
  const operator = OPERATOR_ALIASES[cond.operator] || cond.operator || 'equals';

  // answered/notAnswered ask ABOUT emptiness; every other operator compares a
  // value, and an empty answer has nothing to compare — it must not divert a
  // branch while the user is still typing (Number('') is 0, so lessThan once
  // fired on a cleared number field). A rule with no `value` cannot compare
  // either: String(undefined) === String(undefined) made it fire precisely
  // while unanswered. Both are lint errors (FLOW_CONDITION_NO_VALUE); at
  // runtime they simply never match.
  if (operator !== 'answered' && operator !== 'notAnswered') {
    if (isEmptyValue(value)) return false;
    if (cond.value === undefined) return false;
  }

  switch (operator) {
    case 'equals':
      return String(value) === String(cond.value);
    case 'notEquals':
      return String(value) !== String(cond.value);
    case 'contains':
      if (Array.isArray(value)) return value.some((v) => String(v) === String(cond.value));
      if (typeof value === 'string') return value.includes(String(cond.value));
      return false;
    case 'greaterThan': {
      const a = Number(value); const b = Number(cond.value);
      return Number.isFinite(a) && Number.isFinite(b) && a > b;
    }
    case 'lessThan': {
      const a = Number(value); const b = Number(cond.value);
      return Number.isFinite(a) && Number.isFinite(b) && a < b;
    }
    case 'in':
      return Array.isArray(cond.value) && cond.value.some((v) => String(v) === String(value));
    case 'answered':
      return !isEmptyValue(value);
    case 'notAnswered':
      return isEmptyValue(value);
    default:
      return false;
  }
}

// ── Normalization ──
//
// normalizeFlowConfig fills defaults and derives lookups; it never rejects.
// The normalized shape is what every other function here expects — callers
// normalize once and pass the result around (idempotent: normalizing a
// normalized config is a no-op).

// A `group` field (type: 'group') carries its inner `fields` and a `repeat`
// label bag; both get defaults on a COPY of the field, never on the input.
function normalizeField(field) {
  if (!field || typeof field !== 'object' || field.type !== 'group') return field;
  const repeat = field.repeat && typeof field.repeat === 'object' ? field.repeat : {};
  return {
    ...field,
    repeat: { addAnother: 'Add another', itemLabel: '{n}', ...repeat },
    fields: Array.isArray(field.fields) ? field.fields.slice() : [],
  };
}

// A shallow screen copy with its defaults. `fields` is a NEW array (and a
// group field inside it a new object), so an emitted `const FLOW` is never
// written to: the old `{ ...s }` copy shared the input's arrays.
function normalizeScreen(screen) {
  if (!screen || typeof screen !== 'object') return screen;
  return {
    ...screen,
    fields: Array.isArray(screen.fields) ? screen.fields.map(normalizeField) : [],
    next: Array.isArray(screen.next) ? screen.next : [],
  };
}

function normalizeFlowConfig(config) {
  if (!config || typeof config !== 'object') return null;
  if (config._normalized) return config;

  const screens = Array.isArray(config.screens) ? config.screens.map(normalizeScreen) : [];
  const screenMap = {};
  const questionIds = [];
  const exitIds = [];
  screens.filter((screen) => screen && typeof screen.id === 'string').forEach((screen) => {
    screenMap[screen.id] = screen;
    (screen.type === 'exit' ? exitIds : questionIds).push(screen.id);
  });

  // Sections: every question screen belongs to exactly one. Unlisted screens
  // fall into an implicit trailing section so a sectionless flow just works.
  const declared = Array.isArray(config.sections) ? config.sections.map((s) => ({ ...s })) : [];
  const claimed = new Set();
  for (const section of declared) {
    section.screens = (section.screens || []).filter((id) => screenMap[id] && !exitIds.includes(id));
    section.present = section.present === 'accordion' ? 'accordion' : 'screens';
    section.summary = section.summary === 'end' ? 'end' : 'none';
    for (const id of section.screens) claimed.add(id);
  }
  const unclaimed = questionIds.filter((id) => !claimed.has(id));
  const sections = declared.slice();
  if (unclaimed.length || sections.length === 0) {
    sections.push({
      id: IMPLICIT_SECTION, title: config.title || '', screens: unclaimed,
      present: 'screens', summary: 'none',
    });
  }
  const sectionOf = {};
  for (const section of sections) {
    for (const id of section.screens) sectionOf[id] = section.id;
  }

  // Declaration order = fall-through order: the concatenation of section
  // screen lists. A screen with no matching `next` rule advances to the next
  // id in this order (the linear-wizard degenerate case needs no `next` at all).
  const order = sections.flatMap((s) => s.screens);

  const summary = {
    id: 'check', title: 'Check your answers', layout: 'sections', enabled: true,
    ...(config.summary && typeof config.summary === 'object' ? config.summary : {}),
  };
  if (config.summary === false) summary.enabled = false;

  const buttons = {
    back: true, save: false,
    ...(config.buttons && typeof config.buttons === 'object' ? config.buttons : {}),
  };
  buttons.labels = {
    continue: 'Continue', back: 'Back', save: 'Save', send: 'Accept and send',
    ...(buttons.labels || {}),
  };

  return {
    ...config,
    _normalized: true,
    routed: config.routed !== false,
    version: config.version == null ? 1 : config.version,
    persistence: config.persistence || 'memory',
    progress: config.progress || 'none',
    markOptional: config.markOptional !== false,
    screens, screenMap, sections, sectionOf, order, exitIds, summary, buttons,
  };
}

// ── Path computation ──
//
// Walk from the first screen following `next` rules against the raw answers.
// First matching rule wins (a rule with no `when` always matches — the
// unconditional tail). No matching rule → fall through in declaration order.
// The walk ends at an exit screen, at '@summary', or past the last screen.
// A goto that revisits a screen already on the path is a config error
// (FLOW_UNREACHABLE/loop); the walk stops rather than cycling — never throws.

function computePath(config, answers = {}) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg || cfg.order.length === 0) return { path: [], exit: null, reachedSummary: false };

  const path = [];
  const visited = new Set();
  let current = cfg.order[0];
  let exit = null;
  let reachedSummary = false;

  while (current && !visited.has(current)) {
    const screen = cfg.screenMap[current];
    if (!screen || screen.type === 'exit') { exit = screen ? current : null; break; }
    path.push(current);
    visited.add(current);

    let target = null;
    for (const rule of screen.next) {
      if (rule && evalCondition(rule.when, answers)) {
        target = rule; break;
      }
    }

    if (target && target.exit && cfg.screenMap[target.exit]) {
      exit = target.exit; break;
    }
    if (target && target.goto === SUMMARY_TARGET) {
      reachedSummary = true; break;
    }
    if (target && target.goto && cfg.screenMap[target.goto]) {
      current = target.goto; continue;
    }
    // Fall through in declaration order (also covers a dangling goto —
    // permissive at runtime, an error in lint).
    const idx = cfg.order.indexOf(current);
    current = idx >= 0 && idx + 1 < cfg.order.length ? cfg.order[idx + 1] : null;
  }

  if (!exit && !reachedSummary && path.length && current == null) reachedSummary = true;
  return { path, exit, reachedSummary };
}

// ── Repeatable groups ──
//
// A screen with `repeat: { key, min, max, addAnother }` collects an ARRAY of
// answer sets under answers[key] — one page per instance (/route/screen/n).
// Each instance carries a generated `_more` decision ("add another?"); `_more`
// IS the repeat's branch condition: instance n+1 is active only while
// instance n says yes. Saying no later EXCLUDES the tail from the projection
// but never deletes it from raw — the same retention contract as branches.
//
// An inline `group` FIELD (type: 'group', rendered by s-rfg on any screen) is
// the other repeatable shape: its answer is the instance array itself, with
// no `_more` mechanics; add and remove replace that array, so a removed
// instance is not retained. See screenErrors and projectInstances below.

function repeatKey(screen) {
  return (screen && screen.repeat && screen.repeat.key) || (screen && screen.id);
}

function repeatInstances(screen, answers = {}) {
  const list = answers[repeatKey(screen)];
  return Array.isArray(list) ? list : [];
}

/**
 * How many instances are ACTIVE: instance 1 always; each further instance
 * only while the previous one answered `_more: 'yes'` (capped by max). The
 * count includes the "next empty" instance a trailing yes has opened.
 */
function activeRepeatCount(screen, answers = {}) {
  const max = (screen.repeat && screen.repeat.max) || Infinity;
  const instances = repeatInstances(screen, answers);
  let count = 1;
  while (count < max && instances[count - 1] && String(instances[count - 1]._more) === 'yes') {
    count += 1;
  }
  return count;
}

/** Position identity — repeatIndex included; the ONE comparison everywhere. */
function samePosition(a, b) {
  if (!a || !b) return false;
  return a.kind === b.kind
    && (a.screenId || null) === (b.screenId || null)
    && (a.sectionId || null) === (b.sectionId || null)
    && (a.repeatIndex || null) === (b.repeatIndex || null);
}

/** '{n}' in a template names the instance; without it the index is appended. */
function instanceLabel(template, index) {
  const text = template || '';
  return text.includes('{n}') ? text.replaceAll('{n}', String(index)) : `${text} ${index}`;
}

/** '{n}' in a repeat screen's title names the instance: 'Director {n}'. */
function repeatTitle(screen, index) {
  return instanceLabel((screen && screen.title) || '', index);
}

/** A group instance's label from repeat.itemLabel: 'Phone number {n}'. */
function groupItemLabel(group, index) {
  return instanceLabel((group && group.repeat && group.repeat.itemLabel) || '{n}', index);
}

// ── Field visibility & projection ──

function visibleFields(screen, answers = {}) {
  if (!screen || !Array.isArray(screen.fields)) return [];
  return screen.fields.filter((f) => f && (f.revealWhen == null || evalCondition(f.revealWhen, answers)));
}

// Field descriptors carry validator rules inline. ONE extraction, exported so
// the engine validates with exactly the rules completeness is judged by —
// two notions of "answered" (the model's isEmptyValue vs the validator's
// isEmpty, which accepts [] and false) once deadlocked a flow: validation
// passed, completeness never did, and send() submitted a body the projection
// had already dropped the answer from.
const RULE_KEYS = ['required', 'format', 'pattern', 'minLength', 'maxLength',
  'min', 'max', 'minItems', 'maxItems', 'match', 'matchError', 'const', 'enum', 'message'];

function fieldsToRules(fields) {
  const schema = {};
  for (const field of fields || []) {
    if (!field || field.name == null) continue;
    const rules = {};
    for (const key of RULE_KEYS) {
      if (field[key] !== undefined) rules[key] = field[key];
    }
    schema[field.name] = rules;
  }
  return schema;
}

/** One repeat instance's completeness — visibility judged on ITS values. */
function isInstanceComplete(screen, instance = {}) {
  const errors = validateFormData(instance, fieldsToRules(visibleFields(screen, instance)));
  return Object.keys(errors).length === 0 && !isEmptyValue(instance._more);
}

// ── Group fields ──
//
// A `group` field holds an ARRAY of instance objects under one name (s-rfg
// renders it: array in, array out, add and remove inline). Its bounds
// (minItems/maxItems) are judged on the PROJECTED instances, never through
// validateFormData, whose empty short-circuit skips an absent array before
// minItems. Inner fields are one level deep and carry no reveal (lint:
// FLOW_GROUP_NESTED, FLOW_GROUP_FIELD).

function innerFields(group) {
  const fields = group && Array.isArray(group.fields) ? group.fields : [];
  return fields.filter((f) => f && f.name != null);
}

/** An instance is blank when every declared inner value is empty. */
function isEmptyInstance(group, instance) {
  if (!instance || typeof instance !== 'object') return true;
  return innerFields(group).every((f) => isEmptyValue(instance[f.name]));
}

/** One projected instance: the declared inner fields with a non-empty value. */
function pickInstance(group, instance) {
  const row = {};
  innerFields(group)
    .filter((f) => !isEmptyValue(instance[f.name]))
    .forEach((f) => { row[f.name] = instance[f.name]; });
  return row;
}

/**
 * The non-blank instances, each with its RAW 1-based position `n`: the ONE
 * numbering every surface uses. The error name ('<group>@<n>.<field>'), the
 * summary row's key ('<group>.<n>.<field>') and its label ('Phone number n')
 * all count the array s-rfg rendered, blank instances included, so with
 * phones [{}, {...}] the panel, the error link and the summary all say 2.
 */
function keptInstances(group, value) {
  const raw = Array.isArray(value) ? value : [];
  return raw.map((instance, i) => ({ instance, n: i + 1 }))
    .filter((entry) => !isEmptyInstance(group, entry.instance));
}

/** The projected instances: raw minus the blank ones, each picked down. */
function projectInstances(group, value) {
  return keptInstances(group, value).map((entry) => pickInstance(group, entry.instance));
}

/** The group's own message when the projected count breaks a bound. */
function groupBoundsError(group, count) {
  const belowMin = group.minItems != null && count < group.minItems;
  const aboveMax = group.maxItems != null && count > group.maxItems;
  if (!belowMin && !aboveMax) return null;
  if (group.message) return group.message;
  return belowMin ? `Add at least ${group.minItems}` : `Add at most ${group.maxItems}`;
}

/** One instance's inner errors, keyed '<group>@<n>.<field>' (n 1-based, raw). */
function instanceErrors(group, instance, n) {
  const out = {};
  Object.entries(validateFormData(instance, fieldsToRules(innerFields(group))))
    .forEach(([name, text]) => { out[`${group.name}@${n}.${name}`] = text; });
  return out;
}

function groupErrors(group, value) {
  const kept = keptInstances(group, value);
  const errors = {};
  const bounds = groupBoundsError(group, kept.length);
  if (bounds) errors[group.name] = bounds;
  kept.forEach((entry) => Object.assign(errors, instanceErrors(group, entry.instance, entry.n)));
  return errors;
}

/**
 * Every error on one screen, keyed the way the composite names its fields:
 * a top-level non-group field under its own name (validateFormData, as
 * before); a group's bounds under the bare group name, judged on the
 * PROJECTED instances (a wholly blank first instance under minItems: 1 yields
 * only this message, never an inner one); every inner field of every
 * NON-BLANK instance under '<group>@<n>.<field>', n counting the RAW array
 * from 1 so the key always names the control s-rfg rendered at that
 * position. The ONE validation walk: isScreenComplete, firstIncomplete and
 * the engine's next() and send() all read it.
 */
function screenErrors(screen, answers = {}) {
  const fields = visibleFields(screen, answers);
  const errors = validateFormData(answers, fieldsToRules(fields.filter((f) => f.type !== 'group')));
  fields.filter((f) => f.type === 'group' && f.name != null)
    .forEach((group) => Object.assign(errors, groupErrors(group, answers[group.name])));
  return errors;
}

function isScreenComplete(screen, answers = {}) {
  if (screen && screen.repeat) {
    const min = screen.repeat.min || 1;
    const instances = repeatInstances(screen, answers);
    const active = activeRepeatCount(screen, answers);
    if (instances.length < Math.max(min, 1)) return false;
    for (let n = 1; n <= active; n += 1) {
      if (!instances[n - 1] || !isInstanceComplete(screen, instances[n - 1])) return false;
    }
    return true;
  }
  return Object.keys(screenErrors(screen, answers)).length === 0;
}

/**
 * The projection: only answers exposed by the currently-active path (and
 * un-concealed reveals). This object IS the submit body and the summary's
 * source — the raw layer is never read by either.
 */
function projectOutput(config, answers = {}) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg) return {};
  const { path } = computePath(cfg, answers);
  const output = {};
  for (const id of path) {
    const screen = cfg.screenMap[id];
    if (screen.repeat) {
      // Active instances only, each picked down to its visible fields —
      // the `_more` decision is flow mechanics, never payload.
      const instances = repeatInstances(screen, answers);
      const active = activeRepeatCount(screen, answers);
      const picked = [];
      for (let n = 1; n <= active && n <= instances.length; n += 1) {
        const instance = instances[n - 1] || {};
        const row = {};
        for (const field of visibleFields(screen, instance)) {
          if (field.name != null && !isEmptyValue(instance[field.name])) {
            row[field.name] = instance[field.name];
          }
        }
        picked.push(row);
      }
      if (picked.length) output[repeatKey(screen)] = picked;
      continue;
    }
    visibleFields(screen, answers)
      .filter((field) => field.name != null)
      .map((field) => [field.name, field.type === 'group'
        ? projectInstances(field, answers[field.name]) : answers[field.name]])
      .filter(([, value]) => !isEmptyValue(value))
      .forEach(([name, value]) => { output[name] = value; });
  }
  return output;
}

/**
 * First screen on the active path that is not yet complete — the resume
 * point. An optional `completed` set (screen ids the user has actually
 * moved past) tightens the check for screens with no required fields.
 */
function firstUnanswered(config, answers = {}, completed = null) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg) return null;
  const { path } = computePath(cfg, answers);
  for (const id of path) {
    const screen = cfg.screenMap[id];
    if (!isScreenComplete(screen, answers)) return id;
    // A screen with no required fields is "complete" vacuously — only the
    // engine's completed-set (screens the user actually moved past) can
    // confirm it. Without confirmation it is still the resume point.
    const hasRequired = visibleFields(screen, answers).some((f) => f.required);
    if (!hasRequired && completed && !completed.has(id)) return id;
  }
  return null;
}

// ── Summary rows ──

function formatAnswer(field, value) {
  if (isEmptyValue(value)) return '';
  const opts = Array.isArray(field && field.opts) ? field.opts : null;
  const label = (v) => {
    if (opts) {
      const hit = opts.find((o) => o && String(o.value) === String(v));
      if (hit && hit.label != null) return String(hit.label);
    }
    return String(v);
  };
  if (Array.isArray(value)) return value.map(label).join(', ');
  return label(value);
}

function summaryRow(key, label, value, notProvided, changePosition) {
  return {
    key, label, value, notProvided,
    changePosition: { ...changePosition },
    hiddenLabel: String(label).toLowerCase(),
  };
}

function fieldLabel(field) {
  return field.label != null ? field.label : field.name;
}

// Paged repeats: one row per instance per field, titled by the instance
// ('Director 2', the separator, 'Full name'), each linking to ITS page.
function repeatRows(screen, section, answers) {
  const instances = repeatInstances(screen, answers);
  const count = Math.min(activeRepeatCount(screen, answers), instances.length);
  return instances.slice(0, count).flatMap((raw, i) => {
    const instance = raw || {};
    const n = i + 1;
    return visibleFields(screen, instance).map((field) => summaryRow(
      `${repeatKey(screen)}.${n}.${field.name}`,
      `${repeatTitle(screen, n)}${SUMMARY_SEP}${fieldLabel(field)}`,
      formatAnswer(field, instance[field.name]),
      isEmptyValue(instance[field.name]),
      { sectionId: section.id, screenId: screen.id, repeatIndex: n },
    ));
  });
}

// Custom screens contribute explicit summary items.
function itemRows(screen, section, answers) {
  return screen.summary.items
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const value = answers[item.key];
      return summaryRow(item.key, item.label != null ? item.label : item.key,
        isEmptyValue(value) ? '' : String(value), isEmptyValue(value),
        { sectionId: section.id, screenId: screen.id });
    });
}

// A group field: one row per NON-BLANK instance per inner field, the paged
// repeat's row shape without an instance in changePosition (the Change link
// lands on the screen, where s-rfg renders every instance). The instance is
// numbered by its RAW position (keptInstances), the same n the error name
// carries: a leading blank instance is not listed, and the one after it is
// 'Phone number 2' on the panel, in the error link and in the summary alike.
function groupRows(group, value, position) {
  return keptInstances(group, value).flatMap((entry) => innerFields(group).map((field) => summaryRow(
    `${group.name}.${entry.n}.${field.name}`,
    `${groupItemLabel(group, entry.n)}${SUMMARY_SEP}${fieldLabel(field)}`,
    formatAnswer(field, entry.instance[field.name]),
    isEmptyValue(entry.instance[field.name]),
    position,
  )));
}

function plainRow(screen, field, value, position) {
  const label = field.label != null ? field.label
    : (screen.fields.length === 1 && screen.title != null ? screen.title : field.name);
  return summaryRow(field.name, label, formatAnswer(field, value), isEmptyValue(value), position);
}

function fieldRows(screen, section, answers) {
  const position = { sectionId: section.id, screenId: screen.id };
  return visibleFields(screen, answers).flatMap((field) => (field.type === 'group'
    ? groupRows(field, answers[field.name], position)
    : [plainRow(screen, field, answers[field.name], position)]));
}

function screenRows(screen, section, answers) {
  if (screen.repeat) return repeatRows(screen, section, answers);
  if (screen.summary && Array.isArray(screen.summary.items)) return itemRows(screen, section, answers);
  return fieldRows(screen, section, answers);
}

/**
 * Check-your-answers rows, grouped by section. `scope` limits to one section
 * (per-section summaries); null covers the whole flow. Rows carry a
 * changePosition; mapping it to a URL or hash is the navigator's concern.
 */
function summaryRows(config, answers = {}, scope = null) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg) return [];
  const { path } = computePath(cfg, answers);
  const onPath = new Set(path);
  return cfg.sections
    .filter((section) => !scope || section.id === scope)
    .map((section) => ({
      section: { id: section.id, title: section.title || '' },
      rows: section.screens
        .filter((id) => onPath.has(id))
        .flatMap((id) => screenRows(cfg.screenMap[id], section, answers)),
    }))
    .filter((group) => group.rows.length);
}

// ── Frames & slots ──
//
// A frame is any registered component tag or a DSL template exposing named
// slots as rg= regions. Resolution: the MOST SPECIFIC scope that defines a
// frame wins WHOLESALE — screen over section over flow. Within a scope the
// value may be a rules array [{when, frame}]; the first matching rule wins,
// and a scope whose rules all miss falls outward to the next scope.

function resolveFrameValue(value, answers) {
  if (value == null) return null;
  if (typeof value === 'string' || !Array.isArray(value)) {
    return typeof value === 'string' ? value : null;
  }
  // A DSL template is an array whose head is a tag string; a rules array is
  // an array of {frame} objects. Disambiguate on the first entry.
  const head = value[0];
  if (typeof head === 'string') return value;
  for (const rule of value) {
    if (rule && rule.frame != null && evalCondition(rule.when, answers)) {
      return rule.frame;
    }
  }
  return null;
}

function resolveFrame(config, position = {}, answers = {}) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg) return null;
  const screen = position.screenId ? cfg.screenMap[position.screenId] : null;
  const section = cfg.sections.find((s) => s.id === (position.sectionId
    || (screen ? cfg.sectionOf[screen.id] : null)));

  return resolveFrameValue(screen && screen.frame, answers)
    || resolveFrameValue(section && section.frame, answers)
    || resolveFrameValue(cfg.frame, answers)
    || null;
}

/**
 * Distribute a screen's children into the active frame's slots, light-DOM
 * style: a child carrying an `@slot=<name>` token goes to that named slot;
 * untargeted children keep appearance order in the default slot (flow-form).
 * The @slot token is stripped from the returned copies — it addresses the
 * frame, it is not an attribute of the child.
 */
function distributeSlots(children) {
  const named = {};
  const defaults = [];
  if (!Array.isArray(children)) return { named, defaults };

  for (const child of children) {
    if (!Array.isArray(child)) { defaults.push(child); continue; }
    let slot = null;
    const copy = child.filter((token) => {
      if (typeof token === 'string' && token.startsWith('@slot=')) {
        slot = token.slice('@slot='.length).trim();
        return false;
      }
      return true;
    });
    if (slot) (named[slot] = named[slot] || []).push(copy);
    else defaults.push(copy);
  }
  return { named, defaults };
}

// ── Addressing ──
//
// Positions are { sectionId, screenId } (screenId null on a section-summary
// or final-summary position). The navigator maps them to addresses; these
// helpers are the pure half of that mapping.

function positionToAddress(config, position) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg || !cfg.route) return null;
  if (!position) return { url: cfg.route, hash: null };
  if (position.kind === 'summary') return { url: `${cfg.route}/${cfg.summary.id}`, hash: null };

  const section = cfg.sections.find((s) => s.id === position.sectionId
    || s.screens.includes(position.screenId));
  if (section && section.present === 'accordion') {
    if (position.kind === 'sectionSummary') return { url: `${cfg.route}/${section.id}`, hash: SUMMARY_TARGET.slice(1) };
    return { url: `${cfg.route}/${section.id}`, hash: position.screenId || null };
  }
  if (position.kind === 'sectionSummary') {
    return { url: `${cfg.route}/${section ? section.id : position.sectionId}-${cfg.summary.id}`, hash: null };
  }
  if (position.repeatIndex) {
    return { url: `${cfg.route}/${position.screenId}/${position.repeatIndex}`, hash: null };
  }
  return { url: `${cfg.route}/${position.screenId}`, hash: null };
}

function addressToPosition(config, step, hash = null, n = null) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg || !step) return null;
  // A declared screen id always wins the segment: resolving the summary
  // grammar first shadowed a screen literally named 'check' (the default
  // summary id) or '<section>-check' — unreachable by URL, and the guard then
  // judged reachability for the wrong position kind. Lint reports the
  // collision (FLOW_ID_COLLISION); the runtime prefers the real screen.
  const declared = cfg.screenMap[step];
  if (declared && !declared.repeat) {
    return { kind: 'screen', sectionId: cfg.sectionOf[step] || null, screenId: step };
  }
  if (declared && declared.repeat) {
    return {
      kind: 'screen', sectionId: cfg.sectionOf[step] || null, screenId: step,
      repeatIndex: Math.max(1, Number(n) || 1),
    };
  }
  if (step === cfg.summary.id) return { kind: 'summary', sectionId: null, screenId: null };

  const section = cfg.sections.find((s) => s.id === step);
  if (section && section.present === 'accordion') {
    if (hash === SUMMARY_TARGET.slice(1)) return { kind: 'sectionSummary', sectionId: section.id, screenId: null };
    const screenId = hash && section.screens.includes(hash) ? hash : section.screens[0];
    return { kind: 'screen', sectionId: section.id, screenId };
  }
  const sectionSummaryOf = cfg.sections.find((s) => `${s.id}-${cfg.summary.id}` === step);
  if (sectionSummaryOf) return { kind: 'sectionSummary', sectionId: sectionSummaryOf.id, screenId: null };

  return null;
}

/**
 * The resume point as a navOrder ENTRY — instance-aware where firstUnanswered
 * (screen-granular, kept for compatibility) cannot be. Returns null when the
 * whole flow is complete.
 */
function firstIncomplete(config, answers = {}, completed = null) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg) return null;
  for (const entry of navOrder(cfg, answers)) {
    if (entry.kind !== 'screen') continue;
    const screen = cfg.screenMap[entry.screenId];
    if (entry.repeatIndex) {
      const instance = repeatInstances(screen, answers)[entry.repeatIndex - 1];
      if (!instance || !isInstanceComplete(screen, instance)) return entry;
      continue;
    }
    if (!isScreenComplete(screen, answers)) return entry;
    const hasRequired = visibleFields(screen, answers).some((f) => f.required);
    if (!hasRequired && completed && !completed.has(entry.screenId)) return entry;
  }
  return null;
}

/**
 * The full navigation order for the active path: screens, section-end
 * summaries where configured, then the final summary. Drives backUrl and
 * progress; the navigator walks this list in both directions.
 */
function navOrder(config, answers = {}) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg) return [];
  const { path, exit, reachedSummary } = computePath(cfg, answers);
  const onPath = new Set(path);
  const out = [];

  for (const section of cfg.sections) {
    const active = section.screens.filter((id) => onPath.has(id));
    for (const id of active) {
      const screen = cfg.screenMap[id];
      if (screen.repeat) {
        // One navigable position per ACTIVE instance.
        const count = activeRepeatCount(screen, answers);
        for (let n = 1; n <= count; n += 1) {
          out.push({ kind: 'screen', sectionId: section.id, screenId: id, repeatIndex: n });
        }
      } else {
        out.push({ kind: 'screen', sectionId: section.id, screenId: id });
      }
    }
    if (active.length && section.summary === 'end') {
      out.push({ kind: 'sectionSummary', sectionId: section.id, screenId: null });
    }
  }
  if (exit) out.push({ kind: 'exit', sectionId: null, screenId: exit });
  else if (cfg.summary.enabled && (reachedSummary || path.length)) {
    out.push({ kind: 'summary', sectionId: null, screenId: null });
  }
  return out;
}

// ── Position state ──
//
// The store keys a position stands for, computed pure so both runtimes write
// the same map: the engine's _setPosition and _writeChrome (core/services/
// flow.js) on the client, and the server for a flow URL's first paint
// (server/ssr/render.js seeds them, so the frame's `:dataSource=flow.<id>.*`
// bindings serialize with the same text hydration keeps).

const flowKey = (flowId, suffix) => `flow.${flowId}.${suffix}`;

/** The keys that NAME a position: position, screenId, kind. */
function positionKeys(config, position) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg || !position) return {};
  return {
    [flowKey(cfg.id, 'position')]: position,
    [flowKey(cfg.id, 'screenId')]: position.screenId || null,
    [flowKey(cfg.id, 'kind')]: position.kind,
  };
}

/**
 * The chrome of a position: title, subtitle, progress, the nav flags and
 * labels, and the resolved frame. Accordion sections: the page h1 is the
 * SECTION; each panel header carries its screen title (the composite renders
 * those). The Continue label turns into the send label on the summary.
 */
function chromeKeys(config, position, answers = {}) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg || !position) return {};
  const order = navOrder(cfg, answers);
  const index = order.findIndex((p) => samePosition(p, position));
  const screen = position.screenId ? cfg.screenMap[position.screenId] : null;
  const section = cfg.sections.find((s) => s.id === position.sectionId);

  let title = '';
  if (position.kind === 'summary') title = cfg.summary.title;
  else if (position.kind === 'sectionSummary') title = `${cfg.summary.title}${section ? `${SUMMARY_SEP}${section.title}` : ''}`;
  else if (screen && section && section.present === 'accordion') title = section.title || '';
  else if (screen && screen.repeat && position.repeatIndex) title = repeatTitle(screen, position.repeatIndex);
  else if (screen) title = screen.title || '';

  const isLastBeforeSubmit = position.kind === 'summary';
  const screens = order.filter((p) => p.kind === 'screen');
  const progressLabel = cfg.progress === 'count' && position.kind === 'screen'
    ? `Question ${screens.findIndex((p) => p.screenId === position.screenId) + 1} of ${screens.length}`
    : '';
  const key = (suffix) => flowKey(cfg.id, suffix);
  return {
    [key('title')]: title,
    [key('subtitle')]: (screen && screen.subtitle) || (section && section.subtitle) || '',
    [key('progressLabel')]: progressLabel,
    [key('nav.hasBack')]: cfg.buttons.back !== false && index > 0,
    [key('nav.canSave')]: Boolean(cfg.buttons.save) && cfg.persistence !== 'memory',
    [key('nav.continueLabel')]: isLastBeforeSubmit ? cfg.buttons.labels.send : cfg.buttons.labels.continue,
    [key('nav.backLabel')]: cfg.buttons.labels.back,
    [key('nav.saveLabel')]: cfg.buttons.labels.save,
    [key('nav.isExit')]: position.kind === 'exit',
    [key('frame')]: resolveFrame(cfg, position, answers),
  };
}

/**
 * Where a flow URL lands before the engine runs: the position the step and
 * instance address, or, for the bare resume route, the first navigable
 * position of a fresh visit. A declared exit screen answers as an exit
 * position (the kind the engine's next() sets and the composite renders).
 * The server paints this; the client's guards decide (a draft can move the
 * resume point, an unreached step redirects), so it is a first paint, never
 * a verdict.
 */
function requestPosition(config, step = null, n = null) {
  const cfg = normalizeFlowConfig(config);
  if (!cfg) return null;
  const answers = {};
  if (!step) return navOrder(cfg, answers)[0] || null;
  const position = addressToPosition(cfg, step, null, n);
  if (position && position.screenId && cfg.exitIds.includes(position.screenId)) {
    return { kind: 'exit', sectionId: null, screenId: position.screenId };
  }
  return position;
}

/**
 * Everything the server seeds for a flow URL: the normalized config, the
 * request position, and the store keys (positionKeys + chromeKeys, computed
 * over no answers) the frame binds. Null when the address names nothing.
 */
function flowRequest(config, step = null, n = null) {
  const cfg = normalizeFlowConfig(config);
  const position = requestPosition(cfg, step, n);
  if (!cfg || !position) return null;
  return {
    config: cfg,
    position,
    keys: { ...positionKeys(cfg, position), ...chromeKeys(cfg, position, {}) },
  };
}

// ── Routes ──

/**
 * Route entries for a routed flow — WITHOUT guard closures (the engine owns
 * those; keeping this pure lets the SSR router and lint build the same table).
 */
function buildFlowRoutes(config, component = 's-flw') {
  const cfg = normalizeFlowConfig(config);
  if (!cfg || cfg.routed === false || !cfg.route) return [];
  return [
    { path: cfg.route, component, meta: { flowId: cfg.id, flowKind: 'resume' } },
    { path: `${cfg.route}/:step`, component, meta: { flowId: cfg.id, flowKind: 'step' } },
    { path: `${cfg.route}/:step/:n`, component, meta: { flowId: cfg.id, flowKind: 'repeat' } },
  ];
}

// ── Validation (consumed by tools/swc-lint.mjs — one implementation) ──

const FINDING = (code, message, screen = null) => ({ code, message, screen });

function validateFlowConfig(rawConfig) {
  const findings = [];
  const cfg = normalizeFlowConfig(rawConfig);
  if (!cfg) return [FINDING('FLOW_INVALID', 'flow config is not an object')];

  // FLOW_IMPERATIVE — flows are JSON; a function anywhere is imperative escape.
  const walk = (value, path) => {
    if (typeof value === 'function') {
      findings.push(FINDING('FLOW_IMPERATIVE', `function value at ${path} — flow configs are pure JSON`));
      return;
    }
    if (Array.isArray(value)) value.forEach((v, i) => walk(v, `${path}[${i}]`));
    else if (value && typeof value === 'object') {
      for (const [k, v] of Object.entries(value)) {
        if (k === 'screenMap' || k === 'sectionOf') continue; // derived lookups
        walk(v, `${path}.${k}`);
      }
    }
  };
  walk(rawConfig, cfg.id || 'flow');

  const orderIndex = new Map(cfg.order.map((id, i) => [id, i]));
  const fieldOwner = new Map();
  for (const id of cfg.order) {
    for (const f of cfg.screenMap[id].fields) {
      if (f && f.name != null && !fieldOwner.has(f.name)) fieldOwner.set(f.name, id);
    }
  }

  const condFields = (cond, acc = []) => {
    if (!cond || typeof cond !== 'object') return acc;
    if (typeof cond.field === 'string') acc.push(cond.field);
    for (const sub of [...(cond.all || []), ...(cond.any || []), cond.not]) {
      if (sub) condFields(sub, acc);
    }
    return acc;
  };

  // FLOW_ID_COLLISION — the address grammar reserves the summary id,
  // '<section>-<summaryId>' and the '@' separator (changeCmd's instance form).
  const sectionSummaryIds = new Set(cfg.sections.map((sec) => `${sec.id}-${cfg.summary.id}`));
  for (const id of Object.keys(cfg.screenMap)) {
    if (id === cfg.summary.id || sectionSummaryIds.has(id)) {
      findings.push(FINDING('FLOW_ID_COLLISION',
        `screen '${id}' collides with the summary address grammar — rename the screen or set summary.id`, id));
    }
    if (id.includes('@')) {
      findings.push(FINDING('FLOW_ID_COLLISION',
        `screen '${id}' contains '@', reserved as the repeat-instance separator`, id));
    }
  }

  // FLOW_ID_COLLISION also covers FIELD names: '@' is the instance separator
  // of the error grammar ('<group>@<n>.<field>'), top-level or inner.
  const namesOf = (fields) => (Array.isArray(fields) ? fields : [])
    .filter((f) => f && f.name != null)
    .flatMap((f) => [String(f.name), ...(f.type === 'group' ? namesOf(f.fields) : [])]);
  cfg.order
    .flatMap((id) => namesOf(cfg.screenMap[id].fields).map((name) => ({ id, name })))
    .filter((entry) => entry.name.includes('@'))
    .forEach((entry) => findings.push(FINDING('FLOW_ID_COLLISION',
      `field '${entry.name}' on '${entry.id}' contains '@', reserved as the instance separator`, entry.id)));

  // FLOW_GROUP_* (a group is one level deep; every inner field is named and
  // labelled, has opts when its type needs them, contains no '.', and
  // carries no reveal; a group never sits on a paged-repeat screen, where
  // the `_more` radio and the group's own add/remove would both count
  // instances).
  const innerProblem = (inner) => {
    if (!inner || typeof inner !== 'object') return 'is not an object';
    if (inner.name == null || inner.label == null) return 'needs a name and a label';
    if (String(inner.name).includes('.')) return "has a name containing '.'";
    if ((inner.type === 'radio' || inner.type === 'checkboxGroup')
      && !(Array.isArray(inner.opts) && inner.opts.length)) return 'needs non-empty opts';
    if (inner.revealWhen != null) return 'carries revealWhen (no reveals inside a group)';
    return null;
  };
  const groupFindings = (screenId, screen, group) => {
    const inner = Array.isArray(group.fields) ? group.fields : [];
    const where = `group '${group.name}' on '${screenId}'`;
    return [
      ...(String(group.name).includes('.')
        ? [FINDING('FLOW_GROUP_FIELD', `${where} has a name containing '.'`, screenId)] : []),
      ...(screen.repeat
        ? [FINDING('FLOW_GROUP_IN_REPEAT', `${where} sits on a repeat screen (a paged repeat and an inline group cannot share one)`, screenId)] : []),
      ...inner.filter((f) => f && f.type === 'group')
        .map((f) => FINDING('FLOW_GROUP_NESTED', `${where} nests group '${f.name}' (groups are one level deep)`, screenId)),
      ...inner.filter((f) => !(f && f.type === 'group'))
        .map((f) => ({ f, problem: innerProblem(f) }))
        .filter((entry) => entry.problem)
        .map((entry) => FINDING('FLOW_GROUP_FIELD', `inner field '${entry.f && entry.f.name}' of ${where} ${entry.problem}`, screenId)),
    ];
  };
  cfg.order.forEach((id) => {
    const screen = cfg.screenMap[id];
    screen.fields.filter((f) => f && f.type === 'group')
      .forEach((group) => findings.push(...groupFindings(id, screen, group)));
  });

  // FLOW_REPEAT_IN_ACCORDION (positionToAddress answers the accordion branch
  // before the repeatIndex branch, so every instance of a paged repeat inside
  // an accordion section collapses onto one hash and the guard loops; a
  // group field on the panel is the supported shape).
  cfg.sections
    .filter((section) => section.present === 'accordion')
    .flatMap((section) => section.screens
      .filter((id) => cfg.screenMap[id].repeat)
      .map((id) => ({ sectionId: section.id, id })))
    .forEach((entry) => findings.push(FINDING('FLOW_REPEAT_IN_ACCORDION',
      `repeat screen '${entry.id}' sits in accordion section '${entry.sectionId}' (present the section as screens, or use a group field on a panel)`, entry.id)));

  // FLOW_CONDITION_NO_VALUE — a comparison with nothing to compare against.
  // It never fires at runtime (see evalCondition); silently, without this.
  const checkValue = (cond, where, screenId) => {
    if (!cond || typeof cond !== 'object') return;
    for (const sub of [...(cond.all || []), ...(cond.any || []), cond.not]) {
      if (sub) checkValue(sub, where, screenId);
    }
    if (typeof cond.field !== 'string') return;
    const operator = OPERATOR_ALIASES[cond.operator] || cond.operator || 'equals';
    if (operator === 'answered' || operator === 'notAnswered') return;
    if (cond.value === undefined) {
      findings.push(FINDING('FLOW_CONDITION_NO_VALUE',
        `${where} on '${screenId}' compares '${cond.field}' with no value — use operator 'answered' to test presence`, screenId));
    }
  };
  for (const id of cfg.order) {
    for (const rule of cfg.screenMap[id].next) checkValue(rule && rule.when, 'next rule', id);
    for (const field of cfg.screenMap[id].fields) checkValue(field && field.revealWhen, `reveal for '${field && field.name}'`, id);
  }

  const reachable = new Set(cfg.order.length ? [cfg.order[0]] : []);
  for (const id of cfg.order) {
    const screen = cfg.screenMap[id];
    const conditional = screen.next.filter((r) => r && r.when);
    const unconditional = screen.next.filter((r) => r && !r.when);

    // FLOW_NO_DEFAULT_ROUTE — conditions with no unconditional tail.
    if (conditional.length && !unconditional.length) {
      findings.push(FINDING('FLOW_NO_DEFAULT_ROUTE',
        `screen '${id}' has conditional next rules but no unconditional tail`, id));
    }

    for (const rule of screen.next) {
      if (!rule) continue;
      const target = rule.exit || rule.goto;
      // FLOW_DANGLING_GOTO
      if (target && target !== SUMMARY_TARGET && !cfg.screenMap[target]) {
        findings.push(FINDING('FLOW_DANGLING_GOTO',
          `screen '${id}' targets unknown screen '${target}'`, id));
      }
      if (rule.goto && cfg.screenMap[rule.goto]) reachable.add(rule.goto);
      // FLOW_FORWARD_CONDITION — a rule reading a field from a LATER screen.
      for (const fieldName of condFields(rule.when)) {
        const owner = fieldOwner.get(fieldName);
        if (owner != null && (orderIndex.get(owner) ?? 0) > (orderIndex.get(id) ?? 0)) {
          findings.push(FINDING('FLOW_FORWARD_CONDITION',
            `screen '${id}' branches on '${fieldName}' which is asked later (on '${owner}')`, id));
        }
      }
    }

    for (const field of screen.fields) {
      if (!field || !field.revealWhen) continue;
      for (const fieldName of condFields(field.revealWhen)) {
        const owner = fieldOwner.get(fieldName);
        if (owner != null && (orderIndex.get(owner) ?? 0) > (orderIndex.get(id) ?? 0)) {
          findings.push(FINDING('FLOW_FORWARD_CONDITION',
            `reveal for '${field.name}' on '${id}' depends on '${fieldName}' asked later`, id));
        }
        // FLOW_DEEP_REVEAL — a reveal whose controller is itself revealed.
        const controller = screen.fields.find((f) => f && f.name === fieldName);
        if (controller && controller.revealWhen) {
          findings.push(FINDING('FLOW_DEEP_REVEAL',
            `reveal for '${field.name}' on '${id}' chains off revealed field '${fieldName}' — GOV.UK allows simple follow-ups only`, id));
        }
      }
    }
  }

  // FLOW_UNREACHABLE_SCREEN — fall-through order feeds reachability too.
  for (let i = 0; i + 1 < cfg.order.length; i++) {
    if (reachable.has(cfg.order[i])) {
      const screen = cfg.screenMap[cfg.order[i]];
      const alwaysDiverts = screen.next.some((r) => r && !r.when && (r.goto || r.exit));
      if (!alwaysDiverts) reachable.add(cfg.order[i + 1]);
    }
  }
  for (const id of cfg.order) {
    if (!reachable.has(id)) {
      findings.push(FINDING('FLOW_UNREACHABLE_SCREEN', `screen '${id}' is unreachable`, id));
    }
  }

  return findings;
}
return { evalCondition, normalizeFlowConfig, computePath, repeatKey, repeatInstances, activeRepeatCount, samePosition, repeatTitle, groupItemLabel, visibleFields, fieldsToRules, isInstanceComplete, screenErrors, isScreenComplete, projectOutput, firstUnanswered, formatAnswer, summaryRows, resolveFrame, distributeSlots, positionToAddress, addressToPosition, firstIncomplete, navOrder, positionKeys, chromeKeys, requestPosition, flowRequest, buildFlowRoutes, validateFlowConfig };
})();

const __m72 = (() => {
const asFormField = __m66.asFormField;
const fieldDataSetter = __m66.fieldDataSetter;
const dispatchValueChanged = __m66.dispatchValueChanged;
const announce = __m63.announce;
const A11Y_DEFAULT_STRINGS = __m70.A11Y_DEFAULT_STRINGS;
const groupItemLabel = __m71.groupItemLabel;
const fieldChild = __m67.fieldChild;
const sameAnswer = __m67.sameAnswer;
// s-rfg / swc-repeat-group: a repeatable group FIELD (array in, array out).
//
// The value is an array of instance objects, one per rendered instance. Each
// instance is a role=group region named by its heading ('Phone number 2'),
// holding the group's inner fields (one level deep, rendered through the
// same fieldChild helper s-f uses) and a Remove button; Add another sits
// below the instances. The group's own bounds message lands in the trailing
// s-fe the host's aria-describedby names; inner messages land on the inner
// fields' own s-fe. Usable in any s-f, and what the flow composite renders
// for a screen field of type 'group' (the engine bounds a group over its
// PROJECTED instances through screenErrors; a standalone s-f bounds the raw
// array through its schema).
//
// The field protocol, seen from the form or the composite above:
//   · inner fields are named '<group>@<n>.<field>', n 1-based over the
//     rendered instances (the engine's error grammar); their fieldConnected
//     and fieldDisconnected bubble unchanged, so s-f._fields and
//     s-flw._flwFields learn the compound names for error application and
//     focus;
//   · an inner valueChanged is STOPPED here, the array is mutated IN PLACE
//     (the composite's change re-sync compares by identity) and ONE
//     valueChanged { name: '<group>', value } is re-emitted; a `change`
//     (commit) squares every inner field up one microtask later, because a
//     text field's valueChanged is one keystroke stale (its value getter
//     reads a batched internal store; the composite waits for the same
//     settle), and re-emits once when anything moved;
//   · add and remove REPLACE the array with a new one, dispatch valueChanged
//     with it (the composite writes it into answers, so the engine never
//     keeps a removed instance), then groupItemsChanged { name } (the
//     composite forwards it to flowEngine.clearErrors: compound names may
//     have been renumbered).
// State is structure, never a style write: a button is absent at a bound.
// Each button carries its own ~cl:call token (the Remove one with its
// instance number), so no handler reads the DOM to learn what was pressed.
//
// The strings: the Add and Remove labels and the two announcements come, in
// order, from the instance's own props (addLabel, removeLabel, addedMessage,
// removedMessage), the group's `repeat.addAnother` (Add only), the app's
// t.a11y.* keys of the moment (addAnother, remove, itemAdded, itemRemoved,
// the channel every other component's names travel through, so a locale
// pack renames them without s-rfg knowing), then the framework defaults.
// The two labels are bound to internal keys, so a pack landing mid-edit
// relabels the live buttons in place.






// Instance heading ids are minted here so a region's aria-labelledby can name
// its own heading in the same DSL (no subtree query); unique per element.
let rfgSeq = 0;

// Each string: the prop that overrides it and the app key it falls back to
// (whose framework default is in A11Y_DEFAULT_STRINGS).
const STRINGS = {
  add: { prop: 'addLabel', key: 't.a11y.addAnother' },
  remove: { prop: 'removeLabel', key: 't.a11y.remove' },
  added: { prop: 'addedMessage', key: 't.a11y.itemAdded' },
  removed: { prop: 'removedMessage', key: 't.a11y.itemRemoved' },
};

// '<group>@<n>.<field>': the instance separator changeCmd already parses.
function innerName(group, n, field) {
  return `${group}@${n}.${field}`;
}

function parseInnerName(name) {
  const m = String(name).match(/^[^@]+@(\d+)\.(.+)$/);
  return m ? { n: Number(m[1]), field: m[2] } : null;
}

// The commit re-sync judges "moved" with sameAnswer (field-types.js): an
// unanswered control and a missing key are the same answer, an array (a
// checkbox group) compares by its contents (F-742).

const def = {
  tags: ['swc-repeat-group', 's-rfg'],
  dispatches: ['groupItemsChanged'],
  props: ['group', 'addLabel', 'removeLabel', 'addedMessage', 'removedMessage'],
  internalStore: true,
  listeners: {
    valueChanged: '_onRfgValueChanged',
    change: '_onRfgChange',
    fieldConnected: '_onRfgFieldConnected',
    fieldDisconnected: '_onRfgFieldDisconnected',
  },
  // The two live labels follow the app's strings: a locale pack landing
  // after the instances rendered relabels the buttons in place.
  subscriptions: { 't.a11y.addAnother': '_syncRfgStrings', 't.a11y.remove': '_syncRfgStrings' },
  aria: { refs: { describedby: 's-fe[rg=rfg-error]' } },
  children: [
    ['s-l', '@rg=rfg-label', ':dk=label'],
    ['s-cn', '@rg=rfg-items', ':dk=rfgItems', ':df=children'],
    ['s-fe', '@rg=rfg-error', ':dk=error'],
  ],
  methods: {
    // Returned BY REFERENCE and mutated in place on an inner edit; replaced
    // only by add, remove and the setter.
    get value() {
      if (!Array.isArray(this._value)) this._value = [];
      return this._value;
    },
    set value(v) {
      // '' arrives for null (setFieldDataEntry) and on a form clear.
      this._value = Array.isArray(v) ? v : [];
      this._renderItems();
    },
    set data(obj) {
      if (!obj || typeof obj !== 'object') return;
      // The composite publishes errors as `field.data = { ...field.data, error }`
      // and the data proxy spreads EVERY internal key, the instance DSL
      // included: re-setting rfgItems would rebuild the instances under the
      // user and under the inline errors just applied. Only _renderItems
      // writes that key.
      const entries = Object.entries(obj).filter(([key]) => key !== 'rfgItems');
      fieldDataSetter(this, Object.fromEntries(entries));
    },
    get data() {
      if (this._internalData) return this._dataProxy;
      return { name: this._name, value: this.value };
    },
    onGroupChange() { this._syncRfgStrings(); this._renderItems(); },
    onAddLabelChange() { this._syncRfgStrings(); },
    onRemoveLabelChange() { this._syncRfgStrings(); },

    // One string by precedence: the instance's prop, the app's key of the
    // moment, the framework default.
    _rfgString(which) {
      const { prop, key } = STRINGS[which];
      if (this[prop]) return String(this[prop]);
      const fromApp = this.store && this.store.getValue ? this.store.getValue(key) : undefined;
      return fromApp || A11Y_DEFAULT_STRINGS[key];
    },
    // The Add label alone has a third source: the group's own repeat block.
    _rfgAddLabel() {
      const repeat = this.group && this.group.repeat;
      return (repeat && repeat.addAnother) || this._rfgString('add');
    },
    // An announcement template with the instance heading in place.
    _rfgMessage(which, label) {
      return this._rfgString(which).replace('{label}', label);
    },
    _syncRfgStrings() {
      this.setData('rfgAddLabel', this._rfgAddLabel());
      this.setData('rfgRemoveLabel', this._rfgString('remove'));
    },

    // The rendered count: at least minItems (an unanswered group still shows
    // its first instance), else one per value entry.
    _rfgCount() {
      const min = (this.group && this.group.minItems) || 0;
      return Math.max(min, this.value.length);
    },

    // The rendered instances as a NEW array: the value's own objects, padded
    // with blank ones up to the rendered count.
    _rfgInstances(count) {
      const value = this.value;
      return Array.from({ length: count }, (_, i) => value[i] || {});
    },

    _renderItems() {
      if (!this._isConnected) return;
      const group = this.group;
      this._announceDepartures();
      this.refs = {};
      if (!group || typeof group !== 'object') {
        this.setData('rfgItems', []);
        return;
      }
      const count = this._rfgCount();
      const min = group.minItems || 0;
      const atMax = group.maxItems != null && count >= group.maxItems;
      const uid = this._rfgUid || (this._rfgUid = ++rfgSeq);
      const items = this._rfgInstances(count)
        .map((instance, i) => this._rfgItem(group, instance, i + 1, count > min, uid));
      // The label is a span child bound to the internal key: a button's own
      // data sink is `data`, a span's is its text.
      const add = atMax ? [] : [['button', '@type=button', '@rg=rfg-add', ':ref=rfgAdd', '~cl:call:_rfgAdd', [['span', '$rfgAddLabel']]]];
      this.setData('rfgItems', [...items, ...add]);
    },

    // One instance: a labelled group region (its heading's id minted here, so
    // aria-labelledby resolves without a query), the inner fields named by
    // position, and Remove unless the count sits at minItems. The region
    // takes programmatic focus (tabindex=-1): add and remove land focus on
    // it, and so does the composite's error link for the group's own bounds
    // message, since its target rule takes the first [tabindex] descendant.
    _rfgItem(group, instance, n, removable, uid) {
      const id = `rfg-${uid}-${n}`;
      const fields = (Array.isArray(group.fields) ? group.fields : []).filter((f) => f && f.name != null);
      const inner = fields
        .map((f) => fieldChild(innerName(this._name, n, f.name), { ...f, value: instance[f.name] }))
        .filter(Boolean);
      // The instance number rides in the button's own call token.
      const remove = removable
        ? [['button', '@type=button', '@rg=rfg-remove', `~cl:call:_rfgRemove:${n}`, [['span', '$rfgRemoveLabel']]]] : [];
      return ['s-cn', '@rg=rfg-item', '@role=group', `@aria-labelledby=${id}`, '@tabindex=-1', `:ref=rfgItem${n}`, [
        ['s-l', '@rg=rfg-item-title', `@id=${id}`, `=${groupItemLabel(group, n)}`],
        ...inner,
        ...remove,
      ]];
    },

    // A field the re-render detaches dispatches its own fieldDisconnected
    // from OUTSIDE the tree (disconnectedCallback runs after removal), so no
    // ancestor hears it and s-f._fields and s-flw._flwFields would keep the
    // dropped instance's names. The host announces the departures itself,
    // while still attached, before the instances are replaced.
    _announceDepartures() {
      Object.entries(this._rfgFields || {})
        .filter(([, field]) => field.isConnected)
        .forEach(([name, field]) => this.dispatchEvent(new CustomEvent('fieldDisconnected', {
          bubbles: true, detail: { name, field },
        })));
      this._rfgFields = {};
    },

    // The inner fields by compound name, for the departures above and the
    // commit re-sync below (the object arrives on the protocol event; nothing
    // is queried). The host's own fieldConnected passes through.
    _onRfgFieldConnected(e) {
      if (e.target === this || !e.detail || !e.detail.name) return;
      this._rfgFields = this._rfgFields || {};
      this._rfgFields[e.detail.name] = e.detail.field;
    },
    _onRfgFieldDisconnected(e) {
      const name = e.detail && e.detail.name;
      if (this._rfgFields && name && this._rfgFields[name] === e.detail.field) delete this._rfgFields[name];
    },

    // One inner answer into its instance object, IN PLACE (a padded instance
    // gets its object on first write).
    _rfgWrite(parsed, answer) {
      const value = this.value;
      while (value.length < parsed.n) value.push({});
      value[parsed.n - 1][parsed.field] = answer;
    },

    // An inner edit, live: keep the instance current and re-emit once with
    // the group's name; the compound name never leaves this element. The
    // group's own valueChanged (target === this) passes.
    _onRfgValueChanged(e) {
      if (e.target === this) return;
      e.stopPropagation();
      const parsed = e.detail && parseInnerName(e.detail.name);
      if (!parsed) return;
      this._rfgWrite(parsed, e.detail.value);
      dispatchValueChanged(this, this.value);
    },

    // A commit (blur, or the click that forces one): one microtask later the
    // inner getters carry the committed, coerced value. Every inner field
    // that moved is written, then ONE valueChanged.
    _onRfgChange() {
      queueMicrotask(() => {
        const value = this.value;
        const moved = Object.entries(this._rfgFields || {})
          .map(([name, field]) => ({ parsed: parseInnerName(name), field }))
          .filter(({ parsed, field }) => parsed && field.isConnected && field.value !== undefined
            && !sameAnswer(field.value, (value[parsed.n - 1] || {})[parsed.field]));
        moved.forEach(({ parsed, field }) => this._rfgWrite(parsed, field.value));
        if (moved.length) dispatchValueChanged(this, value);
      });
    },

    // The Add button's call token carries no value, so the event arrives
    // here and is not needed.
    _rfgAdd() {
      const group = this.group || {};
      const count = this._rfgCount();
      if (group.maxItems != null && count >= group.maxItems) return;
      this._rfgReplace([...this._rfgInstances(count), {}], count + 1,
        this._rfgMessage('added', groupItemLabel(group, count + 1)));
    },

    // `n` is the instance number the Remove button's token minted.
    _rfgRemove(which) {
      const n = Number(which);
      const group = this.group || {};
      const count = this._rfgCount();
      if (!(n >= 1 && n <= count) || count <= (group.minItems || 0)) return;
      const next = this._rfgInstances(count).filter((_, i) => i !== n - 1);
      this._rfgReplace(next, Math.min(n, next.length), this._rfgMessage('removed', groupItemLabel(group, n)));
    },

    // The one path for a structural change: a NEW array, the re-render, the
    // two events in the order the composite relies on (the answer write
    // first, the error clear second), the announcement, then focus on the
    // instance now at that position (or Add another when none is left).
    _rfgReplace(next, focusN, message) {
      this._value = next;
      this._renderItems();
      dispatchValueChanged(this, next);
      this.dispatchEvent(new CustomEvent('groupItemsChanged', { bubbles: true, detail: { name: this._name } }));
      announce(message);
      const target = this.refs[`rfgItem${focusN}`] || this.refs.rfgAdd;
      if (target && typeof target.focus === 'function') target.focus();
    },
  },
  callbacks: {
    connected() { this._syncRfgStrings(); this._renderItems(); },
  },
};

// The def declares its own value and data accessors, so the mixin installs
// no accessor and no change listener: tag registration (s-f discovery),
// fieldConnected after connected() and fieldDisconnected before
// disconnected() are what it adds.
const __default = asFormField(def, { changeEvents: [] });
return { __default };
})();

const __m73 = (() => {
const ensureAriaId = __m17.ensureAriaId;
const fieldConnected = __m66.fieldConnected;
// s-fld — the ONE generic field shell. Wraps ANY form-capable control
// (anything carrying the field mixin: s-sel, s-dp, s-sld, s-trsel, s-csc,
// s-cb-g, a raw s-i, …) with the label / control / error stack the 12
// generated *-field tags used to clone per input type:
//
//   ['s-fld', '@name=email', '@label=Email', [
//     ['s-sel', ':dataKey=options'],
//   ]]
//
// The control itself is the form field (its mixin emits fieldConnected, the
// form registers IT); s-fld is presentation and wiring only — it names the
// control, associates the label (htmlFor for native controls,
// aria-labelledby otherwise), points aria-describedby at the persistent
// error element, and routes the control's error state into that element.
// The s-fe PERSISTS (s-fe:empty hides it) so its auto-id is stable and
// aria-describedby always resolves.

const CONTROL_SELECTOR = 'input, textarea, select, [role], [name]';

// Addons live in two forms, because a corpus scan of 1,103 registry components
// found 79 input-group addons split 17.7% plain text / 78.5% real content
// (icons 39%, buttons and menus 38%):
//
//   @prefix / @suffix   static text — rendered as ::before / ::after on the
//                       row. No element, no node in the DOM.
//   [slot=prefix|suffix] anything else — an icon, a button, a menu. Stays a
//                       real element because it takes events and state.
//
// Both put the control and its addons in ONE bordered row, with the control
// itself borderless — the shape shadcn draws with
// `has-[>[data-align=inline-start]]:[&>input]:pl-2`.
const ADDON_SLOTS = ':scope > [slot=prefix], :scope > [slot=suffix]';

const __default = {
    tags: ['s-fld', 's-fld'],
    props: ['@name', '@label', '@prefix', '@suffix'],
    css: `s-fld, swc-field { display: flex; flex-direction: column; }
s-fld > s-fe:empty, swc-field > s-fe:empty { display: none; }
`,
    callbacks: {
        connected() {
            requestAnimationFrame(() => {
                const label = this.querySelector(':scope > label, :scope > s-l, :scope > [is="s-l"]')
                    || this._stampLabel();
                const error = this.querySelector(':scope > s-fe') || this._stampError();
                const control = [...this.children].find((el) =>
                    el !== label && el !== error
                    && !el.matches('[slot=prefix], [slot=suffix]') && el.tagName !== 'S-FLD-ROW'
                    && (el.matches(CONTROL_SELECTOR) || el.querySelector('input, textarea, select')));
                if (!control) return;

                // Addons present → put the control and them in one bordered row.
                this._stampRow(control, error);

                // The control is the field: hand it this shell's name so its
                // fieldConnected/valueChanged speak for the right key. Its own
                // name (attribute or :data) wins — the shell only fills a gap.
                const name = this.getAttribute('name');
                if (name && !control._name && !control.getAttribute('name')) {
                    control._name = name;
                    // The control's own fieldConnected fired before this shell
                    // could name it, so the form ignored the nameless event —
                    // re-announce now that the registration can succeed.
                    fieldConnected(control);
                }

                // Label association: native controls (or customized built-ins
                // wrapping one) take htmlFor; custom elements take
                // aria-labelledby, which every role the mixin targets honors.
                const native = control.matches('input, textarea, select')
                    ? control : control.querySelector('input, textarea, select');
                if (label) {
                    if (native) label.htmlFor = ensureAriaId(native);
                    else control.setAttribute('aria-labelledby', ensureAriaId(label));
                }
                (native || control).setAttribute('aria-describedby', ensureAriaId(error));

                // Error routing: the form writes field.data.error on the
                // CONTROL; the mixin reflects it and announces — the shell
                // renders it in the persistent alert element.
                this.addEventListener('fieldErrorChanged', (e) => {
                    error.textContent = e.detail.error || '';
                });
            });
        },
    },
    methods: {
        _stampLabel() {
            const text = this.getAttribute('label');
            if (!text) return null;
            const label = document.createElement('label', { is: 's-l' });
            label.textContent = text;
            this.prepend(label);
            return label;
        },
        _stampError() {
            const error = document.createElement('s-fe');
            this.append(error);
            return error;
        },
        /**
         * Wrap prefix / control / suffix in one bordered row.
         *
         * Only runs when there is something to add — a plain field keeps the
         * markup it had, so nothing regresses for the 12 existing *-field tags
         * or for any control already relying on being a direct child.
         * Idempotent: re-running finds the existing row and leaves it alone.
         */
        _stampRow(control, error) {
            if (this.querySelector(':scope > s-fld-row')) return null;
            const prefix = this.getAttribute('prefix');
            const suffix = this.getAttribute('suffix');
            const slotted = [...this.querySelectorAll(ADDON_SLOTS)];
            if (!prefix && !suffix && !slotted.length) return null;

            const row = document.createElement('s-fld-row');
            if (prefix) row.setAttribute('prefix', prefix);
            if (suffix) row.setAttribute('suffix', suffix);

            // Order is prefix → control → suffix regardless of authoring order,
            // so markup does not have to encode visual sequence.
            control.replaceWith(row);
            slotted.filter((el) => el.getAttribute('slot') === 'prefix').forEach((el) => row.append(el));
            row.append(control);
            slotted.filter((el) => el.getAttribute('slot') === 'suffix').forEach((el) => row.append(el));
            if (error) this.append(error);
            return row;
        },
    },
};
return { __default };
})();

const __m74 = (() => {
// s-fld-row — the addon row inside s-fld.
//
// Stamped by s-fld (never authored) when a field carries @prefix/@suffix text
// or [slot=prefix|suffix] children. It exists so the border can sit on ONE box
// containing the control and its addons: the row is bordered, the control
// inside gives up its own border, and a prefix therefore renders INSIDE the
// field rather than beside it.
//
// Why a tag and not a class: markup carries no classes (rule 5), and the row
// needs to be selectable both by itself and in :has() relationships for focus
// and error state.
//
// No behaviour and no props — s-fld owns the composition, this def owns the
// look. Text addons are attributes rendered through ::before/::after, so the
// 17.7% of registry addons that are plain text ($ € USD https:// @gmail.com)
// cost no DOM node at all; everything else is a slotted element, because an
// icon, button or menu cannot be pseudo-element content.
const __default = {
    tags: ['s-fld-row', 's-fld-row'],
    props: ['@prefix', '@suffix'],
    css: `s-fld > s-fld-row, swc-field > swc-field-row {
  display: flex;
  align-items: center;
  gap: calc(var(--spacing, 0.25rem) * 2);
  width: 100%;
  min-width: 0;
  border: 1px solid var(--sc-input, var(--c-border));
  border-radius: calc(var(--sc-radius, 0.5rem) - 2px);
  background: var(--sc-background, var(--c-bg));
  padding-inline: calc(var(--spacing, 0.25rem) * 3);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
s-fld > s-fld-row > :is(input[is=s-i], textarea[is=s-ta], input, textarea) {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  background: none;
  box-shadow: none;
  padding-inline: 0;
  outline: none;
}
/* Focus ring moves to the row — :has() is why the control can stay borderless
   without losing the affordance. */
s-fld > s-fld-row:has(:focus-visible) {
  border-color: var(--sc-ring, var(--c-primary));
  box-shadow: 0 0 0 3px color-mix(in oklab, var(--sc-ring, var(--c-primary)) 20%, transparent);
}
/* Error state: the control's mixin reflects @error to aria-invalid, so the row
   reads the attribute the framework already maintains — no second flag. */
s-fld > s-fld-row:has([aria-invalid=true]) {
  border-color: var(--sc-destructive, var(--c-danger));
  box-shadow: 0 0 0 3px color-mix(in oklab, var(--sc-destructive, var(--c-danger)) 20%, transparent);
}
/* A multi-line control makes the row tall; addons pin to the first line rather
   than floating at its vertical centre. */
s-fld > s-fld-row:has(textarea) {
  align-items: flex-start;
  padding-block: calc(var(--spacing, 0.25rem) * 2);
}
/* The ring is the ROW's. form.css gives every s-i an outline and a border
   colour on :focus; inside the row that drew a second ring within the first.
   The list carries the [error] forms too, so this outranks the error focus
   rule the same way. */
s-fld > s-fld-row > :is(input[is=s-i][error], input[is=s-i], textarea[is=s-ta][error], textarea[is=s-ta], input, textarea):is(:focus, :focus-visible) {
  outline: none;
  box-shadow: none;
  border: 0;
}
/* Text addons: content only, no element. */
s-fld > s-fld-row[prefix]::before { content: attr(prefix); }
s-fld > s-fld-row[suffix]::after  { content: attr(suffix); }
s-fld > s-fld-row[prefix]::before,
s-fld > s-fld-row[suffix]::after,
s-fld > s-fld-row > [slot=prefix],
s-fld > s-fld-row > [slot=suffix] {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  color: var(--sc-muted-foreground, var(--c-text-secondary));
  white-space: nowrap;
}
/* block-start / block-end addons stack instead — 7.6% of corpus usage. */
s-fld > s-fld-row[psn=block] { flex-direction: column; align-items: stretch; }`,
};
return { __default };
})();

const __m75 = (() => {
const asFormField = __m66.asFormField;
const dispatchValueChanged = __m66.dispatchValueChanged;
// THE checkbox. Standalone it toggles itself and, given a name, participates
// in <form is="s-f"> directly (value = its checked state) — no wrapper tag
// required. Inside a selectable group the group owns the state and the
// bubbled 'selected' event drives the selection mixin instead.
const __default = asFormField({
    tags: ['s-cb', 's-cb'],
    props: ['@selected', '@disabled'],
    aria: { role: 'checkbox', checked: 'selected', disabled: 'disabled' },
    emits: { click: 'selected' },
    callbacks: {
        connected() {
            // Same standalone contract as s-tgb: only a DIRECT selectable
            // parent (s-cb-g) owns the state; distant selectable ancestors
            // are not this checkbox's group, and the self-managed 'selected'
            // emission stops before they can adopt it.
            const managed = () => Boolean(this.parentElement && this.parentElement._isSelectable);
            this.addEventListener('click', () => {
                if (this.disabled) return;
                if (managed()) return;
                this.toggleSelected();
                dispatchValueChanged(this, this.value);
            });
            this.addEventListener('selected', (e) => {
                if (e.target === this && !managed()) e.stopPropagation();
            });
        },
    },
}, {
    getValue() { return this.hasAttribute('selected'); },
    // Dual contract: a BOOLEAN is the form checked-state; anything else is the
    // option IDENTITY a stamping list assigns (item.value = 'sms') — which
    // must never tick the box.
    setValue(v) {
        if (typeof v !== 'boolean') { this._optionValue = v; return; }
        if (v) this.setAttribute('selected', '');
        else this.removeAttribute('selected');
    },
});
return { __default };
})();

const __m76 = (() => {
const asFormField = __m66.asFormField;
// A named group of checkboxes; each s-cb carries its own checkbox role. With
// a name it is a form field whose value is the array of selected item values.
const __default = asFormField({
    tags: ['s-cb-g', 's-cb-g'],
    aria: { role: 'group', itemRole: 'checkbox' },
    itemRenderer: 's-cb',
    selectable: true,
}, {
    getValue() {
        return (this.selectedItems || []).map((i) => i._data?.value ?? i._optionValue ?? i.getAttribute('value'));
    },
    setValue(v) {
        const wanted = new Set(Array.isArray(v) ? v : [v]);
        [...this.children].forEach((item) => {
            const val = item._data?.value ?? item.getAttribute('value');
            if (wanted.has(val)) item.setAttribute('selected', '');
            else item.removeAttribute('selected');
        });
    },
    changeEvents: ['selected'],
});
return { __default };
})();

const __m77 = (() => {
const __default = { tags: ['s-cb-w', 's-cb-w'] };
return { __default };
})();

const __m78 = (() => {
const __default = {
    tags: ['s-ch-i', 's-ch-i'],
    dispatches: ['change'],
    props: ['@name', '@placeholder'],
    internalStore: true,
    children: [
        ['s-cn.chi-wrap', [
            ['s-cn.chi-chips', ':ref=chipList', ':dataKey=chipElements', ':dataField=children'],
            ['input', ':ref=input', '@type=text'],
        ]],
    ],
    callbacks: {
        connected() {
            // A bound :value= reaches the setter BEFORE this runs, so the chips
            // it produced must not be wiped here.
            this._chips = this._chips || [];

            const input = this.refs.input;

            if (this.placeholder) {
                input.placeholder = this.placeholder;
            }

            // Wrap click focuses input
            this.addEventListener('click', (e) => {
                // A chip's remove control carries its own index and handles
                // itself through ~cl; a click that reaches here is bare
                // wrapper space, which focuses the input.
                if (this._claimed) { this._claimed = false; return; }
                input.focus();
            });

            // Keydown on input: comma/enter adds chip, backspace removes last
            input.addEventListener('keydown', (e) => {
                if ((e.key === ',' || e.key === 'Enter') && input.value.trim()) {
                    e.preventDefault();
                    this._addChip(input.value.trim());
                    input.value = '';
                } else if (e.key === 'Backspace' && !input.value && this._chips.length) {
                    this._removeChip(this._chips.length - 1);
                }
            });

            // Input event: split on comma for paste support
            input.addEventListener('input', () => {
                if (input.value.includes(',')) {
                    const parts = input.value.split(',').map(s => s.trim()).filter(Boolean);
                    input.value = '';
                    parts.forEach(p => this._addChip(p));
                }
            });

            // Initialize from value attribute
            const initial = this.getAttribute('value');
            if (initial) {
                initial.split(',').map(s => s.trim()).filter(Boolean).forEach(v => this._addChip(v, true));
            }
        },
    },
    methods: {
        set value(val) {
            this._chips = [];
            if (Array.isArray(val)) {
                val.forEach(v => this._addChip(v, true));
            } else if (typeof val === 'string' && val) {
                val.split(',').map(s => s.trim()).filter(Boolean).forEach(v => this._addChip(v, true));
            }
            this._syncChipElements();
        },
        get value() {
            return this._chips ? [...this._chips] : [];
        },

        get formValue() {
            return this._chips ? this._chips.join(',') : '';
        },

        _addChip(text, silent) {
            if (this._chips.includes(text)) return;
            this._chips.push(text);
            this._syncChipElements();
            if (!silent) this._fireChange();
        },

        // The remove control sits inside the wrapper whose click focuses the
        // input; claiming the click keeps a removal from also refocusing.
        _removeChipAt(index) {
            this._claimed = true;
            this._removeChip(Number(index));
        },
        _removeChip(index) {
            this._chips.splice(index, 1);
            this._syncChipElements();
            this._fireChange();
        },

        _syncChipElements() {
            if (!this._internalData) return;
            this.data.chipElements = this._chips.map((text, i) =>
                ['s-ch', `="${text}"`, [
                    ['s-ch-x', `~cl:call:_removeChipAt:${i}`, '="\u00D7"'],
                ]]
            );
        },

        _fireChange() {
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: [...this._chips] },
            }));
        },
    },
};
return { __default };
})();

const __m79 = (() => {
// OTP input (s-otp) — a segmented one-time-code field. One <input> per character
// so mobile keyboards and password managers behave, presented as a single value
// to the form: @name + value accessors + a change dispatch is the whole field
// protocol (same contract s-ch-i uses).
//
// The segments are an implementation detail on purpose. Everything outside reads
// `value` as one string, so a partially typed code is simply a shorter string.
const DIGITS = /^\d$/;

// ONE builder for the segments, used by the client render and by the SSR hook,
// so first paint and hydration cannot produce different boxes. maxLength 1 keeps
// a segment single-character without a keypress guard that would also block IME
// and autofill; only the first segment claims one-time-code so a password
// manager fills the code once rather than six times.
function segmentDefs(len, numeric, disabled) {
    return Array.from({ length: len }, (_, i) => [
        'input.otp-seg', '@type=text', '@maxlength=1',
        `@autocomplete=${i === 0 ? 'one-time-code' : 'off'}`,
        `@inputmode=${numeric ? 'numeric' : 'text'}`,
        `@aria-label=${i + 1} of ${len}`,
        ...(disabled ? ['@disabled='] : []),
    ]);
}

// The length/type/disabled the markup asks for, read the same way on both sides.
function otpConfig(get) {
    const len = Math.max(1, parseInt(get('length'), 10) || 6);
    return { len, numeric: (get('type') || 'numeric') !== 'text', disabled: get('disabled') != null };
}

const __default = {
    tags: ['s-otp', 's-otp'],
    dispatches: ['change'],
    props: ['@name', '@length', '@type', '@disabled'],
    internalStore: true,
    // Server twin of the client render: without it the segments only existed
    // after hydration, so first paint was an empty box. connected() adopts what
    // the server sent rather than rendering a second set.
    ssr({ attrs }) {
        const { len, numeric, disabled } = otpConfig((k) => attrs[k]);
        return segmentDefs(len, numeric, disabled);
    },
    callbacks: {
        connected() {
            // Numeric is the default because one-time codes overwhelmingly are:
            // it selects the digit keypad on mobile. @type=text opts out.
            const cfg = otpConfig((k) => this.getAttribute(k));
            this._otpLen = cfg.len;
            this._otpNumeric = cfg.numeric;

            this._segs = Array.from(this.children).filter((c) => c.classList.contains('otp-seg'));
            if (!this._segs.length) this._renderSegments();
            this._wireSegments();

            const initial = this.getAttribute('value');
            if (initial) this.value = initial;
        },
    },
    methods: {
        // The segments are ours and we hold them; nothing re-queries the DOM to
        // find a box it just stamped.
        _segments() {
            if (!this._segs || !this._segs.length) {
                this._segs = Array.from(this.children).filter((c) => c.classList.contains('otp-seg'));
            }
            return this._segs;
        },
        _renderSegments() {
            // The sanctioned builder, from the SAME defs the server emits.
            this._applyChildElements(
                segmentDefs(this._otpLen, this._otpNumeric, this.hasAttribute('disabled')),
                this,
            );
            this._segs = Array.from(this.children).filter((c) => c.classList.contains('otp-seg'));
        },
        _wireSegments() {
            const segs = this._segments();
            // A segment IS the event target — it holds no children — so owning
            // the list is enough to identify it.
            const segFor = (t) => (segs.includes(t) ? t : null);

            this.addEventListener('input', (e) => {
                const seg = segFor(e.target);
                if (!seg) return;
                if (this._otpNumeric && seg.value && !DIGITS.test(seg.value)) {
                    seg.value = '';
                    return;
                }
                if (seg.value) {
                    const next = segs[segs.indexOf(seg) + 1];
                    if (next) { next.focus(); next.select(); }
                }
                this._emitOtpChange();
            });

            this.addEventListener('keydown', (e) => {
                const seg = segFor(e.target);
                if (!seg) return;
                const i = segs.indexOf(seg);
                if (e.key === 'Backspace' && !seg.value && i > 0) {
                    // Backspace on an empty box steps back AND clears, so holding
                    // it deletes the code rather than stalling on each boundary.
                    e.preventDefault();
                    segs[i - 1].focus();
                    segs[i - 1].value = '';
                    this._emitOtpChange();
                } else if (e.key === 'ArrowLeft' && i > 0) {
                    e.preventDefault(); segs[i - 1].focus(); segs[i - 1].select();
                } else if (e.key === 'ArrowRight' && i < segs.length - 1) {
                    e.preventDefault(); segs[i + 1].focus(); segs[i + 1].select();
                }
            });

            // A pasted code arrives in one segment; spread it across the rest.
            this.addEventListener('paste', (e) => {
                const seg = segFor(e.target);
                if (!seg) return;
                e.preventDefault();
                const text = (e.clipboardData?.getData('text') || '').trim();
                const chars = (this._otpNumeric ? text.replace(/\D/g, '') : text).split('');
                let i = segs.indexOf(seg);
                for (const ch of chars) {
                    if (i >= segs.length) break;
                    segs[i].value = ch;
                    i++;
                }
                (segs[Math.min(i, segs.length - 1)] || seg).focus();
                this._emitOtpChange();
            });

            // Clicking anywhere lands on the first empty box: a half-typed code
            // should never continue from the middle.
            this.addEventListener('focusin', (e) => {
                if (!segFor(e.target)) return;
                const firstEmpty = segs.filter((s) => !s.value)[0];
                if (firstEmpty && segs.indexOf(e.target) > segs.indexOf(firstEmpty)) {
                    firstEmpty.focus();
                }
            });
        },
        _emitOtpChange() {
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: this.value, complete: this.value.length === this._otpLen },
            }));
        },
        set value(val) {
            const text = String(val ?? '');
            const segs = this._segments();
            segs.forEach((seg, i) => { seg.value = text[i] || ''; });
        },
        get value() {
            return this._segments().map((s) => s.value).join('');
        },
    },
};
return { __default };
})();

const __m80 = (() => {
const __default = {
    tags: ['s-i', 's-i'],
    baseElement: 'HTMLInputElement',
    props: ['@type', '@error', '@disabled', '@placeholder', '@required', '@name', '@autocomplete'],
    // required/disabled/readonly are native input semantics AT already
    // announces; the error flag is framework state and needs the mapping.
    aria: { invalid: 'error' },
};
return { __default };
})();

const __m81 = (() => {
const __default = {
    tags: ['s-rb', 's-rb'],
    props: ['@selected', '@disabled'],
    aria: { role: 'radio', checked: 'selected', disabled: 'disabled' },
    emits: { click: 'selected' }
};
return { __default };
})();

const __m82 = (() => {
const __default = {
    tags: ['s-rb-g', 's-rb-g'],
    aria: { role: 'radiogroup', itemRole: 'radio' },
    keyboardNav: { orientation: 'both', selectOnFocus: true },
    itemRenderer: 's-rb',
    selectable: true
};
return { __default };
})();

const __m83 = (() => {
const __default = {
    tags: ['s-ta', 's-ta'],
    baseElement: 'HTMLTextAreaElement',
    props: ['@error', '@disabled', '@placeholder', '@required', '@name', '@rows', '@cols'],
    // required/disabled are native textarea semantics; error is framework state.
    aria: { invalid: 'error' },
};
return { __default };
})();

const __m84 = (() => {
const highlightOption = __m9.highlightOption;
const ensureAriaId = __m17.ensureAriaId;
const asFormField = __m66.asFormField;
const __default = asFormField({
    tags: ['s-sel', 's-sel'],
    dispatches: ['change', 'valueChanged'],
    props: ['@open', '@searchable', '@multiple', '@placeholder', '@disabled', '@clearable', '@value'],
    // The combobox role lives on the DISPLAY (the focusable element) — see
    // the template; the host def keeps the state reflection and item roles.
    aria: { disabled: 'disabled', itemRole: 'option' },
    // Note: 'value' must not be a storeAttr — storeAttr bindings coerce the
    // store key (= element id) to a boolean attribute, which blanks the
    // string value attribute whenever the selection store updates
    storeAttrs: ['open'],
    floatingPanel: '.sel-panel',
    selectable: true,
    itemRenderer: 's-sel-o',
    internalStore: true,
    children: [
        ['s-cn.sel-display', '@tabindex=0', '@role=combobox', '@aria-haspopup=listbox', '@aria-expanded=false', ':ref=display', [
            ['span.sel-text', ':ref=displayText', ':dataKey=displayText'],
            ['s-cn.sel-chips', ':ref=chips', ':dataKey=chipDefs', ':dataField=children'],
            // Native button: internal chrome (the carousel precedent) — an s-b
            // here inherits every public button skin, including compiled themes.
            ['button.sel-clear', '@type=button', '@aria-label=$t.a11y.clear', ':ref=clearBtn', [['s-ic', '@n=x']]],
        ]],
        ['s-cn.sel-panel', ':ref=panel', [
            ['input.sel-search', '@type=text', '@placeholder=Search...', ':ref=search'],
            ['s-cn.sel-options', ':ref=optionsList', '@role=listbox'],
        ]],
    ],
    callbacks: {
        connected() {
            // Children DSL is deferred by connectedCallback for list components
            // with no data yet — build the static template now so the refs
            // (display, panel, optionsList) exist for both authored and
            // data-driven modes.
            if (this._childElements && !this._childrenBuilt) {
                this._childrenBuilt = true;
                this._applyChildElements(this._childElements, this);
            }

            // Move existing authored options into the options container
            ['s-sel-o', 's-sel-o'].forEach(tag => {
                Array.from(this.getElementsByTagName(tag))
                    .filter(opt => !this.refs.optionsList.contains(opt))
                    .forEach(opt => this.refs.optionsList.appendChild(opt));
            });

            // Data-driven items render into the panel's options list,
            // not into the host (which would wipe the template)
            this._listContainer = this.refs.optionsList;

            // The floating panel setup in connectedCallback ran before the
            // template was built — resolve the panel now that it exists
            this._setupFloatingPanel();

            // Multi mode: align the selection mixin so it doesn't clear
            // sibling selected attributes on each option click
            if (this.multiple) this.multiSelect = true;

            // An authored aria-label on the host names the DISPLAY, which is
            // the focused combobox element (a11y metadata — the sanctioned
            // setAttribute exception). In forms the field's :lf label is the
            // richer route; this covers standalone selects.
            if (this.hasAttribute('aria-label') && this.refs.display && !this.refs.display.hasAttribute('aria-label')) {
                this.refs.display.setAttribute('aria-label', this.getAttribute('aria-label'));
            }

            this._selectedValues = [];
            this._focusedIndex = -1;

            // Initialize display text
            this.setData('displayText', this.placeholder || 'Select...');

            // Initialize from value attr
            const initVal = this._value;
            if (initVal) this._setValueFromString(initVal);

            // Click delegation on self
            this.addEventListener('click', (e) => {
                // Clear button
                if (e.target.closest('.sel-clear')) {
                    e.stopPropagation();
                    this._clearAll();
                    return;
                }

                // Display click - toggle open. The chips live inside the
                // display, and a chip's remove control handles its own click
                // through ~cl — toggling the panel on top of it would reopen
                // what the reader just dismissed.
                if (this.refs.display.contains(e.target) && !this.refs.chips.contains(e.target)) {
                    if (this.disabled) return;
                    this._toggle();
                    return;
                }

                // Option selection
                const opt = e.target.closest('s-sel-o, swc-select-option');
                if (opt && this.refs.optionsList.contains(opt) && !opt.hasAttribute('disabled')) {
                    this._selectOption(opt);
                }
            });

            // Search filtering - addEventListener justified: behavior boundary on native input
            this.refs.search.addEventListener('input', () => {
                this._filterOptions(this.refs.search.value);
            });
            this.refs.search.addEventListener('click', (e) => e.stopPropagation());

            // Keyboard navigation
            this.addEventListener('keydown', (e) => {
                if (!this.open) {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        this._openPanel();
                    }
                    return;
                }

                const opts = this._getVisibleOptions();
                switch (e.key) {
                    case 'ArrowDown':
                        e.preventDefault();
                        this._focusedIndex = Math.min(this._focusedIndex + 1, opts.length - 1);
                        this._highlightOption(opts);
                        break;
                    case 'ArrowUp':
                        e.preventDefault();
                        this._focusedIndex = Math.max(this._focusedIndex - 1, 0);
                        this._highlightOption(opts);
                        break;
                    case 'Enter':
                        e.preventDefault();
                        if (this._focusedIndex >= 0 && opts[this._focusedIndex]) {
                            this._selectOption(opts[this._focusedIndex]);
                        }
                        break;
                }
            });
        },
    },
    methods: {
        // Note: no _open() wrapper — the open prop's backing field is _open,
        // so a method named _open would be shadowed after the first toggle
        _toggle() { this._togglePanel(); },
        _close() { this._closePanel(); this.refs.display.focus(); },

        onOpenChange(isOpen) {
            // a11y metadata on the display (mirrors the framework aria def-field).
            this.refs.display?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            if (!isOpen) this.refs.display?.removeAttribute('aria-activedescendant');
            if (isOpen) {
                this._focusedIndex = -1;
                this.refs.search.value = '';
                this._filterOptions('');
                if (this.searchable) {
                    requestAnimationFrame(() => this.refs.search.focus());
                }
            }
        },

        _getVisibleOptions() {
            return this._getAllOptions().filter(o => !o.hasAttribute('hidden'));
        },

        _getAllOptions() {
            // getElementsByTagName on optionsList justified: authored/dynamic children from itemRenderer
            const selO = Array.from(this.refs.optionsList.getElementsByTagName('s-sel-o'));
            const fullO = Array.from(this.refs.optionsList.getElementsByTagName('s-sel-o'));
            return [...selO, ...fullO];
        },

        _filterOptions(query) {
            const q = query.toLowerCase();
            this._getAllOptions().forEach(opt => {
                const text = opt.textContent.toLowerCase();
                if (!q || text.includes(q)) {
                    opt.removeAttribute('hidden');
                } else {
                    opt.setAttribute('hidden', '');
                }
            });
        },

        _highlightOption(opts) {
            highlightOption(opts, this._focusedIndex);
            // The visually highlighted option is the active descendant — the
            // announcement half of the combobox pattern (focus stays on the
            // display; AT follows this reference).
            const active = opts[this._focusedIndex];
            if (active) this.refs.display?.setAttribute('aria-activedescendant', ensureAriaId(active));
            else this.refs.display?.removeAttribute('aria-activedescendant');
        },

        _selectOption(opt) {
            const val = opt.getAttribute('value') || opt.textContent.trim();
            const isMulti = this.multiple;

            if (isMulti) {
                const idx = this._selectedValues.indexOf(val);
                if (idx >= 0) {
                    this._selectedValues.splice(idx, 1);
                    opt.removeAttribute('selected');
                } else {
                    this._selectedValues.push(val);
                    opt.setAttribute('selected', '');
                }
                this._updateMultiDisplay();
                this.setAttribute('value', this._selectedValues.join(','));
            } else {
                this._getAllOptions().forEach(o => o.removeAttribute('selected'));
                opt.setAttribute('selected', '');
                this._selectedValues = [val];
                this.setData('displayText', opt.textContent.trim());
                this.refs.displayText.setAttribute('hasvalue', '');
                this.setAttribute('value', val);
                this._close();
            }

            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: isMulti ? [...this._selectedValues] : val },
            }));
            this.dispatchEvent(new CustomEvent('valueChanged', {
                bubbles: true,
                detail: { value: isMulti ? [...this._selectedValues] : val, name: this.getAttribute('name') },
            }));
        },

        _deselectValue(val) {
            const idx = this._selectedValues.indexOf(val);
            if (idx < 0) return;
            this._selectedValues.splice(idx, 1);
            this._getAllOptions()
                .filter(o => (o.getAttribute('value') || o.textContent.trim()) === val)
                .forEach(o => o.removeAttribute('selected'));
            this._updateMultiDisplay();
            this.setAttribute('value', this._selectedValues.join(','));
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: [...this._selectedValues] },
            }));
        },

        _updateMultiDisplay() {
            if (this._selectedValues.length === 0) {
                this.setData('displayText', this.placeholder || 'Select...');
                this.refs.displayText.removeAttribute('hasvalue');
                this.setData('chipDefs', []);
                return;
            }
            this.setData('displayText', '');
            this.setData('chipDefs', this._selectedValues.map(val => {
                const matchOpt = this._getAllOptions().find(o =>
                    (o.getAttribute('value') || o.textContent.trim()) === val
                );
                const label = matchOpt ? matchOpt.textContent.trim() : val;
                // The chip's remove control carries the value it removes, so
                // deselection never reads a coordinate back off the chip.
                return ['span.sel-chip', `="${label}"`, [
                    ['span.sel-chip-x', `~cl:call:_deselectValue:${val}`, '="\u00D7"'],
                ]];
            }));
        },

        _clearAll() {
            this._selectedValues = [];
            this._getAllOptions().forEach(o => o.removeAttribute('selected'));
            this.setData('displayText', this.placeholder || 'Select...');
            this.refs.displayText.removeAttribute('hasvalue');
            this.setData('chipDefs', []);
            this.setAttribute('value', '');
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: this.multiple ? [] : '' },
            }));
        },

        _setValueFromString(str) {
            const isMulti = this.multiple;
            const vals = isMulti ? str.split(',').map(s => s.trim()).filter(Boolean) : [str.trim()];
            this._selectedValues = [];
            this._getAllOptions().forEach(o => o.removeAttribute('selected'));
            vals.forEach(val => {
                const matchOpt = this._getAllOptions().find(o =>
                    (o.getAttribute('value') || o.textContent.trim()) === val
                );
                if (matchOpt) {
                    matchOpt.setAttribute('selected', '');
                    this._selectedValues.push(val);
                }
            });
            if (isMulti) {
                this._updateMultiDisplay();
            } else if (this._selectedValues.length) {
                const opt = this._getAllOptions().find(o =>
                    (o.getAttribute('value') || o.textContent.trim()) === this._selectedValues[0]
                );
                if (opt) {
                    this.setData('displayText', opt.textContent.trim());
                    this.refs.displayText.setAttribute('hasvalue', '');
                }
            }
        },

        get value() {
            return this.multiple ? [...this._selectedValues] : (this._selectedValues[0] || '');
        },
        set value(v) {
            if (Array.isArray(v)) {
                this._setValueFromString(v.join(','));
            } else {
                this._setValueFromString(String(v || ''));
            }
            this.setAttribute('value', Array.isArray(v) ? v.join(',') : String(v || ''));
        },
    },
});
return { __default };
})();

const __m85 = (() => {
const asFormField = __m66.asFormField;
const __default = asFormField({
    tags: ['s-inum', 's-inum'],
    dispatches: ['change', 'valueChanged'],
    props: ['@value', '@min', '@max', '@step', '@precision', '@disabled', '@sz'],
    storeAttrs: ['value'],
    children: [
        ['button.inum-dec', ':ref=decBtn', '@type=button', '@tabindex=-1', '="\u2212"'],
        ['input.inum-input', ':ref=input', '@type=text', '@inputmode=decimal'],
        ['button.inum-inc', ':ref=incBtn', '@type=button', '@tabindex=-1', '="+"'],
    ],
    callbacks: {
        connected() {
            this._readConfig();
            this._value = this._value !== undefined ? parseFloat(this._value) : 0;

            this.refs.input.value = this._formatValue(this._value);

            // Event delegation on self for button clicks
            this.addEventListener('click', (e) => {
                if (this.disabled) return;
                if (e.target === this.refs.decBtn || this.refs.decBtn.contains(e.target)) {
                    this._stepValue(-1);
                } else if (e.target === this.refs.incBtn || this.refs.incBtn.contains(e.target)) {
                    this._stepValue(1);
                }
            });

            // Long press via mousedown on self, detect which button
            let holdTimer = null;
            let holdInterval = null;
            const startHold = (dir) => {
                holdTimer = setTimeout(() => {
                    holdInterval = setInterval(() => this._stepValue(dir), 75);
                }, 400);
            };
            const stopHold = () => {
                clearTimeout(holdTimer);
                clearInterval(holdInterval);
                holdTimer = null;
                holdInterval = null;
            };
            this.addEventListener('mousedown', (e) => {
                if (this.disabled) return;
                if (e.target === this.refs.decBtn || this.refs.decBtn.contains(e.target)) {
                    startHold(-1);
                } else if (e.target === this.refs.incBtn || this.refs.incBtn.contains(e.target)) {
                    startHold(1);
                }
            });
            document.addEventListener('mouseup', stopHold);
            this._stopHold = stopHold;

            // Input typing — change event on input (native input, listener OK)
            this.refs.input.addEventListener('change', () => {
                const raw = this.refs.input.value.trim();
                const num = parseFloat(raw);
                if (isNaN(num)) {
                    this.refs.input.value = this._formatValue(this._value);
                    return;
                }
                this._setValue(num);
            });

            // Keyboard on input (native input, listener OK)
            this.refs.input.addEventListener('keydown', (e) => {
                if (e.key === 'ArrowUp') { e.preventDefault(); this._stepValue(1); }
                if (e.key === 'ArrowDown') { e.preventDefault(); this._stepValue(-1); }
            });
        },
        disconnected() {
            if (this._stopHold) this._stopHold();
        },
    },
    methods: {
        get value() { return this._value; },
        set value(v) {
            const num = parseFloat(v);
            if (!isNaN(num)) this._setValue(num);
        },

        get min() { return this._min; },
        set min(v) { this._min = v === null || v === undefined ? -Infinity : parseFloat(v); },

        get max() { return this._max; },
        set max(v) { this._max = v === null || v === undefined ? Infinity : parseFloat(v); },

        get step() { return this._step; },
        set step(v) { this._step = parseFloat(v) || 1; },

        get precision() { return this._precision; },
        set precision(v) { this._precision = v === null || v === undefined ? null : parseInt(v, 10); },

        _readConfig() {
            this._step = parseFloat(this.step) || 1;
            this._min = this._min !== undefined ? parseFloat(this._min) : -Infinity;
            this._max = this._max !== undefined ? parseFloat(this._max) : Infinity;
            this._precision = this._precision !== undefined ? parseInt(this._precision, 10) : null;
        },

        _formatValue(v) {
            if (this._precision !== null) return v.toFixed(this._precision);
            return String(v);
        },
        _clamp(v) {
            // Bounds default open: before connected() runs _readConfig, _min/
            // _max are undefined and Math.min(undefined, v) would poison the
            // value to NaN.
            return Math.min(this._max ?? Infinity, Math.max(this._min ?? -Infinity, v));
        },
        _stepValue(dir) {
            if (this.disabled) return;
            const next = this._value + dir * this._step;
            this._setValue(next);
        },
        _setValue(v) {
            v = this._clamp(v);
            if (this._precision !== null) {
                v = parseFloat(v.toFixed(this._precision));
            }
            this._value = v;
            // A store-bound @value=$key applies before the children are
            // stamped; buffer the value then — connected() writes it into
            // the input once the refs exist.
            if (this.refs.input) this.refs.input.value = this._formatValue(v);
            this.setAttribute('value', String(v));
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: v },
            }));
            this.dispatchEvent(new CustomEvent('valueChanged', {
                bubbles: true,
                detail: { value: v, name: this.getAttribute('name') },
            }));
        },

        onStepChange() { this._readConfig(); },
        onMinChange() { this._readConfig(); },
        onMaxChange() { this._readConfig(); },
        onPrecisionChange() { this._readConfig(); },
    },
});
return { __default };
})();

const __m86 = (() => {
const asFormField = __m66.asFormField;
// Thumbs are a LIST, not a low/high pair: one value is a plain slider, two is
// the classic range, and `value="20,50,80"` is three independent stops. The
// pair-specific `@range` flag survives as the shorthand that seeds two thumbs
// at min and max. `@marks` draws the scale, `@show-value` pins the per-thumb
// label open, `@show-minmax` prints the bounds at the track ends.
const __default = asFormField({
    tags: ['s-sld', 's-sld'],
    dispatches: ['change'],
    props: ['@value', '@min', '@max', '@step', '@disabled', '@vertical', '@range',
        '@marks', '@show-value', '@show-minmax'],
    aria: { role: 'slider', valuenow: 'value', valuemin: 'min', valuemax: 'max', disabled: 'disabled' },
    storeAttrs: ['value'],
    cssVars: { 'fill-start': '%', 'fill-size': '%', 'handle-low': '%', 'handle-high': '%' },
    internalStore: true,
    children: [
        ['s-cn.sld-track', ':ref=track', [
            ['s-cn.sld-fill', ':ref=fill'],
            ['s-cn.sld-marks', ':ref=marks', ':dataKey=markChildren', ':dataField=children'],
            ['s-cn.sld-handles', ':ref=handles', ':dataKey=handleChildren', ':dataField=children'],
            // Inside the track, not beside it: the compiled skin makes the host
            // a flex ROW, so a sibling here became a column next to the track
            // instead of a caption under it. The track is also the box whose
            // left and right edges the bounds must line up with.
            ['s-cn.sld-bounds', [
                ['s-cn.sld-bound-min', ':ref=boundMin'],
                ['s-cn.sld-bound-max', ':ref=boundMax'],
            ]],
        ]],
    ],
    callbacks: {
        connected() {
            this._readConfig();
            this._dragging = null;

            // Parse initial value from the raw attribute state (_value is set by
            // attributeChangedCallback) — the value getter derives from _values,
            // which is not initialized yet at this point. A bound :value= that
            // arrived before this ran is held in _pendingValue and takes
            // precedence: it used to be overwritten here, so every bound slider
            // rendered at min.
            const initVal = this._pendingValue ?? this._value ?? this.getAttribute('value');
            this._pendingValue = undefined;
            // An explicit initial value wins. Failing that, keep whatever the
            // setter already produced: a sibling attribute like @min or @step
            // runs _readConfig before connect, which makes the setter take its
            // normal path instead of parking in _pendingValue — and re-seeding
            // from the (property-bound, so absent) attribute wiped it, leaving
            // every such slider at min.
            if (initVal != null) this._values = this._parseValues(initVal);
            else if (!this._values) this._values = this._parseValues(null);

            this._buildMarks();
            this._buildHandles();
            this._updatePositions();

            // Track click — delegated on self
            this.addEventListener('mousedown', (e) => {
                if (this.disabled) return;
                const i = this._handleIndexFromTarget(e.target);
                if (i !== -1) {
                    e.preventDefault();
                    e.stopPropagation();
                    this._dragging = i;
                    return;
                }
                // Track click — the nearest thumb takes the position
                if (!this.refs.track.contains(e.target)) return;
                const val = this._valueFromEvent(e);
                const nearest = this._nearestIndex(val);
                this._setValueAt(nearest, val);
                this._dragging = nearest;
                this._updatePositions();
                this._fireChange();
            });

            this._onMouseMove = (e) => {
                if (this._dragging === null) return;
                this._setValueAt(this._dragging, this._valueFromEvent(e));
                this._updatePositions();
                this._fireChange();
            };
            this._onMouseUp = () => { this._dragging = null; };
            document.addEventListener('mousemove', this._onMouseMove);
            document.addEventListener('mouseup', this._onMouseUp);

            // Keyboard — delegated on self
            this.addEventListener('keydown', (e) => {
                const i = this._handleIndexFromTarget(e.target);
                if (i !== -1) this._handleKey(e, i);
            });
        },
        disconnected() {
            document.removeEventListener('mousemove', this._onMouseMove);
            document.removeEventListener('mouseup', this._onMouseUp);
        },
    },
    methods: {
        get value() {
            if (!this._values) return this._min ?? 0;
            return this._values.length > 1 ? [...this._values] : this._values[0];
        },
        set value(v) {
            // Before connected() runs _readConfig there is no range to clamp
            // against, so clamping here would yield NaN. Hold the raw value and
            // let connected() seed from it.
            if (this._min === undefined) { this._pendingValue = v; return; }
            const next = this._parseValues(v);
            // A late value must not silently change how many thumbs exist —
            // rebuilding them would drop focus mid-interaction.
            if (this._values && next.length !== this._values.length) {
                this._values = next;
                this._buildHandles();
            } else {
                this._values = next;
            }
            if (this.refs.track) this._updatePositions();
        },

        get min() { return this._min; },
        set min(v) { this._min = parseFloat(v) || 0; if (this.refs.track) this._refresh(); },

        get max() { return this._max; },
        set max(v) { this._max = parseFloat(v) || 100; if (this.refs.track) this._refresh(); },

        get step() { return this._step; },
        set step(v) { this._step = parseFloat(v) || 1; },

        _readConfig() {
            this._min = parseFloat(this.min) || 0;
            this._max = parseFloat(this.max) || 100;
            this._step = parseFloat(this.step) || 1;
            this._isRange = this.hasAttribute('range');
            this._isVertical = this.hasAttribute('vertical');
        },

        // "20,50,80" → three thumbs; "" under @range → the two bounds; a bare
        // number → one. Anything unparseable falls back to min, as before.
        _parseValues(raw) {
            const list = Array.isArray(raw) ? raw : String(raw ?? '').split(',');
            const nums = list.map((p) => parseFloat(p)).filter((n) => Number.isFinite(n));
            if (!nums.length) return this._isRange ? [this._min, this._max] : [this._min];
            if (nums.length === 1 && this._isRange) return [this._clamp(nums[0]), this._max];
            return nums.map((n) => this._clamp(n)).sort((a, b) => a - b);
        },

        _buildHandles() {
            // Stamped through the bound slot, so the :ref tokens register on
            // this component and every thumb is reachable by name — no
            // querySelector, no index written into the DOM.
            this.data.handleChildren = this._values.map((v, i) => [
                's-cn.sld-handle', `:ref=handle${i}`, '@tabindex=0', '@role=slider',
                `@aria-valuemin=${this._min}`, `@aria-valuemax=${this._max}`,
                `@aria-orientation=${this._isVertical ? 'vertical' : 'horizontal'}`,
                [['s-cn.sld-tooltip', `:ref=tooltip${i}`]],
            ]);
            // With several thumbs the HOST is no longer the slider — it groups
            // them, and a combined aria-valuenow would be meaningless.
            if (this._values.length > 1) {
                this.setAttribute('role', 'group');
                this.removeAttribute('aria-valuenow');
            }
        },

        _buildMarks() {
            this.data.markChildren = this._parseMarks().map((m) => [
                's-cn.sld-mark', `@style=--sld-pos: ${this._pct(m.value)}%`,
                [['s-cn.sld-mark-label', `="${m.label}"`]],
            ]);
        },

        // `@marks=` alone ticks every step; `@marks=0,50,100` pins positions;
        // `@marks=0:Low,100:High` captions them. A tick is labelled with its own
        // value unless the spec says otherwise (`50:` = tick, no caption).
        _parseMarks() {
            const raw = this.getAttribute('marks');
            if (raw === null) return [];
            const spec = raw.trim();
            if (!spec) {
                const steps = Math.round((this._max - this._min) / this._step);
                // A fine step would stamp hundreds of nodes; past this an author
                // should pass the positions they actually want.
                if (!Number.isFinite(steps) || steps < 1 || steps > 20) return [];
                return Array.from({ length: steps + 1 }, (_, i) => {
                    const value = this._min + i * this._step;
                    return { value, label: String(value) };
                });
            }
            return spec.split(',').map((part) => {
                const idx = part.indexOf(':');
                const value = parseFloat(idx === -1 ? part : part.slice(0, idx));
                if (!Number.isFinite(value)) return null;
                return { value, label: idx === -1 ? String(value) : part.slice(idx + 1).trim() };
            }).filter(Boolean);
        },

        // Identity against the refs we stamped — the event target may be the
        // tooltip inside a thumb, so containment rather than equality.
        _handleIndexFromTarget(target) {
            for (let i = 0; i < this._values.length; i++) {
                const h = this.refs[`handle${i}`];
                if (h && h.contains(target)) return i;
            }
            return -1;
        },

        _nearestIndex(val) {
            let best = 0;
            let bestDist = Infinity;
            this._values.forEach((v, i) => {
                const d = Math.abs(val - v);
                if (d < bestDist) { bestDist = d; best = i; }
            });
            return best;
        },

        // Thumbs keep their order: one can never be dragged past its neighbours,
        // so `value` stays sorted and the fill span stays meaningful.
        _setValueAt(i, val) {
            const lower = i > 0 ? this._values[i - 1] : this._min;
            const upper = i < this._values.length - 1 ? this._values[i + 1] : this._max;
            this._values[i] = Math.min(upper, Math.max(lower, this._clamp(val)));
        },

        _clamp(v) {
            v = Math.round(v / this._step) * this._step;
            return Math.min(this._max, Math.max(this._min, v));
        },
        _valueFromEvent(e) {
            const rect = this.refs.track.getBoundingClientRect();
            let ratio;
            if (this._isVertical) {
                ratio = 1 - (e.clientY - rect.top) / rect.height;
            } else {
                ratio = (e.clientX - rect.left) / rect.width;
            }
            ratio = Math.max(0, Math.min(1, ratio));
            return this._clamp(this._min + ratio * (this._max - this._min));
        },
        _pct(v) {
            return ((v - this._min) / (this._max - this._min)) * 100;
        },
        // min/max changed under us: the scale, the thumb bounds and the captions
        // all derive from them.
        _refresh() {
            this._values = this._values.map((v) => this._clamp(v));
            this._buildMarks();
            this._buildHandles();
            this._updatePositions();
        },
        _updatePositions() {
            this._values.forEach((v, i) => {
                const handle = this.refs[`handle${i}`];
                if (!handle) return;
                handle.style.setProperty('--sld-pos', this._pct(v) + '%');
                handle.setAttribute('aria-valuenow', String(v));
                const tip = this.refs[`tooltip${i}`];
                if (tip) tip.textContent = v;
            });

            // One thumb fills from the start; several fill the span they cover.
            if (this._values.length > 1) {
                const start = this._pct(this._values[0]);
                const end = this._pct(this._values[this._values.length - 1]);
                this.style.setProperty('--sld-fill-start', start + '%');
                this.style.setProperty('--sld-fill-size', (end - start) + '%');
                this.removeAttribute('aria-valuenow');
            } else {
                this.style.setProperty('--sld-fill-start', '0%');
                this.style.setProperty('--sld-fill-size', this._pct(this._values[0]) + '%');
            }

            if (this.refs.boundMin) this.refs.boundMin.textContent = this._min;
            if (this.refs.boundMax) this.refs.boundMax.textContent = this._max;
        },
        _handleKey(e, i) {
            if (this.disabled) return;
            let delta = 0;
            if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = this._step;
            else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -this._step;
            else if (e.key === 'Home') delta = -Infinity;
            else if (e.key === 'End') delta = Infinity;
            else return;
            e.preventDefault();
            this._setValueAt(i, this._values[i] + delta);
            this._updatePositions();
            this._fireChange();
        },
        _fireChange() {
            const multi = this._values.length > 1;
            this.setAttribute('value', this._values.join(','));
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: multi ? [...this._values] : this._values[0] },
            }));
        },

        onMinChange() { this._readConfig(); if (this.refs.track) this._refresh(); },
        onMaxChange() { this._readConfig(); if (this.refs.track) this._refresh(); },
        onStepChange() { this._readConfig(); if (this.refs.track) this._buildMarks(); },
        onMarksChange() { if (this.refs.track) this._buildMarks(); },
    },
}, { changeEvents: ['change'] });
return { __default };
})();

const __m87 = (() => {
const asFormField = __m66.asFormField;
// ── Rate ──
// A rating input rendered as a small, data-driven list of star items. The
// parent (s-rt) turns the current value into one data object per star — its
// index, glyph and fill state (empty / half / full) — and pushes that array
// into the star list. Each star renders itself from that data, so the fill
// state is driven reactively by the value: rebuilding the array IS the render.
// No DOM lookups, no data-* index attribute, no imperative cache-and-render loop.

// Star item — one clickable star. Its glyph and layout come entirely from the
// item data the list assigns (char + half flag). The fill state (data-rt-*) and
// the rt-star class ride along as item.attrs / item.class, applied declaratively
// by the list mixin, so the star never mutates its own DOM or looks itself up.
const rateStar = {
    tags: ['s-rt-star', 's-rt-star'],
    children: [
        // Whole-star glyph — used when half stars are disabled.
        ['span', '?!data:half', '$char'],
        // Split glyph — two overlaid halves let the left and right colours
        // differ, which is how a half-filled star is shown.
        ['span.rt-half.rt-left', '?data:half', '$char'],
        ['span.rt-half.rt-right', '?data:half', '$char'],
    ],
};

// Star list — a plain data-driven list; renders one s-rt-star per array item.
const rateStars = {
    tags: ['s-rt-stars', 's-rt-stars'],
    itemRenderer: 's-rt-star',
};

const rate = asFormField({
    tags: ['s-rt', 's-rt'],
    dispatches: ['change'],
    props: ['@value', '@count', '@allowHalf', '@disabled', '@character'],
    storeAttrs: ['value'],
    internalStore: true,
    children: [
        ['s-rt-stars.rt-stars', ':dk=stars'],
    ],
    callbacks: {
        connected() {
            this._readConfig();
            this._value = parseFloat(this.value) || 0;
            this._hoverValue = 0;
            this._renderStars();

            // Click — delegated on self. The hit star is found from the event
            // path and its index read from item data, never from a selector.
            this.addEventListener('click', (e) => {
                if (this.disabled) return;
                const val = this._valueFromEvent(e);
                if (val === null) return;
                this.value = val;
                this.dispatchEvent(new CustomEvent('change', {
                    bubbles: true,
                    detail: { value: val },
                }));
            });

            // Hover preview — delegated on self.
            this.addEventListener('mousemove', (e) => {
                if (this.disabled) return;
                const val = this._valueFromEvent(e);
                if (val === null) return;
                this._hoverValue = val;
                this._renderStars();
            });

            this.addEventListener('mouseleave', () => {
                this._hoverValue = 0;
                this._renderStars();
            });
        },
    },
    methods: {
        get value() { return this._value; },
        set value(v) {
            // Reflect to the attribute; onValueChange normalises and re-renders.
            this.setAttribute('value', String(parseFloat(v) || 0));
        },

        get count() { return this._count; },
        set count(v) {
            this.setAttribute('count', String(parseInt(v, 10) || 5));
        },

        _readConfig() {
            this._count = parseInt(this.count, 10) || 5;
            this._char = this.character || '★';
            this._allowHalf = this.allowHalf !== undefined && this.allowHalf !== false;
        },

        // Build one data object per star. The fill state derives from the
        // current display value (a hover preview takes precedence over the set
        // value) and rides along as an attribute the CSS colours; the class and
        // glyph travel as data too. A signature guard skips the rebuild when
        // nothing visible changed, so a hover that stays within one star is free.
        _renderStars() {
            const display = this._hoverValue || this._value;
            const sig = `${display}|${this._count}|${this._char}|${this._allowHalf}`;
            if (sig === this._starSig) return;
            this._starSig = sig;

            this.data.stars = Array.from({ length: this._count }, (_, i) => {
                const index = i + 1;
                const state = this._fillState(display, index);
                return {
                    index,
                    char: this._char,
                    half: this._allowHalf,
                    class: 'rt-star',
                    attrs: { [`data-rt-${state}`]: true },
                };
            });
        },

        _fillState(display, index) {
            if (display >= index) return 'full';
            if (this._allowHalf && display >= index - 0.5) return 'half';
            return 'empty';
        },

        // Resolve the rating from a pointer event. The star element is the first
        // node in the event path that carries star item data; its index comes
        // from that data. Half values are decided by which side of the star the
        // pointer sits on, measured geometrically, not read off a marker element.
        _valueFromEvent(e) {
            const star = e.composedPath().find(
                (node) => node && node._data && typeof node._data.index === 'number'
            );
            if (!star) return null;
            const { index } = star._data;
            if (!this._allowHalf) return index;

            const rect = star.getBoundingClientRect();
            const isLeftHalf = (e.clientX - rect.left) < rect.width / 2;
            return isLeftHalf ? index - 0.5 : index;
        },

        onValueChange(v) {
            this._value = parseFloat(v) || 0;
            if (this._isConnected) this._renderStars();
        },
        onCountChange() {
            this._readConfig();
            if (this._isConnected) this._renderStars();
        },
        onCharacterChange() {
            this._readConfig();
            if (this._isConnected) this._renderStars();
        },
        onAllowHalfChange() {
            this._readConfig();
            if (this._isConnected) this._renderStars();
        },
    },
}, { changeEvents: ['change'] });

const __default = [rateStar, rateStars, rate];
return { __default };
})();

const __m88 = (() => {
// ── OKLCH Color Utilities ──
// CSS Color Level 4 conversion chain:
// oklch ↔ oklab ↔ linear-sRGB ↔ sRGB ↔ hex/rgb/hsl
//
// Matrices from Björn Ottosson / CSS Color 4 spec.
// All functions are pure — no DOM dependencies.

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

// ── Oklab ↔ OKLCH (cylindrical ↔ rectangular) ──

function oklchToOklab(L, C, H) {
  const hRad = H * DEG;
  return { L, a: C * Math.cos(hRad), b: C * Math.sin(hRad) };
}

function oklabToOklch(L, a, b) {
  const C = Math.sqrt(a * a + b * b);
  let H = Math.atan2(b, a) * RAD;
  if (H < 0) H += 360;
  return { L, C, H };
}

// ── Oklab ↔ linear-sRGB ──
// Path: Oklab → LMS' (inverse M2) → cube → LMS → linear-sRGB (inverse M1)

function oklabToLinearRgb(L, a, b) {
  // Oklab → LMS' (cube-root domain)
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  // Cube to get LMS
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  // LMS → linear-sRGB (computed inverse of forward sRGB→LMS matrix)
  return {
    r:  4.0767416613480 * l - 3.3077115904082 * m + 0.2309699287294 * s,
    g: -1.2684380040922 * l + 2.6097574006634 * m - 0.3413193963102 * s,
    b: -0.0041960865418 * l - 0.7034186144594 * m + 1.7076147009309 * s,
  };
}

function linearRgbToOklab(r, g, b) {
  // linear-sRGB → LMS
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  // Cube root to get LMS'
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  // LMS' → Oklab
  return {
    L:  0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    a:  1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    b:  0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  };
}

// ── sRGB gamma ──

function linearToSrgb(c) {
  return c >= 0.0031308
    ? 1.055 * Math.pow(c, 1 / 2.4) - 0.055
    : 12.92 * c;
}

function srgbToLinear(c) {
  return c >= 0.04045
    ? Math.pow((c + 0.055) / 1.055, 2.4)
    : c / 12.92;
}

// ── High-level conversions ──

function oklchToSrgb(L, C, H) {
  const lab = oklchToOklab(L, C, H);
  const lin = oklabToLinearRgb(lab.L, lab.a, lab.b);
  return {
    r: Math.round(Math.min(255, Math.max(0, linearToSrgb(lin.r) * 255))),
    g: Math.round(Math.min(255, Math.max(0, linearToSrgb(lin.g) * 255))),
    b: Math.round(Math.min(255, Math.max(0, linearToSrgb(lin.b) * 255))),
  };
}

function srgbToOklch(r, g, b) {
  const lin = {
    r: srgbToLinear(r / 255),
    g: srgbToLinear(g / 255),
    b: srgbToLinear(b / 255),
  };
  const lab = linearRgbToOklab(lin.r, lin.g, lin.b);
  return oklabToOklch(lab.L, lab.a, lab.b);
}

// ── Gamut checking & mapping ──

function isInGamut(L, C, H) {
  const lab = oklchToOklab(L, C, H);
  const lin = oklabToLinearRgb(lab.L, lab.a, lab.b);
  const eps = 0.000075;
  return lin.r >= -eps && lin.r <= 1 + eps
      && lin.g >= -eps && lin.g <= 1 + eps
      && lin.b >= -eps && lin.b <= 1 + eps;
}

function gamutMap(L, C, H) {
  if (C === 0 || isInGamut(L, C, H)) return { L, C, H };
  let lo = 0, hi = C;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (isInGamut(L, mid, H)) lo = mid;
    else hi = mid;
  }
  return { L, C: lo, H };
}

function maxChroma(L, H) {
  if (L <= 0 || L >= 1) return 0;
  let lo = 0, hi = 0.5;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (isInGamut(L, mid, H)) lo = mid;
    else hi = mid;
  }
  return lo;
}

// ── Hex ↔ sRGB ──

function hexToSrgb(hex) {
  let h = hex.replace(/^#/, '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (h.length === 4) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2] + h[3] + h[3];
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
    a: h.length === 8 ? parseInt(h.substring(6, 8), 16) / 255 : 1,
  };
}

function srgbToHex(r, g, b) {
  return '#' + [r, g, b].map(c => Math.round(c).toString(16).padStart(2, '0')).join('');
}

// ── sRGB ↔ HSL ──

function srgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + 6) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

function hslToSrgb(h, s, l) {
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

// ── Parse any CSS color string → OKLCH ──

function parseColor(str) {
  if (!str) return { L: 0, C: 0, H: 0, a: 1 };
  str = str.trim();

  // oklch(L C H) or oklch(L C H / a)
  const oklchMatch = str.match(
    /^oklch\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*(?:\/\s*([\d.]+))?\s*\)$/
  );
  if (oklchMatch) {
    return {
      L: parseFloat(oklchMatch[1]),
      C: parseFloat(oklchMatch[2]),
      H: parseFloat(oklchMatch[3]),
      a: oklchMatch[4] !== undefined ? parseFloat(oklchMatch[4]) : 1,
    };
  }

  // Hex: #rgb, #rrggbb, #rgba, #rrggbbaa
  const hexMatch = str.match(/^#([0-9a-f]{3,8})$/i);
  if (hexMatch) {
    const { r, g, b, a } = hexToSrgb(str);
    const lch = srgbToOklch(r, g, b);
    return { ...lch, a };
  }

  // rgb(r,g,b) or rgba(r,g,b,a)
  const rgbMatch = str.match(
    /^rgba?\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)\s*(?:[,/]\s*([\d.]+))?\s*\)$/
  );
  if (rgbMatch) {
    const lch = srgbToOklch(+rgbMatch[1], +rgbMatch[2], +rgbMatch[3]);
    return { ...lch, a: rgbMatch[4] !== undefined ? parseFloat(rgbMatch[4]) : 1 };
  }

  // hsl(h,s%,l%) or hsla(h,s%,l%,a)
  const hslMatch = str.match(
    /^hsla?\(\s*([\d.]+)\s*[,\s]\s*([\d.]+)%?\s*[,\s]\s*([\d.]+)%?\s*(?:[,/]\s*([\d.]+))?\s*\)$/
  );
  if (hslMatch) {
    const rgb = hslToSrgb(+hslMatch[1], +hslMatch[2], +hslMatch[3]);
    const lch = srgbToOklch(rgb.r, rgb.g, rgb.b);
    return { ...lch, a: hslMatch[4] !== undefined ? parseFloat(hslMatch[4]) : 1 };
  }

  return { L: 0, C: 0, H: 0, a: 1 };
}

// ── Format OKLCH → any CSS color string ──

function formatColor(L, C, H, a, fmt) {
  const round = (v, d = 3) => +v.toFixed(d);

  if (fmt === 'oklch') {
    const base = `oklch(${round(L)} ${round(C)} ${round(H, 2)})`;
    return a < 1 ? `oklch(${round(L)} ${round(C)} ${round(H, 2)} / ${round(a, 2)})` : base;
  }

  const mapped = gamutMap(L, C, H);
  const rgb = oklchToSrgb(mapped.L, mapped.C, mapped.H);

  if (fmt === 'hex') {
    return a < 1
      ? `rgba(${rgb.r},${rgb.g},${rgb.b},${round(a, 2)})`
      : srgbToHex(rgb.r, rgb.g, rgb.b);
  }

  if (fmt === 'rgb') {
    return a < 1
      ? `rgba(${rgb.r},${rgb.g},${rgb.b},${round(a, 2)})`
      : `rgb(${rgb.r},${rgb.g},${rgb.b})`;
  }

  if (fmt === 'hsl') {
    const hsl = srgbToHsl(rgb.r, rgb.g, rgb.b);
    return a < 1
      ? `hsla(${Math.round(hsl.h)},${Math.round(hsl.s)}%,${Math.round(hsl.l)}%,${round(a, 2)})`
      : `hsl(${Math.round(hsl.h)},${Math.round(hsl.s)}%,${Math.round(hsl.l)}%)`;
  }

  // Default to oklch
  return formatColor(L, C, H, a, 'oklch');
}
return { oklchToOklab, oklabToOklch, oklabToLinearRgb, linearRgbToOklab, linearToSrgb, srgbToLinear, oklchToSrgb, srgbToOklch, isInGamut, gamutMap, maxChroma, hexToSrgb, srgbToHex, parseColor, formatColor };
})();

const __m89 = (() => {
const asFormField = __m66.asFormField;
const srgbToOklch = __m88.srgbToOklch;
const oklchToSrgb = __m88.oklchToSrgb;
const isInGamut = __m88.isInGamut;
const gamutMap = __m88.gamutMap;
const maxChroma = __m88.maxChroma;
const parseColor = __m88.parseColor;
const formatColor = __m88.formatColor;
const srgbToHex = __m88.srgbToHex;
// ── s-clrp-preset ── Single preset swatch — item renderer for s-clrp-presets
const clrpPreset = {
  tag: 's-clrp-preset',
  // The swatch KNOWS its colour, so it announces it on click rather than
  // parking it in a dataset for the grid to read back off the element.
  emits: {},
  callbacks: {
    connected() {
      this.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('swatchpick', {
          bubbles: true,
          detail: { value: this._color || '' },
        }));
      });
    },
  },
  methods: {
    get data() { return this._data; },
    set data(v) {
      this._data = v;
      const color = typeof v === 'string' ? v : (v && v.color) || '';
      this._color = color;
      this.style.setProperty('--clrp-preset-bg', color);
    },
  },
};

// ── s-clrp-area ── L×C canvas + cursor, owns drag for lightness/chroma
const clrpArea = {
  tag: 's-clrp-area',
  children: [
    ['canvas', ':ref=canvas', '@width=260', '@height=150'],
    ['s-cn.clrp-cursor'],
  ],
  callbacks: {
    connected() {
      this._ctx = this.refs.canvas.getContext('2d');
      this._cScale = 0.3;

      this.addEventListener('pointerdown', (e) => {
        this.setPointerCapture(e.pointerId);
        this._update(e);
      });
      this.addEventListener('pointermove', (e) => {
        if (!this.hasPointerCapture(e.pointerId)) return;
        this._update(e);
      });
      this.addEventListener('pointerup', (e) => {
        this.releasePointerCapture(e.pointerId);
      });
    },
  },
  methods: {
    renderCanvas(L, C, H) {
      const ctx = this._ctx;
      const canvas = this.refs.canvas;
      const { width: W, height: Hpx } = canvas;
      const imgData = ctx.createImageData(W, Hpx);
      const data = imgData.data;
      const cMax = Math.max(maxChroma(0.5, H), 0.01);
      this._cScale = cMax * 1.1;

      for (let y = 0; y < Hpx; y++) {
        const l = 1 - y / (Hpx - 1);
        for (let x = 0; x < W; x++) {
          const c = (x / (W - 1)) * this._cScale;
          const idx = (y * W + x) * 4;
          if (isInGamut(l, c, H)) {
            const rgb = oklchToSrgb(l, c, H);
            data[idx] = rgb.r;
            data[idx + 1] = rgb.g;
            data[idx + 2] = rgb.b;
            data[idx + 3] = 255;
          } else {
            data[idx + 3] = 0;
          }
        }
      }
      ctx.putImageData(imgData, 0, 0);
    },

    _update(e) {
      const rect = this.refs.canvas.getBoundingClientRect();
      const xRatio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const yRatio = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
      let L = 1 - yRatio;
      let C = xRatio * this._cScale;

      // Read current hue from inherited CSS var (parent sets --clrp-hue on self, cascades down)
      const H = parseFloat(getComputedStyle(this).getPropertyValue('--clrp-hue')) || 0;

      if (!isInGamut(L, C, H)) {
        const mapped = gamutMap(L, C, H);
        C = mapped.C;
      }

      this.dispatchEvent(new CustomEvent('area-change', {
        bubbles: true,
        detail: { L, C },
      }));
    },
  },
};

// ── s-clrp-hue ── Hue strip + handle, pure CSS gradient background
const clrpHue = {
  tag: 's-clrp-hue',
  children: [
    ['s-cn.clrp-hue-handle'],
  ],
  callbacks: {
    connected() {
      this.addEventListener('pointerdown', (e) => {
        this.setPointerCapture(e.pointerId);
        this._update(e);
      });
      this.addEventListener('pointermove', (e) => {
        if (!this.hasPointerCapture(e.pointerId)) return;
        this._update(e);
      });
      this.addEventListener('pointerup', (e) => {
        this.releasePointerCapture(e.pointerId);
      });
    },
  },
  methods: {
    _update(e) {
      const rect = this.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      this.dispatchEvent(new CustomEvent('hue-change', {
        bubbles: true,
        detail: { H: x * 360 },
      }));
    },
  },
};

// ── s-clrp-alpha ── Alpha strip + handle, checkerboard + gradient
const clrpAlpha = {
  tag: 's-clrp-alpha',
  children: [
    ['s-cn.clrp-alpha-handle'],
  ],
  callbacks: {
    connected() {
      this.addEventListener('pointerdown', (e) => {
        this.setPointerCapture(e.pointerId);
        this._update(e);
      });
      this.addEventListener('pointermove', (e) => {
        if (!this.hasPointerCapture(e.pointerId)) return;
        this._update(e);
      });
      this.addEventListener('pointerup', (e) => {
        this.releasePointerCapture(e.pointerId);
      });
    },
  },
  methods: {
    _update(e) {
      const rect = this.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      this.dispatchEvent(new CustomEvent('alpha-change', {
        bubbles: true,
        detail: { a: Math.round(x * 100) / 100 },
      }));
    },
  },
};

// ── s-clrp-presets ── Preset color grid, data-driven via itemRenderer
const clrpPresets = {
  tag: 's-clrp-presets',
  itemRenderer: 's-clrp-preset',
  callbacks: {
    connected() {
      // A swatch announces its own colour (see s-clrp-preset), and the grid
      // just forwards it — nothing here reads a colour back out of the DOM.
      this.addEventListener('swatchpick', (e) => {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('preset-select', {
          bubbles: true,
          detail: { value: e.detail.value },
        }));
      });
    },
  },
  methods: {
    get presets() { return this._presets || []; },
    set presets(colors) {
      this._presets = Array.isArray(colors) ? colors : [];
      this.data = this._presets;
    },
  },
};

// ── s-clrp ── Parent orchestrator — owns OKLCH state, delegates all sub-component events
const colorPicker = asFormField({
  tags: ['s-clrp', 's-clrp'],
  dispatches: ['change'],
  props: ['@value', '@open', '@format', '@presets', '@disabled'],
  storeAttrs: ['value', 'open'],
  floatingPanel: 1,
  children: [
    ['s-cn.clrp-swatch', ':ref=swatch'],
    ['s-cn.clrp-panel', ':ref=panel', [
      ['s-clrp-area', ':ref=area'],
      ['s-clrp-hue', ':ref=hueStrip'],
      ['s-clrp-alpha', ':ref=alphaStrip'],
      ['s-i', ':ref=textInput', '@type=text'],
      ['s-clrp-presets', ':ref=presets'],
    ]],
  ],
  callbacks: {
    connected() {
      this._L = 0.623;
      this._C = 0.188;
      this._H = 260;
      this._a = 1;

      // Parse initial value from backing field (set by framework from attribute)
      const initVal = this._value || '#1677ff';
      const parsed = parseColor(initVal);
      this._L = parsed.L;
      this._C = parsed.C;
      this._H = parsed.H;
      this._a = parsed.a;

      // Populate presets
      this._applyPresets();

      // Initial canvas render and UI sync — defer until sub-components are upgraded
      requestAnimationFrame(() => {
        if (this.refs.area?.renderCanvas) {
          this.refs.area.renderCanvas(this._L, this._C, this._H);
        }
        this._updateUI();
      });

      // Swatch click — delegation on self, target swatch by ref
      this.addEventListener('click', (e) => {
        if (!this.refs.swatch.contains(e.target)) return;
        if (this.disabled) return;
        this._togglePanel();
      });

      // Sub-component events bubble up — handle via delegation on self
      this.addEventListener('area-change', (e) => {
        this._L = e.detail.L;
        this._C = e.detail.C;
        this._updateUI();
        this._fireChange();
      });

      this.addEventListener('hue-change', (e) => {
        this._H = e.detail.H;
        if (this.refs.area?.renderCanvas) this.refs.area.renderCanvas(this._L, this._C, this._H);
        this._updateUI();
        this._fireChange();
      });

      this.addEventListener('alpha-change', (e) => {
        this._a = e.detail.a;
        this._updateUI();
        this._fireChange();
      });

      this.addEventListener('preset-select', (e) => {
        const col = parseColor(e.detail.value);
        this._L = col.L;
        this._C = col.C;
        this._H = col.H;
        this._a = col.a;
        if (this.refs.area?.renderCanvas) this.refs.area.renderCanvas(this._L, this._C, this._H);
        this._updateUI();
        this._fireChange();
      });

      // Text input change — delegation on self, guard by element identity
      this.addEventListener('change', (e) => {
        if (e.target !== this.refs.textInput) return;
        const col = parseColor(this.refs.textInput.value.trim());
        this._L = col.L;
        this._C = col.C;
        this._H = col.H;
        this._a = col.a;
        if (this.refs.area?.renderCanvas) this.refs.area.renderCanvas(this._L, this._C, this._H);
        this._updateUI();
        this._fireChange();
      });
    },
  },
  methods: {
    get value() {
      return formatColor(this._L, this._C, this._H, this._a, this._format || 'oklch');
    },
    set value(v) {
      const col = parseColor(v);
      this._L = col.L;
      this._C = col.C;
      this._H = col.H;
      this._a = col.a;
      this._value = v;
      if (this.refs.area) {
        if (this.refs.area?.renderCanvas) this.refs.area.renderCanvas(this._L, this._C, this._H);
        this._updateUI();
      }
    },

    get format() { return this._format || 'oklch'; },
    set format(v) {
      this._format = v || 'oklch';
      if (this.refs.textInput) this._updateUI();
    },

    get presets() { return this._presets || ''; },
    set presets(v) {
      this._presets = v || '';
      this._applyPresets();
    },

    _applyPresets() {
      if (!this.refs.presets) return;
      const raw = this._presets || '';
      const colors = raw
        ? raw.split(',').map(s => s.trim()).filter(Boolean)
        : ['#f5222d', '#fa8c16', '#fadb14', '#52c41a', '#13c2c2', '#1677ff', '#722ed1', '#eb2f96', '#000000', '#ffffff'];
      this.refs.presets.presets = colors;
    },

    _updateUI() {
      const mapped = gamutMap(this._L, this._C, this._H);
      const rgb = oklchToSrgb(mapped.L, mapped.C, mapped.H);
      const hex = srgbToHex(rgb.r, rgb.g, rgb.b);
      const colorStr = this._a < 1
        ? `rgba(${rgb.r},${rgb.g},${rgb.b},${this._a})`
        : hex;

      // CSS vars set on self only — cascade to children for handle/cursor positioning
      this.style.setProperty('--clrp-color', colorStr);
      this.style.setProperty('--clrp-hue', this._H);

      const cScale = (this.refs.area && this.refs.area._cScale) || 0.3;
      const xPct = (this._C / cScale) * 100;
      const yPct = (1 - this._L) * 100;
      this.style.setProperty('--clrp-area-x', Math.min(100, Math.max(0, xPct)) + '%');
      this.style.setProperty('--clrp-area-y', Math.min(100, Math.max(0, yPct)) + '%');
      this.style.setProperty('--clrp-hue-pos', (this._H / 360 * 100) + '%');
      this.style.setProperty('--clrp-alpha-pos', (this._a * 100) + '%');
      this.style.setProperty('--clrp-hex', hex);

      if (this.refs.textInput) this.refs.textInput.value = this.value;
    },

    _fireChange() {
      const val = this.value;
      this._value = val;
      this.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        detail: { value: val },
      }));
    },

    onFormatChange(v) {
      this._format = v || 'oklch';
      if (this.refs.textInput) this._updateUI();
    },

    onPresetsChange(v) {
      this._presets = v || '';
      this._applyPresets();
    },
  },
}, { changeEvents: ['change', 'preset-select'] });

const colorPickerComponents = [clrpPreset, clrpArea, clrpHue, clrpAlpha, clrpPresets, colorPicker];
const __default = [clrpPreset, clrpArea, clrpHue, clrpAlpha, clrpPresets, colorPicker];
return { clrpPreset, clrpArea, clrpHue, clrpAlpha, clrpPresets, colorPickerComponents, __default };
})();

const __m90 = (() => {
const asFormField = __m66.asFormField;
const __default = asFormField({
    tags: ['s-dp', 's-dp'],
    dispatches: ['change', 'valueChanged'],
    props: ['@value', '@open', '@format', '@placeholder', '@disabled', '@min', '@max'],
    aria: { role: 'combobox', expanded: 'open', disabled: 'disabled', haspopup: '=dialog' },
    internalStore: true,
    storeAttrs: ['value', 'open'],
    floatingPanel: 1,
    children: [
        ['s-cn.dp-input', ':ref=inputWrap', [
            ['input', ':ref=input', '@type=text', '@readonly=', '@placeholder=YYYY-MM-DD'],
            ['s-ic', '@n=cal', '@sz=sm'],
            ['span.dp-clear', '="\u00D7"'],
        ]],
        ['s-cn.dp-panel', [
            ['s-cn.dp-header', [
                ['button.dp-nav', '@type=button', '~cl:call:_shiftMonth:-1', '="\u2039"'],
                ['span.dp-title', ':ref=title', ':dataKey=monthTitle'],
                ['button.dp-nav', '@type=button', '~cl:call:_shiftMonth:1', '="\u203A"'],
            ]],
            ['s-cn.dp-weekdays', [
                ['span', '="Su"'], ['span', '="Mo"'], ['span', '="Tu"'],
                ['span', '="We"'], ['span', '="Th"'], ['span', '="Fr"'], ['span', '="Sa"'],
            ]],
            ['s-cn.dp-grid', ':ref=grid', ':dataKey=days', ':dataField=children'],
        ]],
    ],
    callbacks: {
        connected() {
            this._viewDate = new Date();
            this._selected = null;

            this._readConfig();

            // Parse initial value
            const initVal = this.value;
            if (initVal) {
                const d = new Date(initVal);
                if (!isNaN(d.getTime())) {
                    this._selected = d;
                    this._viewDate = new Date(d);
                }
            }

            const input = this.refs.input;
            input.placeholder = this.placeholder || this._format;
            if (this._selected) input.value = this._formatDate(this._selected);

            this._buildCalendar();

            // Click delegation on self
            this.addEventListener('click', (e) => {
                // Clear button
                if (e.target.closest('.dp-clear')) {
                    this._clearValue();
                    return;
                }
                // Input area click -> toggle open
                if (this.refs.inputWrap.contains(e.target)) {
                    if (this.disabled) return;
                    this._togglePanel();
                    return;
                }
                // The month arrows and every day cell carry what they mean.
            });
        },
    },
    methods: {
        _shiftMonth(delta) {
            this._viewDate.setMonth(this._viewDate.getMonth() + Number(delta));
            this._buildCalendar();
        },
        _pickDay(ts) {
            this._selectDate(new Date(Number(ts)));
        },
        get value() { return this._value || ''; },
        set value(v) {
            if (!v) { this._clearValue(); return; }
            const d = new Date(v);
            if (!isNaN(d.getTime())) {
                this._selected = d;
                this._viewDate = new Date(d);
                this.setAttribute('value', this._toISODate(d));
                if (this.refs.input) {
                    this.refs.input.value = this._formatDate(d);
                    this._buildCalendar();
                }
            }
        },

        get format() { return this._format || 'YYYY-MM-DD'; },
        set format(v) {
            this.setAttribute('format', v || 'YYYY-MM-DD');
            this._readConfig();
            if (this.refs.input) {
                this.refs.input.placeholder = this._format;
                if (this._selected) this.refs.input.value = this._formatDate(this._selected);
            }
        },

        _readConfig() {
            this._format = this.format;
        },

        _buildCalendar() {
            if (!this._internalData) return;

            const y = this._viewDate.getFullYear();
            const m = this._viewDate.getMonth();
            this.data.monthTitle = `${this._viewDate.toLocaleString('default', { month: 'long' })} ${y}`;

            const minDate = this.min ? new Date(this.min) : null;
            const maxDate = this.max ? new Date(this.max) : null;
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const firstDay = new Date(y, m, 1).getDay();
            const daysInMonth = new Date(y, m + 1, 0).getDate();
            const prevDays = new Date(y, m, 0).getDate();

            const cells = [];

            // Previous month fill
            Array.from({ length: firstDay }, (_, i) => firstDay - 1 - i).forEach(i => {
                cells.push(['span.dp-day.dp-other', `="${prevDays - i}"`]);
            });

            // Current month
            Array.from({ length: daysInMonth }, (_, i) => i + 1).forEach(day => {
                const date = new Date(y, m, day);
                date.setHours(0, 0, 0, 0);
                const ts = date.getTime();

                // The cell carries its own timestamp as the value it selects.
                const attrs = [`~cl:call:_pickDay:${ts}`];
                const classes = ['dp-day'];

                if (ts === today.getTime()) classes.push('dp-today');
                if (this._selected) {
                    const sel = new Date(this._selected);
                    sel.setHours(0, 0, 0, 0);
                    if (ts === sel.getTime()) classes.push('dp-selected');
                }

                const isDisabled = (minDate && date < minDate) || (maxDate && date > maxDate);
                if (isDisabled) attrs.push('@disabled=');

                cells.push([`span.${classes.join('.')}`, ...attrs, `="${day}"`]);
            });

            // Next month fill
            const totalCells = firstDay + daysInMonth;
            const remaining = totalCells % 7 ? 7 - (totalCells % 7) : 0;
            Array.from({ length: remaining }, (_, i) => i + 1).forEach(i => {
                cells.push(['span.dp-day.dp-other', `="${i}"`]);
            });

            this.data.days = cells;
        },

        _selectDate(date) {
            this._selected = date;
            this.refs.input.value = this._formatDate(date);
            const isoVal = this._toISODate(date);
            this.setAttribute('value', isoVal);
            this._closePanel();
            this._buildCalendar();
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: isoVal, date },
            }));
            this.dispatchEvent(new CustomEvent('valueChanged', {
                bubbles: true,
                detail: { value: isoVal, name: this.getAttribute('name') },
            }));
        },

        _clearValue() {
            this._selected = null;
            if (this.refs.input) this.refs.input.value = '';
            this.setAttribute('value', '');
            this._buildCalendar();
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: '' },
            }));
        },

        _formatDate(d) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return this._format
                .replace('YYYY', yyyy)
                .replace('MM', mm)
                .replace('DD', dd);
        },

        _toISODate(d) {
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        },

        onFormatChange() {
            this._readConfig();
            if (this.refs.input && this._selected) this.refs.input.value = this._formatDate(this._selected);
        },
    },
});
return { __default };
})();

const __m91 = (() => {
const asFormField = __m66.asFormField;
const __default = asFormField({
    tags: ['s-tp', 's-tp'],
    dispatches: ['change'],
    props: ['@value', '@open', '@format', '@disabled', '@minuteStep', '@hourStep'],
    aria: { role: 'combobox', expanded: 'open', disabled: 'disabled', haspopup: '=listbox' },
    internalStore: true,
    storeAttrs: ['value', 'open'],
    floatingPanel: 1,
    children: [
        ['s-cn.tp-input', ':ref=inputWrap', [
            ['input', ':ref=input', '@type=text', '@readonly=', '@placeholder=HH:mm'],
            ['s-ic', '@n=ck', '@sz=sm'],
        ]],
        ['s-cn.tp-panel', [
            ['s-cn.tp-columns', ':ref=columns', ':dataKey=columnDefs', ':dataField=children'],
            ['s-cn.tp-footer', [
                ['s-b.tp-now', ':ref=nowBtn', '@sz=sm', '@v=gh', '="Now"'],
                ['s-b.tp-ok', ':ref=okBtn', '@sz=sm', '@c=pr', '="OK"'],
            ]],
        ]],
    ],
    callbacks: {
        connected() {
            this._hour = 0;
            this._minute = 0;
            this._second = 0;
            this._period = 'AM';

            this._readConfig();

            // Parse initial value
            if (this.value) this._parseTime(this.value);

            this._updateInputDisplay();
            this._buildColumns();

            // Click delegation on self
            this.addEventListener('click', (e) => {
                // Input wrap click -> toggle open
                if (this.refs.inputWrap.contains(e.target)) {
                    if (this.disabled) return;
                    this._togglePanel();
                    return;
                }

                // Cells carry their own column and value and handle themselves.

                // Now button
                if (this.refs.nowBtn.contains(e.target)) {
                    const now = new Date();
                    this._hour = this._is12h ? (now.getHours() % 12 || 12) : now.getHours();
                    this._minute = now.getMinutes();
                    this._second = now.getSeconds();
                    this._period = now.getHours() >= 12 ? 'PM' : 'AM';
                    this._highlightCurrent();
                    return;
                }

                // OK button
                if (this.refs.okBtn.contains(e.target)) {
                    this._confirm();
                }
            });
        },
    },
    methods: {
        get value() { return this._value || ''; },
        set value(v) {
            this._parseTime(v);
            this.setAttribute('value', v || '');
            this._updateInputDisplay();
            this._highlightCurrent();
        },

        get format() { return this._format || 'HH:mm'; },
        set format(v) {
            this.setAttribute('format', v || 'HH:mm');
            this._readConfig();
            this._buildColumns();
            this._updateInputDisplay();
        },

        // "<column>-<value>" — both halves are ours, stamped into the token.
        _pickCell(coord) {
            const raw = String(coord);
            const dash = raw.indexOf('-');
            const col = raw.slice(0, dash);
            const val = raw.slice(dash + 1);
            if (col === 'hour') this._hour = parseInt(val, 10);
            else if (col === 'minute') this._minute = parseInt(val, 10);
            else if (col === 'second') this._second = parseInt(val, 10);
            else if (col === 'period') this._period = val;
            this._buildColumns();
        },

        _readConfig() {
            this._fmt = this.format;
            this._hourStep = parseInt(this.hourStep, 10) || 1;
            this._minuteStep = parseInt(this.minuteStep, 10) || 1;
            this._is12h = this._fmt.includes('hh') || this._fmt.includes('A');
            this._hasSeconds = this._fmt.includes('ss');
            if (this.refs?.input) this.refs.input.placeholder = this._fmt;
        },

        _buildColumns() {
            if (!this._internalData) return;

            const defs = [];

            // Hour column
            const hourMax = this._is12h ? 13 : 24;
            const hourStart = this._is12h ? 1 : 0;
            const hourCells = [];
            Array.from({ length: Math.ceil((hourMax - hourStart) / this._hourStep) }, (_, idx) => hourStart + idx * this._hourStep)
                .forEach(i => {
                    hourCells.push(
                        ['span.tp-cell', `~cl:call:_pickCell:hour-${i}`,
                            ...(i === this._hour ? ['@selected='] : []),
                            `="${String(i).padStart(2, '0')}"`]
                    );
                });
            defs.push(['s-cn.tp-col', hourCells]);

            // Minute column
            const minCells = [];
            Array.from({ length: Math.ceil(60 / this._minuteStep) }, (_, idx) => idx * this._minuteStep)
                .forEach(i => {
                    minCells.push(
                        ['span.tp-cell', `~cl:call:_pickCell:minute-${i}`,
                            ...(i === this._minute ? ['@selected='] : []),
                            `="${String(i).padStart(2, '0')}"`]
                    );
                });
            defs.push(['s-cn.tp-col', minCells]);

            // Second column
            if (this._hasSeconds) {
                const secCells = [];
                Array.from({ length: 60 }, (_, i) => i).forEach(i => {
                    secCells.push(
                        ['span.tp-cell', `~cl:call:_pickCell:second-${i}`,
                            ...(i === this._second ? ['@selected='] : []),
                            `="${String(i).padStart(2, '0')}"`]
                    );
                });
                defs.push(['s-cn.tp-col', secCells]);
            }

            // AM/PM column
            if (this._is12h) {
                defs.push(['s-cn.tp-col', [
                    ['span.tp-cell', '~cl:call:_pickCell:period-AM',
                        ...(this._period === 'AM' ? ['@selected='] : []), '="AM"'],
                    ['span.tp-cell', '~cl:call:_pickCell:period-PM',
                        ...(this._period === 'PM' ? ['@selected='] : []), '="PM"'],
                ]]);
            }

            // Selection rides in the stamped defs above, so there is nothing to
            // re-apply after render — and scheduling _highlightCurrent here
            // would now recurse straight back into this method.
            this.data.columnDefs = defs;
        },

        // Selection is stamped from state, so "highlight" is just a rebuild —
        // no scan for the cell that matches, and no attribute juggling.
        _highlightCurrent() {
            if (this.refs?.columns) this._buildColumns();
        },

        _confirm() {
            this._updateInputDisplay();
            const val = this.refs.input.value;
            this.setAttribute('value', val);
            this._closePanel();
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: val, hour: this._hour, minute: this._minute, second: this._second },
            }));
        },

        _updateInputDisplay() {
            if (!this.refs?.input) return;
            const hh = String(this._hour).padStart(2, '0');
            const mm = String(this._minute).padStart(2, '0');
            const ss = String(this._second).padStart(2, '0');
            let str = this._fmt
                .replace('HH', hh).replace('hh', hh)
                .replace('mm', mm)
                .replace('ss', ss);
            if (this._is12h) str = str.replace('A', this._period);
            if (this.value || this._hour || this._minute || this._second) {
                this.refs.input.value = str;
            }
        },

        _parseTime(str) {
            if (!str) return;
            const clean = str.replace(/\s*(AM|PM)\s*/i, (_, p) => { this._period = p.toUpperCase(); return ''; });
            const segs = clean.split(':').map(Number);
            if (segs.length >= 2) {
                this._hour = segs[0] || 0;
                this._minute = segs[1] || 0;
                this._second = segs[2] || 0;
            }
        },

        onFormatChange() { this._readConfig(); this._buildColumns(); this._updateInputDisplay(); },
        onMinuteStepChange() { this._readConfig(); this._buildColumns(); },
        onHourStepChange() { this._readConfig(); this._buildColumns(); },
    },
}, { changeEvents: ['change'] });
return { __default };
})();

const __m92 = (() => {
// Transfer (s-xfr): dual-list "shuttle" control, fully data-driven.
//
// Design: two framework list components (s-xfr-list) drive their rows from a
// data array through an itemRenderer (s-xfr-item). Per-item selection flows
// through the selection-mixin (each item carries a `selected` prop and the list
// tracks `this.items`), so there is no checkbox to query. Filtering is a
// per-item `?data:_match` show-if: the controller writes each row's `_match`
// flag into the item's own internal store and the row mounts/unmounts itself,
// so nothing is shown or hidden imperatively. Item key and label ride along as
// item data, so no identifier attributes are needed.
//
// The checkbox glyphs are two stacked s-ic icons; a `[selected]` attribute (set
// by the selection-mixin on the item, or by the controller on the header check)
// decides which one shows, entirely through the scoped CSS below. This keeps
// the visual reactive without querying or toggling any child.

// ── Header select-all check: reflects its own `selected` prop ──
const transferCheck = {
    tags: ['s-xfr-check', 's-xfr-check'],
    props: ['@selected'],
    css: `
        s-xfr-check { display: inline-flex; cursor: pointer; flex-shrink: 0; }
        s-xfr-check s-ic[when=on] { display: none; }
        s-xfr-check s-ic[when=off] { display: inline-flex; }
        s-xfr-check[selected] s-ic[when=on] { display: inline-flex; }
        s-xfr-check[selected] s-ic[when=off] { display: none; }
    `,
    children: [
        ['s-ic', '@when=on', '@n=square-check'],
        ['s-ic', '@when=off', '@n=square'],
    ],
};

// ── Item renderer: one selectable row (checkbox glyph + label) ──
// The row wraps in ?data:_match so it removes itself when the search query
// excludes it. Clicking anywhere on the row emits 'selected', which the parent
// list's selection-mixin turns into a selection toggle; the resulting
// [selected] attribute on this element drives the checkbox glyph via CSS.
const transferItem = {
    tags: ['s-xfr-item', 's-xfr-item'],
    aria: { role: 'option', selected: 'selected' },
    props: ['@selected'],
    css: `
        s-xfr-item { display: block; }
        s-xfr-item s-ic[when=on] { display: none; }
        s-xfr-item s-ic[when=off] { display: inline-flex; }
        s-xfr-item[selected] s-ic[when=on] { display: inline-flex; }
        s-xfr-item[selected] s-ic[when=off] { display: none; }
    `,
    children: [
        ['s-cn.xfr-item', '?data:_match', [
            ['s-ic', '@when=on', '@n=square-check'],
            ['s-ic', '@when=off', '@n=square'],
            ['s-l', '$label'],
        ]],
    ],
    emits: { click: 'selected' },
};

// ── List: data-driven, multi-select via the selection-mixin ──
const transferList = {
    tags: ['s-xfr-list', 's-xfr-list'],
    // A dual-listbox half: multi-select is the whole point of a transfer.
    aria: { role: 'listbox', itemRole: 'option', multiselectable: '=true' },
    itemRenderer: 's-xfr-item',
    selectable: true,
    css: `
        s-xfr-list { display: block; }
    `,
};

// ── Main component: two panels, search, select-all, move buttons ──
const transfer = {
    tags: ['s-xfr', 's-xfr'],
    dispatches: ['change'],
    props: ['@disabled'],
    internalStore: true,
    children: [
        ['s-cn.xfr-container', [
            ['s-cn.xfr-panel', [
                ['s-cn.xfr-header', [
                    ['s-xfr-check', ':rf=sourceCheckAll'],
                    ['s-l.xfr-count', ':rf=sourceCount', ':dk=sourceCountText'],
                ]],
                ['s-i.xfr-search', '@type=text', '@placeholder=Search...', '$sourceQuery'],
                ['s-xfr-list.xfr-list', ':rf=sourceList', '@multi-select='],
            ]],
            ['s-cn.xfr-actions', [
                ['button', '@type=button', ':rf=toRight', '@disabled=', [['s-ic', '@n=chevron-right']]],
                ['button', '@type=button', ':rf=toLeft', '@disabled=', [['s-ic', '@n=chevron-left']]],
            ]],
            ['s-cn.xfr-panel', [
                ['s-cn.xfr-header', [
                    ['s-xfr-check', ':rf=targetCheckAll'],
                    ['s-l.xfr-count', ':rf=targetCount', ':dk=targetCountText'],
                ]],
                ['s-i.xfr-search', '@type=text', '@placeholder=Search...', '$targetQuery'],
                ['s-xfr-list.xfr-list', ':rf=targetList', '@multi-select='],
            ]],
        ]],
    ],
    callbacks: {
        connected() {
            // Idempotent state init. The demo wrapper can push data into the
            // `data` setter (before or after this runs), so only seed what is
            // absent and never clobber items the setter has populated.
            this._sourceItems ??= [];
            this._targetItems ??= [];
            this._query ??= { source: '', target: '' };

            // Search boxes bind two-way to internal store keys; react to changes
            // declaratively rather than listening on the input elements.
            this.subscribeData('sourceQuery', (v) => this._applyFilter('source', v || ''));
            this.subscribeData('targetQuery', (v) => this._applyFilter('target', v || ''));

            // Event delegation on self — never on the child controls.
            this.addEventListener('click', (e) => this._onClick(e));
            this.addEventListener('selected', (e) => this._onSelected(e));

            this._renderBoth();
        },
    },
    methods: {
        _matchText(label, q) {
            if (!q) return true;
            return String(label ?? '').toLowerCase().includes(String(q).toLowerCase());
        },

        _renderSide(side) {
            const list = side === 'source' ? this.refs.sourceList : this.refs.targetList;
            if (!list) return;
            const items = side === 'source' ? this._sourceItems : this._targetItems;
            const q = this._query[side];
            list.data = items.map((it) => ({ ...it, _match: this._matchText(it.label, q) }));
        },

        _renderBoth() {
            this._renderSide('source');
            this._renderSide('target');
            this._refresh('source');
            this._refresh('target');
        },

        _selectedEls(side) {
            const list = side === 'source' ? this.refs.sourceList : this.refs.targetList;
            // Selection-mixin state: registered item elements carrying `selected`.
            return (list?.items || []).filter((el) => el.selected);
        },

        _refresh(side) {
            const list = side === 'source' ? this.refs.sourceList : this.refs.targetList;
            const total = list?.items?.length || 0;
            const selected = this._selectedEls(side).length;

            const countKey = side === 'source' ? 'sourceCountText' : 'targetCountText';
            this.setData(countKey, `${selected}/${total}`);

            const moveBtn = side === 'source' ? this.refs.toRight : this.refs.toLeft;
            if (moveBtn) moveBtn.disabled = selected === 0;

            const checkAll = side === 'source' ? this.refs.sourceCheckAll : this.refs.targetCheckAll;
            if (checkAll) checkAll.selected = total > 0 && selected === total;
        },

        _applyFilter(side, q) {
            this._query[side] = q;
            const list = side === 'source' ? this.refs.sourceList : this.refs.targetList;
            // The controller sets each row's per-item match flag; the row shows or
            // hides itself reactively through its ?data:_match binding.
            (list?.items || []).forEach((el) => el.setData('_match', this._matchText(el._data?.label, q)));
        },

        _onClick(e) {
            if (this.disabled) return;
            if (this.refs.sourceCheckAll?.contains(e.target)) {
                this._toggleAll('source');
                return;
            }
            if (this.refs.targetCheckAll?.contains(e.target)) {
                this._toggleAll('target');
                return;
            }
            if (this.refs.toRight?.contains(e.target)) {
                this._moveItems('source', 'target');
                return;
            }
            if (this.refs.toLeft?.contains(e.target)) {
                this._moveItems('target', 'source');
            }
        },

        _onSelected(e) {
            if (this.disabled) return;
            if (this.refs.sourceList?.contains(e.target)) {
                this._refresh('source');
                return;
            }
            if (this.refs.targetList?.contains(e.target)) {
                this._refresh('target');
            }
        },

        _toggleAll(side) {
            const list = side === 'source' ? this.refs.sourceList : this.refs.targetList;
            const items = list?.items || [];
            const allSelected = items.length > 0 && this._selectedEls(side).length === items.length;
            // Drive selection through the mixin's setter so its tracked set and
            // each item's `selected` prop stay in sync.
            list.selectedItems = allSelected ? [] : items;
            this._refresh(side);
        },

        _moveItems(from, to) {
            const movingKeys = new Set(this._selectedEls(from).map((el) => el._data?.key));
            if (movingKeys.size === 0) return;

            const fromArr = from === 'source' ? this._sourceItems : this._targetItems;
            const moving = fromArr.filter((it) => movingKeys.has(it.key));
            const remaining = fromArr.filter((it) => !movingKeys.has(it.key));

            if (from === 'source') {
                this._sourceItems = remaining;
                this._targetItems = [...this._targetItems, ...moving];
            } else {
                this._targetItems = remaining;
                this._sourceItems = [...this._sourceItems, ...moving];
            }

            // Fresh render drops selection on both sides (new item elements start
            // unselected), so moved rows arrive cleared.
            this._renderBoth();

            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { targetKeys: this._targetItems.map((i) => i.key) },
            }));
        },

        set data(items) {
            if (!Array.isArray(items)) return;
            this._sourceItems = items.map((i) => ({
                key: i.key || i.value || String(i),
                label: i.label || i.title || i.key || String(i),
                ...i,
            }));
            // Fresh data resets both panels and selection state.
            this._targetItems = [];
            this._query = { source: '', target: '' };
            this._renderBoth();
        },

        get targetKeys() {
            return this._targetItems.map((i) => i.key);
        },
    },
};

const __default = [transferCheck, transferItem, transferList, transfer];
return { __default };
})();

const __m93 = (() => {
const asFormField = __m66.asFormField;
const middleTruncate = __m9.middleTruncate;
const isPanelOpen = __m23.isPanelOpen;
let cascaderSeq = 0;

const __default = asFormField({
    tags: ['s-csc', 's-csc'],
    dispatches: ['change', 'valueChanged'],
    props: ['@value', '@open', '@placeholder', '@disabled', '@separator'],
    aria: { role: 'combobox', expanded: 'open', disabled: 'disabled', haspopup: '=tree' },
    internalStore: true,
    storeAttrs: ['value', 'open'],
    floatingPanel: 1,
    children: [
        ['s-cn.csc-display', ':ref=display', '@tabindex=0', [
            ['span.csc-text', ':ref=displayText', ':dataKey=displayLabel'],
        ]],
        ['s-cn.csc-panel', ':ref=panel', ':dataKey=columns', ':dataField=children'],
    ],
    callbacks: {
        connected() {
            // A bound :options= / :value= reaches the setters BEFORE this runs,
            // so these seeds only apply when nothing has arrived yet — an
            // unconditional reset left the panel with no tree to open.
            this._options = this._options || [];
            this._selectedPath = this._selectedPath || [];
            this._activePath = this._activePath || [];
            this._separator = this.separator || ' / ';

            this.data.displayLabel = this.placeholder || 'Select...';

            // Click delegation on self
            this.addEventListener('click', (e) => {
                // Display toggle
                if (this.refs.display.contains(e.target)) {
                    if (this.disabled) return;
                    this._togglePanel();
                    return;
                }

            });

            // Keyboard contract: the display keeps focus, the highlighted
            // option is announced via aria-activedescendant, and navigation
            // runs on the options tree (object state), never the stamped DOM.
            this.addEventListener('keydown', (e) => {
                if (this.disabled) return;
                // Read the popover state synchronously — the open prop lags a
                // frame behind togglePopover() (native toggle event). isPanelOpen
                // also keeps `:popover-open` away from engines it would throw on.
                const isOpen = this._panel ? isPanelOpen(this._panel) : this.open;
                if (!isOpen) {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        this._togglePanel();
                        // onOpenChange re-runs this on the async toggle event;
                        // doing it here too gives the next keystroke a cursor.
                        this._initPanelState();
                    }
                    return;
                }
                const col = this._kb?.col ?? 0;
                const opts = this._columnOptions(col);
                const row = this._kb?.row ?? -1;
                switch (e.key) {
                    case 'ArrowDown': e.preventDefault(); this._kbMove(col, Math.min(opts.length - 1, row + 1)); break;
                    case 'ArrowUp': e.preventDefault(); this._kbMove(col, Math.max(0, row - 1)); break;
                    case 'ArrowRight': {
                        e.preventDefault();
                        const opt = opts[row];
                        if (opt && opt.children && opt.children.length) this._choose(opt.value, col, { keepOpen: true });
                        break;
                    }
                    case 'ArrowLeft':
                        e.preventDefault();
                        if (col > 0) {
                            this._activePath = this._activePath.slice(0, col - 1 + 1);
                            const parentOpts = this._columnOptions(col - 1);
                            this._kbMove(col - 1, Math.max(0, parentOpts.findIndex((o) => o.value === this._activePath[col - 1])));
                        }
                        break;
                    case 'Enter': case ' ': {
                        e.preventDefault();
                        const opt = opts[row];
                        if (opt) this._choose(opt.value, col);
                        break;
                    }
                    case 'Escape':
                        e.preventDefault();
                        this._closePanel();
                        this.refs.display.focus();
                        break;
                }
            });
        },
    },
    methods: {
        onOpenChange(isOpen) {
            // The popover's toggle event arrives a frame late; if the opening
            // keystroke already initialized (and the user kept typing), this
            // late init must NOT wipe their navigation.
            if (isOpen) { if (!this._panelInited) this._initPanelState(); }
            else this._panelInited = false;
        },
        _initPanelState() {
            this._panelInited = true;
            this._activePath = [...this._selectedPath];
            this._kb = { col: 0, row: Math.max(0, this._columnOptions(0).findIndex((o) => o.value === this._activePath[0])) };
            this._renderColumns();
        },
        // The options visible in a column: the root list for column 0, then
        // the children of each _activePath ancestor.
        _columnOptions(level) {
            let current = this._options;
            for (let i = 0; i < level; i++) {
                const found = current.find((o) => o.value === this._activePath[i]);
                if (!found || !found.children) return [];
                current = found.children;
            }
            return current;
        },
        _kbMove(col, row) {
            this._kb = { col, row };
            this._renderColumns();
        },
        // Shared select/descend path for click and keyboard.
        _choose(val, level, { keepOpen } = {}) {
            this._activePath = this._activePath.slice(0, level);
            this._activePath.push(val);
            const node = this._findNode(val, level);
            if (node && node.children && node.children.length) {
                this._kb = { col: level + 1, row: 0 };
                this._renderColumns();
            } else if (keepOpen) {
                this._renderColumns();
            } else {
                this._selectedPath = [...this._activePath];
                this._updateDisplay();
                this._closePanel();
                const pathValues = [...this._selectedPath];
                this.setAttribute('value', pathValues.join(','));
                this.dispatchEvent(new CustomEvent('change', {
                    bubbles: true,
                    detail: { value: pathValues, labels: this._getLabelsForPath(pathValues) },
                }));
                this.dispatchEvent(new CustomEvent('valueChanged', {
                    bubbles: true,
                    detail: { value: pathValues.join(','), name: this._name || this.getAttribute('name') },
                }));
            }
        },
        _findNode(val, level) {
            let current = this._options;
            Array.from({ length: level }).forEach((_, i) => {
                const found = current.find(o => o.value === this._activePath[i]);
                if (!found || !found.children) { current = []; return; }
                current = found.children;
            });
            return current.find(o => o.value === val) || null;
        },
        _renderColumns() {
            if (!this._internalData) return;
            const columnDefs = [];
            let currentOptions = this._options;

            columnDefs.push(this._buildColumnDef(currentOptions, 0));

            this._activePath.forEach((activeVal, i) => {
                const found = currentOptions.find(o => o.value === activeVal);
                if (found && found.children && found.children.length) {
                    currentOptions = found.children;
                    columnDefs.push(this._buildColumnDef(currentOptions, i + 1));
                }
            });

            this.data.columns = columnDefs;
        },
        _buildColumnDef(items, level) {
            const kb = this._kb || {};
            // Remember the column's model so a cell can be identified by its
            // position rather than by a value written into the markup.
            (this._cols || (this._cols = []))[level] = items;
            const cells = items.map((item, row) => {
                const label = item.label || item.value;
                const hasChildren = item.children && item.children.length;
                const isActive = this._activePath[level] === item.value;
                const isKbActive = kb.col === level && kb.row === row;
                return [`s-cn.csc-item${isKbActive ? '.csc-kb-active' : ''}`,
                    `@id=${this._cellId(level, row)}`,
                    '@role=option',
                    `@aria-selected=${isActive}`,
                    ...(hasChildren ? ['@aria-expanded=' + String(isActive)] : []),
                    `~cl:call:_chooseAt:${level}-${row}`,
                    ...(hasChildren ? ['@data-has-children='] : []),
                    ...(isActive ? ['@data-active='] : []),
                    `="${label}"`,
                ];
            });

            if (kb.col === level && kb.row >= 0 && cells[kb.row]) {
                this.refs.display.setAttribute('aria-activedescendant', this._cellId(level, kb.row));
            }
            return ['s-cn.csc-col', '@role=listbox', cells];
        },
        // "<level>-<row>" — both are integers this component stamped itself, so
        // the coordinate is our own and never has to be read back off an element.
        _chooseAt(coord) {
            const [level, row] = String(coord).split('-').map(Number);
            const item = (this._cols || [])[level] && this._cols[level][row];
            if (item) this._choose(item.value, level);
        },
        _cellId(level, row) {
            if (!this._cellIdBase) this._cellIdBase = `csc${++cascaderSeq}`;
            return `${this._cellIdBase}-c${level}r${row}`;
        },
        _updateDisplay() {
            if (this._selectedPath.length === 0) {
                this.data.displayLabel = this.placeholder || 'Select...';
                return;
            }
            // Same elision the tree select uses, so a long path reads the same
            // in both: the tail of a path is what distinguishes it.
            const labels = this._getLabelsForPath(this._selectedPath);
            this.data.displayLabel = middleTruncate(labels.join(this._separator));
        },
        _getLabelsForPath(path) {
            const labels = [];
            let current = this._options;
            path.forEach(val => {
                const found = current.find(o => o.value === val);
                if (found) {
                    labels.push(found.label || found.value);
                    current = found.children || [];
                }
            });
            return labels;
        },
        set options(tree) {
            this._options = Array.isArray(tree) ? tree : [];
            this._selectedPath = [];
            this._activePath = [];
            this._updateDisplay();
        },
        get value() {
            return [...this._selectedPath];
        },
        set value(v) {
            if (Array.isArray(v)) {
                this._selectedPath = v;
            } else if (typeof v === 'string' && v) {
                this._selectedPath = v.split(',').map(s => s.trim());
            } else {
                this._selectedPath = [];
            }
            this._activePath = [...this._selectedPath];
            this._updateDisplay();
        },
    },
});
return { __default };
})();

const __m94 = (() => {
const highlightOption = __m9.highlightOption;
const __default = {
    tags: ['s-ac', 's-ac'],
    dispatches: ['search', 'select'],
    props: ['@open', '@placeholder', '@disabled'],
    aria: { role: 'combobox', expanded: 'open', disabled: 'disabled', haspopup: '=listbox' },
    internalStore: true,
    storeAttrs: ['open'],
    floatingPanel: 1,
    // `:dataSource=` writes el[dataField], which defaults to `data` — so the
    // suggestion rows were landing on the list-data setter and never reaching
    // the filter. The suggestions ARE this component's data field.
    dataField: 'dataSource',
    children: [
        ['input.ac-input', ':ref=input', '@type=text'],
        ['s-cn.ac-dropdown', ':ref=dropdown', ':dataKey=filteredItems', ':dataField=children'],
    ],
    callbacks: {
        connected() {
            // A bound :dataSource= reaches the setter BEFORE this runs, so an
            // unconditional reset threw the rows away and the panel had nothing
            // to suggest for anything the reader typed.
            if (!this._dataSource) this._dataSource = [];
            this._focusedIndex = -1;

            const input = this.refs.input;

            if (this.placeholder) {
                input.placeholder = this.placeholder;
            }

            // Input events
            input.addEventListener('input', () => {
                const q = input.value;
                this.dispatchEvent(new CustomEvent('search', {
                    bubbles: true,
                    detail: { value: q },
                }));
                this._filterAndShow(q);
            });

            input.addEventListener('focus', () => {
                if (input.value) this._filterAndShow(input.value);
            });

            // Keyboard navigation
            input.addEventListener('keydown', (e) => {
                if (!this.open) {
                    if (e.key === 'ArrowDown') {
                        this._filterAndShow(input.value);
                    }
                    return;
                }
                const items = this.refs.dropdown.children;
                switch (e.key) {
                    case 'ArrowDown':
                        e.preventDefault();
                        this._focusedIndex = Math.min(this._focusedIndex + 1, items.length - 1);
                        this._highlightItem(items);
                        break;
                    case 'ArrowUp':
                        e.preventDefault();
                        this._focusedIndex = Math.max(this._focusedIndex - 1, 0);
                        this._highlightItem(items);
                        break;
                    case 'Enter':
                        e.preventDefault();
                        this._pick(this._focusedIndex);
                        break;
                }
            });

        },
    },
    methods: {
        _filterAndShow(query) {
            const q = (query || '').toLowerCase();
            this._focusedIndex = -1;

            const filtered = this._dataSource.filter(item => {
                const label = typeof item === 'string' ? item : (item.label || item.value || '');
                return label.toLowerCase().includes(q);
            });

            if (filtered.length === 0) {
                this._close();
                return;
            }

            // Keep the shown rows as a MODEL and stamp each one's index. The
            // panel is our own output, so picking a suggestion is an array
            // lookup — never a value read back out of the element.
            this._filtered = filtered.map(item => (typeof item === 'string'
                ? { label: item, value: item }
                : { label: item.label || item.value, value: item.value || item.label }));
            this.data.filteredItems = this._filtered.map((row, i) => (
                ['s-cn.ac-item', `~cl:call:_pick:${i}`, `="${row.label}"`]
            ));

            this._openPanel();
        },
        _highlightItem(items) { highlightOption(items, this._focusedIndex); },
        _pick(index) {
            const row = (this._filtered || [])[Number(index)];
            if (!row) return;
            this.refs.input.value = row.label;
            this._close();
            this.dispatchEvent(new CustomEvent('select', {
                bubbles: true,
                detail: { value: row.value },
            }));
        },
        _close() {
            this._closePanel();
            this._focusedIndex = -1;
        },
        set dataSource(items) {
            this._dataSource = Array.isArray(items) ? items : [];
            // Rows can arrive AFTER the reader has typed — a `search` handler
            // fetching from a server is the whole point of the event. Re-filter
            // so late results populate the open panel instead of waiting for
            // the next keystroke.
            if (this.refs && this.refs.input && document.activeElement === this.refs.input) {
                this._filterAndShow(this.refs.input.value);
            }
        },
        get dataSource() { return this._dataSource || []; },
        get value() { return this.refs.input ? this.refs.input.value : ''; },
        set value(v) {
            if (this.refs.input) this.refs.input.value = v || '';
        },
    },
};
return { __default };
})();

const __m95 = (() => {
const highlightOption = __m9.highlightOption;
const onOutsideClick = __m9.onOutsideClick;
const removeOutsideClick = __m9.removeOutsideClick;
const __default = {
    tags: ['s-mtn', 's-mtn'],
    dispatches: ['select'],
    props: ['@prefix', '@placeholder', '@disabled'],
    internalStore: true,
    children: [
        ['textarea.mtn-textarea', ':ref=textarea'],
        ['s-cn.mtn-popup', ':ref=popup', ':dataKey=suggestions', ':dataField=children'],
    ],
    callbacks: {
        connected() {
            this._prefix = this.prefix || '@';
            // A bound :options= reaches the setter BEFORE this runs; resetting
            // it here left the mention popup with nobody to suggest.
            this._options = this._options || [];
            this._focusedIndex = -1;
            this._mentionStart = -1;

            const textarea = this.refs.textarea;

            if (this.placeholder) {
                textarea.placeholder = this.placeholder;
            }

            // Input monitoring
            textarea.addEventListener('input', () => {
                this._checkForMention();
            });

            // Keyboard navigation
            textarea.addEventListener('keydown', (e) => {
                if (!this.refs.popup.hasAttribute('visible')) return;

                const items = this.refs.popup.children;
                switch (e.key) {
                    case 'ArrowDown':
                        e.preventDefault();
                        this._focusedIndex = Math.min(this._focusedIndex + 1, items.length - 1);
                        this._highlightItem(items);
                        break;
                    case 'ArrowUp':
                        e.preventDefault();
                        this._focusedIndex = Math.max(this._focusedIndex - 1, 0);
                        this._highlightItem(items);
                        break;
                    case 'Enter':
                        e.preventDefault();
                        this._pick(this._focusedIndex);
                        break;
                    case 'Escape':
                        e.preventDefault();
                        this._hidePopup();
                        break;
                }
            });

            // Click outside closes popup
            onOutsideClick(this, (e) => {
                if (!this.contains(e.target)) this._hidePopup();
            });
        },
        disconnected() {
            removeOutsideClick(this);
        },
    },
    methods: {
        _checkForMention() {
            const textarea = this.refs.textarea;
            const text = textarea.value;
            const cursor = textarea.selectionStart;

            let start = -1;
            for (let i = cursor - 1; i >= 0; i--) {
                if (text[i] === this._prefix) {
                    if (i === 0 || /\s/.test(text[i - 1])) {
                        start = i;
                        break;
                    }
                }
                if (/\s/.test(text[i])) break;
            }

            if (start < 0) {
                this._hidePopup();
                return;
            }

            this._mentionStart = start;
            const query = text.substring(start + 1, cursor).toLowerCase();
            this._showSuggestions(query);
        },
        _showSuggestions(query) {
            const filtered = this._options.filter(o => {
                const label = typeof o === 'string' ? o : (o.label || o.value || '');
                return label.toLowerCase().includes(query);
            });

            if (filtered.length === 0) {
                this._hidePopup();
                return;
            }

            this._focusedIndex = -1;

            // The shown rows are a MODEL; each stamped row carries its index,
            // so inserting a mention is an array lookup rather than a value read
            // back off the element.
            this._filtered = filtered.map(opt => (typeof opt === 'string'
                ? { label: opt, value: opt }
                : { label: opt.label || opt.value, value: opt.value || opt.label }));
            this.data.suggestions = this._filtered.map((row, i) => (
                ['s-cn.mtn-item', `~cl:call:_pick:${i}`, `="${row.label}"`]
            ));

            this.refs.popup.setAttribute('visible', '');
        },
        _hidePopup() {
            this.refs.popup.removeAttribute('visible');
            this._focusedIndex = -1;
        },
        _highlightItem(items) { highlightOption(items, this._focusedIndex, false); },
        _pick(index) {
            const row = (this._filtered || [])[Number(index)];
            if (row) this._insertMention(row.value);
        },
        _insertMention(value) {
            const textarea = this.refs.textarea;
            const text = textarea.value;
            const cursor = textarea.selectionStart;
            const before = text.substring(0, this._mentionStart);
            const after = text.substring(cursor);
            const mention = this._prefix + value + ' ';

            textarea.value = before + mention + after;
            const newPos = before.length + mention.length;
            textarea.setSelectionRange(newPos, newPos);
            textarea.focus();

            this._hidePopup();
            this.dispatchEvent(new CustomEvent('select', {
                bubbles: true,
                detail: { value },
            }));
        },
        set options(items) {
            this._options = Array.isArray(items) ? items : [];
        },
        get value() { return this.refs.textarea ? this.refs.textarea.value : ''; },
        set value(v) {
            if (this.refs.textarea) this.refs.textarea.value = v || '';
        },
    },
};
return { __default };
})();

const __m96 = (() => {
const asFormField = __m66.asFormField;
const middleTruncate = __m9.middleTruncate;
const isPanelOpen = __m23.isPanelOpen;
let treeSelectSeq = 0;

const __default = asFormField({
    tags: ['s-trsel', 's-trsel'],
    dispatches: ['change', 'valueChanged'],
    props: ['@value', '@open', '@multiple', '@searchable', '@placeholder', '@disabled',
        '@showFullPath', '@separator', '@truncate'],
    aria: { role: 'combobox', expanded: 'open', disabled: 'disabled', haspopup: '=tree' },
    internalStore: true,
    storeAttrs: ['value', 'open'],
    floatingPanel: 1,
    children: [
        ['s-cn.trsel-display', ':ref=display', '@tabindex=0', [
            ['span.trsel-text', ':ref=displayText', ':dataKey=displayLabel'],
            ['s-cn.trsel-chips', ':ref=chips', ':dataKey=chipElements', ':dataField=children'],
        ]],
        ['s-cn.trsel-panel', [
            ['input.trsel-search', ':ref=search', '@type=text', '@placeholder=Search...'],
            ['s-cn.trsel-tree', ':ref=tree', '@role=tree', ':dataKey=treeNodes', ':dataField=children'],
        ]],
    ],
    callbacks: {
        connected() {
            this._initState();
            this._updateDisplay();

            // Click delegation on self
            this.addEventListener('click', (e) => {
                // Display toggle (but not chips area)
                if (this.refs.display.contains(e.target) && !this.refs.chips.contains(e.target)) {
                    if (this.disabled) return;
                    this._togglePanel();
                    return;
                }

                // Everything inside the panel — the row, its twisty, its
                // checkbox — carries its own row index and handles itself
                // through ~cl. Nothing left to delegate here.
            });

            // Search
            this.refs.search.addEventListener('input', () => {
                this._renderTree(this.refs.search.value.toLowerCase());
            });
            this.refs.search.addEventListener('click', (e) => e.stopPropagation());

            if (this.multiple) this.refs.tree.setAttribute('aria-multiselectable', 'true');

            // Combobox keyboard contract. Focus stays on the display (or the
            // search input); the active tree row is announced through
            // aria-activedescendant and navigated from _visibleNodes — the JS
            // array _renderTree builds — never by walking the stamped DOM.
            this.addEventListener('keydown', (e) => {
                if (this.disabled) return;
                const nodes = this._visibleNodes || [];
                const move = (delta) => {
                    if (!nodes.length) return;
                    this._activeIndex = Math.min(nodes.length - 1, Math.max(0, (this._activeIndex ?? -1) + delta));
                    this._renderTree(this.refs.search.value.toLowerCase(), true);
                };
                // The `open` prop syncs from the native popover's ASYNC toggle
                // event; a fast second keystroke must not read it stale and
                // toggle the panel straight back shut. isPanelOpen also keeps
                // `:popover-open` away from engines it would throw on.
                const isOpen = this._panel ? isPanelOpen(this._panel) : this.open;
                if (!isOpen) {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        this._togglePanel();
                        // onOpenChange re-runs this on the async toggle event;
                        // doing it here too means the very next keystroke
                        // already has rows to navigate.
                        this._initPanelState();
                    }
                    return;
                }
                const active = nodes[this._activeIndex ?? -1];
                switch (e.key) {
                    case 'ArrowDown': e.preventDefault(); move(1); break;
                    case 'ArrowUp': e.preventDefault(); move(-1); break;
                    case 'ArrowRight':
                        if (active?.hasChildren && !active.expanded) {
                            e.preventDefault();
                            this._expandedKeys.add(active.val);
                            this._renderTree(this.refs.search.value.toLowerCase(), true);
                        }
                        break;
                    case 'ArrowLeft':
                        if (active?.hasChildren && active.expanded) {
                            e.preventDefault();
                            this._expandedKeys.delete(active.val);
                            this._renderTree(this.refs.search.value.toLowerCase(), true);
                        }
                        break;
                    case 'Enter': case ' ':
                        if (active) {
                            e.preventDefault();
                            if (this.multiple && this._selectedValues.includes(active.val)) this._deselectValue(active.val);
                            else this._selectValue(active.val);
                        }
                        break;
                    case 'Escape':
                        e.preventDefault();
                        this._closePanel();
                        this.refs.display.focus();
                        break;
                }
            });
        },
    },
    methods: {
        // Store subscriptions resume in activate(), which runs before this def's
        // connected() — so a bound :treeData= / :value= reaches the setters while
        // the element still has no state. Both setters seed it through here, and
        // connected() must not clobber what already landed (a bound treeData was
        // being thrown away, leaving the panel permanently empty).
        _initState() {
            if (this._expandedKeys) return;
            this._treeData = [];
            this._selectedValues = [];
            this._expandedKeys = new Set();
        },
        // Row controls, addressed by the index this component stamped. A twisty
        // or checkbox sits INSIDE its row, so its handler runs first as the
        // click bubbles; claiming the click stops the row from also selecting.
        _claim() { this._claimed = true; },
        _rowAt(index) { return (this._visibleNodes || [])[Number(index)]; },
        _toggleAt(index) {
            this._claim();
            const row = this._rowAt(index);
            if (!row) return;
            if (this._expandedKeys.has(row.val)) this._expandedKeys.delete(row.val);
            else this._expandedKeys.add(row.val);
            this._renderTree(this.refs.search.value.toLowerCase());
        },
        _checkAt(index) {
            this._claim();
            const row = this._rowAt(index);
            if (!row) return;
            if (this._selectedValues.includes(row.val)) this._deselectValue(row.val);
            else this._selectValue(row.val);
        },
        _pickAt(index) {
            if (this._claimed) { this._claimed = false; return; }
            const row = this._rowAt(index);
            if (row) this._selectValue(row.val);
        },
        onOpenChange(isOpen) {
            // The popover's toggle event arrives a frame late; if the opening
            // keystroke already initialized (and the user kept typing), this
            // late init must NOT wipe their navigation.
            if (isOpen) {
                if (!this._panelInited) this._initPanelState();
                if (this.searchable) {
                    requestAnimationFrame(() => this.refs.search.focus());
                }
            } else {
                this._panelInited = false;
            }
        },
        _initPanelState() {
            this._panelInited = true;
            this.refs.search.value = '';
            this._renderTree('');
        },
        _renderTree(filter, keepActive) {
            if (!this._internalData) return;
            if (!keepActive) this._activeIndex = -1;
            // Object state is the truth: the flat list of visible rows drives
            // keyboard navigation and activedescendant, not DOM traversal.
            this._visibleNodes = [];
            const nodeDefs = [];
            this._collectNodeDefs(this._treeData, nodeDefs, 0, filter);
            this.data.treeNodes = nodeDefs;
            const active = this._visibleNodes[this._activeIndex ?? -1];
            const adId = active ? this._nodeId(this._activeIndex) : '';
            [this.refs.display, this.refs.search].forEach((el) => {
                if (adId) el.setAttribute('aria-activedescendant', adId);
                else el.removeAttribute('aria-activedescendant');
            });
        },
        _nodeId(index) {
            if (!this._nodeIdBase) this._nodeIdBase = `trsel${++treeSelectSeq}`;
            return `${this._nodeIdBase}-n${index}`;
        },
        _collectNodeDefs(nodes, output, depth, filter) {
            nodes.forEach(node => {
                const label = node.label || node.title || node.value;
                const val = node.value || node.key;
                const hasChildren = node.children && node.children.length > 0;
                const matchesFilter = !filter || label.toLowerCase().includes(filter);

                let descendantMatch = false;
                if (filter && hasChildren) {
                    descendantMatch = this._hasMatchingDescendant(node.children, filter);
                }

                if (filter && !matchesFilter && !descendantMatch) return;

                const isExpanded = this._expandedKeys.has(val) || Boolean(filter && descendantMatch);
                const isSelected = this._selectedValues.includes(val);
                const isMulti = this.multiple;
                const index = this._visibleNodes.length;
                this._visibleNodes.push({ val, label, hasChildren, expanded: isExpanded, depth });
                const isActive = index === (this._activeIndex ?? -1);

                const children = [];

                if (hasChildren) {
                    children.push(['s-ic.trsel-toggle', `~cl:call:_toggleAt:${index}`, '@n=cr', ...(isExpanded ? ['@expanded='] : [])]);
                }

                if (isMulti) {
                    children.push(['input', '@type=checkbox', '@tabindex=-1', `~cl:call:_checkAt:${index}`, ...(isSelected ? ['@checked='] : [])]);
                }

                children.push(['span.trsel-label',
                    ...(!isMulti && isSelected ? ['@selected='] : []),
                    `="${label}"`,
                ]);

                // Real tree semantics: every row is a treeitem with its level,
                // expansion and selection announced, addressable by the stable
                // id aria-activedescendant points at.
                output.push([`s-cn.trsel-node${isActive ? '.trsel-active' : ''}`,
                    `@id=${this._nodeId(index)}`,
                    '@role=treeitem',
                    `@aria-level=${depth + 1}`,
                    `@aria-selected=${isSelected}`,
                    ...(hasChildren ? [`@aria-expanded=${isExpanded}`] : []),
                    `~cl:call:_pickAt:${index}`,
                    `@style=--trsel-depth: ${depth}`,
                    children,
                ]);

                if (hasChildren && isExpanded) {
                    this._collectNodeDefs(node.children, output, depth + 1, filter);
                }
            });
        },
        _hasMatchingDescendant(nodes, filter) {
            return nodes.some(n => {
                const label = (n.label || n.title || n.value || '').toLowerCase();
                if (label.includes(filter)) return true;
                return n.children ? this._hasMatchingDescendant(n.children, filter) : false;
            });
        },
        _selectValue(val) {
            const isMulti = this.multiple;
            if (isMulti) {
                if (!this._selectedValues.includes(val)) {
                    this._selectedValues.push(val);
                }
            } else {
                this._selectedValues = [val];
                this._closePanel();
            }
            this._updateDisplay();
            this._renderTree(this.refs.search?.value?.toLowerCase() || '');
            this._fireChange();
        },
        _deselectValue(val) {
            const idx = this._selectedValues.indexOf(val);
            if (idx >= 0) this._selectedValues.splice(idx, 1);
            this._updateDisplay();
            this._renderTree(this.refs.search?.value?.toLowerCase() || '');
            this._fireChange();
        },
        _updateDisplay() {
            const isMulti = this.multiple;

            if (this._selectedValues.length === 0) {
                this.data.displayLabel = this.placeholder || 'Select...';
                this.data.chipElements = [];
                return;
            }

            if (isMulti) {
                this.data.displayLabel = '';
                this.data.chipElements = this._selectedValues.map(val => {
                    const label = this._labelFor(val);
                    return ['span.trsel-chip', [
                        ['span', `="${label}"`],
                        ['s-ic.trsel-chip-x', `~cl:call:_deselectValue:${val}`, '@n=x'],
                    ]];
                });
            } else {
                this.data.displayLabel = this._labelFor(this._selectedValues[0]);
                this.data.chipElements = [];
            }
        },
        // The label a selection SHOWS. @showFullPath prints the ancestor chain
        // ("Somerset / Bruton") — the presentation that used to be the only
        // reason to reach for a cascader instead of this. @separator overrides
        // the join, @truncate the elision width (0 turns it off).
        _labelFor(val) {
            let label = null;
            if (this.showFullPath) {
                const path = this._findPath(this._treeData, val);
                if (path) label = path.join(this.separator || ' / ');
            }
            if (label == null) label = this._findLabel(this._treeData, val) || val;
            const edge = this.truncate === undefined || this.truncate === null || this.truncate === ''
                ? 5 : parseInt(this.truncate, 10);
            return edge > 0 ? middleTruncate(label, edge) : String(label);
        },
        // The chain of labels from the root down to `val`, or null if absent.
        _findPath(nodes, val, trail = []) {
            for (const n of nodes || []) {
                const next = [...trail, n.label || n.title || n.value];
                if ((n.value || n.key) === val) return next;
                if (n.children) {
                    const found = this._findPath(n.children, val, next);
                    if (found) return found;
                }
            }
            return null;
        },
        _findLabel(nodes, val) {
            let result = null;
            nodes.forEach(n => {
                if (result) return;
                if ((n.value || n.key) === val) { result = n.label || n.title || n.value; return; }
                if (n.children) {
                    const found = this._findLabel(n.children, val);
                    if (found) result = found;
                }
            });
            return result;
        },
        _fireChange() {
            const isMulti = this.multiple;
            const val = isMulti ? [...this._selectedValues] : (this._selectedValues[0] || '');
            this.setAttribute('value', isMulti ? this._selectedValues.join(',') : String(val));
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: val },
            }));
            this.dispatchEvent(new CustomEvent('valueChanged', {
                bubbles: true,
                detail: { value: isMulti ? this._selectedValues.join(',') : String(val), name: this._name || this.getAttribute('name') },
            }));
        },
        set treeData(nodes) {
            this._initState();
            this._treeData = Array.isArray(nodes) ? nodes : [];
            this._selectedValues = [];
            this._expandedKeys.clear();
            this._updateDisplay();
            if (this.open && this.refs?.tree) this._renderTree('');
        },
        get value() {
            const sel = this._selectedValues || [];
            return this.multiple ? [...sel] : (sel[0] || '');
        },
        set value(v) {
            this._initState();
            if (Array.isArray(v)) this._selectedValues = [...v];
            else if (typeof v === 'string' && v) this._selectedValues = v.split(',').map(s => s.trim());
            else this._selectedValues = [];
            this._updateDisplay();
        },
    },
});
return { __default };
})();

const __m97 = (() => {
const store = __m0.store;
// Carousel — chrome around a SINGLE s-st stack. The stack already owns
// scroll-snap, snap detection, selection-scrolls-to-item and the sync
// machinery; the carousel adds prev/next buttons and a proportional indicator
// row (s-tgb-g, the unified selectable row) and rewrites nothing.


let carSeq = 0;

const __default = {
    tags: ['s-car', 's-car'],
    props: ['@vertical', 'selectedIndex', 'itemRenderer', 'itemRendererField'],
    children: [
        ['s-st.car-track', ':ref=track'],
        ['button.car-prev', '@aria-label=$t.a11y.previous', '~cl:call:_prev'],
        ['button.car-next', '@aria-label=$t.a11y.next', '~cl:call:_next'],
        ['s-tgb-g.car-indicator', ':ref=indicator', '@aria-label=$t.a11y.slides'],
    ],
    callbacks: {
        connected() {
            const track = this.refs.track;
            const indicator = this.refs.indicator;
            if (!track.id) track.id = `swccar${++carSeq}-track`;
            if (!indicator.id) indicator.id = `swccar${carSeq}-ind`;
            if (this.vertical) track.setAttribute('l', 'v');

            // Indicator drives the stack through the stack's OWN sync mixin
            // (the s-tbs wiring, reused): reopen the latch, point it at the
            // indicator, and selection clicks scroll the track.
            track.setAttribute('sync', indicator.id);
            track._syncSetup = false;
            if (typeof track._setupSync === 'function') {
                track._syncSetup = true;
                track._setupSync();
            }

            // The stack publishes its selection to store[track.id] — swipe
            // and snap included. Mirror it onto the indicator and this host.
            this._sub = store.subscribe(track.id, (sel) => {
                const idx = sel && typeof sel.selectedIndex === 'number' ? sel.selectedIndex : 0;
                this._selectedIndex = idx;
                if (indicator.selectedIndex !== idx) indicator.selectedIndex = idx;
            });

            // Data assigned before connect (refs did not exist yet) renders now.
            if (this._data && !track.children.length) this.data = this._data;
            // Server-rendered indicators arrive already stamped, so the data
            // setter above never runs for them and their names would be
            // missing on exactly the pages that render without JavaScript
            // first. Label whatever is there.
            this._labelIndicator();
            this._langSub = store.subscribe('t.a11y.slide', () => this._labelIndicator());
        },
        disconnected() {
            if (this._sub) { this._sub.unsubscribe(); this._sub = null; }
            if (this._langSub) { this._langSub.unsubscribe(); this._langSub = null; }
        },
    },
    methods: {
        set data(items) {
            this._data = items;
            const track = this.refs && this.refs.track;
            if (!track || !Array.isArray(items)) return;
            // The stack stamps the slides: forward the renderer contract.
            if (this.itemRenderer) track.itemRenderer = this.itemRenderer;
            if (this.itemRendererField) track.itemRendererField = this.itemRendererField;
            track.data = items;
            // The indicator is a proportional bar: its segments carry no text
            // by design (the stylesheet sets font-size: 0), so stamping them
            // with empty labels leaves a radiogroup of unnamed radios — a
            // WCAG 4.1.2 failure on every carousel, and one that reads as
            // "blank blank blank" rather than as anything a listener can act
            // on. The names come from the a11y string store instead.
            this.refs.indicator.data = items.map((_, i) => ({ value: i, text: '' }));
            this._labelIndicator();
            if (items.length) this.scrollToIndex(0);
        },
        get data() { return this._data; },

        /**
         * Name each indicator segment "Slide N".
         *
         * Written as an attribute rather than as item text because text would
         * be visible markup the stylesheet then has to hide, and a restyle
         * would put it back on screen. Re-run when the locale pack changes, so
         * a language switch does not leave English names behind — the prev/next
         * buttons get that for free from their `@aria-label=$t.a11y.*` binding,
         * and stamped children have no binding to inherit.
         */
        _labelIndicator() {
            const indicator = this.refs && this.refs.indicator;
            if (!indicator) return;
            const word = store.getValue('t.a11y.slide') || 'Slide';
            [...indicator.children].forEach((child, i) => {
                child.setAttribute('aria-label', `${word} ${i + 1}`);
            });
        },

        _prev() { this.scrollToIndex((this._selectedIndex || 0) - 1); },
        _next() { this.scrollToIndex((this._selectedIndex || 0) + 1); },

        scrollToIndex(index) {
            const track = this.refs.track;
            const count = track ? track.children.length : 0;
            if (!count) return;
            const clamped = Math.max(0, Math.min(index, count - 1));
            this._selectedIndex = clamped;
            // Selection mixin setter: selects the child, and the stack's
            // _onSelectionChange smooth-scrolls to it.
            track.selectedIndex = clamped;
            if (this.refs.indicator) this.refs.indicator.selectedIndex = clamped;
        },
    },
};
return { __default };
})();

const __m98 = (() => {
const __default = {
    tags: ['s-car-p', 's-car-p'],
    // The slide picker is the carousel's tablist (APG carousel pattern).
    aria: { role: 'tablist', itemRole: 'tab' },
    keyboardNav: { orientation: 'horizontal', selectOnFocus: true },
    itemRenderer: 's-car-i',
    selectable: true
};
return { __default };
})();

const __m99 = (() => {
const __default = {
    tags: ['s-ch-l', 's-ch-l'],
    aria: { role: 'listbox', itemRole: 'option' },
    keyboardNav: { orientation: 'horizontal' },
    itemRenderer: 's-ch',
    selectable: true,
    css: `
        s-ch-l, swc-chip-list {
            display: flex;
            flex-wrap: wrap;
            gap: var(--s2);
        }
    `
};
return { __default };
})();

const __m100 = (() => {
const __default = {
    tags: ['s-dd', 's-dd'],
    props: ['@open'],
    // menuitemradio, not menuitem: a dropdown holds a CURRENT VALUE, and plain
    // menuitem has no selected state in ARIA — so the chosen item was marked
    // only by the bare `selected` attribute that CSS reads, and a listener
    // opening the theme picker heard "Light, Dark, Signal" with no way to tell
    // which one is active. The role is in ITEM_STATE, so selection co-writes
    // aria-checked from here on with no extra machinery.
    aria: { role: 'button', expanded: 'open', haspopup: '=menu', itemRole: 'menuitemradio' },
    // A role=menu is arrow-navigated with ONE item in the tab order (APG), and
    // s-mn has said so all along. Without it every item carried tabindex=0, so
    // Tab walked through all of them instead of leaving the menu — three extra
    // stops for the theme picker, more for anything data-driven.
    keyboardNav: { orientation: 'vertical', selectOnFocus: false, typeahead: true },
    // The host IS the trigger: it carries the role and expanded state, so it
    // must be the focusable, Enter/Space-operable element (a role AT can see
    // on something a keyboard cannot reach is worse than no role).
    focusable: true,
    floatingPanel: '.dd-panel',
    itemRenderer: 's-di',
    selectable: true,
    children: [
        ['span.dd-display', ':ref=ddDisplay', '@static='],
        ['s-cn.dd-panel', ':ref=ddPanel', '@static=', '@role=menu'],
    ],
    callbacks: {
        connected() {
            // Move any inline s-di markup children into the panel before floating
            // panel setup. Iterate own direct children (a property, not a query);
            // already-moved items live under the panel and are excluded naturally.
            const panel = this.refs.ddPanel;
            if (panel) {
                Array.from(this.children)
                    .filter(c => c.tagName === 'S-DI' || c.tagName === 'SWC-DROPDOWN-ITEM')
                    .forEach(di => panel.appendChild(di));
            }

            this._setupFloatingPanel();
            this._listContainer = this.refs.ddPanel;

            // Display click -> toggle panel (delegated on self). A click whose
            // target is the host itself is the keyboard path — the focusable
            // machinery synthesises this.click() from Enter/Space.
            this.addEventListener('click', (e) => {
                if (e.target === this || e.target.closest('.dd-display')) {
                    e.stopPropagation();
                    this._togglePanel();
                    return;
                }
                const item = e.target.closest('s-di, swc-dropdown-item');
                if (item) this._closePanel();
            });

            this._syncDisplay();
        },
    },
    methods: {
        _syncDisplay() {
            const display = this.refs.ddDisplay;
            if (!display) return;
            // Read the selection-mixin's tracked state, not a DOM query.
            const selected = this.selectedItems[0];
            display.textContent = selected?.textContent || '';
        },
        onOpenChange(isOpen) {
            if (!isOpen) this._syncDisplay();
        },
        // Called by selection mixin after an item is selected
        _onSelectionChange() {
            this._syncDisplay();
        },
        // Called by list mixin after items render (data-driven)
        _afterRender() {
            this._syncDisplay();
        },
    },
};
return { __default };
})();

const __m101 = (() => {
// Split button: one default action you can press directly, plus a caret that
// opens the related ones. "Save" with "Save as…" and "Save and close" behind
// the arrow — the common case stays one click, the variants stay discoverable.
//
// This is a COMPOSITION, not a new popup implementation: the panel is the same
// floatingPanel machinery s-dd uses, the items are s-di, and selection comes
// from the same mixin. What is genuinely new is the split — two hit targets in
// one control — and the aria that goes with it, because only the caret owns
// the popup.
//
//   @label  the default action's text          @icon  its leading icon
//   @c @sz @v  ride through to both halves so the pair always matches
//   action  event — the default action was pressed
//   select  from the selection mixin, like every other selectable list: the
//           item behind the caret that was chosen (detail.value)
const __default = {
    tags: ['swc-split-button', 's-spb'],
    dispatches: ['action'],
    props: ['@label', '@icon', '@c', '@sz', '@v', '@open', '@disabled'],
    // The HOST is a group, not the trigger: a split button has two controls,
    // and putting the popup role on the wrapper would tell a screen reader the
    // default action opens a menu, which it does not. The caret carries
    // haspopup/expanded itself (set in connected()).
    aria: { role: 'group', itemRole: 'menuitem' },
    floatingPanel: '.spb-panel',
    itemRenderer: 's-di',
    selectable: true,
    children: [
        ['s-b.spb-main', ':ref=main', '~cl:call:_onAction', [
            ['s-ic.spb-icon', ':ref=mainIcon', '?prop:icon'],
            ['span.spb-label', ':ref=mainLabel', '#label'],
        ]],
        ['s-b.spb-toggle', ':ref=toggle', '~cl:call:_toggle', [
            ['s-ic', '@n=chevron-down'],
        ]],
        ['s-cn.spb-panel', ':ref=panel', '@static=', '@role=menu'],
    ],
    callbacks: {
        connected() {
            // Authored s-di children belong in the panel, the same move s-dd
            // makes. Own children, not a query.
            const panel = this.refs.panel;
            if (panel) {
                Array.from(this.children)
                    .filter((c) => c.tagName === 'S-DI' || c.tagName === 'SWC-DROPDOWN-ITEM')
                    .forEach((di) => panel.appendChild(di));
            }
            this._setupFloatingPanel();
            this._listContainer = panel;

            // Only the caret announces the popup.
            const toggle = this.refs.toggle;
            if (toggle) {
                toggle.setAttribute('aria-haspopup', 'menu');
                toggle.setAttribute('aria-expanded', 'false');
                if (!toggle.hasAttribute('aria-label')) {
                    toggle.setAttribute('aria-label', 'More actions');
                }
            }

            // Choosing an item closes the panel. Reporting WHICH item is the
            // selection mixin's job — it already dispatches `select` with the
            // item's value, and dispatching a second one here produced two
            // events per click, the duplicate carrying a textContent fallback
            // because a stamped s-di holds its value as a property rather than
            // an attribute.
            this.addEventListener('click', (e) => {
                const item = e.target.closest && e.target.closest('s-di, swc-dropdown-item');
                if (item && panel && panel.contains(item)) this._closePanel();
            });

            this._syncParts();
        },
    },
    methods: {
        // The two halves share the button vocabulary so a split button can
        // never end up with a primary action and a secondary caret.
        _syncParts() {
            const pass = (el) => {
                if (!el) return;
                ['c', 'sz', 'v'].forEach((a) => {
                    const v = this.getAttribute(a);
                    if (v == null) el.removeAttribute(a); else el.setAttribute(a, v);
                });
                el.toggleAttribute('disabled', this.hasAttribute('disabled'));
            };
            pass(this.refs.main);
            pass(this.refs.toggle);
            const icon = this.refs.mainIcon;
            if (icon && this.icon) icon.n = this.icon;
        },
        _onAction() {
            if (this.disabled) return;
            this.dispatchEvent(new CustomEvent('action', { bubbles: true }));
        },
        _toggle(e) {
            if (this.disabled) return;
            // The caret's click must not read as the default action.
            if (e && e.stopPropagation) e.stopPropagation();
            this._togglePanel();
        },
        onOpenChange(isOpen) {
            this.refs.toggle?.setAttribute('aria-expanded', String(!!isOpen));
        },
        onIconChange() { this._syncParts(); },
        onCChange() { this._syncParts(); },
        onSzChange() { this._syncParts(); },
        onVChange() { this._syncParts(); },
        onDisabledChange() { this._syncParts(); },
    },
};
return { __default };
})();

const __m102 = (() => {
const listBase = __m48.__default;
// List (s-ls) — the base list, decorated.
//
// The spread is the point: s-ls adds NO behaviour to s-lb, only a name that
// list.css draws a bordered, rounded, clipped box around. Anything functional
// belongs in list-base.js so both tags get it, and the spread makes it
// impossible for the two to drift apart without an explicit edit here.


const __default = {
  ...listBase,
  tags: ['s-ls', 's-ls'],
};
return { __default };
})();

const __m103 = (() => {
const store = __m0.store;
// Menu (s-mn) — a store-toggled popup list. Keyboard: opening moves focus to the
// first item, arrow keys navigate (keyboard-nav mixin), Enter/Space selects, and
// Escape closes and returns focus to whatever opened it (the trigger button).
const __default = {
    tags: ['s-mn', 's-mn'],
    props: ['@open'],
    aria: { role: 'menu', itemRole: 'menuitem' },
    storeAttrs: ['open'],
    keyboardNav: { orientation: 'vertical', selectOnFocus: false, typeahead: true },
    itemRenderer: 's-mn-i',
    selectable: true,
    callbacks: {
        connected() {
            this.addEventListener('keydown', (e) => {
                if (e.key !== 'Escape') return;
                e.preventDefault();
                this._closeMenu();
            });
            // `open` is driven by a storeAttr binding, so a MutationObserver is
            // the reliable signal (an on<Prop>Change callback does not fire for
            // storeAttr-set attributes). Mirrors the focus-trap mixin.
            if (typeof MutationObserver !== 'undefined') {
                this._openObserver = new MutationObserver(() => this._onOpenToggle());
                this._openObserver.observe(this, { attributes: true, attributeFilter: ['open'] });
            }
        },
        disconnected() {
            if (this._openObserver) this._openObserver.disconnect();
        },
    },
    methods: {
        _onOpenToggle() {
            if (!this.hasAttribute('open')) return;
            // Record what to return focus to, then move focus to the first item
            // once the menu is visible.
            this._menuReturnFocus = document.activeElement;
            const first = (typeof this._navItems === 'function' ? this._navItems() : [])[0];
            if (first) requestAnimationFrame(() => this._focusNavItem && this._focusNavItem(first));
        },
        _closeMenu() {
            if (this.id) store.setValue(this.id, false);
            else this.removeAttribute('open');
            const target = this._menuReturnFocus;
            this._menuReturnFocus = null;
            if (target && typeof target.focus === 'function') target.focus();
        },
    },
};
return { __default };
})();

const __m104 = (() => {
// Context menu (s-ctx) — the host is the right-click surface and its panel opens
// AT THE POINTER, not anchored to the host box. That is the whole reason this
// cannot be a dropdown with a different trigger: every other floating panel in
// the library anchors to its owner, so the shared CSS anchor rules in reset.css
// deliberately do not name s-ctx and the coordinates are set inline instead.
//
// Items are s-mn-i, so menu styling plus the keyboard-nav and selection mixins
// carry over unchanged — only the trigger and the placement are new.
const __default = {
    tags: ['s-ctx', 's-ctx'],
    props: ['@open'],
    aria: { role: 'menu', itemRole: 'menuitem' },
    keyboardNav: { orientation: 'vertical', selectOnFocus: false, typeahead: true },
    itemRenderer: 's-mn-i',
    selectable: true,
    floatingPanel: '.ctx-panel',
    children: [
        ['s-cn.ctx-panel', ':ref=ctxPanel', '@static=', '@role=menu'],
    ],
    callbacks: {
        connected() {
            // Authored items live as direct children; move them into the panel
            // before floating-panel setup. Same ordering constraint as s-dd —
            // iterate the live children property, not a query, so items already
            // moved under the panel are excluded naturally.
            const panel = this.refs.ctxPanel;
            if (panel) {
                Array.from(this.children)
                    .filter(c => c.tagName === 'S-MN-I' || c.tagName === 'SWC-MENU-ITEM')
                    .forEach(item => panel.appendChild(item));
            }

            this._setupFloatingPanel();
            this._listContainer = this.refs.ctxPanel;

            // The mixin registers the panel as popover="auto", which light-dismisses
            // on the pointerup that follows the opening right-click — the menu would
            // flash and vanish, and did. A context menu therefore has to be manual
            // and own its dismissal; _bindCtxDismiss below binds it one task later,
            // after the opening gesture is finished.
            if (panel) panel.setAttribute('popover', 'manual');

            this.addEventListener('contextmenu', (e) => {
                // A nested s-ctx owns its own region: let the innermost host win
                // and leave the browser menu reachable outside any of them.
                if (e.target.closest('s-ctx, swc-context-menu') !== this) return;
                e.preventDefault();
                this._openAt(e.clientX, e.clientY);
            });

            // Selecting an item dismisses, matching every other menu surface.
            this.addEventListener('click', (e) => {
                if (e.target.closest('s-mn-i, swc-menu-item')) this._closePanel();
            });

        },
        disconnected() {
            this._unbindCtxDismiss();
        },
    },
    methods: {
        _openAt(x, y) {
            const panel = this.refs.ctxPanel;
            if (!panel) return;
            this._ctxReturnFocus = document.activeElement;

            // Show first, measure second: a popover has no box until it is in the
            // top layer, so clamping against the viewport has to happen after.
            // No-Popover engines take the mixin's fallback (data-fp-open +
            // outside-pointerdown dismiss); its listener binds after the opening
            // right-click's pointerdown, so the menu survives the gesture.
            if (typeof panel.showPopover === 'function') {
                if (!panel.matches(':popover-open')) panel.showPopover();
            } else {
                this._openPanel();
            }
            panel.style.left = `${x}px`;
            panel.style.top = `${y}px`;

            const box = panel.getBoundingClientRect();
            // Flip rather than clamp when the menu would overflow — sliding it
            // back under the cursor would put the pointer on top of an item and
            // the next click would select something the user never aimed at.
            if (x + box.width > window.innerWidth) panel.style.left = `${Math.max(0, x - box.width)}px`;
            if (y + box.height > window.innerHeight) panel.style.top = `${Math.max(0, y - box.height)}px`;

            const first = (typeof this._navItems === 'function' ? this._navItems() : [])[0];
            if (first) requestAnimationFrame(() => this._focusNavItem && this._focusNavItem(first));

            this._bindCtxDismiss();
        },
        _bindCtxDismiss() {
            if (this._ctxDismiss) return;
            this._ctxDismiss = (e) => {
                if (e.type === 'keydown') {
                    if (e.key !== 'Escape') return;
                    e.preventDefault();
                } else if (this.refs.ctxPanel?.contains(e.target)) {
                    return;
                }
                this._closePanel();
            };
            // Escape binds now: it has nothing to do with the opening gesture, and
            // deferring it would leave a window where the menu ignores Escape.
            // Capture phase so dismissal wins over whatever sits under the menu.
            document.addEventListener('keydown', this._ctxDismiss, true);

            // The pointer listener is the one that must wait a task — bound now it
            // would fire on the pointerup of the right-click that opened the menu
            // and close it immediately.
            this._ctxPointerBind = setTimeout(() => {
                this._ctxPointerBind = null;
                if (this._ctxDismiss) document.addEventListener('pointerdown', this._ctxDismiss, true);
            }, 0);
        },
        _unbindCtxDismiss() {
            if (this._ctxPointerBind) { clearTimeout(this._ctxPointerBind); this._ctxPointerBind = null; }
            if (!this._ctxDismiss) return;
            document.removeEventListener('pointerdown', this._ctxDismiss, true);
            document.removeEventListener('keydown', this._ctxDismiss, true);
            this._ctxDismiss = null;
        },
        onOpenChange(isOpen) {
            if (isOpen) return;
            this._unbindCtxDismiss();
            const target = this._ctxReturnFocus;
            this._ctxReturnFocus = null;
            if (target && typeof target.focus === 'function') target.focus();
        },
    },
};
return { __default };
})();

const __m105 = (() => {
// Menubar (s-mnb) — a horizontal row of s-dd triggers behaving as one menubar:
// arrow keys move between triggers, and once any menu is open, moving to another
// trigger opens it immediately instead of requiring a second click. That
// "open follows focus, but only while open" rule is what separates a menubar
// from a row of independent dropdowns.
//
// The bar deliberately does NOT use the keyboard-nav mixin: that mixin navigates
// `this.items`, the selection-registered options of a list, whereas a menubar
// navigates its child menus. Registering s-dd as selectable options of the bar
// would nest two selection mixins and make each menu's own item selection
// ambiguous, so the roving tabindex is kept here instead.
const TRIGGERS = 's-dd, swc-dropdown';

const __default = {
    tags: ['s-mnb', 's-mnb'],
    aria: { role: 'menubar' },
    methods: {
        _mnbTriggers() {
            return Array.from(this.children)
                .filter(c => c.matches?.(TRIGGERS) && !c.hasAttribute('disabled'));
        },
        _mnbAnyOpen() {
            return this._mnbTriggers().some(t => t.hasAttribute('open'));
        },
        _mnbFocus(trigger, { openIfOpen = true } = {}) {
            if (!trigger) return;
            const wasOpen = this._mnbAnyOpen();
            this._mnbTriggers().forEach((t) => {
                t.tabIndex = t === trigger ? 0 : -1;
                // Close every sibling first: two open panels in one bar is the
                // bug this component exists to prevent.
                if (t !== trigger && t.hasAttribute('open')) t._closePanel?.();
            });
            trigger.focus();
            if (wasOpen && openIfOpen) trigger._openPanel?.();
        },
    },
    callbacks: {
        connected() {
            const triggers = this._mnbTriggers();
            triggers.forEach((t, i) => {
                t.tabIndex = i === 0 ? 0 : -1;

                // A dropdown's display shows the SELECTED item — right for a
                // value control, wrong for a menubar, where "File" stays "File"
                // after you pick Open. Pin the label for triggers that declare
                // one, replacing the instance's sync so selection cannot clear it.
                const label = t.getAttribute('label');
                if (label == null) return;
                const paint = () => {
                    const display = t.querySelector('.dd-display');
                    if (display) display.textContent = label;
                };
                t._syncDisplay = paint;
                paint();
            });

            this.addEventListener('keydown', (e) => {
                const list = this._mnbTriggers();
                if (!list.length) return;
                const current = e.target.closest(TRIGGERS);
                const idx = list.indexOf(current);
                if (idx === -1) return;

                if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    this._mnbFocus(list[(idx + 1) % list.length]);
                } else if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    this._mnbFocus(list[(idx - 1 + list.length) % list.length]);
                } else if (e.key === 'Home') {
                    e.preventDefault();
                    this._mnbFocus(list[0]);
                } else if (e.key === 'End') {
                    e.preventDefault();
                    this._mnbFocus(list[list.length - 1]);
                }
            });

            // Hover switches menus only once the bar is already "active" — a
            // stray pointer crossing a closed menubar must not open anything.
            this.addEventListener('pointerover', (e) => {
                const trigger = e.target.closest(TRIGGERS);
                if (!trigger || trigger.parentElement !== this) return;
                if (!this._mnbAnyOpen() || trigger.hasAttribute('open')) return;
                this._mnbFocus(trigger);
            });
        },
    },
};
return { __default };
})();

const __m106 = (() => {
const __default = {
    tags: ['s-pn-c', 's-pn-c'],
    aria: { role: 'listbox', itemRole: 'option' },
    itemRenderer: 's-li',
    selectable: true
};
return { __default };
})();

const __m107 = (() => {
const __default = {
    tags: ['s-sbs', 's-sbs'],
    itemRenderer: 's-sb'
};
return { __default };
})();

const __m108 = (() => {
// Stack — scroll-snap container. All items in DOM. Selection scrolls to item.

const __default = {
  tags: ['s-st', 's-st'],
  // A scrollable region must be keyboard-reachable (focusable) and its
  // stamped panels are groups; synced to a tablist, the pair machinery
  // upgrades them to labelled tabpanels.
  aria: { role: 'group', itemRole: 'group' },
  focusable: true,
  itemRenderer: 's-cn',
  selectable: true,
  syncFromParent: true,
  managesChildActivation: true,
  props: ['@l', '@dir', '@sync'],
  callbacks: {
    connected() {
      let scrollTimer;
      this.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this._detectSnap(), 80);
      }, { passive: true });
    },

    activated() {
      this.selectedIndex = 0;
    },
  },

  methods: {
    // Orientation vocabulary: @l=v / @l=h (short, canonical) with the legacy
    // dir=vertical still honored.
    _isVertical() {
      return this.getAttribute('l') === 'v' || this.dir === 'vertical';
    },

    _onSelectionChange(child) {
      if (!child) return;
      const idx = this._childIndexMap.get(child) ?? 0;
      const isVertical = this._isVertical();
      const size = isVertical ? this.offsetHeight : this.offsetWidth;
      this.scrollTo({
        [isVertical ? 'top' : 'left']: idx * size,
        behavior: 'smooth',
      });
    },

    // An axis flip keeps the selection but the scroll offset belongs to the
    // OLD axis — re-scroll to the selected panel on the new one.
    onLChange() {
      const selected = Array.from(this.children).find((c) => c.hasAttribute && c.hasAttribute('selected'));
      if (selected) this._onSelectionChange(selected);
    },
    onDirChange() { this.onLChange(); },

    _detectSnap() {
      const isVertical = this._isVertical();
      const size = isVertical ? this.offsetHeight : this.offsetWidth;
      const pos = isVertical ? this.scrollTop : this.scrollLeft;
      const idx = Math.round(pos / size);
      if (idx !== this.selectedIndex) {
        this.selectedIndex = idx;
      }
    },
  },
};
return { __default };
})();

const __m109 = (() => {
const __default = {
    tags: ['s-stpr', 's-stpr'],
    aria: { role: 'list', itemRole: 'listitem' },
    itemRenderer: 's-stp',
    selectable: true
};
return { __default };
})();

const __m110 = (() => {
const ssrSegmentChildren = __m21.ssrSegmentChildren;
const __default = {
    tags: ['s-tb-l', 's-tb-l'],
    // @seg=<key>: the server marks the tab the address names as selected, so
    // /page?tab=two paints its face first and hydration finds it right
    // (F-811). Authored tabs only; a data-stamped list selects on the client.
    // The tab role and aria-selected come from the serializer's item contract
    // (aria.itemRole below), on this expansion as on the static path.
    ssr(input) { return ssrSegmentChildren(input, { attr: 'selected' }); },
    // itemRole seeds each stamped tab's role/aria-selected at registration
    // (s-tb's own aria def keeps them in sync after that); the tablist role
    // also drives the sync mixin's pair linking — a stack syncing to this
    // list gets role=tabpanel + aria-controls/labelledby wiring per index.
    aria: { role: 'tablist', itemRole: 'tab' },
    keyboardNav: { orientation: 'horizontal', selectOnFocus: true },
    itemRenderer: 's-tb',
    selectable: true
};
return { __default };
})();

const __m111 = (() => {
// Declarative value presentation — the one place that decides how a raw value
// becomes display text plus presentation attributes. Shared by the grids
// (`s-dg`, `s-tg`), the stat tile and the charts, so "negative is red" is
// written once as DATA and means the same thing everywhere.
//
// The whole point is that a rule is JSON, not a callback. A `formatFunction`
// cannot cross the wire, cannot be emitted by a generator, and cannot be
// evaluated during SSR. A rule can do all three.
//
//   {
//     key: 'dayChange',
//     format: { style: 'currency', currencyKey: 'currency', precision: 2 },
//     rules: [ { when: { lt: 0 }, tone: 'dg' },
//              { when: { gt: 0 }, tone: 'su' },
//              { when: { absGte: 5 }, weight: 'strong' } ],
//     empty: '—',
//   }
//
// Two deliberate choices:
//
// Predicates are conjunction-only — every comparator in a `when` object must
// hold. There is no `or`, and none is coming: a disjunction is two rules. This
// matches the permission guards, where `every()` means a rule can only ever
// narrow, never widen, and it keeps the vocabulary small enough for a generator
// to emit correctly.
//
// Absent is not zero. `empty` is a separate branch evaluated BEFORE any rule,
// because null, undefined and '' are the absence of a measurement while 0 is a
// measurement. A grid that renders a missing yield as "0.00" is stating a
// falsehood about the data.

// Comparators. Each takes the coerced value and the rule's operand. Unknown
// comparators return false rather than throwing — nothing throws in a render
// path, and a typo must not blank the screen.
const COMPARATORS = {
    lt: (v, n) => typeof v === 'number' && v < n,
    lte: (v, n) => typeof v === 'number' && v <= n,
    gt: (v, n) => typeof v === 'number' && v > n,
    gte: (v, n) => typeof v === 'number' && v >= n,
    // Magnitude, for "beyond ±5%" thresholds — the common financial rule that
    // otherwise needs two mirrored comparisons.
    absGte: (v, n) => typeof v === 'number' && Math.abs(v) >= n,
    absLt: (v, n) => typeof v === 'number' && Math.abs(v) < n,
    eq: (v, n) => v === n,
    ne: (v, n) => v !== n,
    in: (v, list) => Array.isArray(list) && list.includes(v),
    between: (v, pair) => typeof v === 'number' && Array.isArray(pair) && v >= pair[0] && v <= pair[1],
};

// A value is ABSENT when there is no measurement: null, undefined, '' or NaN.
// Zero and false are measurements and are never absent.
function isAbsent(v) {
    return v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
}

// Every comparator in the `when` object must hold. An empty/missing `when`
// matches — that is how a caller writes an unconditional default rule.
function matches(when, value) {
    if (!when || typeof when !== 'object') return true;
    for (const [op, operand] of Object.entries(when)) {
        const cmp = COMPARATORS[op];
        if (!cmp || !cmp(value, operand)) return false;
    }
    return true;
}

// Thousands grouping, applied to the integer part only. Kept here rather than
// in the stat tile so the grid, the tile and the chart axis all group the same
// way — this logic previously existed only inside `s-stat`.
function group(intPart, sep) {
    return sep === '' ? intPart : intPart.replace(/\B(?=(\d{3})+(?!\d))/g, sep || ',');
}

const CURRENCY_SYMBOLS = { GBP: '£', USD: '$', EUR: '€', JPY: '¥', CHF: 'CHF ', AUD: 'A$', CAD: 'C$' };

// Raw value → display string, per the column's `format` descriptor. The row is
// passed so a per-row currency (`currencyKey`) can be read — mixed-currency
// books are the normal case in a portfolio, not an edge case.
function formatValue(value, format, row) {
    if (!format) return String(value);
    const f = typeof format === 'string' ? { style: format } : format;
    const num = typeof value === 'number' ? value : Number(value);

    if (f.style === 'text' || Number.isNaN(num)) return String(value);

    const precision = f.precision === undefined || f.precision === null ? null : Number(f.precision);
    const fixed = precision === null ? String(num) : num.toFixed(precision);
    const negative = fixed.startsWith('-');
    const parts = (negative ? fixed.slice(1) : fixed).split('.');
    parts[0] = group(parts[0], f.grouping);
    let body = parts.join('.');

    if (f.style === 'percent') body += '%';
    if (f.style === 'currency') {
        const code = (f.currencyKey && row && row[f.currencyKey]) || f.currency || '';
        const symbol = CURRENCY_SYMBOLS[code] !== undefined ? CURRENCY_SYMBOLS[code] : (code ? `${code} ` : '');
        body = `${symbol}${body}`;
    }
    // The sign leads the whole thing, symbol included: "-£84.20", not "£-84.20".
    if (negative) body = `-${body}`;
    if (f.prefix) body = `${f.prefix}${body}`;
    if (f.suffix) body = `${body}${f.suffix}`;
    return body;
}

// The unit a column measures in, for the HEADER. A reader scanning a column of
// numbers should not have to infer what they are.
//
// Returns null when the unit varies per row (`currencyKey`): naming one currency
// in the header would be false for the other rows, and those values carry their
// own symbol precisely because they differ.
function columnUnit(col) {
    const f = col && col.format;
    if (!f || f.style === 'text') return null;
    if (f.style === 'percent') return '%';
    if (f.style === 'currency') {
        if (f.currencyKey) return null;
        if (!f.currency) return null;
        return CURRENCY_SYMBOLS[f.currency] !== undefined ? CURRENCY_SYMBOLS[f.currency].trim() : f.currency;
    }
    return null;
}

// A column of numbers aligns right — header, values and totals together — so
// the digits line up and the eye can compare magnitudes down the column. This
// is why it must be decided per COLUMN and applied to all three, rather than
// styled onto whichever part someone remembered.
function isNumericColumn(col) {
    if (!col) return false;
    if (col.type === 'spark' || col.type === 'chip' || col.type === 'actions') return false;
    const f = col.format;
    return !!((f && f.style && f.style !== 'text') || col.agg);
}

// The header text, with its unit appended when there is one.
function columnHeading(col) {
    const label = (col && (col.label || col.key)) || '';
    const unit = columnUnit(col);
    return unit ? `${label} (${unit})` : label;
}

// The full evaluation for one value: absence branch first, then the first
// matching rule wins. Returns display text plus the presentation attributes the
// caller turns into DSL — never a style string, because a component never
// writes inline styles.
//
//   { text, tone, weight, absent }
//
// `tone` is one of the framework's existing severity codes (su/dg/wn/nt) so the
// stylesheet that already understands `[c=su]` keeps owning the colour. A rule
// naming a tone the CSS does not define renders unstyled rather than broken.
function presentValue(value, col, row) {
    const cfg = col || {};
    if (isAbsent(value)) {
        // No rules run against an absent value. A rule like `{ lt: 0 }` must not
        // silently treat missing data as zero and colour the cell red.
        const empty = cfg.empty;
        const text = empty === undefined || empty === null ? '—' : String(empty);
        return { text, absent: true };
    }

    const out = { text: formatValue(value, cfg.format, row), absent: false };
    const rules = Array.isArray(cfg.rules) ? cfg.rules : null;
    if (!rules) return out;

    // First match wins for each presentation channel, so an earlier rule's tone
    // is not overwritten by a later one — but a later rule may still contribute
    // a channel no earlier rule set (tone from the sign rule, weight from the
    // threshold rule).
    for (const rule of rules) {
        if (!rule || !matches(rule.when, value)) continue;
        if (rule.tone && !out.tone) out.tone = rule.tone;
        if (rule.weight && !out.weight) out.weight = rule.weight;
        if (rule.text !== undefined && out.textOverridden !== true) {
            out.text = String(rule.text);
            out.textOverridden = true;
        }
    }
    delete out.textOverridden;
    return out;
}

// Aggregation over child rows, for tree-grid section totals. Declared on the
// column as `agg: 'sum' | 'avg' | 'count' | 'min' | 'max'`.
//
// Absent values are SKIPPED, never coerced to zero: the average of [10, null]
// is 10, not 5. `count` counts rows that carry a measurement, which is the
// figure a reader means by "how many priced positions".
const REDUCERS = {
    sum: (nums) => nums.reduce((a, b) => a + b, 0),
    avg: (nums) => (nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null),
    count: (nums) => nums.length,
    min: (nums) => (nums.length ? Math.min(...nums) : null),
    max: (nums) => (nums.length ? Math.max(...nums) : null),
};

function aggregate(rows, col) {
    const kind = col && col.agg;
    if (!kind || !REDUCERS[kind] || !Array.isArray(rows)) return null;

    // Refuse to total a mixed-currency column. £25,716 + $9,184 is not a
    // quantity, and rendering one anyway states something false — the reader
    // has no way to know the number is meaningless. Absent is the honest
    // answer, and the cell already knows how to render that.
    const currencyKey = col.format && col.format.currencyKey;
    if (currencyKey && kind !== 'count') {
        const seen = new Set();
        for (const row of rows) {
            const code = row && row[currencyKey];
            if (code !== undefined && code !== null && code !== '') seen.add(code);
        }
        if (seen.size > 1) return null;
    }

    const nums = [];
    for (const row of rows) {
        const v = row ? row[col.key] : null;
        if (isAbsent(v)) continue;
        const n = typeof v === 'number' ? v : Number(v);
        if (!Number.isNaN(n)) nums.push(n);
    }
    // An aggregate over nothing is absent, not zero — the same rule as a cell.
    if (!nums.length && kind !== 'count') return null;
    return REDUCERS[kind](nums);
}

// ── Rule inference ──
//
// Field-name heuristics, deliberately small and boring: a wrong guess produces a
// slightly odd column, never a broken one, and a caller who cares passes columns
// explicitly. This lives here rather than in the generate endpoint so that BOTH
// sides can use it — the server bakes rules into a contract, and a client using
// `cols: 'auto'` re-derives them when the data is swapped. If it lived only on
// the server, an adaptive screen would lose its colouring the moment it was
// handed a different dataset, which is exactly what adaptivity is supposed to
// survive.
const PERCENT_FIELD = /(pct|percent|rate|yield|ratio|margin|return)$/i;
const MONEY_FIELD = /(price|value|cost|amount|total|balance|salary|revenue|fee|spend|budget)/i;
const COUNT_FIELD = /(count|qty|quantity|units|shares)/i;

const SIGN_RULES = [
    { when: { lt: 0 }, tone: 'dg' },
    { when: { gt: 0 }, tone: 'su' },
    // Three-way on purpose: a two-way rule renders an exact zero as a loss.
    { when: { eq: 0 }, tone: 'nt' },
];

function currencyKeyFor(sample) {
    return ['currency', 'ccy', 'currencyCode'].find((k) => sample && sample[k] !== undefined) || null;
}

// What the data itself says about one field.
function profileField(rows, key) {
    let hasAbsent = false;
    let hasSeries = false;
    let present = 0;
    let numeric = 0;
    let hasNegative = false;
    for (const row of rows) {
        const v = row ? row[key] : undefined;
        if (isAbsent(v)) { hasAbsent = true; continue; }
        present += 1;
        if (Array.isArray(v)) { hasSeries = true; continue; }
        const n = typeof v === 'number' ? v : Number(v);
        if (!Number.isNaN(n) && v !== true && v !== false) { numeric += 1; if (n < 0) hasNegative = true; }
    }
    return { hasAbsent, hasSeries, numeric: present > 0 && numeric === present, hasNegative };
}

// One column descriptor with its rules, derived from the values themselves.
function columnRules(key, label, rows, opts = {}) {
    const p = profileField(rows, key);
    const sample = rows.find((r) => r && typeof r === 'object') || {};
    const col = { key, label };

    // A field holding arrays is a trend, not a number to print.
    if (p.hasSeries) return { key, label, type: 'spark', seriesKey: key };

    if (p.numeric) {
        if (PERCENT_FIELD.test(key)) {
            col.format = { style: 'percent', precision: 2 };
        } else if (MONEY_FIELD.test(key)) {
            const currencyKey = currencyKeyFor(sample);
            col.format = currencyKey
                ? { style: 'currency', currencyKey, precision: 2, grouping: ',' }
                : { style: 'currency', currency: opts.currency || 'GBP', precision: 2, grouping: ',' };
        } else if (COUNT_FIELD.test(key)) {
            col.format = { style: 'number', precision: 0, grouping: ',' };
        } else {
            col.format = { style: 'number', precision: 2, grouping: ',' };
        }

        // Sign colouring only where a sign actually occurs — painting an
        // all-positive column green says nothing and reads as decoration.
        if (p.hasNegative) col.rules = [...SIGN_RULES];

        // A percentage averages; everything else sums. Summing a column of
        // percentages produces a number that means nothing.
        if (opts.aggregate) col.agg = PERCENT_FIELD.test(key) ? 'avg' : 'sum';
    }

    // Declared only where the data actually has gaps, so the contract stays as
    // small as the data requires.
    if (p.hasAbsent) col.empty = '—';

    return col;
}

// Column inference for adaptive screens: given rows, derive the column set from
// the fields the data actually carries. This is what lets one generated screen
// render a different book without being regenerated.
//
// Keys are collected in first-seen order across a sample of rows (not just the
// first row, which may itself have absent fields). A field whose every sampled
// value is absent is dropped rather than rendered as a column of dashes.
function inferColumns(rows, opts) {
    const o = opts || {};
    const sample = Array.isArray(rows) ? rows.slice(0, o.sample || 50) : [];
    const seen = [];
    const populated = new Set();
    for (const row of sample) {
        if (!row || typeof row !== 'object') continue;
        for (const key of Object.keys(row)) {
            // Framework bookkeeping and nested structures are not columns.
            if (key.startsWith('_')) continue;
            const v = row[key];
            // An array of numbers is a trend, and becomes a sparkline column —
            // but only in rules mode, where something knows what to do with it.
            // Plain inference keeps its promise that nested data is not a column.
            const isSeries = o.rules && Array.isArray(v) && v.length > 0 && v.every((n) => typeof n === 'number');
            if (v && typeof v === 'object' && !isSeries) continue;
            if (!seen.includes(key)) seen.push(key);
            if (!isAbsent(v)) populated.add(key);
        }
    }
    const omit = new Set(o.omit || []);
    const keys = seen.filter((key) => populated.has(key) && !omit.has(key));
    // `rules: true` attaches the inferred format/rules/agg/empty, so an
    // adaptive grid keeps its colouring and its totals when the data is
    // swapped — not just its column names.
    if (o.rules) return keys.map((key) => columnRules(key, o.labels?.[key] || titleise(key), sample, o));
    return keys.map((key) => ({ key, label: o.labels && o.labels[key] ? o.labels[key] : titleise(key) }));
}

// `dayChangePct` → "Day Change Pct". Good enough to be useful, and a caller
// that cares passes `labels`.
function titleise(key) {
    return key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^./, (c) => c.toUpperCase());
}
return { isAbsent, matches, formatValue, columnUnit, isNumericColumn, columnHeading, presentValue, aggregate, columnRules, inferColumns };
})();

const __m112 = (() => {
const presentValue = __m111.presentValue;
const aggregate = __m111.aggregate;
const inferColumns = __m111.inferColumns;
const columnHeading = __m111.columnHeading;
const isNumericColumn = __m111.isNumericColumn;
const resolveGuard = __m10.resolveGuard;
const guardContext = __m10.guardContext;
const REDACTED_TEXT = __m10.REDACTED_TEXT;
const globalStore = __m0.store;
// A column's width becomes a grid track. `width: 150` is the natural way to say
// a fixed column and is what table libraries hand over (TanStack's `size`), but
// a bare number is not a CSS length: `grid-template-columns: 150 150 175 100`
// is invalid, the whole declaration is dropped, and the grid silently collapses
// to ONE column with every cell on its own row. Numbers mean pixels.
const trackFor = (c) => {
    const w = c && c.width;
    // An explicit width is the author's call, including the right to clip.
    if (w !== undefined && w !== null && w !== '') {
        return typeof w === 'number' || /^\d+(\.\d+)?$/.test(String(w)) ? `${w}px` : String(w);
    }
    // A numeric column is tracked so it can never be narrower than its widest
    // value. Losing digits to an ellipsis changes what a number says, which is
    // a correctness problem rather than a cosmetic one.
    return isNumericColumn(c) ? 'minmax(max-content, 1fr)' : '1fr';
};

// aria-sort takes 'ascending' / 'descending' / 'none' / 'other'. The header's
// `sort` attribute reflects straight onto aria-sort, so it carries the valid
// vocabulary rather than a short form the stylesheet would then translate.
const ASC = 'ascending';
const DESC = 'descending';

const dgHeader = {
    tags: ['s-dg-h', 's-dg-h'],
    props: ['@sort', '@key', '@pinned'],
    // Valid grid context now exists (s-dg-hdr/s-dg-c are rows): a real
    // columnheader whose sort state reflects to aria-sort.
    aria: { role: 'columnheader', sort: 'sort' },
    emits: { click: 'itemclick' },
};

// The editor a cell shows while it is being edited: a select when the column
// declares options, a text/number input otherwise. The value and the label
// travel as object tokens so any character survives.
function editorDSL(cell) {
    const label = `Edit ${cell.label || cell.key}`;
    const value = cell.value ?? '';
    if (Array.isArray(cell.options)) {
        const options = cell.options.map((opt) => {
            const optValue = typeof opt === 'object' && opt !== null ? opt.value : opt;
            const optText = typeof opt === 'object' && opt !== null ? opt.text : opt;
            const tokens = [{ '@value': String(optValue) }];
            if (String(optValue) === String(value)) tokens.push('@selected=');
            tokens.push({ ':textContent': String(optText) });
            return ['option', ...tokens];
        });
        return ['select', { '@aria-label': label }, options];
    }
    return ['input', `@type=${cell.inputType === 'number' ? 'number' : 'text'}`, { '@value': String(value), '@aria-label': label }];
}

// One cell → DSL, by column type. Default is plain text; `chip` renders a status
// pill, `actions` renders a row of icon buttons (each carries `act`, handled by
// the grid), `render` defers to a per-column item component fed the whole row,
// `dsl` carries authored content (a filter control, the footer). A cell that is
// editable carries the attribute; one that is being edited carries its editor.
function cellToDSL(cell) {
    if (cell === null || cell === undefined || typeof cell === 'string' || typeof cell === 'number') {
        return ['span', '@role=gridcell', `="${cell ?? ''}"`];
    }
    // A guarded cell carries its state for the stylesheet and for testing.
    const guard = cell.guard ? [`@data-guard=${cell.guard}`] : [];
    // Editing state, pinning and the column identity ride on the cell element
    // as attributes (state the stylesheet paints) and a property (state the
    // grid reads back), never as data-* coordinates.
    const state = [];
    if (cell.pinned) state.push('@pinned=');
    if (cell.span) state.push('@span=');
    if (cell.editing) {
        return ['span', '@role=gridcell', '@editing=', ...state, { ':colKey': cell.key }, [editorDSL(cell)]];
    }
    if (cell.editable) state.push('@editable=', { ':colKey': cell.key });
    if (cell.type === 'dsl') {
        return ['span', '@role=gridcell', ...state, cell.dsl];
    }
    if (cell.type === 'render') {
        // The column's `renderer` tag is an item component; it receives the row
        // via .data and renders the cell itself (image, avatar, progress, …).
        return [cell.renderer, '@role=gridcell', ...state, { ':data': cell.row }];
    }
    if (cell.type === 'chip') {
        const attrs = cell.severity ? [`@c=${cell.severity}`] : [];
        return ['s-ch', '@role=gridcell', ...attrs, ...guard, ...state, `="${cell.value ?? ''}"`];
    }
    // A formatted value: text plus presentation ATTRIBUTES. The stylesheet owns
    // the colour — `@tone=` carries the framework's severity code, so `su` here
    // means what it means on a chip, without inheriting `[c=]`'s fill variables.
    // `@absent` marks a cell that carries no measurement, so CSS can mute it and
    // a test can assert it is not a zero.
    if (cell.type === 'value') {
        const attrs = [];
        if (cell.tone) attrs.push(`@tone=${cell.tone}`);
        if (cell.emph) attrs.push(`@emph=${cell.emph}`);
        if (cell.absent) attrs.push('@absent=');
        if (cell.num) attrs.push('@num=');
        return ['span', '@role=gridcell', ...attrs, ...guard, ...state, `="${cell.text}"`];
    }
    // A per-row trend line. The chart keeps its own role=img and label — a
    // canvas has no text alternative — so the gridcell role goes on a wrapper
    // rather than overwriting it.
    if (cell.type === 'spark') {
        return ['span', '@role=gridcell', ...state, [
            ['s-spk', { '@label': cell.label }, { ':data': cell.series }],
        ]];
    }
    if (cell.type === 'actions') {
        return ['s-cn', '@role=gridcell', '@l=row', ...guard, ...state, (cell.actions || []).map((a) =>
            // The action's machine name is the accessible fallback; callers
            // pass a.label for a human one.
            ['s-b', '@v=ic', '@sz=sm', `@act=${a.act}`, { '@aria-label': a.label || a.act }, [['s-ic', `@n=${a.icon}`]]])];
    }
    return ['span', '@role=gridcell', ...state, `="${cell.value ?? ''}"`];
}

// A column → the cell descriptor for a given row. Shared by the client render
// path (_cell) and the SSR expansion so the two can't drift. A column may declare
// a `renderer` (item component fed the whole row), or a `type`: 'chip' / 'actions';
// anything else is plain text.
// A column may carry a guard: `guard: 'salary.view'`.
//
// The full precedence chain resolves here — record verdict, then ambient rule,
// then deny. An earlier version passed only `{ data: row }`, which meant a
// column guard could consult NOTHING but an inline `_guards` entry: a name with
// no entry failed closed with no recourse, and no locally registered rule could
// ever apply in a grid. That made column guards unusable for anything but a
// datasource that already ships verdicts.
//
// The store differs by side on purpose. In the browser the module singleton is
// the one user's session. During SSR it is the PER-REQUEST store handed to
// ssr(), because the module-level one is shared across requests and reading a
// session from it would be a cross-tenant bug.
//
// `hidden` cannot omit the cell — that would shift every column after it out of
// alignment. It renders an EMPTY cell instead, and the point is preserved
// regardless: the value never reaches the markup.
//
// Editing and pinning are column properties, so they are attached here: an
// editable column's cell is editable only when its guard resolves to enabled,
// which keeps a redacted, hidden, read-only or disabled cell out of the editor.
function cellFor(col, row, store) {
    const base = baseCellFor(col, row);
    if (!col.guard) return decorate(base, col, true);

    const outcome = resolveGuard(col.guard, guardContext(store, row));
    if (outcome.state === 'enabled') return decorate(base, col, true);
    if (outcome.state === 'hidden') return decorate({ type: 'value', text: '', guard: 'hidden' }, col, false);
    if (outcome.state === 'redact') return decorate({ type: 'value', text: REDACTED_TEXT, guard: 'redact' }, col, false);
    // readOnly / disabled / conflict keep the content and carry the state.
    return decorate(typeof base === 'string'
        ? { type: 'value', text: base, guard: outcome.state }
        : { ...base, guard: outcome.state }, col, false);
}

// A column that declares neither `editable` nor `pinned` keeps its descriptor
// byte for byte — a plain string stays a plain string — so existing grids are
// untouched by the capabilities they do not use.
function decorate(base, col, enabled) {
    const editable = enabled && col.editable === true;
    const pinned = col.pinned === 'left';
    // A numeric column's cells align with their heading: a `type: 'number'`
    // column that declares no format still carries `num` on every cell.
    const num = isNumericColumn(col) && (typeof base === 'string' || (base && base.type === 'value' && !base.num));
    if (!editable && !pinned && !num) return base;
    const cell = typeof base === 'string' ? { type: 'value', text: base } : { ...base };
    if (num) cell.num = true;
    if (editable) { cell.editable = true; cell.key = col.key; }
    if (pinned) cell.pinned = true;
    return cell;
}

// Whether a column's guard lets THIS row's cell be read. A column without a
// guard is readable; anything but an enabled verdict is not.
function readable(col, row, store) {
    if (!col.guard) return true;
    return resolveGuard(col.guard, guardContext(store, row)).state === 'enabled';
}

function baseCellFor(col, row) {
    if (col.renderer) {
        return { type: 'render', renderer: col.renderer, row };
    }
    const raw = row[col.key];
    if (col.type === 'chip') {
        return { type: 'chip', value: raw ?? '', severity: col.severity && col.severity[raw] };
    }
    if (col.type === 'actions') {
        return { type: 'actions', actions: col.actions || [] };
    }
    // `seriesKey` lets the trend live in a different field from the column's
    // own key, which is the normal case: a "Trend" column reading `series`.
    if (col.type === 'spark') {
        return {
            type: 'spark',
            series: row[col.seriesKey || col.key] || [],
            label: `${col.label || col.key} trend`,
        };
    }
    // Declarative presentation is OPT-IN per column: a column that declares
    // none of format/rules/empty keeps the plain-text behaviour byte for byte,
    // so existing grids are untouched. Declaring any one of them routes the
    // value through the shared evaluator — including the absent branch, which
    // is why `empty` alone is enough to fix a column that renders nulls as ''.
    if (col.format || col.rules || col.empty !== undefined) {
        const p = presentValue(raw, col, row);
        return { type: 'value', text: p.text, tone: p.tone, emph: p.weight, absent: p.absent, num: isNumericColumn(col) };
    }
    return String(raw ?? '');
}

// The text a cell shows, for the CSV export: the same descriptor the DOM gets,
// so a guarded cell exports exactly what it displays and nothing more.
function cellText(cell) {
    if (cell === null || cell === undefined) return '';
    if (typeof cell !== 'object') return String(cell);
    if (cell.type === 'value') return cell.text ?? '';
    if (cell.type === 'chip') return String(cell.value ?? '');
    return '';
}

// The filter control for a column: a select over the column's options, or a
// search input. Identity rides on the control as the `colKey` property; the
// grid tells a filter from an editor by that property alone.
function filterDSL(col, value) {
    const label = `Filter ${columnHeading(col)}`;
    if (Array.isArray(col.options)) {
        const options = [['option', { '@value': '' }, '=All']];
        col.options.forEach((opt) => {
            const optValue = typeof opt === 'object' && opt !== null ? opt.value : opt;
            const optText = typeof opt === 'object' && opt !== null ? opt.text : opt;
            const tokens = [{ '@value': String(optValue) }];
            if (String(optValue) === String(value)) tokens.push('@selected=');
            tokens.push({ ':textContent': String(optText) });
            options.push(['option', ...tokens]);
        });
        return ['select', { '@aria-label': label, ':colKey': col.key }, options];
    }
    return ['input', '@type=search', { '@aria-label': label, '@value': String(value ?? ''), ':colKey': col.key }];
}

const isControl = (el) => Boolean(el) && (el.tagName === 'INPUT' || el.tagName === 'SELECT');

// A row. Its cells are the children; each cell gets a reference back to the
// row and its column position as JS properties at render time, so a keyboard
// or pointer event on a cell resolves to (row, column) without walking the DOM.
const dgCell = {
    tags: ['s-dg-c', 's-dg-c'],
    props: ['@selected'],
    aria: { role: 'row' },
    // The row hands itself over: detail.item is this element, whose _index the
    // grid resolves against ITS displayed order (it owns the sort). Action
    // buttons never reach this — the grid intercepts them in capture phase.
    emits: { click: 'cellclick' },
    methods: {
        // Custom render (not a fixed itemRenderer) so a cell can be text, a chip, or actions.
        set data(cells) {
            this._data = cells;
            this.replaceChildren();
            this._applyChildElements((cells || []).map(cellToDSL), this);
            Array.from(this.children).forEach((cell, i) => { cell._row = this; cell._col = i; });
            // A structural row with nothing in it (no filters, no totals, no
            // footer) leaves the tree entirely: an empty role=row is a hole
            // for arrow navigation and a lie to assistive technology.
            this.hidden = !(cells && cells.length);
        },
        get data() { return this._data; },
    },
};

const dgHeaderList = {
    tag: 's-dg-hdr',
    aria: { role: 'row' },
    itemRenderer: 's-dg-h',
};

const dgBody = {
    tag: 's-dg-body',
    aria: { role: 'rowgroup' },
    itemRenderer: 's-dg-c',
};

// ── s-dg ── THE data grid. One component carries every tabular capability
// as attributes and column configuration: sort (always), per-column filters
// (@filterable), pagination (@page-size), row selection (@selectable, @multi,
// @key-field), inline editing (column.editable, column.options), column resize
// (@resizable) and pinning (column.pinned), aggregation totals (column.agg)
// and CSV export (@exportable). A grid that declares none of them renders
// exactly what it always did.
//
// The pipeline is unsorted → filtered → sorted → paged. Totals roll over the
// FILTERED rows (a total that ignores the filter is a lie); the export writes
// the filtered + sorted set with guards applied. Sort, filters, selection and
// page are state and survive data re-sets; selection survives only when
// @key-field names a stable identity, otherwise a replacement clears it.
//
// Every region is a row of cells (filters, body, totals, footer), so the
// role=grid structure stays valid and the grid-nav mixin roves across all of
// it. Only BODY rows are s-dg-c — a consumer's `s-dg-c` query keeps meaning
// "a data row" — the other regions are containers with a row role, hidden
// rather than left as an empty row when they have nothing to show.
//
// Events: cellaction {action, row, rowIndex}, rowclick {row, rowIndex, item},
// celledit {key, value, oldValue, row}, rowselect {value, rows},
// export {csv, count}.
const dataGrid = {
    tags: ['s-dg', 's-dg'],
    dispatches: ['cellaction', 'rowclick', 'celledit', 'rowselect', 'export'],
    props: ['@striped', '@hover', '@page-size', '@filterable', '@exportable', '@selectable', '@multi',
        '@resizable', '@loading', '@key-field', '@empty-text'],
    // An interactive grid (sort, row actions): 2D arrow navigation over the
    // row/columnheader/gridcell structure via the grid-nav mixin.
    aria: { role: 'grid' },
    gridNav: true,
    children: [
        ['s-dg-hdr', ':rf=hdr', '$headers', '~itcl:call:_sort'],
        ['s-cn', '@role=row', '@filters=', ':rf=filters', ':dk=filterDefs', ':df=children'],
        ['s-dg-body', ':rf=body', '$rows'],
        ['s-cn', '@role=row', '@totals=', ':rf=totals', ':dk=totalDefs', ':df=children'],
        ['s-cn', '@role=row', '@foot=', ':rf=foot', ':dk=footDefs', ':df=children'],
    ],
    // SSR expansion: the grid derives its regions from cols+data through JS the
    // serializer can't run (setData in the property setters), so build the same
    // DSL here: headers, the filter row when @filterable, the first page of
    // rows, the totals row when a column aggregates, and the footer when the
    // grid pages or exports. cols arrive via :cols (props.cols) or a { columns }
    // data bundle; rows via :dataSource or the data array. Mirrors _render; the
    // --cols custom property is set on the host for the grid CSS. Interactive
    // state (sort, filters, selection) is client-side by definition.
    ssr({ store, dataSource, props, attrs }) {
        let cols = props && props.cols;
        let rows = dataSource && store ? store.getValue(dataSource) : (props && props.data);
        if (rows && !Array.isArray(rows) && typeof rows === 'object') {
            cols = cols || rows.columns;
            rows = rows.data || rows.rows;
        }
        rows = Array.isArray(rows) ? rows : [];
        // Same inference on the server, so an adaptive grid is not blank until
        // hydration — the columns must already be in the HTML.
        if (cols === 'auto') cols = inferColumns(rows, { rules: true, ...((props && props.autoColumns) || {}) });
        if (!Array.isArray(cols) || !cols.length) return null;
        const a = attrs || {};
        const has = (name) => a[name] !== undefined && a[name] !== null;
        const style = `--cols:${cols.map(trackFor).join(' ')}`;
        const headers = cols.map((col) => ['s-dg-h', `@key=${col.key}`,
            ...(isNumericColumn(col) ? ['@num='] : []),
            ...(col.pinned === 'left' ? ['@pinned='] : []),
            `="${columnHeading(col)}"`]);
        const children = [['s-dg-hdr', headers]];
        if (has('filterable')) {
            children.push(['s-cn', '@role=row', '@filters=', cols.map((col) => cellToDSL({ type: 'dsl', dsl: [filterDSL(col, '')], pinned: col.pinned === 'left' }))]);
        }
        const pageSize = Number(a['page-size']) || 0;
        const pageRows = pageSize > 0 ? rows.slice(0, pageSize) : rows;
        // The per-request store, never the module singleton. Loading and the
        // empty state paint the same rows the client paints for them.
        // A selectable grid's rows carry their (unselected) state from the
        // first paint, the same as the client paints them — the loading and
        // empty rows included, since the client paints those too.
        const selectable = has('selectable') || has('multi');
        const rowTokens = selectable ? ['@aria-selected=false'] : [];
        let body;
        if (has('loading')) body = skeletonRows(cols, rowTokens);
        else if (!pageRows.length) body = emptyRows(a['empty-text'], rowTokens);
        else body = pageRows.map((row) => ['s-dg-c', ...rowTokens, cols.map((col) => cellToDSL(cellFor(col, row, store)))]);
        children.push(['s-dg-body', body]);
        if (!has('loading') && rows.length && cols.some((col) => col && col.agg)) {
            children.push(['s-cn', '@role=row', '@totals=', totalCells(cols, rows, store).map(cellToDSL)]);
        }
        const foot = footDSL(pageSize, rows.length, 1, has('exportable'));
        if (foot) children.push(['s-cn', '@role=row', '@foot=', [cellToDSL({ type: 'dsl', span: true, dsl: [foot] })]]);
        return { style, children };
    },
    callbacks: {
        // The :dataSource binding sets .cols (via .data) while the grid is still
        // being built, before connect. At that point the --cols custom property
        // cannot land, so re-apply it once connected. This also covers the
        // router re-inserting a cached page element.
        connected() {
            this._filters = this._filters || {};
            this._selection = this._selection || new Set();
            this._widths = this._widths || {};
            this._page = this._page || 1;
            this._applyCols();
            this._render();
            if (this._dgWired) return;
            this._dgWired = true;

            // Row-action buttons emit a `cellaction` event (and don't trigger a
            // row click). CAPTURE phase: stopping here keeps the click from ever
            // reaching the cell, whose own `cellclick` emit must not fire for an
            // action press.
            this.addEventListener('click', (e) => {
                const btn = e.target.closest('[act]');
                if (!btn) return;
                e.stopPropagation();
                const cell = e.target.closest('[role=gridcell]');
                const rowIndex = cell && cell._row && Number.isFinite(cell._row._index) ? cell._row._index : -1;
                this.dispatchEvent(new CustomEvent('cellaction', { bubbles: true, detail: { action: btn.getAttribute('act'), rowIndex, row: this._displayed()[rowIndex] } }));
            }, true);
            // A click on an editable cell opens its editor; the footer's export
            // button exports. Both are located from the event target (which
            // element was pressed), and their state is read from the JS
            // references the row stamped on its cells.
            this.addEventListener('click', (e) => {
                if (this._suppressClick) { this._suppressClick = false; e.stopPropagation(); return; }
                if (e.target.closest('.dg-export')) { this._download(); return; }
                const cell = e.target.closest('[role=gridcell]');
                if (cell && cell.hasAttribute('editable') && !this._edit && cell._row) this._startEdit(cell._row._index, cell._col);
            });
            // A row click announces the ROW: the cell element arrives on
            // `cellclick` (its emits), and only the grid can turn its _index
            // into the raw row, because the displayed order is the grid's sort.
            // The same click toggles selection when the grid is selectable.
            this.addEventListener('cellclick', (e) => {
                const rowEl = e.detail && e.detail.item;
                if (!rowEl || !Number.isFinite(rowEl._index)) return;
                const row = this._displayed()[rowEl._index];
                if (!row) return;
                this.dispatchEvent(new CustomEvent('rowclick', {
                    bubbles: true,
                    detail: { row, rowIndex: rowEl._index, item: rowEl },
                }));
                if (this.selectable || this.multi) this._toggleRow(row);
            });
            // Filters: a search input filters on every keystroke, a select on
            // change. Only the result regions re-render, so the control keeps
            // its focus and its caret across continuous typing.
            const onFilter = (e) => {
                const t = e.target;
                if (!isControl(t) || t.colKey === undefined) return;
                if (t.tagName === 'SELECT' && e.type !== 'change') return;
                // A search input fires `change` when it loses focus; if its
                // value is what the grid already filters on there is nothing
                // to do, and re-rendering would pull the headers out from
                // under the click that took the focus.
                if ((this._filters[t.colKey] || '') === (t.value || '')) return;
                this._filters[t.colKey] = t.value;
                this._page = 1;
                this._render();
            };
            this.addEventListener('input', onFilter);
            this.addEventListener('change', (e) => {
                if (e.target && e.target.localName === 's-pg') { this._pageTo(e.detail && e.detail.current); return; }
                if (isControl(e.target) && e.target.colKey === undefined && this._edit) { this._commit(e.target.value); return; }
                onFilter(e);
            });
            // Leaving the editor commits it. A commit that already happened
            // (Enter, Tab) has cleared _edit, so the blur that follows is inert.
            this.addEventListener('focusout', (e) => {
                if (this._edit && isControl(e.target) && e.target.colKey === undefined) this._commit(e.target.value);
            });
            // Keys inside a control (editor or filter) belong to its caret:
            // CAPTURE phase, so the grid-nav bubble listener never sees them.
            this.addEventListener('keydown', (e) => this._controlKeydown(e), true);
            // Keys on a focused cell: Enter/Space on a columnheader sorts,
            // mirroring the click path; Enter or F2 on an editable cell edits;
            // Space on a body cell toggles its row's selection.
            this.addEventListener('keydown', (e) => this._cellKeydown(e));
            // Column resize: a press on the trailing edge of a header, with the
            // pointer captured so a release anywhere ends it.
            this.addEventListener('pointerdown', (e) => this._beginResize(e));
        },
    },
    methods: {
        set cols(v) {
            this._cols = v;
            this._applyCols();
        },
        get cols() { return this._cols; },

        _applyCols() {
            if (!this._isConnected || !Array.isArray(this._cols)) return;
            this._applyTracks();
            this.toggleAttribute('scroll', Boolean(this.resizable) || this._cols.some((c) => c && c.pinned === 'left'));
            this._rebuildHeaders();
            this._rebuildFilters();
        },

        _applyTracks() {
            this.style.setProperty('--cols', this._cols.map((col) => (
                this._widths[col.key] ? `${this._widths[col.key]}px` : trackFor(col)
            )).join(' '));
        },

        set data(v) {
            let incoming;
            if (v && typeof v === 'object' && !Array.isArray(v)) {
                incoming = v.columns;
                this._unsorted = v.data || v.rows || [];
            } else {
                this._unsorted = Array.isArray(v) ? v : [];
            }
            // `columns: 'auto'` (or `.cols = 'auto'` set before the data
            // arrives) derives the column set from the rows themselves. This is
            // what lets one screen render a different dataset without being
            // edited: the columns are a function of the data, not a constant.
            if (incoming === 'auto' || (incoming === undefined && this._cols === 'auto')) {
                // `rules: true` — a swapped dataset keeps its formatting and
                // colouring, not just its column names. Losing the rules on a
                // data swap would defeat the point of being adaptive.
                this.cols = inferColumns(this._unsorted, { rules: true, ...(this.autoColumns || {}) });
            } else if (incoming) {
                this.cols = incoming;
            }
            // Selection follows a stable identity across a data re-set (store
            // re-fires). Without @key-field the identity IS the row object, so
            // a replacement clears it rather than transferring it by position.
            if (this._selection && this._selection.size) {
                const alive = new Set((this._unsorted || []).map((row) => this._rowId(row)));
                this._selection.forEach((id) => { if (!alive.has(id)) this._selection.delete(id); });
            }
            this._render();
        },
        get data() { return this._unsorted; },

        get value() { return Array.from(this._selection || []); },
        get selectedRows() {
            const ids = this._selection || new Set();
            return (this._unsorted || []).filter((row) => ids.has(this._rowId(row)));
        },
        clearSelection() {
            if (!this._selection || !this._selection.size) return;
            this._selection.clear();
            this._paintSelection();
            this._announceSelection();
        },

        onPageSizeChange() { this._page = 1; this._render(); },
        onFilterableChange() { this._rebuildFilters(); this._render(); },
        onExportableChange() { this._render(); },
        onLoadingChange() { this._render(); },
        onSelectableChange() { this._paintSelection(); },
        onMultiChange() { this._paintSelection(); },
        onResizableChange() { this._applyCols(); },

        // ── The pipeline: unsorted → filtered → sorted → paged ──
        _rowId(row) {
            const keyField = this['key-field'];
            return keyField && row && row[keyField] !== undefined ? row[keyField] : row;
        },
        // The value a filter or a sort may consult. An unguarded column
        // offers its raw value (so numbers sort as numbers); a guarded column
        // offers only what its cell DISPLAYS for that row — a redacted or
        // hidden value is unreachable through filter membership or sort
        // order, which would otherwise disclose it to anyone who can compare.
        _comparable(col, row) {
            if (!col) return undefined;
            if (!col.guard) return row[col.key];
            return cellText(this._cell(col, row));
        },
        _filteredRows() {
            const active = Object.entries(this._filters || {}).filter(([, v]) => v !== '' && v != null);
            if (!active.length) return this._unsorted || [];
            const cols = this._cols || [];
            return (this._unsorted || []).filter((row) => active.every(([key, wanted]) => {
                const col = cols.find((c) => c.key === key);
                // A filter kept from a column that is no longer displayed is
                // inert: there is no cell whose displayed value it could
                // match, and the raw field may be one a guard withholds.
                if (!col) return true;
                const value = this._comparable(col, row);
                if (col && Array.isArray(col.options)) return String(value ?? '') === String(wanted);
                return String(value ?? '').toLowerCase().includes(String(wanted).toLowerCase());
            }));
        },
        _sortedRows(rows) {
            if (!this._sortKey || !this._sortDir) return rows;
            const key = this._sortKey;
            const col = (this._cols || []).find((c) => c.key === key);
            const mult = this._sortDir === ASC ? 1 : -1;
            return [...rows].sort((a, b) => {
                const va = this._comparable(col, a) ?? '';
                const vb = this._comparable(col, b) ?? '';
                if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
                return String(va).localeCompare(String(vb)) * mult;
            });
        },
        _pageLen() { return Number(this['page-size']) || 0; },
        _pagedRows(rows) {
            const ps = this._pageLen();
            if (!ps) return rows;
            const maxPage = Math.max(1, Math.ceil(rows.length / ps));
            if (this._page > maxPage) this._page = maxPage;
            return rows.slice((this._page - 1) * ps, this._page * ps);
        },
        // The rows on screen, in display order: what a row element's _index
        // indexes into.
        _displayed() { return this._display || []; },

        // ── Render: every result region regenerated from state. The filter
        // row is NOT among them — it is rebuilt only when the columns change,
        // so a control keeps its focus while its value narrows the rows. ──
        _render() {
            if (!this._isConnected || !Array.isArray(this._cols) || !this._cols.length) return;
            const filtered = this._filteredRows();
            const sorted = this._sortedRows(filtered);
            const display = this._pagedRows(sorted);
            // While loading, the placeholders stand for no data row: a click on
            // one selects nothing and announces nothing, and the selection kept
            // in state waits for the rows to come back.
            this._display = this.loading ? [] : display;
            this._edit = null;

            if (this.loading) {
                this.setData('rows', skeletonCells(this._cols));
            } else if (!display.length) {
                this.setData('rows', emptyCells(this['empty-text']));
            } else {
                this.setData('rows', display.map((row) => this._cols.map((col) => this._cell(col, row))));
            }
            this._rebuildHeaders();
            const hasAgg = !this.loading && display.length && this._cols.some((c) => c && c.agg);
            this._setRegion('totals', 'totalDefs', hasAgg ? totalCells(this._cols, filtered, globalStore) : []);
            const foot = footDSL(this._pageLen(), filtered.length, this._page, Boolean(this.exportable));
            this._setRegion('foot', 'footDefs', foot ? [{ type: 'dsl', span: true, dsl: [foot] }] : []);
            this._paintSelection();
            this._applyPins();
        },

        _rebuildHeaders() {
            if (!Array.isArray(this._cols)) return;
            this.setData('headers', this._cols.map((col) => {
                const attrs = { key: col.key };
                if (this._sortKey === col.key && this._sortDir) attrs.sort = this._sortDir;
                // Numeric columns align right, header included — otherwise the
                // heading floats away from the digits it labels.
                if (isNumericColumn(col)) attrs.num = '';
                if (col.pinned === 'left') attrs.pinned = '';
                return { text: columnHeading(col), value: col.key, attrs };
            }));
        },

        _rebuildFilters() {
            if (!Array.isArray(this._cols)) return;
            this._setRegion('filters', 'filterDefs', !this.filterable ? [] : this._cols.map((col) => ({
                type: 'dsl', pinned: col.pinned === 'left', dsl: [filterDSL(col, (this._filters || {})[col.key] || '')],
            })));
        },

        // A structural region (filters, totals, footer) gets its cells as DSL
        // and leaves the tree when it has none: an empty role=row is a hole for
        // arrow navigation and a lie to assistive technology. Its cells get
        // their column position stamped, the same as a body row's.
        _setRegion(ref, key, cells) {
            this.setData(key, cells.map(cellToDSL));
            const el = this.refs && this.refs[ref];
            if (!el) return;
            el.hidden = !cells.length;
            Array.from(el.children).forEach((cell, i) => { cell._col = i; });
        },

        // A column may declare a `renderer` (item-component tag fed the whole row),
        // or a `type`: 'chip' (value → severity via col.severity map) or 'actions'
        // (col.actions: [{ act, icon }]). Anything else is plain text.
        _cell(col, row) { return cellFor(col, row, globalStore); },

        _sort(e) {
            if (this._suppressSort) { this._suppressSort = false; return; }
            const key = e?.detail?.value;
            if (!key) return;

            let dir = null;
            if (this._sortKey !== key) dir = ASC;
            else if (this._sortDir === ASC) dir = DESC;

            this._sortKey = dir ? key : null;
            this._sortDir = dir;
            this._render();
        },

        _pageTo(page) {
            const p = Number(page);
            if (!p || p === this._page) return;
            this._page = p;
            this._render();
        },

        // ── Selection ──
        _toggleRow(row) {
            const id = this._rowId(row);
            if (this.multi) {
                if (this._selection.has(id)) this._selection.delete(id);
                else this._selection.add(id);
            } else {
                const was = this._selection.has(id);
                this._selection.clear();
                if (!was) this._selection.add(id);
            }
            this._paintSelection();
            this._announceSelection();
        },
        _paintSelection() {
            const selectable = Boolean(this.selectable || this.multi);
            if (selectable) this.setAttribute('aria-multiselectable', this.multi ? 'true' : 'false');
            else this.removeAttribute('aria-multiselectable');
            const body = this.refs && this.refs.body;
            if (!body) return;
            const display = this._displayed();
            Array.from(body.children).forEach((rowEl) => {
                const row = display[rowEl._index];
                const on = selectable && row !== undefined && this._selection.has(this._rowId(row));
                rowEl.toggleAttribute('selected', on);
                if (selectable) rowEl.setAttribute('aria-selected', on ? 'true' : 'false');
                else rowEl.removeAttribute('aria-selected');
            });
        },
        _announceSelection() {
            this.dispatchEvent(new CustomEvent('rowselect', {
                bubbles: true,
                detail: { value: this.value, rows: this.selectedRows },
            }));
        },

        // ── Editing ──
        _rowEl(rowIndex) {
            const body = this.refs && this.refs.body;
            return body ? Array.from(body.children).find((el) => el._index === rowIndex) || null : null;
        },
        _startEdit(rowIndex, colIndex) {
            const row = this._displayed()[rowIndex];
            const col = (this._cols || [])[colIndex];
            const rowEl = this._rowEl(rowIndex);
            if (!row || !col || !rowEl) return;
            const cells = this._cols.map((c) => this._cell(c, row));
            const target = cells[colIndex];
            // Only a cell the guard left editable opens an editor.
            if (!target || typeof target !== 'object' || !target.editable) return;
            if (this._edit) this._commitCurrent();
            this._edit = { rowIndex, colIndex };
            cells[colIndex] = {
                ...target, editing: true, key: col.key, label: columnHeading(col), value: row[col.key],
                options: col.options, inputType: col.type,
            };
            rowEl.data = cells;
            this._applyPins();
            const editor = rowEl.children[colIndex] && rowEl.children[colIndex].firstElementChild;
            if (editor) {
                editor.focus();
                if (editor.tagName === 'INPUT' && typeof editor.select === 'function') editor.select();
            }
        },
        // The editor's current value, for a commit driven by a key rather than
        // by the control's own change event.
        _commitCurrent() {
            const edit = this._edit;
            const rowEl = edit && this._rowEl(edit.rowIndex);
            const editor = rowEl && rowEl.children[edit.colIndex] && rowEl.children[edit.colIndex].firstElementChild;
            if (editor) this._commit(editor.value);
            else this._edit = null;
        },
        // Exactly once: the edit is taken off the grid before anything else
        // runs, so the blur that follows an Enter or a Tab finds nothing to
        // commit. The value is normalised to the column's type before it is
        // compared, so re-typing a number's own digits is not a change.
        _commit(raw) {
            const edit = this._edit;
            if (!edit) return;
            this._edit = null;
            const row = this._displayed()[edit.rowIndex];
            const col = (this._cols || [])[edit.colIndex];
            if (!row || !col) return;
            // The verdict is re-taken at commit: a guard that changed while
            // the editor was open (a permission withdrawn) refuses the write.
            const current = this._cell(col, row);
            if (!current || typeof current !== 'object' || !current.editable) {
                this._restoreRow(edit.rowIndex);
                this._focusCell(edit.rowIndex, edit.colIndex);
                return;
            }
            const oldValue = row[col.key];
            // A number column commits a number, or null for an emptied field:
            // absence is not zero, so clearing a 0 is a change and clearing a
            // 7 does not commit ''.
            const isNumber = col.type === 'number';
            const value = isNumber ? (raw === '' || raw === null || raw === undefined ? null : Number(raw)) : raw;
            const changed = isNumber
                ? !Object.is(oldValue ?? null, value)
                : String(oldValue ?? '') !== String(value ?? '');
            if (!changed) {
                this._restoreRow(edit.rowIndex);
                this._focusCell(edit.rowIndex, edit.colIndex);
                return;
            }
            row[col.key] = value;
            this.dispatchEvent(new CustomEvent('celledit', {
                bubbles: true,
                detail: { key: col.key, value, oldValue, row },
            }));
            // A committed edit can move a total, a sort position or a filter
            // match: the pipeline recomputes from the edited data. The row may
            // have left the page; focus then falls to the nearest row, or to
            // the column's header when no row is left.
            this._render();
            const at = this._displayed().indexOf(row);
            this._focusCell(at >= 0 ? at : Math.min(edit.rowIndex, this._displayed().length - 1), edit.colIndex);
        },
        _cancel() {
            const edit = this._edit;
            if (!edit) return;
            this._edit = null;
            this._restoreRow(edit.rowIndex);
            this._focusCell(edit.rowIndex, edit.colIndex);
        },
        _restoreRow(rowIndex) {
            const row = this._displayed()[rowIndex];
            const rowEl = this._rowEl(rowIndex);
            if (row && rowEl) { rowEl.data = this._cols.map((c) => this._cell(c, row)); this._applyPins(); }
        },
        _focusCell(rowIndex, colIndex) {
            const rowEl = rowIndex >= 0 ? this._rowEl(rowIndex) : null;
            const hdr = this.refs && this.refs.hdr;
            const cell = (rowEl && rowEl.children[colIndex])
                || (hdr && Array.from(hdr.children).find((h) => h._index === colIndex))
                || null;
            if (!cell) return;
            if (typeof this._focusGridCell === 'function') this._focusGridCell(cell);
            else { cell.tabIndex = 0; cell.focus(); }
        },
        // The next editable cell in reading order from (rowIndex, colIndex),
        // over the displayed rows: state, not the DOM, so it is unaffected by
        // the re-render a commit may trigger.
        _nextEditable(rowIndex, colIndex, direction) {
            const display = this._displayed();
            const cols = this._cols || [];
            let r = rowIndex;
            let c = colIndex + direction;
            while (r >= 0 && r < display.length) {
                while (c >= 0 && c < cols.length) {
                    const cell = this._cell(cols[c], display[r]);
                    if (cell && typeof cell === 'object' && cell.editable) return { rowIndex: r, colIndex: c };
                    c += direction;
                }
                r += direction;
                c = direction > 0 ? 0 : cols.length - 1;
            }
            return null;
        },
        _controlKeydown(e) {
            const target = e.target;
            if (!isControl(target)) return;
            const editing = this._edit && target.colKey === undefined;
            if (editing) {
                const edit = this._edit;
                if (e.key === 'Tab') {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    const row = this._displayed()[edit.rowIndex];
                    this._commit(target.value);
                    // The row may have moved, or left the page: step on from
                    // where it is now, or from where it was.
                    const from = this._displayed().indexOf(row);
                    const origin = from >= 0 ? { rowIndex: from, colIndex: edit.colIndex }
                        : { rowIndex: Math.min(edit.rowIndex, this._displayed().length - 1), colIndex: e.shiftKey ? this._cols.length : -1 };
                    const next = this._displayed().length ? this._nextEditable(origin.rowIndex, origin.colIndex, e.shiftKey ? -1 : 1) : null;
                    if (next) this._startEdit(next.rowIndex, next.colIndex);
                    // At either boundary the commit stands and focus stays
                    // where _commit put it.
                    return;
                }
                if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this._commit(target.value);
                    return;
                }
                if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this._cancel();
                    return;
                }
            }
            // Caret keys stay in the field — grid-nav never sees them.
            if (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End') e.stopImmediatePropagation();
        },
        _cellKeydown(e) {
            const target = e.target;
            if (isControl(target)) return;
            if (e.key === 'Enter' || e.key === ' ') {
                const h = target.closest && target.closest('s-dg-h, swc-data-grid-header');
                if (h) { e.preventDefault(); this._sort({ detail: { value: this._cols[h._index] && this._cols[h._index].key } }); return; }
            }
            const cell = target.closest && target.closest('[role=gridcell]');
            if (!cell || !cell._row || !Number.isFinite(cell._row._index)) return;
            if ((e.key === 'Enter' || e.key === 'F2') && cell.hasAttribute('editable')) {
                e.preventDefault();
                this._startEdit(cell._row._index, cell._col);
                return;
            }
            if (e.key === ' ' && (this.selectable || this.multi)) {
                const row = this._displayed()[cell._row._index];
                if (row) { e.preventDefault(); this._toggleRow(row); }
            }
        },

        // ── Column resize: a press within the trailing edge of a header ──
        _beginResize(e) {
            if (!this.resizable || e.button !== 0) return;
            const header = e.target.closest && e.target.closest('s-dg-h, swc-data-grid-header');
            if (!header || !Number.isFinite(header._index)) return;
            const col = (this._cols || [])[header._index];
            if (!col || col.resizable === false) return;
            const rect = header.getBoundingClientRect();
            if (e.clientX < rect.right - 8) return;
            e.preventDefault();
            const startX = e.clientX;
            const startW = rect.width;
            const key = col.key;
            const move = (ev) => {
                this._widths[key] = Math.max(48, Math.round(startW + ev.clientX - startX));
                this._applyTracks();
            };
            const end = () => {
                header.removeEventListener('pointermove', move);
                header.removeEventListener('pointerup', end);
                header.removeEventListener('pointercancel', end);
                header.removeEventListener('lostpointercapture', end);
                // The release lands as a click on the header; that click is a
                // resize, not a sort.
                this._suppressSort = true;
                this._suppressClick = true;
                setTimeout(() => { this._suppressSort = false; this._suppressClick = false; }, 0);
                this._applyPins();
            };
            if (typeof header.setPointerCapture === 'function') header.setPointerCapture(e.pointerId);
            header.addEventListener('pointermove', move);
            header.addEventListener('pointerup', end);
            header.addEventListener('pointercancel', end);
            header.addEventListener('lostpointercapture', end);
        },

        // ── Pinned columns: sticky against the host's own scroll, offset by
        // the measured width of the pinned columns before them. The measured
        // value reaches the stylesheet through a custom property. ──
        _applyPins() {
            if (!Array.isArray(this._cols) || !this._cols.some((c) => c && c.pinned === 'left')) return;
            const hdr = this.refs && this.refs.hdr;
            if (!hdr) return;
            requestAnimationFrame(() => {
                const headers = Array.from(hdr.children);
                const offsets = new Map();
                let offset = 0;
                this._cols.forEach((col, i) => {
                    if (col.pinned !== 'left') return;
                    offsets.set(i, offset);
                    const h = headers.find((el) => el._index === i);
                    offset += h ? h.getBoundingClientRect().width : 0;
                });
                headers.forEach((h) => { if (offsets.has(h._index)) h.style.setProperty('--pin-off', `${offsets.get(h._index)}px`); });
                const rows = [this.refs.filters, ...Array.from(this.refs.body ? this.refs.body.children : []), this.refs.totals].filter(Boolean);
                rows.forEach((rowEl) => Array.from(rowEl.children).forEach((cell) => {
                    if (offsets.has(cell._col)) cell.style.setProperty('--pin-off', `${offsets.get(cell._col)}px`);
                }));
            });
        },

        // ── CSV export: the filtered + sorted set, guards applied, header
        // labels. Pure string builder — exportCSV() is safe to call anywhere. ──
        exportCSV() {
            const cols = this._cols || [];
            const rows = this._sortedRows(this._filteredRows());
            const esc = (v) => {
                const s = String(v ?? '');
                return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };
            const lines = [cols.map((c) => esc(columnHeading(c))).join(',')];
            rows.forEach((row) => lines.push(cols.map((c) => esc(cellText(this._cell(c, row)))).join(',')));
            return lines.join('\n');
        },
        _download() {
            const csv = this.exportCSV();
            this.dispatchEvent(new CustomEvent('export', {
                bubbles: true,
                detail: { csv, count: this._sortedRows(this._filteredRows()).length },
            }));
            if (typeof document === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return;
            const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'export.csv';
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        },

        // grid-nav's row set: rows that hold cells. A hidden structural row
        // (no filters, no totals, no footer) is not a stop.
        _gridRows() {
            return Array.from(this.querySelectorAll('[role=row]')).filter((row) => !row.hidden);
        },
    },
};

// The totals row: the first column labels it, an aggregating column carries
// its aggregate presented the way the column presents its values. A guarded
// column totals only when EVERY row in the set may be read: a sum over a set
// that hides one salary states that salary to anyone who can subtract, and a
// one-row total states it outright. The total is absent instead.
function totalCells(cols, rows, store) {
    return cols.map((col, i) => {
        if (i === 0 && !col.agg) return { type: 'value', text: 'Total', emph: 'strong', pinned: col.pinned === 'left' };
        if (!col.agg) return { type: 'value', text: '', pinned: col.pinned === 'left' };
        const allReadable = rows.every((row) => readable(col, row, store));
        const p = presentValue(allReadable ? aggregate(rows, col) : null, col, rows[0]);
        return { type: 'value', text: p.text, absent: !allReadable || Boolean(p.absent), num: isNumericColumn(col), emph: 'strong', pinned: col.pinned === 'left', guard: allReadable ? undefined : 'hidden' };
    });
}

// The loading rows: three rows of skeletons, one per column. The empty state
// is OPT-IN through @empty-text: a grid that never declared one keeps
// rendering no rows at all, as it always did.
function skeletonCells(cols) {
    return Array.from({ length: 3 }, () => cols.map(() => ({ type: 'dsl', dsl: [['s-sk']] })));
}
function skeletonRows(cols, rowTokens = []) {
    return skeletonCells(cols).map((cells) => ['s-dg-c', ...rowTokens, cells.map(cellToDSL)]);
}
function emptyCells(text) {
    return text ? [[{ type: 'dsl', span: true, dsl: [['s-emp', { '@description': text }]] }]] : [];
}
function emptyRows(text, rowTokens = []) {
    return emptyCells(text).map((cells) => ['s-dg-c', ...rowTokens, cells.map(cellToDSL)]);
}

// The footer: a pager when the grid pages, an export button when it exports.
// The pager's limits are set BEFORE its current page, because s-pg clamps a
// page against the count it knows at that moment.
function footDSL(pageSize, total, current, exportable) {
    const parts = [];
    if (pageSize > 0) parts.push(['s-pg', { ':pageSize': pageSize, ':total': total, ':current': current }]);
    if (exportable) parts.push(['s-b.dg-export', '@sz=sm', '=Export CSV']);
    if (!parts.length) return null;
    return ['s-cn.dg-foot', '@l=row', parts];
}



const __default = [dgHeader, dgCell, dgHeaderList, dgBody, dataGrid];
return { trackFor, cellToDSL, cellFor, baseCellFor, __default };
})();

const __m113 = (() => {
// Table (s-tbl) — authored header + cell children over a CSS grid, with real
// ARIA table structure: on connect the flat cells are wrapped into
// display:contents row elements (<span role=row>), so AT hears a table of
// rows while the grid layout still sees the cells as direct grid items.
// Headers are columnheaders whose sort state reflects to aria-sort via their
// def; sortable ones are keyboard targets (Enter/Space sort — delegated).
const __default = {
    tags: ['s-tbl', 's-tbl'],
    props: ['@striped', '@hover', '@responsive'],
    aria: { role: 'table' },
    // Server twin of _buildRows: group the flat authored children into the same
    // display:contents rows, so first paint carries the ARIA table structure
    // instead of a bag of cells that only becomes a table on hydration.
    // _buildRows unwraps any existing [role=row] before rebuilding, so
    // hydration re-derives the identical shape rather than nesting.
    ssr({ childDefs }) {
        if (!Array.isArray(childDefs) || !childDefs.length) return null;
        const tagOf = (d) => (Array.isArray(d) && typeof d[0] === 'string' ? d[0].split(/[.#]/)[0] : '');
        const isHeader = (d) => tagOf(d) === 's-tbl-h' || tagOf(d) === 's-tbl-h';
        const spans = (d) => d.some((t) => typeof t === 'string' && /^@span(=|$)/.test(t));

        // A slotted child is a PART, not a cell. `s-tbl [slot=caption]` is a
        // compiled rule in table.css, but a caption counted as a cell shifts
        // every row by one column: the caption text landed in the first data
        // row and the last row was left orphaned with a single cell.
        const slotted = (d) => d.some((t) => typeof t === 'string' && /^@slot(=|$)/.test(t));

        const headers = childDefs.filter(isHeader);
        const cols = headers.filter((h) => !spans(h)).length;
        if (!cols) return null;

        // Cells get role=cell unless the author set their own role, mirroring
        // the client.
        const asCell = (d) => (d.some((t) => typeof t === 'string' && /^@role=/.test(t))
            ? d : [d[0], '@role=cell', ...d.slice(1)]);

        const rows = [['span', '@role=row', '@head=', headers]];
        const cells = childDefs.filter((d) => !isHeader(d) && !slotted(d));
        // Slotted parts stay where they are, outside the row structure.
        childDefs.filter(slotted).forEach((d) => rows.unshift(d));
        for (let i = 0; i < cells.length; i += cols) {
            const row = ['span', '@role=row'];
            if ((i / cols) % 2 === 1) row.push('@alt=');
            row.push(cells.slice(i, i + cols).map(asCell));
            rows.push(row);
        }
        return { children: rows, style: `--cols: ${cols}` };
    },
    callbacks: {
        connected() {
            this._init();
        },
    },
    methods: {
        _init() {
            this._headers = Array.from(this.getElementsByTagName('s-tbl-h'))
                .filter(h => !h.hasAttribute('span'));
            const cols = this._headers.length;

            if (cols > 0) {
                this.style.setProperty('--cols', cols);
            }

            // Sort via click delegation on self; Enter/Space mirror it for the
            // keyboard (sortable headers carry tabindex, set below).
            this.addEventListener('click', (e) => {
                const h = e.target.closest('s-tbl-h[sort]');
                if (h) this._sort(this._headers.indexOf(h), h);
            });
            this.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                const h = e.target.closest && e.target.closest('s-tbl-h[sort]');
                if (h) { e.preventDefault(); this._sort(this._headers.indexOf(h), h); }
            });
            this._headers
                .filter(h => h.hasAttribute('sort') && !h.hasAttribute('tabindex'))
                .forEach(h => h.setAttribute('tabindex', '0'));

            this._buildRows(cols);

            if (this.responsive !== undefined && this.responsive !== false) {
                this._setupMobileLabels();
            }
        },

        // Wrap the flat authored children into display:contents rows — one
        // header row, then one row per `cols` data cells — so the ARIA table
        // structure exists without the grid layout ever seeing a wrapper box.
        // Idempotent: a reconnect unwraps first. Rows are native spans (an
        // allowed native element; display:contents comes from table.css).
        _buildRows(cols) {
            if (!cols) return;
            Array.from(this.querySelectorAll(':scope > [role=row]'))
                .forEach((row) => { row.replaceWith(...row.childNodes); });

            const makeRow = (head) => {
                const row = document.createElement('span');
                row.setAttribute('role', 'row');
                if (head) row.setAttribute('head', '');
                return row;
            };

            const headRow = makeRow(true);
            this.prepend(headRow);
            Array.from(this.children)
                .filter((c) => c.tagName === 'S-TBL-H' || c.tagName === 'SWC-TABLE-HEADER')
                .forEach((h) => headRow.appendChild(h));

            // Slotted children are parts (a caption), not cells — see the note
            // in ssr(). Counting them shifted every data row by one column.
            const cells = Array.from(this.children)
                .filter((c) => c !== headRow && !c.hasAttribute('slot'));
            for (let i = 0; i < cells.length; i += cols) {
                const row = makeRow(false);
                if ((i / cols) % 2 === 1) row.setAttribute('alt', '');
                this.appendChild(row);
                cells.slice(i, i + cols).forEach((cell) => {
                    if (!cell.hasAttribute('role')) cell.setAttribute('role', 'cell');
                    row.appendChild(cell);
                });
            }
        },

        _dataRows() {
            return Array.from(this.querySelectorAll(':scope > [role=row]:not([head])'));
        },

        _getDataCells() {
            const rows = this._dataRows();
            if (rows.length) return rows.flatMap((r) => Array.from(r.children));
            const headerCount = this.getElementsByTagName('s-tbl-h').length;
            return Array.from(this.children)
                .filter((c) => !c.hasAttribute('slot'))
                .slice(headerCount);
        },

        _setupMobileLabels() {
            const cols = this._headers.length;
            this._getDataCells().forEach((cell, i) => {
                const colIndex = i % cols;
                cell.setAttribute('lbl', this._headers[colIndex].textContent);
            });
        },

        _sort(colIndex, header) {
            const currentSort = header.getAttribute('sort');
            const newSort = currentSort === 'asc' ? 'desc' : 'asc';

            // Reset all sortable headers, then set the active one — aria-sort
            // follows through the s-tbl-h def's reflection.
            this._headers
                .filter(h => h.hasAttribute('sort'))
                .forEach(h => h.setAttribute('sort', ''));
            header.setAttribute('sort', newSort);

            // Rows are elements now: sort and re-append them whole, restriping
            // as they land.
            const rows = this._dataRows();
            rows.sort((a, b) => {
                const aVal = a.children[colIndex]?.textContent?.trim() || '';
                const bVal = b.children[colIndex]?.textContent?.trim() || '';

                const aNum = parseFloat(aVal);
                const bNum = parseFloat(bVal);
                if (!isNaN(aNum) && !isNaN(bNum)) {
                    return newSort === 'asc' ? aNum - bNum : bNum - aNum;
                }

                return newSort === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            });

            rows.forEach((row, i) => {
                row.toggleAttribute('alt', i % 2 === 1);
                this.appendChild(row);
            });
        },
    },
};
return { __default };
})();

const __m114 = (() => {
const presentValue = __m111.presentValue;
const aggregate = __m111.aggregate;
const columnHeading = __m111.columnHeading;
const isNumericColumn = __m111.isNumericColumn;
// ── s-tg-h ── Header cell (tag-only, CSS target)
const tgHeader = {
    tags: ['s-tg-h', 's-tg-h'],
    props: ['@key'],
};

// ── s-tg-s ── Section row (tag-only; identity carried as the :nodePath
// property, depth as a CSS-targeting attribute). No data-* is read to
// identify the node — the parent grid reads section.nodePath directly.
const tgSection = {
    tags: ['s-tg-s', 's-tg-s'],
    props: ['@data-depth', '@expanded'],
};

// ── s-tg-c ── Data cell with internal store for content + edit mode.
// The cell owns its editing surface: it renders the input/select into its
// own :rf=content child, self-focuses it, and emits cellCommit. On connect
// it announces itself with a bubbling cellConnected event so the parent grid
// can index it by identity — no DOM query is ever needed to find a cell.
const tgCell = {
    tags: ['s-tg-c', 's-tg-c'],
    props: ['@editable', '@editing', '@fallback', '@data-path', '@data-key', '@data-depth'],
    internalStore: true,
    children: [
        ['s-cn.tg-c-content', ':rf=content', ':dk=cellContent', ':df=children'],
    ],
    callbacks: {
        connected() {
            // Announce identity to the grid (event delegation on the parent).
            this.dispatchEvent(new CustomEvent('cellConnected', { bubbles: true }));
            this.addEventListener('change', (e) => {
                const { target } = e;
                if (target.tagName === 'SELECT' || target.tagName === 'INPUT') this._commitValue(target.value);
            });
            this.addEventListener('focusout', (e) => {
                const { target } = e;
                if (target.tagName === 'INPUT' || target.tagName === 'SELECT') this._commitValue(target.value);
            });
        },
    },
    methods: {
        set data(incoming) {
            if (!incoming || typeof incoming !== 'object') return;
            this._internalData.setValues(incoming);
            this._processData(incoming);
        },
        get data() { return this._dataProxy; },

        _processData(data) {
            this._rowData = data.rowData;
            this._colKey = data.key;
            this._nodePath = data.nodePath;
            this._fallbackValue = data.fallback || '';

            if (data.editing === true) {
                this.editing = true;
                this._buildEditDSL(data);
                return;
            }

            if (data.renaming === true) {
                this.editing = true;
                this._buildRenameDSL(data);
                return;
            }

            this.editing = false;
            // `display` is the formatted text from the column's rules; `value`
            // stays the RAW value, because editing must start from the number
            // the user is changing, not from "£1,200.50".
            const value = data.display !== undefined && data.display !== null ? data.display : data.value;

            // Fallback — ghosted text from default locale
            if (!value && this._fallbackValue) {
                this.fallback = true;
                this._internalData.setValue('cellContent', String(this._fallbackValue));
                return;
            }

            this.fallback = false;

            // DSL array — nested components
            if (Array.isArray(value)) {
                this._internalData.setValue('cellContent', value);
                return;
            }

            // String or number — plain text
            this._internalData.setValue('cellContent', String(value ?? ''));
        },

        _buildEditDSL(data) {
            const value = data.value ?? '';

            if (data.type === 'select') {
                const optionsDSL = (data.options || []).map((opt) => {
                    const optVal = typeof opt === 'object' ? opt.value : opt;
                    const optText = typeof opt === 'object' ? opt.text : opt;
                    const tokens = [`@value=${optVal}`];
                    if (String(optVal) === String(value)) tokens.push('@selected=');
                    tokens.push(`="${optText}"`);
                    return ['option', ...tokens];
                });
                this._internalData.setValue('cellContent', [['select', optionsDSL]]);
            } else {
                this._internalData.setValue('cellContent', [
                    ['input', '@type=text', `@value=${value}`],
                ]);
            }

            this._focusEditElement();
        },

        _buildRenameDSL(data) {
            const value = data.value ?? '';
            this._internalData.setValue('cellContent', [
                ['input', '@type=text', `@value=${value}`],
            ]);
            this._focusEditElement();
        },

        _focusEditElement() {
            requestAnimationFrame(() => {
                const container = this.refs.content;
                if (!container) return;
                const input = container.children[0];
                if (!input) return;
                input.focus();
                if (input.tagName === 'INPUT' && typeof input.select === 'function') input.select();
            });
        },

        _commitValue(newValue) {
            if (!this.hasAttribute('editing')) return;
            this.dispatchEvent(new CustomEvent('cellCommit', {
                bubbles: true,
                detail: { path: this._nodePath, key: this._colKey, value: newValue, rowData: this._rowData },
            }));
        },
    },
};

// ── s-tg ── Tree Grid container: one config object in, everything decided here
//
// Consumer API:
//   tg.columns = [...];  tg.data = treeData;
//   — or —
//   ['s-tg', { ':data': { columns: [...], rows: [...] } }]
//
// The grid generates ALL DOM — headers, section rows, and leaf cells — as flat DSL.
// No intermediate row component. CSS grid treats everything flat via display:contents on s-cn.
// Sections (folders) span all columns. Leaf cells participate in the column grid.
// Tree depth indentation via --tg-depth CSS variable on sections and key cells.
//
// Everything the grid needs at runtime arrives as component state, not from the
// DOM: the section/cell structure comes from the data model (_columns/_rows),
// expand state lives in _expandState, an in-progress add lives in _pendingAdd,
// and the live cell elements register themselves (cellConnected) into an ordered
// index (_cellOrder) and an identity map (_cellById). Editing and keyboard
// navigation walk those structures — never a DOM lookup.

const treeGrid = {
    tags: ['s-tg', 's-tg'],
    dispatches: ['cellEdit', 'keyRename', 'nodeDelete', 'sectionAdd', 'sectionToggle'],
    props: ['@striped', '@hover'],
    internalStore: true,
    children: [
        ['s-cn.tg-headers', ':rf=headerContainer', ':dk=headerDefs', ':df=children'],
        ['s-cn.tg-body', ':rf=bodyContainer', ':dk=contentDefs', ':df=children'],
    ],
    callbacks: {
        connected() {
            if (!this._expandState) this._expandState = new Map();
            this._cellOrder = this._cellOrder || [];
            this._cellById = this._cellById || new Map();
            this._pendingAdd = null;

            // Wire delegated listeners once — the element instance is reused
            // across route revisits, so re-adding would stack duplicates.
            if (this._tgWired) { this._rebuild(); return; }
            this._tgWired = true;

            // Cells register themselves as they mount (event delegation on self).
            this.addEventListener('cellConnected', (e) => {
                if (e.target !== this) this._registerCell(e.target);
            });

            this.addEventListener('click', (e) => {
                const section = e.target.closest('s-tg-s');
                if (section) {
                    this._handleSectionClick(e, section);
                    return;
                }

                const cell = e.target.closest('s-tg-c');
                if (!cell) return;

                // Delete button inside a cell
                if (e.target.closest('.tg-act-delete')) {
                    this._fireDelete(cell._nodePath, cell._rowData);
                    return;
                }

                // Editable cell click — start editing
                if (cell.hasAttribute('editable') && !cell.hasAttribute('editing')) {
                    this._startEdit(cell);
                }
            });

            this.addEventListener('dblclick', (e) => {
                const cell = e.target.closest('s-tg-c');
                if (!cell) return;
                if (cell._colKey === '_key') {
                    this._startRename(cell);
                } else if (cell.hasAttribute('editable') && cell.hasAttribute('fallback') && cell._fallbackValue) {
                    // Double-click fallback cell: copy fallback value and start editing
                    this._startEditWithFallback(cell);
                }
            });

            this.addEventListener('cellCommit', (e) => {
                const cell = e.target.closest('s-tg-c');
                if (!cell) return;
                const { value } = e.detail;
                if (this._pendingAdd && cell._nodePath === this._pendingAdd.tempId) {
                    this._finishAddKey(value);
                    return;
                }
                if (cell._internalData.getValue('renaming')) {
                    this._endRename(cell, value);
                } else {
                    this._endEdit(cell, value);
                }
            });

            // Commit an in-progress "add section" when its inline input loses focus.
            this.addEventListener('focusout', (e) => {
                if (this._pendingAdd && this._pendingAdd.type === 'section' && e.target === this.refs.addInput) {
                    this._finishAddSection(e.target.value);
                }
            });

            this.addEventListener('keydown', (e) => this._handleKeydown(e));
            this._rebuild();
        },
    },
    methods: {
        // ── Data: columns + tree rows ──
        set data(incoming) {
            if (!incoming || typeof incoming !== 'object') return;

            if (Array.isArray(incoming)) {
                this._rows = incoming;
                this._rebuild();
                return;
            }

            if (incoming.columns) this._columns = incoming.columns;
            this._rows = incoming.rows || incoming.data || [];
            this._rebuild();
        },
        get data() { return this._dataProxy; },

        set columns(val) { this._columns = val; this._rebuild(); },
        get columns() { return this._columns; },

        // ── Cell registry: cells announce themselves on connect (cellConnected),
        // giving the grid an ordered, DOM-order index (_cellOrder) plus an
        // identity map (_cellById). Rebuilt from scratch on every _buildContent.
        _cellKey(nodePath, key) { return `${nodePath}::${key}`; },

        _registerCell(cell) {
            this._cellOrder.push({ el: cell, editable: cell.hasAttribute('editable') });
            this._cellById.set(this._cellKey(cell._nodePath, cell._colKey), cell);
        },

        _cellFor(nodePath, key) {
            return this._cellById.get(this._cellKey(nodePath, key)) || null;
        },

        // ── Rebuild: headers + flat content from tree ──
        _rebuild() {
            if (!this._isConnected) return;
            if (!Array.isArray(this._columns) || this._columns.length === 0) return;

            // Numeric columns cannot be narrower than their content — see
            // trackFor in data-grid.js for why a clipped number is a bug.
            this.style.setProperty('--cols', this._columns
                .map((c) => c.width || (isNumericColumn(c) ? 'minmax(max-content, 1fr)' : '1fr'))
                .join(' '));
            this._buildHeaders();
            this._buildContent();
        },

        _buildHeaders() {
            this._internalData.setValue('headerDefs', this._columns.map((col) => {
                const tokens = [`@key=${col.key}`];
                // The heading carries the unit and the column's alignment, so a
                // reader knows what the digits are and the heading sits over
                // them rather than away to the left.
                if (isNumericColumn(col)) tokens.push('@num=');
                tokens.push(`="${columnHeading(col)}"`);
                return ['s-tg-h', ...tokens];
            }));
        },

        _buildContent() {
            // Reset the registry — the setValue below recreates every cell, and
            // each one re-registers via cellConnected as it mounts.
            this._cellOrder = [];
            this._cellById = new Map();

            const rows = this._rows;
            if (!Array.isArray(rows)) { this._internalData.setValue('contentDefs', []); return; }

            const defs = [];
            this._collectNodes(rows, '', 0, defs);
            // Root-level pending add (no owning section) appends at the end.
            if (this._pendingAdd && !this._pendingAdd.sectionPath) this._appendPendingAdd(defs, 0);
            this._internalData.setValue('contentDefs', defs);
        },

        // ── Recursive flattening: tree nodes → flat DSL array ──
        _collectNodes(nodes, parentPath, depth, defs) {
            (nodes || []).forEach((node) => this._collectNode(node, parentPath, depth, defs));
        },

        _collectNode(node, parentPath, depth, defs) {
            if (node._section) { this._collectSection(defs, node, parentPath, depth); return; }
            if (node._key !== undefined) {
                const defaultLocale = this._columns[1]?.key;
                this._collectLeaf(defs, node, parentPath, depth, this._columns, defaultLocale);
            }
        },

        _collectSection(defs, node, parentPath, depth) {
            // Hydration-safe: on a server-rendered page the data binding can
            // apply before connected() has initialised the expand-state map.
            if (!this._expandState) this._expandState = new Map();
            const path = parentPath ? `${parentPath}.${node._section}` : node._section;
            if (!this._expandState.has(path)) {
                this._expandState.set(path, node._expanded !== false);
            }
            const expanded = this._expandState.get(path);
            const childCount = this._countChildren(node);
            const countLabel = !expanded && childCount ? ` (${childCount})` : '';

            // A section row rolls its children up when any column declares an
            // `agg`. Hierarchy without totals is half a financial grid.
            //
            // Totals mode is an ATTRIBUTE, not a second component: the row stops
            // being a spanning flexbox and joins the column grid so the numbers
            // sit under their headers. Without `agg` on any column the row is
            // byte-identical to what it was, which is what keeps the translation
            // grid — the only consumer today — untouched.
            const totals = this._columns.some((c) => c && c.agg) ? this._sectionTotals(node) : null;

            const label = ['s-cn.tg-section-left.tg-act-toggle', [
                ['s-ic.tg-arrow', expanded ? '@n=cd' : '@n=cr'],
                ['span', `="${node._section}"`],
                ...(countLabel ? [['span.tg-count', `="${countLabel}"`]] : []),
            ]];

            // swc-lint-ok: :nodePath is the section's identity, an expando the grid reads back by design
            const sectionDef = ['s-tg-s',
                `:nodePath=${path}`,
                `@data-depth=${depth}`,
                `@style=--tg-depth: ${depth}`,
                ...(expanded ? ['@expanded='] : []),
                ...(totals ? ['@totals='] : []),
                totals
                    // The label occupies the first track; every other column
                    // gets its own cell so a total lands under its heading.
                    // The editing actions are omitted deliberately — they are
                    // translation-grid affordances, and in totals mode their
                    // container would consume a column a number belongs in.
                    ? [label, ...totals]
                    : [
                        label,
                        ['s-cn.tg-actions', [
                            ['s-ic.tg-action-btn.tg-act-add-key', '@n=plus'],
                            ['s-ic.tg-action-btn.tg-act-add-section', '@n=folder-plus'],
                            ['s-ic.tg-del.tg-act-delete', '@n=x'],
                        ]],
                    ],
            ];

            defs.push(sectionDef);

            // Recurse into children if expanded
            if (expanded && node.children) {
                this._collectNodes(node.children, path, depth + 1, defs);
            }

            // A pending add owned by this section slots in after its children.
            if (this._pendingAdd && this._pendingAdd.sectionPath === path) {
                this._appendPendingAdd(defs, depth + 1);
            }
        },

        _collectLeaf(defs, node, parentPath, depth, columns, defaultLocale) {
            const path = parentPath ? `${parentPath}.${node._key}` : String(node._key);
            columns.forEach((col) => defs.push(this._leafCellDef(node, path, col, depth, defaultLocale)));
        },

        // Build a single leaf cell def in display mode. Editing is applied later,
        // imperatively, on the live cell element (see _startEdit) — never baked
        // into the def, so a rebuild never traps a cell mid-edit.
        // A row may override a column's editing config for ITS cell via
        // `_cellCfg: { [colKey]: { editable, type, options } }` — how a grid
        // makes one column behave differently per row (e.g. an options column
        // editable only on choice-type rows).
        _leafCellDef(node, path, col, depth, defaultLocale) {
            const cfg = (node && node._cellCfg && node._cellCfg[col.key]) || col;
            const value = node ? (node[col.key] ?? '') : '';
            const isKeyCol = col.key === '_key';
            // The ghosted default-locale fallback is a translation-grid
            // feature; rows opt their cells out via _cellCfg fallback: false.
            const cellFallback = (cfg.fallback !== false && !isKeyCol && col.key !== defaultLocale && node)
                ? (defaultLocale ? (node[defaultLocale] || '') : '')
                : '';
            const isEditable = cfg.editable !== false && cfg.type !== 'readonly';
            const cellDepth = isKeyCol ? depth + 1 : 0;

            const tokens = [];
            if (isEditable) tokens.push('@editable=');
            if (isKeyCol) tokens.push(`@data-depth=${cellDepth}`, `@style=--tg-depth: ${cellDepth}`);
            // A label column may still ellipsize — that is conventional — but
            // the full value must stay recoverable, so it rides as the title.
            // Numeric columns never reach this: their track cannot be narrower
            // than their content.
            if (isKeyCol && node && node._key) tokens.push({ '@title': String(node._key) });
            if (!value && cellFallback) tokens.push('@fallback=');

            // A trend column renders a chart rather than text. The cell's DSL
            // path (an array value becomes nested components) already supports
            // this, so a sparkline is a column type rather than a renderer.
            if (!isKeyCol && col.type === 'spark') {
                return ['s-tg-c', ...tokens, {
                    ':data': {
                        value: [['s-spk', { '@label': `${col.label || col.key} trend` },
                            { ':data': (node && node[col.seriesKey || col.key]) || [] }]],
                        nodePath: path,
                        key: col.key,
                        rowData: node,
                        fallback: '',
                    },
                }];
            }

            // Declarative presentation, opt-in per column and evaluated by the
            // same module the data-grid uses — a rule means the same thing in
            // both grids because there is only one evaluator.
            let display;
            if (!isKeyCol && (col.format || col.rules || col.empty !== undefined)) {
                // Read the raw field again rather than the `?? ''` coerced
                // `value` above: that coercion turns a null into '', which would
                // hide the difference between absent and empty.
                const p = presentValue(node ? node[col.key] : null, col, node);
                display = p.text;
                if (isNumericColumn(col)) tokens.push('@num=');
                if (p.tone) tokens.push(`@tone=${p.tone}`);
                if (p.weight) tokens.push(`@emph=${p.weight}`);
                if (p.absent) tokens.push('@absent=');
            }

            const cellPayload = {
                value: isKeyCol ? this._buildKeyContent(node ? node._key : '') : value,
                display,
                nodePath: path,
                key: col.key,
                rowData: node,
                fallback: cellFallback,
            };
            if (cfg.type) cellPayload.type = cfg.type;
            if (cfg.options) cellPayload.options = cfg.options;

            return ['s-tg-c', ...tokens, { ':data': cellPayload }];
        },

        _buildKeyContent(keyName) {
            return [
                ['span.tg-key-text', `="${keyName}"`],
                ['s-ic.tg-del.tg-act-delete', '@n=x'],
            ];
        },

        // Every leaf under a section, however deeply nested. A sub-section's
        // positions belong to its parent's total too — a portfolio total that
        // skipped nested holdings would be wrong rather than merely incomplete.
        _leafDescendants(node, out = []) {
            if (!node || !node.children) return out;
            node.children.forEach((child) => {
                if (child._section) this._leafDescendants(child, out);
                else out.push(child);
            });
            return out;
        },

        // One cell per column after the first, which the section label occupies.
        // A column declaring `agg` shows its roll-up, formatted by that column's
        // own rules so a total reads like the values it totals. A column without
        // one renders an empty cell, so the row stays aligned to the header.
        _sectionTotals(node) {
            const leaves = this._leafDescendants(node);
            return this._columns.slice(1).map((col) => {
                if (!col || !col.agg) return ['span.tg-total'];
                // The section row has no currency of its own. When every leaf
                // shares one, lend it to the formatter so the total carries the
                // same symbol as the values it totals; when they differ,
                // aggregate() has already refused and this renders absent.
                const currencyKey = col.format && col.format.currencyKey;
                let context = node;
                if (currencyKey) {
                    const codes = new Set(leaves.map((l) => l && l[currencyKey]).filter(Boolean));
                    if (codes.size === 1) context = { [currencyKey]: [...codes][0] };
                }
                const presented = presentValue(aggregate(leaves, col), col, context);
                const def = ['span.tg-total'];
                if (isNumericColumn(col)) def.push('@num=');
                if (presented.tone) def.push(`@tone=${presented.tone}`);
                if (presented.weight) def.push(`@emph=${presented.weight}`);
                if (presented.absent) def.push('@absent=');
                // Set as a PROPERTY, not an `=` token: the DSL parses a
                // numeric-looking literal into a Number, so "228.20" would
                // render as 228.2 and a formatted total would silently lose its
                // trailing zero.
                def.push({ ':textContent': presented.text });
                return def;
            });
        },

        _countChildren(node) {
            if (!node.children) return node._childCount || 0;
            let count = 0;
            node.children.forEach((child) => {
                if (child._section) {
                    count += this._countChildren(child);
                } else {
                    count++;
                }
            });
            return count;
        },

        // ── Section actions ──
        _handleSectionClick(e, section) {
            const path = section.nodePath;
            // Temp "add section" rows carry no identity — ignore clicks on them.
            if (path === undefined) return;

            // Each control names itself with its own class; the section's
            // identity is the nodePath PROPERTY it already carries, never an
            // attribute written for script to read back.
            if (e.target.closest('.tg-act-add-key')) this._beginAdd(path, 'key');
            else if (e.target.closest('.tg-act-add-section')) this._beginAdd(path, 'section');
            else if (e.target.closest('.tg-act-delete')) this._fireDelete(path, this._findNodeByPath(path));
            else this._toggleSection(path);
        },

        _toggleSection(path) {
            const expanded = !this._expandState.get(path);
            this._expandState.set(path, expanded);
            this._buildContent();
            this.dispatchEvent(new CustomEvent('sectionToggle', {
                bubbles: true,
                detail: { path, expanded },
            }));
        },

        _findNodeByPath(targetPath) {
            const walk = (nodes, parentPath) => {
                let found = null;
                nodes.forEach((node) => {
                    if (found) return;
                    const segment = node._section || node._key;
                    const path = parentPath ? `${parentPath}.${segment}` : String(segment);
                    if (path === targetPath) { found = node; return; }
                    if (node.children) {
                        const child = walk(node.children, path);
                        if (child) found = child;
                    }
                });
                return found;
            };
            return walk(this._rows || [], '');
        },

        // ── Inline editing ──
        _startEdit(cell) {
            const col = this._columns?.find((c) => c.key === cell._colKey);
            if (!col) return;
            // Per-row override, same as _leafCellDef: the row's _cellCfg wins.
            const cfg = (cell._rowData && cell._rowData._cellCfg && cell._rowData._cellCfg[col.key]) || col;
            if (cfg.editable === false || cfg.type === 'readonly') return;

            cell.data = {
                value: cell._rowData?.[col.key] ?? '',
                editing: true,
                nodePath: cell._nodePath,
                key: col.key,
                rowData: cell._rowData,
                fallback: cell._fallbackValue,
                type: cfg.type,
                options: cfg.options,
            };
        },

        _startEditWithFallback(cell) {
            const col = this._columns?.find((c) => c.key === cell._colKey);
            if (!col) return;

            cell.data = {
                value: cell._fallbackValue,
                editing: true,
                nodePath: cell._nodePath,
                key: col.key,
                rowData: cell._rowData,
                fallback: '',
                type: col.type,
                options: col.options,
            };
        },

        _endEdit(cell, newValue) {
            if (!cell.hasAttribute('editing')) return;

            const { _colKey: key, _nodePath: nodePath, _rowData: rowData } = cell;
            const oldValue = rowData?.[key];

            // Update data
            if (rowData) rowData[key] = newValue;

            // Revert cell to display mode
            const fallback = cell._fallbackValue || '';
            cell.data = {
                value: newValue,
                editing: false,
                nodePath,
                key,
                rowData,
                fallback,
            };

            if (oldValue !== newValue) {
                this.dispatchEvent(new CustomEvent('cellEdit', {
                    bubbles: true,
                    detail: { colKey: key, value: newValue, oldValue, rowData, nodePath },
                }));
            }
        },

        // ── Rename key ──
        _startRename(cell) {
            cell.data = {
                value: cell._rowData?._key ?? '',
                renaming: true,
                nodePath: cell._nodePath,
                key: '_key',
                rowData: cell._rowData,
                fallback: '',
            };
            cell._internalData.setValue('renaming', true);
        },

        _endRename(cell, newName) {
            const trimmed = (newName || '').trim();
            const oldName = cell._rowData?._key ?? '';
            cell._internalData.setValue('renaming', false);

            // Revert to display mode
            const finalName = trimmed || oldName;
            cell.data = {
                value: this._buildKeyContent(finalName),
                editing: false,
                nodePath: cell._nodePath,
                key: '_key',
                rowData: cell._rowData,
                fallback: '',
            };

            if (trimmed && trimmed !== oldName) {
                const oldPath = cell._nodePath;
                const parts = oldPath.split('.');
                parts[parts.length - 1] = trimmed;
                const newPath = parts.join('.');
                this.dispatchEvent(new CustomEvent('keyRename', {
                    bubbles: true,
                    detail: { oldPath, newPath, node: cell._rowData },
                }));
            }
        },

        // ── Inline add ──
        // An add is a piece of grid STATE (_pendingAdd). _buildContent renders a
        // temporary row/cell for it in the right structural position; the live
        // element is then reached through the cell registry / refs — no query.
        _nextTempId(type) {
            this._tempSeq = (this._tempSeq || 0) + 1;
            return `_new_${type}_${this._tempSeq}`;
        },

        _beginAdd(sectionPath, type) {
            if (type === 'section') {
                this._beginAddSection(sectionPath);
            } else {
                this._beginAddKey(sectionPath);
            }
        },

        _appendPendingAdd(defs, depth) {
            if (this._pendingAdd.type === 'section') {
                defs.push(['s-tg-s',
                    `@data-depth=${depth}`,
                    `@style=--tg-depth: ${depth}`,
                    [['input', ':rf=addInput', '@type=text', '@placeholder=Section name']],
                ]);
                return;
            }

            const columns = this._columns;
            const defaultLocale = columns[1]?.key;
            columns.forEach((col) => defs.push(
                this._leafCellDef(null, this._pendingAdd.tempId, col, depth, defaultLocale)
            ));
        },

        _beginAddSection(sectionPath) {
            // Ensure section is expanded so the new row is visible
            if (sectionPath && !this._expandState.get(sectionPath)) {
                this._expandState.set(sectionPath, true);
            }

            const tempId = this._nextTempId('section');
            this._pendingAdd = { sectionPath, type: 'section', tempId };
            this._buildContent();

            // The temp input registered itself as refs.addInput during render.
            requestAnimationFrame(() => {
                const input = this.refs.addInput;
                if (input) input.focus();
            });
        },

        _finishAddSection(value) {
            const sectionPath = this._pendingAdd ? this._pendingAdd.sectionPath : '';
            const name = (value || '').trim();
            this._pendingAdd = null;
            this._buildContent();
            if (name) {
                this.dispatchEvent(new CustomEvent('sectionAdd', {
                    bubbles: true,
                    detail: { path: sectionPath, type: 'section', name },
                }));
            }
        },

        _beginAddKey(sectionPath) {
            // Ensure section is expanded so the new row is visible
            if (sectionPath && !this._expandState.get(sectionPath)) {
                this._expandState.set(sectionPath, true);
            }

            const tempId = this._nextTempId('key');
            this._pendingAdd = { sectionPath, type: 'key', tempId };
            this._buildContent();

            // The temp key cell is in the registry now — put it into edit mode.
            const keyCell = this._cellFor(tempId, '_key');
            if (keyCell) {
                keyCell.data = {
                    value: '',
                    editing: true,
                    nodePath: tempId,
                    key: '_key',
                    rowData: null,
                    fallback: '',
                };
            }
        },

        _finishAddKey(value) {
            const pending = this._pendingAdd;
            const sectionPath = pending ? pending.sectionPath : '';
            const name = (value || '').trim();
            // Clear the temp cell's editing flag first: the rebuild below removes
            // it, and removing a focused input fires a blur → cellCommit that would
            // otherwise re-enter _endEdit mid-rebuild. Neutralised, its own commit
            // guard (needs the editing attribute) short-circuits.
            const tempCell = pending ? this._cellFor(pending.tempId, '_key') : null;
            if (tempCell) tempCell.editing = false;
            this._pendingAdd = null;
            this._buildContent();
            if (name) {
                this.dispatchEvent(new CustomEvent('sectionAdd', {
                    bubbles: true,
                    detail: { path: sectionPath, type: 'key', name },
                }));
            }
        },

        _cancelAdd() {
            this._pendingAdd = null;
            this._buildContent();
        },

        _fireDelete(path, node) {
            this.dispatchEvent(new CustomEvent('nodeDelete', {
                bubbles: true,
                detail: { path, node },
            }));
        },

        // ── Keyboard navigation ──
        // The focused input/select IS the keydown target, so its live value is
        // e.target.value — no reach into the cell. The next cell to edit comes
        // from the ordered registry (_cellOrder), not a DOM scan.
        _handleKeydown(e) {
            // Inline "add section" input
            if (this._pendingAdd && this._pendingAdd.type === 'section' && e.target === this.refs.addInput) {
                if (e.key === 'Enter') { e.preventDefault(); this._finishAddSection(e.target.value); }
                else if (e.key === 'Escape') { e.preventDefault(); this._cancelAdd(); }
                return;
            }

            const cell = e.target.closest('s-tg-c');
            if (!cell || !cell.hasAttribute('editing')) return;

            const value = e.target.value;
            const isPendingKey = this._pendingAdd && cell._nodePath === this._pendingAdd.tempId;

            if (e.key === 'Tab') {
                e.preventDefault();
                if (isPendingKey) { this._finishAddKey(value); return; }
                cell._commitValue(value);
                requestAnimationFrame(() => {
                    const next = this._adjacentEditable(cell, e.shiftKey ? -1 : 1);
                    if (next) this._startEdit(next);
                });
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (isPendingKey) { this._finishAddKey(value); return; }
                cell._commitValue(value);
                requestAnimationFrame(() => {
                    const below = this._cellBelow(cell);
                    if (below?.hasAttribute('editable')) this._startEdit(below);
                });
            } else if (e.key === 'Escape') {
                if (isPendingKey) { this._cancelAdd(); return; }
                const { _colKey: key, _nodePath: nodePath, _rowData: rowData, _fallbackValue: fallback } = cell;
                const isKeyCol = key === '_key';
                cell.data = {
                    value: isKeyCol ? this._buildKeyContent(rowData?._key ?? '') : (rowData?.[key] ?? ''),
                    editing: false,
                    nodePath,
                    key,
                    rowData,
                    fallback: fallback || '',
                };
            }
        },

        _adjacentEditable(cell, direction) {
            const editables = this._cellOrder.filter((entry) => entry.editable);
            const idx = editables.findIndex((entry) => entry.el === cell);
            if (idx < 0) return null;
            return editables[idx + direction]?.el || null;
        },

        _cellBelow(cell) {
            const idx = this._cellOrder.findIndex((entry) => entry.el === cell);
            if (idx < 0) return null;
            const colCount = this._columns?.length || 1;
            return this._cellOrder[idx + colCount]?.el || null;
        },
    },
};

const __default = [tgHeader, tgSection, tgCell, treeGrid];
return { __default };
})();

const __m115 = (() => {
// Tabs — data passes to tab-list and stack via internal store.
// Set panelRenderer property to control stack item renderer.
let tabsSeq = 0;

const __default = {
  tags: ['s-tbs', 's-tbs'],
  props: ['@psn'],
  internalStore: true,
  children: [
    ['s-tb-l', ':ref=tbl', ':dataKey=tabLabels'],
    ['s-st', ':ref=stack', ':dataKey=tabData'],
  ],
  callbacks: {
    connected() {
      const stack = this.refs.stack;
      const tbl = this.refs.tbl;
      // Manual composition: authored s-tb-l/s-st children override the def-injected pair
      const authored = Array.from(this.children)
        .filter(child => child !== tbl && child !== stack)
        .filter(child => child.tagName === 'S-TB-L' || child.tagName === 'S-ST');
      if (authored.length) {
        [tbl, stack].filter(Boolean).forEach(el => el.remove());
        this.refs.tbl = null;
        this.refs.stack = null;
        return;
      }
      // Sync stack to tab-list selection. The stack latched _syncSetup at its
      // own connect — before this callback existed to set the attribute — and
      // the tab list usually has no id yet, so both halves of the wiring must
      // be done here: give the list an id, then reopen the latch so the sync
      // mixin re-reads the attribute and actually subscribes.
      if (stack && tbl) {
        if (!tbl.id) tbl.id = `swctbs${++tabsSeq}`;
        stack.setAttribute('sync', tbl.id);
        stack._syncSetup = false;
        if (typeof stack._setupSync === 'function') {
          stack._syncSetup = true;
          stack._setupSync();
        }
      }
      // Forward panelRenderer to stack itemRenderer
      if (this.panelRenderer && stack) {
        stack.itemRenderer = this.panelRenderer;
      }
      // Default: select first tab if none selected
      requestAnimationFrame(() => {
        if (tbl?.items?.length && !tbl.selectedItem) {
          tbl._setSelectedItem(tbl.items[0]);
        }
      });
    },
  },
  methods: {
    set data(val) {
      this._data = val;
      if (this._internalData) {
        // The tab shows item.label, the stack panel shows item.text — split
        // the one authored array into the two shapes the list mixin stamps.
        this._internalData.setValue('tabLabels', (val || []).map((v) => ({ text: v.label ?? v.text })));
        this._internalData.setValue('tabData', val);
      }
    },
    get data() { return this._data; },
    set panelRenderer(val) {
      this._panelRenderer = val;
      const stack = this.refs?.stack;
      if (stack) stack.itemRenderer = val;
    },
    get panelRenderer() { return this._panelRenderer; },
  },
};
return { __default };
})();

const __m116 = (() => {
const __default = {
    tags: ['s-b-g', 's-b-g'],
    props: ['@l'],
};
return { __default };
})();

const __m117 = (() => {
const measureThenWrite = __m9.measureThenWrite;
const ssrSegmentChildren = __m21.ssrSegmentChildren;
// THE selectable button row — one component, one child tag (s-tgb). Toggle
// group, segmented control, tab strip and link group are one pattern with
// different decoration (@v) and semantics:
//   default        exclusive choice, sliding pill, always one selected
//   @multi=        independent on/off states (toolbar); zero selected is legal
//   @v=buttons     bordered button-row look
//   @v=line        the swc tab underline look
//   @v=links       undecorated text row
// Tabs are this row synced to a stack — navigation is a behavior, not a
// different component (s-tbs composes s-tb-l for the aria pairing machinery).
//
// s-seg/swc-segmented (and their s-seg-o child) are ABANDONED: they were an
// alias of this def plus a second child tag for a job s-tgb already did.
const __default = {
    tags: ['s-tgb-g', 's-tgb-g'],
    dispatches: ['change'],
    // @l is the stack axis (s-st / s-bg vocabulary): h = row (default),
    // v = column. A sidebar nav is this same exclusive row stood on end.
    // @optional= — exclusive, but zero selection is a legal state. A segmented
    // control always has one of its choices active; a NAVIGATION row does not,
    // because the page you are on may not be in it (a product detail, a legal
    // page, the homepage when Home is the wordmark rather than a nav item).
    // Without it the row adopts its first item, which fires a change, which the
    // shell reads as "the user chose this" and navigates. Every deep link went
    // quietly back to whatever the first nav entry happened to be.
    props: ['@value', '@sz', '@disabled', '@block', '@settled', '@multi', '@optional', '@v', '@l'],
    // @seg=<key>: the server marks the option the address names as selected
    // (F-811). Authored options only; a data-stamped row selects on the client.
    // The radio role and aria-checked come from the serializer's item contract
    // (aria.itemRole below), on this expansion as on the static path.
    ssr(input) { return ssrSegmentChildren(input, { attr: 'selected' }); },
    // Carrying @l makes this element match `[l] { gap: var(--layout-gap) }` in
    // @layer layout, which sits ABOVE components — so a vertical row inherited a
    // 16px page gap and pulled the joined toolbar strip apart at its dividers.
    // The row owns its internal spacing (dividers, sliding pill), so it opts
    // out. A component's own css: is injected unlayered, which is the escape
    // hatch layout.css documents for exactly this. Everything else about the
    // axis lives in toggle-group.css with the rest of the row's looks.
    css: `s-tgb-g[l] { gap: 0; }`,
    // The DEFAULT (exclusive) contract. Multi mode replaces both halves of it
    // at runtime — see _applyModeSemantics.
    aria: { role: 'radiogroup', itemRole: 'radio' },
    // Note: no storeAttrs — a 'value' storeAttr binding coerces the store key
    // (= element id) to a boolean attribute, blanking the string value
    // attribute whenever the selection mixin writes the selection store
    selectable: true,
    // Both axes: the row is horizontal by default but @l=v stands it up, and
    // the mixin binds keys statically, so it accepts arrows on either axis
    // rather than going dead in a vertical nav.
    keyboardNav: { orientation: 'both', selectOnFocus: true },
    itemRenderer: 's-tgb',
    callbacks: {
        connected() {
            requestAnimationFrame(() => {
                this._applyModeSemantics();
                // Exclusive mode never presents zero selection: default to the
                // first option (the s-tbs contract, generalized).
                const optional = this.hasAttribute('optional');
                if (!optional && (!this._isMultiSelect || !this._isMultiSelect())) {
                    const options = Array.from(this.children).filter((c) => c.tagName !== 'TEMPLATE');
                    const hasSelected = options.some((o) => o.hasAttribute('selected'));
                    if (options.length && !hasSelected && this._setSelectedItem) {
                        this._setSelectedItem(options[0]);
                    }
                }
            });
            // (Re)mount: drop the settled flag so the highlight lands on the
            // selected option INSTANTLY (the stylesheet only enables the slide
            // transition under [settled]). Two frames later the flag returns
            // and real selection changes animate again. Without this, every
            // hydration and every router page-cache re-insert replays the
            // slide as if the page had just loaded.
            this.settled = false;
            this._updateHighlight();
            requestAnimationFrame(() => requestAnimationFrame(() => { this.settled = true; }));
            // Re-measure when the control resizes: option widths follow their
            // labels, so a viewport change, font load or late-stamped children
            // all move the selected option under the pill.
            if (!this._ro) {
                this._ro = new ResizeObserver(() => this._updateHighlight());
                this._ro.observe(this);
            }
        },
        disconnected() {
            if (this._ro) {
                this._ro.disconnect();
                this._ro = null;
            }
        }
    },
    methods: {
        // One child tag, two announced contracts. Exclusive mode is a
        // radiogroup of radios (the def's own aria), multi mode is a group of
        // pressed buttons — and since the ITEM contract is mode-dependent too,
        // multi mode drops the def's itemRole so the selection mixin stops
        // co-writing aria-checked onto what are honestly toggle buttons. Each
        // toggle then announces itself (_syncAria).
        _applyModeSemantics() {
            const multi = Boolean(this._isMultiSelect && this._isMultiSelect());
            this._ariaItemRole = multi ? null : 'radio';
            // The two roles this def owns are swapped freely — the mode can
            // flip at runtime, so `group` has to be replaceable as well as
            // `radiogroup`. Any OTHER role was authored, and is left alone.
            const role = this.getAttribute('role');
            if (!role || role === 'radiogroup' || role === 'group') {
                this.setAttribute('role', multi ? 'group' : 'radiogroup');
            }
            Array.from(this.getElementsByTagName('s-tgb'))
                .forEach((o) => { if (o._syncAria) o._syncAria(); });
        },
        _updateHighlight() {
            // The pill highlights ONE selection; multi mode styles each
            // pressed option instead (see [multi] rules in toggle-group.css).
            if (this._isMultiSelect && this._isMultiSelect()) { this._applyModeSemantics(); return; }
            const options = this.getElementsByTagName('s-tgb');
            if (options.length === 0) return;

            // Measure the selected option rather than assuming equal slices:
            // options are nowrap and sized by their labels, so index math only
            // holds in [block] mode. offsetLeft is relative to this element
            // (position: relative) and is NOT affected by the row's scroll
            // offset, so a scrolled strip keeps its pill on the right option.
            // [selected] bold is already applied by the time this runs, so the
            // measurement includes it. Batched so several rows upgrading
            // together read one clean layout instead of forcing a reflow apiece.
            let selected = options[0];
            Array.from(options).forEach((opt) => {
                if (opt.hasAttribute('selected')) selected = opt;
            });

            // Both axes are measured in one read: @l=v stands the row up, and
            // the stylesheet picks left/width or top/height off the same pass.
            measureThenWrite(
                () => ({
                    left: selected.offsetLeft, width: selected.offsetWidth,
                    top: selected.offsetTop, height: selected.offsetHeight,
                }),
                ({ left, width, top, height }) => {
                    this.style.setProperty('--seg-left', `${left}px`);
                    this.style.setProperty('--seg-width', `${width}px`);
                    this.style.setProperty('--seg-top', `${top}px`);
                    this.style.setProperty('--seg-height', `${height}px`);
                },
            );
        },
        // The mode is an attribute, so it can flip after connect — a row that
        // becomes (or stops being) a toolbar has to re-announce itself and its
        // children then, not at the next click.
        onMultiChange() {
            this._applyModeSemantics();
            this._updateHighlight();
        },
        onValueChange() {
            this._updateHighlight();
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { value: this.value }
            }));
        },
        // Called by the selection mixin after an option is selected.
        // Reflecting the value attribute triggers attributeChangedCallback,
        // which fires onValueChange (highlight + change event).
        _onSelectionChange(child) {
            this._applyModeSemantics();
            this._updateHighlight();
            if (this._isMultiSelect && this._isMultiSelect()) {
                const vals = Array.from(this.children)
                    .filter((o) => o.hasAttribute && o.hasAttribute('selected'))
                    .map((o) => o.getAttribute('value') ?? o._data?.value ?? o.textContent.trim());
                this.setAttribute('value', vals.join(','));
                return;
            }
            const val = child.getAttribute('value') ?? child.textContent.trim();
            this.setAttribute('value', val);
        }
    }
};
return { __default };
})();

const __m118 = (() => {
const setDragged = __m27.setDragged;
const getDragged = __m27.getDragged;
const clearDragged = __m27.clearDragged;
// ── Tree ── recursive, data-driven tree.
//
// A single s-tree instance is used two ways, decided purely by the shape of the
// data it is given:
//
//   • list mode  (data = array of nodes) — the root, and every nested subtree.
//     The list mixin renders one child s-tree per node. No row of its own.
//   • node mode  (data = a node object)  — renders its own row (twisty, checkbox,
//     icon, label) from the children template, then a single nested s-tree that
//     holds the node's children.
//
// Recursion is expressed entirely through the DSL: the node template contains a
// nested ['s-tree', '?expanded', ':dk=childNodes'] that mounts itself when the
// node is expanded and reads the child nodes from the owner's internal store.
// Nothing is created with document.createElement, nothing is shown/hidden with
// style.display, and no state is written imperatively — the @-props reflect
// their own attributes, which the CSS targets.

// The dragged node rides the framework's drag context as an OBJECT REFERENCE —
// the same singleton the dragSource/dropZone contract uses — so identity never
// round-trips through dataTransfer (which does not persist across programmatic
// events and is unreadable during dragover anyway). The tree keeps its own
// positional drop mechanics (before/after/inside) rather than the generic
// dropZone: every node is BOTH source and zone, and position is part of the
// payload.


const TREE_TAGS = new Set(['S-TREE', 'SWC-TREE']);

// Flags a parent tree hands down to each child node so the whole tree shares one
// configuration. Propagated as properties (parent → child, never a DOM query).
const INHERIT_FLAGS = ['checkable', 'showLine', 'showIcon', 'checkOnClick', 'defaultExpandAll', 'dnd'];

// The child node elements of a tree, read from the live children collection (a
// property, not a query). A node wraps its children in one nested s-tree; a
// list-mode tree holds the node elements directly.
function childNodeEls(el) {
    const trees = Array.from(el.children).filter((c) => TREE_TAGS.has(c.tagName));
    if (Array.isArray(el._data)) return trees;
    const wrapper = trees[0];
    return wrapper ? Array.from(wrapper.children).filter((c) => TREE_TAGS.has(c.tagName)) : [];
}

// Resolve a node's checked / indeterminate state from its own data subtree.
function checkStateFromData(node) {
    const children = (node && node.children) || [];
    if (!children.length) return { checked: Boolean(node && node.checked), indeterminate: false };
    const states = children.map(checkStateFromData);
    const allChecked = states.every((s) => s.checked && !s.indeterminate);
    const noneChecked = states.every((s) => !s.checked && !s.indeterminate);
    return { checked: allChecked, indeterminate: !allChecked && !noneChecked };
}

// Stamp a checked value across a node and its whole data subtree. Keeps collapsed
// (unmounted) branches correct so they render right when later expanded.
function markDataChecked(node, value) {
    node.checked = value;
    (node.children || []).forEach((child) => markDataChecked(child, value));
}

function subtreeHasValue(nodes, value) {
    return (nodes || []).some((n) => n.value === value || subtreeHasValue(n.children, value));
}

// Collect the values of all checked leaves from a data subtree.
function collectCheckedLeaves(nodes, keys) {
    const leaves = (nodes || []).filter((n) => !(n.children && n.children.length));
    const branches = (nodes || []).filter((n) => n.children && n.children.length);
    leaves.filter((n) => n.checked).forEach((n) => keys.push(n.value));
    branches.forEach((n) => collectCheckedLeaves(n.children, keys));
}

// Nodes nest, so one click reaches every ancestor node. Bubbling delivers it to
// the INNERMOST node first, which claims it; every ancestor then sees the claim
// and stands down. This replaces asking the DOM, on every click/drag/drop,
// which s-tree is nearest the target — the innermost handler already knows.
const CLAIMED = Symbol('s-tree-claim');
function claimEvent(e) {
    if (e[CLAIMED]) return false;
    e[CLAIMED] = true;
    return true;
}

// ── s-tw ── twisty: the expand/collapse control. Presentation only — the parent
// s-tree owns the [expanded] state and the CSS rotates this chevron from it.
const treeTwisty = {
    tags: ['s-tree-twisty', 's-tw'],
    children: [['s-ic', '@n=cr']],
};

// ── s-cbx ── checkbox box: presentation only. The parent s-tree owns
// [checked]/[indeterminate]; the CSS draws the tick / dash from those attributes.
const treeCheckbox = {
    tags: ['s-tree-checkbox', 's-cbx'],
};

// ── s-tree ── the node / list element.
const tree = {
    tags: ['s-tree', 's-tree'],
    dispatches: ['select', 'treecheckchange', 'treemove'],
    itemRenderer: 's-tree',
    props: ['@expanded', '@checked', '@indeterminate', '@disabled',
            '@checkable', '@showLine', '@showIcon', '@checkOnClick',
            '@defaultExpandAll', '@leaf', '@dnd', 'selectedValue'],
    children: [
        ['s-tw', '~cl:call:_onTwisty'],
        ['s-cbx', '~cl:call:_onCheckbox'],
        ['s-ic', '$n=nodeIcon'],
        ['s-l', '^label'],
        ['s-tree', '?data:expanded', ':dk=childNodes',
            '#checkable:checkable', '#showLine:showLine', '#showIcon:showIcon',
            '#checkOnClick:checkOnClick', '#defaultExpandAll:defaultExpandAll',
            '#dnd:dnd', '#selectedValue:selectedValue'],
    ],
    callbacks: {
        connected() {
            // Click delegation on self. Only the node whose row was clicked acts;
            // ancestors ignore bubbled clicks from their descendants.
            this.addEventListener('click', (e) => {
                // The twisty and the checkbox handle themselves and claim the
                // click, so reaching here means the ROW itself was clicked.
                if (!claimEvent(e)) return;
                if (this.disabled) return;

                if (this.leaf) {
                    this.dispatchEvent(new CustomEvent('select', {
                        bubbles: true,
                        detail: { value: this._data?.value, label: this._data?.label },
                    }));
                    return;
                }
                if (this.checkOnClick && this.checkable) { this._toggleCheck(); return; }
                this.expanded = !this.expanded;
            });

            // A descendant's check change bubbles up; every node-mode ancestor
            // recomputes its own state from its direct children, innermost first.
            this.addEventListener('treecheckchange', (e) => {
                if (e.target === this) return;
                this._recalcFromChildren();
            });

            // APG tree keyboard, delegated at the ROOT only (every instance
            // registers, but nested groups/nodes bail on the role check, so a
            // bubbled keydown is handled exactly once). Collapsed subtrees are
            // unmounted, so every treeitem in the DOM is a visible row. A
            // roving tabindex keeps one row in the tab order; focusin roves it
            // so mouse and keyboard stay consistent.
            this.addEventListener('keydown', (e) => this._treeKeydown(e));
            this.addEventListener('focusin', (e) => {
                if (this.getAttribute('role') !== 'tree') return;
                const item = this._itemFor(e.target);
                if (item) this._roveTree(item);
            });
            requestAnimationFrame(() => {
                if (this.getAttribute('role') === 'tree') this._roveTree(this._treeItems()[0] || null);
            });

            // ── Drag & drop (opt-in via @dnd) ── Reorder / reparent nodes by dragging one row onto
            // another. The tree emits a bubbling 'treemove' { from, to, position } and owns no data
            // mutation: the host reorders its own data and re-renders. Only the innermost node under
            // the pointer acts. A node cannot drop onto itself.
            this.addEventListener('dragstart', (e) => {
                if (!this.dnd || Array.isArray(this._data)) return;
                if (!claimEvent(e)) return;
                setDragged(this);
                // Firefox refuses to start a drag with an empty payload.
                if (e.dataTransfer) { e.dataTransfer.setData('text/plain', ''); e.dataTransfer.effectAllowed = 'move'; }
                e.stopPropagation();
            });
            this.addEventListener('dragover', (e) => {
                if (!this.dnd || Array.isArray(this._data)) return;
                if (!claimEvent(e)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                this.setAttribute('droppos', this._dropPosition(e));
            });
            this.addEventListener('dragleave', (e) => {
                if (!claimEvent(e)) return;
                this.removeAttribute('droppos');
            });
            this.addEventListener('drop', (e) => {
                if (!this.dnd || Array.isArray(this._data)) return;
                if (!claimEvent(e)) return;
                e.preventDefault();
                e.stopPropagation();
                const position = this._dropPosition(e);
                this.removeAttribute('droppos');
                const source = getDragged();
                const from = source && source._data ? source._data.value ?? null : null;
                clearDragged();
                const to = this._data?.value;
                if (from != null && to != null && String(from) !== String(to)) {
                    this.dispatchEvent(new CustomEvent('treemove', { bubbles: true, detail: { from, to, position } }));
                }
            });
        },
    },
    methods: {
        // ── Keyboard (root only — see the connected() delegation note) ──
        // Which of our items owns this event target. Containment against the
        // list we already have, rather than a fresh climb through the DOM.
        // Scanned BACKWARDS: the list is in document order, so ancestors come
        // first and a forward search would answer a leaf's event with its
        // parent — which is how ArrowLeft from a leaf collapsed the branch
        // instead of moving up to it.
        _itemFor(target, items) {
            const list = items || this._treeItems();
            for (let i = list.length - 1; i >= 0; i--) {
                if (list[i] === target || list[i].contains(target)) return list[i];
            }
            return null;
        },
        _onTwisty(e) {
            claimEvent(e);
            if (!this.disabled) this.expanded = !this.expanded;
        },
        _onCheckbox(e) {
            claimEvent(e);
            if (!this.disabled) this._toggleCheck();
        },
        _treeItems() {
            return Array.from(this.querySelectorAll('[role=treeitem]:not([aria-disabled="true"])'));
        },
        _roveTree(active) {
            const items = this._treeItems();
            const target = items.includes(active) ? active : items[0];
            items.forEach((el) => { el.tabIndex = el === target ? 0 : -1; });
        },
        _focusTreeItem(item) {
            if (!item) return;
            this._roveTree(item);
            item.focus();
        },
        _treeKeydown(e) {
            if (this.getAttribute('role') !== 'tree') return;
            const items = this._treeItems();
            const item = this._itemFor(e.target, items);
            if (!item) return;
            const idx = items.indexOf(item);
            if (idx < 0) return;

            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                this._focusTreeItem(items[idx + (e.key === 'ArrowDown' ? 1 : -1)]);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (item.getAttribute('aria-expanded') === 'false') item.expanded = true;
                else {
                    // The first child is simply the next item in document order,
                    // provided it sits inside this one.
                    const child = items[idx + 1];
                    if (child && item.contains(child)) this._focusTreeItem(child);
                }
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                if (item.getAttribute('aria-expanded') === 'true') item.expanded = false;
                else {
                    // The parent is the nearest EARLIER item that contains this
                    // one — read off the ordered list we already built.
                    const parent = items.slice(0, idx).reverse().find((i) => i.contains(item));
                    if (parent) this._focusTreeItem(parent);
                }
            } else if (e.key === 'Home') {
                e.preventDefault();
                this._focusTreeItem(items[0]);
            } else if (e.key === 'End') {
                e.preventDefault();
                this._focusTreeItem(items[items.length - 1]);
            } else if (e.key === 'Enter' || e.key === ' ') {
                // The row's own click path (select / expand / check per config).
                e.preventDefault();
                item.click();
            } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== ' ') {
                // First-letters typeahead over the visible labels.
                this._treeTypeBuffer = (this._treeTypeBuffer || '') + e.key.toLowerCase();
                clearTimeout(this._treeTypeTimer);
                this._treeTypeTimer = setTimeout(() => { this._treeTypeBuffer = ''; }, 1000);
                for (let i = 1; i <= items.length; i++) {
                    const candidate = items[(idx + i) % items.length];
                    const label = (candidate.querySelector('label, s-l')?.textContent || candidate.textContent || '').trim().toLowerCase();
                    if (label.startsWith(this._treeTypeBuffer)) { this._focusTreeItem(candidate); break; }
                }
            }
        },

        // ── Accessibility ──
        // List mode is the tree root (parent is not a tree) or a nested group
        // (parent is a node's s-tree); node mode is a treeitem. Role + aria-state
        // derive from the node's own properties — a11y metadata, so setAttribute
        // (mirrors the framework's aria def-field).
        _updateAria() {
            if (Array.isArray(this._data)) {
                const nested = this.parentElement && TREE_TAGS.has(this.parentElement.tagName);
                this.setAttribute('role', nested ? 'group' : 'tree');
                return;
            }
            if (!this._data) return;
            this.setAttribute('role', 'treeitem');
            if (this.leaf) this.removeAttribute('aria-expanded');
            else this.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
            this.setAttribute('aria-selected', this.selected ? 'true' : 'false');
            if (this.checkable) {
                this.setAttribute('aria-checked', this.indeterminate ? 'mixed' : (this.checked ? 'true' : 'false'));
            }
            if (this.disabled) this.setAttribute('aria-disabled', 'true');
            else this.removeAttribute('aria-disabled');
        },

        // ── Check state ──
        _toggleCheck() {
            const value = !this.checked;
            if (this._data) markDataChecked(this._data, value);
            this._syncFromData();
            this.dispatchEvent(new CustomEvent('treecheckchange', { bubbles: true }));
        },

        // Push the current data state onto this element and every materialised
        // descendant. Collapsed branches stay correct via their (already marked)
        // data and refresh when expanded.
        _syncFromData() {
            const state = checkStateFromData(this._data || {});
            this.checked = state.checked;
            this.indeterminate = state.indeterminate;
            childNodeEls(this).forEach((el) => el._syncFromData());
            this._updateAria();
        },

        _recalcFromChildren() {
            if (Array.isArray(this._data) || !this._data) return;
            const kids = childNodeEls(this);
            if (!kids.length) return;
            const total = kids.length;
            const checkedCount = kids.filter((k) => k.checked && !k.indeterminate).length;
            const anyIndeterminate = kids.some((k) => k.indeterminate);
            const allChecked = checkedCount === total;
            this.checked = allChecked;
            this.indeterminate = !allChecked && (anyIndeterminate || checkedCount > 0);
            this._data.checked = allChecked;
            this._updateAria();
        },

        // ── Per-node data arrival (node mode only; list mode never calls this) ──
        onDataChange(data) {
            if (Array.isArray(data) || !data) return;

            this.leaf = !(data.children && data.children.length);
            this.disabled = Boolean(data.disabled);
            if (this.dnd) this.draggable = true; // node rows are draggable when DnD is enabled
            this._internalData.setValue('nodeIcon', data.icon || (this.leaf ? 'file' : 'folder'));
            this._internalData.setValue('childNodes', data.children || []);

            const state = checkStateFromData(data);
            this.checked = state.checked;
            this.indeterminate = state.indeterminate;

            this.expanded = this.expanded || Boolean(data.expanded) || Boolean(this.defaultExpandAll);
            // Mirror expanded into the internal store so the nested subtree's
            // ?data:expanded show-if mounts it (a bare ?expanded would bind the
            // global store, never this node). onExpandedChange keeps it in sync.
            this._internalData.setValue('expanded', this.expanded);
            this._updateAria();
        },

        // The nested subtree mounts via ?data:expanded, so every expanded change
        // (click, expandAll, reveal-on-select) must reach the internal store.
        onExpandedChange(value) {
            this._internalData.setValue('expanded', Boolean(value));
            this._updateAria();
        },

        onDefaultExpandAllChange(value) {
            if (!value || Array.isArray(this._data) || this.leaf) return;
            this.expanded = true;
        },

        // ── Drag & drop ──
        // Cascade dnd through the whole tree (mirrors onSelectedValueChange): a list-mode tree fans it
        // to its node children; a node makes its own row draggable and forwards to its nested subtree.
        onDndChange(value) {
            if (Array.isArray(this._data)) {
                childNodeEls(this).forEach((el) => { el.dnd = value; });
                return;
            }
            this.draggable = Boolean(value) && !!this._data;
            Array.from(this.children)
                .filter((c) => TREE_TAGS.has(c.tagName))
                .forEach((el) => { el.dnd = value; });
        },
        // Where in the target row the pointer sits: before / inside / after. Leaves can't take
        // children, so 'inside' on a leaf falls back to 'after'. Reads the label row geometry.
        _dropPosition(e) {
            const label = Array.from(this.children).find((c) => c.tagName === 'LABEL');
            const r = (label || this).getBoundingClientRect();
            const y = e.clientY - r.top;
            if (y < r.height * 0.3) return 'before';
            if (y > r.height * 0.7) return 'after';
            return this.leaf ? 'after' : 'inside';
        },

        // ── Config + selection fan-out to freshly rendered child nodes ──
        _afterRender() {
            this._updateAria();
            const kids = Array.isArray(this._data) ? childNodeEls(this) : [];
            const active = INHERIT_FLAGS.filter((flag) => this[flag]);
            const selectedValue = this.selectedValue;
            kids.forEach((el) => {
                active.forEach((flag) => { el[flag] = this[flag]; });
                el.selectedValue = selectedValue;
            });
        },

        onSelectedValueChange(value) {
            if (Array.isArray(this._data)) {
                childNodeEls(this).forEach((el) => { el.selectedValue = value; });
                return;
            }
            if (!this._data) return;
            const match = value != null && this._data.value === value;
            this.selected = match;
            this._updateAria();
            const reveal = value != null && !match && subtreeHasValue(this._data.children, value);
            this.expanded = this.expanded || reveal;
        },

        // ── Public API ──
        expandAll() {
            this._setExpandedDeep(true);
        },

        collapseAll() {
            this._setExpandedDeep(false);
        },

        _setExpandedDeep(value) {
            // Expand this node first so its subtree mounts, then recurse into the
            // now-materialised children.
            const expandables = childNodeEls(this).filter((el) => !el.leaf && !Array.isArray(el._data));
            expandables.forEach((el) => { el.expanded = value; });
            childNodeEls(this).forEach((el) => el._setExpandedDeep(value));
        },

        getCheckedKeys() {
            const keys = [];
            const roots = Array.isArray(this._data) ? this._data : [this._data].filter(Boolean);
            collectCheckedLeaves(roots, keys);
            return keys;
        },
    },
};

const __default = [treeTwisty, treeCheckbox, tree];
return { __default };
})();

const __m119 = (() => {
const __default = {
    tags: ['s-pg', 's-pg'],
    dispatches: ['change'],
    props: ['@current', '@total', '@pageSize', '@disabled', '@showTotal', '@showSizeChanger', '@showQuickJumper'],
    aria: { role: 'navigation' },
    storeAttrs: ['current'],
    internalStore: true,
    children: [
        ['s-cn.pg-wrapper', ':ref=wrapper', ':dataKey=pageChildren', ':dataField=children'],
    ],
    callbacks: {
        connected() {
            this._render();

            // Every control below carries the page or size it means, so there
            // is no click delegation left to write. The jumper is the one
            // exception: its value arrives on Enter, and it is held by ref.
            this.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter' || e.target !== this.refs.jumper) return;
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= this.pageCount) {
                    this.current = val;
                }
                e.target.value = '';
            });
        },
    },
    methods: {
        get current() {
            return parseInt(this._current || '1', 10);
        },
        set current(n) {
            const page = Math.max(1, Math.min(n, this.pageCount));
            this.setAttribute('current', page);
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: { current: page, pageSize: this._getPageSize() },
            }));
            this._render();
        },
        get pageCount() {
            const total = parseInt(this.total || '0', 10);
            const size = this._getPageSize();
            return Math.max(1, Math.ceil(total / size));
        },
        _getPageSize() {
            return parseInt(this.pageSize || '10', 10);
        },
        _render() {
            if (!this._internalData) return;
            const current = this.current;
            const total = parseInt(this.total || '0', 10);
            const pageCount = this.pageCount;
            const pageSize = this._getPageSize();

            const dsl = [];

            // Total text
            if (this.showTotal !== undefined && this.showTotal !== false) {
                dsl.push(['span.pg-total', `="Total ${total} items"`]);
            }

            // Prev button
            dsl.push(['button.pg-prev', `~cl:call:_goPage:${current - 1}`,
                ...(current <= 1 ? ['@disabled='] : []),
                '=\u2039',
            ]);

            // Page numbers with ellipsis
            this._getPageRange(current, pageCount).forEach(p => {
                if (p === '...') {
                    dsl.push(['span.pg-ellipsis', '=\u2026']);
                } else {
                    dsl.push(['button.pg-num', `~cl:call:_goPage:${p}`,
                        ...(p === current ? ['@active='] : []),
                        `="${p}"`,
                    ]);
                }
            });

            // Next button
            dsl.push(['button.pg-next', `~cl:call:_goPage:${current + 1}`,
                ...(current >= pageCount ? ['@disabled='] : []),
                '=\u203A',
            ]);

            // Size changer
            if (this.showSizeChanger !== undefined && this.showSizeChanger !== false) {
                const sizeOpts = [10, 20, 50, 100].map(sz =>
                    ['s-cn.pg-size-opt', `~cl:call:_setSize:${sz}`,
                        ...(sz === pageSize ? ['@active='] : []),
                        `="${sz} / page"`,
                    ]
                );
                dsl.push(['s-cn.pg-size-dd', ':ref=sizeDd', [
                    ['button.pg-size-trigger', '~cl:call:_toggleSize', `="${pageSize} / page"`],
                    ['s-cn.pg-size-menu', sizeOpts],
                ]]);
            }

            // Quick jumper
            if (this.showQuickJumper !== undefined && this.showQuickJumper !== false) {
                dsl.push(['span.pg-jumper', [
                    ['span', '="Go to "'],
                    ['input.pg-jumper-input', ':ref=jumper', '@type=number', '@min=1', `@max=${pageCount}`],
                ]]);
            }

            this.data.pageChildren = dsl;
        },
        // The controls name the page and size they mean; the dropdown is held
        // by ref, so closing it needs no scan for a marker attribute.
        _goPage(page) {
            if (this.disabled) return;
            const n = Number(page);
            if (n >= 1 && n <= this.pageCount) this.current = n;
        },
        _setSize(size) {
            if (this.disabled) return;
            this.setAttribute('pageSize', Number(size));
            this.refs.sizeDd?.removeAttribute('open');
            this.current = 1;
        },
        _toggleSize() {
            if (this.disabled) return;
            this.refs.sizeDd?.toggleAttribute('open');
        },
        _getPageRange(current, total) {
            if (total <= 7) {
                return Array.from({ length: total }, (_, i) => i + 1);
            }
            const pages = [];
            pages.push(1);
            let start = Math.max(2, current - 2);
            let end = Math.min(total - 1, current + 2);

            if (current <= 4) {
                start = 2;
                end = 5;
            } else if (current >= total - 3) {
                start = total - 4;
                end = total - 1;
            }

            if (start > 2) pages.push('...');
            Array.from({ length: end - start + 1 }, (_, i) => start + i).forEach(i => pages.push(i));
            if (end < total - 1) pages.push('...');
            pages.push(total);
            return pages;
        },
        onCurrentChange() { this._render(); },
        onTotalChange() { this._render(); },
        onPageSizeChange() { this._render(); },
    },
};
return { __default };
})();

const __m120 = (() => {
// Shared canvas machinery for the chart components (s-gau, s-spk, s-lch, s-btc,
// s-chm, s-bch, s-dnt, s-sbr, s-tmp, s-org). The DSL cannot create namespaced
// SVG, so every chart draws on a DPR-scaled canvas, the same approach as the
// analytics composites' s-kpi-ch, whose color and mark decisions carry over:
// categorical hues in fixed SERIES order (validated palette), thin marks,
// recessive 1px grids, labels in text tokens, value relief for low-contrast
// hues, hover tooltip where a mark maps to a datum.
const SERIES = ['#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7'];

const FONT = '11px system-ui, sans-serif';

function ink(el, name, fallback) {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

function niceMax(v) {
  if (v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

function fmt(v) {
  if (Math.abs(v) >= 1000000) return (v / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(Math.round(v * 100) / 100);
}

// Ellipsize text to a pixel width with the current canvas font. A prefix only
// ever gets wider, so the longest one that fits is a binary search — the
// shrink-one-character-at-a-time version called measureText once per character
// removed, per label, and a treemap of long labels paid that inside the same
// init task the whole page is blocked on.
function truncate(ctx, text, maxW) {
  if (ctx.measureText(text).width <= maxW) return text;
  let lo = 1;
  let hi = text.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + '…').width <= maxW) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo) + '…';
}

// Ink bundle read per draw so theme flips re-color on the next paint.
function inks(el) {
  return {
    text: ink(el, '--c-text', '#1f2430'),
    muted: ink(el, '--c-text-secondary', '#6b7280'),
    grid: ink(el, '--c-border', '#e5e7eb'),
    surface: ink(el, '--c-surface', '#fff'),
  };
}

// Recessive grid: 3 lines with y labels in muted ink. Returns the y scale.
function drawGrid(ctx, { left, right, top, ih, w }, max, k) {
  ctx.strokeStyle = k.grid;
  ctx.lineWidth = 1;
  ctx.fillStyle = k.muted;
  ctx.font = FONT;
  ctx.textAlign = 'right';
  [0, 0.5, 1].forEach((t) => {
    const gy = top + ih - t * ih;
    ctx.beginPath(); ctx.moveTo(left, gy); ctx.lineTo(w - right, gy); ctx.stroke();
    ctx.fillText(fmt(max * t), left - 6, gy + 3);
  });
  return (v) => top + ih - (v / max) * ih;
}

// Legend row: colored chip + name per entry, muted ink. Returns the row height.
function drawLegend(ctx, entries, x, y, k) {
  ctx.font = FONT;
  ctx.textAlign = 'left';
  let cx = x;
  entries.forEach((e) => {
    ctx.fillStyle = e.color;
    ctx.fillRect(cx, y, 8, 8);
    ctx.fillStyle = k.muted;
    ctx.fillText(e.name, cx + 12, y + 8);
    cx += 12 + ctx.measureText(e.name).width + 16;
  });
  return 18;
}

// Bar with a 4px rounded top anchored to the baseline (never rounded bottoms).
function barPath(ctx, x, top, bw, baseline) {
  const r = Math.min(4, bw / 2, Math.max(0, baseline - top));
  ctx.beginPath();
  ctx.moveTo(x, top + r);
  ctx.arcTo(x, top, x + r, top, r);
  ctx.arcTo(x + bw, top, x + bw, top + r, r);
  ctx.lineTo(x + bw, baseline);
  ctx.lineTo(x, baseline);
  ctx.closePath();
}

// Sparse x labels: first, middle, last, centered under their marks.
function sparseXLabels(ctx, labels, px, baselineY, k) {
  const n = labels.length;
  ctx.fillStyle = k.muted;
  ctx.font = FONT;
  ctx.textAlign = 'center';
  [0, Math.floor((n - 1) / 2), n - 1]
    .filter((v, i, a) => a.indexOf(v) === i)
    .forEach((i) => ctx.fillText(String(labels[i] ?? ''), px(i), baselineY));
}

// ── Draw scheduling ──────────────────────────────────────────────────────
// One implementation for every canvas chart in the framework: the components'
// canvasChart factory below AND the analytics composites' s-kpi-ch, which
// imports these four helpers.
//
// The rule these enforce: a chart never asks the DOM how wide it is. The
// ResizeObserver already measured the box during layout and hands the number
// over in contentRect, so using it costs nothing; reading clientWidth instead
// forces a synchronous layout, and because a draw WRITES the host's height,
// chart N+1's read had to flush chart N's write. Ten canvases on one page did
// that 62 times.

// The measured height leaves as a custom property, not an inline style — the
// stylesheet's `height: var(--swc-chart-h)` consumes it (house rule: runtime
// geometry travels through custom properties).
function setChartHeight(host, h) {
  host.style.setProperty('--swc-chart-h', `${h}px`);
}

// The width the observer measured, never a fresh DOM read. Falls back to
// clientWidth only before the first observation (or where ResizeObserver does
// not exist), where nothing else has dirtied layout yet.
function chartWidth(host) {
  return host._cw || host.clientWidth || 320;
}

// rAF, deliberately: a microtask cannot end the task it was queued in, so
// queueMicrotask would coalesce the draws without ever yielding the thread.
function scheduleChartDraw(host) {
  if (host._drawRaf) return;
  const raf = typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame
    : (fn) => setTimeout(fn, 16);
  host._drawRaf = raf(() => {
    host._drawRaf = 0;
    // Data can arrive before the observer has reported a width. Drawing now
    // would mean falling back to a clientWidth read, and with every chart in
    // the same animation frame each of those reads flushes the height the
    // previous chart just wrote. The observation is already on its way; it
    // brings the width with it and schedules the draw again.
    if (host._cw == null && host._ro) return;
    host._draw();
  }) || -1;
}

function observeChartSize(host) {
  if (typeof ResizeObserver === 'undefined') { scheduleChartDraw(host); return; }
  host._ro = new ResizeObserver((entries) => {
    const rect = entries && entries[0] && entries[0].contentRect;
    if (rect && typeof rect.width === 'number') {
      // Setting the height re-triggers the observer with the same width. That
      // echo has nothing new to draw, and dropping it is what turns four
      // unconditional draws per canvas back into one.
      if (rect.width === host._cw && host._chartDrawn) return;
      host._cw = rect.width;
    }
    scheduleChartDraw(host);
  });
  host._ro.observe(host);
}

function unobserveChartSize(host) {
  if (host._ro) { host._ro.disconnect(); host._ro = null; }
  if (host._drawRaf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(host._drawRaf);
  host._drawRaf = 0;
}

// Component factory. cfg:
//   tags     ['swc-long-name', 's-xxx']
//   layout   (data, w, host) -> { h, ...anything the draw needs }  (data is non-null)
//   draw     (ctx, g, k) with g = { data, w, h, l, hover, host }
//   hitTest  optional (g, mx, my) -> { i, text, x, y } | null; enables the tooltip
// The data contract is a plain object set through the `data` property
// (':data=$storeKey' in DSL). Null/empty data clears the canvas and never throws.
function canvasChart({ tags, layout, draw, hitTest, aria, props, dispatches }) {
  return {
    tags,
    // Same trap as aria/props below: a key this factory does not name is
    // silently dropped, so a chart that fires `pinselect` documented nothing.
    ...(dispatches ? { dispatches } : {}),
    // Callers declare their aria contract (role=img + @label) and extra props;
    // dropping them here silently was how ten charts shipped with no role and
    // no accessible name — llm-facts rule: an unlabelled chart is a lint error.
    aria: aria || { role: 'img', label: 'label' },
    props: [...new Set(['@label', ...(props || [])])],
    children: [
      ['canvas', ':ref=cv'],
      ['s-cn', ':ref=tip', '@rg=tip'],
    ],
    callbacks: {
      connected() {
        if (hitTest) {
          this.addEventListener('mousemove', (e) => this._onMove(e));
          this.addEventListener('mouseleave', () => this._onLeave());
        }
        // No draw here: observing delivers an initial observation, so the draw
        // that used to run on this line was the same draw done twice.
        observeChartSize(this);
      },
      disconnected() {
        unobserveChartSize(this);
      },
    },
    methods: {
      set data(v) { this._chart = v || null; this._hover = -1; scheduleChartDraw(this); },
      get data() { return this._chart; },

      _geometry() {
        const cv = this.refs && this.refs.cv;
        if (!cv || !this._isConnected) return null;
        const w = chartWidth(this);
        const data = this._chart;
        const l = data ? layout(data, w, this) : { h: 0 };
        return { data, w, h: l.h, l, hover: this._hover, host: this, cv };
      },

      _draw() {
        const g = this._geometry();
        if (!g) return;
        this._chartDrawn = true;
        const dpr = window.devicePixelRatio || 1;
        const h = Math.max(g.h, 1);
        const k = inks(this);
        g.cv.width = g.w * dpr;
        g.cv.height = h * dpr;
        setChartHeight(this, h);
        const ctx = g.cv.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, g.w, h);
        if (!g.data || !g.h) return;
        draw(ctx, g, k);
      },

      _onMove(e) {
        const g = this._geometry();
        const tip = this.refs && this.refs.tip;
        if (!g || !g.data || !tip) return;
        const rect = this.getBoundingClientRect();
        const hit = hitTest(g, e.clientX - rect.left, e.clientY - rect.top);
        const i = hit ? hit.i : -1;
        if (i !== this._hover) { this._hover = i; this._draw(); }
        if (!hit) { tip.style.display = 'none'; return; }
        tip.textContent = hit.text;
        tip.style.left = `${hit.x}px`;
        tip.style.top = `${hit.y}px`;
        tip.style.display = 'block';
      },

      _onLeave() {
        this._hover = -1;
        if (this.refs && this.refs.tip) this.refs.tip.style.display = 'none';
        this._draw();
      },
    },
  };
}
return { SERIES, FONT, ink, niceMax, fmt, truncate, inks, drawGrid, drawLegend, barPath, sparseXLabels, setChartHeight, chartWidth, scheduleChartDraw, observeChartSize, unobserveChartSize, canvasChart };
})();

const __m121 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
const fmt = __m120.fmt;
const drawGrid = __m120.drawGrid;
const barPath = __m120.barPath;
const FONT = __m120.FONT;
const niceMax = __m120.niceMax;
// Categorical bars with visible direct value labels (the relief rule), one hue
// (the catalog's "bar-chart" display).
// data: { points: [{ label, value, color? }], color?, height? (220) }


const PAD = { top: 16, right: 12, bottom: 22, left: 40 };

const slotX = (i, n, iw) => {
  const slot = iw / n;
  const bw = Math.min(32, slot - 2);
  return { x: PAD.left + i * slot + (slot - bw) / 2, bw, slot };
};

const __default = canvasChart({
  tags: ['swc-bar-chart', 's-bch'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data) => ({ h: data.height || 220, points: data.points || [] }),
  draw(ctx, g, k) {
    const points = g.l.points;
    if (!points.length) return;
    const frame = { left: PAD.left, right: PAD.right, top: PAD.top, ih: g.h - PAD.top - PAD.bottom, w: g.w };
    const max = niceMax(Math.max(...points.map((p) => p.value)));
    const y = drawGrid(ctx, frame, max, k);
    const iw = g.w - PAD.left - PAD.right;
    const baseline = PAD.top + frame.ih;
    points.forEach((p, i) => {
      const { x, bw } = slotX(i, points.length, iw);
      const hue = p.color || g.data.color || SERIES[0];
      barPath(ctx, x, y(p.value), bw, baseline);
      ctx.fillStyle = i === g.hover ? hue : hue + 'e6';
      ctx.fill();
      ctx.fillStyle = k.muted;
      ctx.font = FONT;
      ctx.textAlign = 'center';
      ctx.fillText(fmt(p.value), x + bw / 2, y(p.value) - 5);
      ctx.fillText(String(p.label ?? ''), x + bw / 2, g.h - 6);
    });
  },
  hitTest(g, mx) {
    const points = g.l.points;
    if (!points.length) return null;
    const iw = g.w - PAD.left - PAD.right;
    const i = Math.floor((mx - PAD.left) / (iw / points.length));
    if (i < 0 || i >= points.length) return null;
    const { x, bw } = slotX(i, points.length, iw);
    const max = niceMax(Math.max(...points.map((p) => p.value)));
    const ih = g.h - PAD.top - PAD.bottom;
    return {
      i,
      text: `${points[i].label ?? ''}: ${fmt(points[i].value)}`,
      x: x + bw / 2,
      y: PAD.top + ih - (points[i].value / max) * ih,
    };
  },
});
return { __default };
})();

const __m122 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
const fmt = __m120.fmt;
const drawGrid = __m120.drawGrid;
const barPath = __m120.barPath;
const sparseXLabels = __m120.sparseXLabels;
const niceMax = __m120.niceMax;
// Bars over time: dense series, sparse x labels, values on hover rather than a
// number on every bar (the catalog's "bar-time-chart" display).
// data: { points: [{ label, value }], color?, height? (220) }


const PAD = { top: 12, right: 12, bottom: 22, left: 40 };

const __default = canvasChart({
  tags: ['swc-bar-time-chart', 's-btc'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data) => ({ h: data.height || 220, points: data.points || [] }),
  draw(ctx, g, k) {
    const points = g.l.points;
    if (!points.length) return;
    const frame = { left: PAD.left, right: PAD.right, top: PAD.top, ih: g.h - PAD.top - PAD.bottom, w: g.w };
    const max = niceMax(Math.max(...points.map((p) => p.value)));
    const y = drawGrid(ctx, frame, max, k);
    const iw = g.w - PAD.left - PAD.right;
    const slot = iw / points.length;
    const bw = Math.max(1, Math.min(32, slot - 2));
    const hue = g.data.color || SERIES[0];
    const baseline = PAD.top + frame.ih;
    points.forEach((p, i) => {
      barPath(ctx, PAD.left + i * slot + (slot - bw) / 2, y(p.value), bw, baseline);
      ctx.fillStyle = i === g.hover ? hue : hue + 'e6';
      ctx.fill();
    });
    sparseXLabels(ctx, points.map((p) => p.label), (i) => PAD.left + i * slot + slot / 2, g.h - 6, k);
  },
  hitTest(g, mx) {
    const points = g.l.points;
    if (!points.length) return null;
    const iw = g.w - PAD.left - PAD.right;
    const slot = iw / points.length;
    const i = Math.floor((mx - PAD.left) / slot);
    if (i < 0 || i >= points.length) return null;
    const max = niceMax(Math.max(...points.map((p) => p.value)));
    const ih = g.h - PAD.top - PAD.bottom;
    return {
      i,
      text: `${points[i].label ?? ''}: ${fmt(points[i].value)}`,
      x: PAD.left + i * slot + slot / 2,
      y: PAD.top + ih - (points[i].value / max) * ih,
    };
  },
});
return { __default };
})();

const __m123 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
const fmt = __m120.fmt;
const FONT = __m120.FONT;
// Value-per-day grid, GitHub-contribution style (the catalog's
// "calendar-heatmap" display). Magnitude is a sequential job: ONE hue stepped
// by alpha, never a rainbow. Weeks are columns, days are rows.
// data: { days: [{ date: 'YYYY-MM-DD', value }], color?, cell? (12) }


const DAY = 86400000;
const LEFT = 28;
const TOP = 16;
const STEPS = ['26', '4d', '80', 'b3', 'ff']; // alpha suffixes, light to dark
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function gridOf(data) {
  const days = (data.days || []).filter((d) => d && d.date);
  if (!days.length) return null;
  const stamps = days.map((d) => ({ t: Date.parse(d.date + 'T00:00:00Z'), value: d.value || 0 }));
  const min = Math.min(...stamps.map((s) => s.t));
  const max = Math.max(...stamps.map((s) => s.t));
  // Monday-first columns: back the start up to its week's Monday.
  const startDow = (new Date(min).getUTCDay() + 6) % 7;
  const start = min - startDow * DAY;
  const weeks = Math.ceil((max - start + DAY) / (7 * DAY));
  const byDay = new Map(stamps.map((s) => [Math.floor((s.t - start) / DAY), s.value]));
  return { start, weeks, byDay, peak: Math.max(...stamps.map((s) => s.value), 1) };
}

const __default = canvasChart({
  tags: ['s-cal-heatmap', 's-chm'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data, w) => {
    const grid = gridOf(data);
    if (!grid) return { h: 60, grid };
    const cell = Math.max(6, Math.min(data.cell || 12, Math.floor((w - LEFT) / grid.weeks) - 2));
    return { h: TOP + 7 * (cell + 2) + 6, grid, cell };
  },
  draw(ctx, g, k) {
    const { grid, cell } = g.l;
    if (!grid) return;
    const hue = g.data.color || SERIES[0];
    ctx.font = FONT;

    Array.from({ length: grid.weeks }).forEach((_, wk) => {
      Array.from({ length: 7 }).forEach((_, dow) => {
        const idx = wk * 7 + dow;
        const value = grid.byDay.get(idx);
        const x = LEFT + wk * (cell + 2);
        const y = TOP + dow * (cell + 2);
        const step = value == null || value <= 0
          ? null
          : STEPS[Math.min(STEPS.length - 1, Math.floor((value / grid.peak) * STEPS.length))];
        ctx.fillStyle = step == null ? k.grid + '66' : hue + step;
        ctx.beginPath();
        ctx.roundRect(x, y, cell, cell, 2);
        ctx.fill();
      });
    });

    // A month label on each week column that starts a new month. A label that
    // would collide with its predecessor replaces it: the stub month a
    // backed-up first week drags in loses to the real start.
    const monthAt = (wk) => new Date(grid.start + wk * 7 * DAY).getUTCMonth();
    const starts = Array.from({ length: grid.weeks }, (_, wk) => wk)
      .filter((wk) => wk === 0 || monthAt(wk) !== monthAt(wk - 1))
      .reduce((acc, wk) => {
        const prev = acc[acc.length - 1];
        return prev != null && (wk - prev) * (cell + 2) < 30 ? [...acc.slice(0, -1), wk] : [...acc, wk];
      }, []);
    ctx.fillStyle = k.muted;
    ctx.textAlign = 'left';
    starts.forEach((wk) => ctx.fillText(MONTHS[monthAt(wk)], LEFT + wk * (cell + 2), 10));

    ctx.fillStyle = k.muted;
    ctx.textAlign = 'right';
    [['Mon', 0], ['Wed', 2], ['Fri', 4]].forEach(([label, dow]) => {
      ctx.fillText(label, LEFT - 6, TOP + dow * (cell + 2) + cell - 2);
    });
  },
  hitTest(g, mx, my) {
    const { grid, cell } = g.l;
    if (!grid) return null;
    const wk = Math.floor((mx - LEFT) / (cell + 2));
    const dow = Math.floor((my - TOP) / (cell + 2));
    if (wk < 0 || wk >= grid.weeks || dow < 0 || dow > 6) return null;
    const idx = wk * 7 + dow;
    const value = grid.byDay.get(idx);
    if (value == null) return null;
    const date = new Date(grid.start + idx * DAY).toISOString().slice(0, 10);
    return { i: idx, text: `${date}: ${fmt(value)}`, x: LEFT + wk * (cell + 2) + cell / 2, y: TOP + dow * (cell + 2) };
  },
});
return { __default };
})();

const __m124 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
const fmt = __m120.fmt;
const FONT = __m120.FONT;
// Donut/pie composition with a center total and a legend (the catalog's
// "donut-chart" display). Slices wear fixed-order SERIES hues separated by a
// 2px surface gap; identity is never color alone, the legend names every slice.
// data: { slices: [{ label, value, color? }], height? (220), centerLabel? }


const TAU = Math.PI * 2;
const slicesOf = (data) => (data.slices || []).filter((s) => s.value > 0);
const hueOf = (s, i) => s.color || SERIES[i % SERIES.length];

function anglesOf(slices) {
  const total = slices.reduce((sum, s) => sum + s.value, 0) || 1;
  let a = -Math.PI / 2;
  return slices.map((s) => {
    const span = (s.value / total) * TAU;
    const arc = { from: a, to: a + span };
    a += span;
    return arc;
  });
}

const __default = canvasChart({
  tags: ['swc-donut-chart', 's-dnt'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data) => {
    const slices = slicesOf(data);
    return { h: (data.height || 220) + Math.ceil(slices.length / 3) * 18, slices };
  },
  draw(ctx, g, k) {
    const { slices } = g.l;
    if (!slices.length) return;
    const plotH = g.data.height || 220;
    const r = Math.min(g.w, plotH) / 2 - 8;
    const cx = g.w / 2;
    const cy = plotH / 2;
    const ring = Math.max(16, r * 0.32);
    const arcs = anglesOf(slices);

    slices.forEach((s, i) => {
      ctx.beginPath();
      ctx.arc(cx, cy, r, arcs[i].from, arcs[i].to);
      ctx.arc(cx, cy, r - ring, arcs[i].to, arcs[i].from, true);
      ctx.closePath();
      ctx.fillStyle = i === g.hover ? hueOf(s, i) : hueOf(s, i) + 'e6';
      ctx.fill();
      // 2px surface gap between adjacent fills.
      ctx.lineWidth = 2; ctx.strokeStyle = k.surface; ctx.stroke();
    });

    const total = slices.reduce((sum, s) => sum + s.value, 0);
    ctx.fillStyle = k.text;
    ctx.font = '600 22px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(fmt(total), cx, cy + 2);
    ctx.fillStyle = k.muted;
    ctx.font = FONT;
    ctx.fillText(String(g.data.centerLabel || 'Total'), cx, cy + 18);

    ctx.textAlign = 'left';
    slices.forEach((s, i) => {
      const col = i % 3;
      const row = Math.floor(i / 3);
      const x = 8 + col * (g.w - 16) / 3;
      const y = plotH + 4 + row * 18;
      ctx.fillStyle = hueOf(s, i);
      ctx.fillRect(x, y, 8, 8);
      ctx.fillStyle = k.muted;
      ctx.fillText(`${s.label} · ${fmt(s.value)}`, x + 12, y + 8);
    });
  },
  hitTest(g, mx, my) {
    const { slices } = g.l;
    if (!slices.length) return null;
    const plotH = g.data.height || 220;
    const r = Math.min(g.w, plotH) / 2 - 8;
    const cx = g.w / 2;
    const cy = plotH / 2;
    const ring = Math.max(16, r * 0.32);
    const dx = mx - cx;
    const dy = my - cy;
    const dist = Math.hypot(dx, dy);
    if (dist > r || dist < r - ring) return null;
    let angle = Math.atan2(dy, dx);
    if (angle < -Math.PI / 2) angle += TAU;
    const arcs = anglesOf(slices);
    const i = arcs.findIndex((a) => angle >= a.from && angle < a.to);
    if (i < 0) return null;
    const mid = (arcs[i].from + arcs[i].to) / 2;
    return {
      i,
      text: `${slices[i].label}: ${fmt(slices[i].value)}`,
      x: cx + Math.cos(mid) * (r - ring / 2),
      y: cy + Math.sin(mid) * (r - ring / 2),
    };
  },
});
return { __default };
})();

const __m125 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
const fmt = __m120.fmt;
const FONT = __m120.FONT;
// Single measure against a range: a 180-degree arc with the value as the hero
// number (the catalog's "gauge" display).
// data: { value, min? (0), max? (100), label?, unit?, color?,
//         thresholds?: [{ upTo, color }] (sorted ascending; colors the arc by
//         the band the value falls in) }


const __default = canvasChart({
  tags: ['swc-gauge', 's-gau'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data, w) => {
    const r = Math.min(w / 2 - 12, 96);
    return { h: r + 46, r };
  },
  draw(ctx, g, k) {
    const d = g.data;
    const min = d.min || 0;
    const max = d.max == null ? 100 : d.max;
    const t = Math.min(1, Math.max(0, ((d.value || 0) - min) / ((max - min) || 1)));
    const band = (d.thresholds || []).filter((b) => (d.value || 0) <= b.upTo)[0];
    const hue = (band && band.color) || d.color || SERIES[0];
    const cx = g.w / 2;
    const cy = g.l.r + 10;
    const lw = 14;

    ctx.lineWidth = lw;
    ctx.lineCap = 'round';
    ctx.strokeStyle = k.grid;
    ctx.beginPath(); ctx.arc(cx, cy, g.l.r - lw / 2, Math.PI, 2 * Math.PI); ctx.stroke();
    ctx.strokeStyle = hue;
    ctx.beginPath(); ctx.arc(cx, cy, g.l.r - lw / 2, Math.PI, Math.PI * (1 + t)); ctx.stroke();

    // Hero number in text ink, never the series hue.
    ctx.fillStyle = k.text;
    ctx.font = '600 24px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${fmt(d.value || 0)}${d.unit || ''}`, cx, cy - 4);
    ctx.fillStyle = k.muted;
    ctx.font = FONT;
    if (d.label) ctx.fillText(String(d.label), cx, cy + 16);
    ctx.textAlign = 'left';
    ctx.fillText(fmt(min), cx - g.l.r + lw / 2, cy + 16);
    ctx.textAlign = 'right';
    ctx.fillText(fmt(max), cx + g.l.r - lw / 2, cy + 16);
  },
});
return { __default };
})();

const __m126 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
const fmt = __m120.fmt;
const drawGrid = __m120.drawGrid;
const drawLegend = __m120.drawLegend;
const sparseXLabels = __m120.sparseXLabels;
const niceMax = __m120.niceMax;
// Line/area chart over time with axes, grid and multi-series support (the
// catalog's "line-chart" display). Hues in fixed SERIES order, never cycled;
// a legend appears for two or more series; the area fill only for one (overlap
// turns to mud). Crosshair hover reads every series at the nearest point.
// data: { series: [{ name?, color?, points: [{ label, value }] }],
//         height? (220), area? (true for a single series) }


const PAD = { top: 12, right: 12, bottom: 22, left: 40 };

const seriesOf = (data) => (data.series || []).filter((s) => (s.points || []).length);
const hueOf = (s, si) => s.color || SERIES[si % SERIES.length];

const __default = canvasChart({
  tags: ['swc-line-chart', 's-lch'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data) => {
    const series = seriesOf(data);
    const legend = series.length > 1 ? 18 : 0;
    return { h: (data.height || 220) + legend, series, legend };
  },
  draw(ctx, g, k) {
    const { series, legend } = g.l;
    if (!series.length) return;
    if (legend) drawLegend(ctx, series.map((s, si) => ({ name: s.name || `Series ${si + 1}`, color: hueOf(s, si) })), PAD.left, 2, k);

    const top = PAD.top + legend;
    const ih = g.h - top - PAD.bottom;
    const iw = g.w - PAD.left - PAD.right;
    const max = niceMax(Math.max(...series.flatMap((s) => s.points.map((p) => p.value))));
    const y = drawGrid(ctx, { left: PAD.left, right: PAD.right, top, ih, w: g.w }, max, k);
    const n = Math.max(...series.map((s) => s.points.length));
    const px = (i) => PAD.left + (n === 1 ? iw / 2 : (i / (n - 1)) * iw);
    const area = data0Area(g.data, series);

    (area ? series : []).forEach((s, si) => {
      ctx.beginPath();
      s.points.forEach((p, i) => (i ? ctx.lineTo(px(i), y(p.value)) : ctx.moveTo(px(0), y(p.value))));
      ctx.lineTo(px(s.points.length - 1), top + ih); ctx.lineTo(px(0), top + ih); ctx.closePath();
      ctx.fillStyle = hueOf(s, si) + '14'; ctx.fill();
    });
    series.forEach((s, si) => {
      ctx.beginPath();
      s.points.forEach((p, i) => (i ? ctx.lineTo(px(i), y(p.value)) : ctx.moveTo(px(0), y(p.value))));
      ctx.strokeStyle = hueOf(s, si); ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.stroke();
    });

    sparseXLabels(ctx, series[0].points.map((p) => p.label), px, g.h - 6, k);

    if (g.hover >= 0 && g.hover < n) {
      ctx.strokeStyle = k.grid;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(px(g.hover), top); ctx.lineTo(px(g.hover), top + ih); ctx.stroke();
      series
        .map((s, si) => ({ p: s.points[g.hover], hue: hueOf(s, si) }))
        .filter((e) => e.p)
        .forEach((e) => {
          ctx.beginPath(); ctx.arc(px(g.hover), y(e.p.value), 4, 0, Math.PI * 2);
          ctx.fillStyle = e.hue; ctx.fill();
          ctx.lineWidth = 2; ctx.strokeStyle = k.surface; ctx.stroke();
        });
    }
  },
  hitTest(g, mx) {
    const { series, legend } = g.l;
    if (!series.length) return null;
    const n = Math.max(...series.map((s) => s.points.length));
    const iw = g.w - PAD.left - PAD.right;
    const px = (i) => PAD.left + (n === 1 ? iw / 2 : (i / (n - 1)) * iw);
    let best = -1; let bd = Infinity;
    Array.from({ length: n }).forEach((_, i) => {
      const d = Math.abs(px(i) - mx);
      if (d < bd) { bd = d; best = i; }
    });
    if (bd > 24) return null;
    const label = series[0].points[best] ? series[0].points[best].label : '';
    const parts = series
      .filter((s) => s.points[best])
      .map((s, si) => `${s.name || `Series ${si + 1}`}: ${fmt(s.points[best].value)}`);
    return { i: best, text: `${label != null ? label + ' ' : ''}${parts.join(' · ')}`, x: px(best), y: PAD.top + legend };
  },
});

// Area is opt-in via data.area, defaulting on for a single series.
function data0Area(data, series) {
  return data.area == null ? series.length === 1 : !!data.area;
}
return { __default };
})();

const __m127 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
const FONT = __m120.FONT;
const fmt = __m120.fmt;
const scheduleChartDraw = __m120.scheduleChartDraw;
// Pins on a map (the catalog's "map-pins" display). Two grounds, one component:
// by default an abstract equirectangular map — honest cartography without a
// tile server: a graticule frame (degree grid fitted to the pins' bounding
// box) and, when the caller supplies real geometry, `outline` polygons in
// border ink; it never fakes a coastline it does not have. Given `tiles`, a
// URL template with {z} {x} {y}, the ground is a real Web Mercator tile layer
// with the zoom fitted to the pins — the map a person expects when the pins
// are places to walk to. Pins are hue dots with hover labels; `origin` is the
// place the pins are measured from, drawn as a ring; clicking a pin dispatches
// `pinselect` (detail { index, pin }) for list sync, and `pinPoint(i)` gives a
// pin's canvas position to whatever wants to stand something beside it.
// data: { pins: [{ lat, lng, label?, value? }], outline?: [[[lat,lng], ...], ...],
//         selected?: index, height? (260),
//         tiles?: 'https://…/{z}/{x}/{y}.png', attribution?: text shown on the
//         map (a tile licence usually requires it), origin?: { lat, lng },
//         maxZoom? (17) }


const PAD = 12;

// Every point the map must show: the pins, the origin, the outline.
function points(data) {
  return [
    ...(data.pins || []).map((p) => [p.lat, p.lng]),
    ...(data.origin ? [[data.origin.lat, data.origin.lng]] : []),
    ...(data.outline || []).flat(),
  ].filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
}

// Bounding box of the points, padded 15% and clamped to the world.
function bounds(data) {
  const pts = points(data);
  if (!pts.length) return { south: -60, north: 75, west: -170, east: 180 };
  let south = Math.min(...pts.map((p) => p[0]));
  let north = Math.max(...pts.map((p) => p[0]));
  let west = Math.min(...pts.map((p) => p[1]));
  let east = Math.max(...pts.map((p) => p[1]));
  const padLat = Math.max(2, (north - south) * 0.15);
  const padLng = Math.max(2, (east - west) * 0.15);
  south = Math.max(-90, south - padLat); north = Math.min(90, north + padLat);
  west = Math.max(-180, west - padLng); east = Math.min(180, east + padLng);
  return { south, north, west, east };
}

// Grid step from the span: whole-degree lines that stay recessive.
const gridStep = (span) => (span > 90 ? 30 : span > 30 ? 10 : span > 10 ? 5 : 1);

// Equirectangular projection fitted into the frame, degrees kept square.
function projector(b, w, h) {
  const iw = w - PAD * 2;
  const ih = h - PAD * 2;
  const spanLng = b.east - b.west || 1;
  const spanLat = b.north - b.south || 1;
  const scale = Math.min(iw / spanLng, ih / spanLat);
  const ox = PAD + (iw - spanLng * scale) / 2;
  const oy = PAD + (ih - spanLat * scale) / 2;
  return {
    x: (lng) => ox + (lng - b.west) * scale,
    y: (lat) => oy + (b.north - lat) * scale,
    frame: { x: ox, y: oy, w: spanLng * scale, h: spanLat * scale },
  };
}

// ── The tile ground ────────────────────────────────────────────────────────
// Web Mercator, the projection every tile server speaks: a tile is 256 px, the
// world at zoom z is 256·2^z px wide, and a point's pixel is the same on every
// server. The zoom is the highest whole one at which every point fits inside
// the padded canvas; the view is centred on the points' box.
const TILE = 256;
const MAX_ZOOM = 17;
const MERC_LAT = 85.0511;
const mercX = (lng, z) => ((lng + 180) / 360) * TILE * 2 ** z;
const mercY = (lat, z) => {
  const s = Math.sin((Math.max(-MERC_LAT, Math.min(MERC_LAT, lat)) * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE * 2 ** z;
};
function fitMercator(pts, w, h, maxZoom) {
  const top = Number.isFinite(maxZoom) ? Math.max(1, Math.min(19, maxZoom)) : MAX_ZOOM;
  if (!pts.length) return { z: 5, cx: mercX(-2.5, 5), cy: mercY(54, 5) };   // nothing to fit: a whole island
  const iw = Math.max(1, w - PAD * 2);
  const ih = Math.max(1, h - PAD * 2);
  const south = Math.min(...pts.map((p) => p[0]));
  const north = Math.max(...pts.map((p) => p[0]));
  const west = Math.min(...pts.map((p) => p[1]));
  const east = Math.max(...pts.map((p) => p[1]));
  let z = top;
  for (; z > 1; z--) {
    if (mercX(east, z) - mercX(west, z) <= iw && mercY(south, z) - mercY(north, z) <= ih) break;
  }
  // One place, or all in one street: a zoom that still shows a neighbourhood.
  if (z === top && pts.length === 1) z = Math.min(top, 16);
  return { z, cx: (mercX(west, z) + mercX(east, z)) / 2, cy: (mercY(north, z) + mercY(south, z)) / 2 };
}
function mercProjector(m, w, h) {
  return {
    x: (lng) => w / 2 + mercX(lng, m.z) - m.cx,
    y: (lat) => h / 2 + mercY(lat, m.z) - m.cy,
    frame: { x: 0, y: 0, w, h },
  };
}
// One projector for both grounds, so hover, click and pinPoint agree with the draw.
const proj = (g) => (g.l.merc ? mercProjector(g.l.merc, g.w, g.h) : projector(g.l.b, g.w, g.h));

// Tiles are shared by every map on the page and kept by URL; a tile that has
// not arrived yet remembers who is waiting and redraws them when it lands.
// Bounded, so a long session of panning around does not keep every tile.
const TILES = new Map();
const TILE_CACHE = 400;
function tile(url, host) {
  let img = TILES.get(url);
  if (!img) {
    img = new Image();
    img.decoding = 'async';
    const wake = () => { (img._waiting || []).forEach((h) => scheduleChartDraw(h)); img._waiting = null; };
    img.addEventListener('load', wake);
    img.addEventListener('error', wake);
    img.src = url;
    TILES.set(url, img);
    if (TILES.size > TILE_CACHE) TILES.delete(TILES.keys().next().value);
  }
  if (!(img.complete && img.naturalWidth)) {
    img._waiting = img._waiting || [];
    if (!img._waiting.includes(host)) img._waiting.push(host);
  }
  return img;
}
function drawTiles(ctx, g) {
  const m = g.l.merc;
  const n = 2 ** m.z;
  const left = m.cx - g.w / 2;
  const top = m.cy - g.h / 2;
  const tx0 = Math.floor(left / TILE);
  const tx1 = Math.floor((left + g.w) / TILE);
  const ty0 = Math.max(0, Math.floor(top / TILE));
  const ty1 = Math.min(n - 1, Math.floor((top + g.h) / TILE));
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      const wx = ((tx % n) + n) % n;   // across the antimeridian, the world repeats
      const url = String(g.data.tiles).replace('{z}', m.z).replace('{x}', wx).replace('{y}', ty);
      const img = tile(url, g.host);
      if (img.complete && img.naturalWidth) ctx.drawImage(img, tx * TILE - left, ty * TILE - top, TILE, TILE);
    }
  }
}

const def = canvasChart({
  tags: ['swc-map-pins', 's-map'],
  dispatches: ['pinselect'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data, w) => {
    const h = data.height || 260;
    return { h, b: bounds(data), pins: data.pins || [], merc: data.tiles ? fitMercator(points(data), w, h, data.maxZoom) : null };
  },
  draw(ctx, g, k) {
    const { b, pins, merc } = g.l;
    const pr = proj(g);
    // The attribution the tile licence asks for, on the map: a DOM region,
    // so it is text, not paint. Empty — and hidden — without tiles.
    const attr = g.host.refs && g.host.refs.attr;
    const credit = merc ? String(g.data.attribution || '') : '';
    if (attr && attr.textContent !== credit) attr.textContent = credit;
    ctx.save();
    ctx.beginPath();
    ctx.rect(pr.frame.x, pr.frame.y, pr.frame.w, pr.frame.h);
    ctx.clip();
    if (merc) {
      drawTiles(ctx, g);
    } else {
      // Frame + graticule in recessive border ink; equator and prime meridian a
      // step stronger so the grid stays readable as geography.
      ctx.strokeStyle = k.grid;
      ctx.lineWidth = 1;
      ctx.strokeRect(pr.frame.x, pr.frame.y, pr.frame.w, pr.frame.h);
      const step = gridStep(Math.max(b.east - b.west, b.north - b.south));
      for (let lng = Math.ceil(b.west / step) * step; lng <= b.east; lng += step) {
        ctx.globalAlpha = lng === 0 ? 0.9 : 0.45;
        ctx.beginPath(); ctx.moveTo(pr.x(lng), pr.y(b.north)); ctx.lineTo(pr.x(lng), pr.y(b.south)); ctx.stroke();
      }
      for (let lat = Math.ceil(b.south / step) * step; lat <= b.north; lat += step) {
        ctx.globalAlpha = lat === 0 ? 0.9 : 0.45;
        ctx.beginPath(); ctx.moveTo(pr.x(b.west), pr.y(lat)); ctx.lineTo(pr.x(b.east), pr.y(lat)); ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
    // Caller-supplied geometry (regions, coastlines) in the same recessive ink.
    (g.data.outline || []).forEach((poly) => {
      ctx.beginPath();
      poly.forEach(([lat, lng], i) => {
        i === 0 ? ctx.moveTo(pr.x(lng), pr.y(lat)) : ctx.lineTo(pr.x(lng), pr.y(lat));
      });
      ctx.closePath();
      ctx.strokeStyle = k.muted;
      ctx.globalAlpha = 0.6;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });
    // The origin — where the pins are measured from — as a ring in text ink
    // over a surface disc: a different mark from a pin, because it is a
    // different thing.
    const o = g.data.origin;
    if (o && Number.isFinite(o.lat) && Number.isFinite(o.lng)) {
      const x = pr.x(o.lng);
      const y = pr.y(o.lat);
      ctx.beginPath(); ctx.arc(x, y, 7, 0, Math.PI * 2);
      ctx.fillStyle = k.surface; ctx.fill();
      ctx.strokeStyle = k.text; ctx.lineWidth = 2; ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = k.text; ctx.fill();
    }
    ctx.restore();
    // Pins: primary-hue dots, value-scaled radius, selected/hover emphasized
    // with a ring; labels only on emphasis (relief without clutter). Over
    // tiles the dots are solid — a translucent dot over a map reads as a stain.
    const maxV = Math.max(1, ...pins.map((p) => Number(p.value) || 0));
    pins.forEach((p, i) => {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
      const x = pr.x(p.lng);
      const y = pr.y(p.lat);
      const r = p.value ? 4 + 6 * Math.sqrt((Number(p.value) || 0) / maxV) : merc ? 6 : 5;
      const active = i === g.hover || i === g.data.selected;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = active || merc ? SERIES[0] : SERIES[0] + 'b3';
      ctx.fill();
      ctx.strokeStyle = k.surface;
      ctx.lineWidth = merc ? 2 : 1.5;
      ctx.stroke();
      if (active) {
        ctx.beginPath();
        ctx.arc(x, y, r + 3, 0, Math.PI * 2);
        ctx.strokeStyle = SERIES[0];
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    });
  },
  hitTest(g, mx, my) {
    const { pins } = g.l;
    const pr = proj(g);
    let best = null;
    pins.forEach((p, i) => {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
      const dx = pr.x(p.lng) - mx;
      const dy = pr.y(p.lat) - my;
      const d = Math.hypot(dx, dy);
      if (d <= 12 && (!best || d < best.d)) best = { d, i, p };
    });
    if (!best) return null;
    const text = [best.p.label, best.p.value !== undefined ? fmt(Number(best.p.value)) : '']
      .filter(Boolean).join(': ') || `${best.p.lat}, ${best.p.lng}`;
    return { i: best.i, text, x: pr.x(best.p.lng), y: pr.y(best.p.lat) - 8 };
  },
});

// The attribution region, beside the canvas and the tooltip the factory gives.
def.children.push(['s-cn', ':ref=attr', '@rg=attr']);

// Pin click → `pinselect` bubbling event, so a composite (s-cmp) or app can
// sync a list selection without reaching into the canvas.
const baseConnected = def.callbacks.connected;
def.callbacks.connected = function connected() {
  baseConnected.call(this);
  this.addEventListener('click', (e) => this._onPinClick(e));
};
def.methods._onPinClick = function _onPinClick(e) {
  const g = this._geometry();
  if (!g || !g.data) return;
  const rect = this.getBoundingClientRect();
  const pr = proj(g);
  const mx = e.clientX - rect.left;
  const my = e.clientY - rect.top;
  let hit = null;
  (g.l.pins || []).forEach((p, i) => {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
    const d = Math.hypot(pr.x(p.lng) - mx, pr.y(p.lat) - my);
    if (d <= 12 && (!hit || d < hit.d)) hit = { d, i, p };
  });
  if (hit) this.dispatchEvent(new CustomEvent('pinselect', { bubbles: true, detail: { index: hit.i, pin: hit.p } }));
};

// Where pin `i` is on the canvas, in CSS pixels — for a caller that wants to
// stand something beside it (a label, a callout) without redoing the projection.
def.methods.pinPoint = function pinPoint(i) {
  const g = this._geometry();
  const p = g && g.data && g.l.pins[i];
  if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return null;
  const pr = proj(g);
  return { x: pr.x(p.lng), y: pr.y(p.lat) };
};

const __default = def;
return { __default };
})();

const __m128 = (() => {
const canvasChart = __m120.canvasChart;
const FONT = __m120.FONT;
const truncate = __m120.truncate;
// Hierarchy of nodes with connectors (the catalog's "org-chart" display).
// Tidy top-down layout: each leaf takes a slot, parents center over their
// children; elbow connectors in border ink; boxes on the surface with the
// name in text ink and the role muted.
// data: { root: { name, role?, children? [] } }  (or the root object directly)


const BOX = { h: 40, gapY: 32, minW: 90, maxW: 150 };

function leaves(node) {
  return (node.children || []).length
    ? node.children.reduce((s, c) => s + leaves(c), 0)
    : 1;
}

function depthOf(node) {
  return 1 + Math.max(0, ...(node.children || []).map(depthOf));
}

// Assign each node a center x in leaf-slot units and a row.
function placed(node, row, offset) {
  const kids = node.children || [];
  if (!kids.length) return [{ node, row, center: offset + 0.5, span: 1 }];
  let cursor = offset;
  const below = kids.flatMap((c) => {
    const sub = placed(c, row + 1, cursor);
    cursor += leaves(c);
    return sub;
  });
  const own = below.filter((p) => p.row === row + 1 && kids.includes(p.node));
  const center = (own[0].center + own[own.length - 1].center) / 2;
  return [{ node, row, center, span: leaves(node) }, ...below];
}

const __default = canvasChart({
  tags: ['swc-org-chart', 's-org'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data, w) => {
    const root = data.root || (data.name ? data : null);
    if (!root) return { h: 0, nodes: [], boxW: BOX.minW };
    const slots = leaves(root);
    const boxW = Math.max(BOX.minW, Math.min(BOX.maxW, (w - 16) / slots - 8));
    const slotW = Math.max(boxW + 8, (w - 16) / slots);
    const nodes = placed(root, 0, 0).map((p) => ({
      ...p,
      x: 8 + p.center * slotW - boxW / 2 + (w - 16 - slots * slotW) / 2,
      y: 4 + p.row * (BOX.h + BOX.gapY),
    }));
    return { h: depthOf(root) * (BOX.h + BOX.gapY) - BOX.gapY + 8, nodes, boxW };
  },
  draw(ctx, g, k) {
    const { nodes, boxW } = g.l;
    if (!nodes.length) return;
    const byNode = new Map(nodes.map((p) => [p.node, p]));

    // Elbow connectors first, under the boxes.
    ctx.strokeStyle = k.grid;
    ctx.lineWidth = 1;
    nodes
      .flatMap((p) => (p.node.children || []).map((c) => ({ from: p, to: byNode.get(c) })))
      .filter((e) => e.to)
      .forEach(({ from, to }) => {
        const x1 = from.x + boxW / 2;
        const y1 = from.y + BOX.h;
        const x2 = to.x + boxW / 2;
        const midY = y1 + BOX.gapY / 2;
        ctx.beginPath();
        ctx.moveTo(x1, y1); ctx.lineTo(x1, midY); ctx.lineTo(x2, midY); ctx.lineTo(x2, to.y);
        ctx.stroke();
      });

    nodes.forEach((p) => {
      ctx.beginPath();
      ctx.roundRect(p.x, p.y, boxW, BOX.h, 6);
      ctx.fillStyle = k.surface;
      ctx.fill();
      ctx.strokeStyle = k.grid;
      ctx.stroke();
      ctx.textAlign = 'center';
      ctx.fillStyle = k.text;
      ctx.font = '600 12px system-ui, sans-serif';
      ctx.fillText(truncate(ctx, String(p.node.name ?? ''), boxW - 12), p.x + boxW / 2, p.y + 17);
      ctx.fillStyle = k.muted;
      ctx.font = FONT;
      ctx.fillText(truncate(ctx, String(p.node.role ?? ''), boxW - 12), p.x + boxW / 2, p.y + 31);
    });
  },
});
return { __default };
})();

const __m129 = (() => {
// Layering for node/edge graphs, shared by the charts that draw them.
//
// Lifted out of relation-graph.js when s-arc needed the same answer: which
// column does each node belong in. Two copies of a graph walk is two places
// for the same diagram to be laid out differently, which is exactly the kind
// of drift the reader notices and cannot explain.
//
// Deterministic by construction — input order, no randomness — because a
// layout that moves between renders cannot be tested, and an untestable layout
// is one that gets corrected by eye for ever.

/**
 * The column index of every node.
 *
 * Every in-degree-0 node is a root and starts at 0. Leftovers (a cycle has no
 * root) seed fresh walks in input order, so every node lands in exactly one
 * layer and none is dropped.
 */
function layersOf(nodes, edges) {
  const idx = new Map(nodes.map((n, i) => [n.id, i]));
  const out = nodes.map(() => []);
  const indeg = nodes.map(() => 0);
  edges
    .filter((e) => idx.has(e.from) && idx.has(e.to))
    .forEach((e) => {
      out[idx.get(e.from)].push(idx.get(e.to));
      indeg[idx.get(e.to)] += 1;
    });
  const layer = nodes.map(() => -1);
  const roots = nodes.map((_, i) => i).filter((i) => indeg[i] === 0);
  const walk = (seed) => {
    const q = [seed];
    if (layer[seed] < 0) layer[seed] = 0;
    while (q.length) {
      const i = q.shift();
      out[i]
        .filter((j) => layer[j] < 0)
        .forEach((j) => { layer[j] = layer[i] + 1; q.push(j); });
    }
  };
  (roots.length ? roots : nodes.length ? [0] : []).forEach(walk);
  nodes.map((_, i) => i).filter((i) => layer[i] < 0).forEach(walk);
  return layer;
}

/** Node indices grouped by column, each column in input order. */
function columnsOf(nodes, edges) {
  const layer = layersOf(nodes, edges);
  const count = Math.max(...layer, 0) + 1;
  const cols = Array.from({ length: nodes.length ? count : 0 }, () => []);
  nodes.forEach((_, i) => cols[layer[i]].push(i));
  return cols;
}
return { layersOf, columnsOf };
})();

const __m130 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
const FONT = __m120.FONT;
const truncate = __m120.truncate;
const layersOf = __m129.layersOf;
// Relation graph (nodes + directed edges), the editor-style graph view: entity
// relations, flow steps, routing topologies. Modelled on the agent_stack flow
// and router editors' node graphs, rebuilt on the shared canvas factory.
// data: { nodes: [{ id, label, kind?, x?, y? }], edges: [{ from, to, label?,
//         kind? }], selected? (node id), height? (320) }
// Layout is a deterministic layered BFS from the in-degree-0 roots (input
// order, no randomness); explicit x/y (fractions of the drawing area, 0..1)
// pin a node out of the automatic layout. Nodes drag with the pointer (drag
// state lives on the component, the input data is never mutated), the empty
// area pans, hovering a node lifts it and its edges, clicking dispatches
// `nodeselect` (detail { index, node }) and reflects the selection.

// The layering is shared with s-arc (components/chart/_graph-layout.js): two
// copies of the same walk is two places for the same diagram to be laid out
// differently.


const PAD = 24;
const NODE_H = 26;

const nodeW = (n) => Math.max(56, Math.min(150, String(n.label ?? n.id).length * 7.2 + 20));

// Base positions in px: columns per layer across the width, rows spread per
// column; explicit x/y fractions override.
function positions(data, w, h) {
  const nodes = data.nodes || [];
  const layer = layersOf(nodes, data.edges || []);
  const cols = Math.max(...layer, 0) + 1;
  // Horizontal inset accounts for the widest node box, so first/last column
  // boxes never clip at the canvas edges.
  const mx = PAD + Math.max(0, ...nodes.map(nodeW)) / 2;
  const iw = w - mx * 2;
  const ih = h - PAD * 2;
  const colX = (l) => mx + (cols === 1 ? iw / 2 : (l * iw) / (cols - 1));
  const byCol = Array.from({ length: cols }, () => []);
  nodes.forEach((n, i) => byCol[layer[i]].push(i));
  const pos = nodes.map(() => null);
  byCol.forEach((col, l) => {
    col.forEach((i, r) => {
      const n = nodes[i];
      pos[i] = {
        x: n.x !== undefined ? mx + n.x * iw : colX(l),
        y: n.y !== undefined ? PAD + n.y * ih : PAD + ((r + 0.5) * ih) / col.length,
      };
    });
  });
  return pos;
}

// Clip a center-to-center segment at the node's rounded-rect boundary.
function clipAt(from, to, hw, hh) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const t = 1 / Math.max(Math.abs(dx) / (hw + 4), Math.abs(dy) / (hh + 4), 1e-6);
  return { x: from.x + dx * Math.min(1, t), y: from.y + dy * Math.min(1, t) };
}

const kindColor = (kinds, kind) => {
  if (kind === undefined || kind === null) return null;
  !kinds.has(kind) && kinds.set(kind, kinds.size);
  return SERIES[kinds.get(kind) % SERIES.length];
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const def = canvasChart({
  tags: ['swc-relation-graph', 's-rel'],
  dispatches: ['nodeselect'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout(data, w, host) {
    const h = data.height || 320;
    const base = positions(data, w, h);
    const off = (host && host._offsets) || {};
    const pan = (host && host._pan) || { x: 0, y: 0 };
    const pos = (data.nodes || []).map((n, i) => ({
      x: base[i].x + (off[n.id] ? off[n.id].x : 0) + pan.x,
      y: base[i].y + (off[n.id] ? off[n.id].y : 0) + pan.y,
    }));
    return { h, pos };
  },
  draw(ctx, g, k) {
    const nodes = g.data.nodes || [];
    if (!nodes.length) return;
    const edges = (g.data.edges || []);
    const idx = new Map(nodes.map((n, i) => [n.id, i]));
    const pos = g.l.pos;
    const kinds = new Map();
    const selId = g.host._sel !== undefined ? g.host._sel : g.data.selected;
    const hoverId = g.hover >= 0 && nodes[g.hover] ? nodes[g.hover].id : undefined;

    ctx.font = FONT;
    edges
      .filter((e) => idx.has(e.from) && idx.has(e.to))
      .forEach((e, ei) => {
        const a = pos[idx.get(e.from)];
        const b = pos[idx.get(e.to)];
        const na = nodes[idx.get(e.from)];
        const nb = nodes[idx.get(e.to)];
        const p1 = clipAt(a, b, nodeW(na) / 2, NODE_H / 2);
        const p2 = clipAt(b, a, nodeW(nb) / 2, NODE_H / 2);
        const near = hoverId !== undefined && (e.from === hoverId || e.to === hoverId);
        const color = kindColor(kinds, e.kind) || k.muted;
        ctx.globalAlpha = hoverId === undefined ? 0.75 : near ? 1 : 0.25;
        ctx.strokeStyle = near ? SERIES[0] : color;
        ctx.lineWidth = near ? 1.6 : 1.1;
        // Gentle deterministic bend, alternating side by edge index, so
        // opposite-direction pairs separate instead of overlapping.
        const mx = (p1.x + p2.x) / 2;
        const my = (p1.y + p2.y) / 2;
        const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
        const bend = Math.min(26, dist * 0.14) * (ei % 2 ? -1 : 1);
        const cx = mx + (-(p2.y - p1.y) / dist) * bend;
        const cy = my + ((p2.x - p1.x) / dist) * bend;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.quadraticCurveTo(cx, cy, p2.x, p2.y);
        ctx.stroke();
        // Arrowhead along the curve's arrival direction.
        const ang = Math.atan2(p2.y - cy, p2.x - cx);
        ctx.beginPath();
        ctx.moveTo(p2.x, p2.y);
        ctx.lineTo(p2.x - 7 * Math.cos(ang - 0.45), p2.y - 7 * Math.sin(ang - 0.45));
        ctx.lineTo(p2.x - 7 * Math.cos(ang + 0.45), p2.y - 7 * Math.sin(ang + 0.45));
        ctx.closePath();
        ctx.fillStyle = ctx.strokeStyle;
        ctx.fill();
        if (e.label && (edges.length <= 24 || near)) {
          ctx.fillStyle = k.muted;
          ctx.textAlign = 'center';
          ctx.fillText(String(e.label), cx, cy - 4);
        }
        ctx.globalAlpha = 1;
      });

    nodes.forEach((n, i) => {
      const p = pos[i];
      const w2 = nodeW(n);
      const active = n.id === hoverId || n.id === selId;
      const color = kindColor(kinds, n.kind) || SERIES[0];
      roundRect(ctx, p.x - w2 / 2, p.y - NODE_H / 2, w2, NODE_H, 6);
      ctx.fillStyle = k.surface;
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = active ? 2 : 1.2;
      ctx.stroke();
      if (n.id === selId) {
        roundRect(ctx, p.x - w2 / 2 - 3, p.y - NODE_H / 2 - 3, w2 + 6, NODE_H + 6, 8);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      ctx.fillStyle = k.text;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(truncate(ctx, String(n.label ?? n.id), w2 - 14), p.x, p.y + 0.5);
      ctx.textBaseline = 'alphabetic';
    });
  },
  hitTest(g, mx, my) {
    const i = hitNode(g, mx, my);
    if (i < 0) return null;
    const n = g.data.nodes[i];
    const text = [String(n.label ?? n.id), n.kind ? `(${n.kind})` : ''].filter(Boolean).join(' ');
    return { i, text, x: g.l.pos[i].x, y: g.l.pos[i].y - NODE_H / 2 - 2 };
  },
});

function hitNode(g, mx, my) {
  const nodes = (g.data && g.data.nodes) || [];
  let hit = -1;
  nodes.forEach((n, i) => {
    const p = g.l.pos[i];
    if (Math.abs(mx - p.x) <= nodeW(n) / 2 + 2 && Math.abs(my - p.y) <= NODE_H / 2 + 2) hit = i;
  });
  return hit;
}

// Pointer interaction rides the host (delegation on self, the map-pins
// precedent): a press on a node drags it, a press on empty canvas pans, and a
// press that never moved past the threshold is a click → select + event.
const baseConnected = def.callbacks.connected;
def.callbacks.connected = function connected() {
  baseConnected.call(this);
  this._offsets = {};
  this._pan = { x: 0, y: 0 };
  this.addEventListener('pointerdown', (e) => this._onDown(e));
  this.addEventListener('pointermove', (e) => this._onDrag(e));
  this.addEventListener('pointerup', (e) => this._onUp(e));
};
Object.assign(def.methods, {
  _local(e) {
    const rect = this.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  },
  _onDown(e) {
    const g = this._geometry();
    if (!g || !g.data) return;
    const at = this._local(e);
    const i = hitNode(g, at.x, at.y);
    this._drag = { i, at, moved: false };
    this.setPointerCapture && this.setPointerCapture(e.pointerId);
  },
  _onDrag(e) {
    if (!this._drag) return;
    const at = this._local(e);
    const dx = at.x - this._drag.at.x;
    const dy = at.y - this._drag.at.y;
    if (Math.hypot(dx, dy) > 3) this._drag.moved = true;
    if (!this._drag.moved) return;
    if (this._drag.i >= 0) {
      const id = this._chart.nodes[this._drag.i].id;
      const o = this._offsets[id] || { x: 0, y: 0 };
      this._offsets[id] = { x: o.x + dx, y: o.y + dy };
    } else {
      this._pan = { x: this._pan.x + dx, y: this._pan.y + dy };
    }
    this._drag.at = at;
    this._draw();
  },
  _onUp(e) {
    const drag = this._drag;
    this._drag = null;
    if (!drag || drag.moved || drag.i < 0) return;
    const node = this._chart && this._chart.nodes && this._chart.nodes[drag.i];
    if (!node) return;
    this._sel = node.id;
    this._draw();
    this.dispatchEvent(new CustomEvent('nodeselect', { bubbles: true, detail: { index: drag.i, node } }));
  },
});
// A new data set resets drag state; selection follows data.selected again.
const baseData = Object.getOwnPropertyDescriptor(def.methods, 'data');
Object.defineProperty(def.methods, 'data', {
  set(v) {
    this._offsets = {};
    this._pan = { x: 0, y: 0 };
    this._sel = undefined;
    baseData.set.call(this, v);
  },
  get() { return baseData.get.call(this); },
  configurable: true,
  enumerable: true,
});

const __default = def;
return { __default };
})();

const __m131 = (() => {
const columnsOf = __m129.columnsOf;
// The geometry behind s-arc, with no DOM in it.
//
// Pure on purpose. The contract's guardrails are claims about positions — no
// two boxes overlap, no edge crosses a box, no two labels collide — and a claim
// about positions can be tested directly if the positions are computed by a
// function you can call. Put the same logic inside a component and the only way
// to check it is to render and look, which is the correction loop this exists
// to remove.


// Two edges between the same pair of nodes are spread along the face by this
// much, so the second is visible rather than drawn under the first.
const PORT_SPREAD_STEP = 10;

const GAP_Y = 16;
const GAP_X = 72;
const PAD = 24;

/**
 * Where every node goes, given how big each one measured.
 *
 * `sizes[i]` is `{ w, h }` for `nodes[i]`, measured from the real element after
 * CSS has sized it between its min and max. Nothing here estimates text: the
 * browser already did it, and it is the only thing that knows the font.
 *
 * Columns are packed with a GAP FLOOR, not divided into equal shares. That is
 * the difference from s-rel, which spreads `ih / col.length` and therefore
 * overlaps the moment a column has more nodes than the height allows. Here the
 * drawing grows and the caller is told; boxes never overlap.
 */
function layout(nodes, edges, sizes, opts = {}) {
  // `available` is the width the diagram may use. Columns spread to fill it,
  // because the space between them is where the edge labels go: a real
  // eleven-screen flow packed itself into half the width and left twelve
  // labels nowhere to sit. Filling the width is the room the routing needs,
  // not decoration.
  //
  // It only ever ADDS to the gap. A diagram wider than its container still
  // scrolls and is never squeezed, because squeezing is what stacks things.
  const available = opts.available || 0;
  const gapX = opts.gapX ?? GAP_X;
  const gapY = opts.gapY ?? GAP_Y;
  const pad = opts.pad ?? PAD;
  const size = (i) => sizes[i] || { w: 0, h: 0 };

  const cols = columnsOf(nodes, edges);
  const colWidth = cols.map((col) => Math.max(0, ...col.map((i) => size(i).w)));
  const colHeight = cols.map((col) => col.reduce((h, i, n) => h + size(i).h + (n ? gapY : 0), 0));
  const tallest = Math.max(0, ...colHeight);

  const natural = cols.reduce((w, _, c) => w + colWidth[c] + (c ? gapX : 0), 0) + pad * 2;
  const gaps = Math.max(0, cols.length - 1);
  const spread = gapX + (available && gaps ? Math.max(0, available - natural) / gaps : 0);

  const x = [];
  cols.forEach((_, c) => {
    x[c] = c === 0 ? pad : x[c - 1] + colWidth[c - 1] + spread;
  });

  // Groups become BANDS: a group owns a horizontal lane across every column,
  // and its nodes sit in that lane wherever their column puts them. Sorting
  // within a column was not enough — a group spanning two columns still made a
  // rectangle that swallowed whatever else shared those rows, so every region
  // was refused as dishonest and none were drawn. A lane is exclusive by
  // construction, which is what makes a region both drawable and true.
  //
  // It is also what the diagrams this is measured against actually do: the
  // eye reads the bands first and the nodes second.
  const bandOf = (i) => nodes[i].group || '';
  const bands = [...new Set(nodes.map((_, i) => bandOf(i)))].sort((a, b) => {
    if (!a) return 1;            // the ungrouped lane goes last
    if (!b) return -1;
    return 0;                    // otherwise first appearance, which is input order
  });

  // A band is as tall as its fullest column needs.
  const bandHeight = new Map(bands.map((g) => {
    const perCol = cols.map((col) => col.filter((i) => bandOf(i) === g)
      .reduce((h, i, n) => h + size(i).h + (n ? gapY : 0), 0));
    return [g, Math.max(0, ...perCol)];
  }));
  const bandTop = new Map();
  bands.reduce((y, g) => {
    bandTop.set(g, y);
    return y + bandHeight.get(g) + (bandHeight.get(g) ? gapY * 2 : 0);
  }, pad);
  const stackHeight = bands.reduce((h, g) => h + bandHeight.get(g) + (bandHeight.get(g) ? gapY * 2 : 0), 0);

  const pos = nodes.map(() => ({ x: 0, y: 0, w: 0, h: 0 }));
  cols.forEach((col, c) => {
    const cursor = new Map(bands.map((g) => [g, bandTop.get(g)]));
    col.forEach((i) => {
      const g = bandOf(i);
      const s2 = size(i);
      const y = cursor.get(g);
      // Centred in its column: a narrow box beside a wide one still has its
      // ports on the same axis, which is what keeps the edges level.
      pos[i] = { x: x[c] + (colWidth[c] - s2.w) / 2, y, w: s2.w, h: s2.h, col: c, band: g };
      cursor.set(g, y + s2.h + gapY);
    });
  });

  const width = (x[cols.length - 1] ?? pad) + (colWidth[cols.length - 1] ?? 0) + pad;
  const height = Math.max(tallest, stackHeight) + pad * 2;
  return { pos, width, height, cols, groups: groupRegions(nodes, pos) };
}

// ── Edge routing ──
//
// An edge is not one curve. Candidates are generated and SCORED, and the best
// is drawn. s-rel bends by edge index parity, which cannot know what is already
// on the canvas; this can, because it looks.

const lerp = (a, b, t) => a + (b - a) * t;
const quad = (p0, c, p1, t) => ({
  x: (1 - t) * (1 - t) * p0.x + 2 * (1 - t) * t * c.x + t * t * p1.x,
  y: (1 - t) * (1 - t) * p0.y + 2 * (1 - t) * t * c.y + t * t * p1.y,
});

const SAMPLES = 24;
const samplesOf = (route) => {
  const steps = Array.from({ length: SAMPLES + 1 }, (_, i) => i / SAMPLES);
  if (route.kind === 'quad') return steps.map((t) => quad(route.a, route.c, route.b, t));
  // A polyline (direct or elbow): walk its points at even parameter.
  const pts = route.points;
  return steps.map((t) => {
    const span = (pts.length - 1) * t;
    const i = Math.min(pts.length - 2, Math.floor(span));
    const f = span - i;
    return { x: lerp(pts[i].x, pts[i + 1].x, f), y: lerp(pts[i].y, pts[i + 1].y, f) };
  });
};

const inBox = (p, b, inset = 2) => p.x > b.x + inset && p.x < b.x + b.w - inset
  && p.y > b.y + inset && p.y < b.y + b.h - inset;

const segmentsCross = (p, p2, q, q2) => {
  const d = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  const d1 = d(q, q2, p); const d2 = d(q, q2, p2);
  const d3 = d(p, p2, q); const d4 = d(p, p2, q2);
  return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
};

/**
 * How many times two sampled routes cross.
 *
 * Every segment, including the last. An earlier version stopped one short of
 * the end of each path, which silently under-counted and made the crossing
 * budgets below read as zero on graphs that plainly had crossings — a measure
 * that always says "fine" is worse than no measure. `segmentsCross` is strict
 * about collinearity, so two routes sharing an endpoint do not count.
 */
function crossings(a, b) {
  const segs = (pts) => pts.slice(1).map((p, i) => [pts[i], p]);
  return segs(a).reduce((n, [p, p2]) => n + segs(b).filter(([q, q2]) => segmentsCross(p, p2, q, q2)).length, 0);
}

const length = (pts) => pts.reduce((sum, p, i) => (i ? sum + Math.hypot(p.x - pts[i - 1].x, p.y - pts[i - 1].y) : 0), 0);

// The scoring table from the contract. Crossing a node box is not a preference
// — it reads as a connection that is not there — so it is priced out of reach
// rather than weighted against the rest.
const PENALTY = { box: 1000, cross: 12, length: 1 / 40 };

function score(route, boxes, skip, placed) {
  const pts = samplesOf(route);
  const hitsBox = boxes.some((b, i) => !skip.includes(i) && pts.some((p) => inBox(p, b)));
  const crossed = placed.reduce((n, other) => n + crossings(pts, other), 0);
  return {
    pts,
    value: -(hitsBox ? PENALTY.box : 0) - crossed * PENALTY.cross - length(pts) * PENALTY.length,
  };
}

// Where an edge leaves and arrives. A forward edge uses the right and left
// faces; anything else uses top or bottom, so an arrowhead lands on a face
// rather than in the rounded corner it would clip through.
function ports(from, to, spread) {
  const forward = to.x > from.x + from.w / 2;
  if (forward) {
    return [
      { x: from.x + from.w, y: from.y + from.h / 2 + spread },
      { x: to.x, y: to.y + to.h / 2 + spread },
    ];
  }
  const above = to.y + to.h / 2 < from.y + from.h / 2;
  return [
    { x: from.x + from.w / 2 + spread, y: above ? from.y : from.y + from.h },
    { x: to.x + to.w / 2 + spread, y: above ? to.y + to.h : to.y },
  ];
}

const candidates = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.hypot(dx, dy) || 1;
  const bow = Math.min(60, dist * 0.22);
  const nx = -dy / dist;
  const ny = dx / dist;
  return [
    { kind: 'line', points: [a, b] },
    { kind: 'quad', a, b, c: { x: mx + nx * bow, y: my + ny * bow } },
    { kind: 'quad', a, b, c: { x: mx - nx * bow, y: my - ny * bow } },
    { kind: 'elbow', points: [a, { x: mx, y: a.y }, { x: mx, y: b.y }, b] },
    { kind: 'elbow', points: [a, { x: a.x, y: my }, { x: b.x, y: my }, b] },
  ];
};

const pathOf = (route) => {
  if (route.kind === 'quad') return `M${route.a.x},${route.a.y} Q${route.c.x},${route.c.y} ${route.b.x},${route.b.y}`;
  return route.points.map((p, i) => `${i ? 'L' : 'M'}${p.x},${p.y}`).join(' ');
};

/**
 * A path for every edge, each chosen against what is already drawn.
 *
 * Order matters and is deliberate: edges are routed in input order, and each
 * sees the ones before it. That makes the result deterministic — the same graph
 * gives the same picture every time, which is what lets a checker assert
 * anything about it at all.
 */
function routeEdges(nodes, edges, pos) {
  const idx = new Map(nodes.map((n, i) => [n.id, i]));
  const placed = [];
  const seen = new Map();
  return edges
    .filter((e) => idx.has(e.from) && idx.has(e.to))
    .map((e) => {
      const fi = idx.get(e.from);
      const ti = idx.get(e.to);
      // Two edges between the same pair are spread along the face rather than
      // drawn on top of each other.
      const key = `${fi}>${ti}`;
      const n = seen.get(key) || 0;
      seen.set(key, n + 1);
      const spread = n * PORT_SPREAD_STEP * (n % 2 ? -1 : 1);

      const [a, b] = ports(pos[fi], pos[ti], spread);
      const scored = candidates(a, b)
        .map((route) => ({ route, ...score(route, pos, [fi, ti], placed) }))
        .sort((p, q) => q.value - p.value);
      const best = scored[0];
      placed.push(best.pts);
      return { edge: e, from: fi, to: ti, d: pathOf(best.route), points: best.pts, score: best.value, a, b };
    });
}

/**
 * Where each edge's label goes, or that it cannot go anywhere.
 *
 * Candidate positions along the route, scored the same way the route itself
 * was: a label that would sit on a node box or on a label already placed is
 * refused, and among what is left the one nearest the middle of the edge wins,
 * because that is where a reader looks for it.
 *
 * When nothing scores, the label is NOT drawn. That is the contract's rule and
 * the reason it exists: s-rel drops labels above 24 edges and says nothing, so
 * a reader cannot tell a diagram with no labels from one whose labels were
 * silently discarded. Here the edge is marked as carrying one, and the
 * inspector shows it.
 *
 * `size(text)` is the caller's measurement — the browser's, once the label is
 * rendered — so nothing here estimates the width of a string.
 */
function placeLabels(routed, boxes, size) {
  const placed = [];
  return routed.map((r) => {
    const text = r.edge.label ? String(r.edge.label) : '';
    if (!text) return { ...r, label: null };
    const { w, h } = size(text);
    // Nearest the middle first: the fractions are ordered by how good a place
    // they are, so the first that fits is the best that fits.
    const spots = [0.5, 0.42, 0.58, 0.34, 0.66, 0.25, 0.75]
      .map((t) => r.points[Math.round(t * (r.points.length - 1))])
      .filter(Boolean)
      .map((p) => ({ x: p.x - w / 2, y: p.y - h / 2, w, h }));

    // EVERY box, this edge's own two included, and with CLEARANCE rather than
    // mere non-overlap. Excluding the endpoints was a mistake that only showed
    // on a real generated flow: a label is placed along an edge, so the boxes
    // it is likeliest to land on are that edge's own ends — "Discover books"
    // sat across "Friend Reads" while the rule reported it clean. And a label
    // whose edge merely meets a box reads as stuck to it, so the boxes are
    // grown for the test and a placed label keeps visible air.
    const clear = spots.filter((box) => !boxes.some((b) => overlap(box, grow(b, LABEL_CLEARANCE)))
      && !placed.some((other) => overlap(box, grow(other, LABEL_CLEARANCE))));
    const spot = clear[0] || null;
    if (spot) placed.push(spot);
    return { ...r, label: spot ? { text, ...spot } : null, labelHidden: !spot && Boolean(text) };
  });
}

const overlap = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

// The air a label keeps around anything it sits near.
const LABEL_CLEARANCE = 4;
const grow = (b, by) => ({ x: b.x - by, y: b.y - by, w: b.w + by * 2, h: b.h + by * 2 });

/** Total crossings across a routed set — the SOFT budget the contract names. */
const totalCrossings = (routed) => routed.reduce(
  (n, r, i) => n + routed.slice(i + 1).reduce((m, other) => m + crossings(r.points, other.points), 0),
  0,
);


// ── Groups ──

const GROUP_PAD = 14;

/**
 * A titled region per group, or none where one cannot be drawn honestly.
 *
 * A region is the bounding box of its members, and it is emitted ONLY when
 * that box holds nothing else. A box labelled "Book" that also encloses the
 * sign-in screen states something false, and a diagram that states something
 * false is worse than one that states less — so where the layout could not
 * bring a group together the region is dropped, silently to the eye but
 * visibly to the caller, which still has every node's group.
 *
 * A group of one gets no region either: a box around a single card adds a
 * border and no information.
 */
function groupRegions(nodes, pos) {
  const names = [...new Set(nodes.map((n) => n.group).filter(Boolean))];
  return names.map((name) => {
    const mine = nodes.map((n, i) => (n.group === name ? i : -1)).filter((i) => i >= 0);
    if (mine.length < 2) return null;
    const box = {
      x: Math.min(...mine.map((i) => pos[i].x)) - GROUP_PAD,
      y: Math.min(...mine.map((i) => pos[i].y)) - GROUP_PAD,
    };
    box.w = Math.max(...mine.map((i) => pos[i].x + pos[i].w)) + GROUP_PAD - box.x;
    box.h = Math.max(...mine.map((i) => pos[i].y + pos[i].h)) + GROUP_PAD - box.y;
    const intruder = nodes.some((n, i) => n.group !== name && overlap(box, pos[i]));
    return intruder ? null : { name, ...box };
  }).filter(Boolean);
}
return { GAP_Y, GAP_X, PAD, layout, crossings, routeEdges, placeLabels, totalCrossings, groupRegions };
})();

const __m132 = (() => {
const layout = __m131.layout;
const routeEdges = __m131.routeEdges;
const placeLabels = __m131.placeLabels;
// s-arc — an interactive architecture diagram that is right the first time.
//
// The contract is ai-studio docs/superpowers/specs/2026-08-29-arc-diagram-contract.md.
// Its point, in one line: the corrections a person would make by eye are stated
// as rules the layout cannot break, so the rounds of looking-and-fixing are not
// needed rather than merely faster.
//
// Why this is not s-rel. s-rel is a <canvas> with role="img": nothing is in the
// DOM, so there is no focusable node, no selectable label, nothing for a screen
// reader beyond one image label, and no CSS. An explorer needs all four. So the
// nodes here are real elements in the light DOM, and the geometry is measured
// off them rather than estimated from a character count.
//
// The shape:
//
//   <s-arc>
//     <div arc-surface>            scrolls; never the page (contract: wide
//       <svg arc-edges>            content scrolls inside its own container)
//         <path arc-edge>          one per edge, so one can be highlighted
//       <s-arc-nodes>              a list with an item renderer
//         <s-arc-node>             focusable, real text, CSS-positioned
//
// The edges are built imperatively and the nodes are not, which looks
// inconsistent and is not: a custom element cannot live inside <svg> (it would
// be an HTML-namespace element the SVG renderer ignores), so an item renderer
// is not available there. The nodes, which is where the accessibility and the
// text live, go through the list mixin exactly as any other repeat does.



// A node's own element. It carries its geometry as custom properties because
// the house rule is absolute: no inline styles, runtime values go through CSS
// custom properties that the stylesheet consumes.
const node = {
  tag: 's-arc-node',
  children: [
    // The bare ^field token, which is how a row's own value becomes text
    // (st-bin.js does the same). :dataSource=^label reads as though it should
    // work and does not: it resolves against the store, not the row, so the
    // label came out empty and every box collapsed to its minimum width.
    ['span', '@rg=arc-label', '^label'],
    // The second line the contract has always specified and nothing rendered.
    // A box with only a name makes every node look alike; the thing that tells
    // a reader what they are looking at is usually the detail — a route, an
    // endpoint, a table.
    // No show-if. `?^detail` reads as though it tests the row's field and does
    // not — `^` interpolation is not available in a show-if, so the token
    // tested a store key nothing writes and hid the line on every node. The
    // span is always rendered and the stylesheet hides it when it is empty,
    // which is how the rest of the framework does a conditional line.
    ['span', '@rg=arc-detail', '^detail'],
  ],
  // Focusable and named, which is the whole reason this is not a canvas.
  aria: { role: 'button', pressed: 'selected' },
  focusable: true,
  props: ['@selected', '@kind'],
  methods: {
    /**
     * Put this node where the diagram measured it should go.
     *
     * Called BY the diagram rather than reacted to here. The host owns the
     * geometry — it is the only thing that has measured every box — and a row
     * that re-derived its own position from its data would be a second place
     * for the layout to live.
     *
     * Until this runs the node has no position at all and the stylesheet keeps
     * it invisible, so a node is never painted at 0,0 and then moved. That jump
     * is small, happens on every render, and is exactly the sort of thing a
     * person ends up "fixing" by eye.
     */
    place(x, y, kind) {
      if (kind !== undefined && kind !== null && kind !== '') this.setAttribute('kind', String(kind));
      this.style.setProperty('--arc-x', `${x}px`);
      this.style.setProperty('--arc-y', `${y}px`);
      this.setAttribute('placed', '');
    },
  },
};

const nodeList = { tag: 's-arc-nodes', itemRenderer: 's-arc-node' };

// An edge's label. HTML rather than SVG <text> for the same reason the nodes
// are HTML: real text that the browser sizes, that CSS can theme, and whose box
// the collision rules can be checked against.
const edgeLabel = {
  tag: 's-arc-label',
  children: [['span', '^text']],
  methods: {
    place(x, y) {
      this.style.setProperty('--arc-x', `${x}px`);
      this.style.setProperty('--arc-y', `${y}px`);
      this.setAttribute('placed', '');
    },
  },
};
const labelList = { tag: 's-arc-labels', itemRenderer: 's-arc-label' };
const edgeRowList = { tag: 's-arc-edge-rows', itemRenderer: 's-arc-edge-row' };

const edgeRow = {
  tag: 's-arc-edge-row',
  children: [
    ['s-cn', '@l=row', '@rg=erow', '%dir=dir', [
      ['s-ch', '@sz=sm', '@rg=edir', '^dirLabel'],
      ['s-l', '@rg=eother', '^other'],
      ['s-l', '@rg=elabel', '^label'],
      // Marked when the diagram could not show this label. The chip is the
      // only place a reader learns the label exists at all.
      ['s-ch', '@sz=sm', '@c=wn', '@rg=ehidden', '?^hidden', '^hiddenNote'],
    ]],
  ],
};

const SVG_NS = 'http://www.w3.org/2000/svg';

// One marker, referenced by every edge. It inherits the stroke through
// context-stroke, so a lit edge's arrowhead lights with it rather than staying
// the colour of an unlit one.
function arrowDefs() {
  const defs = document.createElementNS(SVG_NS, 'defs');
  const marker = document.createElementNS(SVG_NS, 'marker');
  marker.setAttribute('id', 'arc-arrow');
  marker.setAttribute('viewBox', '0 0 10 10');
  marker.setAttribute('refX', '9');
  marker.setAttribute('refY', '5');
  marker.setAttribute('markerWidth', '6');
  marker.setAttribute('markerHeight', '6');
  marker.setAttribute('orient', 'auto-start-reverse');
  const head = document.createElementNS(SVG_NS, 'path');
  head.setAttribute('d', 'M0,1 L9,5 L0,9 z');
  head.setAttribute('fill', 'context-stroke');
  marker.appendChild(head);
  defs.appendChild(marker);
  return defs;
}

// Everything reachable from a node, both ways, transitively. This is the thing
// FLEX's explorer does that reads as understanding rather than decoration: one
// hop tells you what touches this, the whole path tells you what depends on it.
function reach(routed, id) {
  // Directed, and in each direction separately. An undirected flood lights
  // everything the node is connected to, which on any real graph is everything:
  // hovering a leaf lit its siblings, and a highlight that is always on tells
  // you nothing. Downstream is what this depends on; upstream is what depends
  // on it; their union is the paths THROUGH it.
  const walk = (seed, step) => {
    const seen = new Set([seed]);
    let grew = true;
    while (grew) {
      grew = false;
      routed.forEach((r) => {
        const next = step(r.edge, seen);
        if (next && !seen.has(next)) { seen.add(next); grew = true; }
      });
    }
    return seen;
  };
  const down = walk(id, (e, seen) => (seen.has(e.from) ? e.to : null));
  const up = walk(id, (e, seen) => (seen.has(e.to) ? e.from : null));
  const nodes = new Set([...down, ...up]);
  const edges = new Set();
  routed.forEach((r, i) => {
    if (nodes.has(r.edge.from) && nodes.has(r.edge.to)) edges.add(i);
  });
  return { nodes, edges };
}

const arc = {
  tag: 's-arc',
  // No `@selected` here, deliberately. A declared prop is reflected as an
  // ATTRIBUTE, and an attribute is a boolean: assigning a node id collapsed it
  // to `true`, and `true` matches no node, so the selection mark vanished on
  // the next redraw while the inspector — which reads the id — kept working.
  // The id lives in a private field and in the data, which is where the
  // contract puts it.
  props: ['@label'],
  // The diagram announces its own name. Its NODES are the focusable things, so
  // this is a group rather than an image: a reader tabbing into it is told what
  // they have entered before they meet the first node.
  aria: { role: 'group', label: 'label' },
  // The inspector's state is THIS diagram's, not the page's. Two s-arc
  // elements on one page would otherwise overwrite each other's selection
  // through shared global keys — the same mistake the node list would have
  // made if its rows went through the store.
  internalStore: true,
  dispatches: ['nodeselect', 'overflow'],
  children: [
    ['div', '@rg=arc-surface', [
      ['s-arc-nodes', ':ref=nodes'],
      ['s-arc-labels', ':ref=labels'],
    ]],
    // What the selected node is, and everything touching it — including the
    // labels the diagram could not place. That last part is the contract's
    // rule: a label with nowhere to go is REPORTED rather than dropped, so a
    // reader can tell a diagram with no labels from one that lost them.
    // ?data: — the internal store, not the page's. Without the source prefix
    // the show-if reads a global key that nothing writes, so the inspector
    // never appeared however correct its data was.
    ['s-cn', '?data:arcPick', '@rg=insp', [
      ['s-l', '@rg=insp-title', ':dk=arcPickLabel'],
      ['p', '@rg=insp-detail', ':dk=arcPickDetail'],
      ['s-arc-edge-rows', '@rg=insp-edges', ':dk=arcPickEdges'],
    ]],
  ],
  callbacks: {
    connected() { this._draw(); },
    disconnected() { this._stopObserving(); },
  },
  methods: {
    // The accessor is declared here rather than relied on: the framework
    // installs one for a list or a field, and a chart is neither, so every
    // chart in this folder declares its own (see _canvas.js). Without it
    // `el.data = …` sets a plain value, nothing is notified, and the diagram
    // sits empty next to data it is holding.
    set data(v) { this._graph = v || null; this._draw(); },
    get data() { return this._graph; },

    /**
     * Two passes, because the boxes size themselves.
     *
     * The contract asks for min/max content sizing, and the only thing that
     * knows how wide a label really is at this font is the browser. So the
     * nodes are rendered first and left unplaced (CSS keeps an unplaced node
     * out of view rather than painting it at 0,0 and moving it), then measured,
     * then positioned from those measurements.
     */
    _draw() {
      const data = this.data || {};
      const nodes = Array.isArray(data.nodes) ? data.nodes : [];
      const edges = Array.isArray(data.edges) ? data.edges : [];
      this._nodes = nodes;
      this._edges = edges;
      // Handed straight to the list rather than through a store key: a global
      // key would be shared by every s-arc on the page, so two diagrams would
      // overwrite each other's nodes.
      if (this.refs.nodes) this.refs.nodes.data = nodes.map((n) => ({ ...n }));
      if (!nodes.length) return;
      requestAnimationFrame(() => this._measureAndPlace());
    },

    _measureAndPlace() {
      const els = Array.from(this.querySelectorAll('s-arc-node'));
      if (els.length !== this._nodes.length) return;
      const sizes = els.map((el) => {
        const r = el.getBoundingClientRect();
        return { w: Math.round(r.width), h: Math.round(r.height) };
      });
      // The width this diagram may use, measured once: the columns spread into
      // it, and the overflow check below compares against the same number.
      const available = Math.round(this.getBoundingClientRect().width) || 0;
      const { pos, width, height, groups } = layout(this._nodes, this._edges, sizes, { available });
      els.forEach((el, i) => el.place(pos[i].x, pos[i].y, this._nodes[i].kind));
      // `selected` in the data is the caller saying which node is current —
      // the Studio's flow page passes the screen being edited. Without this it
      // was accepted and ignored, so the diagram showed no selection until
      // somebody clicked one.
      this._markSelected(this._picked || (this.data && this.data.selected) || '');

      const surface = this.querySelector('[rg=arc-surface]');
      surface.style.setProperty('--arc-w', `${width}px`);
      surface.style.setProperty('--arc-h', `${height}px`);

      this._paintGroups(surface, groups);
      this._routed = routeEdges(this._nodes, this._edges, pos);
      this._paintEdges(surface, width, height);
      this._placeLabels(pos);
      this._wireHover(els);

      // The contract: a diagram that cannot be drawn honestly at the size it
      // was given says so rather than overlapping or truncating. The surface
      // scrolls; the caller is told what it would have needed.
      if (available && width > available) {
        this.dispatchEvent(new CustomEvent('overflow', {
          bubbles: true, detail: { needed: width, available: Math.round(available) },
        }));
      }
    },

    /**
     * The titled regions behind everything.
     *
     * Behind: a group is context for the nodes, not a thing to click, so it
     * takes no pointer events and no place in the tab order. Its NAME is the
     * only part a reader needs, and it is a real element so a screen reader
     * meets it before the nodes it contains.
     */
    _paintGroups(surface, groups) {
      const old = surface.querySelector('[rg=arc-groups]');
      if (old) old.remove();
      if (!groups || !groups.length) return;
      const host = document.createElement('div');
      host.setAttribute('rg', 'arc-groups');
      groups.forEach((g) => {
        const box = document.createElement('div');
        box.setAttribute('rg', 'arc-group');
        box.style.setProperty('--arc-x', `${g.x}px`);
        box.style.setProperty('--arc-y', `${g.y}px`);
        box.style.setProperty('--arc-w', `${g.w}px`);
        box.style.setProperty('--arc-h', `${g.h}px`);
        const title = document.createElement('span');
        title.setAttribute('rg', 'arc-group-name');
        title.textContent = g.name;
        box.appendChild(title);
        host.appendChild(box);
      });
      surface.insertBefore(host, surface.firstChild);
    },

    // Built imperatively, unlike the nodes, and not as a shortcut: a custom
    // element inside <svg> is an HTML-namespace element the SVG renderer
    // ignores, so an item renderer is not available here.
    _paintEdges(surface, width, height) {
      const old = surface.querySelector('svg[rg=arc-edges]');
      if (old) old.remove();
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('rg', 'arc-edges');
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      svg.setAttribute('width', String(width));
      svg.setAttribute('height', String(height));
      // Decorative: the edges are described by the node list and the
      // inspector, so a screen reader is not read a wall of paths.
      svg.setAttribute('aria-hidden', 'true');
      // An arrowhead per edge. Without one the diagram draws a relationship
      // and hides its direction, which for a flow — where the whole question
      // is what leads to what — loses the point of drawing it. s-rel had them;
      // this did not, which was a regression dressed as a cleaner picture.
      svg.appendChild(arrowDefs());
      this._routed.forEach((r, i) => {
        const path = document.createElementNS(SVG_NS, 'path');
        path.setAttribute('d', r.d);
        path.setAttribute('rg', 'arc-edge');
        path.setAttribute('marker-end', 'url(#arc-arrow)');
        path.setAttribute('data-i', String(i));
        if (r.edge.kind) path.setAttribute('kind', String(r.edge.kind));
        svg.appendChild(path);
      });
      surface.insertBefore(svg, surface.firstChild);
      this._svg = svg;
    },

    /**
     * Labels, measured then placed — the same two passes as the nodes.
     *
     * They are rendered first with no position (the stylesheet keeps an
     * unplaced label invisible), measured, then placed by the scoring in
     * _arc-layout.js. A label with nowhere to go is not drawn and is marked
     * hidden, which the inspector reports.
     */
    _placeLabels(pos) {
      const list = this.refs.labels;
      if (!list) return;
      const withLabels = this._routed.filter((r) => r.edge.label);
      list.data = withLabels.map((r, i) => ({ i, text: String(r.edge.label) }));
      const els = Array.from(this.querySelectorAll('s-arc-label'));
      const size = (text) => {
        const el = els[withLabels.findIndex((r) => String(r.edge.label) === text)] || els[0];
        if (!el) return { w: text.length * 7, h: 16 };
        const rect = el.getBoundingClientRect();
        return { w: Math.round(rect.width), h: Math.round(rect.height) };
      };
      this._routed = placeLabels(this._routed, pos, size);
      const placedRoutes = this._routed.filter((r) => r.edge.label);
      placedRoutes.forEach((r, i) => {
        const el = els[i];
        if (!el) return;
        if (r.label) el.place(r.label.x, r.label.y);
        else el.remove();
      });
    },

    _wireHover(els) {
      els.forEach((el, i) => {
        if (el._arcWired) return;
        el._arcWired = true;
        el.addEventListener('pointerenter', () => this._highlight(this._nodes[i].id));
        el.addEventListener('focus', () => this._highlight(this._nodes[i].id));
        el.addEventListener('pointerleave', () => this._highlight(''));
        el.addEventListener('blur', () => this._highlight(''));
        el.addEventListener('click', () => this._select(this._nodes[i]));
      });
    },

    // State attributes plus a class, never written styles: the stylesheet owns
    // what "muted" looks like.
    _highlight(id) {
      const on = Boolean(id);
      this.toggleAttribute('lit', on);
      const lit = on ? reach(this._routed || [], id) : { nodes: new Set(), edges: new Set() };
      Array.from(this.querySelectorAll('s-arc-node')).forEach((el, i) => {
        el.toggleAttribute('lit', on && lit.nodes.has(this._nodes[i].id));
      });
      if (!this._svg) return;
      // path[rg=arc-edge], not every path: the arrowhead marker lives in a
      // <defs> and contains one too, so an unscoped query put it at index 0
      // and shifted every edge's lighting by one.
      Array.from(this._svg.querySelectorAll('path[rg=arc-edge]')).forEach((p, i) => {
        p.toggleAttribute('lit', on && lit.edges.has(i));
      });
    },

    // The selected node carries `selected`, which its own aria config reflects
    // as aria-pressed, so the selection is announced and not merely drawn.
    _markSelected(id) {
      Array.from(this.querySelectorAll('s-arc-node')).forEach((el, i) => {
        el.toggleAttribute('selected', Boolean(id) && this._nodes[i] && this._nodes[i].id === id);
      });
    },

    _select(n) {
      this._picked = n.id;
      this._markSelected(n.id);
      const routed = this._routed || [];
      const touching = routed.filter((r) => r.edge.from === n.id || r.edge.to === n.id);
      const nameOf = (id) => {
        const found = this._nodes.filter((x) => x.id === id)[0];
        return found ? String(found.label ?? found.id) : id;
      };
      // The inspector's rows. `hidden` is the point of it: an edge whose label
      // the diagram could not place is listed here with the label, so it is
      // reported rather than lost.
      this._internalData.setValues({
        arcPick: n.id,
        arcPickLabel: String(n.label ?? n.id),
        arcPickDetail: String(n.detail ?? ''),
        arcPickEdges: touching.map((r) => ({
          dir: r.edge.from === n.id ? 'out' : 'in',
          dirLabel: r.edge.from === n.id ? '→' : '←',
          other: nameOf(r.edge.from === n.id ? r.edge.to : r.edge.from),
          label: String(r.edge.label ?? ''),
          hidden: Boolean(r.labelHidden),
          hiddenNote: r.labelHidden ? 'not shown on the diagram' : '',
        })),
      });
      this.dispatchEvent(new CustomEvent('nodeselect', {
        bubbles: true,
        detail: { node: n, edges: touching.map((r) => r.edge), hiddenLabels: touching.filter((r) => r.labelHidden).length },
      }));
    },

    _stopObserving() { this._routed = null; this._svg = null; },
  },
  css: `
s-arc { display: block; }
s-arc [rg=arc-surface] { position: relative; overflow: auto; width: 100%; min-height: var(--arc-h, 0); }
s-arc [rg=arc-groups] { position: absolute; inset-block-start: 0; inset-inline-start: 0; pointer-events: none; }
s-arc [rg=arc-group] { position: absolute; inset-block-start: var(--arc-y); inset-inline-start: var(--arc-x); width: var(--arc-w); height: var(--arc-h); border: 1px dashed var(--c-border); border-radius: var(--radius-field); background: var(--c-surface-muted, transparent); }
s-arc [rg=arc-group-name] { position: absolute; inset-block-start: -9px; inset-inline-start: 10px; padding: 0 6px; background: var(--c-bg); font-size: 10px; font-weight: 600; letter-spacing: 0.04em; color: var(--c-text-secondary); }
/* No text-transform: the name is the caller's, and upper-casing it turned
   ReadingListItem into READINGLISTITEM while the same entity is written
   ReadingListItem everywhere else on the page. A diagram that renames things
   is a diagram you have to translate. */
s-arc svg[rg=arc-edges] { position: absolute; inset-block-start: 0; inset-inline-start: 0; pointer-events: none; }
s-arc svg[rg=arc-edges] path { fill: none; stroke: var(--c-border); stroke-width: 1.25; }
s-arc[lit] svg[rg=arc-edges] path { opacity: 0.2; }
s-arc[lit] svg[rg=arc-edges] path[lit] { opacity: 1; stroke: var(--c-primary); stroke-width: 1.75; }
s-arc s-arc-nodes { display: block; position: relative; width: var(--arc-w, 100%); height: var(--arc-h, auto); }
s-arc s-arc-node { position: absolute; visibility: hidden; box-sizing: border-box;
  min-width: var(--arc-node-min, 96px); max-width: var(--arc-node-max, 220px);
  padding: 8px 12px; border: 1px solid var(--c-border); border-radius: var(--radius-field);
  background: var(--c-surface); font-size: 13px; line-height: 1.35; cursor: pointer; }
s-arc s-arc-node[placed] { visibility: visible; inset-block-start: var(--arc-y); inset-inline-start: var(--arc-x); }
/* The NAME may wrap, to two lines, which is what the contract says and what
   keeps a long screen name readable rather than cut. The DETAIL is the line
   that truncates: a route is recognisable from its start, a name is not. */
s-arc s-arc-node span[rg=arc-label] { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; font-weight: 600; }
s-arc s-arc-node span[rg=arc-detail]:empty { display: none; }
s-arc s-arc-node span[rg=arc-detail] { display: block; margin-top: 2px; font-size: 11px; color: var(--c-text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* The kind was written onto every node and read by nothing, so a form, a
   collection and an auth screen were the same white box. A stripe rather than
   a wash: it says which sort of thing this is without turning the diagram into
   a colour puzzle, and it survives a theme because every colour is a token. */
s-arc s-arc-node { border-inline-start: 3px solid var(--c-border); }
s-arc s-arc-node[kind=collection] { border-inline-start-color: var(--c-primary); }
s-arc s-arc-node[kind=item] { border-inline-start-color: var(--c-info, var(--c-primary)); }
s-arc s-arc-node[kind=form] { border-inline-start-color: var(--c-success, #2e7d32); }
s-arc s-arc-node[kind=auth] { border-inline-start-color: var(--c-warning, #b26a00); }
s-arc s-arc-node[kind=marketing] { border-inline-start-color: var(--c-accent, #7c4dff); }
s-arc[lit] s-arc-node { opacity: 0.35; }
s-arc[lit] s-arc-node[lit] { opacity: 1; border-color: var(--c-primary); }
s-arc s-arc-node:focus-visible { outline: 2px solid var(--c-primary); outline-offset: 2px; }
s-arc s-arc-labels { display: block; position: absolute; inset-block-start: 0; inset-inline-start: 0; pointer-events: none; }
s-arc s-arc-label { position: absolute; visibility: hidden; padding: 1px 6px; border-radius: 10px; background: var(--c-surface); color: var(--c-text-secondary); font-size: 11px; line-height: 1.3; white-space: nowrap; }
s-arc s-arc-label[placed] { visibility: visible; inset-block-start: var(--arc-y); inset-inline-start: var(--arc-x); }
s-arc[lit] s-arc-label { opacity: 0.3; }
s-arc s-cn[rg=insp] { margin-top: 12px; padding: 12px 14px; border: 1px solid var(--c-border); border-radius: var(--radius-field); background: var(--c-surface); }
s-arc label[is=s-l][rg=insp-title] { display: block; font-weight: 600; margin-bottom: 4px; }
s-arc p[rg=insp-detail] { margin: 0 0 8px; font-size: 13px; color: var(--c-text-secondary); }
s-arc-edge-row s-cn[rg=erow] { --layout-gap: 6px; align-items: center; flex-wrap: wrap; padding: 3px 0; font-size: 12px; }
s-arc-edge-row label[is=s-l][rg=eother] { font-weight: 600; }
s-arc-edge-row label[is=s-l][rg=elabel] { color: var(--c-text-secondary); }
`,
};

// Default-exported as an ARRAY, the way input/fields.js does it: the catalog
// extractor reads `mod.default`, so a named export would leave every s-arc tag
// out of the vocabulary the lint gate checks against and out of the docs.
const arcDefs = [node, nodeList, edgeLabel, labelList, edgeRow, edgeRowList, arc];
const __default = arcDefs;
return { __default };
})();

const __m133 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
// Inline trend line, no axes, no labels (the catalog's "sparkline" display).
// data: [number, ...] or { points: [number | { value }], color?, height? (28) }


const norm = (data) => {
  const raw = Array.isArray(data) ? data : (data.points || []);
  return raw.map((p) => (typeof p === 'number' ? p : (p && p.value) || 0));
};

const __default = canvasChart({
  tags: ['swc-sparkline', 's-spk'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data) => ({ h: (!Array.isArray(data) && data.height) || 28, values: norm(data) }),
  draw(ctx, g, k) {
    const v = g.l.values;
    if (v.length < 2) return;
    const hue = (!Array.isArray(g.data) && g.data.color) || SERIES[0];
    const min = Math.min(...v);
    const span = Math.max(...v) - min || 1;
    const px = (i) => 1 + (i / (v.length - 1)) * (g.w - 2);
    const py = (x) => 2 + (1 - (x - min) / span) * (g.h - 4);

    ctx.beginPath();
    v.forEach((x, i) => (i ? ctx.lineTo(px(i), py(x)) : ctx.moveTo(px(0), py(x))));
    ctx.lineTo(px(v.length - 1), g.h); ctx.lineTo(px(0), g.h); ctx.closePath();
    ctx.fillStyle = hue + '14'; ctx.fill();

    ctx.beginPath();
    v.forEach((x, i) => (i ? ctx.lineTo(px(i), py(x)) : ctx.moveTo(px(0), py(x))));
    ctx.strokeStyle = hue; ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.stroke();
  },
});
return { __default };
})();

const __m134 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
const fmt = __m120.fmt;
const drawGrid = __m120.drawGrid;
const drawLegend = __m120.drawLegend;
const niceMax = __m120.niceMax;
// Stacked categorical composition (the catalog's "stacked-bar" display).
// Segments wear fixed-order SERIES hues with a 2px surface gap between fills;
// the legend is always present (composition is at least two series).
// data: { categories: [label, ...],
//         series: [{ name, color?, values: [number per category] }],
//         height? (220) }


const PAD = { top: 12, right: 12, bottom: 22, left: 40 };
const hueOf = (s, i) => s.color || SERIES[i % SERIES.length];

const __default = canvasChart({
  tags: ['s-sted-bar', 's-sbr'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data) => ({
    h: (data.height || 220) + 18,
    categories: data.categories || [],
    series: (data.series || []).filter((s) => (s.values || []).length),
  }),
  draw(ctx, g, k) {
    const { categories, series } = g.l;
    if (!categories.length || !series.length) return;
    drawLegend(ctx, series.map((s, i) => ({ name: s.name || `Series ${i + 1}`, color: hueOf(s, i) })), PAD.left, 2, k);

    const top = PAD.top + 18;
    const ih = g.h - top - PAD.bottom;
    const iw = g.w - PAD.left - PAD.right;
    const totals = categories.map((_, c) => series.reduce((sum, s) => sum + (s.values[c] || 0), 0));
    const max = niceMax(Math.max(...totals));
    drawGrid(ctx, { left: PAD.left, right: PAD.right, top, ih, w: g.w }, max, k);

    const slot = iw / categories.length;
    const bw = Math.min(32, slot - 2);
    categories.forEach((label, c) => {
      const x = PAD.left + c * slot + (slot - bw) / 2;
      let yTop = top + ih;
      series
        .map((s, si) => ({ hue: hueOf(s, si), v: s.values[c] || 0 }))
        .filter((e) => e.v > 0)
        .forEach((e, fi) => {
          const sh = (e.v / max) * ih;
          ctx.fillStyle = c === g.hover ? e.hue : e.hue + 'e6';
          // 2px surface gap between stacked fills, taken from the segment top.
          ctx.fillRect(x, yTop - sh + (fi ? 2 : 0), bw, sh - (fi ? 2 : 0));
          yTop -= sh;
        });
      ctx.fillStyle = k.muted;
      ctx.font = '11px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(String(label ?? ''), x + bw / 2, g.h - 6);
    });
  },
  hitTest(g, mx) {
    const { categories, series } = g.l;
    if (!categories.length || !series.length) return null;
    const iw = g.w - PAD.left - PAD.right;
    const slot = iw / categories.length;
    const c = Math.floor((mx - PAD.left) / slot);
    if (c < 0 || c >= categories.length) return null;
    const parts = series.map((s, si) => `${s.name || `Series ${si + 1}`}: ${fmt(s.values[c] || 0)}`);
    return {
      i: c,
      text: `${categories[c]} ${parts.join(' · ')}`,
      x: PAD.left + c * slot + slot / 2,
      y: PAD.top + 18,
    };
  },
});
return { __default };
})();

const __m135 = (() => {
const canvasChart = __m120.canvasChart;
const SERIES = __m120.SERIES;
const fmt = __m120.fmt;
const FONT = __m120.FONT;
const truncate = __m120.truncate;
// Hierarchical area tiles (the catalog's "treemap" display), squarified.
// Area encodes magnitude, so color is the SEQUENTIAL job: one hue stepped by
// alpha with value, never a hue per tile. 2px surface gaps between fills;
// labels only on tiles that fit them, the tooltip reads the rest.
// data: { items: [{ label, value }], color?, height? (240) }


// Squarify: peel strips off the short side, keeping tile aspect near 1.
function tilesOf(items, w, h) {
  const sorted = items.filter((i) => i.value > 0).sort((a, b) => b.value - a.value);
  const total = sorted.reduce((s, i) => s + i.value, 0);
  if (!total) return [];
  const scale = (w * h) / total;
  const rects = [];
  let rest = sorted;
  let rx = 0; let ry = 0; let rw = w; let rh = h;

  const worst = (row, side) => {
    const sum = row.reduce((s, i) => s + i.value * scale, 0);
    const strip = sum / side;
    return Math.max(...row.map((i) => {
      const len = (i.value * scale) / strip;
      return Math.max(strip / len, len / strip);
    }));
  };

  while (rest.length) {
    const side = Math.min(rw, rh);
    let row = [rest[0]];
    rest = rest.slice(1);
    while (rest.length && worst([...row, rest[0]], side) <= worst(row, side)) {
      row = [...row, rest[0]];
      rest = rest.slice(1);
    }
    const strip = row.reduce((s, i) => s + i.value * scale, 0) / side;
    let off = 0;
    const vertical = rw >= rh;
    row.forEach((i) => {
      const len = (i.value * scale) / strip;
      rects.push(vertical
        ? { item: i, x: rx, y: ry + off, w: strip, h: len }
        : { item: i, x: rx + off, y: ry, w: len, h: strip });
      off += len;
    });
    if (vertical) { rx += strip; rw -= strip; } else { ry += strip; rh -= strip; }
  }
  return rects;
}

const __default = canvasChart({
  tags: ['s-treemap', 's-tmp'],
  // A canvas has no text alternative — the chart announces as a labelled
  // image. Authors set @label (or bind @aria-label) with what the chart shows.
  aria: { role: 'img', label: 'label' },
  props: ['@label'],
  layout: (data, w) => {
    const h = data.height || 240;
    return { h, tiles: tilesOf(data.items || [], w, h) };
  },
  draw(ctx, g, k) {
    const { tiles } = g.l;
    if (!tiles.length) return;
    const hue = g.data.color || SERIES[0];
    const peak = tiles[0].item.value;
    tiles.forEach((t, i) => {
      const depth = 0.25 + 0.65 * (t.item.value / peak);
      ctx.globalAlpha = i === g.hover ? Math.min(1, depth + 0.15) : depth;
      ctx.fillStyle = hue;
      ctx.fillRect(t.x + 1, t.y + 1, Math.max(0, t.w - 2), Math.max(0, t.h - 2));
      ctx.globalAlpha = 1;
    });
    // Labels in text ink, only where the tile fits label and value.
    ctx.font = FONT;
    ctx.textAlign = 'left';
    tiles
      .filter((t) => t.w >= 56 && t.h >= 32)
      .forEach((t) => {
        const label = truncate(ctx, String(t.item.label ?? ''), t.w - 14);
        ctx.fillStyle = k.text;
        ctx.fillText(label, t.x + 7, t.y + 15);
        ctx.fillStyle = k.muted;
        ctx.fillText(fmt(t.item.value), t.x + 7, t.y + 28);
      });
  },
  hitTest(g, mx, my) {
    const i = g.l.tiles.findIndex((t) => mx >= t.x && mx < t.x + t.w && my >= t.y && my < t.y + t.h);
    if (i < 0) return null;
    const t = g.l.tiles[i];
    return { i, text: `${t.item.label}: ${fmt(t.item.value)}`, x: t.x + t.w / 2, y: t.y + 8 };
  },
});
return { __default };
})();

const __m136 = (() => {
// The panel of an accordion: the content region of an s-dt, named by @value= so an accordion carrying @seg= can open it from the address.
// The value makes a panel selectable by id the way a tab is (F-811): the
// accordion opens the panel the address names and writes the open one back.
const __default = { tags: ['s-acc-d', 's-acc-d'], props: ['@value'], aria: { role: 'region' } };
return { __default };
})();

const __m137 = (() => {
const __default = {
    // s-sum/swc-summary are aliases: summary.js was a byte-identical def, and
    // one implementation per pattern wins — s-dt and s-acc both stamp THIS def.
    tags: ['s-acc-s', 's-acc-s', 's-sum', 's-sum'],
    // The disclosure trigger: role=button + the click emit derive focusability
    // (tabindex + Enter/Space → click → 'toggle'), which subsumed the ad-hoc
    // keydown that used to live in s-dt. aria-expanded is reflected by the
    // owning s-dt, whose `open` attribute is the state's home.
    aria: { role: 'button' },
    emits: { click: 'toggle' }
};
return { __default };
})();

const __m138 = (() => {
// Breadcrumb trail: a data-driven list. Each item in the data array is
// { label, url } and renders as one crumb (an anchor). The last crumb is made
// non-interactive via CSS (pointer-events: none on :last-child).

// s-bc-i — item renderer: a single crumb rendered as a link.
// ^label binds item.label to the anchor text; %href=url binds item.url to href.
const breadcrumbItem = {
    tags: ['swc-breadcrumb-item', 's-bc-i'],
    children: [
        ['a', '^label', '%href=url'],
    ],
};

// s-bc — breadcrumb container. Renders one s-bc-i per { label, url } in its data
// array. Bind an array via $storeKey / :dataSource=key, e.g.
//   ['s-bc', ':dataSource=t.comp.breadcrumbs.basicTrail']
const breadcrumbs = {
    tags: ['s-bc', 's-bc'],
    aria: { role: 'navigation' },
    itemRenderer: 's-bc-i',
};

const __default = [breadcrumbItem, breadcrumbs];
return { __default };
})();

const __m139 = (() => {
const __default = {
    tags: ['s-b', 's-b'],
    // Customized built-in: extend the native <button> so it inherits keyboard
    // focus, tab order, Enter/Space activation, the button role, and disabled
    // semantics for free — the same pattern the inputs use (HTMLInputElement).
    baseElement: 'HTMLButtonElement',
    props: ['@icon', '@iconPos', '@align'],
    children: [
        ['s-ic', ':ref=iconEl', '?prop:icon'],
    ],
    callbacks: {
        connected() {
            // Default to type=button so a button inside a form does not trigger
            // native submission — the form component owns submit via delegation.
            if (!this.hasAttribute('type')) this.setAttribute('type', 'button');
            if (this.icon) this._applyIcon();
        }
    },
    methods: {
        _applyIcon() {
            if (this.refs.iconEl && this.icon) {
                this.refs.iconEl.n = this.icon;
            }
        },
        onIconChange() { this._applyIcon(); },
    },
};
return { __default };
})();

const __m140 = (() => {
const __default = {
    tags: ['s-car-i', 's-car-i'],
    props: ['@selected'],
    aria: { role: 'tab', selected: 'selected' },
    emits: { click: 'select' }
};
return { __default };
})();

const __m141 = (() => {
const __default = {
    tags: ['s-ch-x', 's-ch-x'],
    // A remove affordance: announced as a button, keyboard-operable via the
    // focusable derivation. The English default localises when an instance
    // binds '@aria-label=$t.a11y.remove'.
    aria: { role: 'button', label: '=Remove' },
    emits: { click: 'close' }
};
return { __default };
})();

const __m142 = (() => {
const __default = {
    tags: ['s-ch', 's-ch'],
    props: ['@selected', '@clickable', '@d'],
    // A chip is a button in SOME uses and a word in others, so the role is
    // conditional. A filter chip (@clickable) or one that carries selection
    // (@selected) is announced as a toggle button and takes a tab stop; a
    // status chip — "Not saved", "required", a kind label — is text, and is
    // announced as text.
    //
    // It used to be a button always. That put static text in the tab order and
    // told a screen reader it was an unpressed toggle, which was not merely
    // noisy: clicking a chip does not change `selected` (the click EMITS, it
    // does not toggle), so the promised control did nothing when operated. The
    // people who could not check that against the screen were exactly the ones
    // being told.
    //
    // Inside a chip-list the container's itemRole (option) wins either way,
    // because item stamping runs before this def's role.
    aria: { role: 'button', roleWhen: ['clickable', 'selected'], pressed: 'selected' },
    emits: { click: 'selected' }
};
return { __default };
})();

const __m143 = (() => {
// Divider is now a simple visual separator only.
// For resizable split panels, use s-spl (splitter).
const __default = { tags: ['s-dv', 's-dv'] };
return { __default };
})();

const __m144 = (() => {
const ITEM_STATE = __m17.ITEM_STATE;
const __default = {
    tags: ['s-di', 's-di'],
    props: ['@selected'],
    // menuitem is the DEFAULT contract — an action in a split button's menu.
    // A dropdown holds a current value instead, and says so by declaring
    // itemRole: 'menuitemradio'; _syncAria below is what lets one tag answer
    // to both without the container reaching in to rewrite roles.
    aria: { role: 'menuitem', disabled: 'disabled' },
    emits: { click: 'select' },
    methods: {
        /**
         * Adopt the contract the owning container declares.
         *
         * Called by the selection mixin with the container's itemRole. Plain
         * `menuitem` has no selected state in ARIA, so a dropdown whose items
         * stayed menuitems marked its current value with nothing but the bare
         * `selected` attribute CSS reads — the theme picker announced "Light,
         * Dark, Signal" with no way to hear which was active.
         */
        _syncAria(itemRole) {
            // Remember what the container said. Later syncs come from this
            // item's own selected-change, which has no container to ask —
            // walking up to find one lands on the floating panel (role=menu,
            // no itemRole of its own) and would quietly demote the item back
            // to a plain menuitem on the first click.
            if (itemRole) this._containerItemRole = itemRole;
            const role = this._containerItemRole || this.getAttribute('role') || 'menuitem';
            this.setAttribute('role', role);
            const stateAttr = ITEM_STATE[role];
            if (!stateAttr) return;
            // Present on EVERY item, not just the chosen one: absent means
            // "not selectable" to a reader, which is a different statement
            // from "selectable and not currently selected".
            this.setAttribute(stateAttr, this.hasAttribute('selected') ? 'true' : 'false');
        },
        onSelectedChange() { this._syncAria(); },
    },
};
return { __default };
})();

const __m145 = (() => {
const __default = {
    tags: ['s-fab', 's-fab'],
    props: ['@icon', '@disabled'],
    // A floating ACTION button is a button: announced role, pressed state via
    // click emit, and derived focusability (role + emit → Enter/Space work).
    aria: { role: 'button', disabled: 'disabled' },
    emits: { click: 'press' },
    children: [
        // Unconditional: a ?prop-conditional child never stamps when the
        // attribute parses after render, which left the FAB permanently
        // empty. An s-ic with no name renders nothing anyway.
        ['s-ic', ':ref=iconEl'],
    ],
    callbacks: {
        connected() {
            if (this.icon) this._applyIcon();
        }
    },
    methods: {
        _applyIcon() {
            if (this.refs.iconEl && this.icon) {
                this.refs.iconEl.n = this.icon;
            }
        },
        onIconChange() { this._applyIcon(); },
    },
};
return { __default };
})();

const __m146 = (() => {
// Icons from Lucide (https://lucide.dev) — ISC License
// Copyright (c) for portions of Lucide are held by Cole Bemis 2013-2022
// as part of Feather Icons. All other copyright for Lucide are held by
// Lucide Contributors 2022-present.
// 1694 icons + short aliases for common icons

const icons = {
  'arrow-down': '<path d="M12 5v14" /><path d="m19 12-7 7-7-7" />',
  'arrow-down-up': '<path d="m3 16 4 4 4-4" /><path d="M7 20V4" /><path d="m21 8-4-4-4 4" /><path d="M17 4v16" />',
  'arrow-right': '<path d="M5 12h14" /><path d="m12 5 7 7-7 7" />',
  'arrow-up-right': '<path d="M7 7h10v10" /><path d="M7 17 17 7" />',
  'ban': '<circle cx="12" cy="12" r="10" /><path d="M4.929 4.929 19.07 19.071" />',
  'boxes': '<path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z" /><path d="m7 16.5-4.74-2.85" /><path d="m7 16.5 5-3" /><path d="M7 16.5v5.17" /><path d="M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z" /><path d="m17 16.5-5-3" /><path d="m17 16.5 4.74-2.85" /><path d="M17 16.5v5.17" /><path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z" /><path d="M12 8 7.26 5.15" /><path d="m12 8 4.74-2.85" /><path d="M12 13.5V8" />',
  'brain': '<path d="M12 18V5" /><path d="M15 13a4.17 4.17 0 0 1-3-4 4.17 4.17 0 0 1-3 4" /><path d="M17.598 6.5A3 3 0 1 0 12 5a3 3 0 1 0-5.598 1.5" /><path d="M17.997 5.125a4 4 0 0 1 2.526 5.77" /><path d="M18 18a4 4 0 0 0 2-7.464" /><path d="M19.967 17.483A4 4 0 1 1 12 18a4 4 0 1 1-7.967-.517" /><path d="M6 18a4 4 0 0 1-2-7.464" /><path d="M6.003 5.125a4 4 0 0 0-2.526 5.77" />',
  'chart-no-axes-column': '<path d="M5 21v-6" /><path d="M12 21V3" /><path d="M19 21V9" />',
  'check-check': '<path d="M18 6 7 17l-5-5" /><path d="m22 10-7.5 7.5L13 16" />',
  'chevron-down': '<path d="m6 9 6 6 6-6" />',
  'chevron-left': '<path d="m15 18-6-6 6-6" />',
  'chevron-right': '<path d="m9 18 6-6-6-6" />',
  'circle': '<circle cx="12" cy="12" r="10" />',
  'circle-check': '<circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />',
  'circle-x': '<circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" />',
  'clipboard-check': '<rect width="8" height="4" x="8" y="2" rx="1" ry="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="m9 14 2 2 4-4" />',
  'copy': '<rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />',
  'database': '<ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5V19A9 3 0 0 0 21 19V5" /><path d="M3 12A9 3 0 0 0 21 12" />',
  'ellipsis': '<circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" />',
  'ellipsis-vertical': '<circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" />',
  'external-link': '<path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />',
  'eye-off': '<path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" /><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" /><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" /><path d="m2 2 20 20" />',
  'file-check': '<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /><path d="M14 2v5a1 1 0 0 0 1 1h5" /><path d="m9 15 2 2 4-4" />',
  'file-diff': '<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /><path d="M9 10h6" /><path d="M12 13V7" /><path d="M9 17h6" />',
  'file-question-mark': '<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /><path d="M12 17h.01" /><path d="M9.1 9a3 3 0 0 1 5.82 1c0 2-3 3-3 3" />',
  'folder-plus': '<path d="M12 10v6" /><path d="M9 13h6" /><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />',
  'gavel': '<path d="m14 13-8.381 8.38a1 1 0 0 1-3.001-3l8.384-8.381" /><path d="m16 16 6-6" /><path d="m21.5 10.5-8-8" /><path d="m8 8 6-6" /><path d="m8.5 7.5 8 8" />',
  'git-compare': '<circle cx="18" cy="18" r="3" /><circle cx="6" cy="6" r="3" /><path d="M13 6h3a2 2 0 0 1 2 2v7" /><path d="M11 18H8a2 2 0 0 1-2-2V9" />',
  'globe-lock': '<path d="M15.686 15A14.5 14.5 0 0 1 12 22a14.5 14.5 0 0 1 0-20 10 10 0 1 0 9.542 13" /><path d="M2 12h8.5" /><path d="M20 6V4a2 2 0 1 0-4 0v2" /><rect width="8" height="5" x="14" y="6" rx="1" />',
  'image-off': '<line x1="2" x2="22" y1="2" y2="22" /><path d="M10.41 10.41a2 2 0 1 1-2.83-2.83" /><line x1="13.5" x2="6" y1="13.5" y2="21" /><line x1="18" x2="21" y1="12" y2="15" /><path d="M3.59 3.59A1.99 1.99 0 0 0 3 5v14a2 2 0 0 0 2 2h14c.55 0 1.052-.22 1.41-.59" /><path d="M21 15V5a2 2 0 0 0-2-2H9" />',
  'info': '<circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />',
  'key': '<path d="m15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4" /><path d="m21 2-9.6 9.6" /><circle cx="7.5" cy="15.5" r="5.5" />',
  'landmark': '<path d="M10 18v-7" /><path d="M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z" /><path d="M14 18v-7" /><path d="M18 18v-7" /><path d="M3 22h18" /><path d="M6 18v-7" />',
  'languages': '<path d="m5 8 6 6" /><path d="m4 14 6-6 2-3" /><path d="M2 5h12" /><path d="M7 2h1" /><path d="m22 22-5-10-5 10" /><path d="M14 18h6" />',
  'layout-dashboard': '<rect width="7" height="9" x="3" y="3" rx="1" /><rect width="7" height="5" x="14" y="3" rx="1" /><rect width="7" height="9" x="14" y="12" rx="1" /><rect width="7" height="5" x="3" y="16" rx="1" />',
  'loader-circle': '<path d="M21 12a9 9 0 1 1-6.219-8.56" />',
  'mail': '<path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" /><rect x="2" y="4" width="20" height="16" rx="2" />',
  'maximize': '<path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />',
  'menu': '<path d="M4 5h16" /><path d="M4 12h16" /><path d="M4 19h16" />',
  'message-circle': '<path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />',
  'minimize': '<path d="M8 3v3a2 2 0 0 1-2 2H3" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" /><path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" />',
  'minus': '<path d="M5 12h14" />',
  'moon': '<path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401" />',
  'plug': '<path d="M12 22v-5" /><path d="M15 8V2" /><path d="M17 8a1 1 0 0 1 1 1v4a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1z" /><path d="M9 8V2" />',
  'plus': '<path d="M5 12h14" /><path d="M12 5v14" />',
  'rotate-ccw': '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" />',
  'ruler': '<path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z" /><path d="m14.5 12.5 2-2" /><path d="m11.5 9.5 2-2" /><path d="m8.5 6.5 2-2" /><path d="m17.5 15.5 2-2" />',
  'scale': '<path d="M12 3v18" /><path d="m19 8 3 8a5 5 0 0 1-6 0zV7" /><path d="M3 7h1a17 17 0 0 0 8-2 17 17 0 0 0 8 2h1" /><path d="m5 8 3 8a5 5 0 0 1-6 0zV7" /><path d="M7 21h10" />',
  'scan-search': '<path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><circle cx="12" cy="12" r="3" /><path d="m16 16-1.9-1.9" />',
  'search': '<path d="m21 21-4.34-4.34" /><circle cx="11" cy="11" r="8" />',
  'search-check': '<path d="m8 11 2 2 4-4" /><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />',
  'server': '<rect width="20" height="8" x="2" y="2" rx="2" ry="2" /><rect width="20" height="8" x="2" y="14" rx="2" ry="2" /><line x1="6" x2="6.01" y1="6" y2="6" /><line x1="6" x2="6.01" y1="18" y2="18" />',
  'settings-2': '<path d="M14 17H5" /><path d="M19 7h-9" /><circle cx="17" cy="17" r="3" /><circle cx="7" cy="7" r="3" />',
  'shield': '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />',
  'shield-check': '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" />',
  'shopping-bag': '<path d="M16 10a4 4 0 0 1-8 0" /><path d="M3.103 6.034h17.794" /><path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z" />',
  'split': '<path d="M16 3h5v5" /><path d="M8 3H3v5" /><path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" /><path d="m15 9 6-6" />',
  'square': '<rect width="18" height="18" x="3" y="3" rx="2" />',
  'square-check': '<rect width="18" height="18" x="3" y="3" rx="2" /><path d="m9 12 2 2 4-4" />',
  'sun': '<circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" />',
  'triangle-alert': '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /><path d="M12 9v4" /><path d="M12 17h.01" />',
  'user-check': '<path d="m16 11 2 2 4-4" /><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />',
  'users': '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><path d="M16 3.128a4 4 0 0 1 0 7.744" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><circle cx="9" cy="7" r="4" />',
  'workflow': '<rect width="8" height="8" x="3" y="3" rx="2" /><path d="M7 11v4a2 2 0 0 0 2 2h4" /><rect width="8" height="8" x="13" y="13" rx="2" />',
  'x': '<path d="M18 6 6 18" /><path d="m6 6 12 12" />',

  // Short aliases
  'pl': '<path d="M5 12h14" /><path d="M12 5v14" />',
  'sd': '<path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" />',
  'cd': '<path d="m6 9 6 6 6-6" />',
  'cr': '<path d="m9 18 6-6-6-6" />',
  'ck': '<circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />',
  'cal': '<path d="M8 2v4" /><path d="M16 2v4" /><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18" />',
};
return { icons };
})();

const __m147 = (() => {
// The icon glyph is a CSS mask, not markup — `s-ic` has no children, so an
// icon is invisible until a rule for its name exists. The client generates
// those rules on first use; the SERVER has to emit the same ones for the icons
// in the page it just rendered, or every icon in first paint is a blank box.
//
// Both callers come through here so the two can never drift: one function
// builds the rule, one scanner finds the names a rendered document needs.

// `filled` paints the glyph solid instead of stroking its outline. The set is
// drawn as outlines, which a mask renders as a hollow shape — fine for chrome,
// wrong for anything whose MEANING is fill: a star rating, a saved bookmark, a
// liked heart.
function iconMaskRule(name, pathData, filled) {
  const paint = filled
    ? 'fill="currentColor" stroke="none"'
    : 'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" ${paint}>${pathData}</svg>`;
  const uri = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  // The filled rule is one attribute more specific than the outline one, so it
  // wins inside the same layer without needing !important or a later position.
  const selector = filled ? `[n="${name}"][fill]` : `[n="${name}"]`;
  const box = filled ? '' : 'display:inline-flex;align-items:center;justify-content:center;width:1em;height:1em;font-size:20px;background-color:currentColor;';
  // Same cascade layer as icon.css so s-ic[size=sm/lg] (higher specificity, same
  // layer) wins over the per-icon font-size. Unlayered, this rule would beat
  // every layered rule.
  //
  // The second rule is the LEGACY duplicate: pre-@layer engines drop the
  // layered rule wholesale (blank icons), so the same declarations ship again
  // under `html[data-nolayers]` — an attribute only those engines carry (the
  // server stamps it for legacy UAs; the head feature-detect script is the
  // backstop). On modern browsers the guard never matches; on legacy ones the
  // flattened bundle's size overrides still win (their selectors carry
  // id-level :not(#\\#) boosts from the flattening).
  const decls = `${box}-webkit-mask-image:${uri};mask-image:${uri};-webkit-mask-size:contain;mask-size:contain;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;`;
  return `@layer components{${selector}{${decls}}}\n`
    + `html[data-nolayers] ${selector}{${decls}}\n`;
}

// Every icon an already-serialized document references, as `${name}` or
// `${name} fill` — the same key shape the client's registry uses, so a rule
// emitted here suppresses the client's duplicate.
function iconKeysInHtml(html) {
  const keys = new Set();
  for (const m of String(html || '').matchAll(/<(?:s-ic|swc-icon)\b([^>]*)>/g)) {
    const attrs = m[1];
    const name = attrs.match(/\bn="([^"]+)"/);
    if (!name) continue;
    // A filled icon needs BOTH rules: the outline one carries the box metrics.
    keys.add(name[1]);
    if (/\bfill(?:=|[\s>])/.test(attrs)) keys.add(`${name[1]} fill`);
  }
  return keys;
}

// The <style> body for a rendered document: every referenced icon that the set
// actually defines. Unknown names are skipped rather than emitting a broken
// rule — the client still has its own chance once a late icon pack lands.
function iconCssForHtml(html, icons) {
  let css = '';
  for (const key of iconKeysInHtml(html)) {
    const [name, variant] = key.split(' ');
    const data = icons[name];
    if (data) css += iconMaskRule(name, data, variant === 'fill');
  }
  return css;
}
return { iconMaskRule, iconKeysInHtml, iconCssForHtml };
})();

const __m148 = (() => {
const icons = __m146.icons;
const iconMaskRule = __m147.iconMaskRule;
const _registered = new Set();
const _pending = new Set();
let _styleEl = null;

function _ensureStyleEl() {
    if (_styleEl) return _styleEl;
    // SSR already emitted a #swc-icon-css block for the icons in first paint.
    // Adopt it rather than starting a second one, and seed the registry from
    // its selectors so those icons are not re-generated on hydration.
    const served = document.getElementById('s-ic-css');
    if (served) {
        _styleEl = served;
        for (const m of served.textContent.matchAll(/\[n="([^"]+)"\](\[fill\])?/g)) {
            _registered.add(m[2] ? `${m[1]} fill` : m[1]);
        }
        return _styleEl;
    }
    _styleEl = document.createElement('style');
    _styleEl.id = 's-ic-css';
    document.head.appendChild(_styleEl);
    return _styleEl;
}

function _getIconData(name) {
    return icons[name] || window.__swcIcons?.[name];
}

// `filled` paints the glyph solid instead of stroking its outline. The set is
// drawn as outlines, which a mask renders as a hollow shape — fine for chrome,
// wrong for anything whose MEANING is fill: a star rating, a saved bookmark, a
// liked heart. Opt in per element with the `fill` attribute; the solid mask is
// generated on first use so an app that never asks for one never ships it.
// The rule itself is built by the SHARED generator, so the client and the SSR
// pass cannot produce different masks for the same icon.
function _generateCSS(name, pathData, filled) {
    _ensureStyleEl().textContent += iconMaskRule(name, pathData, filled);
}

function registerIconCSS(name, filled) {
    // The outline rule carries the box metrics, so a filled icon needs both.
    if (filled) registerIconCSS(name);
    const key = filled ? `${name} fill` : name;
    if (_registered.has(key)) return;
    const data = _getIconData(name);
    if (!data) { _pending.add(key); return; }
    _registered.add(key);
    _pending.delete(key);
    _generateCSS(name, data, filled);
}

function flushPending() {
    _pending.forEach((key) => {
        const [name, variant] = key.split(' ');
        registerIconCSS(name, variant === 'fill');
    });
}

// Handle external icon script — may load before or after this module
if (typeof window !== 'undefined') {
    const existing = window.__swcIcons;
    if (existing) {
        // Already loaded (defer script ran before module)
        Object.assign(icons, existing);
        flushPending();
    } else {
        // Not loaded yet — intercept the assignment
        let _value;
        Object.defineProperty(window, '__swcIcons', {
            set(v) { _value = v; Object.assign(icons, v); flushPending(); window.dispatchEvent(new CustomEvent('s-ics-loaded')); },
            get() { return _value; },
            configurable: true,
        });
    }
}

const __default = {
    tags: ['s-ic', 's-ic'],
    props: ['@n', '@sz', '@fill'],
    callbacks: {
        connected() { if (this.n) registerIconCSS(this.n, this.hasAttribute('fill')); },
    },
    methods: {
        onNChange(value) { if (value) registerIconCSS(value, this.hasAttribute('fill')); },
        onFillChange() { if (this.n) registerIconCSS(this.n, this.hasAttribute('fill')); },
    },
};
return { __default };
})();

const __m149 = (() => {
// The alert role announces the message the moment validation writes it —
// fields keep the element in the DOM (hidden via :empty when clear) so its
// id is stable for the input's aria-describedby.
const __default = { tags: ['s-fe', 's-fe'], aria: { role: 'alert' } };
return { __default };
})();

const __m150 = (() => {
const __default = { tags: ['s-l', 's-l'], baseElement: 'HTMLLabelElement' };
return { __default };
})();

const __m151 = (() => {
// s-ln — a real link. The def stamps an inner <a> and relocates authored
// content into it, so href/target behave natively (focus, Enter, middle-click,
// context menu, AT announcement) and link.css's `s-ln a` rules finally match
// something. Without this the tag was an unstyled inline span whose @href did
// nothing.
const __default = {
    tags: ['s-ln', 's-ln'],
    // aria-current rides on the inner <a>, not on the host. The host is a
    // custom element and no screen reader treats it as the link; the anchor is
    // the link, so "you are already on this page" has to be stated there or it
    // is not stated at all. Declared as a prop so a binding (%aria-current=…)
    // reaches it and so a change moves the mark rather than leaving two.
    props: ['@href', '@target', '@aria-current'],
    // Server twin of connected(): emit the same inner <a> so first paint is a
    // REAL link — crawlable, focusable, followable without JS. Without it the
    // server sent a bare custom element and the href only existed after
    // hydration.
    ssr({ attrs, childDefs, text }) {
        const a = ['a'];
        if (attrs.href) a.push(`@href=${attrs.href}`);
        if (attrs.target) a.push(`@target=${attrs.target}`);
        if (attrs['aria-current']) a.push(`@aria-current=${attrs['aria-current']}`);
        if (text != null && text !== '') a.push(`=${text}`);
        if (childDefs && childDefs.length) a.push(childDefs);
        return [a];
    },
    callbacks: {
        connected() {
            if (this._a) return;
            // SSR already emitted the inner <a>; adopt it rather than nesting a
            // second one around it.
            const only = this.children.length === 1 ? this.firstElementChild : null;
            if (only && only.tagName === 'A') { this._a = only; return; }
            const a = document.createElement('a');
            if (this.href) a.href = this.href;
            if (this.target) a.target = this.target;
            if (this.ariaCurrent) a.setAttribute('aria-current', this.ariaCurrent);
            while (this.firstChild) a.appendChild(this.firstChild);
            this.appendChild(a);
            this._a = a;
        },
    },
    methods: {
        onHrefChange() { if (this._a) this._a.href = this.href || ''; },
        onAriaCurrentChange() {
          if (!this._a) return;
          const v = this.ariaCurrent;
          if (v) this._a.setAttribute('aria-current', v);
          else this._a.removeAttribute('aria-current');
        },
        onTargetChange() { if (this._a) this._a.target = this.target || ''; },
    },
};
return { __default };
})();

const __m152 = (() => {
const __default = {
    tags: ['s-li', 's-li'],
    props: ['@selected'],
    // click, not mouseup: keyboard and AT-synthesised activation produce click
    // events; nothing produces a bare mouseup. The role is listitem, not
    // option — s-li is the DEFAULT renderer for generic content lists; true
    // pick-lists (select, menu, transfer) have purpose-built items.
    aria: { role: 'listitem' },
    emits: { click: 'selected' }
};
return { __default };
})();

const __m153 = (() => {
const __default = {
    tags: ['s-mn-i', 's-mn-i'],
    props: ['@selected', '@disabled'],
    aria: { role: 'menuitem', disabled: 'disabled' },
    emits: { click: 'select' }
};
return { __default };
})();

const __m154 = (() => {
// A loading placeholder standing in for content that has not arrived. It had
// no props at all: every shape came from a stylesheet variant, so the size of
// the thing being stood in for was not expressible, and a paragraph placeholder
// meant hand-writing one <s-sk> per line. A skeleton that cannot match the
// block it replaces is decoration — the page still jumps when the real content
// lands.
//
//   @lines=3            a paragraph placeholder: one bar per line
//   @width= @height=    the box it stands in for (a bare number is px)
//   @gap=               space between lines
//   @v=                 circle / heading / avatar shapes (unchanged)
const __default = {
    tags: ['s-sk', 's-sk'],
    props: ['@v', '@lines', '@width', '@height', '@gap'],
    // Decorative by contract: a placeholder has nothing to announce, and the
    // loading status belongs to whatever is doing the loading.
    aria: { hidden: 'true' },
    callbacks: {
        connected() {
            this._syncLines();
            this._syncSize();
        }
    },
    methods: {
        // One bar per line. Rebuilt rather than appended, so lowering the count
        // removes bars instead of leaving the old ones behind.
        _syncLines() {
            const n = Math.max(0, parseInt(this.lines || '0', 10) || 0);
            this._applyChildElements(
                Array.from({ length: n }, () => ['span.sk-line']),
                this,
            );
        },
        // Sizes travel as custom properties, the s-img convention: the
        // stylesheet keeps ownership of the geometry, and a bare number is read
        // as px so `@height=12` does not silently do nothing.
        _syncSize() {
            ['width', 'height', 'gap'].forEach((prop) => {
                const val = this[prop];
                if (!val) { this.style.removeProperty(`--sk-${prop}`); return; }
                const css = /^-?[\d.]+$/.test(String(val)) ? `${val}px` : String(val);
                this.style.setProperty(`--sk-${prop}`, css);
            });
        },
        onLinesChange() { this._syncLines(); },
        onWidthChange() { this._syncSize(); },
        onHeightChange() { this._syncSize(); },
        onGapChange() { this._syncSize(); },
    }
};
return { __default };
})();

const __m155 = (() => {
// A single transient toast, stamped by s-sbs (snackbar-stack). role=status so
// its arrival is announced politely without stealing focus.
const __default = {
    tags: ['swc-snackbar', 's-sb'],
    aria: { role: 'status' },
};
return { __default };
})();

const __m156 = (() => {
const __default = {
    tags: ['s-sb-x', 's-sb-x'],
    aria: { role: 'button', label: '=Dismiss' },
    emits: { click: 'close' }
};
return { __default };
})();

const __m157 = (() => {
// Skip link — the first tab stop of a page with chrome: invisible until
// focused (see utilities.css), jumps keyboard focus straight to the router
// outlet ([role=main]). A customized built-in anchor so it is announced and
// operated as the link it is; the label rides the t.a11y strings.
const __default = {
    tags: ['swc-skip-link', 's-skip'],
    baseElement: 'HTMLAnchorElement',
    // :dataSource, not $key — the label must come from the GLOBAL store's
    // t.a11y strings regardless of any internal store on the owner.
    children: [['span', ':dataField=textContent', ':dataSource=t.a11y.skipToContent']],
    callbacks: {
        connected() {
            if (!this.getAttribute('href')) this.setAttribute('href', '#');
            this.addEventListener('click', (e) => {
                e.preventDefault();
                const main = document.querySelector('[role=main]');
                if (!main) return;
                if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
                main.focus();
            });
        },
    },
};
return { __default };
})();

const __m158 = (() => {
const __default = { tags: ['s-stp-i', 's-stp-i'] };
return { __default };
})();

const __m159 = (() => {
const __default = { tags: ['s-stp-l', 's-stp-l'] };
return { __default };
})();

const __m160 = (() => {
const __default = {
    tags: ['s-stp', 's-stp'],
    props: ['@selected', '@completed', '@error'],
    aria: { role: 'listitem', current: 'selected' }
};
return { __default };
})();

const __m161 = (() => {
const __default = { tags: ['s-sw-w', 's-sw-w'] };
return { __default };
})();

const __m162 = (() => {
const __default = {
    tags: ['s-tb', 's-tb'],
    // @value= is the tab's identity: what its list's value setter matches, and
    // what @seg= reads from the address and writes back (F-811). The selection
    // mixin reads the property first and the attribute as its fallback, and
    // s-tgb, the same row pattern, declares it, so the tab does too: written
    // as `:value=` it lands in a declared prop, not a dead expando.
    props: ['@value', '@selected'],
    aria: { role: 'tab', selected: 'selected' },
    emits: { click: 'select' }
};
return { __default };
})();

const __m163 = (() => {
const __default = {
    tags: ['s-tbl-h', 's-tbl-h'],
    props: ['@sort', '@span'],
    // s-tbl now wraps its cells in display:contents rows, so the header sits
    // in valid table context: columnheader with the sort state reflected to
    // aria-sort ('asc'/'desc'; the bare sortable marker reflects as absent).
    // Sortable headers are keyboard targets — s-tbl grants them tabindex and
    // delegates Enter/Space to the same sort the click performs.
    aria: { role: 'columnheader', sort: 'sort' },
    emits: { click: 'sort' }
};
return { __default };
})();

const __m164 = (() => {
const renderDSL = __m30.renderDSL;
// THE child of the selectable row — ONE tag, both modes. The segmented option
// (s-seg-o) was a second tag doing this same job; it is abandoned, and a
// toggle button is what an option always was.
//
// A toggle button is a LEAF whose content is its label: authored markup
// (<s-tgb value=day>Day</s-tgb>), authored DSL (['s-tgb', '@value=day',
// ['Day']]) and data-stamped rows all put the label in the same place, and the
// list mixin's text stamping keeps working. A children: template would have
// meant a second home for the label (a <span> the authored form never uses)
// plus an empty <s-ic> in every option's first paint.
//
// Semantics are the GROUP's call: exclusive → radio (aria-checked), multi or
// standalone → pressed button (aria-pressed). A def's aria: block is static,
// so only what holds in both modes is declared there and _syncAria owns the
// pair. aria-pressed on role=radio is not a permitted attribute combination
// (axe: aria-allowed-attr), so "set both and let the reader sort it out" is
// not an option.
const __default = {
    tags: ['s-tgb', 's-tgb'],
    props: ['@value', '@selected', '@disabled', '@iconOn', '@iconOff'],
    aria: { role: 'button', disabled: 'disabled' },
    emits: { click: 'selected' },
    callbacks: {
        connected() {
            // Data can arrive before connection when a group stamps items;
            // re-apply it now that the element is live.
            if (this._data) this.data = this._data;
            this._updateIcon();
            this._syncAria();
            // Standalone toggle contract (same shape as s-cb): clicking flips
            // its own pressed state. Only a DIRECT selectable parent (its
            // group) owns the state instead — a selectable ancestor further
            // up (a gallery list, a page shell) is not this toggle's group.
            const managed = () => Boolean(this.parentElement && this.parentElement._isSelectable);
            this.addEventListener('click', () => {
                if (this.disabled) return;
                if (managed()) return;
                this.toggleSelected();
            });
            // Self-managed: the 'selected' emission is nobody else's signal —
            // stop it before an unrelated ancestor list adopts this toggle
            // as one of its items.
            this.addEventListener('selected', (e) => {
                if (e.target === this && !managed()) e.stopPropagation();
            });
        },
    },
    methods: {
        // A stamped row carries its label as text and its icon pair as data.
        // Attributes go through the PROPERTIES (which reflect the kebab-cased
        // attribute) — setAttribute('iconOn', …) writes `iconon`, which the
        // prop getter never reads.
        set data(item) {
            this._data = item;
            if (!item || typeof item !== 'object') return;
            if (item.iconOn) this.iconOn = item.iconOn;
            if (item.iconOff) this.iconOff = item.iconOff;
            const label = item.text ?? item.label;
            // The list mixin stamps item.text into a leaf renderer, but it
            // skips that once this element has an element child (the icon), and
            // it never knew about item.label — so the label is written here and
            // the mixin's pass is a harmless no-op. Writing the text drops any
            // icon with it; _updateIcon puts it back on the next line.
            if (label != null && String(label) !== this.textContent) {
                this.textContent = String(label);
                this._icon = null;
            }
            this._updateIcon();
        },
        get data() { return this._data; },
        _updateIcon() {
            const name = this.selected
                ? (this.iconOn || '')
                : (this.iconOff || '');
            // No icon pair configured → the label alone is the button face, and
            // the icon element is ABSENT rather than hidden. This used to write
            // this.refs.icon.style.display, which is the one thing a component
            // may never do: an empty <s-ic> also shipped in every SSR'd option
            // and only vanished at hydration. Ordering is CSS's job
            // (`s-tgb > s-ic { order: -1 }`), so the icon can simply be
            // appended and removed.
            if (!name) {
                if (this._icon) this._icon.remove();
                this._icon = null;
                return;
            }
            // "Do I already own an icon" is a PARENT test, not an isConnected
            // test: the element is still detached while its own tokens are
            // applied (@selected, @iconOn and @iconOff each land here), and an
            // isConnected check answers false for an icon this element just
            // appended — which stamped a second one on every pre-selected
            // toggle with an icon pair.
            if (this._icon && this._icon.parentElement === this) this._icon.n = name;
            else this._icon = renderDSL(['s-ic', `@n=${name}`], null, this);
        },
        // Which of the two aria state attributes this toggle carries is decided
        // by its group's mode, so the group re-runs this after mode changes and
        // after selection; the toggle itself runs it on connect (a standalone
        // toggle has no group to ask).
        _syncAria() {
            const group = this.parentElement;
            const grouped = Boolean(group && group._isSelectable);
            const exclusive = grouped && !(group._isMultiSelect && group._isMultiSelect());
            const on = this.selected ? 'true' : 'false';
            const role = this.getAttribute('role');
            if (exclusive) {
                // `button` and `radio` are the two roles this component owns
                // and swaps between as its group's mode changes, so both are
                // replaceable. Any OTHER role was authored, and is left alone.
                if (!role || role === 'button') this.setAttribute('role', 'radio');
                this.removeAttribute('aria-pressed');
                this.setAttribute('aria-checked', on);
            } else {
                if (!role || role === 'radio') this.setAttribute('role', 'button');
                this.removeAttribute('aria-checked');
                this.setAttribute('aria-pressed', on);
            }
        },
        onSelectedChange() {
            this._updateIcon();
            this._syncAria();
        },
        onIconOnChange() {
            this._updateIcon();
        },
        onIconOffChange() {
            this._updateIcon();
        },
    },
};
return { __default };
})();

const __m165 = (() => {
const __default = {
    tags: ['s-tgb-ic', 's-tgb-ic'],
    props: ['@selected', 'onI', 'offI'],
    children: [
        ['s-ic', '@data-role=on', '#onI:n'],
        ['s-ic', '@data-role=off', '#offI:n'],
    ],
    aria: { role: 'button', pressed: 'selected' },
    emits: { click: 'selected' },
};
return { __default };
})();

const __m166 = (() => {
const __default = { tags: ['s-tt', 's-tt'] };
return { __default };
})();

const __m167 = (() => {
const __default = {
    tags: ['s-desc-i', 's-desc-i'],
    props: ['@label', '@span']
};
return { __default };
})();

const __m168 = (() => {
const __default = {
    tags: ['s-img', 's-img'],
    dispatches: ['error', 'preview'],
    // `loading` and `fetchpriority` pass straight through to the inner <img>:
    // long feeds opt into @loading=lazy below the fold, and a route's LCP
    // candidate (a product hero, a first card) declares @fetchpriority=high.
    props: ['@src', '@alt', '@width', '@height', '@preview', '@fallback', '@loading', '@fetchpriority'],
    dataField: 'src',
    internalStore: true,
    // SSR emits the inner <img> WITH its src: without this, first-paint HTML
    // carried a bare <img> and every image on the page became discoverable
    // only after the bundle booted and _applyProps ran — the browser's
    // preload scanner never saw the LCP image (Lighthouse: "make the LCP
    // image discoverable from the HTML immediately"). Mirrors _applyProps;
    // the client's connect re-applies the same values idempotently.
    ssr({ store, data, dataSource, attrs, props }) {
        const src = props?.src ?? attrs?.src ?? (dataSource && store ? store.getValue(dataSource) : data);
        if (!src || typeof src !== 'string') return null;
        const img = { '@src': src };
        const alt = props?.alt ?? attrs?.alt;
        if (alt) img['@alt'] = String(alt);
        if (attrs?.loading) img['@loading'] = attrs.loading;
        if (attrs?.fetchpriority) img['@fetchpriority'] = attrs.fetchpriority;
        return { children: [['img', img]] };
    },
    children: [
        // Loaded image. Destroyed reactively when the internal `broken` flag
        // is set, so there is no imperative style.display toggle.
        ['img', ':rf=img', '?!data:broken'],
        // Broken placeholder. Mounts only when `broken` is set. The `broken`
        // attribute is the CSS hook (tag + attribute selector, never a class).
        ['s-cn', '@broken=', '?data:broken', [
            ['s-ic', '@n=image-off'],
        ]],
        ['s-cn.img-preview-overlay', ':rf=overlay', '?data:previewing', [
            ['s-cn.img-preview-toolbar', [
                ['button.img-preview-btn', '@title=Zoom out', '~cl:call:_zoomOut', '="\u2212"'],
                ['button.img-preview-btn', '@title=Zoom in', '~cl:call:_zoomIn', '="+"'],
                ['button.img-preview-btn', '@title=Rotate', '~cl:call:_rotate', '="\u21BB"'],
                ['button.img-preview-btn.img-preview-close', '@title=Close', '~cl:call:_closePreview', '="\u00D7"'],
            ]],
            // Preview backdrop. Identified in JS by its :rf ref (never a class
            // lookup) and in CSS by the preview-wrap attribute.
            ['s-cn', '@preview-wrap=', ':rf=previewWrap', '~cl:call:_onOverlayClick', [
                ['img.img-preview-img', ':rf=previewImg'],
            ]],
        ]],
    ],
    callbacks: {
        connected() {
            this._scale = 1;
            this._rotation = 0;
            this._applyProps();
            const img = this.refs.img;
            if (img) {
                img.onerror = () => this._handleError();
            }
            if (this.preview && img) {
                img.onclick = () => this._openPreview(img.src);
            }

            this._onKey = (e) => {
                if (e.key === 'Escape' && this.data.previewing) this._closePreview();
            };
            document.addEventListener('keydown', this._onKey);
        },
        disconnected() {
            document.removeEventListener('keydown', this._onKey);
        }
    },
    methods: {
        _applyProps() {
            const img = this.refs.img;
            if (!img) return;
            if (this.src) img.src = this.src;
            if (this.alt) img.alt = this.alt;
            if (this.loading) img.loading = this.loading;
            if (this.fetchpriority) img.fetchPriority = this.fetchpriority;
            this._applySizeCssVar('width');
            this._applySizeCssVar('height');
        },
        _applySizeCssVar(prop) {
            const val = this[prop];
            if (!val) {
                this.style.removeProperty(`--${prop}`);
                return;
            }
            const cssVal = val.includes('%') || val.includes('px') ? val : val + 'px';
            this.style.setProperty(`--${prop}`, cssVal);
        },
        _handleError() {
            const img = this.refs.img;
            if (!img) return;

            const fallback = this.fallback;
            if (fallback && img.src !== fallback) {
                img.src = fallback;
            } else {
                // Flip the internal `broken` flag: the show-if bindings destroy
                // the image and mount the placeholder reactively. No DOM
                // creation, no class, no style.display.
                this._internalData.setValue('broken', true);
            }
            this.dispatchEvent(new CustomEvent('error', { bubbles: true }));
        },
        _openPreview(src) {
            this.dispatchEvent(new CustomEvent('preview', { bubbles: true, detail: { src } }));

            this._scale = 1;
            this._rotation = 0;
            this.data.previewing = true;

            requestAnimationFrame(() => {
                const previewImg = this.refs.previewImg;
                if (previewImg) {
                    previewImg.src = src;
                    previewImg.style.removeProperty('--img-transform');
                }
            });
        },
        _closePreview() {
            this.data.previewing = false;
        },
        _zoomOut() {
            this._scale = Math.max(0.5, this._scale - 0.5);
            this._applyPreviewTransform();
        },
        _zoomIn() {
            this._scale = Math.min(5, this._scale + 0.5);
            this._applyPreviewTransform();
        },
        _rotate() {
            this._rotation = (this._rotation + 90) % 360;
            this._applyPreviewTransform();
        },
        _applyPreviewTransform() {
            const previewImg = this.refs.previewImg;
            if (previewImg) {
                // Drive the zoom/rotate through a custom property the CSS reads.
                previewImg.style.setProperty('--img-transform', `scale(${this._scale}) rotate(${this._rotation}deg)`);
            }
        },
        _onOverlayClick(e) {
            // Close when clicking the wrap background, not the image itself.
            // The wrap is identified by its :rf ref, never a class lookup.
            if (e.target === e.currentTarget || e.target === this.refs.previewWrap) {
                this._closePreview();
            }
        },
        onSrcChange() { this._applyProps(); },
        onAltChange() { this._applyProps(); },
        onWidthChange() { this._applySizeCssVar('width'); },
        onHeightChange() { this._applySizeCssVar('height'); },
    }
};
return { __default };
})();

const __m169 = (() => {
// Names out of the icon SET: 'warning', 'close' and 'sync' are not in it, so
// three of the five states rendered a blank box where the glyph should be.
const MSG_ICONS = {
    info: 'info', success: 'circle-check', warning: 'triangle-alert',
    error: 'circle-x', loading: 'loader-circle',
};

function msgItemDef(id, content, type) {
    return ['s-cn.msg-item', `@type=${type || 'info'}`, [
        type === 'loading'
            ? ['s-ic.msg-icon.msg-icon-spin', `@n=${MSG_ICONS[type]}`]
            : ['s-ic.msg-icon', `@n=${MSG_ICONS[type] || MSG_ICONS.info}`],
        ['span.msg-text', `="${content}"`],
    ]];
}

const __default = {
    tags: ['s-msg', 's-msg'],
    props: ['@type', '@duration'],
    aria: { role: 'status' },
    internalStore: true,
    children: [
        ['s-cn.msg-container', ':ref=container', ':dataKey=items', ':dataField=children'],
    ],
    callbacks: {
        connected() {
            this._items = [];
            this._nextId = 0;
        }
    },
    methods: {
        open(content, type = 'info', duration = 3000) {
            const id = ++this._nextId;
            this._items.push({ id, content, type });
            this._syncItems();

            if (duration > 0) {
                setTimeout(() => {
                    // Mark as exiting via state attribute, then remove after animation
                    // The rows ARE this._items in order, so the one to mark is
                    // found by its position in that array — no id written into
                    // the markup and read back out.
                    const container = this.refs.container;
                    const index = this._items.findIndex(item => item.id === id);
                    if (container && index >= 0) container.children[index]?.setAttribute('exiting', '');
                    setTimeout(() => {
                        this._items = this._items.filter(item => item.id !== id);
                        this._syncItems();
                    }, 300);
                }, duration);
            }

            return id;
        },
        _syncItems() {
            this._internalData.setValue('items',
                this._items.map(item => msgItemDef(item.id, item.content, item.type))
            );
        }
    }
};
return { __default };
})();

const __m170 = (() => {
// Names out of the icon SET. The -circle suffixes were the wrong way round for
// this set (circle-check, not check-circle) and 'warning' is not in it at all,
// so every one of the four states rendered a blank box.
const NTF_ICONS = { success: 'circle-check', error: 'circle-x', warning: 'triangle-alert', info: 'info' };

function ntfItemDef(id, { message, description, type }) {
    const itemChildren = [];

    if (type) {
        itemChildren.push(['s-ic.ntf-icon', `@n=${NTF_ICONS[type] || NTF_ICONS.info}`]);
    }

    const contentChildren = [
        ['s-cn.ntf-message', `="${message || ''}"`],
    ];
    if (description) {
        contentChildren.push(['s-cn.ntf-description', `="${description}"`]);
    }

    itemChildren.push(['s-cn.ntf-content', contentChildren]);
    // The close control carries the id it dismisses.
    itemChildren.push(['s-b.ntf-close', '@v=ic', '@aria-label=$t.a11y.dismiss', `~cl:call:_dismiss:${id}`, [['s-ic', '@n=x']]]);

    const spec = ['s-cn.ntf-item'];
    if (type) spec.push(`@type=${type}`);
    spec.push(itemChildren);

    return spec;
}

const __default = {
    tags: ['s-ntf', 's-ntf'],
    props: ['@psn'],
    aria: { role: 'status' },
    internalStore: true,
    children: [
        ['s-cn.ntf-container', ':ref=container', ':dataKey=items', ':dataField=children'],
    ],
    callbacks: {
        connected() {
            this._items = [];
            this._nextId = 0;
            if (!this.psn) this.psn = 'topRight';

        }
    },
    methods: {
        open({ message, description, type, duration = 4500 }) {
            const id = ++this._nextId;
            this._items.push({ id, message, description, type });
            this._syncItems();

            if (duration > 0) {
                setTimeout(() => {
                    if (this._items.some(item => item.id === id)) {
                        this._dismiss(id);
                    }
                }, duration);
            }

            return id;
        },
        _dismiss(id) {
            id = Number(id);
            // Mark as exiting via state attribute, then remove from data after animation
            // The rows ARE this._items in order — position, not a stamped id.
            const container = this.refs.container;
            const index = this._items.findIndex(item => item.id === Number(id));
            if (container && index >= 0) container.children[index]?.setAttribute('exiting', '');
            setTimeout(() => {
                this._items = this._items.filter(item => item.id !== id);
                this._syncItems();
            }, 200);
        },
        _syncItems() {
            this._internalData.setValue('items',
                this._items.map(item => ntfItemDef(item.id, item))
            );
        }
    }
};
return { __default };
})();

const __m171 = (() => {
// Code block (the catalog's "code-block" display): an editor-style panel, dark
// in both themes, with line numbers, light syntax coloring and a copy button.
// Lifted from the smartwebcomponents.com code-view component so the framework
// owns it. Bind the source reactively with ':dataField=code' +
// ':dataSource=<key>' or assign `.code` directly. The copy button uses the
// 'copy' and 'check' icons; the host app's icon set must include both.
//
// The coloring is a one-regex tokenizer (strings, object keys, numbers,
// keywords, comments): enough to read JSON and shell without shipping a
// highlighting library.
const TOKEN = /("(?:[^"\\]|\\.)*")(\s*:)?|\b\d+(?:\.\d+)?\b|\b(?:true|false|null)\b|\/\/[^\n]*/g;

const spanFor = (text, kind) => ['span', `@t=${kind}`, [text]];

function lineSpans(line) {
  const out = [];
  let last = 0;
  Array.from(line.matchAll(TOKEN)).forEach((m) => {
    m.index > last && out.push(['span', [line.slice(last, m.index)]]);
    const [whole, str, colon] = m;
    const kind = str ? (colon ? 'k' : 's') : /^\/\//.test(whole) ? 'c' : /^[\d.]/.test(whole) ? 'n' : 'b';
    out.push(spanFor(colon ? str : whole, kind));
    colon && out.push(['span', [colon]]);
    last = m.index + whole.length;
  });
  last < line.length && out.push(['span', [line.slice(last)]]);
  return out.length ? out : [['span', [' ']]];
}

const __default = {
  tags: ['swc-code-block', 's-code'],
  methods: {
    set code(value) {
      this._code = String(value ?? '');
      this.replaceChildren();
      if (!this._code.trim()) return;
      this._applyChildElements([
        ['s-b', '@copy=', '@v=ic', '@sz=sm', '@aria-label=$t.a11y.copy', '~cl:call:_copy', [
          ['s-ic', ':ref=copyIcon', '@n=copy'],
        ]],
        ['s-cn', '@lines=', this._code.split('\n').map((line, i) => (
          ['s-cn', '@ln=', [
            ['span', '@no=', [String(i + 1)]],
            ['span', '@src=', lineSpans(line)],
          ]]
        ))],
      ], this);
    },
    get code() { return this._code; },
    _copy() {
      navigator.clipboard.writeText(this._code);
      const icon = this.refs.copyIcon;
      if (!icon) return;
      icon.n = 'check';
      clearTimeout(this._copyTimer);
      this._copyTimer = setTimeout(() => { icon.n = 'copy'; }, 1400);
    },
  },
};
return { __default };
})();

const __m172 = (() => {
// Markdown view (the catalog's "markdown-view" display): renders a markdown
// string as framework DSL — headings, paragraphs, bold/italic/strikethrough,
// links, ordered/unordered lists, blockquotes, rules, inline code and fenced
// code blocks (through s-code). Parsing emits DSL nodes directly, so text
// stays text content end to end: no HTML string is ever built, nothing to
// sanitize. Assign `.md` or bind ':dataField=md' + ':dataSource=<key>'.
// The chat composite (s-cnv) uses it for rich message bodies.

// Inline spans: `code`, ***bold italic***, **bold**, *italic*, ~~strike~~,
// [label](url). Bold/italic contents recurse so nesting works.
const INLINE = /`([^`]+)`|\*\*\*([^*]+)\*\*\*|\*\*([^*]+)\*\*|\*([^*\n]+)\*|~~([^~]+)~~|\[([^\]]+)\]\(([^)\s]+)\)/g;

function inline(text) {
  const out = [];
  let last = 0;
  Array.from(String(text).matchAll(INLINE)).forEach((m) => {
    m.index > last && out.push(m.input.slice(last, m.index));
    const [, code, boldItalic, bold, italic, strike, label, url] = m;
    if (code !== undefined) out.push(['code', [code]]);
    else if (boldItalic !== undefined) out.push(['strong', [['em', inline(boldItalic)]]]);
    else if (bold !== undefined) out.push(['strong', inline(bold)]);
    else if (italic !== undefined) out.push(['em', inline(italic)]);
    else if (strike !== undefined) out.push(['del', inline(strike)]);
    else out.push(['a', `@href=${url}`, '@target=_blank', '@rel=noopener noreferrer', inline(label)]);
    last = m.index + m[0].length;
  });
  last < text.length && out.push(text.slice(last));
  return out.length ? out : [''];
}

// A paragraph keeps its single newlines as line breaks.
const paragraph = (block) => ['p', block.split('\n').flatMap((line, i) => (
  i === 0 ? inline(line) : [['br'], ...inline(line)]
))];

const LIST_LINE = /^(?:[-*]|\d+\.)\s+/;
const listNode = (lines) => [
  /^\d/.test(lines[0]) ? 'ol' : 'ul',
  lines.map((l) => ['li', inline(l.replace(LIST_LINE, ''))]),
];

// One non-code segment → block DSL: headings, rules, quotes, list runs,
// blank-line-separated paragraphs.
function blocks(segment) {
  const out = [];
  let list = null;
  let para = [];
  const flush = () => {
    list && out.push(listNode(list));
    para.length && out.push(paragraph(para.join('\n')));
    list = null;
    para = [];
  };
  segment.split('\n').forEach((line) => {
    const t = line.trim();
    const heading = t.match(/^(#{1,3})\s+(.*)$/);
    if (!t) { flush(); return; }
    if (heading) { flush(); out.push([`h${heading[1].length}`, inline(heading[2])]); return; }
    if (/^(-{3,}|\*{3,})$/.test(t)) { flush(); out.push(['s-dv']); return; }
    if (t.startsWith('> ')) { flush(); out.push(['s-cn', '@rg=mkd-quote', [paragraph(t.slice(2))]]); return; }
    if (LIST_LINE.test(t)) { para.length && flush(); (list = list || []).push(t); return; }
    list && flush();
    para.push(line);
  });
  flush();
  return out;
}

// Fenced ``` segments become s-code panels; everything between them parses as
// markdown blocks. The fence text rides OUTSIDE the DSL (fences array + a ref
// per panel, assigned after render): a ':code' prop token would run through
// the DSL value parser, which JSON-parses parseable strings — a valid-JSON
// fence would arrive as an object.
function markdownToDSL(text) {
  const src = String(text ?? '');
  const nodes = [];
  const fences = [];
  let last = 0;
  Array.from(src.matchAll(/```(\w*)\n([\s\S]*?)```/g)).forEach((m) => {
    nodes.push(...blocks(src.slice(last, m.index)));
    nodes.push(['s-code', `:ref=fence${fences.length}`]);
    fences.push(m[2].replace(/\s+$/, ''));
    last = m.index + m[0].length;
  });
  nodes.push(...blocks(src.slice(last)));
  return { nodes, fences };
}

const __default = {
  tags: ['swc-markdown', 's-mkd'],
  methods: {
    set md(value) {
      this._md = String(value ?? '');
      this.replaceChildren();
      if (!this._md.trim()) return;
      const { nodes, fences } = markdownToDSL(this._md);
      this._applyChildElements(nodes, this);
      fences.forEach((code, i) => {
        const panel = this.refs && this.refs[`fence${i}`];
        if (panel) panel.code = code;
      });
    },
    get md() { return this._md; },
  },
};
return { markdownToDSL, __default };
})();

const __m173 = (() => {
// Declarative video embed (the catalog's "video-embed" display). Lazy by
// design: renders a poster placeholder (play glyph drawn by CSS, title label)
// and only creates the third-party iframe / native video element on click, so
// a page render makes no external requests. YouTube and Vimeo URLs (or bare
// ids via provider) map to their embed players; anything else plays through a
// native <video> element.
// Props: src (watch URL, embed URL or file URL), provider ('youtube'|'vimeo'),
// videoId, title, ratio ('16:9' default | '4:3' | '1:1').

// Recognize watch/share URL shapes and normalize to an embed URL.
function embedUrl({ src, provider, videoId }) {
  const url = String(src || '');
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt || provider === 'youtube') {
    const id = (yt && yt[1]) || videoId;
    return id ? { kind: 'iframe', url: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1` } : null;
  }
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d{6,})/);
  if (vm || provider === 'vimeo') {
    const id = (vm && vm[1]) || videoId;
    return id ? { kind: 'iframe', url: `https://player.vimeo.com/video/${id}?autoplay=1` } : null;
  }
  return url ? { kind: 'video', url } : null;
}

const __default = {
  tags: ['s-vid', 's-vid'],
  // String props are attribute-reflected so authored DSL can pass them as
  // plain @attr tokens; videoId stays a property (camelCase attribute names
  // do not reflect cleanly).
  props: ['@src', '@provider', '@title', '@ratio', 'videoId', '@playing'],
  children: [
    ['s-cn', '@rg=poster', '~cl:call:_play', [
      ['s-cn', '@rg=play'],
      ['s-l', '@rg=vtitle', '#title'],
    ]],
  ],
  methods: {
    _play() {
      const target = embedUrl(this);
      if (!target || this.playing) return;
      this.playing = true;
      // A video player has no SWC-primitive equivalent: the embed IS the
      // third-party iframe (or the browser's native video element), created
      // only on explicit user intent. swc-lint-ok
      const el = document.createElement(target.kind === 'iframe' ? 'iframe' : 'video'); // swc-lint-ok: video playback needs the native embed element; created lazily on click only
      if (target.kind === 'iframe') {
        el.src = target.url;
        el.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
        el.allowFullscreen = true;
        el.title = this.title || 'Video';
      } else {
        el.src = target.url;
        el.controls = true;
        el.autoplay = true;
      }
      this.replaceChildren(el);
    },
    onRatioChange() {
      this.style.aspectRatio = String(this.ratio || '16 / 9').replace(':', ' / ');
    },
  },
  callbacks: {
    connected() {
      this.onRatioChange();
    },
  },
};
return { __default };
})();

const __m174 = (() => {
// The type→icon map is the same vocabulary s-msg uses for the same four
// meanings, so an info alert and an info message show the same glyph.
// Names out of the icon SET. 'warning' and 'close' are not in it and never
// were — the components that named them rendered a blank box wherever the glyph
// should be, because a mask rule is only generated for a name the set defines.
const ALERT_ICONS = { info: 'info', success: 'circle-check', warning: 'triangle-alert', error: 'circle-x' };

const __default = {
    tags: ['s-al', 's-al'],
    dispatches: ['close'],
    //   @psn       float the alert instead of leaving it in flow: top,
    //              top-left, top-right, bottom, bottom-left, bottom-right
    //   @duration  ms before it dismisses itself; absent or 0 means it stays
    props: ['@type', '@closable', '@banner', '@showIcon', '@open', '@psn', '@duration'],
    aria: { role: 'alert' },
    storeAttrs: ['open'],
    children: [
        // A real icon element, not a CSS `content:` glyph. The stylesheet drew
        // ℹ ✓ ⚠ ✗ as text on ::before — font characters, which render at
        // whatever weight the font happens to have (or as tofu when it has
        // none), ignore the icon set entirely, and cannot be sized or coloured
        // with the rest of the chrome. s-ic masks the real SVG.
        ['s-ic.al-icon', ':ref=icon', '?prop:showIcon'],
        // The dismiss affordance is a REAL button, like every other overlay's:
        // a bare span was unreachable by keyboard and unnamed to a screen
        // reader, on a control whose whole job is to be operable.
        ['s-b.al-close', '@v=ic', '@aria-label=$t.a11y.close', '?prop:closable',
            '~cl:set:^open:false', [['s-ic', '@n=x']]],
    ],
    callbacks: {
        connected() {
            if (!this.hasAttribute('open') && this.getAttribute('open') !== 'false') {
                this.setAttribute('open', '');
            }
            this._syncIcon();
            this._armDismiss();
        },
        disconnected() {
            clearTimeout(this._dismissTimer);
        }
    },
    methods: {
        // The name follows @type. Written through the bound ref rather than
        // looked up, and re-run on type changes so a live alert that escalates
        // from info to error changes its glyph with its colours.
        _syncIcon() {
            const icon = this.refs.icon;
            if (icon) icon.n = ALERT_ICONS[this.type] || ALERT_ICONS.info;
        },
        onTypeChange() { this._syncIcon(); },
        onShowIconChange() {
            // ?prop:showIcon mounts the element, so the ref only exists once
            // the container has rebuilt.
            requestAnimationFrame(() => this._syncIcon());
        },
        // @duration is a per-OPEN timer, not a one-shot at connect: an alert
        // reopened from a store key has to start counting again, and one closed
        // by hand before the timer fires must not close itself a second time
        // afterwards.
        _armDismiss() {
            clearTimeout(this._dismissTimer);
            const ms = parseInt(this.duration || '0', 10);
            if (!ms || !this.open) return;
            this._dismissTimer = setTimeout(() => {
                if (this.open) this.open = false;
            }, ms);
        },
        onDurationChange() { this._armDismiss(); },
        onOpenChange(newVal) {
            if (newVal === false || newVal === null) {
                clearTimeout(this._dismissTimer);
                this.dispatchEvent(new CustomEvent('close', { bubbles: true }));
                return;
            }
            this._armDismiss();
        }
    }
};
return { __default };
})();

const __m175 = (() => {
// Error summary — the GOV.UK error-summary contract as a reusable primitive
// (design-system.service.gov.uk/components/error-summary/): a role=alert box
// at the top of the content, a "There is a problem" heading, and one link per
// error that hands focus to the offending field. The flow composite (s-flw)
// consumes it for every screen; s-f forms can adopt it for standalone pages —
// which is exactly why it is a primitive and not flow-private (one
// implementation per pattern).
//
// Contract:
//   :errors  [{ text, name }] — render + self-focus when non-empty
//   heading  overridable via @heading (defaults to the GOV.UK wording)
//   each item dispatches `errorSelect { name }` — the OWNER, who holds the
//   field map, moves focus/scroll to the field; the primitive stays generic.
const __default = {
  tags: ['s-errsum', 's-errsum'],
  dispatches: ['errorSelect'],
  props: ['@heading'],
  aria: { role: 'alert' },
  internalStore: true,
  children: [
    ['h2.errsum-title', ':ref=title', '=There is a problem'],
    ['ul.errsum-list', ':ref=list'],
  ],
  methods: {
    get errors() { return this._errors || []; },
    set errors(list) {
      this._errors = Array.isArray(list) ? list.filter((e) => e && e.text) : [];
      if (this._isConnected) this._renderList();
    },
    onHeadingChange() {
      if (this.refs.title) this.refs.title.textContent = this.heading || 'There is a problem';
    },
    _renderList() {
      const list = this.refs.list;
      if (!list) return;
      list.replaceChildren();
      for (const error of this._errors) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        // A same-page fragment target: the OWNER moves focus on errorSelect;
        // the href keeps the link a real link (keyboard, a11y, copy-paste).
        link.href = `#${error.name || ''}`;
        link.textContent = error.text;
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.dispatchEvent(new CustomEvent('errorSelect', {
            bubbles: true, detail: { name: error.name },
          }));
        });
        item.appendChild(link);
        list.appendChild(item);
      }
      // Present-with-errors: the alert takes focus so the failure announces
      // and the keyboard lands on the recovery links (GOV.UK behavior).
      if (this._errors.length) {
        this.setAttribute('tabindex', '-1');
        this.focus();
      }
    },
  },
  callbacks: {
    connected() {
      if (this.heading) this.onHeadingChange();
      this._renderList();
    },
  },
};
return { __default };
})();

const __m176 = (() => {
const __default = {
    tags: ['s-av', 's-av'],
    props: ['@src', '@alt', '@sz', '@shape', '@imgError'],
    passAttr: { 'img': ['src', 'alt'] },
    internalStore: true,
    children: [
        ['img'],
        // Initials are a VISUAL stand-in for the name, derived from alt — never
        // content. Left exposed, an avatar beside its own name label announced
        // three times: the img's alt, then "M L", then the label.
        ['span.av-initials', ':dataKey=initials', '@aria-hidden=true'],
    ],
    callbacks: {
        connected() {
            // img error/load events do not bubble — delegate via capture on self.
            // The imgError prop reflects to the img-error attribute for CSS.
            ['error', 'load'].forEach(type => this.addEventListener(type, (e) => {
                if (e.target.tagName === 'IMG') this.imgError = type === 'error';
            }, true));
            // Server-rendered avatars arrive with the img and initials already
            // stamped, so neither prop setter has run for them.
            this._syncName();
        }
    },
    methods: {
        _syncInitials() {
            const text = this.alt;
            this.data.initials = text
                ? text.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
                : '';
        },
        onSrcChange() {
            this._syncInitials();
            this.imgError = false;
            // passAttr only carries src at render time; a reactive :src bind
            // lands on the prop, so project it onto the inner img here.
            const img = this.getElementsByTagName('img')[0];
            if (!img) return;
            if (this.src) img.src = this.src;
            else img.removeAttribute('src');
            this._syncName();
        },
        onAltChange() { this._syncInitials(); this._syncName(); },

        /**
         * Say the name exactly once, from whichever element is really carrying
         * it.
         *
         * With a photo that is the img, and the host stays out of the way. With
         * no photo the img has no src — an element that will never show an
         * image and must not be announced as one — so the name moves to the
         * host as role=img, and the empty img is hidden. An avatar with no alt
         * is decorative in both directions and announces nothing at all.
         */
        _syncName() {
            const img = this.getElementsByTagName('img')[0];
            const name = this.alt || '';
            const hasPhoto = Boolean(this.src);
            if (img) {
                if (hasPhoto) { img.removeAttribute('aria-hidden'); img.setAttribute('alt', name); }
                else { img.setAttribute('alt', ''); img.setAttribute('aria-hidden', 'true'); }
            }
            if (!hasPhoto && name) {
                this.setAttribute('role', 'img');
                this.setAttribute('aria-label', name);
            } else {
                if (this.getAttribute('role') === 'img') this.removeAttribute('role');
                this.removeAttribute('aria-label');
            }
        },
    }
};
return { __default };
})();

const __m177 = (() => {
// ── s-av-more ── Overflow indicator ("+N"). A plain item renderer: the group's
// list sets its data { text: '+N' }, which the list mixin writes as textContent.
const avatarMore = {
    tags: ['s-av-more', 's-av-more'],
};

// ── s-av-g ── Avatar group with @max overflow.
//
// Doctrine: visibility is data-driven, never imperative. Consumer avatars are
// authored inline (['s-av-g', [['s-av', ...], ...]]). The group IS the list
// (itemRenderer s-av): on connect it reads those inline avatars once into a
// model (_avatarData), then hands the list a copy sliced to `max`. Only the
// first `max` avatars are rendered — the overflow ones are never created. When
// there is overflow, one extra polymorphic item (s-av-more via itemRendererField)
// is appended as the "+N" indicator, so its presence is purely a function of the
// data. Nothing is hidden; there is no .hidden / display toggling.
//
// Why the group is the list (not a wrapper around a nested list): a component
// that owns a children: template has its inline childDefs wiped by the hydration
// rebuild (connectedCallback clears SSR children, then rebuilds only the
// template). With no template, the authored avatars survive as real children and
// can be read on every fresh render — SSR and client alike.
const avatarGroup = {
    tags: ['s-av-g', 's-av-g'],
    props: ['@max'],
    itemRenderer: 's-av',
    itemRendererField: 'r',
    callbacks: {
        connected() {
            // Read the authored avatars once. After the first render this.children
            // are list-produced s-av items, so re-reading would capture those;
            // the guard keeps the original model. A fresh element instance (e.g.
            // the post-hydration client render) has its own inline avatars and
            // captures them on its own first connect.
            if (!this._avatarData) {
                const inline = Array.from(this.children)
                    .filter((el) => el.tagName === 'S-AV' || el.tagName === 'SWC-AVATAR')
                    .map((el) => ({
                        attrs: Object.fromEntries(
                            Array.from(el.attributes).map((a) => [a.name, a.value])
                        ),
                    }));
                // Avatars can be BOUND rather than authored inline. A bound
                // :dataSource= has already put its rows on the list by now, so
                // adopt them as the model — reading only the (empty) inline
                // children and rendering from that is what set this.data = []
                // over the rows and left every bound group blank.
                this._avatarData = inline.length ? inline
                    : (Array.isArray(this._data) ? this._data.slice() : []);
            }
            this._render();
        },
    },
    methods: {
        _render() {
            const all = this._avatarData || [];
            const max = parseInt(this.max || '0', 10);
            const visible = max ? all.slice(0, max) : all;
            const overflow = max ? Math.max(0, all.length - max) : 0;

            // Setting .data drives the list mixin (_renderList), which replaces the
            // group's children with exactly these items — sliced avatars plus, only
            // when overflowing, the "+N" indicator.
            this.data = overflow > 0
                ? [...visible, { r: 's-av-more', text: `+${overflow}` }]
                : visible;
        },
        onMaxChange() { this._render(); },
    },
};

const __default = [avatarMore, avatarGroup];
return { __default };
})();

const __m178 = (() => {
const __default = {
    tags: ['s-bdg', 's-bdg'],
    props: ['@count', '@dot', '@overflow', '@status', '@c'],
    internalStore: true,
    children: [
        ['span.bdg-sup.bdg-dot'],
        ['span.bdg-sup.bdg-count', ':dataKey=displayCount'],
    ],
    methods: {
        _sync() {
            const count = parseInt(this.count, 10);
            const overflow = parseInt(this.overflow, 10) || 99;
            this.data.displayCount =
                isNaN(count) || count <= 0 ? '' : count > overflow ? `${overflow}+` : `${count}`;
        },
        onCountChange() { this._sync(); },
        onOverflowChange() { this._sync(); },
    }
};
return { __default };
})();

const __m179 = (() => {
const __default = {
    tags: ['s-prg', 's-prg'],
    props: ['@percent', '@type', '@status', '@sz', '@showInfo', '@strokeWidth'],
    aria: { role: 'progressbar', valuenow: 'percent', valuemin: '=0', valuemax: '=100' },
    cssVars: { percent: '%', strokeWidth: 'px' },
    internalStore: true,
    children: [
        ['s-cn.prg-outer', '?data:isLine', [
            ['s-cn.prg-inner'],
        ]],
        ['span.prg-info', '?data:showLineInfo', ':dataKey=lineInfo'],
        ['s-cn.prg-circle', '?data:isCircle', [
            ['span.prg-circle-info', '?data:showCircleInfo', ':dataKey=circleInfo'],
        ]],
    ],
    callbacks: {
        connected() {
            this._sync();
        }
    },
    methods: {
        _sync() {
            const type = this.type || 'line';
            const isCircle = type === 'circle';
            const showInfo = this.showInfo !== undefined && this.showInfo !== false;
            const percent = Math.min(100, Math.max(0, parseFloat(this.percent || '0')));
            const { status } = this;

            let infoText = `${Math.round(percent)}%`;
            if (status === 'success') infoText = '\u2713';
            else if (status === 'exception') infoText = '\u2717';

            this.data = {
                isCircle,
                isLine: !isCircle,
                showLineInfo: !isCircle && showInfo,
                showCircleInfo: isCircle && showInfo,
                lineInfo: infoText,
                circleInfo: infoText,
            };
        },
        onPercentChange() { this._sync(); },
        onTypeChange() { this._sync(); },
        onStatusChange() { this._sync(); },
        onShowInfoChange() { this._sync(); },
    }
};
return { __default };
})();

const __m180 = (() => {
const __default = {
    tags: ['s-emp', 's-emp'],
    props: ['@description'],
    internalStore: true,
    // The children are a static template (an illustration and one line of
    // text), so the server may emit them and hydration adopt them. Without
    // the flag the registry admits the def for its projection but renders
    // the host alone, and the description is still nowhere in the HTML.
    ssrChildren: true,
    // The description is DERIVED, so it is projected: project() is pure, the
    // core runs it on connect and on every change to a declared prop, and the
    // serializer runs the same function. Writing the internal store from
    // connected() and onDescriptionChange() kept the text client-only, and an
    // s-emp with `:description=` or `@description=` served as an empty element.
    // A bare `@description=` (attribute present, no value) is `true`, not a
    // string, and falls back to the default wording rather than reading "true".
    project({ description }) {
        return { desc: typeof description === 'string' && description ? description : 'No data' };
    },
    // Authored content REPLACES the illustration and the description (see
    // connected() below), and the server ships that same shape: without this
    // hook first paint read "No data" above the message the author wrote, and
    // only hydration took the default away. Nothing authored: null, and the
    // template renders with the projected description.
    ssr({ childDefs }) {
        return Array.isArray(childDefs) && childDefs.length ? childDefs : null;
    },
    children: [
        ['s-cn.emp-icon', ':ref=defIcon'],
        ['p.emp-desc', ':ref=defDesc', ':dataKey=desc'],
    ],
    callbacks: {
        connected() {
            // Authored DSL/light-DOM content overrides the default icon + description
            const { defIcon, defDesc } = this.refs;
            const customContent = Array.from(this.childNodes)
                .filter(node => node !== defIcon && node !== defDesc)
                .filter(node => node.nodeType === 1 || (node.nodeType === 3 && node.textContent.trim()));
            if (!customContent.length) return;
            [defIcon, defDesc].filter(Boolean).forEach(el => el.remove());
        }
    },
};
return { __default };
})();

const __m181 = (() => {
const __default = {
    tags: ['s-spn', 's-spn'],
    props: ['@spinning', '@sz', '@tip'],
    aria: { role: 'status' },
    internalStore: true,
    children: [
        ['s-cn.spn-overlay', '?prop:spinning', [
            ['span.spn-dot', [
                ['i'], ['i'], ['i'], ['i'],
            ]],
            ['span.spn-tip', '?data:hasTip', ':dataKey=tipText'],
        ]],
    ],
    callbacks: {
        connected() { this._sync(); }
    },
    methods: {
        _sync() {
            const tip = this.tip || '';
            this.data = { hasTip: !!tip, tipText: tip };
        },
        onTipChange() { this._sync(); },
        onSpinningChange() {
            requestAnimationFrame(() => this._sync());
        },
    }
};
return { __default };
})();

const __m182 = (() => {
const formatValue = __m111.formatValue;
const isAbsent = __m111.isAbsent;
const __default = {
    tags: ['s-stat', 's-stat'],
    // `format` picks the presentation (number/currency/percent); `empty` opts
    // the tile into the absent branch. Both are additive — a tile declaring
    // neither formats exactly as it did before.
    //
    // Named `format` and not `style`: `element.style` is the CSSStyleDeclaration,
    // and a prop of that name would shadow it.
    props: ['@title', '@value', '@prefix', '@suffix', '@precision', '@groupSeparator', '@format', '@currency', '@empty'],
    children: [
        ['s-cn.stat-title', '#title'],
        ['s-cn.stat-value', [
            ['span.stat-prefix', '#prefix'],
            ['span.stat-content'],
            ['span.stat-suffix', '#suffix'],
        ]],
    ],
    callbacks: {
        connected() {
            this._updateFormattedValue();
        }
    },
    methods: {
        // Formatting is the shared evaluator's job (components/_format.js), not
        // this component's. The grouping and precision logic that used to live
        // here was a second implementation of what the grids do, which is how a
        // tile and the column it summarises end up disagreeing about what a
        // thousand separator is.
        _updateFormattedValue() {
            const contentEl = this.getElementsByClassName('stat-content')[0];
            if (!contentEl) return;

            const raw = this.value;
            if (isAbsent(raw)) {
                // Absent handling is opt-in: without `empty` a tile renders the
                // blank it always did, so existing tiles do not change.
                contentEl.textContent = this.empty ? String(this.empty) : '';
                return;
            }

            const num = parseFloat(raw);
            if (Number.isNaN(num)) {
                // Not a number — a tile may legitimately show a word.
                contentEl.textContent = String(raw);
                return;
            }

            contentEl.textContent = formatValue(num, {
                style: this.format || 'number',
                // Absent precision means "leave the digits alone", which is the
                // behaviour this component already had.
                precision: this.precision ? parseInt(this.precision, 10) : null,
                grouping: this.groupSeparator === undefined || this.groupSeparator === null ? ',' : this.groupSeparator,
                currency: this.currency || '',
            });
        },
        onValueChange() { this._updateFormattedValue(); },
        onPrecisionChange() { this._updateFormattedValue(); },
        onGroupSeparatorChange() { this._updateFormattedValue(); },
        onFormatChange() { this._updateFormattedValue(); },
        onCurrencyChange() { this._updateFormattedValue(); },
        onEmptyChange() { this._updateFormattedValue(); },
    }
};
return { __default };
})();

const __m183 = (() => {
const __default = {
    tags: ['s-tl', 's-tl'],
    props: ['@mode', '@pending'],
    itemRenderer: 's-tl-i',
    internalStore: true,
    children: [
        ['s-cn.tl-pending', '?prop:pending', [
            ['span.tl-pending-dot'],
            ['span.tl-pending-text', '?data:hasPendingText', ':dataKey=pendingText'],
        ]],
    ],
    callbacks: {
        connected() {
            this._syncPending();
        }
    },
    methods: {
        _syncPending() {
            // Timeline is a list, so `this.data` is the items array — internal
            // store writes go through setData. The pending text span mounts only
            // when there is a real message (?data:hasPendingText), so no imperative
            // hide is needed for the boolean pending state.
            const text = this.pending;
            const hasText = !!(text && text !== 'true' && text !== '');
            this.setData('pendingText', hasText ? text : '');
            this.setData('hasPendingText', hasText);
        },
        // No onModeChange: @mode reflects to the attribute and the stylesheet
        // reads it directly, so a mode switch needs no JS at all.
        onPendingChange() {
            // The ?prop:pending conditional creates/destroys the pending element.
            // Re-sync the derived text state after the container rebuilds.
            requestAnimationFrame(() => this._syncPending());
        }
    }
};
return { __default };
})();

const __m184 = (() => {
const __default = {
    tags: ['s-tl-i', 's-tl-i'],
    props: ['@c', '@label']
};
return { __default };
})();

const __m185 = (() => {
// The four states are icons; the three HTTP statuses are numbers, and a number
// is text. Status glyphs used to be unicode characters (\u2713 \u2717 \u26A0 \u2139) drawn by
// whatever font happened to be loaded \u2014 a different weight from every other
// glyph in the library, and tofu in a font that lacks them.
const RESULT_ICONS = {
    success: 'circle-check',
    error: 'circle-x',
    warning: 'triangle-alert',
    info: 'info',
};
const RESULT_CODES = { 404: '404', 403: '403', 500: '500' };

const __default = {
    tags: ['s-res', 's-res'],
    props: ['@status', '@title', '@subTitle'],
    internalStore: true,
    children: [
        ['s-cn.res-icon', ':ref=icon', [
            ['s-ic', ':ref=iconGlyph', '?data:hasIcon'],
            ['span.res-code', '?data:hasCode', ':dataKey=codeText'],
        ]],
        ['s-cn.res-title', ':ref=title', '#title'],
        ['s-cn.res-subtitle', ':ref=subtitle', '?data:hasSubtitle', '#subTitle'],
        ['s-cn.res-extra', ':ref=extra'],
    ],
    callbacks: {
        connected() {
            this._syncIcon();
            this._moveSlottedContent();
        }
    },
    methods: {
        _syncIcon() {
            const status = this.status || 'info';
            const code = RESULT_CODES[status];
            this.data.hasCode = !!code;
            this.data.hasIcon = !code;
            this.data.codeText = code || '';
            this.data.hasSubtitle = !!this.subTitle;
            // The ?data: conditional mounts the icon, so set the name once the
            // container has rebuilt around it.
            requestAnimationFrame(() => {
                const glyph = this.refs.iconGlyph;
                if (glyph && !code) glyph.n = RESULT_ICONS[status] || RESULT_ICONS.info;
            });
        },
        _moveSlottedContent() {
            // Move slotted (authored) children into the always-present extra
            // container. Def children (icon/title/subtitle/extra) are identified
            // via refs so only authored rows move. The extra container hides
            // itself when empty via CSS :empty, so there is no imperative .hidden.
            const { icon, title, subtitle, extra } = this.refs;
            const defContainers = new Set([icon, title, subtitle, extra].filter(Boolean));
            const slotted = Array.from(this.children).filter(child => !defContainers.has(child));
            slotted.forEach(child => extra.appendChild(child));
        },
        onStatusChange() { this._syncIcon(); },
        onSubTitleChange() { this._syncIcon(); },
    }
};
return { __default };
})();

const __m186 = (() => {
const __default = {
    tags: ['s-wm', 's-wm'],
    props: ['@content', '@rotate', '@gap', '@opacity'],
    children: [
        ['s-cn.wm-overlay'],
    ],
    callbacks: {
        connected() {
            this._render();
        }
    },
    methods: {
        _render() {
            const content = this.content;
            if (!content) {
                this.style.removeProperty('--wm-bg');
                return;
            }

            const rotate = parseFloat(this.rotate || '-22');
            const gap = parseInt(this.gap || '100', 10);
            const opacity = parseFloat(this.opacity || '0.15');
            const fontSize = 14;

            // Estimate text width for SVG viewBox sizing
            const charWidth = fontSize * 0.6;
            const textWidth = content.length * charWidth;
            const cellW = Math.max(textWidth + gap, 120);
            const cellH = Math.max(fontSize * 2 + gap, 60);

            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${cellW}" height="${cellH}">` +
                `<text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" ` +
                `font-size="${fontSize}" fill="rgba(0,0,0,${opacity})" ` +
                `transform="rotate(${rotate} ${cellW / 2} ${cellH / 2})">${this._escapeXml(content)}</text>` +
                `</svg>`;

            const dataUri = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
            this.style.setProperty('--wm-bg', dataUri);
        },
        _escapeXml(str) {
            return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
        },
        onContentChange() { this._render(); },
        onRotateChange() { this._render(); },
        onGapChange() { this._render(); },
        onOpacityChange() { this._render(); }
    }
};
return { __default };
})();

const __m187 = (() => {
const __default = {
    tags: ['s-sel-o', 's-sel-o'],
    props: ['@value', '@disabled', '@selected'],
    aria: { role: 'option', selected: 'selected', disabled: 'disabled' },
    emits: { click: 'selected' },
};
return { __default };
})();

const __m188 = (() => {
const CreateComponent = __m30.CreateComponent;
const noteDeclarativeTags = __m30.noteDeclarativeTags;
const queueComponents = __m5.queueComponents;
const accordion = __m31.__default;
const article = __m32.__default;
const aside = __m33.__default;
const card = __m34.__default;
const container = __m35.__default;
const outlet = __m36.__default;
const details = __m37.__default;
const drawer = __m38.__default;
const footer = __m39.__default;
const header = __m40.__default;
const main = __m41.__default;
const modal = __m42.__default;
const nav = __m43.__default;
const panel = __m44.__default;
const popover = __m45.__default;
const section = __m46.__default;
const affix = __m47.__default;
const calendar = __m49.__default;
const command = __m50.__default;
const descriptions = __m51.__default;
const masonry = __m52.__default;
const popconfirm = __m53.__default;
const splitter = __m54.__default;
const tour = __m56.__default;
const upload = __m64.__default;
const form = __m69.__default;
const fieldDefs = __m68.__default;
const repeatGroup = __m72.__default;
const field = __m73.__default;
const fieldRow = __m74.__default;
const checkbox = __m75.__default;
const checkboxGroup = __m76.__default;
const checkboxWrapper = __m77.__default;
const chipInput = __m78.__default;
const otpInput = __m79.__default;
const input = __m80.__default;
const radioButton = __m81.__default;
const radioButtonGroup = __m82.__default;
const textarea = __m83.__default;
const select = __m84.__default;
const inputNumber = __m85.__default;
const slider = __m86.__default;
const rate = __m87.__default;
const colorPicker = __m89.__default;
const datePicker = __m90.__default;
const timePicker = __m91.__default;
const transfer = __m92.__default;
const cascader = __m93.__default;
const autoComplete = __m94.__default;
const mentions = __m95.__default;
const treeSelect = __m96.__default;
const carousel = __m97.__default;
const carouselPagination = __m98.__default;
const chipList = __m99.__default;
const dropdown = __m100.__default;
const splitButton = __m101.__default;
const list = __m102.__default;
const listBase = __m48.__default;
const menu = __m103.__default;
const contextMenu = __m104.__default;
const menubar = __m105.__default;
const panelContent = __m106.__default;
const snackbarStack = __m107.__default;
const stack = __m108.__default;
const stepper = __m109.__default;
const tabList = __m110.__default;
const dataGrid = __m112.__default;
const table = __m113.__default;
const treeGrid = __m114.__default;
const tabs = __m115.__default;
const buttonGroup = __m116.__default;
const toggleButtonGroup = __m117.__default;
const tree = __m118.__default;
const pagination = __m119.__default;
const barChart = __m121.__default;
const barTimeChart = __m122.__default;
const calendarHeatmap = __m123.__default;
const donutChart = __m124.__default;
const gauge = __m125.__default;
const lineChart = __m126.__default;
const mapPins = __m127.__default;
const orgChart = __m128.__default;
const relationGraph = __m130.__default;
const arcDefs = __m132.__default;
const sparkline = __m133.__default;
const stackedBar = __m134.__default;
const treemap = __m135.__default;
const accordionDetail = __m136.__default;
const accordionSummary = __m137.__default;
const breadcrumbs = __m138.__default;
const button = __m139.__default;
const carouselIndicator = __m140.__default;
const chipClose = __m141.__default;
const chip = __m142.__default;
const divider = __m143.__default;
const dropdownItem = __m144.__default;
const fab = __m145.__default;
const icon = __m148.__default;
const fieldError = __m149.__default;
const label = __m150.__default;
const link = __m151.__default;
const listItem = __m152.__default;
const menuItem = __m153.__default;
const skeleton = __m154.__default;
const snackbar = __m155.__default;
const snackbarClose = __m156.__default;
const skipLink = __m157.__default;
const stepIndicator = __m158.__default;
const stepLabel = __m159.__default;
const step = __m160.__default;
const switchWrapper = __m161.__default;
const tab = __m162.__default;
const tableHeader = __m163.__default;
const toggleButton = __m164.__default;
const toggleIconButton = __m165.__default;
const tooltip = __m166.__default;
const descriptionsItem = __m167.__default;
const image = __m168.__default;
const message = __m169.__default;
const notification = __m170.__default;
const codeBlock = __m171.__default;
const markdownView = __m172.__default;
const videoEmbed = __m173.__default;
const alert = __m174.__default;
const errorSummary = __m175.__default;
const avatar = __m176.__default;
const avatarGroup = __m177.__default;
const badge = __m178.__default;
const progress = __m179.__default;
const empty = __m180.__default;
const spin = __m181.__default;
const statistic = __m182.__default;
const timeline = __m183.__default;
const timelineItem = __m184.__default;
const result = __m185.__default;
const watermark = __m186.__default;
const selectOption = __m187.__default;
// Container


























// Input



























// List
























// Chart









// An array, like fieldDefs: s-arc is a host plus the node element and its list.





// Presentation
















// Old panel sub-components removed — panel.js exports all sub-components as array



































const all = [
  // Container
  accordion, affix, article, aside, calendar, card, container, outlet, descriptions, details,
  drawer, footer, header, main, masonry, modal, nav, panel, popconfirm, popover,
  section, splitter, tour, upload, command,
  // Input
  checkbox, checkboxGroup, checkboxWrapper, chipInput, otpInput, form, field, fieldRow,
  fieldDefs, repeatGroup,
  input, radioButton, radioButtonGroup, textarea,
  select, selectOption, inputNumber, slider, rate, colorPicker, datePicker,
  timePicker, transfer, cascader, autoComplete, mentions, treeSelect,
  // List
  buttonGroup, carousel, carouselPagination, chipList, contextMenu, dataGrid, dropdown, splitButton, list, listBase,
  menu, menubar,
  pagination, panelContent, snackbarStack, stack, stepper, tabList, table,
  tabs, toggleButtonGroup, tree, treeGrid,
  // Chart
  barChart, barTimeChart, calendarHeatmap, donutChart, gauge, lineChart,
  arcDefs, mapPins, orgChart, relationGraph, sparkline, stackedBar, treemap,
  // Presentation
  accordionDetail, accordionSummary, breadcrumbs, button, carouselIndicator,
  chipClose, chip, divider, dropdownItem, fab,
  fieldError, icon, label, link, listItem,
  menuItem,
  skeleton, snackbar, snackbarClose, skipLink, stepIndicator, stepLabel, step,
  switchWrapper, tab, tableHeader, toggleButton, toggleIconButton, tooltip,
  descriptionsItem, image, message, notification,
  alert, errorSummary, avatar, avatarGroup, badge, progress, empty, spin,
  statistic, timeline, timelineItem, result, watermark, codeBlock, markdownView, videoEmbed,
];

// Flat list of every framework component def. Exported so the SSR registry can
// learn which tags are customized built-ins (baseElement) and emit the correct
// native tag (<button is="s-b">, <input is="s-i">, ...) instead of an autonomous
// <s-b>/<s-i> tag that a customized-built-in registration can never upgrade.
const componentDefs = all.flat();

// Register on the client (and under the SSR loader's DOM stubs). Guarded so the
// SSR render path can import `componentDefs` in a bare-Node environment (e.g.
// tests) without triggering CreateComponent, which needs HTMLElement.
//
// Customized built-ins register FIRST: a connectedCallback that stamps
// ['s-b', …] before s-b is defined creates an autonomous <s-b> that a
// customized-built-in registration can never upgrade (the modal's close
// button hit exactly this). An autonomous element created early still
// upgrades when its definition lands; a built-in does not.
if (typeof HTMLElement !== 'undefined') {
  // Classified first, registered second (see noteDeclarativeTags) — hydration
  // asks whether a tag's children are the DSL's to adopt before every one of
  // these has been registered, so classification stays inline even when
  // registration is queued below.
  noteDeclarativeTags(componentDefs);
  const ordered = [...componentDefs]
    .sort((a, b) => Boolean(b.baseElement) - Boolean(a.baseElement));
  // On a server-rendered page every define() here upgrades elements that are
  // already in the document, so registering the whole set inline is one very
  // long task. Queue it instead: same order, sliced across tasks, and drained
  // synchronously the moment an input event reaches a not-yet-defined tag
  // (core/core/register-queue.js). A client-rendered boot has nothing in the
  // document to upgrade and needs the registry complete before CreateApp
  // renders, so it stays inline.
  const ssr = typeof document !== 'undefined' && document.querySelector
    && document.querySelector('[data-swc-ssr]');
  if (ssr) queueComponents(ordered, { register: CreateComponent });
  else ordered.forEach(def => CreateComponent(def));
}
return { componentDefs };
})();

const __m189 = (() => {
const store = __m0.store;
// SnackbarService — singleton for toast notifications.
// Uses native popover="manual" for top-layer fixed positioning.
//
// Usage from any component:
//   this.services.snackbar.show('Message saved', { color: 'su', duration: 3000 })



let _instance = null;
let _id = 0;

class SnackbarService {
  _stack = null;

  constructor() { _instance = this; }

  static getInstance() { return _instance; }

  /**
   * Show a toast notification.
   * @param {string} text - message text
   * @param {Object} [opts]
   * @param {string} [opts.color] - 'su' | 'wn' | 'dg' (or omit for default)
   * @param {number} [opts.duration] - auto-dismiss ms (default 3000, 0 = sticky)
   */
  show(text, opts = {}) {
    const stack = this._getStack();
    const item = document.createElement('s-sb');
    item.textContent = text;
    if (opts.color) item.setAttribute('c', opts.color);
    stack.appendChild(item);
    try { stack.showPopover(); } catch {}

    const duration = opts.duration ?? 3000;
    if (duration > 0) {
      setTimeout(() => {
        item.remove();
        if (!stack.children.length) try { stack.hidePopover(); } catch {}
      }, duration);
    }

    return item;
  }

  /**
   * Show success toast.
   */
  success(text, opts = {}) { return this.show(text, { ...opts, color: 'su' }); }

  /**
   * Show warning toast.
   */
  warning(text, opts = {}) { return this.show(text, { ...opts, color: 'wn' }); }

  /**
   * Show error toast.
   */
  error(text, opts = {}) { return this.show(text, { ...opts, color: 'dg' }); }

  /**
   * Clear all snackbars.
   */
  clear() {
    if (!this._stack) return;
    this._stack.replaceChildren();
    try { this._stack.hidePopover(); } catch {}
  }

  // ── Internal ──

  _getStack() {
    if (this._stack) return this._stack;
    this._stack = document.createElement('s-sbs');
    this._stack.id = 's-sbs';
    this._stack.setAttribute('popover', 'manual');
    document.body.appendChild(this._stack);
    return this._stack;
  }
}

const snackbarService = new SnackbarService();
return { snackbarService };
})();

const __m190 = (() => {
const watchCommand = __m59.watchCommand;
const globalStore = __m0.store;
const validateFormData = __m65.validateFormData;
const normalizeFlowConfig = __m71.normalizeFlowConfig;
const computePath = __m71.computePath;
const projectOutput = __m71.projectOutput;
const visibleFields = __m71.visibleFields;
const summaryRows = __m71.summaryRows;
const navOrder = __m71.navOrder;
const positionToAddress = __m71.positionToAddress;
const addressToPosition = __m71.addressToPosition;
const buildFlowRoutes = __m71.buildFlowRoutes;
const samePosition = __m71.samePosition;
const firstIncomplete = __m71.firstIncomplete;
const repeatKey = __m71.repeatKey;
const repeatInstances = __m71.repeatInstances;
const fieldsToRules = __m71.fieldsToRules;
const activeRepeatCount = __m71.activeRepeatCount;
const screenErrors = __m71.screenErrors;
const positionKeys = __m71.positionKeys;
const chromeKeys = __m71.chromeKeys;
// The flow engine — the runtime half of the declarative flow feature
// (core/services/flow-model.js is the pure half; composites/flow/flow.js is
// the rendering half). One engine animates every registered flow:
//
//   answers   the RAW layer (flow.<id>.answers + per-field fan-out keys),
//             written live as fields change — never pruned by branch flips;
//   derived   path / output / summary / chrome / nav keys the frame binds;
//   commands  flow.<id>.navCmd — 'next' | 'back' | 'save' | 'send' written by
//             any button (the frame contract); the engine validates, moves,
//             persists and submits. Validation runs on next/send, never back;
//   routes    for routed flows, generated route entries + fail-closed guards
//             (jumping ahead redirects to the resume point);
//   drafts    memory | local (engine-owned localStorage adapter) | server
//             (declared endpoints — the engine never calls fetch()).
//
// Everything the engine writes is a global store key, so frames and screens
// bind with plain DSL tokens and no imperative wiring. Nothing here throws in
// a render path: bad configs are lint's job (validateFlowConfig), and the
// engine degrades to inert rather than raising.






const SAVE_DEBOUNCE_DEFAULT = 800;

// The error entry a failed submit publishes. It names no field and no
// screen (errorScreens stays null for it): the answers were fine, the POST
// was not, so an error-summary link for it has nowhere to go and the
// composite leaves the position alone. Consumers match on this name.
const SUBMIT_ERROR = '_submit';

// The http client writes a STRING to a request's errorStoreKey (the body's
// `error` or `message`, else the status text or 'Network error'); an object
// written by hand is read the same way.
function submitErrorText(error) {
  if (typeof error === 'string') return error;
  if (error && typeof error.message === 'string') return error.message;
  if (error && typeof error.error === 'string') return error.error;
  return 'Your answers could not be sent';
}


class FlowEngine {
  constructor(deps = {}) {
    this.store = deps.store || globalStore;
    this.router = deps.router || null;      // injected lazily — see _router()
    this.httpClient = deps.httpClient || null;
    this._ls = deps.localStorage || null;   // test seam; real storage otherwise
    this._flows = {};                       // id → { cfg, completed:Set, ... }
  }

  // Lazy singletons: importing router/http-client eagerly would drag the DOM
  // coupling into every consumer of the engine (SSR included). The real
  // singletons are attached by CreateApp; tests inject their own.
  _router() { return this.router; }
  _http() { return this.httpClient; }

  key(flowId, suffix) { return `flow.${flowId}.${suffix}`; }

  flow(flowId) { return this._flows[flowId] || null; }

  /** Subscribe on a flow's behalf; remove() drops the subscription with it. */
  _watch(state, storeKey, callback) {
    const sub = this.store.subscribe(storeKey, callback);
    state.unsubs.push(sub.unsubscribe ? () => sub.unsubscribe() : sub);
  }

  /**
   * _watch for a key whose CHANGES matter, not its standing value:
   * store.subscribe replays the current value at registration, so a submit
   * key holding a value from before this flow existed (a previous
   * registration under the same id, a key another flow shares) would reach
   * the callback as if it had just been written. The replay is skipped;
   * only a later write gets through.
   */
  _watchChanges(state, storeKey, callback) {
    let armed = false;
    this._watch(state, storeKey, (value) => { if (armed) callback(value); });
    armed = true;
  }

  /**
   * Register flows. Returns the route entries (guards attached) the caller
   * appends to the router config — CreateApp wires this before router.init
   * so generated routes and authored routes land in one table.
   */
  addFlows(...configs) {
    const routes = [];
    for (const raw of configs) {
      const cfg = normalizeFlowConfig(raw);
      if (!cfg || !cfg.id || this._flows[cfg.id]) continue;
      const state = {
        cfg,
        completed: new Set(),
        saveTimer: null,
        submitted: false,
        unsubs: [],
      };
      this._flows[cfg.id] = state;

      this._initPersistence(state);
      this._recompute(cfg.id);
      this._watchCommands(state);
      this._watchRoute(state);
      this._watchSubmit(state);
      this._fireLoads(state);

      if (cfg.routed && cfg.route) {
        routes.push(...this._guardedRoutes(state));
      } else {
        // Embedded flows start at their first position immediately.
        this._setPosition(cfg.id, this._resumePosition(cfg.id), { navigate: false });
      }
    }
    return routes;
  }

  /** The flow whose route owns this path, or null. */
  match(path) {
    if (typeof path !== 'string') return null;
    for (const state of Object.values(this._flows)) {
      const base = state.cfg.route;
      if (base && (path === base || path.startsWith(base + '/'))) return state.cfg;
    }
    return null;
  }

  // ── Answers (the raw layer) ──

  answers(flowId) {
    return this.store.getValue(this.key(flowId, 'answers')) || {};
  }

  setAnswer(flowId, name, value) {
    const state = this.flow(flowId) && this._flows[flowId];
    if (!state || name == null) return;
    const merged = { ...this.answers(flowId), [name]: value };
    this.store.setValues({
      [this.key(flowId, 'answers')]: merged,
      [this.key(flowId, `answers.${name}`)]: value,
    });
    this._recompute(flowId);
    this._scheduleSave(state);
  }

  setAnswers(flowId, values) {
    if (!values || typeof values !== 'object') return;
    for (const [name, value] of Object.entries(values)) this.setAnswer(flowId, name, value);
  }

  /** One field of one repeat instance (1-based). Instances create lazily. */
  setRepeatAnswer(flowId, screenId, index, name, value) {
    const state = this._flows[flowId];
    const screen = state && state.cfg.screenMap[screenId];
    if (!screen || !screen.repeat || !(index >= 1) || name == null) return;
    const key = repeatKey(screen);
    const list = repeatInstances(screen, this.answers(flowId)).slice();
    while (list.length < index) list.push({});
    list[index - 1] = { ...list[index - 1], [name]: value };
    this.setAnswer(flowId, key, list);
  }

  output(flowId) {
    const state = this._flows[flowId];
    return state ? projectOutput(state.cfg, this.answers(flowId)) : {};
  }

  // ── Derived keys ──

  _recompute(flowId) {
    const state = this._flows[flowId];
    if (!state) return;
    const answers = this.answers(flowId);
    const { path, exit } = computePath(state.cfg, answers);
    const output = projectOutput(state.cfg, answers);
    const resume = firstIncomplete(state.cfg, answers, state.completed);
    const complete = resume == null && !exit;
    this.store.setValues({
      [this.key(flowId, 'path')]: path,
      [this.key(flowId, 'output')]: output,
      [this.key(flowId, 'complete')]: complete,
      [this.key(flowId, 'summaryRows')]: summaryRows(state.cfg, answers),
    });
    // The current screen can be deactivated by an external write (summary
    // edit + Back, a hydrated draft): fall back to the resume point.
    const position = this.store.getValue(this.key(flowId, 'position'));
    if (position && position.kind === 'screen' && !path.includes(position.screenId)
      && !state.cfg.exitIds.includes(position.screenId)) {
      this._setPosition(flowId, this._resumePosition(flowId));
    } else if (position) {
      this._writeChrome(state, position);
    }
  }

  _resumePosition(flowId) {
    const state = this._flows[flowId];
    if (!state) return null;
    const answers = this.answers(flowId);
    const resume = firstIncomplete(state.cfg, answers, state.completed);
    if (resume) return resume;
    const order = navOrder(state.cfg, answers);
    return order[order.length - 1] || null;
  }

  // ── Position & chrome ──

  position(flowId) {
    return this.store.getValue(this.key(flowId, 'position')) || null;
  }

  _setPosition(flowId, position, opts = {}) {
    const state = this._flows[flowId];
    if (!state || !position) return;
    const previous = this.position(flowId);
    // Moving clears published errors, unless the caller is moving BECAUSE of
    // one (the composite opening a collapsed accordion panel for an error
    // summary link): keepErrors leaves errors and errorList standing.
    this.store.setValues({
      ...positionKeys(state.cfg, position),
      ...(opts.keepErrors ? {} : {
        [this.key(flowId, 'errors')]: null,
        [this.key(flowId, 'errorList')]: null,
      }),
    });
    this._writeChrome(state, position);

    if (opts.navigate !== false && state.cfg.routed && this._router()) {
      const address = positionToAddress(state.cfg, position);
      if (address) {
        const current = this.store.getValue('route');
        if (address.url === current) {
          // Same page (accordion panel, summary hash) — lean-router contract:
          // record the hash, project from store, never re-route.
          this._setHash(address.hash);
        } else {
          this._router().navigateTo(address.url, opts.replace ? { replace: true } : {});
          this._setHash(address.hash);
        }
      }
    }
    if (previous && !samePosition(previous, position)) {
      // A tick the composite watches to move focus to the new heading/panel.
      this.store.setValue(this.key(flowId, 'focusTick'),
        (this.store.getValue(this.key(flowId, 'focusTick')) || 0) + 1);
    }
  }

  _setHash(hash) {
    if (typeof window === 'undefined' || !window.history) return;
    const base = window.location.pathname + window.location.search;
    const url = hash ? `${base}#${hash}` : base;
    if (url !== base + window.location.hash) window.history.replaceState({}, '', url);
  }

  // The chrome is the model's (chromeKeys): the server seeds the same map for
  // a flow URL's first paint, so the two cannot word a title differently.
  _writeChrome(state, position) {
    this.store.setValues(chromeKeys(state.cfg, position, this.answers(state.cfg.id)));
  }

  // ── Commands (the frame contract) ──

  _watchCommands(state) {
    const flowId = state.cfg.id;
    state.unsubs.push(watchCommand(this.store, this.key(flowId, 'navCmd'), (cmd) => {
      if (cmd === 'next') this.next(flowId);
      else if (cmd === 'back') this.back(flowId);
      else if (cmd === 'save') this.save(flowId);
      else if (cmd === 'send') this.send(flowId);
    }));

    // Plain go-to (accordion panel headers, steppers): reachability enforced,
    // no summary-return semantics.
    state.unsubs.push(watchCommand(this.store, this.key(flowId, 'goCmd'), (screenId) => {
      this.goTo(flowId, {
        kind: 'screen', sectionId: state.cfg.sectionOf[screenId] || null, screenId,
      });
    }));

    // Summary Change links are plain declarative writes too: the screen id
    // lands on changeCmd, the engine remembers which summary sent it.
    this._watch(state, this.key(flowId, 'changeCmd'), (target) => {
      if (!target) return;
      this.store.setValue(this.key(flowId, 'changeCmd'), null);
      // 'screenId' or 'screenId@n' (a repeat instance's row).
      const [screenId, n] = String(target).split('@');
      const current = this.position(flowId);
      const from = current && (current.kind === 'summary' || current.kind === 'sectionSummary')
        ? current : { kind: 'summary', sectionId: null, screenId: null };
      this.change(flowId, {
        sectionId: state.cfg.sectionOf[screenId] || null, screenId,
        ...(n ? { repeatIndex: Number(n) } : {}),
      }, from);
    });
  }

  _validateScreen(state, screenId) {
    const screen = state.cfg.screenMap[screenId];
    if (!screen) return {};
    return screenErrors(screen, this.answers(state.cfg.id));
  }

  _validateInstance(state, screenId, index) {
    const screen = state.cfg.screenMap[screenId];
    if (!screen || !screen.repeat) return {};
    const instance = repeatInstances(screen, this.answers(state.cfg.id))[index - 1] || {};
    const rules = fieldsToRules(visibleFields(screen, instance));
    const max = screen.repeat.max || Infinity;
    if (index < max) {
      rules._more = { required: true, message: screen.repeat.addAnother || 'Select yes or no' };
    }
    return validateFormData(instance, rules);
  }

  _publishErrors(flowId, errors, byScreen = null) {
    const list = Object.entries(errors).map(([name, text]) => ({ name, text }));
    this.store.setValues({
      [this.key(flowId, 'errors')]: Object.keys(errors).length ? errors : null,
      [this.key(flowId, 'errorList')]: list.length ? list : null,
      [this.key(flowId, 'errorScreens')]: byScreen,
    });
    return list.length > 0;
  }

  /**
   * Drop every published error without moving. s-rfg renumbers its instances
   * on add and remove, so a compound name ('<group>@<n>.<field>') can point
   * at the wrong control the moment the array changes; the composite calls
   * this on groupItemsChanged. errorScreens goes too (only _publishErrors
   * resets it otherwise).
   */
  clearErrors(flowId) {
    if (!this._flows[flowId]) return;
    this.store.setValues({
      [this.key(flowId, 'errors')]: null,
      [this.key(flowId, 'errorList')]: null,
      [this.key(flowId, 'errorScreens')]: null,
    });
  }

  next(flowId) {
    const state = this._flows[flowId];
    const position = this.position(flowId);
    if (!state || !position) return;
    const answers = this.answers(flowId);

    if (position.kind === 'screen' && position.repeatIndex) {
      const screen = state.cfg.screenMap[position.screenId];
      const max = (screen.repeat && screen.repeat.max) || Infinity;
      // At the cap there is no add-another question — the decision is made.
      if (position.repeatIndex >= max) {
        const instance = repeatInstances(screen, answers)[position.repeatIndex - 1];
        if (!instance || instance._more === undefined) {
          this.setRepeatAnswer(flowId, position.screenId, position.repeatIndex, '_more', 'no');
        }
      }
      const errors = this._validateInstance(state, position.screenId, position.repeatIndex);
      if (this._publishErrors(flowId, errors)) return;
      state.completed.add(position.screenId);
      this._scheduleSave(state);
    } else if (position.kind === 'screen') {
      const errors = this._validateScreen(state, position.screenId);
      if (this._publishErrors(flowId, errors)) return;
      state.completed.add(position.screenId);
      this._scheduleSave(state);
    } else {
      this._publishErrors(flowId, {});
    }
    this._recompute(flowId);

    // Summary-edit return: walk any newly-opened unanswered screens, then
    // return to whichever summary sent us (forms-runner behavior).
    const returnTo = this.store.getValue(this.key(flowId, 'return'));
    const order = navOrder(state.cfg, this.answers(flowId));
    if (returnTo && position.kind === 'screen') {
      const resume = firstIncomplete(state.cfg, this.answers(flowId), state.completed);
      const returnIndex = order.findIndex((p) => p.kind === returnTo.kind
        && (p.sectionId || null) === (returnTo.sectionId || null));
      const resumeIndex = resume ? order.findIndex((p) => samePosition(p, resume)) : -1;
      if (resume && (returnIndex < 0 || resumeIndex < returnIndex)) {
        this._setPosition(flowId, order[resumeIndex]);
        return;
      }
      this.store.setValue(this.key(flowId, 'return'), null);
      this._setPosition(flowId, returnTo);
      return;
    }

    if (position.kind === 'summary') { this.send(flowId); return; }

    // Exit rules: the walk from THIS screen may now terminate in an exit.
    const { exit } = computePath(state.cfg, this.answers(flowId));
    const index = order.findIndex((p) => samePosition(p, position));
    const following = index >= 0 ? order[index + 1] : null;
    if (following) this._setPosition(flowId, following);
    else if (exit) {
      this._setPosition(flowId, { kind: 'exit', sectionId: null, screenId: exit });
    }
  }

  back(flowId) {
    const state = this._flows[flowId];
    const position = this.position(flowId);
    if (!state || !position) return;
    const order = navOrder(state.cfg, this.answers(flowId));
    const returnTo = this.store.getValue(this.key(flowId, 'return'));
    if (returnTo) {
      // GOV.UK: from a summary-sent edit, back goes to the summary itself.
      this.store.setValue(this.key(flowId, 'return'), null);
      this._setPosition(flowId, returnTo);
      return;
    }
    const index = order.findIndex((p) => samePosition(p, position));
    if (index > 0) this._setPosition(flowId, order[index - 1]);
  }

  /** Jump to any position — the engine enforces reachability (no guard fork). */
  goTo(flowId, position, opts = {}) {
    const state = this._flows[flowId];
    if (!state || !position) return;
    const allowed = this._reachable(state, position);
    this._setPosition(flowId, allowed || this._resumePosition(flowId), opts);
  }

  change(flowId, changePosition, returnPosition) {
    // A summary Change link: remember where to come back to.
    this.store.setValue(this.key(flowId, 'return'), returnPosition || { kind: 'summary', sectionId: null, screenId: null });
    this.goTo(flowId, { kind: 'screen', ...changePosition });
  }

  _reachable(state, position) {
    const flowId = state.cfg.id;
    const answers = this.answers(flowId);
    const order = navOrder(state.cfg, answers);
    if (position.kind === 'exit') {
      const { exit } = computePath(state.cfg, answers);
      return exit === position.screenId ? position : null;
    }
    const index = order.findIndex((p) => samePosition(p, position));
    if (index < 0) return null;
    const resume = this._resumePosition(flowId);
    const resumeIndex = order.findIndex((p) => samePosition(p, resume));
    return index <= resumeIndex || resumeIndex < 0 ? order[index] : null;
  }

  // ── Routed mode: guards + route tracking ──

  _guardedRoutes(state) {
    const flowId = state.cfg.id;
    const routes = buildFlowRoutes(state.cfg);
    const resumeRedirect = () => {
      const position = this._resumePosition(flowId);
      // No position at all (every screen unreachable, or an empty flow that
      // lint only warned about): redirecting would target this very route and
      // bounce until the router's loop-breaker gives up on a blank page.
      if (!position) return true;
      const address = positionToAddress(state.cfg, position);
      if (!address || address.url === state.cfg.route) return true;
      return { redirect: address.url };
    };
    for (const route of routes) {
      if (route.meta.flowKind === 'resume') {
        route.canActivate = [() => resumeRedirect()];
      } else {
        route.canActivate = [(_store, target) => {
          const step = target && target.params ? target.params.step : null;
          const n = target && target.params ? target.params.n : null;
          const position = addressToPosition(state.cfg, step, this._currentHash(), n);
          if (!position) return resumeRedirect();
          if (this._reachable(state, position)) return true;
          return resumeRedirect();
        }];
      }
    }
    return routes;
  }

  _currentHash() {
    if (typeof window === 'undefined' || !window.location.hash) return null;
    return window.location.hash.slice(1) || null;
  }

  _watchRoute(state) {
    if (!state.cfg.routed || !state.cfg.route) return;
    const flowId = state.cfg.id;
    this._watch(state, 'route', (path) => {
      if (typeof path !== 'string') return;
      const base = state.cfg.route;
      if (path !== base && !path.startsWith(base + '/')) return;
      const segments = path === base ? [] : path.slice(base.length + 1).split('/');
      const step = segments[0];
      if (!step) return; // resume guard already redirected
      const position = addressToPosition(state.cfg, step, this._currentHash(), segments[1]);
      if (!position) return;
      const returnParam = this.store.getValue('router.return');
      if (returnParam === 'summary') {
        this.store.setValue(this.key(flowId, 'return'), { kind: 'summary', sectionId: null, screenId: null });
      }
      // The route echo of the engine's own navigateTo resolves to the
      // position just set: nothing to move. Setting it again would render
      // the screen a second time and null the errors a goTo with keepErrors
      // deliberately left standing (the composite's error link into a
      // collapsed panel or a screen already left). The same guard makes a
      // re-write of the address already held a no-op (a route write is not
      // a refresh signal: nothing re-renders, no focusTick), and lets a
      // server-seeded position (the first paint's flow.<id>.position) stand
      // at hydration instead of being set a second time.
      if (samePosition(position, this.position(flowId))) return;
      this._setPosition(flowId, position, { navigate: false });
    });
  }

  // ── Screen-entry loads (declarative service references) ──

  _fireLoads(state) {
    const flowId = state.cfg.id;
    this._watch(state, this.key(flowId, 'screenId'), (screenId) => {
      if (!screenId) return;
      const screen = state.cfg.screenMap[screenId];
      if (!screen || !Array.isArray(screen.loads)) return;
      const http = this._http();
      if (!http) return;
      for (const load of screen.loads) {
        if (!load || typeof load.service !== 'string') continue;
        const [svc, ep] = load.service.split('.');
        const service = http.getService && http.getService(svc);
        if (!service || typeof service[ep] !== 'function') continue;
        const params = {};
        for (const [param, source] of Object.entries(load.params || {})) {
          // A string names a source ('answers.x' or a store key); any other
          // literal is the value itself.
          if (typeof source !== 'string') { params[param] = source; continue; }
          params[param] = source.startsWith('answers.')
            ? this.answers(flowId)[source.slice('answers.'.length)]
            : this.store.getValue(source);
        }
        service[ep]({ params });
      }
    });
  }

  // ── Submit ──

  send(flowId) {
    const state = this._flows[flowId];
    if (!state) return;
    const answers = this.answers(flowId);
    const { path } = computePath(state.cfg, answers);
    // One entry per screen: computePath never lists a screen id twice (its
    // visited set stops the walk before a revisit, tests/flow-model.spec.mjs
    // pins it), so the whole assignment below cannot overwrite an earlier
    // visit's errors.
    const byScreen = {};
    const all = {};
    path.map((id) => [id, this._sendErrors(state, id, answers)])
      .filter(([, errors]) => Object.keys(errors).length)
      .forEach(([id, errors]) => {
        byScreen[id] = errors;
        Object.assign(all, errors);
      });
    if (this._publishErrors(flowId, all, Object.keys(byScreen).length ? byScreen : null)) return;

    // A pending draft save would land after the submission — cancel it.
    if (state.saveTimer) { clearTimeout(state.saveTimer); state.saveTimer = null; }

    const submit = state.cfg.submit || {};
    const storeKey = submit.storeKey || this.key(flowId, 'submitRequest');
    state.submitted = true;
    this.store.setValue(storeKey, this.output(flowId));
  }

  /**
   * One path screen's errors for send(). A plain screen goes through
   * screenErrors (group inner fields already carry compound names). A paged
   * repeat validates its ACTIVE instances only (the same count projectOutput
   * submits and navOrder can navigate to; validating an abandoned instance
   * blocked the submit with an error on a screen the user cannot reach),
   * each keyed '<key>@<n>.<field>' so two instances failing the same field
   * no longer collapse onto one entry. next() keeps plain names for the
   * instance it stands on.
   */
  _sendErrors(state, id, answers) {
    const screen = state.cfg.screenMap[id];
    if (!screen) return {};
    if (!screen.repeat) return screenErrors(screen, answers);
    const key = repeatKey(screen);
    const count = Math.min(activeRepeatCount(screen, answers), repeatInstances(screen, answers).length);
    const merged = {};
    Array.from({ length: count }, (_, i) => i + 1).forEach((n) => {
      Object.entries(this._validateInstance(state, id, n))
        .forEach(([name, text]) => { merged[`${key}@${n}.${name}`] = text; });
    });
    return merged;
  }

  /**
   * Two independent watches over the ATTEMPT send() opened (state.submitted).
   * The resultKey turning truthy while an attempt is open navigates to
   * onSuccess (both keys needed, as before). The errorKey turning truthy
   * while one is open publishes ONE error, { name: SUBMIT_ERROR, text },
   * through the same errors/errorList keys the error summary and the title
   * prefix already render, and CLOSES the attempt: a failed submit is over,
   * so a stale or replayed resultKey value after it cannot navigate as a
   * success (the next send() opens a new attempt). Falsy writes are ignored
   * because the http client resets the keys to null when a request starts,
   * and the value a key holds at registration is ignored too
   * (_watchChanges): only a write after send() belongs to the attempt.
   */
  _watchSubmit(state) {
    const submit = state.cfg.submit || {};
    if (submit.resultKey && submit.onSuccess) {
      this._watchChanges(state, submit.resultKey, (result) => {
        if (!result || !state.submitted) return;
        state.submitted = false;
        const r = this._router();
        if (r) r.navigateTo(submit.onSuccess);
      });
    }
    if (submit.errorKey) {
      this._watchChanges(state, submit.errorKey, (error) => {
        if (!error || !state.submitted) return;
        state.submitted = false;
        this._publishErrors(state.cfg.id, { [SUBMIT_ERROR]: submitErrorText(error) });
      });
    }
  }

  // ── Persistence ──

  _persistMode(cfg) {
    const p = cfg.persistence;
    if (p === 'local') return { mode: 'local' };
    if (p && typeof p === 'object' && p.mode === 'server') return p;
    return { mode: 'memory' };
  }

  _persistKey(cfg) {
    const appId = (this.store._config && this.store._config.appId) || 'app';
    return `swc4:${appId}:flow:${cfg.id}`;
  }

  _localStorage() {
    return this._ls || (typeof globalThis !== 'undefined' ? globalThis.localStorage : null);
  }

  _initPersistence(state) {
    const { cfg } = state;
    const mode = this._persistMode(cfg);
    // The engine owns this flow's draft: keep flow.* out of the app-level
    // store persistence so persist:true cannot double-persist raw answers.
    if (mode.mode !== 'memory' && typeof this.store.addPersistExclude === 'function') {
      this.store.addPersistExclude(`flow.${cfg.id}.`);
    }
    if (mode.mode === 'local') {
      const ls = this._localStorage();
      if (!ls) return;
      try {
        const raw = ls.getItem(this._persistKey(cfg));
        if (!raw) return;
        const draft = JSON.parse(raw);
        if (!draft || draft.version !== cfg.version) return; // version mismatch drops the draft
        this._hydrateDraft(state, draft);
      } catch (e) { /* corrupted draft — start clean */ }
    } else if (mode.mode === 'server') {
      // Draft arrives through a declared endpoint's resultStoreKey
      // (flow.<id>.draft by convention) — merge with local-empty-wins.
      this._watch(state, this.key(cfg.id, 'draft'), (draft) => {
        if (!draft || typeof draft !== 'object') return;
        if (draft.version !== undefined && draft.version !== cfg.version) return;
        this._hydrateDraft(state, draft, { keepLocal: true });
      });
      if (mode.load && this._http()) {
        const [svc, ep] = mode.load.split('.');
        const service = this._http().getService && this._http().getService(svc);
        if (service && typeof service[ep] === 'function') service[ep]({});
      }
    }
  }

  _hydrateDraft(state, draft, opts = {}) {
    const flowId = state.cfg.id;
    const local = this.answers(flowId);
    const merged = opts.keepLocal
      ? { ...(draft.answers || {}), ...local }   // local-empty-wins: local values beat the draft
      : { ...(draft.answers || {}) };
    const fanout = {};
    for (const [name, value] of Object.entries(merged)) {
      fanout[this.key(flowId, `answers.${name}`)] = value;
    }
    this.store.setValues({ [this.key(flowId, 'answers')]: merged, ...fanout });
    for (const id of draft.completed || []) state.completed.add(id);
    this._recompute(flowId);
  }

  _scheduleSave(state) {
    const mode = this._persistMode(state.cfg);
    if (mode.mode === 'memory') return;
    const delay = mode.debounce || SAVE_DEBOUNCE_DEFAULT;
    if (state.saveTimer) clearTimeout(state.saveTimer);
    state.saveTimer = setTimeout(() => { state.saveTimer = null; this._saveNow(state); }, delay);
  }

  save(flowId) {
    const state = this._flows[flowId];
    if (!state) return;
    if (state.saveTimer) { clearTimeout(state.saveTimer); state.saveTimer = null; }
    this._saveNow(state);
  }

  _saveNow(state) {
    const { cfg } = state;
    const mode = this._persistMode(cfg);
    const draft = {
      version: cfg.version,
      answers: this.answers(cfg.id),
      completed: [...state.completed],
    };
    if (mode.mode === 'local') {
      const ls = this._localStorage();
      if (!ls) return;
      try { ls.setItem(this._persistKey(cfg), JSON.stringify(draft)); } catch (e) { /* quota — skip */ }
    } else if (mode.mode === 'server') {
      // A declared storeKey-triggered POST turns this write into the request.
      this.store.setValue(this.key(cfg.id, 'saveRequest'), draft);
    }
  }

  /**
   * Drop a flow: its subscriptions, its pending save, and every store key
   * under flow.<id>. (the engine's namespace: answers and their fan-out,
   * position and chrome, errors, errorScreens, the summary-edit return, a
   * server draft, the command keys). Nulled, not removed, so a bound frame
   * sees the change. The app's own submit keys (cfg.submit.*) are not the
   * engine's and stand. A flow added again under the same id therefore
   * starts clean: addFlows resumes from nothing.
   */
  remove(flowId) {
    const state = this._flows[flowId];
    if (!state) return;
    state.unsubs.forEach((unsub) => { try { unsub(); } catch (e) { /* already gone */ } });
    if (state.saveTimer) clearTimeout(state.saveTimer);
    delete this._flows[flowId];
    const prefix = `flow.${flowId}.`;
    const owned = this.store.getStoreNames().filter((key) => key.startsWith(prefix));
    if (owned.length) this.store.setValues(Object.fromEntries(owned.map((key) => [key, null])));
  }

  /**
   * Remove and re-register ONE flow under its id, in one call: a preview
   * host re-mounting a journey whose config changed, a test re-seeding.
   * Returns the route rows addFlows returns (empty for an embedded flow).
   */
  replaceFlow(config) {
    const cfg = normalizeFlowConfig(config);
    if (!cfg || !cfg.id) return [];
    this.remove(cfg.id);
    return this.addFlows(cfg);
  }
}

const flowEngine = new FlowEngine();
return { SUBMIT_ERROR, FlowEngine, flowEngine };
})();

const __m191 = (() => {
const CreateComponent = __m30.CreateComponent;
const renderDSL = __m30.renderDSL;
const createElementFromDSL = __m30.createElementFromDSL;
const registerService = __m30.registerService;
const noteDeclarativeTags = __m30.noteDeclarativeTags;
const noteNativeTags = __m30.noteNativeTags;
const store = __m0.store;
const router = __m7.router;
const httpClient = __m62.httpClient;
const snackbarService = __m189.snackbarService;
const setHydrating = __m3.setHydrating;
const hydrateElement = __m3.hydrateElement;
const queueComponents = __m5.queueComponents;
const whenComponentQueueDrained = __m5.whenComponentQueueDrained;
const A11Y_DEFAULT_STRINGS = __m70.A11Y_DEFAULT_STRINGS;
const flowEngine = __m190.flowEngine;
const configureUnregistered = __m4.configureUnregistered;
const recoverUnregistered = __m4.recoverUnregistered;
const observeUnregistered = __m4.observeUnregistered;
// Register services for component access via this.services.name
registerService('snackbar', snackbarService);

// A persistent element rendered from `layout`/`overlays` is already in the DOM
// after SSR, so hydration does not re-render it. Its PROPS were never applied
// on the client, though — they only ever existed in the def — so the moment the
// component re-rendered from its own props (a projection, a prop-change
// re-render) it rendered empty. A site's header and footer would arrive
// complete from the server and then blank themselves on hydration, which looks
// like the server is broken and is not.
//
// Applying the def's props to the adopted element closes that: same values the
// server used, so the re-render is a no-op rather than a wipe.
const camel = (name) => name.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

// The server-rendered element a persistent def was adopted as, or null.
function adoptedPersistent(def, parent) {
  if (!Array.isArray(def) || typeof def[0] !== 'string') return null;
  return parent.querySelector(def[0].split(/[#.]/)[0]);
}

function applyPersistentProps(def, parent) {
  const el = adoptedPersistent(def, parent);
  if (!el) return;
  const assign = (name, value) => {
    if (!name || value === undefined) return;
    try { el[name] = value; } catch { /* a read-only field is the def's problem, not hydration's */ }
  };
  for (const token of def.slice(1)) {
    if (token && typeof token === 'object' && !Array.isArray(token)) {
      for (const [key, value] of Object.entries(token)) {
        if (key[0] === ':') assign(camel(key.slice(1)), value);
      }
    } else if (typeof token === 'string' && token[0] === ':') {
      const eq = token.indexOf('=');
      if (eq > 1) assign(camel(token.slice(1, eq)), token.slice(eq + 1));
    }
  }
}

function CreateApp(config) {
  // ── Component registries ──
  // Configured FIRST: a tag can reach the DOM on the very first render, and
  // the recovery has to already know where to ask about it.
  //
  //   registries: [{ name: 'acme', url: 'https://ui.acme.com/swc/components',
  //                  prefix: 'acme-' }]          browser asks it directly
  //   registries: [{ name: 'pro', prefix: 'pro-', via: 'server' }]
  //                                              browser asks OUR server, which
  //                                              holds the credential
  //
  // A prefix means the registry OWNS that namespace; a registry without one is
  // asked about every missing tag, alongside this app's own server. Credentials
  // belong in swc.config.js (components.registries[].auth.env), never here —
  // core/core/unregistered.js drops one found in this config rather than
  // publishing it to every visitor.
  if (config.registries || config.components) {
    // Top-level `registries` is the documented spelling, so it wins; the one
    // under `components` is there for a config that keeps everything together.
    const opts = { ...(config.components || {}) };
    if (config.registries) opts.registries = config.registries;
    configureUnregistered(opts);
  }

  // ── SSR Hydration Detection ──
  const ssrRoot = typeof document !== 'undefined' && document.querySelector
    ? document.querySelector('[data-swc-ssr]')
    : null;
  const hydrating = !!ssrRoot;

  if (hydrating) {
    setHydrating(true);

    // Parse embedded SSR state and hydrate the store
    const stateEl = document.getElementById('swc-ssr-state');
    if (stateEl) {
      try {
        const ssrState = JSON.parse(stateEl.textContent);
        for (const [key, value] of Object.entries(ssrState)) {
          store.setValue(key, value);
        }
      } catch (e) {
        console.warn('[SSR Hydration] Failed to parse SSR state', e);
      }
      stateEl.remove();
    }

    // Walk SSR'd DOM to set _storeKey/_boundStoreRef on elements BEFORE
    // components are defined — define() triggers synchronous upgrade and
    // connectedCallback, which needs these values already in place.
    hydrateElement(ssrRoot, store);
  }

  if (config.defineComponents) {
    // Classified first, registered second — see noteDeclarativeTags: hydration
    // asks whether a tag's children are the DSL's to adopt, and the answer must
    // not depend on where in this array the tag happens to sit. Classification
    // stays inline even when registration is queued below: it is cheap, and an
    // answer that arrives a task late is a wrong answer.
    noteDeclarativeTags(config.defineComponents);
    // Same reasoning for native tags: a redact mask's target must not depend
    // on registration order either — see noteNativeTags.
    noteNativeTags(config.defineComponents);

    // Hydrating: the page is already on screen and the router adopts it as-is,
    // so nothing below needs these tags defined. Registering them in sliced
    // tasks instead of one keeps the upgrade of a server-rendered document off
    // the critical 50ms-per-task budget. Order is preserved exactly, and each
    // def still upgrades with the hydration flag set — see register-queue.js.
    // Client-rendered boot renders from the DSL right after this, so there it
    // stays synchronous.
    if (hydrating) queueComponents(config.defineComponents, { register: CreateComponent, hydrating: true });
    else config.defineComponents.forEach(CreateComponent);
  }

  // Create app encapsulator element: body > <app-tag> > outlet + persistent elements
  const appTag = config.tag || 'swc-app';
  let appEl;

  if (hydrating) {
    // Adopt existing SSR'd app element
    appEl = ssrRoot;
    appEl.removeAttribute('data-swc-ssr');
    // Register the tag if not already defined (browser will upgrade existing element)
    if (!customElements.get(appTag)) {
      customElements.define(appTag, class extends HTMLElement {});
    }
  } else {
    if (!customElements.get(appTag)) {
      customElements.define(appTag, class extends HTMLElement {});
    }
    appEl = document.createElement(appTag);
    document.body.appendChild(appEl);
  }

  // Configure store
  if (config.appId) {
    store.configure({
      appId: config.appId,
      batch: true,
      persist: config.persist,
      session: config.session
    });
  }

  // Register HTTP services
  if (config.services) {
    config.services.forEach(s => httpClient.addServices(s));
  }

  // Register declarative flows AFTER services (screen loads reference
  // endpoints by name) and BEFORE router.init below — the generated routes
  // (guards attached) join the authored table so one table serves both.
  if (Array.isArray(config.flows) && config.flows.length) {
    flowEngine.router = router;
    flowEngine.httpClient = httpClient;
    const flowRoutes = flowEngine.addFlows(...config.flows);
    if (flowRoutes.length) {
      config.routes = [...(config.routes || []), ...flowRoutes];
    }
  }

  // Set initial store state (skip during hydration — SSR state already loaded,
  // including the a11y defaults the server seeded). App state overrides the
  // framework's default accessible-name strings.
  if (!hydrating) {
    Object.entries({ ...A11Y_DEFAULT_STRINGS, ...(config.state || {}) })
      .forEach(([k, v]) => store.setValue(k, v));
  }

  // Theme support — bind store key to app-scoped class
  if (config.theme) {
    store.subscribe('theme', v => {
      const classes = [...appEl.classList].filter(c => c.startsWith('theme-'));
      classes.forEach(c => appEl.classList.remove(c));
      if (v) appEl.classList.add(`theme-${v}`);
    });
  }

  // Locale support — like theme but sets dir and lang attributes
  if (config.locale) {
    const localeConfig = config.locale;
    const RTL_LANGS = ['ar', 'he', 'fa', 'ur', 'ps', 'sd'];

    // Subscribe to locale changes — set dir/lang on app element + persist
    store.subscribe('locale', (localeCode) => {
      if (!localeCode) return;
      const lang = localeCode.split('-')[0].toLowerCase();
      const dir = RTL_LANGS.includes(lang) ? 'rtl' : 'ltr';
      for (const el of [appEl, document.documentElement]) {
        el.setAttribute('dir', dir);
        el.setAttribute('lang', lang);
      }

      // Persist preference
      if (localeConfig.persist) {
        try { localStorage.setItem(`swc4:${config.appId}:locale`, localeCode); } catch {}
      }
    });

    // If urlPattern uses country+lang, derive the combined 'locale' store key.
    // BCP 47 order — language first ('en-us'), not URL order ('us-en') — so the
    // document lang attribute derived from it is valid.
    if (localeConfig.urlPattern && localeConfig.urlPattern.includes(':country') && localeConfig.urlPattern.includes(':lang')) {
      const deriveLocale = () => {
        const country = store.getValue('country');
        const lang = store.getValue('lang');
        if (country && lang) {
          const derived = `${lang}-${country}`;
          if (store.getValue('locale') !== derived) {
            store.setValue('locale', derived);
          }
        }
      };
      store.subscribe('country', deriveLocale);
      store.subscribe('lang', deriveLocale);
    }

    // Resolve initial locale (skip during hydration — SSR already resolved it)
    if (!hydrating) {
      let initialLocale = localeConfig.default || 'en';

      // Check saved preference
      if (localeConfig.persist) {
        try {
          const saved = localStorage.getItem(`swc4:${config.appId}:locale`);
          if (saved && (!localeConfig.supported || localeConfig.supported.includes(saved))) {
            initialLocale = saved;
          }
        } catch {}
      }

      // Try browser language if still on default
      if (initialLocale === (localeConfig.default || 'en') && typeof navigator !== 'undefined') {
        const browserLang = navigator.language?.toLowerCase();
        if (browserLang && localeConfig.supported) {
          const exact = localeConfig.supported.find(s => s === browserLang);
          const partial = localeConfig.supported.find(s => s.startsWith(browserLang.split('-')[0]));
          if (exact) initialLocale = exact;
          else if (partial) initialLocale = partial;
        }
      }

      // Set initial locale (triggers the subscription above)
      store.setValue('locale', initialLocale);

      // If urlPattern uses country+lang, also set the initial split values.
      // Locale codes are BCP 47 (language-country), so lang is the first part.
      if (localeConfig.urlPattern && localeConfig.urlPattern.includes(':country') && localeConfig.urlPattern.includes(':lang')) {
        const parts = initialLocale.split('-');
        if (parts.length >= 2) {
          store.setValue('lang', parts[0]);
          store.setValue('country', parts[1]);
        }
      }
    }
  }

  // Persistent layout elements. With routes, the ROUTER owns them: a route's
  // frame (core/services/route-table.js) decides whether the default chrome
  // is around the outlet, so the router renders it on the way into a default
  // route and takes it down on the way into a framed or bare one, and the
  // client agrees with the server on every navigation, not only the first
  // paint. Without routes there is nothing to decide and they render here.
  // During hydration the elements are already in the DOM, so only their props
  // are applied (see the note on applyPersistentProps — without this the
  // SSR'd chrome blanks itself).
  const routerOwnsChrome = Boolean(config.routes);
  const adopted = (defs) => (defs || []).map((def) => adoptedPersistent(def, appEl)).filter(Boolean);
  if (config.layout) {
    config.layout.forEach(def => {
      if (hydrating) applyPersistentProps(def, appEl);
      else if (!routerOwnsChrome) renderDSL(def, store, appEl);
    });
  }

  // Restore auth tokens synchronously so route guards can check isAuthenticated
  httpClient.restoreTokens();

  // Set up router — use a nested outlet so appEl can host persistent elements
  if (config.routes) {
    const outletTag = `${appTag}-outlet`;
    if (!customElements.get(outletTag)) {
      customElements.define(outletTag, class extends HTMLElement {});
    }
    let outletEl;
    if (hydrating) {
      // Adopt existing SSR'd outlet
      outletEl = appEl.querySelector(outletTag);
    }
    if (!outletEl) {
      outletEl = document.createElement(outletTag);
      outletEl.setAttribute('role', 'main');
      appEl.appendChild(outletEl);
    }
    // The default chrome, as the router needs it: the defs, where they go,
    // how to render one, and which elements are already there (hydration
    // adopted them; a framed or bare first paint had none).
    const chrome = {
      layout: config.layout || [], overlays: config.overlays || [], host: appEl,
      render: (def) => renderDSL(def, store, null),
      mounted: { layout: hydrating ? adopted(config.layout) : [], overlays: hydrating ? adopted(config.overlays) : [] },
    };
    const routerConfig = { routes: config.routes, basePath: config.basePath, chrome };
    if (config.locale?.urlPattern) {
      routerConfig.urlPattern = config.locale.urlPattern;
      // Lets the router validate a stripped segment instead of adopting any
      // first segment as the locale.
      routerConfig.localeValues = config.locale.supported;
    }
    if (hydrating) routerConfig.hydrating = true;
    router.init(routerConfig, outletEl);

    // Subscribe to i18n store keys to update URL when locale changes
    const i18nKeys = router.getI18nStoreKeys();
    if (i18nKeys.length > 0) {
      for (const key of i18nKeys) {
        store.subscribe(key, () => {
          // updateLocaleUrl checks _i18nSyncing internally to avoid loops
          router.updateLocaleUrl();
        });
      }
    }
  }

  // Auth boot — restore tokens and validate sessions
  if (config.services) {
    Promise.resolve().then(async () => {
      for (const s of config.services) {
        const [serviceName, , , options] = s;
        if (options?.auth) {
          const ok = await httpClient.initAuth(serviceName);
          store.setValue('isAuthenticated', ok);
          if (!ok && options.auth.onUnauthorized) {
            router.navigateTo(options.auth.onUnauthorized);
          }
        }
      }
    });
  }

  // Locale manifest — auto-fetch and wire i18n routing
  if (config.locale?.manifestUrl) {
    const manifestServiceName = '_i18n';
    const manifestUrl = config.locale.manifestUrl;

    // Register service if not already in config.services
    httpClient.addServices([manifestServiceName, '', [
      { name: 'getManifest', url: manifestUrl, method: 'GET', resultStoreKey: 'i18nManifest' },
    ]]);

    // Wire manifest → router + locales store
    store.subscribe('i18nManifest', (manifest) => {
      if (!manifest) return;
      router.loadI18n(manifest);
      if (manifest.locales) {
        store.setValue('i18nLocales', manifest.locales.map(l => ({ ...l, text: l.name, value: l.code })));
      }
    });

    // Fetch on boot
    const svc = httpClient.getService(manifestServiceName);
    if (svc?.getManifest) svc.getManifest();
  }

  // Overlays are persistent in the same way, and blank themselves in the same
  // way without their props.
  if (config.overlays) {
    config.overlays.forEach(def => {
      if (hydrating) applyPersistentProps(def, appEl);
      else if (!routerOwnsChrome) renderDSL(def, store, appEl);
    });
  }

  // End hydration mode
  if (hydrating) {
    setHydrating(false);
  }

  // ── Unregistered-tag recovery, for everything the creation sites miss ──
  // The hooks in list-mixin and the router catch a tag before its element
  // exists, which is only possible where the framework does the creating. This
  // catches the rest — authored HTML, a DSL tag that came from data, an
  // element some other script inserted. A late definition upgrades an element
  // already in the DOM, so finding one after the fact costs nothing.
  // `components.observe: false` turns both off — observeUnregistered checks it.
  // The sweep waits for the register queue: while sliced registration is in
  // flight, a server-rendered element whose slice has not landed yet would
  // read as a missing component and be sent to recovery it does not need.
  // With nothing queued this resolves immediately.
  whenComponentQueueDrained().then(() => {
    recoverUnregistered(document);
    observeUnregistered();
  });

  return appEl;
}
return { CreateApp };
})();

const __m192 = (() => {
// SWC 4.0 Framework
return { store: __m0.store, Router: __m7.Router, router: __m7.router, matchUrlToRoute: __m7.matchUrlToRoute, matchRouteTable: __m7.matchRouteTable, routeSpecificity: __m7.routeSpecificity, compareRouteSpecificity: __m7.compareRouteSpecificity, httpClient: __m62.httpClient, CreateApp: __m191.CreateApp, CreateComponent: __m30.CreateComponent, createElementFromDSL: __m30.createElementFromDSL, renderDSL: __m30.renderDSL, announce: __m63.announce };
})();

const __m193 = (() => {
// Shared shell mechanics (docs/composite-refs/shells.md). Shells are persistent
// layout elements rendered beside the router outlet (`<apptag>-outlet[role=main]`);
// each shell's CSS drives the app-level grid via `body > :has(> <tag>)`, so no
// app-tag coupling. This module holds the route-aware nav projection and the
// brand row.
//
// The nav is s-tgb-g — THE selectable button row — with the `links` decoration.
// It used to be a bespoke s-nav-l / s-nav-i pair defined right here, which is a
// second implementation of a pattern the library already owns: an exclusive
// selectable row with one active member. The group tracks the active route as
// its VALUE, so "which item is highlighted" is selection state rather than an
// `active` attribute projected onto rows by hand.

// The nav row for a shell. `axis` is the stack vocabulary: v for the sidebar's
// column, h for the topbar and marketing bars.
function navDSL(axis) {
  // @optional=: the current page is often not IN the nav — a product detail, a
  // contact page, the homepage. Without it the row would adopt its first item
  // and the shell would navigate there, so every such page bounced.
  return ['s-tgb-g', '@v=links', `@l=${axis}`, '@optional=', '@rg=nav', '~ch:call:_onNav:$detail.value', '$nav'];
}

// Rows for the selectable row: the route IS the option value, so the active item
// is whichever value matches the current route — no per-row attribute pass.
function projectNav(self) {
  const route = self.store.getValue('route');
  self.setData('nav', (self.nav || []).map((item) => ({
    text: String(item.label ?? ''),
    value: item.route,
    selected: item.route === route,
  })));
}

// Selecting a nav option navigates. The row owns the highlight; the router owns
// the address.
function onNavSelect(self, route) {
  if (route && route !== self.store.getValue('route')) self.router.navigateTo(route);
}

// Subscribe the shell to route changes (registered in _subs so the lifecycle
// pauses/cleans it like any DSL-created subscription).
function watchRoute(self) {
  const sub = self.store.subscribe('route', () => projectNav(self));
  self._subs.push({ sub });
}

function brandDSL() {
  return ['s-cn', '@rg=brand', [
    ['s-ic', '?prop:brandIcon', '#brandIcon:n'],
    ['s-l', '@rg=brand-label', '#brandLabel'],
  ]];
}

// Shared nav styling, scoped per shell tag.
return { navDSL, projectNav, onNavSelect, watchRoute, brandDSL };
})();

const __m194 = (() => {
const navDSL = __m193.navDSL;
const onNavSelect = __m193.onNavSelect;
const brandDSL = __m193.brandDSL;
// composite: shell-marketing (s-shm). References (docs/composite-refs/shells.md):
//   Tailwind UI marketing headers/footers, shadcn marketing blocks. One component
//   with display:contents so its header (grid row 1) and footer (row 3) wrap the
//   routed outlet (row 2). Primitives: s-hd, s-ft, s-cn, s-nv, s-ic, s-l, s-b, s-ln
//   + s-tgb-g for the nav row.



const meta = {
  id: 'shell-marketing',
  tag: 's-shm',
  group: 'shell',
  variants: ['default'],
  models: ['*'],
  requiresRoles: [],
  optionalRoles: [],
  requires: ['s-hd', 's-ft', 's-cn', 's-nv', 's-ic', 's-l', 's-b', 's-ln', 's-tgb-g'],
  contract: {
    nav: { type: 'action[]', required: true },
    brand: { type: 'slot', required: true },
    cta: { type: 'action', required: false }, // header button { label, route }
    footer: { type: 'slot', required: false }, // { columns: [{ title, links: [{label, route}] }], legal }
  },
  slots: ['header', 'main', 'footer'],
  theme: ['--c-surface', '--c-border', '--c-text-secondary', '--space-5', '--radius-field'],
};

const def = {
  tag: meta.tag,
  // `route` is a declared prop rather than a store read inside the projection:
  // a projection is pure, so the address has to be an input to it. It is also
  // load-bearing — a nav rendered with NO row selected makes the selectable row
  // adopt its first item, which fires a change, which navigates the app to that
  // first item. A shell that silently sends every deep link back to the
  // homepage is the worst kind of bug: it looks like the link was wrong.
  props: ['nav', 'brandLabel', 'brandIcon', 'ctaLabel', 'ctaRoute', 'footer', 'legal', 'route'],
  children: [
    ['s-skip'],
    ['s-hd', [
      brandDSL(),
      ['s-nv', [navDSL('h')]],
      ['s-b', '?prop:ctaLabel', '@c=pr', '~cl:call:_onCta', [['span', '#ctaLabel']]],
    ]],
    ['s-ft', [
      ['s-cn', '@rg=foot-cols', ':dataKey=footCols', ':dataField=children'],
      ['s-l', '?prop:legal', '@rg=legal', '#legal'],
    ]],
  ],
  // Nav rows and footer columns are a pure function of the props, so the
  // serializer produces the same ones the client does. A marketing shell whose
  // navigation only exists after hydration is a site with no internal links as
  // far as a crawler is concerned.
  project({ nav, footer, route }) {
    const cols = (footer && footer.columns) || [];
    return {
      nav: (nav || []).map((item) => ({
        text: String(item.label ?? ''),
        value: item.route,
        selected: item.route === route,
      })),
      footCols: cols.map((col) => (
        ['s-cn', '@rg=foot-col', [
          ['s-l', '@rg=foot-title', [String(col.title ?? '')]],
          ...(col.links || []).map((l) => ['s-ln', `~cl:nav:${l.route}`, [String(l.label)]]),
        ]]
      )),
    };
  },
  callbacks: {
    // The address is client knowledge, so it arrives as a prop write: the
    // projection re-runs and the highlight follows the route without the
    // projection ever reaching into the store itself.
    connected() {
      this.route = this.store.getValue('route') || this.route || '';
      const sub = this.store.subscribe('route', (value) => { this.route = value || ''; });
      this._subs.push({ sub });
    },
  },
  methods: {
    _onNav(route) { onNavSelect(this, route); },
    _onCta() {
      if (this.ctaRoute) this.router.navigateTo(this.ctaRoute);
    },
  },
};

function bind(b) {
  const props = {
    ':nav': b.nav,
    ':brandLabel': b.brand && b.brand.label,
  };
  if (b.brand && b.brand.icon) props[':brandIcon'] = b.brand.icon;
  if (b.cta) { props[':ctaLabel'] = b.cta.label; props[':ctaRoute'] = b.cta.route; }
  // A server render knows the address; passing it makes the highlight correct
  // in the first paint rather than after hydration.
  if (b.route) props[':route'] = b.route;
  if (b.footer) { props[':footer'] = b.footer; if (b.footer.legal) props[':legal'] = b.footer.legal; }
  return ['s-shm', props];
}

const defs = [def];
return { meta, def, bind, defs };
})();

const __m195 = (() => {
const applyInfiniteScroll = __m12.applyInfiniteScroll;
const hasInfiniteScroll = __m12.hasInfiniteScroll;
const removeInfiniteScroll = __m12.removeInfiniteScroll;
// Shared collection topology (composite-system.md; brief: "Collections share one
// topology"). Every collection composite is toolbar → body → footer with the same
// states (loading skeleton, error alert, empty, filtered); only the body
// representation and the row projection differ. This module is the single place
// that topology lives: collectionComposite(cfg) assembles meta/def/bind for one
// collection screen from its body config, and itemComponent() builds the custom-
// render item components (the pattern the framework's own s-dg-c cell uses).

// The framework's own sentinel/observer pair, reused rather than re-written: it
// picks the scroll root, survives replaceChildren, and create-component's
// disconnectedCallback already tears down whatever it attached to an element.
// Import-safe on the server — the module body only allocates a WeakMap, and the
// SSR loader stubs IntersectionObserver for the call sites.


const BASE_PROPS = [
  'title', 'searchLabel', 'createLabel', 'emptyLabel', 'moreLabel', 'endLabel',
  'loading', 'error', 'total', 'page', 'pageSize',
  'searchKey', 'pageKey', 'createRoute', 'rowRoute', 'rowKey', 'map', 'selectKey',
  'empty', 'showBody', 'paged', 'hasMore', 'endless', 'showMore', 'loadingMore', 'atEnd',
];

const BASE_STORE_KEYS = ['rows', 'total', 'loading', 'error', 'query', 'page'];

// Relative time for feed/calendar item lines ("3h ago"; broad indicator, not exact).
function relTime(value) {
  const t = value instanceof Date ? value.getTime() : Date.parse(value);
  if (Number.isNaN(t)) return String(value ?? '');
  const s = Math.round((Date.now() - t) / 1000);
  const abs = Math.abs(s);
  const units = [[31536000, 'y'], [2592000, 'mo'], [604800, 'w'], [86400, 'd'], [3600, 'h'], [60, 'm']];
  const hit = units.find(([sec]) => abs >= sec);
  if (!hit) return 'now';
  const n = Math.round(abs / hit[0]);
  return s >= 0 ? `${n}${hit[1]} ago` : `in ${n}${hit[1]}`;
}

// Unwrap a bound data value into its rows. Client-side the topology's data
// setter already unwrapped the envelope; SSR passes the raw store value — this
// helper makes a PURE project(props) see the same rows either way.
function rowsOf(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') return data.data || data.rows || [];
  return [];
}

// Map one row through the role→slot field map — the pure core of _mapRow, so
// declarative project(props) functions share it instead of re-implementing.
function mapRow(row, map, rowKey) {
  const out = { _raw: row, id: row[rowKey || 'id'] };
  Object.entries(map || {}).forEach(([slot, field]) => { out[slot] = row[field]; });
  return out;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function shortDate(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value ?? '');
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// One button that announces itself (the s-soc-a pattern, shared): a customized
// built-in on HTMLButtonElement, so it keeps native focus, Enter/Space and the
// button role. It carries `act` (what) and `payload` (about which thing) as
// PROPERTIES and emits a bubbling `itemaction` whose detail.item IS this
// element — the composite above reads both straight off that object, and
// nothing ever asks the DOM which control was pressed. Its click stops at the
// button: these controls sit inside items that are themselves controls, and an
// action press must not double as an item open.
const actionButton = {
  tag: 's-act-b',
  baseElement: 'HTMLButtonElement',
  props: ['act', 'payload'],
  emits: { click: 'itemaction' },
  listeners: { click: (e) => e.stopPropagation() },
  callbacks: {
    connected() { if (!this.hasAttribute('type')) this.setAttribute('type', 'button'); },
  },
};

// A custom-render item component: set data → rebuild children from toDSL(item).
// Same mechanism as the framework's s-dg-c cell; gives per-item conditionals
// (optional media/avatar/badge) that static ^field templates cannot express.
//
// The item hands itself over: by default a click announces `itemopen`, whose
// detail.item IS the element (its _data is the projected row) — the composite
// never walks the DOM to work out which item was acted on. A body whose items
// are not controls passes `emits: {}`; one with a distinct event (the kanban
// card's `cardopen`) passes its own. Distinct names matter: `itemopen` from a
// nested composite's items would land in an ancestor collection's handler.
// `extra.methods` merges rather than being overwritten: an item whose row
// carries controls (a cart line's steppers) wires them with ~cl:call:, which
// resolves against the ITEM — that is how a control announces its action
// without a data-* coordinate or a closest() lookup. The factory's own data
// accessors are defined last so a caller cannot displace them.
function itemComponent(tag, toDSL, extra = {}) {
  const { methods: extraMethods, ...rest } = extra;
  return {
    tag,
    emits: { click: 'itemopen' },
    // The same children the data setter below builds, for the server.
    // toDSL is already a PURE function of one row, so the two sides cannot
    // disagree — this hook is the setter's body with the imperative half
    // removed. Without it the serializer stamped an item element per row and
    // left every one of them empty: a board with its lanes and no cards, a
    // catalogue with its grid and no products. The rows were the part a
    // crawler and a first paint never got, which is the wrong half to lose.
    ssr: ({ data }) => (data === null || data === undefined ? [] : toDSL(data).filter(Boolean)),
    ...rest,
    methods: {
      ...extraMethods,
      set data(item) {
        this._data = item;
        this.replaceChildren();
        if (item !== null && item !== undefined) {
          this._applyChildElements(toDSL(item).filter(Boolean), this);
        }
      },
      get data() { return this._data; },
    },
  };
}

function toolbarDSL() {
  return ['s-cn', '@rg=toolbar', [
    ['h2', '?prop:title', '#title'],
    ['s-i', '@type=search', '?prop:searchKey', '#searchLabel:placeholder', '~in:call:_onSearch'],
    ['s-b', '?prop:createLabel', '@c=pr', '~cl:call:_onCreate', [
      ['s-ic', '@n=pl'],
      ['span', '#createLabel'],
    ]],
  ]];
}

const SKELETON_ROWS = 5;
function defaultSkeleton() {
  return ['s-cn', '?prop:loading', '@rg=skeleton', [
    ...Array.from({ length: SKELETON_ROWS }, () => ['s-sk']),
  ]];
}

// Endless-scroll affordances. They ride EVERY footer mode because `endless` is a
// binding flag, not a per-composite constant: the same body is paged in a back
// office and endless on a phone, and only the binding knows which. The sentinel
// the observer watches is appended by applyInfiniteScroll to the composite
// element itself (see _setupEndless) — it must sit outside the body, whose
// children the list mixin rebuilds on every data change.
function endlessDSL() {
  return [
    ['s-cn', '?prop:loadingMore', '@rg=more-loading', '@aria-live=polite', [['s-sk'], ['s-sk']]],
    ['s-cn', '?prop:atEnd', '@rg=more-end', [['s-l', '#endLabel']]],
  ];
}

function footerDSL(footer) {
  if (footer === 'pager') {
    return [['s-pg', '?prop:paged', '#page:current', '#total:total', '#pageSize:pageSize'], ...endlessDSL()];
  }
  if (footer === 'more') {
    return [
      // showMore, not hasMore: the button is what endless mode replaces, while
      // hasMore stays the truth the sentinel reads.
      ['s-cn', '?prop:showMore', '@rg=more', [
        ['s-b', '@v=ol', '~cl:call:_onMore', [['span', '#moreLabel']]],
      ]],
      ...endlessDSL(),
    ];
  }
  return endlessDSL();
}


// Assemble one collection composite from its body config. cfg:
//   id, tag, models, requiresRoles, optionalRoles, requires, internalTags,
//   regions, theme, variants          — meta fields (must match screen-catalog.json)
//   contractExtras                    — extra typed slots beyond the shared base
//   storeExtras                       — extra store keys beyond the shared base
//   body (DSL[]), skeleton?  — the body region (styles live in core/styles/composites.css)
//   footer: 'pager' | 'more' | 'none'
//   extraProps, project?, onItemOpen?, connected?, methods?, listeners?  — behaviour hooks
//   bindExtras?(binding, props)       — instance props beyond the shared base
function collectionComposite(cfg) {
  // cfg.project comes in two shapes during the declarative migration:
  //   project(props) — PURE, arity ≥ 1 — rides the core's declarative seam
  //     (def.project): auto-run on connect/prop change AND run by SSR, so the
  //     body serializes server-side.
  //   project()      — legacy imperative, arity 0, `this`-bound — stays a
  //     methods._project override until its composite migrates.
  const declarativeProject = typeof cfg.project === 'function' && cfg.project.length > 0 ? cfg.project : null;
  const imperativeProject = !declarativeProject && typeof cfg.project === 'function' ? cfg.project : null;

  // `showBody` derived where BOTH runtimes can see it.
  //
  // _recompute() computes it from onLoadingChange/onErrorChange/onTotalChange
  // and the data setter — runtime callbacks, none of which the serializer runs.
  // So the flag was undefined at serialization and `?prop:showBody` hid the
  // body on every server render: a collection composite sent its toolbar, its
  // drawer chrome and its empty conditionals, and none of its rows. The content
  // was the one part that waited for the bundle, which is backwards.
  //
  // It is purely a function of data + loading + error, so project() — which the
  // core auto-runs on connect, prop change and data arrival, and which SSR runs
  // itself — can derive it, and _runProject feeds the result through setData so
  // `?data:showBody` reads it on both sides. `empty` deliberately stays a prop:
  // it needs `_loaded` ("has data ever arrived"), which is instance state no
  // pure function can see, and an empty state asserted before the first load
  // would be a lie rather than a race.
  const derived = (props) => {
    const rows = rowsOf(props.data);
    const blocked = Boolean(props.loading) || Boolean(props.error);
    const out = { showBody: !blocked && rows.length > 0 };
    // `items` too, but ONLY where the topology's own default _project would
    // have produced it — the same `rows.map(_mapRow)` it does, which is pure
    // (map and rowKey are declared props). A composite with a project of its
    // own already decides what its body binds, and writing `items` underneath
    // it would be a second author for one key.
    if (!cfg.project) out.items = rows.map((r) => mapRow(r, props.map, props.rowKey));
    return out;
  };
  // Every collection gets it, including the four with no project of their own.
  // A composite's own regions win on a key clash: it knows its screen, and the
  // shared layer is only claiming names the topology already owns.
  const project = (props) => ({ ...derived(props), ...(declarativeProject ? declarativeProject(props) : null) });

  const meta = {
    id: cfg.id,
    tag: cfg.tag,
    group: 'collection',
    variants: cfg.variants || ['default'],
    models: cfg.models,
    requiresRoles: cfg.requiresRoles,
    optionalRoles: cfg.optionalRoles || [],
    requires: cfg.requires,
    internalTags: cfg.internalTags || [],
    contract: {
      entity: { type: 'entity', required: true },
      source: { type: 'endpoint', method: 'GET', required: true },
      map: { type: 'fieldMap', of: 'entity', required: cfg.mapRequired !== false },
      rowRoute: { type: 'route', required: false },
      create: { type: 'action', required: false },
      // Endless scroll instead of the pager/Load-more control. Needs store.page
      // (the sentinel bumps it) and store.total (it tells the body when to stop).
      endless: { type: 'flag', required: false },
      ...(cfg.contractExtras || {}),
      store: { type: 'storeKeys', keys: [...BASE_STORE_KEYS, ...(cfg.storeExtras || [])], required: true },
    },
    regions: cfg.regions,
    theme: cfg.theme || ['--c-border', '--c-text-secondary', '--space-3', '--space-4', '--text-lg', '--radius-box'],
  };

  const footer = cfg.footer || 'none';

  const def = {
    tag: cfg.tag,
    props: [...BASE_PROPS, ...(cfg.extraProps || [])],
    project,
    children: [
      toolbarDSL(),
      ['s-al', '?prop:error', '@type=error', '@showIcon=', [['span', '#error']]],
      cfg.skeleton || defaultSkeleton(),
      ...cfg.body,
      ['s-emp', '?prop:empty', '#emptyLabel:description'],
      ...footerDSL(footer),
    ],
    // Listening is DECLARED (not wired in connected()) so swc-lint can
    // intersect what a composite listens for with what its internals emit —
    // the trap-2 collision class becomes statically checkable. cfg.listeners
    // merges in a composite's own listens the same way.
    listeners: {
      itemopen: '_onItemOpen',
      change: '_onPageChange',
      ...(cfg.listeners || {}),
    },
    callbacks: {
      connected() {
        if (this.pageSize === undefined) this.pageSize = 10;
        if (this.moreLabel === undefined) this.moreLabel = 'Load more';
        if (this.endLabel === undefined) this.endLabel = 'You are all caught up';
        this._recompute();
        this._setupEndless();
        if (cfg.connected) cfg.connected.call(this);
      },
    },
    methods: {
      // :dataSource writes here. Accepts a plain array or an envelope { data|rows, total }.
      set data(v) {
        const envelope = v && typeof v === 'object' && !Array.isArray(v);
        this._loaded = true;
        this._rows = rowsOf(v);
        if (envelope && v.total != null) this.total = v.total;
        // The page the sentinel asked for has landed: release the guard so the
        // next intersection can fire. Without this a slow service would let one
        // scroll queue several pages.
        this._pending = false;
        this._project();
        this._recompute();
        if (this.endless && this._isConnected) this._rearmEndless();
      },
      get data() { return this._rows; },

      // Project raw rows into the body's internal representation. Default: map each
      // row through the role→slot field map into `items`. Bodies with structure
      // (kanban columns, calendar day lists, tree nesting) override via cfg.project —
      // declarative ones re-run the core seam (rows arrive through the data setter
      // above, not a declared prop, so the setter triggers the re-project;
      // coalesced with the prop-cascade triggers on a microtask).
      _project: imperativeProject
        ? function project() {
          const regions = imperativeProject.call(this);
          // Belt-and-braces for the arity heuristic: a PURE project misdeclared
          // with defaulted params (arity 0) still returns its region map —
          // honor it so the body doesn't silently vanish (SSR still needs the
          // arity ≥ 1 form to ride the seam).
          if (regions && typeof regions === 'object' && !Array.isArray(regions)) {
            Object.entries(regions).forEach(([key, value]) => this.setData(key, value));
          }
        }
        : (declarativeProject
          ? function project() { this._scheduleProject(); }
          : function project() {
            this._items = (this._rows || []).map((r) => this._mapRow(r));
            this.setData('items', this._items);
          }),

      _mapRow(row) {
        return mapRow(row, this.map, this.rowKey);
      },

      onLoadingChange() { this._recompute(); },
      onErrorChange() { this._recompute(); },
      onTotalChange() { this._recompute(); },
      onPageSizeChange() { this._recompute(); },

      _recompute() {
        const count = (this._rows || []).length;
        const blocked = Boolean(this.loading) || Boolean(this.error);
        const endless = Boolean(this.endless);
        this.empty = Boolean(this._loaded) && !blocked && count === 0;
        this.showBody = !blocked && count > 0;
        this.paged = !endless && footer === 'pager' && Number(this.total || 0) > Number(this.pageSize || 10);
        // Endless mode reads hasMore whatever the footer constant says: a pager
        // body bound endless still has to know when to stop asking.
        this.hasMore = (endless || footer === 'more') && Number(this.total || 0) > count;
        this.showMore = !endless && this.hasMore;
        this.loadingMore = endless && Boolean(this._pending) && !blocked;
        this.atEnd = endless && Boolean(this._loaded) && !blocked && count > 0 && !this.hasMore;
      },

      _onSearch(e) {
        const value = e && e.target ? e.target.value : '';
        clearTimeout(this._searchT);
        this._searchT = setTimeout(() => {
          if (this.searchKey) this.store.setValue(this.searchKey, value);
          if (this.pageKey) this.store.setValue(this.pageKey, 1);
        }, 250);
      },

      _onCreate() {
        if (this.createRoute) this.router.navigateTo(this.createRoute);
      },

      _onMore() {
        if (this.pageKey) this.store.setValue(this.pageKey, Number(this.page || 1) + 1);
      },

      // ── Endless scroll ──
      // The observer only asks; the service layer answers by APPENDING to the
      // rows key and raising total, exactly as the Load-more button expects.
      // Endless is therefore a presentation choice: no binding that works with
      // the button needs a different back end to scroll instead.
      _setupEndless() {
        if (!this.endless || typeof IntersectionObserver === 'undefined') return;
        if (hasInfiniteScroll(this)) return;
        applyInfiniteScroll(this, () => this._onEndless());
      },

      _teardownEndless() {
        if (hasInfiniteScroll(this)) removeInfiniteScroll(this);
      },

      // An observer reports TRANSITIONS. When the page that just landed is
      // shorter than the gap to the sentinel — short rows, tall viewport — the
      // sentinel never stopped intersecting, so no further callback arrives and
      // the list stalls half-loaded. Re-arming delivers a fresh initial
      // observation of the current state, which fires again if it is still in
      // view; the _pending guard keeps that a stepped loop rather than a flood.
      // Once there is nothing left, the sentinel comes out entirely.
      _rearmEndless() {
        this._teardownEndless();
        if (this.hasMore) this._setupEndless();
      },

      // Guarded on _pending so one long scroll cannot queue several pages, and
      // on loading/error so a failed page does not spin.
      _onEndless() {
        if (!this.endless || this._pending) return;
        if (this.loading || this.error || !this.hasMore || !this.pageKey) return;
        this._pending = true;
        this._recompute();
        this.store.setValue(this.pageKey, Number(this.page || 1) + 1);
      },

      // The flag can arrive after connect (a bound prop, or a viewport switch),
      // so attaching is not connect-only.
      onEndlessChange() {
        if (!this._isConnected) return;
        if (this.endless) this._setupEndless();
        else this._teardownEndless();
        this._recompute();
      },

      // Item open → selection key (split-shell mode) or detail route. The item
      // announced itself (`itemopen` from its emits); its projected row rides on
      // the element as _data. Bodies with different semantics override via
      // cfg.onItemOpen.
      _onItemOpen: cfg.onItemOpen || function onItemOpen(e) {
        if (!this.selectKey && !this.rowRoute) return;
        const item = e.detail && e.detail.item ? e.detail.item._data : null;
        if (!item || item.id === undefined) return;
        if (this.selectKey) { this.store.setValue(this.selectKey, item._raw || item); return; }
        this.router.navigateTo(this.rowRoute.replace(':id', encodeURIComponent(item.id)));
      },

      _onPageChange(e) {
        const tag = e.target && e.target.tagName ? e.target.tagName.toLowerCase() : '';
        if (tag !== 's-pg' || !this.pageKey) return;
        this.store.setValue(this.pageKey, e.detail && e.detail.current ? e.detail.current : 1);
      },

      ...(cfg.methods || {}),
    },
  };

  function bind(b) {
    const s = b.store;
    const label = (b.entity && (b.entity.labelPlural || b.entity.label)) || 'records';
    const props = {
      ':title': b.title || label.charAt(0).toUpperCase() + label.slice(1),
      ':emptyLabel': b.emptyLabel || `No ${label} yet`,
    };
    if (s.query) { props[':searchKey'] = s.query; props[':searchLabel'] = `Search ${label}`; }
    if (s.page) props[':pageKey'] = s.page;
    if (b.map) props[':map'] = b.map;
    if (b.rowRoute) props[':rowRoute'] = b.rowRoute;
    if (b.rowKey) props[':rowKey'] = b.rowKey;
    if (s.selected) props[':selectKey'] = s.selected;
    if (b.endless) {
      props[':endless'] = true;
      if (b.endLabel) props[':endLabel'] = b.endLabel;
    }
    if (b.create) { props[':createLabel'] = b.create.label; props[':createRoute'] = b.create.route; }
    if (cfg.bindExtras) cfg.bindExtras(b, props);
    return [cfg.tag,
      props,
      `:dataSource=${s.rows}`,
      `:loading=$${s.loading}`,
      `:error=$${s.error}`,
      `:total=$${s.total}`,
      ...(s.page ? [`:page=$${s.page}`] : []),
    ];
  }

  return { meta, def, bind };
}
return { relTime, rowsOf, mapRow, shortDate, actionButton, itemComponent, collectionComposite };
})();

const __m196 = (() => {
// The Stop of a chat composite bound to the stream contract (s-cnv and
// s-tpl-chat): one helper writes the global http.cancel command key the HTTP
// client watches (core/services/http-client.js), with the app's selector
// (`cancel`) or the composer's endpoint name (`service`), through the element's
// own bound `store` (the way composites/auth/onboarding-wizard.js writes a
// bound key; composites never import the store module). The client aborts the
// matching requests, settles their statusStoreKey to 'aborted' and clears the
// key. A no-op with nothing to name.
//
//   writeCancelSelector(el) -> the selector written, or null

function writeCancelSelector(el) {
  const selector = el.cancel || el.service;
  if (!selector) return null;
  el.store.setValue('http.cancel', selector);
  return selector;
}
return { writeCancelSelector };
})();

const __m197 = (() => {
const itemComponent = __m195.itemComponent;
const relTime = __m195.relTime;
const writeCancelSelector = __m196.writeCancelSelector;
// composite: conversation (s-cnv). References (docs/composite-refs/utility-comms-commerce-marketing.md):
//   Slack/Linear thread baselines; chat-window behavior modelled on the
//   agent_stack chat (thread + composer + suggestion chips + typing dots).
//   Bubble thread (own right/tinted, others left) + composer (s-f + textarea
//   field + send); submit clears the field. Messages arrive via :dataSource;
//   map routes roles → { content, actor, avatar, date, own, role }.
//   requiresRoles: content + actor + date.
//   Chat-window extensions (all optional, contract stays backward compatible):
//   role-styled rows (user/assistant/system), markdown bodies through s-mkd
//   when `rich`, a `busy` store key driving typing dots + a disabled send,
//   suggestion chips that send on tap, Enter-to-send (Shift+Enter =
//   newline), an auto-growing textarea, and stick-to-latest scrolling with a
//   jump-down pill once the reader scrolls up.
//   Streaming (the AI layer): an optional `partial` store key renders the reply
//   as it arrives in ONE live bubble (a single text node, s-mkd when `rich`),
//   `status` drives a reflected @streaming that swaps Send for Stop and shows
//   the caret, and `cancel` names the http.cancel selector the Stop writes
//   (falling back to `service`). The composer has ONE trigger: either `service`
//   (the s-f calls the endpoint on submit) or `store.submit` (the endpoint
//   declares storeKey); bind() never emits both.
//   Regions (meta.regions): the composite's three PARTS, by the rg= name the
//   markup below carries, so registry.json and the catalogue name what a
//   stylesheet can reach: `thread` (the message list), `suggestions` (the
//   chips), `composer` (the form and the Stop). Every other rg= here is
//   internal plumbing an author has no business targeting and this file may
//   rename: `cnv-wrap`, `cnv-scroll`, `cnv-live`, `cnv-live-text`,
//   `cnv-caret`, `cnv-typing`, `cnv-dot`, `cnv-jump`, `cnv-form`, `cnv-stop`,
//   `skeleton`, and the bubble's parts (`cnv-bubble`, `cnv-actor`,
//   `cnv-text`, `cnv-time`, `cnv-sys`), which belong to the row component
//   s-cnv-i. tests/composite-regions.spec.mjs holds the list to the markup.




// A message bubble is not a control — `emits: {}` keeps it from announcing
// `itemopen`, which would bubble into an ANCESTOR collection's handler when the
// conversation sits inside one (inbox-split's list pane listens for it).
const bubble = itemComponent('s-cnv-i', (m) => (
  m.role === 'system'
    ? [['s-l', '@rg=cnv-sys', [String(m.content ?? '')]]]
    : [
      m.avatar && !m.own ? ['s-av', '@sz=sm', { ':src': m.avatar, ':alt': m.actor || '' }] : null,
      ['s-cn', '@rg=cnv-bubble', [
        !m.own && m.actor ? ['s-l', '@rg=cnv-actor', [String(m.actor)]] : null,
        m.rich
          ? ['s-cn', '@rg=cnv-text', [['s-mkd', { ':md': String(m.content ?? '') }]]]
          : ['s-l', '@rg=cnv-text', [String(m.content ?? '')]],
        m.date ? ['s-l', '@rg=cnv-time', [relTime(m.date)]] : null,
      ].filter(Boolean)],
    ]
), { emits: {} });

const thread = { tag: 's-cnv-b', itemRenderer: 's-cnv-i' };

// Suggestion chips: tapping one SENDS it — a chip is a whole message the
// product wrote, and making the person confirm their own tap is a second
// step with nothing to confirm (owner-called; every mainstream chat agrees).
// While a reply is in flight the tap only fills, so nothing double-sends.
// The chip announces ITSELF (`sugpick`, a distinct name so nothing upstream
// mistakes it for an item open); its _data is the suggestion.
// The item field is `label` for history: the list mixin used to stamp item.text
// over the chip; it now skips text for renderers that build their own children.
const sugChip = itemComponent('s-cnv-sc', (s) => [['s-ch', '@clickable=', [String(s.label ?? '')]]], {
  emits: { click: 'sugpick' },
});
const sugList = { tag: 's-cnv-s', itemRenderer: 's-cnv-sc' };

const TEXTAREA_MAX_PX = 160;

const meta = {
  id: 'conversation',
  tag: 's-cnv',
  group: 'communication',
  variants: ['default'],
  models: ['message'],
  requiresRoles: ['content', 'actor', 'date'],
  optionalRoles: ['media'],
  requires: ['s-cn', 's-av', 's-l', 's-f', 's-taif', 's-b', 's-ic', 's-ch', 's-mkd', 's-emp', 's-sk', 's-al'],
  internalTags: ['s-cnv-b', 's-cnv-i', 's-cnv-s', 's-cnv-sc'],
  contract: {
    entity: { type: 'entity', required: true },
    source: { type: 'endpoint', method: 'GET', required: true },
    map: { type: 'fieldMap', of: 'entity', required: true }, // content/actor/avatar/date/own/role
    // ONE trigger for the composer: either `service` (the s-f calls the endpoint
    // on submit) or `store.submit` (the app's endpoint declares storeKey), never
    // both. Both real consumers are submit-only, so `service` is optional.
    service: { type: 'endpoint', method: 'POST', required: false }, // send
    rich: { type: 'flag', required: false }, // markdown bodies via s-mkd
    // `partial` and `status` are the stream keys of the endpoint that answers
    // (its textKey and statusStoreKey); both optional.
    store: { type: 'storeKeys', keys: ['rows', 'submit', 'loading', 'error', 'busy', 'suggestions', 'partial', 'status'], required: true },
  },
  regions: ['thread', 'suggestions', 'composer'],
  theme: ['--c-border', '--c-primary-light', '--radius-box', '--space-3'],
};

const def = {
  tag: meta.tag,
  // Composer shell styles live HERE, not in composites.css: component DEF
  // css (this included, and the field's own) is injected unlayered by
  // applyCSS/SSR, and unlayered beats every @layer — so a layered stylesheet
  // can never override the field's def borders. Same precedence class, later
  // registration, higher specificity: the shell wins deterministically.
  css: `
s-cnv form[is=s-f] { display: flex; align-items: flex-end; gap: var(--space-1, 4px); width: 100%; border: 1px solid var(--c-border); border-radius: var(--radius-box, 12px); background: var(--c-surface); padding: 4px 4px 4px 8px; transition: border-color 0.15s; }
s-cnv form[is=s-f]:focus-within { border-color: color-mix(in oklab, var(--c-primary) 45%, var(--c-border)); }
s-cnv form[is=s-f] > s-cn:empty { display: none; }
s-cnv form[is=s-f] s-taif { flex: 1; min-width: 0; }
s-cnv form[is=s-f] s-taif label[is=s-l]:empty { display: none; }
s-cnv form[is=s-f] s-taif textarea[is=s-ta] { width: 100%; border: 0; outline: none; box-shadow: none; background: transparent; resize: none; padding: 8px 6px; font-size: 0.875rem; line-height: 1.5; min-height: 38px; max-height: 160px; }
s-cnv form[is=s-f] s-taif textarea[is=s-ta]:focus, s-cnv form[is=s-f] s-taif textarea[is=s-ta]:focus-visible { border: 0; outline: none; box-shadow: none; }
s-cnv form[is=s-f] > button[is=s-b] { width: 34px; height: 34px; min-width: 0; padding: 0; flex: 0 0 auto; border-radius: var(--radius-md, 8px); margin-bottom: 3px; }
/* Armed like the house template: an empty composer mutes its send — a live
   button over nothing is a dead tap. :placeholder-shown is emptiness in
   pure CSS; Enter on empty was already a no-op. */
s-cnv form[is=s-f]:has(textarea:placeholder-shown) > button[is=s-b] { opacity: 0.45; pointer-events: none; }
s-cnv form[is=s-f] > button[is=s-b] s-ic { width: 16px; height: 16px; }
`,

  props: ['map', 'service', 'storeKey', 'loading', 'error', 'empty', 'ready', 'placeholder', 'busy', 'rich', 'suggestions', 'hasSugs', 'jump', 'partial', 'status', 'cancel', '@streaming'],
  children: [
    ['s-al', '?prop:error', '@type=error', '@showIcon=', [['span', '#error']]],
    ['s-cn', '?prop:loading', '@rg=skeleton', [['s-sk'], ['s-sk'], ['s-sk']]],
    // Nested conditionals ride INTERNAL store keys (?data: + setData): prop
    // show-ifs only re-evaluate at the top level of a def's children, while
    // store-driven ones subscribe and re-evaluate anywhere in the tree. The
    // jump pill overlays the wrapper (absolute), NOT the scroll container:
    // inside it, any positioning scheme scrolls away with the content.
    ['s-cn', '@rg=cnv-wrap', [
      ['s-cn', ':ref=scroll', '@rg=cnv-scroll', [
        ['s-cnv-b', '?data:cnvReady', '@rg=thread', '$items'],
        // The live bubble: the streamed reply bound to ONE text node (an s-l,
        // or an s-mkd when `rich`) plus a caret the stylesheet shows only
        // under [streaming]. It appears with the first delta and leaves when
        // the app's transformFn resets the partial key on done; after an
        // abort or an error the retained text stays, caret gone.
        ['s-cn', '?data:cnvLive', '@rg=cnv-live', [
          ['s-cn', '@rg=cnv-bubble', [
            ['s-l', '?!data:cnvRich', '@rg=cnv-live-text', ':dataKey=cnvPartial'],
            ['s-cn', '?data:cnvRich', '@rg=cnv-live-text', [['s-mkd', ':dataKey=cnvPartial', ':dataField=md']]],
            ['span', '@rg=cnv-caret'],
          ]],
        ]],
        ['s-cn', '?data:cnvBusy', '@rg=cnv-typing', [
          ['s-cn', '@rg=cnv-dot'], ['s-cn', '@rg=cnv-dot'], ['s-cn', '@rg=cnv-dot'],
        ]],
      ]],
      // ~cl:call is safe here: the pill is the composite's OWN child (a call
      // token only misresolves inside an item component's subtree).
      ['s-b', '?data:cnvJump', '@rg=cnv-jump', '@v=ol', '@sz=sm', '@aria-label=$t.a11y.jumpLatest', '~cl:call:_jumpLatest', [['s-ic', '@n=arrow-down']]],
    ]],
    ['s-emp', '?prop:empty', { ':description': 'No messages yet' }],
    ['s-cnv-s', '?prop:hasSugs', '@rg=suggestions', '$sugs'],
    // The composer region wraps the form and the Stop. The Stop is a SIBLING
    // of the s-f, never inside it: s-f submits on a click on ANY descendant
    // button[is=s-b] (components/input/form.js), so a Stop inside the form
    // would send the draft. A plain button (s-tpl-chat's composer precedent),
    // rendered only when there is a selector to write (cancel or service).
    ['s-cn', '@rg=composer', [
      ['s-f', ':ref=form', '@rg=cnv-form', '#service:service', '#storeKey:storeKey', {
        ':schema': { message: { required: true } },
      }, [
        ['s-taif', { ':data': { name: 'message', label: '', placeholder: 'Write a message…' } }],
        // Icon-only, named from the a11y strings — the labelled primary
        // button belonged to a settings form, not a chat (the house chat
        // language is s-tpl-chat's compact send).
        ['s-b', '@c=pr', '@v=ic', '@aria-label=$t.a11y.send', '#busy:disabled', [['s-ic', '@n=sd']]],
      ]],
      // The name binds the GLOBAL store explicitly (:dataSource): under a
      // ?data: gate the subtree's default store is the internal one, where
      // no t.a11y.* key lives, and an @attr=$key there resolves to nothing.
      ['button', '?data:cnvCanStop', '@rg=cnv-stop', '@type=button', ':dataField=attr:aria-label', ':dataSource=t.a11y.stop', '~cl:call:_stop', [['s-ic', '@n=square']]],
    ]],
  ],
  // Declared listens (lint-visible). The scroll observer is the one exception:
  // scroll does not bubble, so it needs CAPTURE on self, which the declarative
  // listeners key does not carry — it stays imperative below.
  listeners: {
    formSubmit: '_onFormSubmit',
    keydown: '_onComposerKeydown',
    input: '_onComposerInput',
    sugpick: '_onSugPick',
  },
  callbacks: {
    connected() {
      this._stuck = true;
      this._recomputeStop();
      this._recompute();
      // Scroll does not bubble; capture on self observes the thread wrapper.
      this.addEventListener('scroll', (e) => {
        const sc = this.refs && this.refs.scroll;
        if (!sc || e.target !== sc) return;
        this._stuck = sc.scrollHeight - sc.scrollTop - sc.clientHeight < 40;
        this.jump = !this._stuck;
        this.setData('cnvJump', !this._stuck);
      }, true);
    },
  },
  methods: {
    set data(v) {
      this._rows = Array.isArray(v) ? v : (v && (v.data || v.rows)) || [];
      this._loaded = true;
      this._project();
    },
    get data() { return this._items; },
    set suggestions(v) {
      this._sugs = (Array.isArray(v) ? v : []).map((s) => (typeof s === 'string' ? { label: s } : { label: s.label ?? s.text }));
      this.setData('sugs', this._sugs);
      this.hasSugs = this._sugs.length > 0;
    },
    get suggestions() { return this._sugs; },
    _project() {
      const m = this.map || {};
      const rich = Boolean(this.rich);
      this._items = (this._rows || []).map((r) => {
        const role = m.role && r[m.role] !== undefined ? String(r[m.role]) : undefined;
        const own = m.own ? Boolean(r[m.own]) : role === 'user';
        return {
          content: r[m.content], actor: r[m.actor], avatar: r[m.avatar],
          date: r[m.date], role, own, rich,
          attrs: { ...(own ? { own: '' } : {}), ...(role ? { role } : {}) },
        };
      });
      this.setData('items', this._items);
      this._recompute();
      if (this._stuck !== false) this._scrollEnd();
    },
    onLoadingChange() { this._recompute(); },
    onErrorChange() { this._recompute(); },
    onRichChange(v) {
      this.setData('cnvRich', Boolean(v));
      if (this._rows) this._project();
    },
    onBusyChange(v) {
      this.setData('cnvBusy', Boolean(v));
      if (v && this._stuck !== false) this._scrollEnd();
    },
    // The streamed reply: one string key, one text node. Empty means no
    // bubble (before the first delta, and after the app resets the key on
    // done); a non-empty value after an abort or an error stays on screen.
    onPartialChange(v) {
      const text = v == null ? '' : String(v);
      this.setData('cnvPartial', text);
      this.setData('cnvLive', text.length > 0);
      if (text && this._stuck !== false) this._scrollEnd();
    },
    // `pending` (the call is out) and `streaming` (chunks arriving) both show
    // the Stop and the caret; done, error and aborted clear them.
    onStatusChange(v) {
      this.streaming = v === 'pending' || v === 'streaming';
    },
    onCancelChange() { this._recomputeStop(); },
    onServiceChange() { this._recomputeStop(); },
    _recomputeStop() {
      this.setData('cnvCanStop', Boolean(this.cancel || this.service));
    },
    // Stop writes the global command key the HTTP client watches (the shared
    // helper: cancel || service, a no-op with neither); the client aborts,
    // settles `aborted` and clears the key.
    _stop() {
      writeCancelSelector(this);
    },
    _recompute() {
      const blocked = Boolean(this.loading) || Boolean(this.error);
      const count = (this._items || []).length;
      this.ready = !blocked && count > 0;
      this.empty = Boolean(this._loaded) && !blocked && count === 0;
      this.setData('cnvReady', this.ready);
    },
    // Clear the composer after every submit (the service call is fired by
    // s-f) and follow the conversation to its newest message. Consumers often
    // flip busy/rows synchronously inside the submit's store write and s-f
    // re-applies field state on that re-render, so the clear defers a frame to
    // land after it.
    _onFormSubmit(e) {
      const form = e.target.closest && e.target.closest('form[is=s-f]');
      if (form && this.contains(form)) {
        requestAnimationFrame(() => {
          form.data = null;
          this._growComposer();
        });
        this._scrollEnd();
      }
    },

    // Enter sends, Shift+Enter (and IME composition) keeps the newline. A
    // stream in flight holds the send: Stop is the control until it settles.
    _onComposerKeydown(e) {
      if (e.key !== 'Enter' || e.shiftKey || e.isComposing) return;
      const t = e.target;
      if (!t || t.tagName !== 'TEXTAREA') return;
      e.preventDefault();
      const form = t.closest('form[is=s-f]');
      if (form && !this.busy && !this.streaming) (form.requestSubmit ? form.requestSubmit() : form.submit());
    },

    // The textarea grows with its content up to the CSS ceiling.
    _onComposerInput(e) {
      if (e.target && e.target.tagName === 'TEXTAREA') this._growComposer(e.target);
    },

    // Suggestion chip → fill the composer. The chip hands itself over; its
    // suggestion is object state on the element.
    _onSugPick(e) {
      const chip = e.detail && e.detail.item;
      if (chip && chip._data) this._pickSuggestion(chip._data);
    },

    // Jump pill → back to the latest message (wired with ~cl:call on the pill).
    _jumpLatest() {
      this._stuck = true;
      this._scrollEnd();
    },
    _scrollEnd() {
      requestAnimationFrame(() => {
        const sc = this.refs && this.refs.scroll;
        if (!sc) return;
        sc.scrollTop = sc.scrollHeight;
        this.jump = false;
        this.setData('cnvJump', false);
      });
    },
    // The measured height rides a custom property the stylesheet consumes
    // (never a written style): `auto` first so scrollHeight reports the
    // content, then the clamp.
    _growComposer(ta) {
      const el = ta || this.getElementsByTagName('textarea')[0];
      if (!el) return;
      el.style.setProperty('--cnv-ta-h', 'auto');
      el.style.setProperty('--cnv-ta-h', `${Math.min(el.scrollHeight, TEXTAREA_MAX_PX)}px`);
    },
    _pickSuggestion(s) {
      const form = this.refs && this.refs.form;
      if (!form || !s) return;
      form.data = { message: String(s.label ?? s ?? '') };
      this._growComposer();
      // The tap IS the send — unless a reply is mid-flight, in which case
      // the text waits in the composer like any other draft.
      if (!this.busy && !this.streaming) {
        (form.requestSubmit ? form.requestSubmit() : form.submit());
        return;
      }
      const ta = this.getElementsByTagName('textarea')[0];
      if (ta) ta.focus();
    },
  },
};

// ONE trigger: `service` when the binding names one (the composer's s-f calls
// it on submit), else `store.submit` (the endpoint declares storeKey). Both is
// the s-f shape that used to crash at client-side placement (Studio gap F12),
// and it would send twice; it is refused with a warning and `service` wins.
// Every optional key is emitted only when declared, so a binding never carries
// an `undefined` token.
function bind(b) {
  const s = b.store;
  const props = { ':map': b.map };
  if (b.service && s.submit) console.warn('[swc] s-cnv: bind with either service or store.submit, not both; using service');
  if (b.service) props[':service'] = b.service;
  else if (s.submit) props[':storeKey'] = s.submit;
  if (b.rich) props[':rich'] = true;
  if (b.cancel) props[':cancel'] = b.cancel;
  const tokens = [`:dataSource=${s.rows}`];
  if (s.loading) tokens.push(`:loading=$${s.loading}`);
  if (s.error) tokens.push(`:error=$${s.error}`);
  if (s.busy) tokens.push(`:busy=$${s.busy}`);
  if (s.suggestions) tokens.push(`:suggestions=$${s.suggestions}`);
  if (s.partial) tokens.push(`:partial=$${s.partial}`);
  if (s.status) tokens.push(`:status=$${s.status}`);
  return ['s-cnv', props, ...tokens];
}

const defs = [bubble, thread, sugChip, sugList, def];
return { meta, def, bind, defs };
})();

const __m198 = (() => {
const chatState = {
  assistantPrimary: true, assistantHidden: true, assistantExpanded: 'false', assistantLauncherLabel: 'Open AI assistant',
  't.a11y.chatHistory': 'Chat history',
  't.a11y.chatMinimise': 'Minimise AI assistant', 't.a11y.chatReset': 'New conversation',
  chatRows: [{ role: 'assistant', actor: 'Pragmatical AI', content: 'What would you like to build, automate or modernise?' }],
  chatSubmit: null, chatRequest: null, chatPartial: '', chatStatus: '', chatError: '', chatBusy: false,
  chatSuggestions: ['Build an app', 'Automate work', 'Modernise a system'],
  't.a11y.send': 'Send message', 't.a11y.stop': 'Stop response', 't.a11y.jumpLatest': 'Jump to latest message',
  't.a11y.chatConversation': 'Conversation with the Pragmatical AI assistant',
};

const chatServices = [['siteChat', '', [{
  name: 'send', url: '/swc/chat', method: 'POST', storeKey: 'chatRequest',
  responseType: 'stream', stream: { protocol: 'sse', textKey: 'chatPartial' },
  timeout: 45000, idleTimeout: 45000,
  statusStoreKey: 'chatStatus', errorStoreKey: 'chatError',
  transformFn: (_done, store) => finishChatTurn(store),
}]]];

function finishChatTurn(store) {
  const content = store.getValue('chatPartial');
  if (content) store.setValue('chatRows', [...(store.getValue('chatRows') || []), { role: 'assistant', actor: 'Pragmatical AI', content }]);
  store.setValues({ chatPartial: '', chatBusy: false });
}

// The native composer publishes a message. Application logic adds conversation
// context; the declared HTTP service owns transport, streaming and cancellation.
function connectChat(component) {
  const store = component.store;
  const submit = store.subscribe('chatSubmit', (payload) => {
    const message = String(payload?.message || '').trim();
    if (!message || store.getValue('chatBusy')) return;
    store.setValue('chatSubmit', null);
    if (message.length > 2000) { store.setValue('chatError', 'Please keep each message under 2,000 characters.'); return; }
    const rows = [...(store.getValue('chatRows') || []), { role: 'user', actor: 'You', own: true, content: message }].slice(-20);
    store.setValues({ chatRows: rows, chatBusy: true, chatSuggestions: [] });
    store.setValue('chatRequest', { messages: rows.slice(-12).map(({ role, content }) => ({ role, content: content.slice(0, 2000) })) });
  });
  const status = store.subscribe('chatStatus', (value) => {
    if (value === 'aborted' || value === 'error') finishChatTurn(store);
  });
  component._subs.push({ sub: submit }, { sub: status });
}
return { chatState, chatServices, connectChat };
})();

const __m199 = (() => {
const conversation = __m197.bind;
const conversationDefs = __m197.defs;
const connectChat = __m198.connectChat;
const chatState = __m198.chatState;
// Add a keyboard entry point to the shipped conversation's scroll region.
// This is a declarative extension; the composite still owns all chat behaviour.
const adaptConversation = (value) => {
  // The composite's submission key must not also become the host's inherited
  // data-binding key when a cached route reconnects.
  if (value === '#storeKey:storeKey') return '#submitKey:storeKey';
  if (!Array.isArray(value)) return value;
  const children = value.map(adaptConversation);
  return value.includes('@rg=cnv-scroll')
    ? [...children, '@tabindex=0', '@role=log', '@aria-label=$t.a11y.chatHistory']
    : children;
};
const chatDefs = conversationDefs.map(def => def.tag === 's-cnv' ? {
  ...def,
  props: def.props.map(prop => prop === 'storeKey' ? 'submitKey' : prop),
  children: adaptConversation(def.children),
} : def);

const conversationView = () => {
  const view = conversation({
        map: { content: 'content', actor: 'actor', own: 'own' }, cancel: 'siteChat.send',
        store: { rows: 'chatRows', submit: 'chatSubmit', busy: 'chatBusy', suggestions: 'chatSuggestions', partial: 'chatPartial', status: 'chatStatus' },
      });
  view[1][':submitKey'] = view[1][':storeKey'];
  delete view[1][':storeKey'];
  return view;
};
const resetConversation = function () {
  this.store.setValue('http.cancel', 'siteChat.send');
  this.store.setValues({ chatRows: chatState.chatRows.map(r => ({ ...r })), chatSuggestions: [...chatState.chatSuggestions], chatPartial: '', chatError: '', chatBusy: false, chatSubmit: null, chatRequest: null });
  this.getElementsByTagName('textarea')[0]?.focus();
};
const primaryAssistant = {
  tag: 'primary-assistant',
  children: [['s-cn', '@rg=primary-assistant', '@l=vertical', '@role=region', '@aria-label=$t.a11y.chatConversation', [
    ['s-cn', '@rg=chat-heading', '@l=row', [
      ['strong', '=Your next application starts here.'],
      ['s-b', '@v=ic', '@rg=chat-reset', '@aria-label=$t.a11y.chatReset', '~cl:call:newConversation', [['s-ic', '@n=rotate-ccw']]],
    ]],
    conversationView(),
    ['p', '?chatError', '@rg=chat-error', '@role=alert', '$chatError'],
    ['p', '@rg=chat-note', '=Pragmatical AI assistant · AI-generated answers. Avoid sharing sensitive information.'],
  ]]],
  methods: { newConversation: resetConversation },
};

// Persistent shell UI. Hiding the panel leaves the native conversation mounted.
const assistantDefs = [...chatDefs, primaryAssistant, {
  tag: 'site-assistant',
  children: [
    ['s-cn', '?!assistantPrimary', '@rg=assistant-panel', '@id=site-assistant-panel', '@role=dialog', '@aria-modal=false', '@aria-label=$t.a11y.chatConversation', ':dataField=hidden', ':dataSource=assistantHidden', '@l=vertical', [
      ['s-cn', '@rg=chat-heading', '@l=row', [
        ['img', '@src=/shots/pragmatical-symbol.svg', '@alt=', '@width=28', '@height=28'],
        ['s-cn', [['strong', '=Pragmatical AI'], ['p', '=AI assistant']]],
        ['s-b', '@v=ic', '@rg=chat-reset', '@aria-label=$t.a11y.chatReset', '~cl:call:newConversation', [['s-ic', '@n=rotate-ccw']]],
        ['s-b', '@v=ic', '@aria-label=$t.a11y.chatMinimise', '~cl:call:minimise', [['s-ic', '@n=minus']]],
      ]],
      conversationView(),
      ['p', '?chatError', '@rg=chat-error', '@role=alert', '$chatError'],
      ['p', '@rg=chat-note', '=AI-generated answers. Avoid sharing sensitive information.'],
    ]],
    ['s-b', '?!assistantPrimary', '@rg=assistant-launcher', '@c=pr', ':ref=launcher', '@aria-label=$assistantLauncherLabel', '@aria-controls=site-assistant-panel', '@aria-expanded=$assistantExpanded', '~cl:call:toggle', [
      ['s-ic', '@n=message-circle'], ['span', '=Ask AI'],
    ]],
  ],
  listeners: { keydown: 'onKeydown' },
  callbacks: {
    connected() {
      // Tab-local persistence also covers a full reload; nothing is published
      // into SSR state and no chat history is written to a public asset.
      try {
        const rows = JSON.parse(sessionStorage.getItem('pragmatical.chat') || 'null');
        if (Array.isArray(rows) && rows.length && rows.length <= 20 && rows.every(r => ['user','assistant'].includes(r.role) && typeof r.content === 'string' && r.content.length <= 10000)) {
          this.store.setValues({ chatRows: rows, chatSuggestions: rows.length > 1 ? [] : [...chatState.chatSuggestions] });
        }
      } catch { /* The current-page conversation still works. */ }
      connectChat(this);
      const rows = this.store.subscribe('chatRows', (value) => {
        try { sessionStorage.setItem('pragmatical.chat', JSON.stringify(value)); } catch { /* Storage can be disabled. */ }
      });
      const visible = this.store.subscribe('assistantHidden', (hidden) => {
        this.store.setValues({ assistantExpanded: hidden ? 'false' : 'true', assistantLauncherLabel: hidden ? 'Open AI assistant' : 'Hide AI assistant' });
        if (!hidden) requestAnimationFrame(() => this.getElementsByTagName('textarea')[0]?.focus());
      });
      const route = this.store.subscribe('route', (path) => {
        const primary = path === '/' || path === '/contact';
        const wasPrimary = this.store.getValue('assistantPrimary');
        this.store.setValue('assistantPrimary', primary);
        if (wasPrimary && !primary && (this.store.getValue('chatRows') || []).length > 1) this.store.setValue('assistantHidden', false);
      });
      this._subs.push({ sub: rows }, { sub: visible }, { sub: route });
    },
  },
  methods: {
    toggle() { if (this.store.getValue('assistantHidden')) this.store.setValue('assistantHidden', false); else this.minimise(); },
    minimise() { this.store.setValue('assistantHidden', true); this.refs.launcher?.focus(); },
    onKeydown(event) { if (event.key === 'Escape' && !this.store.getValue('assistantHidden')) { event.preventDefault(); event.stopPropagation(); this.minimise(); } },
    newConversation: resetConversation,
  },
}];
return { assistantDefs };
})();

const __m200 = (() => {
// Shared by server metadata and the client route subscription.
function navigationState(route = '/') {
  const path = String(route).split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  return Object.fromEntries(['work', 'approach', 'about', 'contact'].map((page) => [
    `navigation.${page}`, path === `/${page}` ? 'page' : 'false',
  ]));
}
return { navigationState };
})();

const __m201 = (() => {
const navigationState = __m200.navigationState;
// A non-modal disclosure. SWC owns rendering/state; refs handle focus only.

const siteNavigation = {
  tag: 'site-navigation', props: ['@expanded'],
  children: [
    ['s-b', '@v=ic', '@rg=menu-toggle', ':ref=toggle', '@aria-label=$t.a11y.menu', '@aria-controls=primary-navigation', '@aria-expanded=$menuOpen', '~cl:call:toggleMenu', [
      ['s-ic', '@n=menu'],
    ]],
    ['nav', '@id=primary-navigation', '@aria-label=$t.a11y.primaryNavigation', '@l=row', ':ref=panel', [
      ['a', ':ref=firstLink', '@href=/work', '@aria-current=$navigation.work', '~cl:call:closeMenu', '=Platform'],
      ['a', '@href=/approach', '@aria-current=$navigation.approach', '~cl:call:closeMenu', '=Approach'],
      ['a', '@href=/about', '@aria-current=$navigation.about', '~cl:call:closeMenu', '=Company'],
      ['a', '@href=/contact', '@aria-current=$navigation.contact', '@rg=nav-contact', '~cl:call:closeMenu', '=Get in touch'],
    ]],
  ],
  callbacks: {
    connected() {
      this._escape = (event) => {
        if (event.key === 'Escape' && this.store.getValue('menuOpen') === 'true') {
          event.preventDefault(); this.closeMenu();
        }
      };
      this.addEventListener('keydown', this._escape);
      this._viewport = matchMedia('(min-width: 761px)');
      this._resize = () => {
        const focused = this.contains(document.activeElement);
        this.store.setValue('menuOpen', 'false');
        if (focused) {
          if (this._viewport.matches) this.refs.firstLink?.focus();
          else this.refs.toggle?.focus();
        }
      };
      this._viewport.addEventListener('change', this._resize);
      const sub = this.store.subscribe('route', (route) => {
        this.store.setValues(navigationState(route));
        this.closeMenu();
      });
      this._subs.push({ sub });
    },
    disconnected() {
      this.removeEventListener('keydown', this._escape);
      this._viewport?.removeEventListener('change', this._resize);
    },
  },
  methods: {
    toggleMenu() { this.store.setValue('menuOpen', this.store.getValue('menuOpen') === 'true' ? 'false' : 'true'); },
    closeMenu() {
      if (this.store.getValue('menuOpen') !== 'true') return;
      this.store.setValue('menuOpen', 'false');
      this.refs.toggle?.focus();
    },
  },
};
return { siteNavigation };
})();

const __m202 = (() => {
// Every word on the site, in one place.
//
// Copy lives here rather than inside page DSL for two reasons: a page becomes a
// layout decision instead of a writing decision, and a second language later is
// additive (add a pack, add a locale key) rather than a rewrite of pages/.
// Flattened to `t.*` store keys at config time — pages bind `:dataSource=t.…`.
//
// CLAIMS DISCIPLINE. Every number here is traceable to a file on disk. The
// figures our own product sites publish (500M lines, 27,000 repos, 48
// government organisations) are NOT reproduced: nothing on disk substantiates
// them, and a number a buyer's technical reviewer cannot be shown the working
// for is worth less than a smaller number they can. Certifications are not
// claimed — none are held. No customer is named, and no employer logo is shown.

const CONTENT = {
  brand: {
    name: 'Pragmatical AI',
    motto: 'AI-first tools for real-world work.',
    legal: '© 2026 Pragmatical AI Ltd.',
  },

  nav: {
    work: 'Work with us',
    products: 'Products',
    approach: 'Approach',
    about: 'About',
    contact: 'Contact',
    cta: 'Start an assessment',
  },

  // ── / ────────────────────────────────────────────────────────────────────
  home: {
    meta: {
      title: 'Pragmatical AI — AI-first tools for real-world work',
      description: 'We build AI-first tools: AI Studio for creating applications, Smart Web Components for their foundation, and practical tools for complex work.',
    },
    hero: {
      eyebrow: 'Applied AI',
      // Static. A claim you rotate is a claim you do not hold — and the
      // rotating word was the most-copied startup device in circulation.
      headline: 'AI, put to work on the problem you actually have.',
      sub: 'The migration nobody wants to start. The contract that comes back changed every round. The process that runs on people copying between systems. We build the tools, and we have done it inside banks and government.',
      cta: 'Start an assessment',
      cta2: 'See the tools',
    },

    // The signature: what the work actually produces. A machine reads the
    // estate or the draft and proposes a finding; a person confirms what it
    // meant; the confirmation is recorded. That sentence is the whole pitch,
    // and until now it was a 14px line in a muted paragraph.
    //
    // Every fragment below is INVENTED. Real ledgers stay inside the client's
    // boundary, which is the point the caption makes — a company that will not
    // show you another client's code is the company a bank hires.
    ledger: {
      caption: 'Illustration. Real findings stay inside your boundary; ours receives audit metadata only.',
      cols: ['Ref', 'Read from', 'What it means', 'State'],
      rows: [
        { ref: '0412', src: 'PROC 3200-CALC-PENALTY', meaning: 'Late-payment penalty is waived for accounts flagged H — undocumented since 2009', state: 'confirmed', label: 'Confirmed' },
        { ref: '0871', src: 'TRIGGER trg_acct_close', meaning: 'Closing an account rewrites six months of history', state: 'confirmed', label: 'Confirmed' },
        { ref: '1104', src: 'cl. 14.3 · round 22', meaning: 'Liability cap returned, reworded, not flagged in the summary', state: 'confirmed', label: 'Confirmed' },
        { ref: '1150', src: 'cl. 9.1 · round 22', meaning: '—', state: 'awaiting', label: 'Awaiting' },
      ],
    },

    // What we are actually hired for, said as the problem rather than as the
    // capability. Each one names the tool that does it, and every tool has its
    // own site — the links open there.
    pains: {
      title: 'Six problems we are hired for.',
      sub: 'Different problems, one method: find what is really happening, agree it with the people who know, then build the part that changes it. The tool under each is ours, which is why the estimate is a build rather than a discovery phase.',
      items: [
        {
          icon: 'scan-search',
          title: 'A system we cannot migrate off',
          body: 'Twenty years of behaviour, no documentation, and a vendor quote priced for the risk. Code Doctor reads the estate — source and database — and hands you the rulebook the migration runs on.',
          tool: 'Code Doctor', href: 'https://codedoctor.ai', tag: 'Legacy',
        },
        {
          icon: 'scale',
          title: 'A contract that comes back different every round',
          body: 'Clause creep: you struck it in round nineteen, and by round twenty-two it was back, reworded, with no summary flagging it. Contract Vetting reads every returned draft against the last one — and against case law, official guidance and your own commercial rules.',
          tool: 'Contract Vetting', href: 'https://contractvetting.com', wide: true, tag: 'Clause creep',
        },
        {
          icon: 'workflow',
          title: 'A process that runs on people copying between systems',
          body: 'The work is real. The copying is not. Define the events, let agents do the work between them, and keep a person on the gate.',
          tool: 'agent-stack', href: '/products/agent-stack', tag: 'Human in the loop',
        },
        {
          icon: 'shopping-bag',
          title: 'A shop no assistant can read',
          body: 'Customers ask ChatGPT what to buy before they open a browser tab. shop2gpt puts your catalogue where the question is being answered.',
          tool: 'shop2gpt', href: 'https://shop2gpt.com', tag: 'Agentic commerce',
        },
        {
          icon: 'globe-lock',
          title: 'An app for every market you are regulated in',
          body: 'Each one wants its own language and its own answer about where the data was processed. SWC gives you both in one app, rendered at the edge. This site runs on it.',
          tool: 'SWC', href: 'https://smartwebcomponents.com', wide: true, tag: 'Data residency',
        },
        {
          icon: 'landmark',
          title: 'A public service that has to pass assessment',
          body: 'Describe the journey and watch it build with the real GOV.UK design system — inside your own boundary, on the model you choose.',
          tool: 'GDS Playground', href: 'https://gdsplayground.com', tag: 'GOV.UK',
        },
      ],
    },

    // The two engagements that do not come in a box. They were missing
    // entirely: the site listed six tools and never said that setting an
    // organisation up to USE AI internally is work we do.
    services: {
      title: 'And two things that do not come in a box.',
      items: [
        {
          icon: 'boxes',
          title: 'End-to-end implementation',
          body: 'Discovery, extraction, the replacement, and equivalence proved before cutover — with our own tools doing the expensive parts.',
          cta: 'How an engagement runs', href: '/implementation',
        },
        {
          icon: 'brain',
          title: 'Internal AI enablement',
          body: 'The plumbing and the screens that let your own people use AI on your own data: the dataflow, the gateway that governs it, and the interfaces the humans in the loop work in.',
          cta: 'Setting up internal AI', href: '/internal-ai',
        },
      ],
    },

    // The experience claim, stated as sectors rather than client names — the
    // house rule is no logos and no named institutions.
    regulated: {
      title: 'We have done this where it is hard.',
      sub: 'Banks and government organisations: environments where the system cannot be switched off, the auditor will ask how you knew, and "we let the model decide" is not an answer. Everything below is a working property, not a promise.',
      items: [
        { icon: 'shield', title: 'Your source stays yours', body: 'Read statically, never executed. Artefacts stay in your environment; our infrastructure receives audit metadata only.' },
        { icon: 'globe-lock', title: 'Your jurisdiction, your cloud', body: 'AWS including GovCloud, Azure including Government, Google Vertex with Assured Workloads, or self-hosted and air-gapped.' },
        { icon: 'languages', title: 'Every market, in its own language', body: 'Localisation is a compliance question before it is a marketing one: the locale is in the URL and in the server render, right-to-left is a supported direction rather than a retrofit, and each market is served from the regions you permit.' },
        { icon: 'user-check', title: 'A human holds the pen', body: 'AI surfaces the pattern; a person confirms what it meant, and the confirmation is recorded. Delivery waits for someone to open the gate.' },
        { icon: 'key', title: 'You own what comes out', body: 'The corpus, the rulebook, the model and the code. Leaving us costs you the engagement and nothing else.' },
      ],
    },

    offer: {
      title: 'The knowledge leaves with the people. Unless it is extracted first.',
      sub: 'The engagement that pays for itself twice: the extraction that makes the rebuild possible also produces a corpus you own — and a model trained on it that answers questions about your business the way your longest-serving engineer would.',
      steps: [
        { icon: 'database', title: 'Extract', body: 'Deterministic facts from source and from the database — stored procedures, triggers, scheduled jobs. The logic source-only tools never see.' },
        { icon: 'user-check', title: 'Confirm', body: 'Structured intent capture with the engineers who remember why. AI surfaces the pattern; a human confirms the meaning. Nothing is assumed.' },
        { icon: 'brain', title: 'Train', body: 'A model trained on your corpus, running in your jurisdiction on your cloud — or air-gapped. The corpus and the model are yours.' },
      ],
      cta: 'How the extraction works',
    },

    products: {
      title: 'The tools we built to do it.',
      sub: 'Six tools, each one the answer to a wall we hit doing this work. You can hire us to point them at your problem, or use them yourself.',
      cta: 'All six, with status and links',
    },


    airtight: {
      title: 'Built for estates that cannot take risks.',
      sub: 'Regulated, sovereign and safety-critical systems are the normal case here. Four commitments, each one a technical property rather than a promise.',
      items: [
        { icon: 'shield', title: 'Your source stays yours', body: 'Source is read statically and never executed. Artefacts never leave your environment; our infrastructure receives audit metadata only.' },
        { icon: 'globe-lock', title: 'Your jurisdiction, your cloud', body: 'The model runs where your data is allowed to be — AWS including GovCloud, Azure including Government, Google Vertex, or self-hosted and air-gapped.' },
        { icon: 'user-check', title: 'Humans confirm intent', body: 'A machine may propose what a system does. A person who knows confirms it. Every confirmation is recorded, and every one is reversible.' },
        { icon: 'key', title: 'You own the output', body: 'The corpus, the rulebook and the model are your assets. Leaving us costs you the engagement, not the knowledge.' },
      ],
    },

    close: {
      headline: 'Find the right tool. Build the next thing.',
      sub: 'Explore the products, or talk to us about putting them to work with your team.',
      cta: 'Talk to us',
    },
  },

  // ── /implementation ──────────────────────────────────────────────────────
  implementation: {
    meta: {
      title: 'End-to-end AI implementation — Pragmatical AI',
      description: 'From the estate you have to the system you meant to build: discovery, intent, target stack, and equivalence proved before cutover.',
    },
    hero: {
      eyebrow: 'Work with us',
      headline: 'From the estate you have to the system you meant to build.',
      sub: 'End-to-end means the parts nobody wants to own: finding what is there, recovering why it was built that way, building the replacement, and proving it behaves the same before anything is switched over.',
      cta: 'Start an assessment',
    },
    stages: {
      title: 'What end to end covers.',
      items: [
        { icon: 'scan-search', title: 'Discovery', body: 'Estate-wide mapping. Repositories, services, databases, schedules and the integrations nobody documented. The output is an inventory you can argue with.' },
        { icon: 'git-compare', title: 'Extraction', body: 'Deterministic facts, lifted at machine scale from source and database alike. Facts first, reasoning second — a model that guesses about a payment path is worse than no model.' },
        { icon: 'user-check', title: 'Intent', body: 'The half that is not in the code: why the exception exists, which customer it was for, what breaks if it goes. Captured from people, in a structured flow, on the record.' },
        { icon: 'boxes', title: 'Build', body: 'A target stack generated from the confirmed model, using our own component and workflow layers where they fit — which is most of the interface and most of the plumbing.' },
        { icon: 'check-check', title: 'Proof', body: 'The new system runs beside the old one and is measured against it. Equivalence is demonstrated under real load before anything is switched over.' },
      ],
    },
    bring: {
      title: 'What we bring pre-built.',
      sub: 'The reason an engagement is not a from-scratch build: four layers already exist, so the work is your domain rather than our scaffolding.',
      stats: [
        { value: '103', label: 'UI primitives, server-rendered' },
        { value: '91', label: 'Composite screens' },
        { value: '20', label: 'Full-page templates' },
        { value: '4', label: 'Source analysers' },
      ],
    },
    shapes: {
      title: 'Three ways this starts.',
      items: [
        { q: 'Assessment', a: 'A scoped read of one estate, one process or one store. You get an inventory, the risk map, and a plan you own — whether or not you continue with us.' },
        { q: 'Design partnership', a: 'We build the first modernised slice with your engineers in the room, and the method transfers to your team as it goes. Design partners shape the roadmap of the products they use.' },
        { q: 'Delivery', a: 'Full implementation against a confirmed model: build, parallel run, cutover, handover. You own the rulebook and everything generated from it.' },
      ],
    },
    close: { headline: 'Let’s build something useful.', cta: 'Talk to us' },
  },

  // ── /legacy-to-model ─────────────────────────────────────────────────────
  legacyToModel: {
    meta: {
      title: 'Legacy process extraction to a custom model — Pragmatical AI',
      description: 'We extract what your systems do into a machine-readable corpus you own, then train a model on it that runs inside your jurisdiction.',
    },
    hero: {
      eyebrow: 'Flagship engagement',
      headline: 'Your processes are documented in the heads of people who are leaving.',
      sub: 'Every organisation with a system older than its staff has the same problem: the operating model is undocumented behaviour, and the code is only its residue. That knowledge is trainable — once somebody does the hard part and extracts it.',
      cta: 'Start an assessment',
    },
    problem: {
      title: 'Why the documentation never helped.',
      items: [
        { icon: 'file-question-mark', title: 'The document describes the intention', body: 'It was written before the exceptions. The exceptions are the business.' },
        { icon: 'database', title: 'The behaviour lives in the database', body: 'Stored procedures, triggers and scheduled jobs carry decisions no application file mentions. Source-only tools never see them.' },
        { icon: 'users', title: 'The reasons live in people', body: 'Why a rule exists is not recoverable from the rule. It is recoverable from whoever put it there — while they are still here.' },
      ],
    },
    pipeline: {
      title: 'Extract first. Reason second.',
      items: [
        { q: 'What gets extracted', a: 'Deterministic facts at machine scale: routes, services, data flows, jobs, integration points, and database-resident logic including stored procedures and triggers. Every fact carries the source location it was lifted from.' },
        { q: 'How intent is captured', a: 'The extraction surfaces patterns and open questions; a structured flow puts them in front of the engineers who know, one confirmation at a time. Every answer is recorded against the fact it explains. A machine never assumes.' },
        { q: 'What the corpus is', a: 'A structured, machine-readable description of how your business operates, versioned like code, queryable, and owned outright by you. It is the durable asset — it survives this engagement, the next migration, and us.' },
        { q: 'What the model is', a: 'A model trained on that corpus, so it answers in your domain rather than in general. It runs on your infrastructure: AWS Bedrock including GovCloud, Azure OpenAI including Government, Google Vertex with Assured Workloads, or self-hosted and air-gapped.' },
        { q: 'What it is used for', a: 'Onboarding an engineer in days rather than quarters. Impact analysis before a change. Answering "what breaks if we turn this off". Planning a migration against what the system does rather than what the wiki claims.' },
      ],
    },
    corpus: {
      title: 'We proved the method on a corpus before selling it.',
      sub: 'Before talking to any customer we built and analysed corpora at estate scale, because a method that has only seen one codebase is an anecdote.',
      stats: [
        { value: '6,254', label: 'Legacy repositories, licence-checked, with provenance' },
        { value: '18,227', label: 'UK public-sector repositories catalogued' },
        { value: '7,053', label: 'Cloned with full history' },
        { value: '4,515', label: 'Projects analysed' },
      ],
    },
    boundaries: {
      title: 'What never leaves.',
      sub: 'This section is the offer itself. If any of it were untrue the engagement would be impossible in a regulated estate.',
      items: [
        { icon: 'shield', title: 'Source is read, never run', body: 'Static analysis only. Nothing executes your code, and nothing needs production access to do the extraction.' },
        { icon: 'server', title: 'Artefacts stay inside', body: 'The corpus, the model and everything generated from them live in your environment. Our infrastructure receives audit metadata — what ran, when, against which source reference.' },
        { icon: 'globe-lock', title: 'The model runs where you say', body: 'Your cloud, your region, your chosen base model — including fully air-gapped, where nothing leaves the building at all.' },
      ],
    },
    close: { headline: 'Start with one system and one week of somebody’s memory.', cta: 'Talk to us' },
  },

  // ── /internal-ai ─────────────────────────────────────────────────────────
  internalAi: {
    meta: {
      title: 'Internal AI enablement — Pragmatical AI',
      description: 'The dataflow, the governance and the interfaces that let your own people use AI on your own data — with a gateway in front of every model call.',
    },
    hero: {
      eyebrow: 'Work with us',
      headline: 'Everyone has a chatbot. Nobody has the plumbing.',
      sub: 'Using AI internally is not a licence decision. It is a question of what the model may see, who may ask it, what each answer costs, what happens to the corrections, and where the humans in the loop actually do their work. We build that.',
      cta: 'Start an assessment',
    },
    flow: {
      title: 'The dataflow, end to end.',
      items: [
        { icon: 'database', title: 'Where the knowledge is', body: 'Systems, documents, databases and the process knowledge that was never written down. The same extraction we use on a legacy estate, pointed at your operations.' },
        { icon: 'file-check', title: 'What the model may see', body: 'A corpus scoped by permission rather than a folder somebody shared. Retrieval that answers from your material and cites which document it came from.' },
        { icon: 'user-check', title: 'Who is in the loop', body: 'Corrections, confirmations and refusals captured as data rather than lost in a chat window — the raw material for the next improvement.' },
        { icon: 'brain', title: 'What comes back', body: 'A model tuned to your domain on the corpus you own, deployed in your jurisdiction, answering in your terms rather than in general ones.' },
      ],
    },
    control: {
      title: 'A gateway in front of every model call.',
      sub: 'The control point most internal rollouts discover they needed after the first invoice or the first incident. It is built, it runs on Node or at the edge unchanged, and it is the single place cost and permission are known.',
      items: [
        { q: 'Who may use which model', a: 'Per-team allow-lists, evaluated at the gateway rather than trusted to each application. A team gets the models it is approved for and nothing else.' },
        { q: 'What it costs, as it happens', a: 'A usage ledger in tokens and computed cost, a monthly spend cap, and a request rate limit. The cap is enforced where prices are known, so an application cannot spend around it.' },
        { q: 'A kill switch', a: 'One switch stops model traffic for a tenant. Not a support ticket to a vendor — a control you hold.' },
        { q: 'Whose keys', a: 'Bring your own provider credentials; they are stored encrypted and handed to the runtime at call time. Changing provider is a configuration change rather than a migration.' },
        { q: 'What happened, and who started it', a: 'RBAC is evaluated before every tool and connector call, and write or destructive actions are denied unless something explicitly grants them. Every action is recorded with its initiator — a person, a scheduled job, a webhook, or another tool.' },
      ],
    },
    interfaces: {
      title: 'The people in the loop need somewhere to work.',
      sub: 'This is the part most AI programmes leave until last and then improvise: the screens where a person reviews, confirms, corrects, labels or rejects what the model produced. They are the difference between a pilot and a process.',
      items: [
        { icon: 'layout-dashboard', title: 'Review and confirmation', body: 'Queues that put one decision in front of one person with the evidence beside it, and record the answer against the thing it explains. Code Doctor’s intent validation is this, shipped.' },
        { icon: 'clipboard-check', title: 'Labelling and evaluation', body: 'The screens that turn expert judgement into training data, and the ones that tell you whether the last change made the model better or only different.' },
        { icon: 'globe-lock', title: 'Built to the same standard as the rest', body: 'On SWC, so they are server-rendered, quick, localised where they need to be, and accessible because the build refuses to ship them otherwise.' },
      ],
    },
    close: { headline: 'Start with one team and one question they ask all day.', cta: 'Talk to us' },
  },

  // ── /agentic-commerce ────────────────────────────────────────────────────
  commerce: {
    meta: {
      title: 'Agentic commerce — Pragmatical AI',
      description: 'Make your catalogue answerable by any assistant. shop2gpt converts a Shopify or WooCommerce store into MCP so a model can browse it, choose from it and buy.',
    },
    hero: {
      eyebrow: 'Work with us',
      headline: 'Your catalogue, answerable by any assistant.',
      sub: 'Customers have started asking a model what to buy before they open a shop. A store an assistant cannot read is a store that is not in the answer.',
      cta: 'See shop2gpt',
      cta2: 'Talk to us',
    },
    what: {
      title: 'What has to be true for an assistant to sell for you.',
      items: [
        { icon: 'plug', title: 'It can read the catalogue', body: 'Products, variants, stock and price exposed as a protocol a model speaks — MCP — rather than a page it has to guess at.' },
        { icon: 'search-check', title: 'It can answer honestly', body: 'Availability and price come from your store at the moment of asking. An assistant that recommends a sold-out product costs you the customer twice.' },
        { icon: 'shield-check', title: 'You keep control', body: 'Pricing, stock, promotions and fulfilment stay in your systems. The assistant gets a read model and a checkout hand-off. Your systems keep the keys.' },
      ],
    },
    how: {
      title: 'How we do it.',
      items: [
        { q: 'Shopify and WooCommerce', a: 'shop2gpt connects to the store you already run and derives the MCP surface from your live catalogue. No re-platforming, no second source of truth for stock or price.' },
        { q: 'Anything else', a: 'Any store with a catalogue API or an export can be mapped. Where a platform is unusual, the mapping is part of the engagement rather than a blocker.' },
        { q: 'The storefront too, if you want it', a: 'The same conversion machinery that reads your catalogue can rebuild the storefront on our component layer — server-rendered, fast, and yours. Optional, and separately scoped.' },
      ],
    },
    close: { headline: 'Point us at your store.', cta: 'Talk to us' },
  },

  // ── /process-automation ──────────────────────────────────────────────────
  automation: {
    meta: {
      title: 'Process automation — Pragmatical AI',
      description: 'Automate the work that never got written down: find the real process, then automate the part that is safe to automate.',
    },
    hero: {
      eyebrow: 'Work with us',
      headline: 'Automate the work that never got written down.',
      sub: 'Automation projects fail on the gap between the documented process and the real one. We start by finding the real one — the same way we read a legacy estate, because it is the same problem wearing different clothes.',
      cta: 'Start an assessment',
    },
    method: {
      title: 'Find it, then automate it.',
      items: [
        { icon: 'scan-search', title: 'Observe the actual path', body: 'What the systems record, what the spreadsheets carry, what the exceptions do. The real process is visible in its traces even when nobody can describe it.' },
        { icon: 'user-check', title: 'Confirm with the people doing it', body: 'The person who does the work knows which step is load-bearing and which is habit. That distinction is the whole difference between an automation that holds and one that gets switched off.' },
        { icon: 'split', title: 'Automate the safe part', body: 'Deterministic steps become code. Judgement steps keep a human, with the model preparing the decision rather than making it. Where the line falls is a decision we make with you, in writing.' },
        { icon: 'clipboard-check', title: 'Hand it over', body: 'You get the automation, the map of the process it implements, and the reasoning for every place a human stayed in the loop.' },
      ],
    },
    close: { headline: 'Bring us the process nobody wants to describe.', cta: 'Talk to us' },
  },

  // ── /products ────────────────────────────────────────────────────────────
  products: {
    meta: {
      title: 'Products — Pragmatical AI',
      description: 'Explore AI Studio, Smart Web Components and our tools for modernisation, agent workflows, contracts, commerce and government services.',
    },
    hero: {
      eyebrow: 'Products',
      headline: 'Tools to build with. Intelligence to work with.',
      sub: 'Start with AI Studio and Smart Web Components, then explore tools built for specific kinds of work. Availability is shown for every product.',
    },
    statusLabels: { live: 'Live', partners: 'Design partners', preview: 'Internal preview', building: 'In build' },
    // href is the page on this site; site is the product's own site, opened in
    // a new tab so a reader comparing tools does not lose their place here.
    items: [
      { id: 'ai-studio', name: 'AI Studio', status: 'preview', href: '/products/ai-studio', site: '',
        body: 'From a brief to flows, wireframes, screens and data. A guided app-building experience on Smart Web Components.' },
      { id: 'code-doctor', name: 'Code Doctor', status: 'partners', href: '/products/code-doctor', site: 'https://codedoctor.ai',
        body: 'Migrate off the system you cannot describe. Estate discovery, extraction from source and database, intent confirmed by your engineers, and a rulebook you own.' },
      { id: 'contract-vetting', name: 'Contract Vetting', status: 'building', href: '/products/contract-vetting', site: 'https://contractvetting.com',
        body: 'Review a contract against case law, official guidance and your own commercial rules — then track every round, including the clauses that quietly come back.' },
      { id: 'agent-stack', name: 'agent-stack', status: 'preview', href: '/products/agent-stack', site: '',
        body: 'Build harnesses for AI data flows: connect sources, carry context between steps and keep a human on the gate. The infrastructure behind Contract Vetting.' },
      { id: 'shop2gpt', name: 'shop2gpt', status: 'live', href: '/products/shop2gpt', site: 'https://shop2gpt.com',
        body: 'Expose your shop to agentic shopping. Your catalogue as MCP, so ChatGPT, Claude and the assistants after them can browse it and buy from it.' },
      { id: 'swc', name: 'Cherga UI (currently SWC)', status: 'live', href: '/products/swc', site: 'https://smartwebcomponents.com',
        body: 'The foundation for AI-powered apps: server-rendered, no build step, no dependencies, localised per market and distributed globally on the edge.' },
      { id: 'gds-playground', name: 'GDS Playground', status: 'preview', href: '/products/gds-playground', site: 'https://gdsplayground.com',
        body: 'Lovable and Cursor, specialised for the GDS cohort. Describe a government service, build it with the real design system, keep every version in git.' },
    ],
  },

  // ── /products/:pid ───────────────────────────────────────────────────────
  // One page shape per product: what it is, why it exists, what it does in
  // specifics, who it is for, and how to get it. Every capability line here is
  // one I can point at a file for — see the claims note at the top of this
  // file. Status labels are the truth on the day.
  productPages: {
    'ai-studio': {
      eyebrow: 'Product · app creation',
      headline: 'Your idea, with a path to an application.',
      sub: 'AI Studio — called SWC Studio in the project — brings the building process into one guided workspace: describe the app, review its flow, refine its wireframes, then work through screens and data.',
      why: { title: 'Make the thinking visible.', body: 'An application is more than a generated screen. Its journey, structure and data need to make sense together. Studio gives each of those decisions a place to be reviewed.' },
      does: { title: 'From context to something concrete.', items: [
        { icon: 'file-check', title: 'Start with context', body: 'Begin with a description and supporting material such as screenshots, a repository or API documentation.' },
        { icon: 'workflow', title: 'Review the flow', body: 'Inspect the screens and the connections between them before refining the interface.' },
        { icon: 'layout-dashboard', title: 'Shape the wireframes', body: 'Work with shells, layouts and named components rather than an unstructured canvas.' },
        { icon: 'database', title: 'Connect screens and data', body: 'Review the entities, services and state that the interface depends on.' },
      ] },
      who: 'People and teams turning an application idea into a structured, reviewable software project.',
      status: 'preview',
      access: 'An internal preview, built on SWC and agent-stack. Contact us to discuss access and the current workflow. Public self-service access is not yet advertised.',
      cta: 'Ask about Studio access', site: null,
    },
    'code-doctor': {
      eyebrow: 'Product · legacy modernisation',
      headline: 'Migrate off the system nobody can describe.',
      sub: 'Most enterprise transformation is cosmetic: a new interface wrapped around forty-year-old behaviour. Code Doctor is for the other kind — it maps the estate, recovers the intent nobody wrote down, and hands you a rulebook the next migration starts from.',
      why: {
        title: 'Why it exists.',
        body: 'Every modernisation we were brought into stalled in the same place: nobody could describe the system being replaced. Not the vendor, not the incumbent team, not the documentation. So the first thing we built was the thing that reads what is already there.',
      },
      does: {
        title: 'What it does.',
        items: [
          { icon: 'scan-search', title: 'Estate topology', body: 'Repositories, services, routes, jobs and the integrations between them, mapped across the estate rather than sampled from one repo.' },
          { icon: 'git-compare', title: 'Deterministic extraction', body: 'Facts lifted at machine scale from Java and Spring, .NET, Python and TypeScript — each one carrying the source location it came from.' },
          { icon: 'database', title: 'Database logic', body: 'Postgres and SQL Server introspection: stored procedures, triggers, functions and scheduled jobs. The decisions source-only tools never see.' },
          { icon: 'user-check', title: 'Intent validation', body: 'A structured flow that puts open questions in front of the engineers who still know, and records the answer against the fact it explains.' },
          { icon: 'key', title: 'The rulebook', body: 'A machine-readable description of the system, owned by you, versioned like code, and reusable by the next migration.' },
          { icon: 'check-check', title: 'Equivalence before cutover', body: 'The generated system is measured against the original rather than declared finished. Demonstrated end to end on a reference pair.' },
        ],
      },
      who: 'Regulated and public-sector estates where the system cannot be switched off, the behaviour cannot be guessed at, and the auditors will ask how you knew.',
      status: 'partners',
      access: 'Onboarding a small number of design partners. An assessment is the first step and the output is yours regardless of what follows.',
      site: { label: 'codedoctor.ai', href: 'https://codedoctor.ai' },
      shot: { src: '/shots/code-doctor.jpg', alt: 'The Code Doctor pipeline: topology discovery, pattern extraction, intent validation, rulebook, parallel-run verification.', caption: 'codedoctor.ai — the pipeline, from estate to proven cutover.' },
    },
    swc: {
      eyebrow: 'Product · the UI layer',
      headline: 'The foundation for AI-powered apps that are fast everywhere.',
      sub: 'Server-rendered components with no build step and a runtime with no dependencies, deployed to the edge and authored from a DSL a model can write. It is what our own apps are built on, including this site.',
      why: {
        title: 'Why it exists.',
        body: 'We were spending more of every engagement on screens than on the thinking behind them. A component library that a model can target — and a server that renders it without a build step — turned the interface from the slowest part of a delivery into the fastest.',
      },
      does: {
        title: 'What it does.',
        items: [
          { icon: 'boxes', title: '103 primitives', body: 'The whole vocabulary of an application interface, each one carrying its own accessibility contract rather than leaving it to the page.' },
          { icon: 'layout-dashboard', title: '91 composite screens', body: 'Whole sections and 20 full-page templates — a mailbox, a dashboard, a storefront — as one component fed one payload.' },
          { icon: 'server', title: 'Server-rendered, no build step', body: 'Pages arrive as HTML with their content in them. The runtime has no dependencies and deploys to the edge, so the same app is close to a user in Frankfurt and one in Singapore.' },
          { icon: 'languages', title: 'Localisation in the framework, not bolted on', body: 'The locale rides the URL (/:locale, or country and language separately), the server renders in it, and direction and lang are set from it — right-to-left included, which is a layout decision the whole component library already makes. Six languages ship on our own site, Arabic among them.' },
          { icon: 'globe-lock', title: 'Regional by deployment', body: 'It runs as an edge worker, so which regions execute it is a deployment decision rather than a rewrite — the lever a data-residency obligation actually needs.' },
          { icon: 'shield-check', title: 'Accessibility enforced by lint', body: 'An icon-only button with no accessible name fails the build. Zero WCAG 2.1 A/AA violations across 62 automated scans.' },
          { icon: 'git-compare', title: 'Migration converters', body: 'Deterministic conversion from Next.js, WordPress and Shopify catalogues, with every finding classed as mapped, approximate or unmapped.' },
        ],
      },
      who: 'Teams building AI-facing products who want the interface to be a solved layer, and anyone modernising onto a stack they will still own in five years.',
      status: 'live',
      access: 'Free to use, with a commercial licence for code-generation products. The full component catalogue and documentation are on the product site.',
      site: { label: 'smartwebcomponents.com', href: 'https://smartwebcomponents.com' },
      shot: { src: '/shots/swc-templates.jpg', alt: 'The SWC template gallery: seventeen full-page application templates.', caption: 'Seventeen full-page templates, each one payload-driven and server-rendered.' },
    },
    shop2gpt: {
      eyebrow: 'Product · agentic commerce',
      headline: 'Put your shop where the shopping is happening.',
      sub: 'Customers ask a model what to buy before they open a shop. shop2gpt turns your catalogue into MCP — the protocol assistants speak — so your products are in the answer rather than behind a page a model has to guess at.',
      why: {
        title: 'Why it exists.',
        body: 'Every commerce client asked the same question in the same month: how do we appear when a customer asks an assistant instead of a search engine. The honest answer needed a live, queryable view of the catalogue. A feed export cannot answer a question.',
      },
      does: {
        title: 'What it does.',
        items: [
          { icon: 'plug', title: 'Store to MCP', body: 'Products, variants, price and stock exposed as a protocol a model can query directly, derived from the store you already run.' },
          { icon: 'shopping-bag', title: 'Shopify and WooCommerce', body: 'Connects to the platform you are on. No re-platforming, and no second source of truth for stock or price.' },
          { icon: 'search-check', title: 'Answers from live data', body: 'Availability and price are read at the moment of asking, so an assistant never recommends what you cannot ship.' },
          { icon: 'shield-check', title: 'You keep the controls', body: 'Pricing, promotions, stock and fulfilment stay in your systems. The assistant gets a read model and a checkout hand-off.' },
        ],
      },
      who: 'Retailers and brands whose customers have started shopping through assistants, and anyone whose catalogue is their most valuable and least machine-readable asset.',
      status: 'live',
      access: 'Connect a store and the MCP surface is generated from your catalogue.',
      site: { label: 'shop2gpt.com', href: 'https://shop2gpt.com' },
    },
    'contract-vetting': {
      eyebrow: 'Product · contract and document review',
      headline: 'Read it against everything that should govern it.',
      sub: 'A contract is checked against four things at once: the law and guidance that bind it, the facts it asserts, the commercial rules you actually run on, and the version of itself you agreed last round. Doing three of those by hand is why review is slow, and why the fourth gets skipped.',
      why: {
        title: 'Why it exists.',
        body: 'One negotiation ran to twenty-six rounds. In each one the counterparty reworded something we had already struck — never the same way twice, never flagged in the summary. A person reading a fresh redline at eleven at night is being asked to remember nineteen previous drafts. Nobody can, and everybody is expected to.',
      },
      does: {
        title: 'What it does.',
        items: [
          { icon: 'file-diff', title: 'Every round, against the last', body: 'What you asked for and got. What you asked for and did not get. And what appeared that nobody asked for — the clause creep that survives because it arrives reworded rather than restored.' },
          { icon: 'gavel', title: 'Against case law and guidance', body: 'Clauses read against the authorities that decide them and the official guidance that applies, with the citation attached rather than a confidence score.' },
          { icon: 'file-check', title: 'Against the facts', body: 'The same reading applied to any document, not only contracts: the claims it makes, checked against sources you can open.' },
          { icon: 'settings-2', title: 'Against your commercial rules', body: 'Your rules, written once and applied to every draft: what a payment term delay costs you, what a liability cap does to margin, whether the engagement sits inside IR35.' },
          { icon: 'workflow', title: 'The round trip, tracked', body: 'Upload, review, send the amendments, and receive the reply into the same thread — so the record of what was agreed lives with the document rather than in an inbox.' },
        ],
      },
      who: 'Anyone negotiating commercial contracts in rounds — procurement, in-house counsel, and the finance people who find out later what the terms actually cost.',
      status: 'building',
      access: 'In build. Design partners with a live negotiation to point it at are the ones shaping it.',
      site: { label: 'contractvetting.com', href: 'https://contractvetting.com' },
    },
    'agent-stack': {
      eyebrow: 'Product · the workflow layer',
      headline: 'The harness around the intelligence.',
      sub: 'Connect sources, carry context through agent steps, keep the outputs and pause where judgement belongs. Contract Vetting gives this a concrete purpose: a review flow that connects documents, knowledge, negotiation history and human sign-off.',
      why: {
        title: 'Why it exists.',
        body: 'The gap between an agent that can explain a migration and an agent that can perform one is all plumbing — auth, state, retries, and the question of who is accountable when it writes. We built the plumbing rather than demoing around it.',
      },
      does: {
        title: 'What it does.',
        items: [
          { icon: 'workflow', title: 'Durable agent runs', body: 'Runs, schedules and a run bus, so a job that takes an hour survives the tab being closed.' },
          { icon: 'git-compare', title: 'Deterministic migration seam', body: 'Wraps the same conversion pipeline the framework ships, so the result of a conversion is reproducible rather than re-generated on each attempt.' },
          { icon: 'user-check', title: 'A confirm gate before anything is pushed', body: 'The run pauses before it touches a repository. Delivery is a decision a person makes, every time.' },
          { icon: 'server', title: 'One codebase, two runtimes', body: 'Cloudflare Workers or Node from the same source, with storage adapters behind one interface.' },
        ],
      },
      who: 'Us, first — it is how the migration work gets delivered. Then teams who want agent work to arrive as reviewable pull requests rather than as advice.',
      status: 'preview',
      access: 'In internal use and not yet open. This page is its only documentation; talk to us if the shape is what you need.',
      site: null,
    },
    'gds-playground': {
      eyebrow: 'Product · government service delivery',
      headline: 'Lovable for government delivery.',
      sub: 'The prompt-to-app experience of Lovable or Cursor, specialised for the GDS cohort: describe the journey, watch it build with the real GOV.UK design system, and keep every version in git. A prototype that survives assessment rather than being thrown away at it.',
      why: {
        title: 'Why it exists.',
        body: 'Government delivery has its own physics, and generic tooling ignores all of it. A prototype that is not built from the actual design system teaches a team the wrong thing and has to be thrown away at assessment.',
      },
      does: {
        title: 'What it does.',
        items: [
          { icon: 'landmark', title: 'Real GOV.UK components', body: 'Journeys render through govuk-frontend 6.1.0 macros — the same components the live services use, not a lookalike.' },
          { icon: 'workflow', title: 'Journeys from a conversation', body: 'Describe the service and the pages appear; refine by talking rather than by wiring a prototype together by hand.' },
          { icon: 'git-compare', title: 'Every project version-controlled', body: 'Each project is a git repository, so a journey has history and a change has an author.' },
          { icon: 'globe-lock', title: 'Sovereign by construction', body: 'Model-agnostic and deployable inside your own boundary, which is a requirement in this sector rather than a preference.' },
        ],
      },
      who: 'Departments and suppliers working to the service standard who need a prototype that survives contact with an assessment.',
      status: 'preview',
      access: 'In use internally on real journeys, with pilots by arrangement.',
      site: { label: 'gdsplayground.com', href: 'https://gdsplayground.com' },
    },
  },

  // ── /about ───────────────────────────────────────────────────────────────
  about: {
    meta: {
      title: 'About — Pragmatical AI',
      description: 'A UK-based applied-AI company. We built the tools we kept wishing existed, in the order the work demanded them.',
    },
    hero: {
      eyebrow: 'About',
      headline: 'We built the tools we kept wishing existed.',
      sub: 'Pragmatical AI is a UK-based applied-AI company. Every product we ship started as a wall we hit doing the work, in the order we hit them.',
    },
    story: {
      title: 'Four walls, four products.',
      // Ordered by what a reader needs to know we do, not by the order we hit
      // them. Leading with the screens told everyone we were a front-end shop.
      items: [
        { q: 'The legacy estate would not be read', a: 'Every modernisation stalled in the same place — nobody could describe the system being replaced. Code Doctor makes that describable: topology across the estate, deterministic extraction from source and database, and the intent recovered from the people who still hold it.' },
        { q: 'The agent could reason but not deliver', a: 'A chat window that recommends a change and cannot make it puts the work back on you. agent-stack gave the reasoning somewhere to land: durable runs, connectors, and a GitHub App that opens the change behind a confirm gate. Nothing touches a repository without a person saying yes.' },
        { q: 'The screens took longer than the thinking', a: 'Generated interfaces looked plausible and did not work, and every fix cost another model call. So we built SWC: components that encode what to show and how, server-rendered, with a runtime that has no dependencies. A model authors the shape once; after that the data is just data.' },
        { q: 'Government work has its own physics', a: 'Public services have a design system, a service standard, and rules about where data may live. GDS Playground is that context made buildable: design a service by conversation, see it rendered with the real components, keep every version in git.' },
      ],
    },

    principles: {
      title: 'How we work.',
      items: [
        { icon: 'ruler', title: 'Practitioners, not a bench', body: 'The people who write the code are the people in the room. We do not staff an engagement with a layer of coordination.' },
        { icon: 'file-check', title: 'Assessment before proposal', body: 'We will not scope a transformation of a system we have not read. The first deliverable is an honest inventory, and it is yours either way.' },
        { icon: 'ban', title: 'What we will not do', body: 'No black-box transformation. No cosmetic modernisation that leaves the behaviour untouched. No architecture whose exit path runs through us.' },
      ],
    },
    // The reading we did before selling anything — a company credential rather
    // than a methodology essay standing on its own page.
    corpus: {
      title: 'We did the reading first.',
      sub: 'Before talking to a customer we built and analysed corpora at estate scale, because a method that has only seen one codebase is an anecdote.',
      stats: [
        { value: '6,254', label: 'Legacy repositories, licence-checked, with provenance' },
        { value: '18,227', label: 'UK public-sector repositories catalogued' },
        { value: '7,053', label: 'Cloned with full history' },
        { value: '4,515', label: 'Projects analysed' },
      ],
    },

    close: { headline: 'Let’s build something useful.', cta: 'Talk to us' },
  },

  // ── /contact ─────────────────────────────────────────────────────────────
  contact: {
    meta: {
      title: 'Ask Pragmatical AI — Cherga, automation and modernisation',
      description: 'Talk with our AI assistant about the Cherga framework, AI Studio, automation and system modernisation.',
    },
  },

  notFound: {
    title: '404',
    message: 'That page is not here. The products are, though.',
    back: 'Back to the homepage',
  },
};

// `t.a.b` from { a: { b } } — the same convention the framework's locale packs
// use, so adding a second language later means adding a pack, not touching a
// single page.
const isNested = ([, v]) => v && typeof v === 'object' && !Array.isArray(v);

function flatten(obj, prefix = 't') {
  const out = {};
  const entries = Object.entries(obj).map(([k, v]) => [`${prefix}.${k}`, v]);
  entries.filter(isNested).forEach(([path, value]) => Object.assign(out, flatten(value, path)));
  entries.filter((e) => !isNested(e)).forEach(([path, value]) => { out[path] = value; });
  return out;
}

const contentState = flatten(CONTENT);
return { CONTENT, flatten, contentState };
})();

const __m203 = (() => {
const shellBind = __m194.bind;
const shellDef = __m194.def;
const assistantDefs = __m199.assistantDefs;
const siteNavigation = __m201.siteNavigation;
const CONTENT = __m202.CONTENT;
// Site chrome: header, navigation, footer. One bind, used as the app layout.
//
// Product navigation and the appearance control share the framework shell.





const NAV = [
  { label: 'Our work', route: '/work' },
  { label: 'Approach', route: '/approach' },
  { label: 'Company', route: '/about' },
];

const appearance = {
  tag: 'site-appearance',
  children: [['s-b', '@v=ic', '@aria-label=$t.a11y.themeAction', '@title=$t.a11y.themeAction', '~cl:call:toggleAppearance', [
    ['s-ic', ':dataField=n', ':dataSource=appearanceIcon'],
  ]]],
  methods: {
    toggleAppearance() {
      this.store.setValue('appearance', this.store.getValue('appearance') === 'dark' ? 'light' : 'dark');
    },
  },
};

// Extend the shipped marketing shell declaratively, retaining its navigation,
// projection, skip link and footer. Theme controls are another SWC component.
const chromeDefs = [...assistantDefs, appearance, siteNavigation, {
  ...shellDef,
  children: [shellDef.children[0],
    ['s-hd', '@l=row', [
      ['s-ln', '@href=/', '@rg=brand-home', [
        ['img', '@src=/shots/pragmatical-symbol.svg', '@alt=', '@width=36', '@height=36'],
        ['span', '@rg=wordmark', '=pragmatical'], ['span', '@rg=wordmark-ai', '=ai'],
      ]],
      ['site-appearance'],
      ['site-navigation', ':dataField=expanded', ':dataSource=menuOpen'],
    ]],
    shellDef.children[2],
    ['site-assistant'],
  ],
}];

const FOOTER = {
  columns: [
    { title: 'Our work', links: [
      { label: 'Cherga', route: '/work#cherga' },
      { label: 'Contract Vetting', route: '/products/contract-vetting' },
      { label: 'UI framework', route: '/products/swc' },
    ] },
    { title: 'Pragmatical AI', links: [
      { label: 'Our approach', route: '/approach' },
      { label: 'Company', route: '/about' },
      { label: 'Get in touch', route: '/contact' },
    ] },
    { title: 'Explore further', links: [
      { label: 'Existing tools', route: '/products' },
      { label: 'Implementation', route: '/implementation' },
    ] },
  ],
  legal: CONTENT.brand.legal,
};

// `route` is passed at bind time on the server (see data/meta.js) so the first
// paint highlights the right nav item; the shell keeps it current after that.
function chrome(route = '') {
  return shellBind({
    nav: NAV,
    brand: { label: CONTENT.brand.name, icon: 'boxes' },
    cta: { label: 'Get in touch', route: '/contact' },
    footer: FOOTER,
    ...(route ? { route } : {}),
  });
}
return { NAV, chromeDefs, FOOTER, chrome };
})();

const __m204 = (() => {
const flatten = __m202.flatten;
const CONTENT = __m202.CONTENT;
const copy = {
  eyebrow: 'Pragmatical AI',
  title: 'The foundations\nand tools for\nAI-first software.',
  intro: 'We build the frameworks and tools that turn AI into useful software. From the interface to the work behind it, designed to work as a whole.',
  primary: 'Explore our work', secondary: 'How we build',
  note: 'One connected experience. From the interface to the work behind it.',
  studio: {
    caption: 'Illustrative Studio workflow. Sample content, not a live generation.',
    brief: 'Create an onboarding app for a small team. Show new starters their tasks, documents and who can help.',
    briefLabel: 'Start with what you need',
    briefNote: 'A description, a screenshot, a repository or API docs. Give the idea some context.',
    flowTitle: 'Understand the journey',
    flowNote: 'Review how the screens connect before moving into the interface.',
    screensNote: 'Review the screens, refine the structure and connect the data.',
  },
  foundation: {
    eyebrow: '01 / Cherga', title: 'AI-first framework.\nBatteries included.',
    body: 'Cherga brings our interface framework, agent execution and system understanding into one platform. Studio is the workspace we are building around it: a connected way to create new applications and evolve existing systems.',
    note: 'Built on our existing SWC framework, becoming Cherga. Platform integration is underway; Cloudflare is our first intended deployment target.',
    link: 'Meet Cherga',
  },
  contracts: {
    eyebrow: '02 / Contract Vetting',
    title: 'Every draft. Every rule.\nOne connected review.',
    body: 'The clause you removed comes back reworded. The commercial context sits in another document. Contract Vetting brings the drafts, sources and your rules into a review that carries its context from one round to the next.',
    harnessTitle: 'Context that survives the next draft.',
    harness: 'We are building a review harness that connects sources, rules and agent work around the reviewer. Contract Vetting gives that engineering a demanding purpose: a clear, traceable review across negotiation rounds.',
    link: 'Explore Contract Vetting', secondary: 'Our approach',
    caption: 'Designed review flow · Contract Vetting is in build',
  },
  products: {
    eyebrow: 'Purpose-built intelligence', title: 'Different work.\nThe same practical thinking.',
    body: 'Tools for understanding systems, connecting commerce and delivering agent work. Each has a clear purpose and an honest status.',
    link: 'Explore the full portfolio',
  },
  principles: {
    eyebrow: 'Built for the real world', title: 'Powerful tools.\nPeople in control.',
    body: 'Useful AI needs more than a model. It needs a structured interface, the right context and a clear path from intention to execution.',
  },
  engagement: {
    eyebrow: 'Build with us', title: 'Have a problem\nworth solving?',
    body: 'We also work with teams to put AI into practice: from understanding an existing estate to building the interfaces and workflows around it.',
    primary: 'Tell us what you have in mind', secondary: 'Meet the company',
  },
};

const homeState = {
  ...flatten(copy, 't.homepage'),
  't.a11y.architecture': 'Cherga platform architecture: authoring, application framework, execution and delivery',
  't.a11y.primaryNavigation': 'Primary navigation', menuOpen: 'false', 't.a11y.menu': 'Toggle navigation',
  studioStep: 'brief', contractView: 'draft', appearance: 'light',
  't.a11y.contractViews': 'Explore the contract review example',
  appearanceIcon: 'moon',
  't.a11y.themeAction': 'Switch to dark mode',
  't.a11y.switchToDark': 'Switch to dark mode',
  't.a11y.switchToLight': 'Switch to light mode',
  't.a11y.studioSteps': 'Explore the Studio workflow',
  'home.framework': [
    { title: 'Router: navigation has a data contract', body: 'Declare routes and the services they trigger. Pages receive the state they need through framework bindings, with a shared convention for how navigation starts data loading.' },
    { title: 'Store: one place for application state', body: 'Services write results, errors and loading status to declared store keys. Bound components respond to those changes, so each screen does not need its own synchronisation code.' },
    { title: 'HTTP service: streams included', body: 'Declare endpoints and destinations for their data. Reuse authentication headers, parsing, caching and request status. Stream AI responses into bound state without inventing another transport layer.' },
    { title: 'Authentication + RBAC: part of the design', body: 'Reuse session services and named permission rules. Interfaces can hide, redact or make fields read-only; service endpoints enforce access to the underlying data and actions.' },
    { title: 'Enterprise UI: configure, bind, compose', body: 'Forms, tables and workspaces already have an implementation. Give them data and configuration. AI selects and composes existing components instead of scaffolding every interaction from scratch.' },
    { title: 'SSR + AI: the same application', body: 'A lightweight Worker renders the page and metadata as HTML for search engines. The application adds interactive components and streaming AI services on that same foundation.' },
  ],
  'home.automation': [
    { title: '01 / Receive', body: 'A request arrives through the application or a connected event.' },
    { title: '02 / Prepare', body: 'Services retrieve context. An agent interprets or prepares the work.' },
    { title: '03 / Decide', body: 'Rules route the process. A person reviews an exception.' },
    { title: '04 / Complete', body: 'The record updates and the result returns to the application.' },
  ],
  'home.edge': [
    { title: 'Deliver', body: 'Application assets served through the CDN.' },
    { title: 'Execute', body: 'Worker-based application behaviour and connected services.' },
    { title: 'Cache deliberately', body: 'Reuse suitable responses with explicit freshness and access policies.' },
  ],
  'home.foundation': [
    { icon: 'boxes', title: 'Create something new', body: 'Build directly with the AI-first UI framework, or shape an application through Studio.' },
    { icon: 'scan-search', title: 'Bring what already exists', body: 'Code Doctor contributes topology discovery and validated intent, so migration fits the whole system.' },
    { icon: 'workflow', title: 'Work as one platform', body: 'Interfaces, services and agent flows share a connected experience. The runner is part of the foundation.' },
  ],
  'home.contractFlow': [
    { number: '01', icon: 'file-diff', title: 'Bring in the round', body: 'The returned draft, previous versions and negotiation context.' },
    { number: '02', icon: 'database', title: 'Ground the review', body: 'Relevant sources, dated references and your commercial rules.' },
    { number: '03', icon: 'scan-search', title: 'Trace what changed', body: 'Changes in meaning, supporting evidence and the questions to resolve.' },
    { number: '04', icon: 'user-check', title: 'Keep a person on the gate', body: 'A reviewer decides. The decision stays with the version.' },
  ],
  'home.products': CONTENT.products.items.filter((p) => ['code-doctor', 'shop2gpt', 'agent-stack'].includes(p.id))
    .map((p) => ({ ...p, statusLabel: CONTENT.products.statusLabels[p.status] })),
  'home.principles': [
    { icon: 'boxes', title: 'Designed for AI from the start', body: 'A structured framework gives AI a useful vocabulary and people a consistent interface.' },
    { icon: 'scan-search', title: 'Context before change', body: 'Understand behaviour, relationships and constraints before deciding what to transform.' },
    { icon: 'user-check', title: 'Visible decisions', body: 'Connect agent work with explicit data flows and human review where judgement matters.' },
  ],
};
return { homeState };
})();

const __m205 = (() => {
// Shared marketing-section mechanics (docs/composite-refs/utility-comms-commerce-marketing.md).
// A section is a full-width band with a centred content column (max 1080px, 64px
// vertical rhythm); marketing-page (s-mkp) stacks bound section instances. This
// factory removes the per-section boilerplate: meta shape, band CSS, and the
// static-props bind.

function sectionComposite(cfg) {
  const meta = {
    id: cfg.id,
    tag: cfg.tag,
    group: 'marketing',
    kind: 'section',
    variants: cfg.variants || ['default'],
    models: cfg.models || ['*'],
    requiresRoles: cfg.requiresRoles || [],
    optionalRoles: cfg.optionalRoles || [],
    requires: cfg.requires,
    internalTags: cfg.internalTags || [],
    contract: cfg.contract,
    regions: cfg.regions,
    theme: cfg.theme || ['--c-border', '--c-text-secondary', '--c-primary', '--space-6'],
  };

  const def = {
    tag: cfg.tag,
    props: cfg.props,
    children: cfg.children,
    // Declarative projection: a PURE project(props) → { region: dslArray }.
    // The core auto-wires it (connect + declared-prop change) and SSR runs the
    // same function, so sections need no hand-written _project() plumbing.
    ...(cfg.project ? { project: cfg.project } : {}),
    ...(cfg.callbacks ? { callbacks: cfg.callbacks } : {}),
    ...(cfg.methods ? { methods: cfg.methods } : {}),
  };

  function bind(b = {}) {
    const props = {};
    (cfg.bindProps || []).forEach((k) => { if (b[k] !== undefined) props[`:${k}`] = b[k]; });
    if (cfg.bindExtras) cfg.bindExtras(b, props);
    return [cfg.tag, props];
  }

  return { meta, def, bind };
}
return { sectionComposite };
})();

const __m206 = (() => {
const sectionComposite = __m205.sectionComposite;
// composite: hero (s-hro). Headline 48/800, sub 18 muted, primary + ghost CTA,
//   optional media right. Media renders via projection (setData), the same
//   pattern as features/testimonials: nested '?prop:' show-ifs evaluate once
//   (see _auth.js note), so the previous '?prop:hasMedia' + with-media attribute
//   never showed client-side; an :empty CSS guard replaces the attribute.



const { meta, def, bind } = sectionComposite({
  id: 'hero',
  tag: 's-hro',
  requires: ['s-cn', 's-l', 's-b', 's-img'],
  contract: {
    headline: { type: 'slot', required: true },
    sub: { type: 'slot', required: false },
    cta: { type: 'action', required: false },
    cta2: { type: 'action', required: false },
    media: { type: 'slot', required: false }, // image url
  },
  regions: ['content', 'media'],
  props: ['headline', 'sub', 'ctaLabel', 'ctaRoute', 'cta2Label', 'cta2Route', 'media'],
  children: [
    ['s-cn', '@rg=band', [
      ['s-cn', '@rg=hro-copy', [
        ['h1', '@rg=hro-headline', '#headline'],
        ['s-l', '?prop:sub', '@rg=hro-sub', '#sub'],
        ['s-cn', '@rg=hro-ctas', [
          ['s-b', '?prop:ctaLabel', '@c=pr', '@sz=lg', '~cl:call:_onCta', [['span', '#ctaLabel']]],
          ['s-b', '?prop:cta2Label', '@v=ol', '@sz=lg', '~cl:call:_onCta2', [['span', '#cta2Label']]],
        ]],
      ]],
      ['s-cn', '@rg=hro-media', ':dataKey=media', ':dataField=children'],
    ]],
  ],
  project({ media }) {
    return { media: media ? [['s-img', { ':src': media }]] : [] };
  },
  methods: {
    _onCta() { if (this.ctaRoute) this.router.navigateTo(this.ctaRoute); },
    _onCta2() { if (this.cta2Route) this.router.navigateTo(this.cta2Route); },
  },
  bindProps: ['headline', 'sub', 'media'],
  bindExtras(b, props) {
    if (b.cta) { props[':ctaLabel'] = b.cta.label; props[':ctaRoute'] = b.cta.route; }
    if (b.cta2) { props[':cta2Label'] = b.cta2.label; props[':cta2Route'] = b.cta2.route; }
  },
});


const defs = [def];
return { meta, def, bind, defs };
})();

const __m207 = (() => {
const sectionComposite = __m205.sectionComposite;
// composite: features (s-fea). Icon + title + body cards, 3-up grid.



const { meta, def, bind } = sectionComposite({
  id: 'features',
  tag: 's-fea',
  requires: ['s-cn', 's-c', 's-l', 's-ic'],
  contract: {
    title: { type: 'slot', required: false },
    sub: { type: 'slot', required: false },
    items: { type: 'slot', required: true }, // [{ icon, title, body }]
  },
  regions: ['grid'],
  props: ['title', 'sub', 'items'],
  children: [
    ['s-cn', '@rg=band', [
      ['h2', '?prop:title', '@rg=sec-title', '#title'],
      ['s-l', '?prop:sub', '@rg=sec-sub', '#sub'],
      ['s-cn', '@rg=fea-grid', ':dataKey=cards', ':dataField=children'],
    ]],
  ],
  project({ items }) {
    return {
      cards: (items || []).map((it) => (
        ['s-c', [
          it.icon ? ['s-ic', `@n=${it.icon}`] : null,
          ['s-l', '@rg=fea-title', [String(it.title ?? '')]],
          it.body !== undefined ? ['s-l', '@rg=fea-body', [String(it.body)]] : null,
        ].filter(Boolean)]
      )),
    };
  },
  bindProps: ['title', 'sub', 'items'],
});


const defs = [def];
return { meta, def, bind, defs };
})();

const __m208 = (() => {
const sectionComposite = __m205.sectionComposite;
// composite: stats-band (s-stb). Inline stat tiles band. requiresRoles: ['measure'].



const { meta, def, bind } = sectionComposite({
  id: 'stats-band',
  tag: 's-stb',
  requiresRoles: ['measure'],
  requires: ['s-cn', 's-l'],
  contract: {
    stats: { type: 'slot', required: true }, // [{ label, value }]
  },
  regions: ['stats'],
  props: ['stats'],
  children: [
    ['s-cn', '@rg=band', [
      ['s-cn', '@rg=stb-row', ':dataKey=tiles', ':dataField=children'],
    ]],
  ],
  project({ stats }) {
    return {
      tiles: (stats || []).map((s) => (
        ['s-cn', '@rg=stb-tile', [
          ['s-l', '@rg=stb-value', [String(s.value ?? '')]],
          ['s-l', '@rg=stb-label', [String(s.label ?? '')]],
        ]]
      )),
    };
  },
  bindProps: ['stats'],
});


const defs = [def];
return { meta, def, bind, defs };
})();

const __m209 = (() => {
const sectionComposite = __m205.sectionComposite;
// composite: faq (s-faq). Accordion of question/answer pairs via s-acc.



const { meta, def, bind } = sectionComposite({
  id: 'faq',
  tag: 's-faq',
  requires: ['s-cn', 's-acc', 's-dt', 's-acc-d', 's-acc-s', 's-l'],
  contract: {
    title: { type: 'slot', required: false },
    items: { type: 'slot', required: true }, // [{ q, a }]
  },
  regions: ['items'],
  props: ['title', 'items'],
  children: [
    ['s-cn', '@rg=band', [
      ['h2', '?prop:title', '@rg=sec-title', '#title'],
      ['s-cn', '@rg=faq-list', ':dataKey=accordion', ':dataField=children'],
    ]],
  ],
  // One s-dt per pair: s-acc-s is the summary row, s-acc-d the region the
  // accordion CSS reveals on [open]. (An earlier shape put the summary INSIDE
  // s-acc-d, which the stylesheet hides until open — so the list rendered as
  // an empty box.)
  project({ items }) {
    return {
      accordion: [
        ['s-acc', (items || []).map((it) => (
          ['s-dt', [
            ['s-acc-s', [String(it.q ?? '')]],
            ['s-acc-d', [['s-l', '@rg=faq-a', [String(it.a ?? '')]]]],
          ]]
        ))],
      ],
    };
  },
  bindProps: ['title', 'items'],
});


const defs = [def];
return { meta, def, bind, defs };
})();

const __m210 = (() => {
const sectionComposite = __m205.sectionComposite;
// composite: cta (s-cta). Primary-tinted band: headline + one button.



const { meta, def, bind } = sectionComposite({
  id: 'cta',
  tag: 's-cta',
  requires: ['s-cn', 's-l', 's-b'],
  contract: {
    headline: { type: 'slot', required: true },
    sub: { type: 'slot', required: false },
    action: { type: 'action', required: true },
  },
  regions: ['content'],
  props: ['headline', 'sub', 'actionLabel', 'actionRoute'],
  children: [
    ['s-cn', '@rg=band', [
      ['h2', '@rg=sec-title', '#headline'],
      ['s-l', '?prop:sub', '@rg=sec-sub', '#sub'],
      ['s-cn', '@rg=cta-action', [
        ['s-b', '@c=pr', '@sz=lg', '~cl:call:_onAction', [['span', '#actionLabel']]],
      ]],
    ]],
  ],
  methods: {
    _onAction() { if (this.actionRoute) this.router.navigateTo(this.actionRoute); },
  },
  bindProps: ['headline', 'sub'],
  bindExtras(b, props) {
    if (b.action) { props[':actionLabel'] = b.action.label; props[':actionRoute'] = b.action.route; }
  },
});


const defs = [def];
return { meta, def, bind, defs };
})();

const __m211 = (() => {
const CONTENT = __m202.CONTENT;
const navigationState = __m200.navigationState;
// Per-request head metadata: the one thing this site needs getServerData for.
//
// Imported by serve-prod.js AND worker/index.js rather than written twice. The
// flagship site duplicates this body between its two entry points with a
// comment on each saying it matches the other, which is a promise rather than a
// mechanism.



const PAGE_META = {
  '/': { title: 'Pragmatical AI — Foundations and tools for AI-first software', description: 'The company behind Cherga and Contract Vetting. Building AI-first interfaces, connected services and agent workflows.' },
  '/work': { title: 'Our work — Pragmatical AI', description: 'Explore Cherga, our integrated AI-first platform direction, and Contract Vetting, our flagship domain product.' },
  '/approach': { title: 'Our approach — Pragmatical AI', description: 'Structured interfaces, system understanding and connected execution. How we approach AI-first software.' },
  '/implementation': CONTENT.implementation.meta,
  '/legacy-to-model': CONTENT.legacyToModel.meta,
  '/agentic-commerce': CONTENT.commerce.meta,
  '/process-automation': CONTENT.automation.meta,
  '/internal-ai': CONTENT.internalAi.meta,
  '/products': CONTENT.products.meta,
  '/about': CONTENT.about.meta,
  '/contact': CONTENT.contact.meta,
};

const FALLBACK = {
  title: `${CONTENT.brand.name} — ${CONTENT.brand.motto}`,
  description: CONTENT.home.meta.description,
};

function docMetaFor(url = '/', params = {}) {
  const path = String(url).split('?')[0].replace(/\/+$/, '') || '/';

  // A product page is named after its product, so the tab and the link preview
  // say which one — five routes, one component, five different titles.
  const product = path.startsWith('/products/')
    ? CONTENT.products.items.find((p) => p.href === path)
    : null;
  const meta = product
    ? { title: `${product.name} — ${CONTENT.brand.name}`, description: product.body }
    : PAGE_META[path] || FALLBACK;

  return {
    ...navigationState(path),
    assistantPrimary: path === '/' || path === '/contact',
    docRootAttrs: { lang: 'en' },
    docTitle: meta.title,
    docDescription: meta.description,
    // The shell highlights the current nav item from this, so the first paint
    // is already on the right one.
    route: path,
    // Path params are published by the client router as `router.<name>` — but
    // the SSR serializer deliberately DROPS `route` and every `router.*` key
    // from the embedded state, on the assumption the client republishes them.
    // It does, and too late: hydration evaluates the page's conditionals first,
    // finds no key, and removes the branch the server had correctly rendered.
    // Every product page was blank on a direct load, a hard refresh, and to a
    // crawler, while working fine when reached by a click.
    //
    // So the param travels under its own name, which IS serialized, and
    // main.js mirrors router.pid onto it for client-side navigation.
    ...Object.fromEntries(Object.entries(params || {}).map(([k, v]) => [k, v])),
  };
}
return { docMetaFor };
})();

const __m212 = (() => {
// Responsive explanatory diagram: semantic text, not labels baked into an image.
const platformMap = { tag: 'platform-map', children: [
  ['s-cn', '@rg=platform-map', '@l=vertical', [
    ['p', '@rg=map-caption', '=One foundation / Multiple capabilities'],
    ['s-cn', '@rg=map-inputs', '@l=fg', [
      ['span', '=AI Studio'], ['span', '=Code Doctor'],
    ]],
    ['s-cn', '@rg=map-core', [
      ['strong', '=Cherga'], ['p', '=Router · store · enterprise components'],
    ]],
    ['s-cn', '@rg=map-outputs', '@l=fg', [
      ['s-cn', [['strong', '=Worker SSR'], ['p', '=HTML + metadata']]],
      ['s-cn', [['strong', '=HTTP service'], ['p', '=Data + AI streams']]],
      ['s-cn', [['strong', '=Shared runner'], ['p', '=Flows + retries']]],
    ]],
    ['s-cn', '@rg=map-network', [['strong', '=Cloudflare'], ['p', '=Delivery · execution · edge cache']]],
    ['p', '@rg=map-caption', '=Conceptual architecture · integration in progress'],
  ]],
] };
return { platformMap };
})();

const __m213 = (() => {
// A readable architecture schema, composed with native framework layouts.
const node = (title, body) => ['s-cn', '@rg=architecture-node', [
  ['strong', `=${title}`], ['p', `=${body}`],
]];
const layer = (number, title, description, nodes) => ['s-cn', '@rg=architecture-layer', '@l=vertical', [
  ['s-cn', '@rg=architecture-label', '@l=row', [['span', `=${number}`], ['strong', `=${title}`], ['p', `=${description}`]]],
  ['s-cn', '@rg=architecture-nodes', '@l=fg', nodes],
]];
const architectureMap = { tag: 'architecture-map', children: [
  ['figure', '@rg=architecture-schema', '@aria-label=$t.a11y.architecture', [
    layer('01', 'Create + understand', 'Different starting points. The same application foundation.', [
      node('AI Studio', 'Shape screens, connect services and compose flows in one workspace.'),
      node('Code Doctor', 'Discover system topology and intent. Plan migration into services and flows.'),
      node('ChergaJS', 'Use the AI-first UI framework directly in your codebase.'),
    ]),
    ['s-cn', '@rg=architecture-connector', '@l=row', [['s-ic', '@n=arrow-down'], ['p', '=Application definitions · data bindings · business rules']]],
    layer('02', 'Cherga application framework', 'The reusable machinery every application needs.', [
      node('Enterprise components', 'Data-ready forms, tables and workspaces, composed declaratively.'),
      node('Router + store', 'Routes select the view. Shared state drives its bound components.'),
      node('Streaming HTTP service', 'Declared endpoints manage results, loading, errors and streamed updates.'),
    ]),
    ['s-cn', '@rg=architecture-policy', '@l=row', [
      ['strong', '=Authentication + RBAC'],
      ['p', '=Session and authentication services · named UI permission rules · authorisation enforced at service endpoints'],
    ]],
    ['s-cn', '@rg=architecture-connector', '@l=row', [['s-ic', '@n=arrow-down-up'], ['p', '=Service requests · application events · results']]],
    layer('03', 'Execute the work', 'A shared runtime for the interface and the process behind it.', [
      node('Lightweight Worker SSR', 'Render HTML and metadata for search. Connect interactive AI capabilities.'),
      node('Services + integrations', 'Connect business data, existing systems and model providers.'),
      node('Shared agent runner', 'Execute flows and agent tasks, with retry handling and human review gates.'),
    ]),
    ['s-cn', '@rg=architecture-connector', '@l=row', [['s-ic', '@n=arrow-down'], ['p', '=Distributed delivery · Worker execution']]],
    ['s-cn', '@rg=architecture-delivery', '@l=row', [
      ['strong', '=Cloudflare-first'],
      ['p', '=CDN-delivered assets · edge-rendered pages · policy-controlled caching · connected services and flows'],
    ]],
    ['figcaption', '=Platform architecture and integration direction. The framework is available today; Studio, modernisation and deployment are being brought together around it.'],
  ]],
] };
return { architectureMap };
})();

const __m214 = (() => {
const platformMap = __m212.platformMap;
const architectureMap = __m213.architectureMap;
const feature = { tag: 'home-feature', children: [['s-cn', '@l=vertical', '@rg=feature', [['h3', '^title'], ['p', '^body']]]] };
const intro = { tag: 'home-intro', children: [['s-sc', '@rg=intro', [
  ['s-cn', '@rg=home-wrap', '@l=vertical', [
    ['s-cn', '@rg=conversation-intro', '@l=vertical', [
      ['s-l', '@rg=eyebrow', '=AI-first framework. One connected platform.'],
      ['h1', '=Build the app.\nWe built the foundation.'],
      ['p', '@rg=lead-small', '=Tell us what you want to create, automate or modernise. Explore how Cherga’s framework, Studio and shared runtime fit your next application.'],
    ]],
    ['primary-assistant'],
    ['s-cn', '@l=row', '@rg=conversation-explore', [
      ['s-b', '@v=tx', '~cl:nav:/work', [['span', '=Explore the platform'], ['s-ic', '@n=arrow-up-right']]],
      ['s-ln', '@href=/approach', '=See the architecture'],
    ]],
    ['s-cn', '@l=fg', '@rg=capability-strip', [
      ['s-cn', [['span', '=01'], ['h3', '=Create'], ['p', '=Studio + direct framework use']]],
      ['s-cn', [['span', '=02'], ['h3', '=Automate'], ['p', '=Services, agents and decisions']]],
      ['s-cn', [['span', '=03'], ['h3', '=Modernise'], ['p', '=Discover topology and intent']]],
      ['s-cn', [['span', '=04'], ['h3', '=Run'], ['p', '=Distributed, Cloudflare-first']]],
    ]],
  ]],
]]] };
const foundation = { tag: 'home-foundation', children: [['s-sc', '@rg=foundation', '@id=cherga', [
  ['s-cn', '@rg=home-wrap', '@l=vertical', [
    ['s-l', '@rg=eyebrow', '=Cherga / The platform we are building'],
    ['s-cn', '@l=fg', '@rg=section-intro', [
      ['h2', '=Enterprise foundations.\nReady for your data.'],
      ['s-cn', [
        ['p', '@rg=lead-small', '=Cherga is built from the architecture up: router, store, streaming HTTP services, authentication and permission-aware enterprise components. Configure the application around your data and business rules.'],
        ['p', '@rg=body-note', '=The interface, data layer and execution model share a deliberate architecture. AI composes application definitions against that foundation; your team supplies the data, rules and behaviour that make the product yours.'],
      ]],
    ]],
    ['architecture-map'],
    ['s-ls', '@l=fg', '@rg=capabilities', ':dataSource=home.framework', ':itemRenderer=home-feature'],
    ['s-cn', '@l=fg', '@rg=research-proof', [
      ['s-cn', [['strong', '=100,000'], ['p', '=repositories analysed']]],
      ['s-cn', [['h3', '=Generate the difference. Reuse the foundation.'], ['p', '=We studied recurring patterns and common mistakes to identify the scaffolding applications repeatedly need. Reusable components and shared behaviour give AI less boilerplate to generate—and teams less boilerplate to maintain.'], ['s-ln', '@href=/approach', '=Why we built a new foundation']]],
    ]],
    ['s-cn', '@l=row', '@rg=platform-foot', [
      ['p', '=Our existing SWC framework is becoming Cherga. Platform integration is underway.'],
      ['s-ln', '@href=/products/swc', '=Use the UI framework'],
    ]],
  ]],
]]] };
const studio = { tag: 'home-studio', children: [['s-sc', '@rg=studio', [
  ['s-cn', '@rg=home-wrap', '@l=vertical', [
    ['s-cn', '@l=fg', '@rg=section-intro', [
      ['s-cn', [['s-l', '@rg=eyebrow', '=AI Studio'], ['h2', '=Start with an idea.\nOr what already exists.']]],
      ['s-cn', [
        ['p', '@rg=lead-small', '=Studio is the workspace we are building around Cherga. Shape the journeys and screens, connect data and services, define the work behind them, and publish through the platform.'],
        ['p', '@rg=body-note', '=Bring a brief, a repository or existing system context. The goal is one continuous environment for creating, migrating and improving an application.'],
        ['s-ln', '@href=/products/ai-studio', '=Explore the Studio direction'],
      ]],
    ]],
    ['s-cn', '@rg=product-capture', [
      ['s-cn', '@l=row', '@rg=capture-heading', [['strong', '=A workspace shaped for its context'], ['span', '=Studio / page builder']]],
      ['img', '@src=/shots/studio-builder.jpg', '@alt=The Studio page builder: a component palette, the canvas, a layer tree, and the properties of the selected block', '@loading=lazy', '@width=2160', '@height=1350'],
      ['s-cn', '@l=row', '@rg=capture-caption', [['p', '=Palette, canvas, layer tree and the properties of whatever is selected.'], ['s-ln', '@href=/products/ai-studio', '=Explore the Studio direction']]],
    ]],
  ]],
]]] };
const automation = { tag: 'home-automation', children: [['s-sc', '@rg=automation', [
  ['s-cn', '@rg=home-wrap', '@l=vertical', [
    ['s-cn', '@l=fg', '@rg=section-intro', [
      ['s-cn', [['s-l', '@rg=eyebrow', '=Automation + agents'], ['h2', '=The screen starts it.\nThe system carries it through.']]],
      ['p', '@rg=lead-small', '=Connect application events, service calls, agent tasks and human decisions. Our runner provides the shared execution foundation, so automation is part of the application experience.'],
    ]],
    ['s-cn', '@rg=process-visual', [
      ['s-l', '@rg=eyebrow', '=Illustrated workflow / A service request'],
      ['s-ls', '@l=fg', '@rg=process-steps', ':dataSource=home.automation', ':itemRenderer=home-feature'],
      ['p', '@rg=process-caption', '=Deterministic rules where they fit. AI where interpretation helps. People where judgement matters.'],
    ]],
    ['s-ln', '@href=/approach', '=How we connect execution'],
  ]],
]]] };
const modernisation = { tag: 'home-modernisation', children: [['s-sc', '@rg=modernisation', [
  ['s-cn', '@rg=home-wrap', '@l=fg', [
    ['s-cn', [
      ['s-l', '@rg=eyebrow', '=System understanding + modernisation'], ['h2', '=Understand the estate.\nThen change it.'],
      ['p', '@rg=lead-small', '=Code Doctor brings topology discovery and validated intent into Cherga. Understand how repositories, services, infrastructure and business processes depend on one another before deciding what to migrate.'],
      ['p', '@rg=body-note', '=Preserve what works. Restructure what needs to change. Turn useful capabilities into connected services and flows, with decisions recorded in a reusable rulebook.'],
      ['s-ln', '@href=/products/code-doctor', '=Inside Code Doctor'],
    ]],
    ['s-cn', '@rg=topology-visual', [
      ['s-l', '@rg=eyebrow', '=From discovery to an integrated target'],
      ['s-cn', '@l=row', '@rg=topology-sources', [['span', '=Repositories'], ['span', '=Infrastructure'], ['span', '=Running services']]],
      ['s-cn', '@rg=topology-core', [['strong', '=Topology + intent'], ['p', '=Relationships, constraints and validated knowledge']]],
      ['s-cn', '@l=row', '@rg=topology-targets', [['span', '=Interfaces'], ['span', '=Services'], ['span', '=Flows']]],
      ['p', '=Conceptual migration path · One connected system model'],
    ]],
  ]],
]]] };
const edge = { tag: 'home-edge', children: [['s-sc', '@rg=edge', [
  ['s-cn', '@rg=home-wrap', '@l=vertical', [
    ['s-l', '@rg=eyebrow', '=Cloudflare-first'],
    ['s-cn', '@l=fg', '@rg=section-intro', [
      ['h2', '=Build for the network.\nPublish as a whole.'],
      ['p', '@rg=lead-small', '=We are designing the deployment path around distributed applications: interfaces delivered through the CDN, suitable content cached at the edge, and services and workflows connected behind them.'],
    ]],
    ['s-ls', '@l=fg', '@rg=edge-layers', ':dataSource=home.edge', ':itemRenderer=home-feature'],
    ['p', '@rg=platform-note', '=Cloudflare is our first intended integrated target. Cache policies depend on content and access requirements; private application state remains a separate responsibility.'],
  ]],
]]] };
const contracts = { tag: 'home-contracts', children: [['s-sc', '@rg=contracts', '@id=contract-vetting', [
  ['s-cn', '@rg=home-wrap', '@l=fg', [
    ['s-cn', [['s-l', '@rg=eyebrow', '=Applied in the domain / Contract Vetting'], ['h2', '=Context across\nevery review.'], ['p', '@rg=lead-small', '=Our domain product connects document rounds, evidence, agent work and reviewer decisions. A concrete application of the data-flow harnesses we build.'], ['s-ln', '@href=/products/contract-vetting', '=Explore Contract Vetting']]],
    ['s-cn', '@rg=contract-note', [
      ['s-l', '@rg=eyebrow', '=Illustrative example / Product in build'], ['h3', '=A clause returns.\nIts history should too.'],
      ['p', '=Returned draft · previous position · supporting context · reviewer decision'],
      ['p', '@rg=body-note', '=Contract Vetting has its own product identity and direction.'],
    ]],
  ]],
]]] };
const engagement = { tag: 'home-engagement', children: [['s-sc', '@rg=engagement', [
  ['s-cn', '@rg=home-wrap', '@l=row', [
    ['s-cn', [['s-l', '@rg=eyebrow', '=Pragmatical AI'], ['h2', '=What are you building?']]],
    ['s-b', '@c=pr', '~cl:nav:/contact', [['span', '=Start a conversation'], ['s-ic', '@n=arrow-up-right']]],
  ]],
]]] };
const homeSectionDefs = [architectureMap, platformMap, feature, intro, foundation, studio, automation, modernisation, edge, contracts, engagement];
return { homeSectionDefs };
})();

const __m215 = (() => {
const homeSectionDefs = __m214.homeSectionDefs;
// Composition only: declarative sections read their content from the store.
const homeDefs = [...homeSectionDefs, {
  tag: 'page-home',
  children: [['home-intro'], ['home-foundation'], ['home-studio'], ['home-automation'], ['home-modernisation'], ['home-edge'], ['home-contracts'], ['home-engagement']],
}];
return { homeDefs };
})();

const __m216 = (() => {
// Band helpers. Every page is a stack of full-width bands with a centred
// column, so the padding, the hairline between bands and the tinted variants
// are decided once here rather than restated per page.
//
// These are DSL builders, not components: a band has no behaviour, and a
// component with no behaviour is a stylesheet wearing a costume.

function band(name, children, { alt = false, tint = false, tight = false } = {}) {
  return ['s-sc', `@band=${name}`, ...(alt ? ['@alt='] : []), ...(tint ? ['@tint='] : []),
    ...(tight ? ['@tight='] : []), [['s-cn', children]]];
}

function eyebrow(text) {
  return text ? ['s-l', '@rg=eyebrow', `=${text}`] : null;
}

// A heading and its standfirst, for bands built by hand rather than by a
// composite that owns its own title.
function sectionHead(title, sub) {
  return ['s-cn', '@rg=head', [
    ['h2', '@rg=head-title', `=${title}`],
    ...(sub ? [['s-l', '@rg=head-sub', `=${sub}`]] : []),
  ]];
}

// An outbound link to a product's own site. Always a new tab: a reader
// comparing five tools should not lose this page to do it, and rel=noopener is
// the price of target=_blank.
function outbound(label, href) {
  return ['s-ln', `@href=${href}`, '@target=_blank', '@rel=noopener noreferrer', '@rg=out', [
    ['span', `=${label}`],
    ['s-ic', '@n=arrow-up-right'],
  ]];
}

const SECTION_CSS = `
/* Two columns, not a stack: the heading and its standfirst sit side by side
   over a hairline, which reclaims the empty right gutter and stops five bands
   opening with the identical left-aligned block. */
s-cn[rg=head] { display: grid; grid-template-columns: 1fr; gap: 14px; max-width: none; margin: 0 0 28px; padding-bottom: 18px; border-bottom: 1px solid var(--c-border); }
@media (min-width: 900px) { s-cn[rg=head] { grid-template-columns: minmax(0, 7fr) minmax(0, 5fr); gap: 48px; align-items: baseline; } }
h2[rg=head-title] { margin: 0; font-family: var(--font-display); font-weight: 700; letter-spacing: -0.015em; font-size: clamp(1.9rem, 1.3rem + 2vw, 3rem); line-height: 1.06; text-wrap: balance; }
label[is=s-l][rg=head-sub] { display: block; max-width: var(--site-text); margin-top: 0; font-size: 1rem; line-height: 1.62; color: var(--c-text-secondary); }
s-cn[rg=band-action] { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 36px; }
s-cn[rg=band-action] button[is=s-b] s-ic { width: 15px; height: 15px; margin-left: 8px; }
s-ln[rg=out] a { display: inline-flex; align-items: center; gap: 6px; color: var(--c-primary-hover); font-weight: 600; font-size: 0.875rem; text-decoration: none; }
s-ln[rg=out] a:hover { text-decoration: underline; }
s-ln[rg=out] s-ic { width: 13px; height: 13px; }
`;
return { band, eyebrow, sectionHead, outbound, SECTION_CSS };
})();

const __m217 = (() => {
const heroBind = __m206.bind;
const featuresBind = __m207.bind;
const statsBind = __m208.bind;
const faqBind = __m209.bind;
const ctaBind = __m210.bind;
const CONTENT = __m202.CONTENT;
const band = __m216.band;
const eyebrow = __m216.eyebrow;
const sectionHead = __m216.sectionHead;
const SECTION_CSS = __m216.SECTION_CSS;
// /implementation — the spine service.








const P = CONTENT.implementation;

const page = {
  tag: 'page-implementation',
  children: [
    band('hero', [
      eyebrow(P.hero.eyebrow),
      heroBind({ headline: P.hero.headline, sub: P.hero.sub, cta: { label: P.hero.cta, route: '/contact' } }),
    ]),
    band('stages', [featuresBind({ title: P.stages.title, items: P.stages.items })], { alt: true }),
    band('bring', [
      sectionHead(P.bring.title, P.bring.sub),
      statsBind({ stats: P.bring.stats }),
    ], { tint: true }),
    band('shapes', [faqBind({ title: P.shapes.title, items: P.shapes.items })]),
    ctaBind({ headline: P.close.headline, action: { label: P.close.cta, route: '/contact' } }),
  ],
  css: `
page-implementation { display: block; }
${SECTION_CSS}
page-implementation s-sc[band=hero] { padding-bottom: 24px; }
page-implementation s-sc[band=hero] s-hro { padding-top: 8px; }
/* Five stages read as a sequence: a four-column grid would break them into
   two rows and imply a pairing that is not there. */
page-implementation s-sc[band=stages] s-fea s-cn[rg=fea-grid] { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
`,
};

const implementationDefs = [page];
return { implementationDefs };
})();

const __m218 = (() => {
const heroBind = __m206.bind;
const featuresBind = __m207.bind;
const statsBind = __m208.bind;
const faqBind = __m209.bind;
const ctaBind = __m210.bind;
const CONTENT = __m202.CONTENT;
const band = __m216.band;
const eyebrow = __m216.eyebrow;
const sectionHead = __m216.sectionHead;
const SECTION_CSS = __m216.SECTION_CSS;
// /legacy-to-model — the flagship offer, and the page a procurement reviewer
// will read hardest. Its boundaries section is the credibility, not the
// small print, so it is a full band rather than a footnote.








const P = CONTENT.legacyToModel;

const page = {
  tag: 'page-legacy',
  children: [
    band('hero', [
      eyebrow(P.hero.eyebrow),
      heroBind({ headline: P.hero.headline, sub: P.hero.sub, cta: { label: P.hero.cta, route: '/contact' } }),
    ]),
    band('problem', [featuresBind({ title: P.problem.title, items: P.problem.items })], { alt: true }),
    band('pipeline', [faqBind({ title: P.pipeline.title, items: P.pipeline.items })]),
    band('corpus', [
      sectionHead(P.corpus.title, P.corpus.sub),
      statsBind({ stats: P.corpus.stats }),
    ], { tint: true }),
    band('boundaries', [featuresBind({ title: P.boundaries.title, sub: P.boundaries.sub, items: P.boundaries.items })], { alt: true }),
    ctaBind({ headline: P.close.headline, action: { label: P.close.cta, route: '/contact' } }),
  ],
  css: `
page-legacy { display: block; }
${SECTION_CSS}
page-legacy s-sc[band=hero] { padding-bottom: 24px; }
page-legacy s-sc[band=hero] s-hro { padding-top: 8px; }
`,
};

const legacyDefs = [page];
return { legacyDefs };
})();

const __m219 = (() => {
const heroBind = __m206.bind;
const featuresBind = __m207.bind;
const faqBind = __m209.bind;
const ctaBind = __m210.bind;
const CONTENT = __m202.CONTENT;
const band = __m216.band;
const eyebrow = __m216.eyebrow;
const SECTION_CSS = __m216.SECTION_CSS;
// /agentic-commerce — the service line shop2gpt delivers.







const P = CONTENT.commerce;

const page = {
  tag: 'page-commerce',
  children: [
    band('hero', [
      eyebrow(P.hero.eyebrow),
      heroBind({
        headline: P.hero.headline,
        sub: P.hero.sub,
        cta: { label: P.hero.cta, route: '/products/shop2gpt' },
        cta2: { label: P.hero.cta2, route: '/contact' },
      }),
    ]),
    band('what', [featuresBind({ title: P.what.title, items: P.what.items })], { alt: true }),
    band('how', [faqBind({ title: P.how.title, items: P.how.items })]),
    ctaBind({ headline: P.close.headline, action: { label: P.close.cta, route: '/contact' } }),
  ],
  css: `
page-commerce { display: block; }
${SECTION_CSS}
page-commerce s-sc[band=hero] { padding-bottom: 24px; }
page-commerce s-sc[band=hero] s-hro { padding-top: 8px; }
`,
};

const commerceDefs = [page];
return { commerceDefs };
})();

const __m220 = (() => {
const heroBind = __m206.bind;
const featuresBind = __m207.bind;
const ctaBind = __m210.bind;
const CONTENT = __m202.CONTENT;
const band = __m216.band;
const eyebrow = __m216.eyebrow;
const SECTION_CSS = __m216.SECTION_CSS;
// /process-automation — the same extraction method, pointed at operations.






const P = CONTENT.automation;

const page = {
  tag: 'page-automation',
  children: [
    band('hero', [
      eyebrow(P.hero.eyebrow),
      heroBind({ headline: P.hero.headline, sub: P.hero.sub, cta: { label: P.hero.cta, route: '/contact' } }),
    ]),
    band('method', [featuresBind({ title: P.method.title, items: P.method.items })], { alt: true }),
    ctaBind({ headline: P.close.headline, action: { label: P.close.cta, route: '/contact' } }),
  ],
  css: `
page-automation { display: block; }
${SECTION_CSS}
page-automation s-sc[band=hero] { padding-bottom: 24px; }
page-automation s-sc[band=hero] s-hro { padding-top: 8px; }
`,
};

const automationDefs = [page];
return { automationDefs };
})();

const __m221 = (() => {
const heroBind = __m206.bind;
const featuresBind = __m207.bind;
const faqBind = __m209.bind;
const ctaBind = __m210.bind;
const CONTENT = __m202.CONTENT;
const band = __m216.band;
const eyebrow = __m216.eyebrow;
const sectionHead = __m216.sectionHead;
const SECTION_CSS = __m216.SECTION_CSS;
// /internal-ai — setting an organisation up to use AI on its own data.
//
// The capability the site was missing entirely: it listed six tools and never
// said that the dataflow, the governance around it and the screens the humans
// in the loop work in are things we build.







const P = CONTENT.internalAi;

const page = {
  tag: 'page-internal-ai',
  children: [
    band('hero', [
      eyebrow(P.hero.eyebrow),
      heroBind({ headline: P.hero.headline, sub: P.hero.sub, cta: { label: P.hero.cta, route: '/contact' } }),
    ]),
    band('flow', [featuresBind({ title: P.flow.title, items: P.flow.items })], { alt: true }),
    band('control', [
      sectionHead(P.control.title, P.control.sub),
      faqBind({ items: P.control.items }),
    ]),
    band('interfaces', [featuresBind({ title: P.interfaces.title, sub: P.interfaces.sub, items: P.interfaces.items })], { alt: true }),
    ctaBind({ headline: P.close.headline, action: { label: P.close.cta, route: '/contact' } }),
  ],
  css: `
page-internal-ai { display: block; }
${SECTION_CSS}
page-internal-ai s-sc[band=hero] { padding-bottom: 24px; }
page-internal-ai s-sc[band=hero] s-hro { padding-top: 8px; }
`,
};

const internalAiDefs = [page];
return { internalAiDefs };
})();

const __m222 = (() => {
const outbound = __m216.outbound;
// The product inventory as a list: name, status, one line, two ways in.
//
// Six products are a catalogue, and a catalogue is read by scanning down a
// column of names — not by comparing six cards of equal weight. The list also
// makes room for the thing cards had nowhere to put: both destinations, the
// page here and the product's own site.


function productListDSL(items, labels) {
  return ['s-cn', '@rg=inv', items.map((p) => (
    ['s-cn', '@rg=inv-row', [
      ['s-cn', '@rg=inv-name', [
        ['h3', `=${p.name}`],
        ['s-cn', '@rg=pill', `@state=${p.status}`, [['s-l', `=${labels[p.status]}`]]],
      ]],
      ['s-l', '@rg=inv-body', `=${p.body}`],
      ['s-cn', '@rg=inv-links', [
        ['s-b', '@v=tx', '@rg=inv-in', `~cl:nav:${p.href}`, [['span', '=Read more'], ['s-ic', '@n=arrow-right']]],
        ...(p.site ? [outbound(p.site.replace('https://', ''), p.site)] : []),
      ]],
    ]]
  ))];
}

const PRODUCT_LIST_CSS = `
/* One grid for the whole ledger, rows on subgrid — each row was its own grid
   container, so every row computed its own column widths and the description
   column wandered by 60px down the list. A ledger whose columns wander is not
   a ledger. */
s-cn[rg=inv] { display: grid; grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.6fr) auto; border-top: 1px solid var(--c-border-strong); }
s-cn[rg=inv-row] { grid-column: 1 / -1; display: grid; grid-template-columns: subgrid; align-items: center; gap: 28px; padding: 22px 4px; border-bottom: 1px solid var(--c-border); transition: background 160ms ease; }
s-cn[rg=inv-row]:hover { background: rgb(148 163 253 / .05); }
s-cn[rg=inv-name] { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
s-cn[rg=inv-name] h3 { margin: 0; font-family: var(--font-display); font-size: 1.25rem; font-weight: 700; letter-spacing: -0.02em; }
label[is=s-l][rg=inv-body] { font-size: 0.9375rem; line-height: 1.55; color: var(--c-text-muted); }
s-cn[rg=inv-links] { display: flex; align-items: center; gap: 18px; justify-content: flex-end; }
button[rg=inv-in] { padding: 0; background: transparent; border: 0; box-shadow: none; color: var(--c-primary-hover); font-weight: 600; font-size: 0.875rem; display: inline-flex; align-items: center; gap: 6px; }
button[rg=inv-in]:hover { text-decoration: underline; background: transparent; }
button[rg=inv-in] s-ic { width: 13px; height: 13px; }
@media (max-width: 900px) {
  s-cn[rg=inv] { grid-template-columns: 1fr; }
  s-cn[rg=inv-row] { grid-template-columns: 1fr; gap: 10px; padding: 20px 4px; }
  s-cn[rg=inv-links] { justify-content: flex-start; }
}
`;
return { productListDSL, PRODUCT_LIST_CSS };
})();

const __m223 = (() => {
const heroBind = __m206.bind;
const ctaBind = __m210.bind;
const CONTENT = __m202.CONTENT;
const productListDSL = __m222.productListDSL;
const PRODUCT_LIST_CSS = __m222.PRODUCT_LIST_CSS;
const band = __m216.band;
const eyebrow = __m216.eyebrow;
const SECTION_CSS = __m216.SECTION_CSS;
// /products — the index. Five cards, one status each.






const P = CONTENT.products;

const page = {
  tag: 'page-products',
  children: [
    band('hero', [
      eyebrow(P.hero.eyebrow),
      heroBind({ headline: P.hero.headline, sub: P.hero.sub }),
    ]),
    band('grid', [productListDSL(P.items, P.statusLabels)], { alt: true }),
    ctaBind({
      headline: CONTENT.home.close.headline,
      sub: CONTENT.home.close.sub,
      action: { label: CONTENT.home.close.cta, route: '/contact' },
    }),
  ],
  css: `
page-products { display: block; }
${SECTION_CSS}
${PRODUCT_LIST_CSS}
page-products s-sc[band=hero] { padding-bottom: 24px; }
page-products s-sc[band=hero] s-hro { padding-top: 8px; }
`,
};

const productsDefs = [page];
return { productsDefs };
})();

const __m224 = (() => {
const featuresBind = __m207.bind;
const ctaBind = __m210.bind;
const CONTENT = __m202.CONTENT;
const band = __m216.band;
const eyebrow = __m216.eyebrow;
const sectionHead = __m216.sectionHead;
const SECTION_CSS = __m216.SECTION_CSS;
// /products/:pid — five products, one component. They differ in content, not in
// shape, so a second page component per product would be four copies of a
// layout waiting to drift apart.
//
// The route parameter picks the content at render time, which is why this page
// is a `?pid=` conditional stack rather than a data lookup: the conditional is
// evaluated by both the server and the client from the same store key, so the
// right product is in the HTML rather than swapped in after hydration.





const STATUS = CONTENT.products.statusLabels;

function productSections(id, p) {
  return ['s-cn', `?pid=${id}`, '@rg=product', [
    band('hero', [
      eyebrow(p.eyebrow),
      ['s-cn', '@rg=prod-head', [
        ['h1', '@rg=prod-h1', `=${p.headline}`],
        ['s-cn', '@rg=pill', `@state=${p.status}`, [['s-l', `=${STATUS[p.status]}`]]],
      ]],
      ['s-l', '@rg=prod-sub', `=${p.sub}`],
      ['s-cn', '@rg=band-action', [
        ['s-b', '@c=pr', '~cl:nav:/contact', [['span', `=${p.cta || 'Talk to us about it'}`], ['s-ic', '@n=arrow-right']]],
        ...(p.site ? [['s-ln', `@href=${p.site.href}`, '@target=_blank', '@rel=noopener noreferrer', '@rg=prod-site', [
          ['span', `=${p.site.label}`], ['s-ic', '@n=arrow-up-right'],
        ]]] : []),
      ]],
    ]),
    // A real view of the thing, where one exists. Nothing is mocked up: a
    // product with no interface yet shows no interface.
    ...(p.shot ? [band('shot', [
      ['s-cn', '@rg=shot', [
        ['img', `@src=${p.shot.src}`, `@alt=${p.shot.alt}`, '@loading=lazy', '@decoding=async', '@width=2160', '@height=1350'],
      ]],
      ['s-l', '@rg=shot-cap', `=${p.shot.caption}`],
    ], { tight: true })] : []),

    band('why', [
      sectionHead(p.why.title),
      ['p', '@rg=prose', `=${p.why.body}`],
    ], { alt: true }),
    band('does', [featuresBind({ title: p.does.title, items: p.does.items })]),
    band('who', [
      sectionHead('Who it is for.'),
      ['p', '@rg=prose', `=${p.who}`],
      sectionHead('Status and access.'),
      ['p', '@rg=prose', `=${p.access}`],
    ], { tint: true }),
  ]];
}

const page = {
  tag: 'page-product',
  children: [
    ...Object.entries(CONTENT.productPages).map(([id, p]) => productSections(id, p)),
    ctaBind({
      headline: CONTENT.home.close.headline,
      action: { label: CONTENT.home.close.cta, route: '/contact' },
    }),
  ],
  css: `
page-product { display: block; }
${SECTION_CSS}
page-product s-cn[rg=prod-head] { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
page-product h1[rg=prod-h1] { margin: 0; font-family: var(--font-display); font-weight: 400; letter-spacing: -0.01em; font-size: clamp(2.25rem, 1.5rem + 3vw, 3.75rem); line-height: 1.06; text-wrap: balance; }
page-product label[is=s-l][rg=prod-sub] { display: block; margin-top: 20px; max-width: var(--site-text); font-size: 1.0625rem; line-height: 1.65; color: var(--c-text-secondary); }
page-product p[rg=prose] { margin: 0 0 20px; max-width: var(--site-text); font-size: 1.0625rem; line-height: 1.7; color: var(--c-text-secondary); }
page-product s-ln[rg=prod-site] { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; color: var(--c-primary); }
page-product s-ln[rg=prod-site] s-ic { width: 14px; height: 14px; }
page-product s-sc[band=hero] { padding-bottom: 40px; }
/* The screenshot sits in a frame that reads as a window onto the product, not
   as a card: one hairline, a slight lift, and the caption outside it. */
page-product s-cn[rg=shot] { display: block; border: 1px solid var(--c-border); border-radius: var(--radius-box); overflow: hidden; background: var(--c-surface); box-shadow: 0 24px 60px rgb(2 6 23 / .5); }
page-product s-cn[rg=shot] img { display: block; width: 100%; height: auto; }
page-product label[is=s-l][rg=shot-cap] { display: block; margin-top: 14px; font-size: 0.8125rem; color: var(--c-text-muted); }
`,
};

// Give each retained product URL a concrete component. This avoids depending on
// a router parameter during hydration and on conditional re-mount subscriptions.
const productDefs = Object.entries(CONTENT.productPages).map(([id, product]) => {
  const section = productSections(id, product);
  section.splice(1, 1); // The route itself selects the product; no conditional token.
  const tag = `page-product-${id}`;
  return { tag, children: [section], css: page.css.replaceAll('page-product', tag) };
});
return { productDefs };
})();

const __m225 = (() => {
// Corporate pages are declarative compositions. Product detail stays on its own destination.
const link = (label, href) => ['s-ln', `@href=${href}`, `=${label}`];
const section = (region, children) => ['s-sc', `@rg=${region}`, [['s-cn', '@rg=home-wrap', '@l=vertical', children]]];
const opening = (eyebrow, title, body) => section('corporate-opening', [
  ['s-l', '@rg=eyebrow', `=${eyebrow}`], ['h1', `=${title}`], ['p', '@rg=lead', `=${body}`],
]);
const essay = (number, title, body, detail) => ['s-cn', '@l=fg', '@rg=essay', [
  ['s-cn', [['s-l', '@rg=eyebrow', `=${number}`], ['h2', `=${title}`]]],
  ['s-cn', '@rg=essay-copy', [['p', '@rg=lead-small', `=${body}`], ['p', `=${detail}`]]],
]];
const corporateDefs = [
  { tag: 'page-work', children: [
    opening('Our work', 'The foundation for\nyour AI applications.', 'Cherga joins an AI-first framework to Studio, automation, system modernisation and distributed execution. Build new applications and bring existing systems into the same architecture.'),
    ['home-foundation'], ['home-studio'], ['home-automation'], ['home-modernisation'], ['home-edge'], ['home-contracts'],
    section('work-examples', [
      ['s-l', '@rg=eyebrow', '=In practice'], ['h2', '=One foundation. Different contexts.'],
      ['p', '@rg=lead-small', '=GDS Playground is an example of a white-labelled AI Studio, shaped around public-service design. It shows how a common foundation can support a specific working environment.'],
      link('Explore GDS Playground', '/products/gds-playground'),
      ['p', '@rg=built-with', '=Our existing tools remain available through their current product pages as we bring the Cherga platform together.'],
      link('Existing tools and availability', '/products'),
    ]), ['home-engagement'],
  ] },
  { tag: 'page-approach', children: [
    opening('Our architecture', 'One foundation.\nFrom interface to execution.', 'Cherga connects an AI-first UI framework, data services and a shared execution runtime. Studio creates against that architecture. Code Doctor brings existing systems into it. Cloudflare is our first integrated delivery target.'),
    section('architecture-overview', [
      ['s-l', '@rg=eyebrow', '=How the platform fits together'],
      ['h2', '=Every layer has a job.\nThe connections are designed in.'],
      ['p', '@rg=lead-small', '=The router, store, HTTP service and enterprise components form the application foundation. Authentication and access rules run through it. The shared runner connects the application to automation, agents and human decisions.'],
      ['architecture-map'],
    ]),
    section('application-lifecycle', [
      ['s-l', '@rg=eyebrow', '=A concrete example / A service-request workspace'],
      ['h2', '=Follow the data.\nSee what you no longer scaffold.'],
      ['p', '@rg=lead-small', '=A team needs a workspace that loads service requests, streams an AI summary and sends exceptions for approval. Here is how that application maps onto Cherga.'],
      ['s-cn', '@rg=lifecycle-flow', '@l=fg', [
        ['s-cn', [['strong', '=01 / Route'], ['p', '=Open /requests. The route selects the workspace and triggers its declared data service.']]],
        ['s-cn', [['strong', '=02 / HTTP service'], ['p', '=The service calls the configured endpoint and writes result, loading and error state to named store keys.']]],
        ['s-cn', [['strong', '=03 / Store + UI'], ['p', '=The request table binds to those keys. Data and status changes update the interface.']]],
        ['s-cn', [['strong', '=04 / Action + flow'], ['p', '=An authorised action calls a service backed by the runner. Agent work, retries and review gates belong to that flow.']]],
      ]],
      ['p', '@rg=lifecycle-return', '=The return path: services send results and streamed updates to the store; bound components update the interface.'],
      ['s-cn', '@rg=responsibility-grid', '@l=fg', [
        ['s-cn', [['h3', '=You define the application.'], ['p', '=The request fields, endpoint contracts, component configuration, access policies and approval rules. The model generates this product-specific definition against existing framework capabilities.']]],
        ['s-cn', [['h3', '=The foundation supplies the machinery.'], ['p', '=Routing, reactive bindings, HTTP handling, streaming support, reusable UI, authentication foundations and permission states. The runner supplies flow execution and retry handling.']]],
      ]],
      ['p', '@rg=platform-note', '=Illustrated application flow. UI permission rules control available actions; endpoints independently enforce authorisation.'],
    ]),
    section('approach-essays', [
      essay('AI-first / By construction', 'Generate the application. Reuse the implementation.', 'Cherga applications are declarative compositions of components, routes, state bindings and services. Those definitions give a model concrete building blocks and explicit places to connect data.', 'A table, an authentication flow or a streaming service does not need a fresh implementation on every generation. The model concentrates on the application definition and business-specific work. Shared implementations reduce the boilerplate to generate, review and maintain.'),
      essay('Research / 100,000 repositories', 'Common problems became framework decisions.', 'We analysed 100,000 repositories to understand recurring structures, common mistakes and the scaffolding teams repeatedly write.', 'That research informs the component library and application conventions. Reusing a defined capability takes less generation work than recreating its internals. Reducing that repeated code is central to our token-efficiency design and to keeping mundane engineering out of every new project.'),
      essay('Studio + modernisation', 'Create new software. Carry existing knowledge forward.', 'AI Studio is the integrated authoring workspace around Cherga: shape the interface, connect services, compose flows and prepare the application for delivery. Direct framework users work with the same foundation through ChergaJS.', 'Code Doctor supplies topology discovery across repositories, services and infrastructure, plus validated intent and a reusable rulebook. Studio can use that system context to plan migration into connected services and flows, preserving the process that the old code served.'),
      essay('Automation + shared execution', 'One runner behind the work.', 'The agent-stack runner is the execution layer within the platform. Application events and service calls connect to deterministic steps, agent tasks, retries and human review gates.', 'The integration target is one crafted environment for creating the app and defining what it does. Domain-specific harnesses add data contracts, tools and review rules to the shared runner, so every application does not need a separate execution foundation.'),
      essay('Worker SSR + distributed applications', 'Search-visible pages. AI-capable applications.', 'The lightweight SSR framework renders HTML and metadata in a Worker. Browser components and streaming services bring the interactive AI experience to that same application.', 'Our Cloudflare-first delivery architecture combines CDN assets, Worker execution and suitable content cached at the edge. Cache policy follows the data and its access requirements. Services and flows connect the distributed interface to the systems doing the work.'),
    ]),
    section('architecture-principle', [
      ['s-l', '@rg=eyebrow', '=The engineering choice'],
      ['h2', '=Own the connections.\nBuild on the foundation.'],
      ['p', '@rg=lead-small', '=Every separately assembled layer adds decisions about state, identity, data and execution. Cherga makes those connections part of the platform design. Our ambition is an enduring foundation for your AI journey—from the first application to enterprise automation and whole-system modernisation.'],
    ]), ['home-engagement'],
  ] },
  { tag: 'page-about', children: [
    opening('The company', 'Practical questions.\nIndependent thinking.', 'Pragmatical AI is a UK-based applied-AI company. We build the foundations and tools we want to use ourselves: software that connects the interface, the system and the work.'),
    section('company-story', [
      essay('Our conviction', 'AI belongs in the design.', 'We believe the next generation of software should be designed around what AI makes possible, with a coherent experience for the people using it.', 'That means building the framework as well as the tools around it. It means understanding existing systems, and giving agent work a clear place within them.'),
      essay('Our direction', 'A platform. A shared foundation.', 'We are bringing our UI framework, Studio, Code Doctor and agent-stack together under Cherga. The goal is one crafted platform for creating applications and evolving existing systems.', 'Cherga.com is planned as the main platform destination, with a dedicated ChergaJS entry point for people who want to use the UI framework directly. Cloudflare is our first intended integrated deployment target.'),
      essay('Our product work', 'Depth in the domain.', 'Contract Vetting is our flagship domain product. It applies our work on connected data flows and agent execution to the realities of document review.', 'It has a distinct identity and product direction. Alongside Cherga, it demonstrates our commitment to building both useful foundations and the tools that put them to work.'),
    ]), ['home-engagement'],
  ] },
];
return { corporateDefs };
})();

const __m226 = (() => {
const contactPageDefs = [{
  tag: 'page-contact',
  children: [
    ['s-sc', '@rg=chat-page', [
      ['s-cn', '@rg=home-wrap', '@l=vertical', [
        ['s-l', '@rg=eyebrow', '=Pragmatical AI assistant'],
        ['h1', '=What are you\nbuilding next?'],
        ['p', '@rg=lead-small', '=Explore the framework. Work through an automation idea. Find a starting point for modernisation.'],
        ['primary-assistant'],
        ['noscript', [['p', '=Enable JavaScript to use the assistant. You can still explore the platform and architecture pages.'], ['s-ln', '@href=/approach', '=Explore the architecture']]],
      ]],
    ]],
  ],
}];
return { contactPageDefs };
})();

const __m227 = (() => {
const CONTENT = __m202.CONTENT;
const page = {
  tag: 'page-not-found',
  children: [
    ['s-sc', '@band=nf', [['s-cn', [
      ['h1', '@rg=nf-code', `=${CONTENT.notFound.title}`],
      ['s-l', '@rg=nf-msg', `=${CONTENT.notFound.message}`],
      ['s-cn', '@rg=band-action', [
        ['s-b', '@c=pr', '~cl:nav:/', [['span', `=${CONTENT.notFound.back}`]]],
        ['s-b', '@v=ol', '~cl:nav:/products', [['span', '=See the products']]],
      ]],
    ]]]],
  ],
  css: `
page-not-found { display: block; }
page-not-found s-sc[band=nf] { display: block; padding: 140px 24px; text-align: center; }
page-not-found s-sc[band=nf] > s-cn { display: block; max-width: var(--site-max); margin: 0 auto; }
page-not-found h1[rg=nf-code] { margin: 0; font-family: var(--font-display); font-size: clamp(4rem, 3rem + 6vw, 7rem); font-weight: 400; color: var(--c-text-muted); line-height: 1; }
page-not-found label[is=s-l][rg=nf-msg] { display: block; margin-top: 18px; font-size: 1.125rem; color: var(--c-text-secondary); }
page-not-found s-cn[rg=band-action] { display: flex; gap: 12px; justify-content: center; margin-top: 32px; }
`,
};

const notFoundDefs = [page];
return { notFoundDefs };
})();

const __m228 = (() => {
const chromeDefs = __m203.chromeDefs;
const homeState = __m204.homeState;
const chatState = __m198.chatState;
const chatServices = __m198.chatServices;
const heroDefs = __m206.defs;
const featureDefs = __m207.defs;
const statsDefs = __m208.defs;
const faqDefs = __m209.defs;
const ctaDefs = __m210.defs;
const CONTENT = __m202.CONTENT;
const contentState = __m202.contentState;
const chrome = __m203.chrome;
const docMetaFor = __m211.docMetaFor;
const homeDefs = __m215.homeDefs;
const implementationDefs = __m217.implementationDefs;
const legacyDefs = __m218.legacyDefs;
const commerceDefs = __m219.commerceDefs;
const automationDefs = __m220.automationDefs;
const internalAiDefs = __m221.internalAiDefs;
const productsDefs = __m223.productsDefs;
const productDefs = __m224.productDefs;
const corporateDefs = __m225.corporateDefs;
const contactPageDefs = __m226.contactPageDefs;
const notFoundDefs = __m227.notFoundDefs;
// The single source of truth for components, routes and seeded state. The
// browser entry (main.js), the node server (serve-prod.js) and the Cloudflare
// worker all read this same object, so there is exactly one description of the
// app and the three cannot disagree.
//
// Everything imported here must be import-safe in Node AND in workerd: defs and
// data only, no DOM at module scope, no node builtins. This file is on the SSR
// path, which runs at the edge.























const appConfig = {
  appId: 'pragmatical',

  defineComponents: [
    ...chromeDefs, ...heroDefs, ...featureDefs, ...statsDefs, ...faqDefs, ...ctaDefs,
    ...homeDefs, ...implementationDefs, ...legacyDefs, ...commerceDefs, ...automationDefs, ...internalAiDefs,
    ...productsDefs, ...productDefs, ...corporateDefs, ...contactPageDefs, ...notFoundDefs,
  ],

  // Copy is seeded as flat t.* keys; the contact form's own keys start empty so
  // the first render has something definite to show rather than a blank that
  // fills in later.
  state: {
    ...contentState,
    ...homeState,
    ...chatState,
  },

  // The header is layout (renders before the outlet), the footer is an overlay
  // (after it) — s-shm is one component that supplies both, using display:
  // contents so its header and footer become rows around the routed view.
  layout: [chrome()],

  // Icon names that arrive as DATA rather than as a literal @n= token are
  // invisible to the bundler's icon tree-shaker, which scans source. Every icon
  // in data/content.js is listed here for that reason; a missing name is not an
  // error, it is a silently absent icon.
  icons: [
    'sun', 'moon', 'menu',
    'layout-dashboard', 'workflow', 'scan-search', 'landmark',
    'database', 'user-check', 'brain', 'shopping-bag', 'settings-2',
    'shield', 'globe-lock', 'key', 'git-compare', 'boxes', 'check-check',
    'file-question-mark', 'users', 'server', 'plug', 'search-check',
    'shield-check', 'split', 'clipboard-check', 'eye-off', 'scale', 'gavel', 'file-diff',
    'chart-no-axes-column', 'ruler', 'file-check', 'ban',
    'arrow-right', 'arrow-up-right', 'languages', 'mail', 'external-link', 'circle',
  ],

  inlineCss: true,
  services: chatServices,

  routes: [
    { path: '/', component: 'page-home' },
    { path: '/work', component: 'page-work' },
    { path: '/approach', component: 'page-approach' },

    // Work with us
    { path: '/implementation', component: 'page-implementation' },
    { path: '/legacy-to-model', component: 'page-legacy' },
    { path: '/agentic-commerce', component: 'page-commerce' },
    { path: '/process-automation', component: 'page-automation' },
    { path: '/internal-ai', component: 'page-internal-ai' },

    // Products. The index and one detail route — five products, one component,
    // because they differ in content rather than in shape.
    { path: '/products', component: 'page-products' },
    ...Object.keys(CONTENT.productPages).map((id) => ({ path: `/products/${id}`, component: `page-product-${id}` })),

    { path: '/about', component: 'page-about' },
    { path: '/contact', component: 'page-contact' },

    { path: '*', component: 'page-not-found' },
  ],
};

// Per-request head metadata. Passed to the renderer by BOTH servers from this
// one place, so the node server and the worker cannot drift apart on what a
// page is called.
return { appConfig, docMetaFor };
})();

const CreateApp = __m192.CreateApp;
const store = __m192.store;
const router = __m192.router;
const A11Y_DEFAULT_STRINGS = __m70.A11Y_DEFAULT_STRINGS;
const appConfig = __m228.appConfig;
// Browser entry. The whole app is app-config.js — this file exists to hand it
// to CreateApp and to seed the framework's accessible-name strings the same way
// the SSR store does, so a pruned t.a11y.* key still has a value after
// hydration.





store.setValues(A11Y_DEFAULT_STRINGS);

// The router publishes path params as `router.<name>`, and those keys are
// stripped from the SSR state by design — so pages bind the plain name and the
// server seeds it (data/meta.js). This mirrors the router's value onto it, so a
// client-side navigation between two product pages updates the same key the
// server-rendered one is bound to.
store.subscribe('router.pid', (value) => store.setValue('pid', value || ''));

CreateApp(appConfig);

// Keep native same-origin links inside the framework router. This preserves
// shell services (including a running chat stream) across every site page.
const sitePaths = new Set(appConfig.routes.map(route => route.path));
document.addEventListener('click', (event) => {
  const link = event.target.closest?.('a[href]');
  if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
  const target = new URL(link.href, location.href);
  if (target.origin !== location.origin || !sitePaths.has(target.pathname)) return;
  if (target.hash && target.pathname === location.pathname && target.search === location.search) return;
  event.preventDefault();
  router.navigateTo(target.pathname + target.search);
  if (target.hash) requestAnimationFrame(() => {
    history.replaceState(history.state, '', target.pathname + target.search + target.hash);
    try { document.getElementById(decodeURIComponent(target.hash.slice(1)))?.scrollIntoView(); } catch { /* An invalid fragment has no scroll target. */ }
  });
  else window.scrollTo(0, 0);
});


// Platform integration only: the control itself is declarative SWC, and the
// selected preference lives in the store. Persist just this preference, never
// contact fields or the application's content/state.
let savedAppearance = 'light';
try { savedAppearance = localStorage.getItem('pragmatical.appearance') || 'light'; } catch { /* Storage can be unavailable. */ }
// Resolve the previous three-way preference once; the UI now has two modes.
if (savedAppearance === 'system') savedAppearance = matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
store.setValue('appearance', savedAppearance === 'dark' ? 'dark' : 'light');
store.subscribe('appearance', (value) => {
  const dark = value === 'dark';
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  store.setValues({
    appearanceIcon: dark ? 'sun' : 'moon',
    't.a11y.themeAction': store.getValue(dark ? 't.a11y.switchToLight' : 't.a11y.switchToDark'),
  });
  try { localStorage.setItem('pragmatical.appearance', value); } catch { /* The control still works without persistence. */ }
});
